import React, { useRef, useState, useCallback, useEffect, useContext, Fragment } from 'react';
import { useLocation, Route, Redirect, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Layout, Button, Dropdown, Menu, Select, Tag, List, Typography, Modal, message, Switch, Collapse, Table, Tooltip, InputNumber, Form, DatePicker, Spin, Skeleton, Drawer, Descriptions } from 'antd';
import { MenuOutlined, LeftOutlined, RightOutlined, UserOutlined, ArrowLeftOutlined, FilterOutlined, CloseOutlined, PlusOutlined, CheckOutlined, LoadingOutlined, WarningFilled, BellFilled, CaretRightOutlined, SettingOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FaCog, FaSignOutAlt, FaCalendarAlt, FaMapMarkedAlt, FaList } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectors, hooks } from 'farmx-redux-core';
import { StyleSheet, css } from 'aphrodite';
import { sensorApi, constants, controlApi, authService } from 'farmx-api';
import moment from 'moment';
import 'antd/dist/antd.css';
import ReactDOMServer from 'react-dom/server';
import WebMercatorViewport from 'viewport-mercator-project';
import bbox from '@turf/bbox';
import ReactResizeDetector from 'react-resize-detector';
import L from 'leaflet';
import { useLeaflet, Map as Map$2, TileLayer, GeoJSON, Marker } from 'react-leaflet';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function PrivateRoute(_ref) {
  var Component = _ref.component,
      authService = _ref.authService,
      adminRequired = _ref.adminRequired,
      rest = _objectWithoutPropertiesLoose(_ref, ["component", "authService", "adminRequired"]);

  var userInfo = authService.useAuth();
  var authorized = userInfo && (!adminRequired || userInfo.admin);
  var location = useLocation();

  var render = function render(props) {
    return /*#__PURE__*/React.createElement(Component, props);
  };

  return authorized ? /*#__PURE__*/React.createElement(Route, _extends({
    render: render
  }, rest)) : /*#__PURE__*/React.createElement(Redirect, {
    to: {
      pathname: '/login',
      state: {
        from: location
      }
    }
  });
}

PrivateRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  authService: PropTypes.shape({
    useAuth: PropTypes.func.isRequired
  }).isRequired,
  adminRequired: PropTypes.bool
};
PrivateRoute.defaultProps = {
  adminRequired: false
};

var Header = Layout.Header,
    Content = Layout.Content;

function useWindowSize() {
  var isClient = typeof window === 'object';
  var getSize = useCallback(function () {
    return {
      width: isClient ? window.innerWidth : undefined,
      height: isClient ? window.innerHeight : undefined
    };
  }, [isClient]);

  var _useState = useState(getSize),
      windowSize = _useState[0],
      setWindowSize = _useState[1];

  var handleResize = useCallback(function () {
    setWindowSize(getSize());
  }, [setWindowSize, getSize]);
  useEffect(function () {
    if (!isClient) return false;
    window.addEventListener('resize', handleResize);
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, isClient]);
  return windowSize;
}

function AppTemplate(props) {
  var size = useWindowSize();
  var scrollRef = useRef(null);
  var isMobile = size.width <= 600;

  var _useState2 = useState(isMobile),
      collapsed = _useState2[0],
      setCollapsed = _useState2[1];

  var NavBarComponent = props.navBarComponent,
      EntitySideBarComponent = props.entitySideBarComponent,
      children = props.children,
      _props$title = props.title,
      title = _props$title === void 0 ? 'FarmX' : _props$title,
      logo = props.logo,
      headerMenu = props.headerMenu,
      theme = props.theme;
  var onCollapse = useCallback(function (collapse) {
    setCollapsed(collapse);
  }, [setCollapsed]);
  var fontColor = theme === 'dark' ? 'white' : '#555';

  var scroll = function scroll(scrollOffset) {
    scrollRef.current.scrollLeft += scrollOffset;
  };

  return /*#__PURE__*/React.createElement(Layout, {
    className: "layout"
  }, /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("title", null, title)), /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement("div", {
    className: "menu-trigger"
  }, /*#__PURE__*/React.createElement(MenuOutlined, {
    className: "trigger",
    style: {
      fontSize: '25px',
      color: fontColor
    },
    onClick: function onClick() {
      return setCollapsed(!collapsed);
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "logo"
  }, /*#__PURE__*/React.createElement("img", {
    className: "header-logo",
    alt: "FarmX",
    src: logo
  })), /*#__PURE__*/React.createElement(Button, {
    className: "scroll-button",
    type: "primary",
    icon: /*#__PURE__*/React.createElement(LeftOutlined, null),
    onClick: function onClick() {
      return scroll(-25);
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "middle-menu",
    ref: scrollRef
  }, /*#__PURE__*/React.createElement(NavBarComponent, {
    isMobile: isMobile
  })), /*#__PURE__*/React.createElement(Button, {
    className: "scroll-button",
    type: "primary",
    icon: /*#__PURE__*/React.createElement(RightOutlined, null),
    onClick: function onClick() {
      return scroll(25);
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "right-menu"
  }, headerMenu)), /*#__PURE__*/React.createElement(Layout, null, EntitySideBarComponent && /*#__PURE__*/React.createElement(EntitySideBarComponent, {
    isMobile: isMobile,
    onCollapse: onCollapse,
    collapsed: collapsed
  }), /*#__PURE__*/React.createElement(Content, {
    className: collapsed ? 'content-container collapsed' : 'content-container'
  }, children)));
}
AppTemplate.propTypes = {
  navBarComponent: PropTypes.elementType,
  entitySideBarComponent: PropTypes.elementType,
  children: PropTypes.element.isRequired,
  title: PropTypes.string,
  logo: PropTypes.string.isRequired,
  headerMenu: PropTypes.element.isRequired,
  theme: PropTypes.oneOf(['dark', 'light'])
};
AppTemplate.defaultProps = {
  navBarComponent: null,
  entitySideBarComponent: null,
  title: 'FarmX',
  theme: 'light'
};

function UserMenu(props) {
  var userInfo = props.userInfo;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  if (!userInfo) return null;
  return /*#__PURE__*/React.createElement(Dropdown, {
    placement: "bottomRight",
    overlay: /*#__PURE__*/React.createElement(Menu, null, /*#__PURE__*/React.createElement(Menu.Item, null, /*#__PURE__*/React.createElement(Link, {
      to: "/settings"
    }, /*#__PURE__*/React.createElement(FaCog, null), /*#__PURE__*/React.createElement("span", null, t('Settings')))), /*#__PURE__*/React.createElement(Menu.Divider, null), /*#__PURE__*/React.createElement(Menu.Item, null, /*#__PURE__*/React.createElement(Link, {
      to: "/logout"
    }, /*#__PURE__*/React.createElement(FaSignOutAlt, null), /*#__PURE__*/React.createElement("span", null, t('Logout')))))
  }, userInfo ? /*#__PURE__*/React.createElement(Button, {
    type: "link",
    className: "ant-dropdown-link user-menu"
  }, /*#__PURE__*/React.createElement("span", {
    className: "user-title"
  }, userInfo.username), /*#__PURE__*/React.createElement(UserOutlined, null)) : /*#__PURE__*/React.createElement(Button, null));
}

UserMenu.propTypes = {
  userInfo: PropTypes.shape({
    username: PropTypes.string.isRequired
  })
};
UserMenu.defaultProps = {
  userInfo: null
};

var Sider = Layout.Sider;

var CollapsableSidebar = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(CollapsableSidebar, _React$Component);

  function CollapsableSidebar() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;

    _this.collapse = function () {
      var onCollapse = _this.props.onCollapse;
      onCollapse(true);
    };

    _this.toggle = function () {
      var _this$props = _this.props,
          collapsed = _this$props.collapsed,
          onCollapse = _this$props.onCollapse;
      onCollapse(!collapsed);
    };

    _this.toggleIfNotMobile = function () {
      var isMobile = _this.props.isMobile;

      if (!isMobile) {
        _this.toggle();
      }
    };

    return _this;
  }

  var _proto = CollapsableSidebar.prototype;

  _proto.render = function render() {
    var _this$props2 = this.props,
        collapsed = _this$props2.collapsed,
        onCollapse = _this$props2.onCollapse,
        children = _this$props2.children,
        theme = _this$props2.theme;
    return /*#__PURE__*/React.createElement(Sider, {
      className: "nav",
      collapsible: true,
      collapsed: collapsed,
      onCollapse: onCollapse,
      collapsedWidth: 50,
      theme: theme
    }, children, /*#__PURE__*/React.createElement("div", {
      className: "sider-filler",
      role: "button",
      tabIndex: 0,
      onKeyDown: this.toggleIfNotMobile,
      onClick: this.toggleIfNotMobile
    }));
  };

  return CollapsableSidebar;
}(React.Component);

CollapsableSidebar.propTypes = {
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
  children: PropTypes.element,
  theme: PropTypes.oneOf(['light', 'dark']),
  isMobile: PropTypes.bool
};
CollapsableSidebar.defaultProps = {
  onCollapse: function onCollapse() {},
  collapsed: false,
  children: null,
  theme: 'dark',
  isMobile: false
};

var styles = {"left-align":"_B5SkY","right-align":"_2Wsl8","selectDropDown":"_4Ic_e","collapse-content":"_1Eppv","collapse-content-collapse-selected":"_2fE0F","header":"_2d_-o","selected-block":"_2zQyk","selected-block-text":"_12vYp","selected-ranch-header":"_3t_l3","collapsible-list-selected":"_1BFq2","collapsible-list":"_1s2Io","ant-collapse":"_2ERLJ","ant-collapse-item":"_19Mfc","ant-collapse-header":"_2bx_o","list-content":"_1QkEB","ant-collapse-content":"_1lNDt","ant-collapse-content-box":"_2D_vV","selectedValue":"_3NTaH","panel-statusBox":"_1GsrB","statusBox":"_XLc70","ant-tooltip-inner":"_2ALx6","list-name":"_YQbVw","loadingIcon":"_HvQyT"};

function ItemSelect(props) {
  var onChange = props.onChange,
      onSelect = props.onSelect,
      onFocus = props.onFocus,
      mode = props.mode,
      renderItem = props.renderItem,
      items = props.items,
      tagRender = props.tagRender,
      placeholder = props.placeholder,
      defaultOption = props.defaultOption,
      filterOption = props.filterOption,
      value = props.value,
      dropdownRender = props.dropdownRender,
      dropdownStyle = props.dropdownStyle,
      menuIsOpen = props.menuIsOpen,
      onClick = props.onClick,
      dropdownStyleValues = props.dropdownStyleValues,
      loading = props.loading;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var children = [];

  if (items && items.length > 0) {
    items.forEach(function (d) {
      children.push(renderItem(d));
    });
  }

  var getDropDownClass = function getDropDownClass(dropDownStyle) {
    var styleClassName;

    if (window.innerWidth > 599) {
      if (dropDownStyle && dropDownStyle.leftValue) {
        var createdStyles = StyleSheet.create({
          leftposition: {
            left: dropDownStyle.leftValue.current + " !important",
            width: dropDownStyle.minWidthValue.current + " !important"
          }
        });
        styleClassName = "" + css(createdStyles.leftposition);
      }
    }

    if (window.innerWidth < 599) {
      styleClassName = styles.selectDropDown + " ";
    }

    return styleClassName;
  };

  return /*#__PURE__*/React.createElement("div", null, value ? /*#__PURE__*/React.createElement(Select, {
    showSearch: true,
    filterOption: filterOption,
    mode: mode,
    style: {
      width: '100%'
    },
    placeholder: t(placeholder || 'Please select'),
    defaultValue: defaultOption || [],
    value: value || undefined,
    onSelect: onSelect,
    onChange: onChange,
    onFocus: onFocus,
    allowClear: true,
    tagRender: tagRender,
    dropdownRender: dropdownRender,
    onMouseDown: function onMouseDown(e) {
      e.stopPropagation();
    },
    dropdownStyle: dropdownStyle,
    optionLabelProp: "label",
    dropdownClassName: getDropDownClass(dropdownStyleValues),
    open: menuIsOpen != null ? menuIsOpen : undefined,
    onClick: menuIsOpen != null ? onClick : undefined,
    listHeight: window.innerWidth < 599 ? window.innerHeight - 100 : 330,
    loading: loading
  }, children) : /*#__PURE__*/React.createElement(Select, {
    showSearch: true,
    filterOption: filterOption,
    mode: mode,
    style: {
      width: '100%'
    },
    placeholder: t(placeholder || 'Please select'),
    defaultValue: defaultOption || [],
    onSelect: onSelect,
    onChange: onChange,
    onFocus: onFocus,
    allowClear: true,
    tagRender: tagRender,
    dropdownRender: dropdownRender,
    onMouseDown: function onMouseDown(e) {
      e.stopPropagation();
    },
    dropdownStyle: dropdownStyle,
    optionLabelProp: "label",
    dropdownClassName: getDropDownClass(dropdownStyleValues),
    open: menuIsOpen != null ? menuIsOpen : undefined,
    onClick: menuIsOpen != null ? onClick : undefined,
    listHeight: window.innerWidth < 599 ? window.innerHeight - 100 : 330,
    loading: loading
  }, children));
}

ItemSelect.propTypes = {
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onFocus: PropTypes.func,
  mode: PropTypes.string,
  renderItem: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.any),
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  filterOption: PropTypes.func,
  value: PropTypes.arrayOf(PropTypes.any),
  dropdownRender: PropTypes.func,
  dropdownStyle: PropTypes.shape({}),
  menuIsOpen: PropTypes.bool,
  onClick: PropTypes.func,
  dropdownStyleValues: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({})]),
  loading: PropTypes.bool
};
ItemSelect.defaultProps = {
  onChange: null,
  onSelect: null,
  onFocus: null,
  mode: null,
  renderItem: null,
  items: [],
  tagRender: null,
  placeholder: null,
  defaultOption: null,
  filterOption: null,
  value: null,
  dropdownRender: null,
  dropdownStyle: {},
  menuIsOpen: null,
  onClick: null,
  dropdownStyleValues: null,
  loading: false
};

var BlockOption = function BlockOption(_ref) {
  var block = _ref.block;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: styles['left-align']
  }, block.name), /*#__PURE__*/React.createElement("span", {
    className: styles['right-align']
  }, block.ranch && block.ranch.name));
};

BlockOption.propTypes = {
  block: PropTypes.shape({
    name: PropTypes.string,
    ranch: PropTypes.shape({
      name: PropTypes.string
    })
  }).isRequired
};

var styles$1 = {"modalSelectHeader":"_3tVdY","modalSelectHeaderText":"_QAFCD","sensorSelectOuter":"_3S7sv","filterOuterDiv":"_1TEtn","scrollbar":"_2cWDT","rc-virtual-list-scrollbar":"_1Qt_F","selectFilter":"_U8k97","filterIcon":"_U-bam","selectFilterDropdown":"_2exjr","ant-select-selection-item":"_1D2_J","ant-select":"_3VbiX","itemSelect":"_3zDiz","addbuttonstyle":"_NlCbD","sensorOptions":"_2YjfU","filterOptions":"_3KaWf","beforeAddButton":"_338QU","optionButton":"_2kC7u","optionOrButton":"_2UVaS","optionOne":"_3Z_Ca","optionTwo":"_1bywn","optionThree":"_508pI","optionFour":"_1LzGR","sensorName":"_g9C0y","ranchName":"_3w1ee","identifier":"_19Snh","blockName":"_32jfj","ant-select-item-option-state":"_Ncgty","closebuttonstyle":"_ED3i0","goBack":"_3MGlB","custom-modal":"_24a5F","ant-modal-content":"_lz-Oi","ant-modal-body":"_1rjCx"};

var DropdownModal = function DropdownModal(_ref) {
  var title = _ref.title,
      displayOptions = _ref.displayOptions,
      mobileScreen = _ref.mobileScreen,
      onClick = _ref.onClick;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/React.createElement("div", {
    className: styles$1.modalSelectOptions,
    style: {
      height: mobileScreen ? 'calc(100vh - 10px)' : 'auto'
    }
  }, mobileScreen ? /*#__PURE__*/React.createElement("div", {
    className: styles$1.modalSelectHeader
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles$1.goBack,
    id: "goBack",
    onClick: onClick
  }, /*#__PURE__*/React.createElement(ArrowLeftOutlined, {
    id: "goBack",
    onClick: onClick
  })), /*#__PURE__*/React.createElement("div", {
    className: styles$1.modalSelectHeaderText
  }, t(title)), /*#__PURE__*/React.createElement("div", null)) : null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: '5px'
    }
  }, displayOptions));
};

DropdownModal.propTypes = {
  title: PropTypes.string,
  displayOptions: PropTypes.shape({}),
  mobileScreen: PropTypes.bool,
  onClick: PropTypes.func
};
DropdownModal.defaultProps = {
  title: null,
  displayOptions: null,
  mobileScreen: false,
  onClick: null
};

var SensorSelectContext = React.createContext(3);

var Option = Select.Option;
var loadFarmData = actions.loadFarmData;
var selectAllBlocks = selectors.selectAllBlocks;

var blockTagRender = function blockTagRender(args) {
  var closable = args.closable,
      onClose = args.onClose,
      label = args.label;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: onClose,
    style: {
      marginRight: 3
    }
  }, label.props ? label.props.block.name : label);
};

var defaultItemRenderer = function defaultItemRenderer(d) {
  return /*#__PURE__*/React.createElement(Option, {
    key: d.id,
    value: d.id,
    label: d.name,
    "data-testid": "" + d.name
  }, /*#__PURE__*/React.createElement(BlockOption, {
    block: d
  }));
};

var BlockSelect = function BlockSelect(_ref) {
  var loadItems = _ref.loadItems,
      items = _ref.items,
      mode = _ref.mode,
      renderItem = _ref.renderItem,
      onSelect = _ref.onSelect,
      onChange = _ref.onChange,
      tagRender = _ref.tagRender,
      placeholder = _ref.placeholder,
      defaultOption = _ref.defaultOption,
      value = _ref.value;
  var dispatch = useDispatch();
  var data = useSelector(selectAllBlocks);
  var blocks = items || data;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var mobileScreen = window.innerWidth < 599;
  var dropdownStyleValues = useContext(SensorSelectContext);
  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadFarmData());
  }, [dispatch, loadItems]);
  var dropdownStyle = null;
  if (window.innerWidth < 599) dropdownStyle = {
    minWidth: '60%'
  };

  function onSelectAction(d) {
    if (onSelect) onSelect(d);

    if (!mobileScreen) {
      setModalState(false);
    }
  }

  return /*#__PURE__*/React.createElement(ItemSelect, {
    mode: mode,
    renderItem: renderItem,
    items: blocks,
    onSelect: onSelectAction,
    onChange: onChange,
    tagRender: tagRender || blockTagRender,
    placeholder: placeholder,
    defaultOption: defaultOption,
    value: value,
    filterOption: function filterOption(input, option) {
      return option.children.props.block.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.block.ranch && option.children.props.block.ranch.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
    dropdownStyle: dropdownStyle,
    onFocus: function onFocus() {
      return setModalState(true);
    },
    menuIsOpen: mobileScreen ? modal : undefined,
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "Block Select",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        }
      });
    } : undefined,
    dropdownStyleValues: dropdownStyleValues
  });
};

BlockSelect.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  renderItem: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  value: PropTypes.arrayOf(PropTypes.any)
};
BlockSelect.defaultProps = {
  mode: null,
  loadItems: null,
  items: null,
  renderItem: defaultItemRenderer,
  onSelect: null,
  onChange: null,
  tagRender: null,
  placeholder: null,
  defaultOption: [],
  value: null
};

var RanchOption = function RanchOption(_ref) {
  var ranch = _ref.ranch;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: styles['left-align']
  }, ranch.name), /*#__PURE__*/React.createElement("span", {
    className: styles['right-align']
  }, ranch.entity && ranch.entity.name));
};

RanchOption.propTypes = {
  ranch: PropTypes.shape({
    name: PropTypes.string,
    entity: PropTypes.shape({
      name: PropTypes.string
    })
  }).isRequired
};

var Option$1 = Select.Option;
var loadFarmData$1 = actions.loadFarmData;
var selectAllRanches = selectors.selectAllRanches;

var ranchTagRender = function ranchTagRender(args) {
  var label = args.label,
      closable = args.closable,
      onClose = args.onClose;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: onClose,
    style: {
      marginRight: 3
    }
  }, label.props ? label.props.ranch.name : label);
};

var defaultItemRenderer$1 = function defaultItemRenderer(d) {
  return /*#__PURE__*/React.createElement(Option$1, {
    key: d.id,
    value: d.id,
    label: d.name,
    "data-testid": "" + d.name
  }, /*#__PURE__*/React.createElement(RanchOption, {
    ranch: d
  }));
};

var RanchSelect = function RanchSelect(_ref) {
  var loadItems = _ref.loadItems,
      items = _ref.items,
      mode = _ref.mode,
      renderItem = _ref.renderItem,
      onSelect = _ref.onSelect,
      onChange = _ref.onChange,
      tagRender = _ref.tagRender,
      placeholder = _ref.placeholder,
      defaultOption = _ref.defaultOption,
      value = _ref.value;
  var dispatch = useDispatch();
  var data = useSelector(selectAllRanches);
  var ranches = items || data;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var mobileScreen = window.innerWidth < 599;
  var dropdownStyleValues = useContext(SensorSelectContext);
  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadFarmData$1());
  }, [dispatch, loadItems]);
  var dropdownStyle = null;
  if (window.innerWidth < 599) dropdownStyle = {
    minWidth: '60%'
  };

  function onSelectAction(d) {
    if (onSelect) onSelect(d);
    if (!mobileScreen) setModalState(false);
  }

  return /*#__PURE__*/React.createElement(ItemSelect, {
    mode: mode,
    renderItem: renderItem,
    items: ranches,
    onSelect: onSelectAction,
    onChange: onChange,
    tagRender: tagRender || ranchTagRender,
    placeholder: placeholder,
    defaultOption: defaultOption,
    value: value,
    filterOption: function filterOption(input, option) {
      return option.children.props.ranch.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.ranch.entity && option.children.props.ranch.entity.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
    dropdownStyle: dropdownStyle,
    onFocus: function onFocus() {
      return setModalState(true);
    },
    menuIsOpen: mobileScreen ? modal : undefined,
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "Ranch Select",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        }
      });
    } : undefined,
    dropdownStyleValues: dropdownStyleValues
  });
};

RanchSelect.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  renderItem: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  value: PropTypes.arrayOf(PropTypes.any)
};
RanchSelect.defaultProps = {
  mode: null,
  loadItems: null,
  items: null,
  renderItem: defaultItemRenderer$1,
  onSelect: null,
  onChange: null,
  tagRender: null,
  placeholder: null,
  defaultOption: [],
  value: null
};

var getNameForSensorType = sensorApi.getNameForSensorType;
function SensorTypeTag(_ref) {
  var sensorType = _ref.sensorType;

  function getColorForType(type) {
    switch (type) {
      case 'weather_station':
        return 'geekblue';

      case 'cavalier':
        return 'purple';

      case 'aquacheck_soil':
        return 'volcano';

      case 'water_pressure':
        return 'blue';

      case 'farmx_dendrometer':
        return 'green';

      case 'pixl_soil':
        return 'orange';

      case 'farmx_infrared':
        return 'lime';

      case 'farmx_frost':
        return 'cyan';

      default:
        return null;
    }
  }

  var color = getColorForType(sensorType);
  return /*#__PURE__*/React.createElement(Tag, {
    color: color
  }, getNameForSensorType(sensorType));
}
SensorTypeTag.propTypes = {
  sensorType: PropTypes.string.isRequired
};

function InstallStateTag(_ref) {
  var state = _ref.state;

  function getColorForState(installState) {
    switch (installState) {
      case 'removed':
        return 'red';

      case 'installed':
        return 'green';

      case 'planned':
        return 'cyan';

      case 'withdrawn':
        return 'purple';

      case 'inventory':
        return 'blue';

      case 'unknown':
        return 'geekblue';

      case 'dead':
        return 'black';

      case 'maintenance':
        return 'magenta';

      default:
        return null;
    }
  }

  return /*#__PURE__*/React.createElement(Tag, {
    color: getColorForState(state)
  }, state);
}

InstallStateTag.propTypes = {
  state: PropTypes.string.isRequired
};

var Text = Typography.Text;
var selectRanchById = selectors.selectRanchById,
    selectBlockById = selectors.selectBlockById;

var SensorOption = function SensorOption(_ref) {
  var sensor = _ref.sensor;
  var ranch = useSelector(function (state) {
    return selectRanchById(state, sensor.ranch);
  });
  var block = useSelector(function (state) {
    return selectBlockById(state, sensor.block);
  });
  return /*#__PURE__*/React.createElement("div", {
    id: "sensorOption"
  }, /*#__PURE__*/React.createElement(List, {
    className: styles$1.sensorOptions
  }, /*#__PURE__*/React.createElement(List.Item, {
    className: styles$1.listSensorOptions
  }, /*#__PURE__*/React.createElement(List.Item.Meta, {
    className: styles$1.optionOne,
    title: sensor.name ? /*#__PURE__*/React.createElement(Text, {
      className: styles$1.sensorName,
      ellipsis: true,
      title: "" + sensor.name
    }, "" + sensor.name) : /*#__PURE__*/React.createElement(Text, {
      style: {
        color: 'lightgray'
      },
      title: "No Name"
    }, "No Name"),
    description: /*#__PURE__*/React.createElement(Text, {
      className: styles$1.identifier,
      ellipsis: true,
      title: "" + sensor.identifier
    }, "" + sensor.identifier)
  }), /*#__PURE__*/React.createElement(List.Item.Meta, {
    title: /*#__PURE__*/React.createElement(Text, {
      className: styles$1.ranchName,
      ellipsis: true,
      title: ranch && ranch.name ? ranch.name : ''
    }, ranch && ranch.name ? ranch.name : ''),
    description: /*#__PURE__*/React.createElement(Text, {
      className: styles$1.blockName,
      ellipsis: true,
      title: block && block.name ? block.name : ''
    }, block && block.name ? block.name : ''),
    className: styles$1.optionTwo
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$1.optionThree
  }, /*#__PURE__*/React.createElement(InstallStateTag, {
    state: sensor.install_state
  })), /*#__PURE__*/React.createElement("div", {
    className: styles$1.optionFour
  }, /*#__PURE__*/React.createElement(SensorTypeTag, {
    styles: {
      "float": 'right'
    },
    sensorType: sensor.type
  })))));
};

SensorOption.propTypes = {
  sensor: PropTypes.shape({
    name: PropTypes.string,
    identifier: PropTypes.string,
    ranch: PropTypes.number,
    block: PropTypes.number,
    type: PropTypes.string,
    install_state: PropTypes.string
  }).isRequired
};

var EntityOption = function EntityOption(_ref) {
  var entity = _ref.entity;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: styles['left-align']
  }, entity.name));
};

EntityOption.propTypes = {
  entity: PropTypes.shape({
    name: PropTypes.string
  }).isRequired
};

var Option$2 = Select.Option;
var loadFarmData$2 = actions.loadFarmData;
var selectAllEntities = selectors.selectAllEntities;

var entityTagRender = function entityTagRender(args) {
  var label = args.label,
      closable = args.closable,
      onClose = args.onClose;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: onClose,
    style: {
      marginRight: 3
    }
  }, label.props ? label.props.entity.name : label);
};

var defaultItemRenderer$2 = function defaultItemRenderer(d) {
  return /*#__PURE__*/React.createElement(Option$2, {
    key: d.id,
    value: d.id,
    label: d.name,
    "data-testid": "" + d.name
  }, /*#__PURE__*/React.createElement(EntityOption, {
    entity: d
  }));
};

var EntitySelect = function EntitySelect(_ref) {
  var loadItems = _ref.loadItems,
      items = _ref.items,
      mode = _ref.mode,
      renderItem = _ref.renderItem,
      onSelect = _ref.onSelect,
      onChange = _ref.onChange,
      tagRender = _ref.tagRender,
      placeholder = _ref.placeholder,
      defaultOption = _ref.defaultOption;
  var dispatch = useDispatch();
  var data = useSelector(selectAllEntities);
  var ranches = items || data;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var mobileScreen = window.innerWidth < 599;
  var dropdownStyle = null;
  if (mobileScreen) dropdownStyle = {
    minWidth: '60%'
  };
  var dropdownStyleValues = useContext(SensorSelectContext);
  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadFarmData$2());
  }, [dispatch, loadItems]);
  return /*#__PURE__*/React.createElement(ItemSelect, {
    mode: mode,
    renderItem: renderItem,
    items: ranches,
    onSelect: onSelect,
    onChange: onChange,
    tagRender: tagRender || entityTagRender,
    placeholder: placeholder,
    defaultOption: defaultOption,
    filterOption: function filterOption(input, option) {
      return option.children.props.entity.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.entity && option.children.props.entity.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
    dropdownStyle: dropdownStyle,
    onFocus: function onFocus() {
      return setModalState(true);
    },
    menuIsOpen: mobileScreen ? modal : undefined,
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "Entity Select",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        }
      });
    } : undefined,
    dropdownStyleValues: dropdownStyleValues
  });
};

EntitySelect.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  renderItem: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any)
};
EntitySelect.defaultProps = {
  mode: null,
  loadItems: null,
  items: null,
  renderItem: defaultItemRenderer$2,
  onSelect: null,
  onChange: null,
  tagRender: null,
  placeholder: null,
  defaultOption: []
};

var Option$3 = Select.Option;
function InstallStateSelect(props) {
  var selectedState = props.selectedState,
      installStates = props.installStates,
      onChange = props.onChange;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var mobileScreen = window.innerWidth < 599;
  var dropdownStyle = null;
  if (mobileScreen) dropdownStyle = {
    minWidth: '60%'
  };
  var dropdownStyleValues = useContext(SensorSelectContext);

  var getDropDownClass = function getDropDownClass(dropDownStyle) {
    var styleClassName;

    if (window.innerWidth > 599) {
      if (dropDownStyle && dropDownStyle.leftValue) {
        var createdStyles = StyleSheet.create({
          leftposition: {
            left: dropDownStyle.leftValue.current + " !important",
            width: dropDownStyle.minWidthValue.current + " !important"
          }
        });
        styleClassName = "" + css(createdStyles.leftposition);
      }
    }

    if (window.innerWidth < 599) {
      styleClassName = styles.selectDropDown + " ";
    }

    return styleClassName;
  };

  return /*#__PURE__*/React.createElement(Select, {
    disabled: installStates.length === 1 && selectedState,
    style: {
      width: '100%'
    },
    allowClear: true,
    value: selectedState,
    placeholder: t('Install State'),
    optionLabelProp: "label",
    dropdownMatchSelectWidth: false,
    onChange: onChange,
    onSelect: !mobileScreen ? function () {
      return setModalState(false);
    } : null,
    dropdownStyle: dropdownStyle,
    onFocus: function onFocus() {
      return setModalState(true);
    },
    open: mobileScreen ? modal : undefined,
    dropdownClassName: getDropDownClass(dropdownStyleValues),
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "InstallState Select",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        }
      });
    } : undefined
  }, installStates.map(function (value, i) {
    return /*#__PURE__*/React.createElement(Option$3, {
      key: String(i),
      label: value,
      value: value,
      "data-testid": "" + value
    }, value);
  }));
}
InstallStateSelect.propTypes = {
  selectedState: PropTypes.string,
  installStates: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func
};
InstallStateSelect.defaultProps = {
  selectedState: undefined,
  installStates: [],
  onChange: function onChange() {}
};

var Option$4 = Select.Option;
function SensorTypeSelect(props) {
  var selectedType = props.selectedType,
      sensorTypes = props.sensorTypes,
      onChange = props.onChange,
      disabled = props.disabled;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var mobileScreen = window.innerWidth < 599;
  var dropdownStyle = null;
  if (mobileScreen) dropdownStyle = {
    minWidth: '60%'
  };
  var dropdownStyleValues = useContext(SensorSelectContext);

  var getDropDownClass = function getDropDownClass(dropDownStyle) {
    var styleClassName;

    if (window.innerWidth > 599) {
      if (dropDownStyle && dropDownStyle.leftValue) {
        var createdStyles = StyleSheet.create({
          leftposition: {
            left: dropDownStyle.leftValue.current + " !important",
            width: dropDownStyle.minWidthValue.current + " !important"
          }
        });
        styleClassName = "" + css(createdStyles.leftposition);
      }
    }

    if (window.innerWidth < 599) {
      styleClassName = styles.selectDropDown + " ";
    }

    return styleClassName;
  };

  return /*#__PURE__*/React.createElement(Select, {
    disabled: sensorTypes.length === 1 && selectedType || disabled,
    style: {
      width: '100%'
    },
    allowClear: true,
    value: selectedType,
    placeholder: t('Type'),
    optionLabelProp: "label",
    dropdownMatchSelectWidth: false,
    onChange: onChange,
    onSelect: !mobileScreen ? function () {
      return setModalState(false);
    } : null,
    dropdownStyle: dropdownStyle,
    onFocus: function onFocus() {
      return setModalState(true);
    },
    open: mobileScreen ? modal : undefined,
    dropdownClassName: getDropDownClass(dropdownStyleValues),
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "SensorType Select",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        }
      });
    } : undefined
  }, sensorTypes.map(function (_ref) {
    var key = _ref[0],
        value = _ref[1];
    return /*#__PURE__*/React.createElement(Option$4, {
      key: key,
      label: value.name,
      "data-testid": "" + value.name
    }, value.name);
  }));
}
var sensorTypePropType = PropTypes.string;
SensorTypeSelect.propTypes = {
  selectedType: sensorTypePropType,
  sensorTypes: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func,
  disabled: PropTypes.bool
};
SensorTypeSelect.defaultProps = {
  selectedType: undefined,
  sensorTypes: [],
  onChange: function onChange() {},
  disabled: false
};

var OP = {
  OR: 'OR',
  AND: 'AND'
};
var INSTALL_STATE = {
  unknown: 'unknown',
  inventory: 'inventory',
  planned: 'planned',
  installed: 'installed',
  removed: 'removed',
  withdrawn: 'withdrawn',
  maintenance: 'maintenance',
  dead: 'dead'
};

var FilterListItem = function FilterListItem(_ref) {
  var onAdd = _ref.onAdd,
      onChange = _ref.onChange,
      changeIndex = _ref.changeIndex,
      onRemove = _ref.onRemove,
      type = _ref.type,
      typeFilter = _ref.typeFilter,
      value = _ref.value,
      op = _ref.op,
      noEdit = _ref.noEdit,
      noDelete = _ref.noDelete;

  var _useState = useState(null),
      selectType = _useState[0],
      setSelectType = _useState[1];

  var _useState2 = useState(null),
      selectVal = _useState2[0],
      setSelectVal = _useState2[1];

  var _useState3 = useState(OP.AND),
      opVal = _useState3[0],
      setOpVal = _useState3[1];

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState4 = useState(false),
      modal = _useState4[0],
      setModalState = _useState4[1];

  var mobileScreen = window.innerWidth < 599;

  var onSensorTypeOrStateSelect = function onSensorTypeOrStateSelect(val) {
    setSelectVal(val);

    if (onChange) {
      onChange({
        index: changeIndex,
        type: selectType,
        value: val,
        op: opVal
      });
    }
  };

  var getSensorTypes = function getSensorTypes() {
    var allTypes = Object.entries(constants.SENSOR_TYPES);
    if (typeFilter) return allTypes.filter(function (sType) {
      return typeFilter.indexOf(sType[0]) >= 0;
    });
    return allTypes;
  };

  var sensorTypes = getSensorTypes();
  var selectedType;

  if (sensorTypes.length === 1) {
    var selType = sensorTypes[0][0];
    selectedType = selType;
  } else if (value) selectedType = value;else if (selectVal) selectedType = selectVal;

  var installStates = Object.values(INSTALL_STATE);
  var selectedState;

  if (installStates.length === 1) {
    var _selType = installStates[0];
    selectedState = _selType;
  } else if (value) selectedState = value;else if (selectVal) selectedState = selectVal;

  useEffect(function () {
    if (type) setSelectType(type);
    if (value) setSelectVal(value);
    if (op) setOpVal(op);
  }, [type, value, op]);

  var onSelectType = function onSelectType(d) {
    setSelectType(d);
    if (!mobileScreen) setModalState(false);
  };

  var onSelectVal = function onSelectVal(val) {
    setSelectVal(val);
  };

  var onClickIcon = function onClickIcon() {
    setSelectType(undefined);
    setSelectVal(undefined);
    setOpVal(OP.AND);

    if (!value) {
      if (selectType && selectVal) onAdd({
        type: selectType,
        value: selectVal,
        op: opVal
      });
    } else onRemove({
      index: changeIndex
    });
  };

  var onChangeType = function onChangeType(d) {
    if (value) {
      onChange({
        index: changeIndex,
        type: d,
        value: selectVal,
        op: opVal
      });
    }
  };

  var onChangeVal = function onChangeVal(val) {
    onChange({
      index: changeIndex,
      type: selectType,
      value: val,
      op: opVal
    });
  };

  var onChangeOp = function onChangeOp(e) {
    var opValue = OP.AND;

    if (e.target.textContent === OP.AND) {
      opValue = OP.OR;
      setOpVal(OP.OR);
    } else setOpVal(OP.AND);

    onChange({
      index: changeIndex,
      type: selectType,
      value: selectVal,
      op: opValue
    });
  };

  var displayOp = function displayOp() {
    return /*#__PURE__*/React.createElement("div", {
      "data-testid": (opVal || op) + "_" + (value || selectVal)
    }, op && /*#__PURE__*/React.createElement(Button, {
      disabled: noEdit,
      className: styles$1.optionOrButton,
      onClick: onChangeOp
    }, opVal || op));
  };

  var filterbuttonstyle = {
    flex: 'none',
    display: 'block',
    color: value ? 'red' : '#40a9ff'
  };
  var buttonstyle = {
    flex: 'none',
    padding: '8px',
    display: 'block',
    cursor: 'pointer',
    color: value ? 'red' : '#40a9ff'
  };
  var addbuttonstyle = {
    borderWidth: '2px',
    fontSize: '12px',
    borderRadius: '6px',
    borderStyle: 'solid',
    height: 'auto',
    "float": 'right',
    color: 'red'
  };
  var selectFilter = {
    background: value ? 'darkgray' : 'lightgray'
  };

  function getComponentForType(selectedFilterType) {
    switch (selectedFilterType) {
      case 'ranch':
        return /*#__PURE__*/React.createElement(RanchSelect, {
          onSelect: onSelectVal,
          onChange: onChangeVal,
          defaultOption: [value],
          placeholder: "Select Ranch"
        });

      case 'block':
        return /*#__PURE__*/React.createElement(BlockSelect, {
          onSelect: onSelectVal,
          onChange: onChangeVal,
          defaultOption: [value],
          placeholder: "Select Block"
        });

      case 'entity':
        return /*#__PURE__*/React.createElement(EntitySelect, {
          onSelect: onSelectVal,
          onChange: onChangeVal,
          defaultOption: [value],
          placeholder: "Select Entity"
        });

      case 'install_state':
        return /*#__PURE__*/React.createElement(InstallStateSelect, {
          onChange: onSensorTypeOrStateSelect,
          selectedState: selectedState,
          installStates: installStates
        });

      case 'type':
        return /*#__PURE__*/React.createElement(SensorTypeSelect, {
          disabled: noEdit,
          onChange: onSensorTypeOrStateSelect,
          selectedType: selectedType,
          sensorTypes: sensorTypes
        });

      default:
        return /*#__PURE__*/React.createElement(Select, {
          style: {
            width: '100%'
          },
          options: [],
          placeholder: "--Select--"
        });
    }
  }

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles$1.selectFilter,
    style: selectFilter
  }, /*#__PURE__*/React.createElement("table", {
    className: styles$1.filterOptions
  }, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(FilterOutlined, {
    className: styles$1.filterIcon,
    style: filterbuttonstyle
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Select, {
    disabled: noEdit,
    className: styles$1.selectFilterDropdown,
    onSelect: onSelectType,
    onChange: onChangeType,
    placeholder: t('Select type'),
    defaultValue: type || selectType,
    value: type || selectType,
    dropdownStyle: {
      minWidth: '100px'
    },
    options: [{
      label: 'ranch',
      value: 'ranch'
    }, {
      label: 'block',
      value: 'block'
    }, {
      label: 'entity',
      value: 'entity'
    }, {
      label: 'install state',
      value: 'install_state'
    }, {
      label: 'sensor type',
      value: 'type'
    }],
    onFocus: function onFocus() {
      return setModalState(true);
    },
    open: mobileScreen ? modal : undefined,
    dropdownClassName: styles.selectDropDown,
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "Select Type",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        }
      });
    } : undefined
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
    className: styles$1.itemSelect
  }, getComponentForType(selectType))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
    className: styles$1.optionButton
  }, displayOp())), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
    className: styles$1.beforeAddButton
  }, value || type ? !noDelete && /*#__PURE__*/React.createElement("div", {
    className: styles$1.closebuttonstyle,
    style: addbuttonstyle
  }, /*#__PURE__*/React.createElement(CloseOutlined, {
    style: buttonstyle,
    onClick: onClickIcon,
    "data-testid": (value || selectVal) + "_CloseIcon"
  })) : /*#__PURE__*/React.createElement("div", {
    className: styles$1.addbuttonstyle
  }, /*#__PURE__*/React.createElement(PlusOutlined, {
    style: buttonstyle,
    onClick: onClickIcon
  })))))))));
};

FilterListItem.propTypes = {
  onAdd: PropTypes.func,
  onChange: PropTypes.func,
  changeIndex: PropTypes.number,
  onRemove: PropTypes.func,
  type: PropTypes.string,
  typeFilter: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  op: PropTypes.string,
  noEdit: PropTypes.bool,
  noDelete: PropTypes.bool
};
FilterListItem.defaultProps = {
  onAdd: null,
  onChange: null,
  changeIndex: null,
  onRemove: null,
  type: null,
  typeFilter: null,
  value: null,
  op: null,
  noEdit: null,
  noDelete: null
};

var Option$5 = Select.Option;
var loadAllSensors = actions.loadAllSensors,
    loadFarmData$3 = actions.loadFarmData;
var selectAllSensors = selectors.selectAllSensors,
    selectLoadingSensors = selectors.selectLoadingSensors,
    selectSensor = selectors.selectSensor,
    selectSensorById = selectors.selectSensorById;

var sensorTagRender = function sensorTagRender(args) {
  var closable = args.closable,
      onClose = args.onClose,
      label = args.label;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: onClose,
    style: {
      marginRight: 3
    }
  }, label.props ? label.props.sensor.name : label);
};

var defaultItemRenderer$3 = function defaultItemRenderer(d) {
  var valueObject = JSON.stringify({
    type: d.type,
    identifier: d.identifier,
    id: d.id
  });

  if (!d.count) {
    return /*#__PURE__*/React.createElement(Option$5, {
      key: d.identifier + " " + d.name,
      value: valueObject,
      label: d.name ? d.name : 'No Name',
      "data-testid": "" + d.name
    }, /*#__PURE__*/React.createElement(SensorOption, {
      sensor: d
    }));
  }

  if (d.count) {
    return /*#__PURE__*/React.createElement(Option$5, {
      key: "" + d.name,
      label: d.name,
      "data-testid": "" + d.name,
      disabled: true
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        textAlign: 'center',
        color: 'rgba(0, 0, 0, 0.45)'
      }
    }, d.name));
  }

  return null;
};

var SensorSelect = function SensorSelect(_ref) {
  var loadItems = _ref.loadItems,
      items = _ref.items,
      mode = _ref.mode,
      renderItem = _ref.renderItem,
      onSelect = _ref.onSelect,
      onChange = _ref.onChange,
      tagRender = _ref.tagRender,
      placeholder = _ref.placeholder,
      defaultOption = _ref.defaultOption,
      dropdownStyle = _ref.dropdownStyle,
      sensorTypeConstraint = _ref.sensorTypeConstraint,
      sensorType = _ref.sensorType,
      sensorIdentifier = _ref.sensorIdentifier,
      sensorId = _ref.sensorId;
  var dispatch = useDispatch();
  var data = useSelector(selectAllSensors);
  var sensors = items || data;

  var _useState = useState([]),
      filterItemList = _useState[0],
      setFilterItemList = _useState[1];

  var _useState2 = useState(false),
      modal = _useState2[0],
      setModalState = _useState2[1];

  var mobileScreen = window.innerWidth < 599;
  var leftValue = useRef(null);
  var minWidthValue = useRef(null);
  var sensorsByPropValues = useSelector(function (state) {
    return selectSensor(state, sensorType, sensorIdentifier);
  });
  var sensorsByPropId = useSelector(function (state) {
    return selectSensorById(state, sensorType, sensorId);
  });
  var sensorLoading = useSelector(function (state) {
    return selectLoadingSensors(state);
  });
  useEffect(function () {
    if (sensorType) {
      var obj = {
        type: 'type',
        value: sensorType,
        op: 'AND'
      };
      setFilterItemList([obj]);
    }
  }, [sensorType]);
  useEffect(function () {
    if (sensorTypeConstraint && sensorTypeConstraint.length > 0) {
      var objList = sensorTypeConstraint.map(function (d) {
        return {
          type: 'type',
          value: d,
          op: 'OR',
          noEdit: true,
          noDelete: true
        };
      });
      setFilterItemList([].concat(objList));
    }
  }, [sensorTypeConstraint]);
  var sensorValue;

  if (sensorsByPropValues && !sensorLoading.loading) {
    sensorValue = {
      type: sensorsByPropValues.type,
      identifier: sensorsByPropValues.identifier,
      id: sensorsByPropValues.id
    };
  } else if (sensorsByPropId && !sensorLoading.loading) {
    sensorValue = {
      type: sensorsByPropId.type,
      identifier: sensorsByPropId.identifier,
      id: sensorsByPropId.id
    };
  } else if (sensorId && !sensorValue && sensorType && sensorIdentifier && !sensorLoading.loading) {
    sensorValue = {
      type: sensorType,
      identifier: sensorIdentifier,
      id: sensorId
    };
  }

  useEffect(function () {
    var divElement = document.querySelector('#sensorSelect');

    if (divElement !== null && !leftValue.current) {
      setTimeout(function () {
        var declare = divElement.parentNode.parentNode.style;

        if (!declare.left.includes('-') && declare.left !== null) {
          leftValue.current = declare.left;
          minWidthValue.current = declare.minWidth;
        }
      }, 1000);
    }
  });
  var filteredSensorData = sensors.filter(function (sensor) {
    if (sensorTypeConstraint && sensorTypeConstraint.length > 0) {
      return sensorTypeConstraint.indexOf(sensor.type) >= 0;
    }

    return true;
  }).filter(function (sensor) {
    return filterItemList.reduce(function (prev, curr) {
      if (curr.op === OP.OR) {
        return prev || sensor[curr.type] === curr.value;
      }

      return prev && sensor[curr.type] === curr.value;
    }, true);
  });
  var countOfFilteredSensorData = [{
    name: filteredSensorData.length + " results",
    count: true
  }];
  var sensorData = [].concat(countOfFilteredSensorData, filteredSensorData);

  var dropdownRender = function dropdownRender(menu) {
    return /*#__PURE__*/React.createElement("div", {
      id: "sensorSelect",
      className: styles$1.sensorSelectOptions,
      style: {
        height: mobileScreen ? 'calc(100vh - 10px)' : 'auto'
      }
    }, mobileScreen ? /*#__PURE__*/React.createElement("div", {
      className: styles$1.modalSelectHeader
    }, /*#__PURE__*/React.createElement(Button, {
      className: styles$1.goBack,
      id: "goBack",
      onClick: function onClick() {
        return setModalState(false);
      }
    }, /*#__PURE__*/React.createElement(ArrowLeftOutlined, {
      id: "goBack",
      onClick: function onClick() {
        return setModalState(false);
      }
    })), /*#__PURE__*/React.createElement("div", {
      className: styles$1.modalSelectHeaderText
    }, "Sensor Select"), /*#__PURE__*/React.createElement("div", null)) : null, /*#__PURE__*/React.createElement("div", {
      className: styles$1.filterOuterDiv,
      style: {
        overflowY: filterItemList.length > 2 ? 'auto' : 'none'
      },
      "data-testid": "scrollableFilterOptionDiv"
    }, filterItemList.map(function (item, index) {
      return /*#__PURE__*/React.createElement(FilterListItem, {
        key: String(index),
        type: item.type,
        value: item.value,
        onRemove: function onRemove(obj) {
          filterItemList.splice(obj.index, 1);
          setFilterItemList([].concat(filterItemList));
        },
        onChange: function onChange(obj) {
          filterItemList[obj.index] = {
            type: obj.type,
            value: obj.value,
            op: obj.op
          };
          setFilterItemList([].concat(filterItemList));
        },
        changeIndex: index,
        op: index !== 0 && item.op ? item.op : '',
        typeFilter: sensorTypeConstraint,
        noEdit: item.noEdit,
        noDelete: item.noDelete
      });
    }), /*#__PURE__*/React.createElement(FilterListItem, {
      onAdd: function onAdd(obj) {
        setFilterItemList([].concat(filterItemList, [obj]));
      },
      onChange: function onChange(obj) {
        filterItemList[obj.index] = {
          type: obj.type,
          value: obj.value,
          op: obj.op
        };
        setFilterItemList([].concat(filterItemList));
      },
      op: filterItemList.length > 0 ? OP.AND : '',
      typeFilter: sensorTypeConstraint
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: '5px'
      }
    }, menu));
  };

  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadAllSensors());
  }, [loadItems, dispatch]);
  useEffect(function () {
    dispatch(loadFarmData$3());
  }, [dispatch]);

  function onSelectAction(d) {
    if (onSelect) onSelect(d);
    if (!mobileScreen) setModalState(false);
  }

  var onChangeAction = function onChangeAction(d) {
    var multipleMode = mode === 'multiple';

    if (onChange && d && !multipleMode) {
      onChange(JSON.parse(d));
    } else if (onChange && d && multipleMode) {
      onChange(d.map(function (e) {
        return JSON.parse(e);
      }));
    } else if (!d) onChange({
      id: undefined,
      type: undefined,
      identifier: undefined
    });
  };

  var customFilter = function customFilter(input, option) {
    if (option.children.props.sensor && option.children.props.sensor.name && option.children.props.sensor.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.sensor && option.children.props.sensor.type.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.sensor && option.children.props.sensor.identifier && option.children.props.sensor.identifier.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.sensor && option.children.props.sensor.install_state && option.children.props.sensor.install_state.toLowerCase().indexOf(input.toLowerCase()) >= 0) {
      return true;
    }

    return false;
  };

  return /*#__PURE__*/React.createElement(SensorSelectContext.Provider, {
    value: {
      leftValue: leftValue,
      minWidthValue: minWidthValue
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$1.sensorSelectOuter
  }, /*#__PURE__*/React.createElement(ItemSelect, {
    mode: mode,
    renderItem: renderItem,
    items: sensorData,
    onSelect: onSelectAction,
    onChange: onChangeAction,
    onFocus: function onFocus() {
      return setModalState(true);
    },
    tagRender: tagRender || sensorTagRender,
    placeholder: placeholder,
    filterOption: function filterOption(input, option) {
      return customFilter(input, option);
    },
    dropdownRender: dropdownRender,
    defaultOption: defaultOption,
    value: sensorValue ? [JSON.stringify(sensorValue)] : null,
    dropdownStyle: mobileScreen ? undefined : dropdownStyle || {
      minWidth: '600px'
    },
    menuIsOpen: mobileScreen ? modal : undefined,
    loading: sensorLoading.loading
  })));
};

SensorSelect.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  renderItem: PropTypes.func,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  dropdownStyle: PropTypes.shape({}),
  sensorTypeConstraint: PropTypes.arrayOf(PropTypes.String),
  sensorId: PropTypes.number,
  sensorIdentifier: PropTypes.string,
  sensorType: PropTypes.string
};
SensorSelect.defaultProps = {
  mode: null,
  loadItems: null,
  items: null,
  renderItem: defaultItemRenderer$3,
  onSelect: null,
  onChange: null,
  tagRender: null,
  placeholder: null,
  defaultOption: [],
  dropdownStyle: null,
  sensorTypeConstraint: null,
  sensorId: null,
  sensorIdentifier: null,
  sensorType: null
};

var getColorForValue = function getColorForValue(value) {
  if (value) return 'green';
  return 'red';
};

function BooleanTag(_ref) {
  var value = _ref.value;
  if (value === undefined || value === null) return null;
  return /*#__PURE__*/React.createElement(Tag, {
    color: getColorForValue(value)
  }, value ? 'yes' : 'no');
}

BooleanTag.propTypes = {
  value: PropTypes.bool
};
BooleanTag.defaultProps = {
  value: undefined
};

var styles$2 = {"custom-table":"_3Nr0O","configuration-message":"_3Eac4","scheduleItemHeader":"_fty8W","sensorHeader":"_1UuGt","stateHeader":"_oczA3","alarmsHeader":"_3M3aF","listContainer":"_3tmMU","anticon":"_AE10G","warningIcon":"_1z515","indicator":"_3yEkF","scheduleHeader":"_3fPOJ","blue":"_3P7nF","red":"_2VVYh","lightgrey":"_3FxuP","red-border-text":"_3dTLR","tagIcon":"_3v141","tagIconOverride":"_2XLqz","iconContainer":"_9UDtg","redMessage":"_s7wqN","scheduleContent":"_3V6X_","normalMessage":"_3h9Q8","icon":"_2ntFS","switch":"_353UI","switchLeft":"_3KdNQ","switchRight":"_2IR-C","bellIconButton":"_H43va","bellIconSize":"_34X0y","status":"_1-1Rp","overrideHeader":"_2AOEJ","anticon-warning":"_cyrUB"};

var ConfirmValveOverrideDialog = function ConfirmValveOverrideDialog(_ref) {
  var title = _ref.title,
      displayText = _ref.displayText,
      buttonText = _ref.buttonText,
      onClick = _ref.onClick;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(true),
      modal = _useState[0],
      setModalState = _useState[1];

  function callOnClick(d) {
    if (d) setModalState(false);
    onClick(d.target.id);
  }

  return /*#__PURE__*/React.createElement("div", null, modal ? /*#__PURE__*/React.createElement(Modal, {
    title: t(title),
    centered: true,
    visible: modal,
    onCancel: callOnClick,
    footer: [/*#__PURE__*/React.createElement(Button, {
      key: "cancel",
      onClick: callOnClick
    }, t('Cancel')), /*#__PURE__*/React.createElement(Button, {
      id: buttonText[0],
      key: buttonText[0],
      value: buttonText[0],
      onClick: callOnClick,
      type: "primary",
      style: {
        background: 'red',
        borderColor: 'red'
      }
    }, /*#__PURE__*/React.createElement("span", {
      id: buttonText[0]
    }, t(buttonText[0]))), /*#__PURE__*/React.createElement(Button, {
      id: buttonText[1],
      key: buttonText[1],
      value: buttonText[1],
      onClick: callOnClick,
      type: "primary"
    }, /*#__PURE__*/React.createElement("span", {
      id: buttonText[1]
    }, t(buttonText[1])))],
    closable: true,
    maskClosable: false,
    destroyOnClose: true
  }, displayText.map(function (d, i) {
    return /*#__PURE__*/React.createElement("p", {
      key: String(i)
    }, t(d));
  })) : null);
};

ConfirmValveOverrideDialog.propTypes = {
  title: PropTypes.string,
  displayText: PropTypes.arrayOf(PropTypes.string),
  buttonText: PropTypes.arrayOf(PropTypes.string),
  onClick: PropTypes.func
};
ConfirmValveOverrideDialog.defaultProps = {
  title: null,
  displayText: null,
  buttonText: null,
  onClick: function onClick() {
    return null;
  }
};

var RefreshContext = React.createContext(3);

var VALVE_TYPE = 'valve';
var VFD_TYPE = 'vfd';
var loadSensorStatus = actions.loadSensorStatus;
var createOverride = controlApi.createOverride,
    endOverride = controlApi.endOverride;
function OverrideStatus(_ref) {
  var type = _ref.type,
      overrideStatus = _ref.overrideStatus,
      valveIdentifier = _ref.valveIdentifier,
      vfdIdentifier = _ref.vfdIdentifier,
      confirmDialogMessages = _ref.confirmDialogMessages,
      loading = _ref.loading,
      currentState = _ref.currentState;
  var dispatch = useDispatch();

  var _useState = useState(false),
      toggleChanged = _useState[0],
      setToggleChanged = _useState[1];

  var HTTP_SUCCESS_RES_CODE = 200;
  var OVERRIDE_STATE_CHECK_INTERVAL = 5000;
  var OVERRIDE_STATE_CHECK_TIMEOUT = 15000;
  var refreshStatusInterval = useRef(null);
  var valveOverrideSyncState = useRef('pending');
  var refreshId = useContext(RefreshContext);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var OVERRIDE_CONFIRM_DIALOG_TITLE = type === VALVE_TYPE ? 'Confirm Valve Override' : 'Confirm Pump Override';
  var buttonText = type === VALVE_TYPE ? ['CLOSE', 'OPEN'] : ['OFF', 'ON'];
  useEffect(function () {
    if (refreshId && valveIdentifier) {
      dispatch(loadSensorStatus({
        type: VALVE_TYPE,
        identifier: valveIdentifier
      }));
    }

    if (refreshId && vfdIdentifier) {
      dispatch(loadSensorStatus({
        type: VFD_TYPE,
        identifier: vfdIdentifier
      }));
    }
  }, [dispatch, valveIdentifier, vfdIdentifier, refreshId]);

  function refreshSensorStatus(param) {
    dispatch(loadSensorStatus(param));
  }

  function clearRefreshInterval() {
    if (refreshStatusInterval.current) {
      clearInterval(refreshStatusInterval.current);
      refreshStatusInterval.current = null;
    }
  }

  function refreshUntilSynced() {
    refreshStatusInterval.current = setInterval(function () {
      if (valveOverrideSyncState.current !== 'pending') {
        clearRefreshInterval();
      } else {
        if (type === VALVE_TYPE) {
          refreshSensorStatus({
            type: type,
            identifier: valveIdentifier
          });
        }

        if (type === VFD_TYPE) {
          refreshSensorStatus({
            type: type,
            identifier: vfdIdentifier
          });
        }
      }
    }, OVERRIDE_STATE_CHECK_INTERVAL);
    setTimeout(clearRefreshInterval, OVERRIDE_STATE_CHECK_TIMEOUT);
  }

  var onConfirmValveOverride = function onConfirmValveOverride(overOp) {
    if (overOp === 'OPEN' || overOp === 'CLOSE' || overOp === 'OFF' || overOp === 'ON') {
      var newState;
      var paramObj;

      if (type === VALVE_TYPE) {
        newState = overOp === 'OPEN' ? 'open' : 'closed';
        paramObj = {
          sensorType: VALVE_TYPE,
          sensorIdentifier: valveIdentifier,
          state: newState
        };
      } else if (type === VFD_TYPE) {
        newState = overOp === 'ON' ? 'on' : 'off';
        paramObj = {
          sensorType: VFD_TYPE,
          sensorIdentifier: vfdIdentifier,
          state: newState
        };
      }

      if (paramObj) {
        var response = createOverride({
          sensorType: type === VALVE_TYPE ? VALVE_TYPE : VFD_TYPE,
          sensorIdentifier: type === VALVE_TYPE ? valveIdentifier : vfdIdentifier,
          state: newState
        });
        response.then(function (res) {
          if (res.status === HTTP_SUCCESS_RES_CODE) {
            if (type === VALVE_TYPE) {
              refreshSensorStatus({
                type: VALVE_TYPE,
                identifier: valveIdentifier
              });
            } else {
              refreshSensorStatus({
                type: VFD_TYPE,
                identifier: vfdIdentifier
              });
            }

            message.success(t('Created override'));
            refreshUntilSynced();
            setToggleChanged(false);
          } else {
            message.error(t('Failed to create override'));
          }
        });
      }
    } else {
      setToggleChanged(false);
    }
  };

  var onChangeToggle = function onChangeToggle(checked) {
    if (!checked) {
      var paramObj;

      if (type === VALVE_TYPE) {
        paramObj = {
          sensorType: VALVE_TYPE,
          sensorIdentifier: valveIdentifier
        };
      } else if (type === VFD_TYPE) {
        paramObj = {
          sensorType: VFD_TYPE,
          sensorIdentifier: vfdIdentifier
        };
      }

      if (paramObj) {
        var response = endOverride(paramObj);
        response.then(function (res) {
          if (res.status === HTTP_SUCCESS_RES_CODE) {
            if (type === VALVE_TYPE) {
              refreshSensorStatus({
                type: VALVE_TYPE,
                identifier: valveIdentifier
              });
              message.success(t('Ended valve override'));
            } else {
              refreshSensorStatus({
                type: VFD_TYPE,
                identifier: vfdIdentifier
              });
              message.success(t('Ended pump override'));
            }

            refreshUntilSynced();
          } else if (type === VALVE_TYPE) {
            message.error(t('Failed to end valve override'));
          } else {
            message.error(t('Failed to end pump override'));
          }
        });
      }
    } else {
      setToggleChanged(true);
    }
  };

  var overrideActive = !!overrideStatus;

  if (overrideActive) {
    valveOverrideSyncState.current = overrideStatus.syncStatus;
  }

  function getOverrideStateColor(overrideStatusObj) {
    if (overrideStatusObj && overrideStatusObj.state) {
      return 'green';
    }

    return null;
  }

  function getValveOverrideStateTitle(overrideStatusObj) {
    if (overrideStatusObj && overrideStatusObj.state) {
      return overrideStatusObj.state.toUpperCase();
    }

    return 'unknown';
  }

  function getSyncStatusIcon(overrideStatusObj) {
    var icons;
    if (!overrideStatus || !overrideStatus.syncStatus) icons = null;

    if (overrideStatusObj) {
      if (overrideStatusObj.syncStatus === 'success') icons = /*#__PURE__*/React.createElement(CheckOutlined, null);
      if (overrideStatusObj.syncStatus === 'pending') icons = /*#__PURE__*/React.createElement(LoadingOutlined, null);
      if (overrideStatusObj.syncStatus === 'error') icons = /*#__PURE__*/React.createElement(WarningFilled, null);
      if (overrideStatusObj.syncStatus === 'aborted') icons = /*#__PURE__*/React.createElement(WarningFilled, null);
    }

    return icons;
  }

  function getControlColumn() {
    if (loading) return /*#__PURE__*/React.createElement(LoadingOutlined, {
      className: styles$2.tagIcon
    });

    if (currentState && !loading) {
      return /*#__PURE__*/React.createElement(Switch, {
        style: {
          background: overrideActive ? getOverrideStateColor(overrideStatus) : null,
          width: 'auto'
        },
        checkedChildren: t(getValveOverrideStateTitle(overrideStatus)),
        unCheckedChildren: t('NONE'),
        "data-testid": valveIdentifier || vfdIdentifier,
        checked: overrideActive,
        onChange: onChangeToggle
      });
    }

    if (currentState === 'unknown' || !currentState) {
      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2.tagIconOverride
      });
    }

    return /*#__PURE__*/React.createElement(LoadingOutlined, {
      className: styles$2.tagIcon
    });
  }

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2["switch"]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$2.switchLeft
  }, getControlColumn()), /*#__PURE__*/React.createElement("div", {
    className: styles$2.switchRight
  }, getSyncStatusIcon(overrideStatus)), /*#__PURE__*/React.createElement("div", null), toggleChanged ? /*#__PURE__*/React.createElement(ConfirmValveOverrideDialog, {
    title: OVERRIDE_CONFIRM_DIALOG_TITLE,
    displayText: confirmDialogMessages,
    buttonText: buttonText,
    onClick: onConfirmValveOverride
  }) : '');
}
OverrideStatus.propTypes = {
  type: PropTypes.string,
  overrideStatus: PropTypes.shape({
    state: PropTypes.string,
    syncStatus: PropTypes.string
  }),
  valveIdentifier: PropTypes.string,
  vfdIdentifier: PropTypes.string,
  confirmDialogMessages: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  currentState: PropTypes.string
};
OverrideStatus.defaultProps = {
  type: null,
  overrideStatus: null,
  valveIdentifier: null,
  vfdIdentifier: null,
  confirmDialogMessages: [],
  loading: null,
  currentState: null
};

var Panel = Collapse.Panel;
var selectPumpSensorStatus = selectors.selectPumpSensorStatus;
function PumpControlList(_ref) {
  var pumps = _ref.pumps,
      onClickCalendar = _ref.onClickCalendar,
      pumpLoading = _ref.pumpLoading;
  var pumpData = useSelector(function (state) {
    return selectPumpSensorStatus(state, pumps);
  });

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  function getTimeDiff(startdate, enddate) {
    var min = enddate.diff(startdate, 'minutes');
    var sec = enddate.diff(startdate, 'seconds');

    if (min < 0 || sec < 0) {
      return null;
    }

    if (min > 59) {
      var hr = Math.floor(min / 60);
      var m = min % 60;
      return hr + " hrs and " + m + " mins";
    }

    if (sec > 59) {
      return min + " mins";
    }

    return sec + " secs";
  }

  function getTimingAttr(openTime, closeTime) {
    var timeOpen;
    var timeClose;
    var opensFirst;

    if (openTime) {
      timeOpen = getTimeDiff(moment(), moment(openTime));
    }

    if (closeTime) {
      timeClose = getTimeDiff(moment(), moment(closeTime));
    }

    if (openTime && closeTime) {
      opensFirst = !!getTimeDiff(moment(openTime), moment(closeTime));
    }

    return [timeOpen, timeClose, opensFirst];
  }

  var getPumpScheduleMessage = function getPumpScheduleMessage(online, state, startDate, stopDate, loading) {
    var _getTimingAttr = getTimingAttr(startDate, stopDate),
        timeOn = _getTimingAttr[0],
        timeOff = _getTimingAttr[1],
        onFirst = _getTimingAttr[2];

    if (state) {
      if (timeOn && onFirst) {
        return t("On in " + timeOn + ".");
      }

      if (timeOff && !onFirst) {
        return t("Off in " + timeOff + ".");
      }

      if (state === 'unknown') {
        return t('Disconnected');
      }

      if (!online && state === 'off') {
        return t('Disconnected (off)');
      }

      return t('Nothing scheduled');
    }

    if (loading) {
      return /*#__PURE__*/React.createElement(LoadingOutlined, {
        className: styles$2.tagIcon
      });
    }

    return t('schedule could not be loaded');
  };

  var getMessageClassName = function getMessageClassName(state, online) {
    var styleStr = styles$2.redMessage + " ";
    var styleStrNot = styles$2.normalMessage + " ";

    if (state === 'unknown' || !online && state === 'off') {
      return styleStr;
    }

    if (!state) return styleStr;
    return styleStrNot;
  };

  var getSchedule = function getSchedule(pump, text) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles$2.iconContainer,
      onClick: function onClick() {
        return onClickCalendar({
          identifier: pump.pumpIdentifier
        });
      },
      role: "presentation"
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$2.icon
    }, /*#__PURE__*/React.createElement(FaCalendarAlt, null)), /*#__PURE__*/React.createElement("div", {
      className: getMessageClassName(pump.pumpCurrentState, pump.pumpOnline)
    }, /*#__PURE__*/React.createElement("span", {
      className: styles$2.scheduleContent
    }, text)));
  };

  var getIndClass = function getIndClass(_ref2) {
    var online = _ref2.pumpOnline,
        status = _ref2.pumpCurrentState,
        loading = _ref2.pumpLoading;
    var styleStr = styles$2.indicator + " ";

    if (!loading) {
      if (!online) return styleStr + styles$2.red;
      if (online && status === 'off') return styleStr + styles$2.lightgrey;
      if (online && status === 'on') return styleStr + styles$2.blue;
    }

    return styleStr + styles$2.lightgrey;
  };

  var getCurrentState = function getCurrentState(pump) {
    var color = pump.pumpCurrentState === 'on' ? '#1890FF' : '#D40202';

    if (pump.pumpLoading) {
      return /*#__PURE__*/React.createElement(LoadingOutlined, {
        className: styles$2.tagIcon
      });
    }

    if (pump.pumpCurrentState) {
      if (pump.pumpCurrentState !== 'unknown') {
        if (pump.pumpOnline && pump.pumpCurrentState === 'off') {
          return /*#__PURE__*/React.createElement(Tag, {
            color: "",
            key: pump.pumpCurrentState,
            className: styles$2.tagIcon
          }, pump.pumpCurrentState.toUpperCase());
        }

        return /*#__PURE__*/React.createElement(Tag, {
          color: color,
          key: pump.pumpCurrentState,
          className: styles$2.tagIcon
        }, pump.pumpCurrentState.toUpperCase());
      }

      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2.warningIcon
      });
    }

    if (!pump.pumpCurrentState && !pump.pumpLoading) {
      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2.warningIcon
      });
    }

    return /*#__PURE__*/React.createElement(LoadingOutlined, {
      className: styles$2.tagIcon
    });
  };

  var getRowClassName = function getRowClassName(record) {
    if (record.pumpCurrentState) {
      if (record.pumpCurrentState === 'unknown' || !record.pumpOnline && (!record.pumpCurrentState || record.pumpCurrentState === 'off')) {
        return styles$2['red-border-text'];
      }
    }

    var className = getIndClass(record);
    return className;
  };

  function widthCalc(columns) {
    var maxwid = 100;
    var result = columns().map(function (element) {
      var values = element;

      if (element.title === 'Alarm') {
        values.width = 100;
      }

      if (element.title === 'Override') {
        values.width = 100;
      }

      if (element.title !== 'Override' && element.title !== 'Alarm') {
        values.width = (maxwid - 10) / (columns().length - 2) + "%";
      }

      return values;
    });
    return result;
  }

  var columns = function columns() {
    return [{
      title: 'Pump',
      className: styles$2.scheduleItemHeader,
      render: function render(pump) {
        if (pump.pumpName) {
          return /*#__PURE__*/React.createElement(Tooltip, {
            title: pump.pumpName
          }, /*#__PURE__*/React.createElement("span", null, pump.pumpName));
        }

        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: 'lightgray'
          }
        }, t('No Name'));
      }
    }, {
      title: 'Schedule',
      className: styles$2.scheduleHeader,
      render: function render(pump) {
        return getSchedule(pump, getPumpScheduleMessage(pump.pumpOnline, pump.pumpCurrentState, pump.pumpStartDate, pump.pumpStopDate, pump.pumpLoading));
      }
    }, {
      title: 'State',
      className: styles$2.stateHeader,
      render: function render(pump) {
        return /*#__PURE__*/React.createElement("div", {
          className: styles$2.status
        }, getCurrentState(pump));
      }
    }, {
      title: 'Override',
      className: styles$2.overrideHeader,
      render: function render(pump) {
        return /*#__PURE__*/React.createElement(OverrideStatus, {
          type: VFD_TYPE,
          overrideStatus: pump.pumpOverrideStatus,
          vfdIdentifier: pump.pumpIdentifier,
          loading: pump.pumpLoading,
          currentState: pump.pumpCurrentState,
          confirmDialogMessages: [getPumpScheduleMessage(pump.pumpOnline, pump.pumpCurrentState, pump.pumpStartDate, pump.pumpStopDate)]
        });
      }
    }, {
      title: 'Alarm',
      className: styles$2.alarmsHeader,
      render: function render() {
        return /*#__PURE__*/React.createElement(Button, {
          className: styles$2.bellIconButton,
          icon: /*#__PURE__*/React.createElement(BellFilled, {
            className: styles$2.bellIconSize
          }),
          disabled: true
        });
      }
    }];
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2.listContainer
  }, /*#__PURE__*/React.createElement(Collapse, {
    expandIconPosition: "right",
    defaultActiveKey: ['1']
  }, /*#__PURE__*/React.createElement(Panel, {
    header: "Pumps",
    key: "1",
    className: styles$2.collapseHeader
  }, pumpLoading || pumpData.length ? /*#__PURE__*/React.createElement(Table, {
    className: styles$2['custom-table'],
    rowClassName: getRowClassName,
    dataSource: pumpData,
    columns: widthCalc(columns),
    pagination: false,
    loading: pumpLoading,
    rowKey: function rowKey(record) {
      return String(record.pumpId);
    }
  }) : /*#__PURE__*/React.createElement("section", {
    className: styles$2['configuration-message']
  }, "No Pumps Configured"))));
}
PumpControlList.propTypes = {
  pumps: PropTypes.arrayOf(PropTypes.object),
  onClickCalendar: PropTypes.func,
  pumpLoading: PropTypes.bool
};
PumpControlList.defaultProps = {
  pumps: [],
  onClickCalendar: null,
  pumpLoading: null
};

var Panel$1 = Collapse.Panel;
var selectBlockSensorStatus = selectors.selectBlockSensorStatus;
function BlockControlList(_ref) {
  var blocks = _ref.blocks,
      onClickCalendar = _ref.onClickCalendar,
      blockLoading = _ref.blockLoading;
  var filteredBlocks = blocks ? blocks.filter(function (d) {
    return d.valve;
  }) : null;
  var blockData = useSelector(function (state) {
    return selectBlockSensorStatus(state, filteredBlocks);
  });

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  function getTimeDiff(startdate, enddate) {
    var min = enddate.diff(startdate, 'minutes');
    var sec = enddate.diff(startdate, 'seconds');

    if (min < 0 || sec < 0) {
      return null;
    }

    if (min > 59) {
      var hr = Math.floor(min / 60);
      var m = min % 60;
      return hr + " hrs and " + m + " mins";
    }

    if (sec > 59) {
      return min + " mins";
    }

    return sec + " secs";
  }

  function getTimingAttr(openTime, closeTime) {
    var timeOpen;
    var timeClose;
    var opensFirst;

    if (openTime) {
      timeOpen = getTimeDiff(moment(), moment(openTime));
    }

    if (closeTime) {
      timeClose = getTimeDiff(moment(), moment(closeTime));
    }

    if (openTime && closeTime) {
      opensFirst = !!getTimeDiff(moment(openTime), moment(closeTime));
    }

    return [timeOpen, timeClose, opensFirst];
  }

  var getValveScheduleMessage = function getValveScheduleMessage(online, state, openTime, closeTime, loading) {
    var _getTimingAttr = getTimingAttr(openTime, closeTime),
        timeOpen = _getTimingAttr[0],
        timeClose = _getTimingAttr[1],
        opensFirst = _getTimingAttr[2];

    if (state) {
      if (timeOpen && opensFirst) {
        return t("Opens In " + timeOpen + ".");
      }

      if (timeClose && !opensFirst) {
        return t("Closes In " + timeClose + ".");
      }

      if (state === 'unknown') {
        return t('Disconnected');
      }

      if (!online && state === 'closed') {
        return t('Disconnected (Closed)');
      }

      return t('Nothing scheduled');
    }

    if (loading) {
      return /*#__PURE__*/React.createElement(LoadingOutlined, {
        className: styles$2.tagIcon
      });
    }

    return t('schedule could not be loaded');
  };

  var getPumpScheduleMessage = function getPumpScheduleMessage(startDate, stopDate) {
    var _getTimingAttr2 = getTimingAttr(startDate, stopDate),
        timeOn = _getTimingAttr2[0],
        timeOff = _getTimingAttr2[1],
        onFirst = _getTimingAttr2[2];

    if (timeOn && onFirst) {
      return "On in " + timeOn + ".";
    }

    if (timeOff && !onFirst) {
      return "Off in " + timeOff + ".";
    }

    return null;
  };

  var getMessageClassName = function getMessageClassName(state, online) {
    var styleStr = styles$2.redMessage + " ";
    var styleStrNot = styles$2.normalMessage + " ";

    if (state === 'unknown' || !online && state === 'closed') {
      return styleStr;
    }

    if (!state) return styleStr;
    return styleStrNot;
  };

  var getSchedule = function getSchedule(block, text) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles$2.iconContainer,
      onClick: function onClick() {
        return onClickCalendar({
          id: block.blockId
        });
      },
      role: "presentation"
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$2.icon
    }, /*#__PURE__*/React.createElement(FaCalendarAlt, null)), /*#__PURE__*/React.createElement("div", {
      className: getMessageClassName(block.valveCurrentState, block.valveOnline)
    }, /*#__PURE__*/React.createElement("span", {
      className: styles$2.scheduleContent
    }, text)));
  };

  var getIndClass = function getIndClass(blockStatus) {
    var styleStr = styles$2.indicator + " ";

    if (!blockStatus.valveLoading) {
      if (blockStatus.valveControlEnabled) {
        if (!blockStatus.valveOnline) return styleStr + styles$2.red;
        if (blockStatus.valveOnline && blockStatus.valveCurrentState === 'closed') return styleStr + styles$2.lightgrey;
        if (blockStatus.valveOnline && blockStatus.valveCurrentState === 'open') return styleStr + styles$2.blue;
      }
    }

    return styleStr + styles$2.lightgrey;
  };

  var getCurrentState = function getCurrentState(block) {
    var color = block.valveCurrentState === 'open' ? '#1890FF' : '#D40202';

    if (block.valveLoading) {
      return /*#__PURE__*/React.createElement(LoadingOutlined, {
        className: styles$2.tagIcon
      });
    }

    if (block.valveCurrentState) {
      if (block.valveCurrentState !== 'unknown') {
        if (block.valveOnline && block.valveCurrentState === 'closed') {
          return /*#__PURE__*/React.createElement(Tag, {
            color: "",
            key: block.valveCurrentState,
            className: styles$2.tagIcon
          }, block.valveCurrentState.toUpperCase());
        }

        return /*#__PURE__*/React.createElement(Tag, {
          color: color,
          key: block.valveCurrentState,
          className: styles$2.tagIcon
        }, block.valveCurrentState.toUpperCase());
      }

      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2.warningIcon
      });
    }

    if (!block.valveCurrentState && !block.valveLoading) {
      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2.warningIcon
      });
    }

    return /*#__PURE__*/React.createElement(LoadingOutlined, {
      className: styles$2.tagIcon
    });
  };

  var getRowClassName = function getRowClassName(record) {
    if (record.valveCurrentState) {
      if (!record.valveControlEnabled || record.valveCurrentState === 'unknown') {
        return styles$2['red-border-text'];
      }

      if (record.valveControlEnabled && !record.valveOnline && (record.valveCurrentState === 'closed' || !record.valveCurrentState)) {
        return styles$2['red-border-text'];
      }
    }

    if (!record.valveLoading && (!record.valveControlEnabled || !record.valveCurrentState)) {
      return styles$2['red-border-text'];
    }

    var className = getIndClass(record);
    return className;
  };

  function widthCalc(columns) {
    var maxwid = 100;
    var result = columns().map(function (element) {
      var values = element;

      if (element.title === 'Alarm') {
        values.width = 100;
      }

      if (element.title === 'Override') {
        values.width = 100;
      }

      if (element.title !== 'Override' && element.title !== 'Alarm') {
        values.width = (maxwid - 10) / (columns().length - 2) + "%";
      }

      return values;
    });
    return result;
  }

  var columns = function columns() {
    return [{
      title: 'Block',
      className: styles$2.scheduleItemHeader,
      render: function render(block) {
        if (block.blockName) {
          return /*#__PURE__*/React.createElement(Tooltip, {
            title: block.blockName
          }, /*#__PURE__*/React.createElement("span", null, block.blockName));
        }

        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: 'lightgray'
          }
        }, t('No Name'));
      }
    }, {
      title: 'Schedule',
      className: styles$2.scheduleHeader,
      render: function render(block) {
        if (block.valveControlEnabled) {
          return getSchedule(block, getValveScheduleMessage(block.valveOnline, block.valveCurrentState, block.valveScheduledOpen, block.valveScheduledClose, block.valveLoading));
        }

        return t('Control not enabled');
      }
    }, {
      title: 'State',
      className: styles$2.stateHeader,
      render: function render(block) {
        if (block.valveControlEnabled) {
          return /*#__PURE__*/React.createElement("div", {
            className: styles$2.status
          }, getCurrentState(block));
        }

        return null;
      }
    }, {
      title: 'Override',
      className: styles$2.overrideHeader,
      render: function render(block) {
        if (block.valveControlEnabled) {
          return /*#__PURE__*/React.createElement(OverrideStatus, {
            type: VALVE_TYPE,
            overrideStatus: block.valveOverrideStatus,
            valveIdentifier: block.valveIdentifier,
            vfdIdentifier: block.vfdIdentifier,
            loading: block.valveLoading,
            currentState: block.valveCurrentState,
            confirmDialogMessages: [getPumpScheduleMessage(block.pumpStartDate, block.pumpStopDate), getValveScheduleMessage(block.valveOnline, block.valveCurrentState, block.valveScheduledOpen, block.valveScheduledClose)]
          });
        }

        return null;
      }
    }, {
      title: 'Alarm',
      className: styles$2.alarmsHeader,
      render: function render() {
        return /*#__PURE__*/React.createElement(Button, {
          className: styles$2.bellIconButton,
          icon: /*#__PURE__*/React.createElement(BellFilled, {
            className: styles$2.bellIconSize
          }),
          disabled: true
        });
      }
    }];
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2.listContainer
  }, /*#__PURE__*/React.createElement(Collapse, {
    expandIconPosition: "right",
    defaultActiveKey: ['1']
  }, /*#__PURE__*/React.createElement(Panel$1, {
    header: "Valves",
    key: "1",
    className: styles$2.collapseHeader
  }, blockLoading || blockData.length ? /*#__PURE__*/React.createElement(Table, {
    className: styles$2['custom-table'],
    rowClassName: getRowClassName,
    dataSource: blockData,
    columns: widthCalc(columns),
    pagination: false,
    loading: blockLoading,
    rowKey: function rowKey(record) {
      return String(record.blockId);
    }
  }) : /*#__PURE__*/React.createElement("section", {
    className: styles$2['configuration-message']
  }, "No Valves Configured"))));
}
BlockControlList.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.object),
  onClickCalendar: PropTypes.func,
  blockLoading: PropTypes.bool
};
BlockControlList.defaultProps = {
  blocks: [],
  onClickCalendar: null,
  blockLoading: null
};

var almonds = require("./almonds~diBVNtXO.png");

var grapes = require("./grapes~VvgQSJjC.png");

var none = require("./none~fggdWnqg.png");

var pistachios = require("./pistachios~DDbsjBIW.png");

var walnuts = require("./walnuts~VdYLyOvB.png");

var Panel$2 = Collapse.Panel;
var Text$1 = Typography.Text;

var EntityList = function EntityList(_ref) {
  var onChange = _ref.onChange,
      selected = _ref.selected,
      data = _ref.data;

  if (data.length) {
    data.map(function (d) {
      var obj = d;
      obj.ranches.sort(function (a, b) {
        return a && a.name.toLowerCase().localeCompare(b && b.name.toLowerCase());
      });
      obj.ranches.map(function (r) {
        var robj = r;
        robj.blocks.sort(function (a, b) {
          return a && a.name.toLowerCase().localeCompare(b && b.name.toLowerCase());
        });
        return robj;
      });
      return obj;
    });
    data.sort(function (a, b) {
      return a && a.name.toLowerCase().localeCompare(b && b.name.toLowerCase());
    });
  }

  function getImage(type) {
    switch (type.toLowerCase()) {
      case 'almonds':
      case 'almond':
        return almonds;

      case 'grapes':
      case 'grape':
        return grapes;

      case 'pistachios':
      case 'pistachio':
        return pistachios;

      case 'walnuts':
      case 'walnut':
        return walnuts;

      default:
        return none;
    }
  }

  return data.map(function (en, i) {
    if (en && en.id) {
      return /*#__PURE__*/React.createElement(Collapse, {
        expandIconPosition: "right",
        key: String(en.id) + "_" + String(i),
        collapsible: "header",
        defaultActiveKey: en.id
      }, /*#__PURE__*/React.createElement(Panel$2, {
        header: en.name,
        key: String(en.id),
        className: styles['collapse-content']
      }, en.ranches.length ? en.ranches.map(function (ra, j) {
        return /*#__PURE__*/React.createElement(Collapse, {
          className: selected.ranch && selected.ranch.includes(ra.id) && selected.block && !selected.block.length ? styles['collapsible-list-selected'] : styles['collapsible-list'],
          expandIcon: function expandIcon(_ref2) {
            var isActive = _ref2.isActive;
            return /*#__PURE__*/React.createElement(CaretRightOutlined, {
              rotate: isActive ? 90 : 0
            });
          },
          defaultActiveKey: selected.ranch,
          key: String(ra.id) + "_" + String(j)
        }, /*#__PURE__*/React.createElement(Panel$2, {
          header: selected.ranch && selected.ranch.includes(ra.id) && selected.block && !selected.block.length ? /*#__PURE__*/React.createElement(Tooltip, {
            title: ra.name
          }, /*#__PURE__*/React.createElement(Text$1, {
            id: String(ra.id),
            onClick: function onClick(d) {
              return onChange({
                ranch: d.target.id
              });
            },
            className: styles['selected-ranch-header']
          }, ra.name)) : /*#__PURE__*/React.createElement(Tooltip, {
            title: ra.name
          }, /*#__PURE__*/React.createElement(Text$1, {
            id: String(ra.id),
            onClick: function onClick(d) {
              return onChange({
                ranch: d.target.id
              });
            }
          }, ra.name)),
          key: String(ra.id)
        }, ra.blocks.map(function (bl, k) {
          return /*#__PURE__*/React.createElement("div", {
            key: bl.name + "_" + String(k),
            className: styles['list-content']
          }, /*#__PURE__*/React.createElement(List.Item, {
            key: bl.id,
            onClick: function onClick(d) {
              return onChange({
                block: d.target.id
              });
            },
            id: bl.id,
            className: selected.ranch && selected.ranch.length && selected.block && selected.block.includes(bl.id) ? styles['selected-block'] : styles.extraList,
            actions: [/*#__PURE__*/React.createElement("img", {
              alt: bl.crop,
              src: getImage(bl.crop)
            })]
          }, /*#__PURE__*/React.createElement(Tooltip, {
            title: bl.name
          }, /*#__PURE__*/React.createElement(Text$1, {
            id: bl.id,
            className: selected.ranch && selected.ranch.length && selected.block && selected.block.includes(bl.id) ? styles['selected-block-text'] : styles['list-name']
          }, bl.name))), /*#__PURE__*/React.createElement("div", {
            className: styles.statusBox
          }));
        })), /*#__PURE__*/React.createElement("div", {
          className: styles['panel-statusBox']
        }));
      }) : /*#__PURE__*/React.createElement(LoadingOutlined, {
        className: styles.loadingIcon
      })));
    }

    return null;
  });
};

EntityList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
  selected: PropTypes.shape({
    ranch: PropTypes.arrayOf(PropTypes.any),
    block: PropTypes.arrayOf(PropTypes.any)
  })
};
EntityList.defaultProps = {
  data: [],
  onChange: null,
  selected: null
};

var styles$3 = {"container":"_1Ma7E","left-half":"_1IjKl","right-half":"_30e5v","formContainer":"_3dEFI","ant-btn":"_2pZwL","updateFormButtons":"_1kPVq"};

function InputNumberUnits(props) {
  var disabled = props.disabled,
      initialValue = props.initialValue,
      onChange = props.onChange,
      unitOptions = props.unitOptions;

  var _useState = useState(unitOptions && unitOptions[0] && unitOptions[0].key || ''),
      units = _useState[0],
      setUnits = _useState[1];

  var _useState2 = useState(initialValue || 0),
      value = _useState2[0],
      setValue = _useState2[1];

  function onUnitsChange(newUnits) {
    setUnits(newUnits);

    if (onChange) {
      onChange({
        value: value,
        units: newUnits
      });
    }
  }

  function onValueChange(newValue) {
    setValue(newValue);

    if (onChange) {
      onChange({
        value: newValue,
        units: units
      });
    }
  }

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("section", {
    className: styles$3.container
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$3['left-half']
  }, /*#__PURE__*/React.createElement(InputNumber, {
    value: value,
    onChange: onValueChange,
    disabled: disabled,
    style: {
      width: '100%'
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: styles$3['right-half']
  }, /*#__PURE__*/React.createElement(Select, {
    value: units,
    onChange: onUnitsChange,
    disabled: disabled
  }, unitOptions.map(function (option) {
    return /*#__PURE__*/React.createElement(Select.Option, {
      key: option.key,
      value: option.key
    }, option.name);
  })))));
}

InputNumberUnits.propTypes = {
  unitOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  disabled: PropTypes.bool,
  initialValue: PropTypes.number,
  onChange: PropTypes.func
};
InputNumberUnits.defaultProps = {
  disabled: false,
  initialValue: 0,
  onChange: null
};

var timeUnits = [{
  key: 'minutes',
  name: 'Minutes'
}, {
  key: 'hours',
  name: 'Hours'
}];

function InputTime(props) {
  var disabled = props.disabled,
      initialValue = props.initialValue,
      onChange = props.onChange;
  return /*#__PURE__*/React.createElement(InputNumberUnits, {
    unitOptions: timeUnits,
    onChange: onChange,
    disabled: disabled,
    initialValue: initialValue
  });
}

InputTime.propTypes = {
  disabled: PropTypes.bool,
  initialValue: PropTypes.number,
  onChange: PropTypes.func
};
InputTime.defaultProps = {
  onChange: null,
  disabled: false,
  initialValue: 0
};

var volumeUnits = [{
  key: 'acrein',
  name: 'acre inches'
}, {
  key: 'cuft',
  name: 'cubic feet'
}];

function InputVolume(props) {
  var disabled = props.disabled,
      initialValue = props.initialValue,
      onChange = props.onChange;
  return /*#__PURE__*/React.createElement(InputNumberUnits, {
    unitOptions: volumeUnits,
    onChange: onChange,
    disabled: disabled,
    initialValue: initialValue
  });
}

InputVolume.propTypes = {
  disabled: PropTypes.bool,
  initialValue: PropTypes.number,
  onChange: PropTypes.func
};
InputVolume.defaultProps = {
  disabled: false,
  initialValue: 0,
  onChange: null
};

var selectBlockSchedule = selectors.selectBlockSchedule;
var scheduleMethods = [{
  value: 'dates',
  label: 'Date Range'
}, {
  value: 'runtime',
  label: 'Run Time'
}, {
  value: 'volume',
  label: 'Water Volume'
}];

var ScheduleForm = function ScheduleForm(_ref) {
  var onCancel = _ref.onCancel,
      onSubmit = _ref.onSubmit,
      onChange = _ref.onChange,
      dateRange = _ref.dateRange,
      blocks = _ref.blocks,
      scheduleId = _ref.scheduleId;

  var _Form$useForm = Form.useForm(),
      form = _Form$useForm[0];

  var _useState = useState('dates'),
      method = _useState[0],
      setMethod = _useState[1];

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var createScheduledIrrigationEvent = controlApi.createScheduledIrrigationEvent,
      updateScheduledIrrigationEvent = controlApi.updateScheduledIrrigationEvent,
      deleteScheduledIrrigationEvent = controlApi.deleteScheduledIrrigationEvent;

  var _useState2 = useState([]),
      block = _useState2[0],
      setBlock = _useState2[1];

  var _useState3 = useState(false),
      loading = _useState3[0],
      setLoadingState = _useState3[1];

  var _useState4 = useState(true),
      btnEnabled = _useState4[0],
      setButtonState = _useState4[1];

  var _useState5 = useState([]),
      paramDateRange = _useState5[0],
      setParamDateRange = _useState5[1];

  var _useState6 = useState({
    state: false,
    dateRange: null
  }),
      deleteConfirmModalState = _useState6[0],
      setDeleteConfirmModalState = _useState6[1];

  var _useState7 = useState(false),
      validationStatus = _useState7[0],
      setValidationStatus = _useState7[1];

  var blockId;

  if (scheduleId && blocks) {
    var blkid = blocks[0];
    blockId = blkid;
  }

  var schedules = useSelector(function (state) {
    return selectBlockSchedule(state, blockId);
  });
  useEffect(function () {
    if (blocks) {
      if (blocks) setBlock(blocks);

      if (dateRange) {
        if (dateRange) setParamDateRange(dateRange);
      }
    }

    if (schedules) {
      var schedule = schedules.scheduled_events.filter(function (d) {
        return d.id === scheduleId;
      });

      if (schedule.length > 0) {
        setParamDateRange([moment(Date.parse(schedule[0].start_date)), moment(Date.parse(schedule[0].stop_date))]);
      }
    }
  }, [blocks, dateRange, scheduleId, schedules]);

  function processRequest(startDate, stopDate, blk, obj, toDelete) {
    var submitObj = obj || {};

    try {
      var callApi = function callApi() {
        try {
          var _temp5 = function _temp5() {
            if (success) {
              message.success(t('Success'));
              setLoadingState(false);
              submitObj.status = 'Created';
              onSubmit(submitObj);
            } else {
              message.error(t('Error'));
              setLoadingState(false);
              submitObj.status = 'Error';
              onSubmit(submitObj);
            }
          };

          var success;

          var _temp6 = function () {
            if (scheduleId && blockId && !toDelete) {
              return Promise.resolve(updateScheduledIrrigationEvent({
                id: scheduleId,
                start_date: startDate,
                stop_date: stopDate,
                block: blockId
              })).then(function (_updateScheduledIrrig) {
                success = _updateScheduledIrrig;
              });
            } else {
              var _temp7 = function () {
                if (toDelete && scheduleId) {
                  return Promise.resolve(deleteScheduledIrrigationEvent({
                    id: scheduleId
                  })).then(function (_deleteScheduledIrrig) {
                    success = _deleteScheduledIrrig;
                  });
                } else {
                  var _temp8 = function () {
                    if (startDate && stopDate && blk) {
                      return Promise.resolve(createScheduledIrrigationEvent({
                        start_date: startDate,
                        stop_date: stopDate,
                        block: blk
                      })).then(function (_createScheduledIrrig) {
                        success = _createScheduledIrrig;
                      });
                    }
                  }();

                  if (_temp8 && _temp8.then) return _temp8.then(function () {});
                }
              }();

              if (_temp7 && _temp7.then) return _temp7.then(function () {});
            }
          }();

          return Promise.resolve(_temp6 && _temp6.then ? _temp6.then(_temp5) : _temp5(_temp6));
        } catch (e) {
          return Promise.reject(e);
        }
      };

      callApi();
    } catch (error) {
      message.error(t('Error'));
      setLoadingState(false);
      submitObj.status = 'Error';
      onSubmit(submitObj);
    }
  }

  function onConfirmDelete() {
    setLoadingState(true);
    processRequest(null, null, null, {}, true);
  }

  function onFinish(values) {
    if (values.method === 'dates' && !paramDateRange.length) {
      setValidationStatus(true);
    }

    if (values.method === 'runtime') {
      var formObj = values;
      formObj.endTime = moment(values.startTime).add(values.runTime.value, values.runTime.units);
    }

    var submitObj = {
      block: values.block,
      method: values.method,
      startDate: paramDateRange && paramDateRange[0] ? paramDateRange[0].format('YYYY-MM-DDTHH:mm:ss.sss') : null,
      endDate: paramDateRange && paramDateRange[1] ? paramDateRange[1].format('YYYY-MM-DDTHH:mm:ss.sss') : null,
      startTime: values.startTime ? values.startTime.format('YYYY-MM-DDTHH:mm:ss.sss') : null,
      runTime: values.runTime,
      endTime: values.endTime ? values.endTime.format('YYYY-MM-DDTHH:mm:ss.sss') : null,
      volume: values.volume
    };
    var endTime;

    if (values.method === 'runtime') {
      endTime = moment(values.startTime).add(values.runTime.value, values.runTime.units);
    }

    var startDate = paramDateRange && paramDateRange[0] ? moment(paramDateRange[0]).toISOString() : moment(values.startTime).toISOString();
    var stopDate = paramDateRange && paramDateRange[1] ? moment(paramDateRange[1]).toISOString() : moment(endTime).toISOString();

    if (moment(startDate).isSame(moment(stopDate))) {
      if (paramDateRange.length) message.error('StartDate and StopDate are same');
    } else if (moment(startDate).isBefore(moment(stopDate))) {
      setLoadingState(true);
      values.block.forEach(function (d) {
        processRequest(startDate, stopDate, d, submitObj);
      });
    }
  }

  function onFormEdit(val) {
    if (onChange) onChange(val);

    if (val && val.length > 1) {
      setParamDateRange(val);
      setValidationStatus(false);
    } else {
      setParamDateRange([]);
      setValidationStatus(true);
    }

    setButtonState(false);
  }

  function renderBlock() {
    return /*#__PURE__*/React.createElement(Form.Item, {
      name: "block",
      label: t('Block(s)'),
      rules: [{
        required: true,
        message: t('Please select block(s)')
      }]
    }, /*#__PURE__*/React.createElement(BlockSelect, {
      placeholder: t('Select Blocks To Irrigate'),
      mode: "multiple",
      onChange: function onChange(d) {
        setBlock(d);
        setButtonState(false);
      },
      defaultOption: block.length > 0 ? block : blocks
    }));
  }

  function renderDateRangeItems() {
    return /*#__PURE__*/React.createElement(Form.Item, {
      label: t('Choose Dates'),
      help: validationStatus ? t('Please select Start Date and End Date') : null,
      validateStatus: validationStatus ? 'error' : 'validating',
      rules: [{
        required: true,
        message: t('Please select Start Date and End Date')
      }]
    }, /*#__PURE__*/React.createElement(DatePicker.RangePicker, {
      showTime: true,
      onChange: onFormEdit,
      disabled: method !== 'dates',
      value: paramDateRange
    }));
  }

  function renderRuntimeItems() {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Form.Item, {
      name: "startTime",
      label: t('Start Time'),
      rules: [{
        required: true,
        message: t('Please select Start Time')
      }]
    }, /*#__PURE__*/React.createElement(DatePicker, {
      showTime: true,
      onChange: onFormEdit,
      onOk: null
    })), /*#__PURE__*/React.createElement(Form.Item, {
      name: "runTime",
      label: t('Run-time')
    }, /*#__PURE__*/React.createElement(InputTime, {
      disabled: method !== 'runtime',
      onChange: onFormEdit
    })));
  }

  function renderVolumeItems() {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Form.Item, {
      name: "startTime",
      label: t('Start Time'),
      rules: [{
        required: true,
        message: t('Please select Start Time')
      }]
    }, /*#__PURE__*/React.createElement(DatePicker, {
      showTime: true,
      onChange: onFormEdit,
      onOk: null
    })), /*#__PURE__*/React.createElement(Form.Item, {
      name: "volume",
      label: t('Volume')
    }, /*#__PURE__*/React.createElement(InputVolume, {
      disabled: method !== 'volume',
      onChange: onFormEdit
    })));
  }

  function onDelete() {
    return /*#__PURE__*/React.createElement(Modal, {
      title: t('Confirm delete schedule'),
      centered: true,
      visible: deleteConfirmModalState.state,
      closable: true,
      onOk: function onOk() {
        onConfirmDelete();
        setDeleteConfirmModalState({
          state: false,
          dateRange: null
        });
      },
      onCancel: function onCancel() {
        setDeleteConfirmModalState({
          state: false,
          dateRange: paramDateRange
        });
      },
      maskClosable: false,
      destroyOnClose: true,
      width: 400,
      okText: t('Delete'),
      okButtonProps: {
        style: {
          background: 'red',
          borderColor: 'red'
        }
      }
    }, t('Do you really want to delete?'));
  }

  var saveBtnStyle = {
    background: 'green',
    borderColor: 'green',
    color: 'white'
  };

  function getButton(val) {
    if (val) {
      return /*#__PURE__*/React.createElement("div", {
        className: styles$3.updateFormButtons
      }, window.innerWidth > 599 ? /*#__PURE__*/React.createElement(Button, {
        htmlType: "button",
        onClick: onCancel
      }, t('Cancel')) : null, /*#__PURE__*/React.createElement(Button, {
        type: "primary",
        htmlType: "button",
        onClick: function onClick() {
          if (moment(moment().format()).isBetween(paramDateRange[0], paramDateRange[1])) {
            setDeleteConfirmModalState({
              state: true,
              dateRange: paramDateRange
            });
          } else onConfirmDelete();
        },
        danger: true
      }, t('Delete')), /*#__PURE__*/React.createElement(Button, {
        type: "primary",
        htmlType: "submit",
        disabled: btnEnabled,
        style: !btnEnabled ? saveBtnStyle : null
      }, t('Save')));
    }

    return /*#__PURE__*/React.createElement(Button, {
      type: "primary",
      htmlType: "submit"
    }, t('Submit'));
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "formContainer",
    id: "form-update"
  }, /*#__PURE__*/React.createElement(Form, {
    layout: "vertical",
    form: form,
    initialValues: {
      method: 'dates',
      chooseDates: dateRange,
      block: blocks
    },
    onFinish: onFinish,
    onChange: onFormEdit,
    scrollToFirstError: true
  }, renderBlock(), /*#__PURE__*/React.createElement(Form.Item, {
    label: t('Schedule method'),
    name: "method",
    rules: [{
      required: true,
      message: t('Please select method')
    }]
  }, /*#__PURE__*/React.createElement(Select, {
    placeholder: t('Select Schedule Method'),
    value: method,
    onChange: function onChange(d) {
      return setMethod(d);
    },
    options: scheduleMethods
  })), method === 'dates' && renderDateRangeItems(), method === 'runtime' && renderRuntimeItems(), method === 'volume' && renderVolumeItems(), deleteConfirmModalState.state ? onDelete() : null, /*#__PURE__*/React.createElement(Form.Item, null, loading ? /*#__PURE__*/React.createElement(LoadingOutlined, null) : getButton(scheduleId))));
};

ScheduleForm.propTypes = {
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  dateRange: PropTypes.arrayOf(PropTypes.any),
  blocks: PropTypes.arrayOf(PropTypes.any),
  scheduleId: PropTypes.number
};
ScheduleForm.defaultProps = {
  onCancel: function onCancel() {
    return null;
  },
  onChange: function onChange() {
    return null;
  },
  onSubmit: function onSubmit() {
    return null;
  },
  dateRange: null,
  blocks: null,
  scheduleId: null
};

var ScheduleCreateForm = function ScheduleCreateForm(_ref) {
  var onSubmit = _ref.onSubmit,
      onChange = _ref.onChange,
      dateRange = _ref.dateRange,
      blocks = _ref.blocks;
  return /*#__PURE__*/React.createElement(ScheduleForm, {
    onChange: onChange,
    onSubmit: onSubmit,
    dateRange: dateRange,
    blocks: blocks
  });
};

ScheduleCreateForm.propTypes = {
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  dateRange: PropTypes.arrayOf(PropTypes.any),
  blocks: PropTypes.arrayOf(PropTypes.any)
};
ScheduleCreateForm.defaultProps = {
  onChange: function onChange() {
    return null;
  },
  onSubmit: function onSubmit() {
    return null;
  },
  dateRange: null,
  blocks: null
};

var ScheduleUpdateForm = function ScheduleUpdateForm(_ref) {
  var onCancel = _ref.onCancel,
      onChange = _ref.onChange,
      onSubmit = _ref.onSubmit,
      blockId = _ref.blockId,
      scheduleId = _ref.scheduleId;
  return /*#__PURE__*/React.createElement(ScheduleForm, {
    onCancel: onCancel,
    onChange: onChange,
    onSubmit: onSubmit,
    blocks: [blockId],
    scheduleId: scheduleId
  });
};

ScheduleUpdateForm.propTypes = {
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  blockId: PropTypes.number,
  scheduleId: PropTypes.number
};
ScheduleUpdateForm.defaultProps = {
  onCancel: function onCancel() {
    return null;
  },
  onChange: function onChange() {
    return null;
  },
  onSubmit: function onSubmit() {
    return null;
  },
  blockId: null,
  scheduleId: null
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var highcharts = createCommonjsModule(function (module) {
  (function (aa, S) {
     module.exports ? (S["default"] = S, module.exports = aa.document ? S(aa) : S) :  (aa.Highcharts && aa.Highcharts.error(16, !0), aa.Highcharts = S(aa));
  })("undefined" !== typeof window ? window : commonjsGlobal, function (aa) {
    function S(d, g, W, u) {
      d.hasOwnProperty(g) || (d[g] = u.apply(null, W));
    }

    var r = {};
    S(r, "parts/Globals.js", [], function () {
      var d = "undefined" !== typeof aa ? aa : "undefined" !== typeof window ? window : {},
          g = d.document,
          W = d.navigator && d.navigator.userAgent || "",
          u = g && g.createElementNS && !!g.createElementNS("http://www.w3.org/2000/svg", "svg").createSVGRect,
          r = /(edge|msie|trident)/i.test(W) && !d.opera,
          M = -1 !== W.indexOf("Firefox"),
          E = -1 !== W.indexOf("Chrome"),
          A = M && 4 > parseInt(W.split("Firefox/")[1], 10);
      return {
        product: "Highcharts",
        version: "8.0.4",
        deg2rad: 2 * Math.PI / 360,
        doc: g,
        hasBidiBug: A,
        hasTouch: !!d.TouchEvent,
        isMS: r,
        isWebKit: -1 !== W.indexOf("AppleWebKit"),
        isFirefox: M,
        isChrome: E,
        isSafari: !E && -1 !== W.indexOf("Safari"),
        isTouchDevice: /(Mobile|Android|Windows Phone)/.test(W),
        SVG_NS: "http://www.w3.org/2000/svg",
        chartCount: 0,
        seriesTypes: {},
        symbolSizes: {},
        svg: u,
        win: d,
        marginNames: ["plotTop", "marginRight", "marginBottom", "plotLeft"],
        noop: function noop() {},
        charts: [],
        dateFormats: {}
      };
    });
    S(r, "parts/Utilities.js", [r["parts/Globals.js"]], function (d) {
      function g() {
        var b,
            a = arguments,
            m = {},
            f = function f(b, a) {
          "object" !== typeof b && (b = {});
          Y(a, function (m, c) {
            !h(m, !0) || q(m) || N(m) ? b[c] = a[c] : b[c] = f(b[c] || {}, m);
          });
          return b;
        };

        !0 === a[0] && (m = a[1], a = Array.prototype.slice.call(a, 2));
        var c = a.length;

        for (b = 0; b < c; b++) {
          m = f(m, a[b]);
        }

        return m;
      }

      function W(b, a, m) {
        var f;
        t(a) ? c(m) ? b.setAttribute(a, m) : b && b.getAttribute && ((f = b.getAttribute(a)) || "class" !== a || (f = b.getAttribute(a + "Name"))) : Y(a, function (a, m) {
          b.setAttribute(m, a);
        });
        return f;
      }

      function u() {
        for (var b = arguments, a = b.length, m = 0; m < a; m++) {
          var f = b[m];
          if ("undefined" !== typeof f && null !== f) return f;
        }
      }

      function r(b, a) {
        if (!b) return a;
        var m = b.split(".").reverse();
        if (1 === m.length) return a[b];

        for (b = m.pop(); "undefined" !== typeof b && "undefined" !== typeof a && null !== a;) {
          a = a[b], b = m.pop();
        }

        return a;
      }

      d.timers = [];

      var M = d.charts,
          E = d.doc,
          A = d.win,
          G = d.error = function (b, a, m, f) {
        var c = P(b),
            p = c ? "Highcharts error #" + b + ": www.highcharts.com/errors/" + b + "/" : b.toString(),
            e = function e() {
          if (a) throw Error(p);
          A.console && console.log(p);
        };

        if ("undefined" !== typeof f) {
          var x = "";
          c && (p += "?");
          d.objectEach(f, function (b, a) {
            x += "\n" + a + ": " + b;
            c && (p += encodeURI(a) + "=" + encodeURI(b));
          });
          p += x;
        }

        m ? d.fireEvent(m, "displayError", {
          code: b,
          message: p,
          params: f
        }, e) : e();
      },
          J = function () {
        function b(b, a, m) {
          this.options = a;
          this.elem = b;
          this.prop = m;
        }

        b.prototype.dSetter = function () {
          var b = this.paths[0],
              a = this.paths[1],
              m = [],
              f = this.now,
              c = b.length;
          if (1 === f) m = this.toD;else if (c === a.length && 1 > f) for (; c--;) {
            var p = parseFloat(b[c]);
            m[c] = isNaN(p) || "A" === a[c - 4] || "A" === a[c - 5] ? a[c] : f * parseFloat("" + (a[c] - p)) + p;
          } else m = a;
          this.elem.attr("d", m, null, !0);
        };

        b.prototype.update = function () {
          var b = this.elem,
              a = this.prop,
              m = this.now,
              f = this.options.step;
          if (this[a + "Setter"]) this[a + "Setter"]();else b.attr ? b.element && b.attr(a, m, null, !0) : b.style[a] = m + this.unit;
          f && f.call(b, m, this);
        };

        b.prototype.run = function (b, a, m) {
          var f = this,
              c = f.options,
              p = function p(b) {
            return p.stopped ? !1 : f.step(b);
          },
              e = A.requestAnimationFrame || function (b) {
            setTimeout(b, 13);
          },
              x = function x() {
            for (var b = 0; b < d.timers.length; b++) {
              d.timers[b]() || d.timers.splice(b--, 1);
            }

            d.timers.length && e(x);
          };

          b !== a || this.elem["forceAnimate:" + this.prop] ? (this.startTime = +new Date(), this.start = b, this.end = a, this.unit = m, this.now = this.start, this.pos = 0, p.elem = this.elem, p.prop = this.prop, p() && 1 === d.timers.push(p) && e(x)) : (delete c.curAnim[this.prop], c.complete && 0 === Object.keys(c.curAnim).length && c.complete.call(this.elem));
        };

        b.prototype.step = function (b) {
          var a = +new Date(),
              m = this.options,
              f = this.elem,
              c = m.complete,
              p = m.duration,
              e = m.curAnim;
          if (f.attr && !f.element) b = !1;else if (b || a >= p + this.startTime) {
            this.now = this.end;
            this.pos = 1;
            this.update();
            var x = e[this.prop] = !0;
            Y(e, function (b) {
              !0 !== b && (x = !1);
            });
            x && c && c.call(f);
            b = !1;
          } else this.pos = m.easing((a - this.startTime) / p), this.now = this.start + (this.end - this.start) * this.pos, this.update(), b = !0;
          return b;
        };

        b.prototype.initPath = function (b, a, m) {
          function f(b) {
            for (C = b.length; C--;) {
              var a = "M" === b[C] || "L" === b[C];
              var m = /[a-zA-Z]/.test(b[C + 3]);
              a && m && b.splice(C + 1, 0, b[C + 1], b[C + 2], b[C + 1], b[C + 2]);
            }
          }

          function c(b, a) {
            for (; b.length < v;) {
              b[0] = a[v - b.length];
              var m = b.slice(0, T);
              [].splice.apply(b, [0, 0].concat(m));
              k && (m = b.slice(b.length - T), [].splice.apply(b, [b.length, 0].concat(m)), C--);
            }

            b[0] = "M";
          }

          function p(b, a) {
            for (var m = (v - b.length) / T; 0 < m && m--;) {
              l = b.slice().splice(b.length / n - T, T * n), l[0] = a[v - T - m * T], F && (l[T - 6] = l[T - 2], l[T - 5] = l[T - 1]), [].splice.apply(b, [b.length / n, 0].concat(l)), k && m--;
            }
          }

          a = a || "";
          var e = b.startX,
              x = b.endX,
              F = -1 < a.indexOf("C"),
              T = F ? 7 : 3,
              l,
              C;
          a = a.split(" ");
          m = m.slice();
          var k = b.isArea,
              n = k ? 2 : 1;
          F && (f(a), f(m));

          if (e && x) {
            for (C = 0; C < e.length; C++) {
              if (e[C] === x[0]) {
                var V = C;
                break;
              } else if (e[0] === x[x.length - e.length + C]) {
                V = C;
                var w = !0;
                break;
              } else if (e[e.length - 1] === x[x.length - e.length + C]) {
                V = e.length - C;
                break;
              }
            }

            "undefined" === typeof V && (a = []);
          }

          if (a.length && P(V)) {
            var v = m.length + V * n * T;
            w ? (c(a, m), p(m, a)) : (c(m, a), p(a, m));
          }

          return [a, m];
        };

        b.prototype.fillSetter = function () {
          d.Fx.prototype.strokeSetter.apply(this, arguments);
        };

        b.prototype.strokeSetter = function () {
          this.elem.attr(this.prop, d.color(this.start).tweenTo(d.color(this.end), this.pos), null, !0);
        };

        return b;
      }();

      d.Fx = J;
      d.merge = g;

      var y = d.pInt = function (b, a) {
        return parseInt(b, a || 10);
      },
          t = d.isString = function (b) {
        return "string" === typeof b;
      },
          D = d.isArray = function (b) {
        b = Object.prototype.toString.call(b);
        return "[object Array]" === b || "[object Array Iterator]" === b;
      },
          h = d.isObject = function (b, a) {
        return !!b && "object" === typeof b && (!a || !D(b));
      },
          N = d.isDOMElement = function (b) {
        return h(b) && "number" === typeof b.nodeType;
      },
          q = d.isClass = function (b) {
        var a = b && b.constructor;
        return !(!h(b, !0) || N(b) || !a || !a.name || "Object" === a.name);
      },
          P = d.isNumber = function (b) {
        return "number" === typeof b && !isNaN(b) && Infinity > b && -Infinity < b;
      },
          e = d.erase = function (b, a) {
        for (var m = b.length; m--;) {
          if (b[m] === a) {
            b.splice(m, 1);
            break;
          }
        }
      },
          c = d.defined = function (b) {
        return "undefined" !== typeof b && null !== b;
      };

      d.attr = W;

      var k = d.splat = function (b) {
        return D(b) ? b : [b];
      },
          n = d.syncTimeout = function (b, a, m) {
        if (0 < a) return setTimeout(b, a, m);
        b.call(0, m);
        return -1;
      },
          f = d.clearTimeout = function (b) {
        c(b) && clearTimeout(b);
      },
          a = d.extend = function (b, a) {
        var m;
        b || (b = {});

        for (m in a) {
          b[m] = a[m];
        }

        return b;
      };

      d.pick = u;

      var l = d.css = function (b, m) {
        d.isMS && !d.svg && m && "undefined" !== typeof m.opacity && (m.filter = "alpha(opacity=" + 100 * m.opacity + ")");
        a(b.style, m);
      },
          v = d.createElement = function (b, m, f, c, p) {
        b = E.createElement(b);
        m && a(b, m);
        p && l(b, {
          padding: "0",
          border: "none",
          margin: "0"
        });
        f && l(b, f);
        c && c.appendChild(b);
        return b;
      },
          z = d.extendClass = function (b, m) {
        var f = function f() {};

        f.prototype = new b();
        a(f.prototype, m);
        return f;
      },
          w = d.pad = function (b, a, m) {
        return Array((a || 2) + 1 - String(b).replace("-", "").length).join(m || "0") + b;
      },
          B = d.relativeLength = function (b, a, m) {
        return /%$/.test(b) ? a * parseFloat(b) / 100 + (m || 0) : parseFloat(b);
      },
          L = d.wrap = function (b, a, m) {
        var f = b[a];

        b[a] = function () {
          var b = Array.prototype.slice.call(arguments),
              a = arguments,
              c = this;

          c.proceed = function () {
            f.apply(c, arguments.length ? arguments : a);
          };

          b.unshift(f);
          b = m.apply(this, b);
          c.proceed = null;
          return b;
        };
      },
          Q = d.format = function (b, a, m) {
        var f = "{",
            c = !1,
            p = [],
            e = /f$/,
            x = /\.([0-9])/,
            F = d.defaultOptions.lang,
            C = m && m.time || d.time;

        for (m = m && m.numberFormatter || T; b;) {
          var l = b.indexOf(f);
          if (-1 === l) break;
          var k = b.slice(0, l);

          if (c) {
            k = k.split(":");
            f = r(k.shift() || "", a);
            if (k.length && "number" === typeof f) if (k = k.join(":"), e.test(k)) {
              var n = parseInt((k.match(x) || ["", "-1"])[1], 10);
              null !== f && (f = m(f, n, F.decimalPoint, -1 < k.indexOf(",") ? F.thousandsSep : ""));
            } else f = C.dateFormat(k, f);
            p.push(f);
          } else p.push(k);

          b = b.slice(l + 1);
          f = (c = !c) ? "}" : "{";
        }

        p.push(b);
        return p.join("");
      },
          H = d.getMagnitude = function (b) {
        return Math.pow(10, Math.floor(Math.log(b) / Math.LN10));
      },
          K = d.normalizeTickInterval = function (b, a, m, f, c) {
        var p = b;
        m = u(m, 1);
        var e = b / m;
        a || (a = c ? [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10] : [1, 2, 2.5, 5, 10], !1 === f && (1 === m ? a = a.filter(function (b) {
          return 0 === b % 1;
        }) : .1 >= m && (a = [1 / m])));

        for (f = 0; f < a.length && !(p = a[f], c && p * m >= b || !c && e <= (a[f] + (a[f + 1] || a[f])) / 2); f++) {
        }

        return p = O(p * m, -Math.round(Math.log(.001) / Math.LN10));
      },
          p = d.stableSort = function (b, a) {
        var m = b.length,
            f,
            c;

        for (c = 0; c < m; c++) {
          b[c].safeI = c;
        }

        b.sort(function (b, m) {
          f = a(b, m);
          return 0 === f ? b.safeI - m.safeI : f;
        });

        for (c = 0; c < m; c++) {
          delete b[c].safeI;
        }
      },
          b = d.arrayMin = function (b) {
        for (var a = b.length, m = b[0]; a--;) {
          b[a] < m && (m = b[a]);
        }

        return m;
      },
          C = d.arrayMax = function (b) {
        for (var a = b.length, m = b[0]; a--;) {
          b[a] > m && (m = b[a]);
        }

        return m;
      },
          x = d.destroyObjectProperties = function (b, a) {
        Y(b, function (m, f) {
          m && m !== a && m.destroy && m.destroy();
          delete b[f];
        });
      },
          R = d.discardElement = function (b) {
        var a = d.garbageBin;
        a || (a = v("div"));
        b && a.appendChild(b);
        a.innerHTML = "";
      },
          O = d.correctFloat = function (b, a) {
        return parseFloat(b.toPrecision(a || 14));
      },
          X = d.setAnimation = function (b, a) {
        a.renderer.globalAnimation = u(b, a.options.chart.animation, !0);
      },
          U = d.animObject = function (b) {
        return h(b) ? g(b) : {
          duration: b ? 500 : 0
        };
      },
          m = d.timeUnits = {
        millisecond: 1,
        second: 1E3,
        minute: 6E4,
        hour: 36E5,
        day: 864E5,
        week: 6048E5,
        month: 24192E5,
        year: 314496E5
      },
          T = d.numberFormat = function (b, a, m, f) {
        b = +b || 0;
        a = +a;
        var c = d.defaultOptions.lang,
            p = (b.toString().split(".")[1] || "").split("e")[0].length,
            e = b.toString().split("e");
        if (-1 === a) a = Math.min(p, 20);else if (!P(a)) a = 2;else if (a && e[1] && 0 > e[1]) {
          var x = a + +e[1];
          0 <= x ? (e[0] = (+e[0]).toExponential(x).split("e")[0], a = x) : (e[0] = e[0].split(".")[0] || 0, b = 20 > a ? (e[0] * Math.pow(10, e[1])).toFixed(a) : 0, e[1] = 0);
        }
        var F = (Math.abs(e[1] ? e[0] : b) + Math.pow(10, -Math.max(a, p) - 1)).toFixed(a);
        p = String(y(F));
        x = 3 < p.length ? p.length % 3 : 0;
        m = u(m, c.decimalPoint);
        f = u(f, c.thousandsSep);
        b = (0 > b ? "-" : "") + (x ? p.substr(0, x) + f : "");
        b += p.substr(x).replace(/(\d{3})(?=\d)/g, "$1" + f);
        a && (b += m + F.slice(-a));
        e[1] && 0 !== +b && (b += "e" + e[1]);
        return b;
      };

      Math.easeInOutSine = function (b) {
        return -.5 * (Math.cos(Math.PI * b) - 1);
      };

      var Z = d.getStyle = function (b, a, m) {
        if ("width" === a) return a = Math.min(b.offsetWidth, b.scrollWidth), m = b.getBoundingClientRect && b.getBoundingClientRect().width, m < a && m >= a - 1 && (a = Math.floor(m)), Math.max(0, a - d.getStyle(b, "padding-left") - d.getStyle(b, "padding-right"));
        if ("height" === a) return Math.max(0, Math.min(b.offsetHeight, b.scrollHeight) - d.getStyle(b, "padding-top") - d.getStyle(b, "padding-bottom"));
        A.getComputedStyle || G(27, !0);
        if (b = A.getComputedStyle(b, void 0)) b = b.getPropertyValue(a), u(m, "opacity" !== a) && (b = y(b));
        return b;
      },
          ca = d.inArray = function (b, a, m) {
        return a.indexOf(b, m);
      },
          F = d.find = Array.prototype.find ? function (b, a) {
        return b.find(a);
      } : function (b, a) {
        var m,
            f = b.length;

        for (m = 0; m < f; m++) {
          if (a(b[m], m)) return b[m];
        }
      };

      d.keys = Object.keys;

      var V = d.offset = function (b) {
        var a = E.documentElement;
        b = b.parentElement || b.parentNode ? b.getBoundingClientRect() : {
          top: 0,
          left: 0
        };
        return {
          top: b.top + (A.pageYOffset || a.scrollTop) - (a.clientTop || 0),
          left: b.left + (A.pageXOffset || a.scrollLeft) - (a.clientLeft || 0)
        };
      },
          ba = d.stop = function (b, a) {
        for (var m = d.timers.length; m--;) {
          d.timers[m].elem !== b || a && a !== d.timers[m].prop || (d.timers[m].stopped = !0);
        }
      },
          Y = d.objectEach = function (b, a, m) {
        for (var f in b) {
          Object.hasOwnProperty.call(b, f) && a.call(m || b[f], b[f], f, b);
        }
      };

      Y({
        map: "map",
        each: "forEach",
        grep: "filter",
        reduce: "reduce",
        some: "some"
      }, function (b, a) {
        d[a] = function (a) {
          return Array.prototype[b].apply(a, [].slice.call(arguments, 1));
        };
      });

      var fa = d.addEvent = function (b, a, m, f) {
        void 0 === f && (f = {});
        var c = b.addEventListener || d.addEventListenerPolyfill;
        var p = "function" === typeof b && b.prototype ? b.prototype.protoEvents = b.prototype.protoEvents || {} : b.hcEvents = b.hcEvents || {};
        d.Point && b instanceof d.Point && b.series && b.series.chart && (b.series.chart.runTrackerClick = !0);
        c && c.call(b, a, m, !1);
        p[a] || (p[a] = []);
        p[a].push({
          fn: m,
          order: "number" === typeof f.order ? f.order : Infinity
        });
        p[a].sort(function (b, a) {
          return b.order - a.order;
        });
        return function () {
          da(b, a, m);
        };
      },
          da = d.removeEvent = function (b, a, m) {
        function f(a, m) {
          var f = b.removeEventListener || d.removeEventListenerPolyfill;
          f && f.call(b, a, m, !1);
        }

        function c(m) {
          var c;

          if (b.nodeName) {
            if (a) {
              var p = {};
              p[a] = !0;
            } else p = m;

            Y(p, function (b, a) {
              if (m[a]) for (c = m[a].length; c--;) {
                f(a, m[a][c].fn);
              }
            });
          }
        }

        var p;
        ["protoEvents", "hcEvents"].forEach(function (e, x) {
          var F = (x = x ? b : b.prototype) && x[e];
          F && (a ? (p = F[a] || [], m ? (F[a] = p.filter(function (b) {
            return m !== b.fn;
          }), f(a, m)) : (c(F), F[a] = [])) : (c(F), x[e] = {}));
        });
      },
          ha = d.fireEvent = function (b, m, f, c) {
        var p;
        f = f || {};

        if (E.createEvent && (b.dispatchEvent || b.fireEvent)) {
          var e = E.createEvent("Events");
          e.initEvent(m, !0, !0);
          a(e, f);
          b.dispatchEvent ? b.dispatchEvent(e) : b.fireEvent(m, e);
        } else f.target || a(f, {
          preventDefault: function preventDefault() {
            f.defaultPrevented = !0;
          },
          target: b,
          type: m
        }), function (a, m) {
          void 0 === a && (a = []);
          void 0 === m && (m = []);
          var c = 0,
              e = 0,
              x = a.length + m.length;

          for (p = 0; p < x; p++) {
            !1 === (a[c] ? m[e] ? a[c].order <= m[e].order ? a[c++] : m[e++] : a[c++] : m[e++]).fn.call(b, f) && f.preventDefault();
          }
        }(b.protoEvents && b.protoEvents[m], b.hcEvents && b.hcEvents[m]);

        c && !f.defaultPrevented && c.call(b, f);
      },
          ia = d.animate = function (b, a, m) {
        var f,
            c = "",
            p,
            e;

        if (!h(m)) {
          var x = arguments;
          m = {
            duration: x[2],
            easing: x[3],
            complete: x[4]
          };
        }

        P(m.duration) || (m.duration = 400);
        m.easing = "function" === typeof m.easing ? m.easing : Math[m.easing] || Math.easeInOutSine;
        m.curAnim = g(a);
        Y(a, function (x, F) {
          ba(b, F);
          e = new J(b, m, F);
          p = null;
          "d" === F ? (e.paths = e.initPath(b, b.d, a.d), e.toD = a.d, f = 0, p = 1) : b.attr ? f = b.attr(F) : (f = parseFloat(Z(b, F)) || 0, "opacity" !== F && (c = "px"));
          p || (p = x);
          p && p.match && p.match("px") && (p = p.replace(/px/g, ""));
          e.run(f, p, c);
        });
      },
          ja = d.seriesType = function (b, a, m, f, c) {
        var p = d.getOptions(),
            e = d.seriesTypes;
        p.plotOptions[b] = g(p.plotOptions[a], m);
        e[b] = z(e[a] || function () {}, f);
        e[b].prototype.type = b;
        c && (e[b].prototype.pointClass = z(d.Point, c));
        return e[b];
      },
          ea = d.uniqueKey = function () {
        var b = Math.random().toString(36).substring(2, 9),
            a = 0;
        return function () {
          return "highcharts-" + b + "-" + a++;
        };
      }(),
          ka = d.isFunction = function (b) {
        return "function" === typeof b;
      };

      A.jQuery && (A.jQuery.fn.highcharts = function () {
        var b = [].slice.call(arguments);
        if (this[0]) return b[0] ? (new d[t(b[0]) ? b.shift() : "Chart"](this[0], b[0], b[1]), this) : M[W(this[0], "data-highcharts-chart")];
      });
      return {
        Fx: J,
        addEvent: fa,
        animate: ia,
        animObject: U,
        arrayMax: C,
        arrayMin: b,
        attr: W,
        clamp: function clamp(b, a, m) {
          return b > a ? b < m ? b : m : a;
        },
        clearTimeout: f,
        correctFloat: O,
        createElement: v,
        css: l,
        defined: c,
        destroyObjectProperties: x,
        discardElement: R,
        erase: e,
        error: G,
        extend: a,
        extendClass: z,
        find: F,
        fireEvent: ha,
        format: Q,
        getMagnitude: H,
        getNestedProperty: r,
        getStyle: Z,
        inArray: ca,
        isArray: D,
        isClass: q,
        isDOMElement: N,
        isFunction: ka,
        isNumber: P,
        isObject: h,
        isString: t,
        merge: g,
        normalizeTickInterval: K,
        numberFormat: T,
        objectEach: Y,
        offset: V,
        pad: w,
        pick: u,
        pInt: y,
        relativeLength: B,
        removeEvent: da,
        seriesType: ja,
        setAnimation: X,
        splat: k,
        stableSort: p,
        stop: ba,
        syncTimeout: n,
        timeUnits: m,
        uniqueKey: ea,
        wrap: L
      };
    });
    S(r, "parts/Color.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var W = g.isNumber,
          u = g.merge,
          r = g.pInt;

      g = function () {
        function d(g) {
          this.parsers = [{
            regex: /rgba\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]?(?:\.[0-9]+)?)\s*\)/,
            parse: function parse(d) {
              return [r(d[1]), r(d[2]), r(d[3]), parseFloat(d[4], 10)];
            }
          }, {
            regex: /rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)/,
            parse: function parse(d) {
              return [r(d[1]), r(d[2]), r(d[3]), 1];
            }
          }];
          this.rgba = [];
          if (!(this instanceof d)) return new d(g);
          this.init(g);
        }

        d.parse = function (g) {
          return new d(g);
        };

        d.prototype.init = function (g) {
          var A, G;
          if ((this.input = g = d.names[g && g.toLowerCase ? g.toLowerCase() : ""] || g) && g.stops) this.stops = g.stops.map(function (t) {
            return new d(t[1]);
          });else {
            if (g && g.charAt && "#" === g.charAt()) {
              var u = g.length;
              g = parseInt(g.substr(1), 16);
              7 === u ? A = [(g & 16711680) >> 16, (g & 65280) >> 8, g & 255, 1] : 4 === u && (A = [(g & 3840) >> 4 | (g & 3840) >> 8, (g & 240) >> 4 | g & 240, (g & 15) << 4 | g & 15, 1]);
            }

            if (!A) for (G = this.parsers.length; G-- && !A;) {
              var y = this.parsers[G];
              (u = y.regex.exec(g)) && (A = y.parse(u));
            }
          }
          this.rgba = A || [];
        };

        d.prototype.get = function (d) {
          var g = this.input,
              G = this.rgba;

          if ("undefined" !== typeof this.stops) {
            var E = u(g);
            E.stops = [].concat(E.stops);
            this.stops.forEach(function (g, t) {
              E.stops[t] = [E.stops[t][0], g.get(d)];
            });
          } else E = G && W(G[0]) ? "rgb" === d || !d && 1 === G[3] ? "rgb(" + G[0] + "," + G[1] + "," + G[2] + ")" : "a" === d ? G[3] : "rgba(" + G.join(",") + ")" : g;

          return E;
        };

        d.prototype.brighten = function (d) {
          var g,
              u = this.rgba;
          if (this.stops) this.stops.forEach(function (g) {
            g.brighten(d);
          });else if (W(d) && 0 !== d) for (g = 0; 3 > g; g++) {
            u[g] += r(255 * d), 0 > u[g] && (u[g] = 0), 255 < u[g] && (u[g] = 255);
          }
          return this;
        };

        d.prototype.setOpacity = function (d) {
          this.rgba[3] = d;
          return this;
        };

        d.prototype.tweenTo = function (d, g) {
          var u = this.rgba,
              A = d.rgba;
          A.length && u && u.length ? (d = 1 !== A[3] || 1 !== u[3], g = (d ? "rgba(" : "rgb(") + Math.round(A[0] + (u[0] - A[0]) * (1 - g)) + "," + Math.round(A[1] + (u[1] - A[1]) * (1 - g)) + "," + Math.round(A[2] + (u[2] - A[2]) * (1 - g)) + (d ? "," + (A[3] + (u[3] - A[3]) * (1 - g)) : "") + ")") : g = d.input || "none";
          return g;
        };

        d.names = {
          white: "#ffffff",
          black: "#000000"
        };
        return d;
      }();

      d.Color = g;
      d.color = g.parse;
      return d.Color;
    });
    S(r, "parts/SvgRenderer.js", [r["parts/Globals.js"], r["parts/Color.js"], r["parts/Utilities.js"]], function (d, g, r) {
      var u = g.parse,
          I = r.addEvent,
          M = r.animate,
          E = r.animObject,
          A = r.attr,
          G = r.createElement,
          J = r.css,
          y = r.defined,
          t = r.destroyObjectProperties,
          D = r.erase,
          h = r.extend,
          N = r.inArray,
          q = r.isArray,
          P = r.isNumber,
          e = r.isObject,
          c = r.isString,
          k = r.merge,
          n = r.objectEach,
          f = r.pick,
          a = r.pInt,
          l = r.removeEvent,
          v = r.splat,
          z = r.stop,
          w = r.uniqueKey,
          B = d.charts,
          L = d.deg2rad,
          Q = d.doc,
          H = d.hasTouch,
          K = d.isFirefox,
          p = d.isMS,
          b = d.isWebKit,
          C = d.noop,
          x = d.svg,
          R = d.SVG_NS,
          O = d.symbolSizes,
          X = d.win;

      var U = d.SVGElement = function () {
        return this;
      };

      h(U.prototype, {
        opacity: 1,
        SVG_NS: R,
        textProps: "direction fontSize fontWeight fontFamily fontStyle color lineHeight width textAlign textDecoration textOverflow textOutline cursor".split(" "),
        init: function init(b, a) {
          this.element = "span" === a ? G(a) : Q.createElementNS(this.SVG_NS, a);
          this.renderer = b;
          d.fireEvent(this, "afterInit");
        },
        animate: function animate(b, a, c) {
          var m = E(f(a, this.renderer.globalAnimation, !0));
          f(Q.hidden, Q.msHidden, Q.webkitHidden, !1) && (m.duration = 0);
          0 !== m.duration ? (c && (m.complete = c), M(this, b, m)) : (this.attr(b, void 0, c), n(b, function (b, a) {
            m.step && m.step.call(this, b, {
              prop: a,
              pos: 1
            });
          }, this));
          return this;
        },
        complexColor: function complexColor(b, a, f) {
          var m = this.renderer,
              c,
              p,
              e,
              x,
              T,
              C,
              l,
              v,
              z,
              Z,
              O,
              R = [],
              B;
          d.fireEvent(this.renderer, "complexColor", {
            args: arguments
          }, function () {
            b.radialGradient ? p = "radialGradient" : b.linearGradient && (p = "linearGradient");
            p && (e = b[p], T = m.gradients, l = b.stops, Z = f.radialReference, q(e) && (b[p] = e = {
              x1: e[0],
              y1: e[1],
              x2: e[2],
              y2: e[3],
              gradientUnits: "userSpaceOnUse"
            }), "radialGradient" === p && Z && !y(e.gradientUnits) && (x = e, e = k(e, m.getRadialAttr(Z, x), {
              gradientUnits: "userSpaceOnUse"
            })), n(e, function (b, a) {
              "id" !== a && R.push(a, b);
            }), n(l, function (b) {
              R.push(b);
            }), R = R.join(","), T[R] ? O = T[R].attr("id") : (e.id = O = w(), T[R] = C = m.createElement(p).attr(e).add(m.defs), C.radAttr = x, C.stops = [], l.forEach(function (b) {
              0 === b[1].indexOf("rgba") ? (c = u(b[1]), v = c.get("rgb"), z = c.get("a")) : (v = b[1], z = 1);
              b = m.createElement("stop").attr({
                offset: b[0],
                "stop-color": v,
                "stop-opacity": z
              }).add(C);
              C.stops.push(b);
            })), B = "url(" + m.url + "#" + O + ")", f.setAttribute(a, B), f.gradient = R, b.toString = function () {
              return B;
            });
          });
        },
        applyTextOutline: function applyTextOutline(b) {
          var a = this.element,
              m;
          -1 !== b.indexOf("contrast") && (b = b.replace(/contrast/g, this.renderer.getContrast(a.style.fill)));
          b = b.split(" ");
          var f = b[b.length - 1];

          if ((m = b[0]) && "none" !== m && d.svg) {
            this.fakeTS = !0;
            b = [].slice.call(a.getElementsByTagName("tspan"));
            this.ySetter = this.xSetter;
            m = m.replace(/(^[\d\.]+)(.*?)$/g, function (b, a, m) {
              return 2 * a + m;
            });
            this.removeTextOutline(b);
            var c = a.textContent ? /^[\u0591-\u065F\u066A-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(a.textContent) : !1;
            var p = a.firstChild;
            b.forEach(function (b, e) {
              0 === e && (b.setAttribute("x", a.getAttribute("x")), e = a.getAttribute("y"), b.setAttribute("y", e || 0), null === e && a.setAttribute("y", 0));
              e = b.cloneNode(!0);
              A(c && !K ? b : e, {
                "class": "highcharts-text-outline",
                fill: f,
                stroke: f,
                "stroke-width": m,
                "stroke-linejoin": "round"
              });
              a.insertBefore(e, p);
            });
            c && K && b[0] && (b = b[0].cloneNode(!0), b.textContent = " ", a.insertBefore(b, p));
          }
        },
        removeTextOutline: function removeTextOutline(b) {
          for (var a = b.length, m; a--;) {
            m = b[a], "highcharts-text-outline" === m.getAttribute("class") && D(b, this.element.removeChild(m));
          }
        },
        symbolCustomAttribs: "x y width height r start end innerR anchorX anchorY rounded".split(" "),
        attr: function attr(b, a, f, c) {
          var m = this.element,
              p,
              e = this,
              x,
              C,
              l = this.symbolCustomAttribs;

          if ("string" === typeof b && "undefined" !== typeof a) {
            var T = b;
            b = {};
            b[T] = a;
          }

          "string" === typeof b ? e = (this[b + "Getter"] || this._defaultGetter).call(this, b, m) : (n(b, function (a, f) {
            x = !1;
            c || z(this, f);
            this.symbolName && -1 !== N(f, l) && (p || (this.symbolAttr(b), p = !0), x = !0);
            !this.rotation || "x" !== f && "y" !== f || (this.doTransform = !0);
            x || (C = this[f + "Setter"] || this._defaultSetter, C.call(this, a, f, m), !this.styledMode && this.shadows && /^(width|height|visibility|x|y|d|transform|cx|cy|r)$/.test(f) && this.updateShadows(f, a, C));
          }, this), this.afterSetters());
          f && f.call(this);
          return e;
        },
        afterSetters: function afterSetters() {
          this.doTransform && (this.updateTransform(), this.doTransform = !1);
        },
        updateShadows: function updateShadows(b, a, f) {
          for (var m = this.shadows, c = m.length; c--;) {
            f.call(m[c], "height" === b ? Math.max(a - (m[c].cutHeight || 0), 0) : "d" === b ? this.d : a, b, m[c]);
          }
        },
        addClass: function addClass(b, a) {
          var m = a ? "" : this.attr("class") || "";
          b = (b || "").split(/ /g).reduce(function (b, a) {
            -1 === m.indexOf(a) && b.push(a);
            return b;
          }, m ? [m] : []).join(" ");
          b !== m && this.attr("class", b);
          return this;
        },
        hasClass: function hasClass(b) {
          return -1 !== (this.attr("class") || "").split(" ").indexOf(b);
        },
        removeClass: function removeClass(b) {
          return this.attr("class", (this.attr("class") || "").replace(c(b) ? new RegExp(" ?" + b + " ?") : b, ""));
        },
        symbolAttr: function symbolAttr(b) {
          var a = this;
          "x y r start end width height innerR anchorX anchorY clockwise".split(" ").forEach(function (m) {
            a[m] = f(b[m], a[m]);
          });
          a.attr({
            d: a.renderer.symbols[a.symbolName](a.x, a.y, a.width, a.height, a)
          });
        },
        clip: function clip(b) {
          return this.attr("clip-path", b ? "url(" + this.renderer.url + "#" + b.id + ")" : "none");
        },
        crisp: function crisp(b, a) {
          a = a || b.strokeWidth || 0;
          var m = Math.round(a) % 2 / 2;
          b.x = Math.floor(b.x || this.x || 0) + m;
          b.y = Math.floor(b.y || this.y || 0) + m;
          b.width = Math.floor((b.width || this.width || 0) - 2 * m);
          b.height = Math.floor((b.height || this.height || 0) - 2 * m);
          y(b.strokeWidth) && (b.strokeWidth = a);
          return b;
        },
        css: function css(b) {
          var m = this.styles,
              f = {},
              c = this.element,
              p = "",
              e = !m,
              C = ["textOutline", "textOverflow", "width"];
          b && b.color && (b.fill = b.color);
          m && n(b, function (b, a) {
            b !== m[a] && (f[a] = b, e = !0);
          });

          if (e) {
            m && (b = h(m, f));
            if (b) if (null === b.width || "auto" === b.width) delete this.textWidth;else if ("text" === c.nodeName.toLowerCase() && b.width) var l = this.textWidth = a(b.width);
            this.styles = b;
            l && !x && this.renderer.forExport && delete b.width;

            if (c.namespaceURI === this.SVG_NS) {
              var k = function k(b, a) {
                return "-" + a.toLowerCase();
              };

              n(b, function (b, a) {
                -1 === C.indexOf(a) && (p += a.replace(/([A-Z])/g, k) + ":" + b + ";");
              });
              p && A(c, "style", p);
            } else J(c, b);

            this.added && ("text" === this.element.nodeName && this.renderer.buildText(this), b && b.textOutline && this.applyTextOutline(b.textOutline));
          }

          return this;
        },
        getStyle: function getStyle(b) {
          return X.getComputedStyle(this.element || this, "").getPropertyValue(b);
        },
        strokeWidth: function strokeWidth() {
          if (!this.renderer.styledMode) return this["stroke-width"] || 0;
          var b = this.getStyle("stroke-width"),
              f = 0;
          if (b.indexOf("px") === b.length - 2) f = a(b);else if ("" !== b) {
            var c = Q.createElementNS(R, "rect");
            A(c, {
              width: b,
              "stroke-width": 0
            });
            this.element.parentNode.appendChild(c);
            f = c.getBBox().width;
            c.parentNode.removeChild(c);
          }
          return f;
        },
        on: function on(b, a) {
          var m,
              f,
              c = this.element,
              p;
          H && "click" === b ? (c.ontouchstart = function (b) {
            m = b.touches[0].clientX;
            f = b.touches[0].clientY;
          }, c.ontouchend = function (b) {
            m && 4 <= Math.sqrt(Math.pow(m - b.changedTouches[0].clientX, 2) + Math.pow(f - b.changedTouches[0].clientY, 2)) || a.call(c, b);
            p = !0;
            b.preventDefault();
          }, c.onclick = function (b) {
            p || a.call(c, b);
          }) : c["on" + b] = a;
          return this;
        },
        setRadialReference: function setRadialReference(b) {
          var a = this.renderer.gradients[this.element.gradient];
          this.element.radialReference = b;
          a && a.radAttr && a.animate(this.renderer.getRadialAttr(b, a.radAttr));
          return this;
        },
        translate: function translate(b, a) {
          return this.attr({
            translateX: b,
            translateY: a
          });
        },
        invert: function invert(b) {
          this.inverted = b;
          this.updateTransform();
          return this;
        },
        updateTransform: function updateTransform() {
          var b = this.translateX || 0,
              a = this.translateY || 0,
              c = this.scaleX,
              p = this.scaleY,
              e = this.inverted,
              x = this.rotation,
              C = this.matrix,
              l = this.element;
          e && (b += this.width, a += this.height);
          b = ["translate(" + b + "," + a + ")"];
          y(C) && b.push("matrix(" + C.join(",") + ")");
          e ? b.push("rotate(90) scale(-1,1)") : x && b.push("rotate(" + x + " " + f(this.rotationOriginX, l.getAttribute("x"), 0) + " " + f(this.rotationOriginY, l.getAttribute("y") || 0) + ")");
          (y(c) || y(p)) && b.push("scale(" + f(c, 1) + " " + f(p, 1) + ")");
          b.length && l.setAttribute("transform", b.join(" "));
        },
        toFront: function toFront() {
          var b = this.element;
          b.parentNode.appendChild(b);
          return this;
        },
        align: function align(b, a, p) {
          var m,
              e = {};
          var x = this.renderer;
          var C = x.alignedObjects;
          var l, k;

          if (b) {
            if (this.alignOptions = b, this.alignByTranslate = a, !p || c(p)) this.alignTo = m = p || "renderer", D(C, this), C.push(this), p = null;
          } else b = this.alignOptions, a = this.alignByTranslate, m = this.alignTo;

          p = f(p, x[m], x);
          m = b.align;
          x = b.verticalAlign;
          C = (p.x || 0) + (b.x || 0);
          var T = (p.y || 0) + (b.y || 0);
          "right" === m ? l = 1 : "center" === m && (l = 2);
          l && (C += (p.width - (b.width || 0)) / l);
          e[a ? "translateX" : "x"] = Math.round(C);
          "bottom" === x ? k = 1 : "middle" === x && (k = 2);
          k && (T += (p.height - (b.height || 0)) / k);
          e[a ? "translateY" : "y"] = Math.round(T);
          this[this.placed ? "animate" : "attr"](e);
          this.placed = !0;
          this.alignAttr = e;
          return this;
        },
        getBBox: function getBBox(b, a) {
          var m,
              c = this.renderer,
              p = this.element,
              e = this.styles,
              x = this.textStr,
              C,
              l = c.cache,
              k = c.cacheKeys,
              T = p.namespaceURI === this.SVG_NS;
          a = f(a, this.rotation, 0);
          var n = c.styledMode ? p && U.prototype.getStyle.call(p, "font-size") : e && e.fontSize;

          if (y(x)) {
            var v = x.toString();
            -1 === v.indexOf("<") && (v = v.replace(/[0-9]/g, "0"));
            v += ["", a, n, this.textWidth, e && e.textOverflow].join();
          }

          v && !b && (m = l[v]);

          if (!m) {
            if (T || c.forExport) {
              try {
                (C = this.fakeTS && function (b) {
                  [].forEach.call(p.querySelectorAll(".highcharts-text-outline"), function (a) {
                    a.style.display = b;
                  });
                }) && C("none"), m = p.getBBox ? h({}, p.getBBox()) : {
                  width: p.offsetWidth,
                  height: p.offsetHeight
                }, C && C("");
              } catch (ea) {
              }

              if (!m || 0 > m.width) m = {
                width: 0,
                height: 0
              };
            } else m = this.htmlGetBBox();

            c.isSVG && (b = m.width, c = m.height, T && (m.height = c = {
              "11px,17": 14,
              "13px,20": 16
            }[e && e.fontSize + "," + Math.round(c)] || c), a && (e = a * L, m.width = Math.abs(c * Math.sin(e)) + Math.abs(b * Math.cos(e)), m.height = Math.abs(c * Math.cos(e)) + Math.abs(b * Math.sin(e))));

            if (v && 0 < m.height) {
              for (; 250 < k.length;) {
                delete l[k.shift()];
              }

              l[v] || k.push(v);
              l[v] = m;
            }
          }

          return m;
        },
        show: function show(b) {
          return this.attr({
            visibility: b ? "inherit" : "visible"
          });
        },
        hide: function hide(b) {
          b ? this.attr({
            y: -9999
          }) : this.attr({
            visibility: "hidden"
          });
          return this;
        },
        fadeOut: function fadeOut(b) {
          var a = this;
          a.animate({
            opacity: 0
          }, {
            duration: b || 150,
            complete: function complete() {
              a.attr({
                y: -9999
              });
            }
          });
        },
        add: function add(b) {
          var a = this.renderer,
              m = this.element;
          b && (this.parentGroup = b);
          this.parentInverted = b && b.inverted;
          "undefined" !== typeof this.textStr && a.buildText(this);
          this.added = !0;
          if (!b || b.handleZ || this.zIndex) var f = this.zIndexSetter();
          f || (b ? b.element : a.box).appendChild(m);
          if (this.onAdd) this.onAdd();
          return this;
        },
        safeRemoveChild: function safeRemoveChild(b) {
          var a = b.parentNode;
          a && a.removeChild(b);
        },
        destroy: function destroy() {
          var b = this,
              a = b.element || {},
              f = b.renderer,
              c = f.isSVG && "SPAN" === a.nodeName && b.parentGroup,
              p = a.ownerSVGElement,
              e = b.clipPath;
          a.onclick = a.onmouseout = a.onmouseover = a.onmousemove = a.point = null;
          z(b);
          e && p && ([].forEach.call(p.querySelectorAll("[clip-path],[CLIP-PATH]"), function (b) {
            -1 < b.getAttribute("clip-path").indexOf(e.element.id) && b.removeAttribute("clip-path");
          }), b.clipPath = e.destroy());

          if (b.stops) {
            for (p = 0; p < b.stops.length; p++) {
              b.stops[p] = b.stops[p].destroy();
            }

            b.stops = null;
          }

          b.safeRemoveChild(a);

          for (f.styledMode || b.destroyShadows(); c && c.div && 0 === c.div.childNodes.length;) {
            a = c.parentGroup, b.safeRemoveChild(c.div), delete c.div, c = a;
          }

          b.alignTo && D(f.alignedObjects, b);
          n(b, function (a, m) {
            b[m] && b[m].parentGroup === b && b[m].destroy && b[m].destroy();
            delete b[m];
          });
        },
        shadow: function shadow(b, a, c) {
          var m = [],
              p,
              e = this.element;
          if (!b) this.destroyShadows();else if (!this.shadows) {
            var x = f(b.width, 3);
            var C = (b.opacity || .15) / x;
            var l = this.parentInverted ? "(-1,-1)" : "(" + f(b.offsetX, 1) + ", " + f(b.offsetY, 1) + ")";

            for (p = 1; p <= x; p++) {
              var k = e.cloneNode(0);
              var n = 2 * x + 1 - 2 * p;
              A(k, {
                stroke: b.color || "#000000",
                "stroke-opacity": C * p,
                "stroke-width": n,
                transform: "translate" + l,
                fill: "none"
              });
              k.setAttribute("class", (k.getAttribute("class") || "") + " highcharts-shadow");
              c && (A(k, "height", Math.max(A(k, "height") - n, 0)), k.cutHeight = n);
              a ? a.element.appendChild(k) : e.parentNode && e.parentNode.insertBefore(k, e);
              m.push(k);
            }

            this.shadows = m;
          }
          return this;
        },
        destroyShadows: function destroyShadows() {
          (this.shadows || []).forEach(function (b) {
            this.safeRemoveChild(b);
          }, this);
          this.shadows = void 0;
        },
        xGetter: function xGetter(b) {
          "circle" === this.element.nodeName && ("x" === b ? b = "cx" : "y" === b && (b = "cy"));
          return this._defaultGetter(b);
        },
        _defaultGetter: function _defaultGetter(b) {
          b = f(this[b + "Value"], this[b], this.element ? this.element.getAttribute(b) : null, 0);
          /^[\-0-9\.]+$/.test(b) && (b = parseFloat(b));
          return b;
        },
        dSetter: function dSetter(b, a, f) {
          b && b.join && (b = b.join(" "));
          /(NaN| {2}|^$)/.test(b) && (b = "M 0 0");
          this[a] !== b && (f.setAttribute(a, b), this[a] = b);
        },
        dashstyleSetter: function dashstyleSetter(b) {
          var f,
              c = this["stroke-width"];
          "inherit" === c && (c = 1);

          if (b = b && b.toLowerCase()) {
            b = b.replace("shortdashdotdot", "3,1,1,1,1,1,").replace("shortdashdot", "3,1,1,1").replace("shortdot", "1,1,").replace("shortdash", "3,1,").replace("longdash", "8,3,").replace(/dot/g, "1,3,").replace("dash", "4,3,").replace(/,$/, "").split(",");

            for (f = b.length; f--;) {
              b[f] = a(b[f]) * c;
            }

            b = b.join(",").replace(/NaN/g, "none");
            this.element.setAttribute("stroke-dasharray", b);
          }
        },
        alignSetter: function alignSetter(b) {
          var a = {
            left: "start",
            center: "middle",
            right: "end"
          };
          a[b] && (this.alignValue = b, this.element.setAttribute("text-anchor", a[b]));
        },
        opacitySetter: function opacitySetter(b, a, f) {
          this[a] = b;
          f.setAttribute(a, b);
        },
        titleSetter: function titleSetter(b) {
          var a = this.element.getElementsByTagName("title")[0];
          a || (a = Q.createElementNS(this.SVG_NS, "title"), this.element.appendChild(a));
          a.firstChild && a.removeChild(a.firstChild);
          a.appendChild(Q.createTextNode(String(f(b, "")).replace(/<[^>]*>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">")));
        },
        textSetter: function textSetter(b) {
          b !== this.textStr && (delete this.bBox, delete this.textPxLength, this.textStr = b, this.added && this.renderer.buildText(this));
        },
        setTextPath: function setTextPath(b, a) {
          var f = this.element,
              c = {
            textAnchor: "text-anchor"
          },
              m = !1,
              p = this.textPathWrapper,
              e = !p;
          a = k(!0, {
            enabled: !0,
            attributes: {
              dy: -5,
              startOffset: "50%",
              textAnchor: "middle"
            }
          }, a);
          var x = a.attributes;

          if (b && a && a.enabled) {
            p && null === p.element.parentNode ? (e = !0, p = p.destroy()) : p && this.removeTextOutline.call(p.parentGroup, [].slice.call(f.getElementsByTagName("tspan")));
            this.options && this.options.padding && (x.dx = -this.options.padding);
            p || (this.textPathWrapper = p = this.renderer.createElement("textPath"), m = !0);
            var l = p.element;
            (a = b.element.getAttribute("id")) || b.element.setAttribute("id", a = w());
            if (e) for (b = f.getElementsByTagName("tspan"); b.length;) {
              b[0].setAttribute("y", 0), P(x.dx) && b[0].setAttribute("x", -x.dx), l.appendChild(b[0]);
            }
            m && p.add({
              element: this.text ? this.text.element : f
            });
            l.setAttributeNS("http://www.w3.org/1999/xlink", "href", this.renderer.url + "#" + a);
            y(x.dy) && (l.parentNode.setAttribute("dy", x.dy), delete x.dy);
            y(x.dx) && (l.parentNode.setAttribute("dx", x.dx), delete x.dx);
            n(x, function (b, a) {
              l.setAttribute(c[a] || a, b);
            });
            f.removeAttribute("transform");
            this.removeTextOutline.call(p, [].slice.call(f.getElementsByTagName("tspan")));
            this.text && !this.renderer.styledMode && this.attr({
              fill: "none",
              "stroke-width": 0
            });
            this.applyTextOutline = this.updateTransform = C;
          } else p && (delete this.updateTransform, delete this.applyTextOutline, this.destroyTextPath(f, b), this.updateTransform(), this.options.rotation && this.applyTextOutline(this.options.style.textOutline));

          return this;
        },
        destroyTextPath: function destroyTextPath(b, a) {
          var f = b.getElementsByTagName("text")[0];

          if (f) {
            if (f.removeAttribute("dx"), f.removeAttribute("dy"), a.element.setAttribute("id", ""), f.getElementsByTagName("textPath").length) {
              for (b = this.textPathWrapper.element.childNodes; b.length;) {
                f.appendChild(b[0]);
              }

              f.removeChild(this.textPathWrapper.element);
            }
          } else if (b.getAttribute("dx") || b.getAttribute("dy")) b.removeAttribute("dx"), b.removeAttribute("dy");

          this.textPathWrapper = this.textPathWrapper.destroy();
        },
        fillSetter: function fillSetter(b, a, f) {
          "string" === typeof b ? f.setAttribute(a, b) : b && this.complexColor(b, a, f);
        },
        visibilitySetter: function visibilitySetter(b, a, f) {
          "inherit" === b ? f.removeAttribute(a) : this[a] !== b && f.setAttribute(a, b);
          this[a] = b;
        },
        zIndexSetter: function zIndexSetter(b, f) {
          var c = this.renderer,
              m = this.parentGroup,
              p = (m || c).element || c.box,
              e = this.element,
              x = !1;
          c = p === c.box;
          var C = this.added;
          var l;
          y(b) ? (e.setAttribute("data-z-index", b), b = +b, this[f] === b && (C = !1)) : y(this[f]) && e.removeAttribute("data-z-index");
          this[f] = b;

          if (C) {
            (b = this.zIndex) && m && (m.handleZ = !0);
            f = p.childNodes;

            for (l = f.length - 1; 0 <= l && !x; l--) {
              m = f[l];
              C = m.getAttribute("data-z-index");
              var k = !y(C);
              if (m !== e) if (0 > b && k && !c && !l) p.insertBefore(e, f[l]), x = !0;else if (a(C) <= b || k && (!y(b) || 0 <= b)) p.insertBefore(e, f[l + 1] || null), x = !0;
            }

            x || (p.insertBefore(e, f[c ? 3 : 0] || null), x = !0);
          }

          return x;
        },
        _defaultSetter: function _defaultSetter(b, a, f) {
          f.setAttribute(a, b);
        }
      });
      U.prototype.yGetter = U.prototype.xGetter;

      U.prototype.translateXSetter = U.prototype.translateYSetter = U.prototype.rotationSetter = U.prototype.verticalAlignSetter = U.prototype.rotationOriginXSetter = U.prototype.rotationOriginYSetter = U.prototype.scaleXSetter = U.prototype.scaleYSetter = U.prototype.matrixSetter = function (b, a) {
        this[a] = b;
        this.doTransform = !0;
      };

      U.prototype["stroke-widthSetter"] = U.prototype.strokeSetter = function (b, a, f) {
        this[a] = b;
        this.stroke && this["stroke-width"] ? (U.prototype.fillSetter.call(this, this.stroke, "stroke", f), f.setAttribute("stroke-width", this["stroke-width"]), this.hasStroke = !0) : "stroke-width" === a && 0 === b && this.hasStroke ? (f.removeAttribute("stroke"), this.hasStroke = !1) : this.renderer.styledMode && this["stroke-width"] && (f.setAttribute("stroke-width", this["stroke-width"]), this.hasStroke = !0);
      };

      g = d.SVGRenderer = function () {
        this.init.apply(this, arguments);
      };

      h(g.prototype, {
        Element: U,
        SVG_NS: R,
        init: function init(a, f, c, p, e, x, C) {
          var m = this.createElement("svg").attr({
            version: "1.1",
            "class": "highcharts-root"
          });
          C || m.css(this.getStyle(p));
          p = m.element;
          a.appendChild(p);
          A(a, "dir", "ltr");
          -1 === a.innerHTML.indexOf("xmlns") && A(p, "xmlns", this.SVG_NS);
          this.isSVG = !0;
          this.box = p;
          this.boxWrapper = m;
          this.alignedObjects = [];
          this.url = (K || b) && Q.getElementsByTagName("base").length ? X.location.href.split("#")[0].replace(/<[^>]*>/g, "").replace(/([\('\)])/g, "\\$1").replace(/ /g, "%20") : "";
          this.createElement("desc").add().element.appendChild(Q.createTextNode("Created with Highcharts 8.0.4"));
          this.defs = this.createElement("defs").add();
          this.allowHTML = x;
          this.forExport = e;
          this.styledMode = C;
          this.gradients = {};
          this.cache = {};
          this.cacheKeys = [];
          this.imgCount = 0;
          this.setSize(f, c, !1);
          var l;
          K && a.getBoundingClientRect && (f = function f() {
            J(a, {
              left: 0,
              top: 0
            });
            l = a.getBoundingClientRect();
            J(a, {
              left: Math.ceil(l.left) - l.left + "px",
              top: Math.ceil(l.top) - l.top + "px"
            });
          }, f(), this.unSubPixelFix = I(X, "resize", f));
        },
        definition: function definition(b) {
          function a(b, c) {
            var p;
            v(b).forEach(function (b) {
              var m = f.createElement(b.tagName),
                  e = {};
              n(b, function (b, a) {
                "tagName" !== a && "children" !== a && "textContent" !== a && (e[a] = b);
              });
              m.attr(e);
              m.add(c || f.defs);
              b.textContent && m.element.appendChild(Q.createTextNode(b.textContent));
              a(b.children || [], m);
              p = m;
            });
            return p;
          }

          var f = this;
          return a(b);
        },
        getStyle: function getStyle(b) {
          return this.style = h({
            fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
            fontSize: "12px"
          }, b);
        },
        setStyle: function setStyle(b) {
          this.boxWrapper.css(this.getStyle(b));
        },
        isHidden: function isHidden() {
          return !this.boxWrapper.getBBox().width;
        },
        destroy: function destroy() {
          var b = this.defs;
          this.box = null;
          this.boxWrapper = this.boxWrapper.destroy();
          t(this.gradients || {});
          this.gradients = null;
          b && (this.defs = b.destroy());
          this.unSubPixelFix && this.unSubPixelFix();
          return this.alignedObjects = null;
        },
        createElement: function createElement(b) {
          var a = new this.Element();
          a.init(this, b);
          return a;
        },
        draw: C,
        getRadialAttr: function getRadialAttr(b, a) {
          return {
            cx: b[0] - b[2] / 2 + a.cx * b[2],
            cy: b[1] - b[2] / 2 + a.cy * b[2],
            r: a.r * b[2]
          };
        },
        truncate: function truncate(b, a, f, c, p, e, x) {
          var m = this,
              C = b.rotation,
              l,
              k = c ? 1 : 0,
              F = (f || c).length,
              n = F,
              v = [],
              w = function w(b) {
            a.firstChild && a.removeChild(a.firstChild);
            b && a.appendChild(Q.createTextNode(b));
          },
              z = function z(e, l) {
            l = l || e;
            if ("undefined" === typeof v[l]) if (a.getSubStringLength) try {
              v[l] = p + a.getSubStringLength(0, c ? l + 1 : l);
            } catch (la) {
            } else m.getSpanWidth && (w(x(f || c, e)), v[l] = p + m.getSpanWidth(b, a));
            return v[l];
          },
              O;

          b.rotation = 0;
          var T = z(a.textContent.length);

          if (O = p + T > e) {
            for (; k <= F;) {
              n = Math.ceil((k + F) / 2), c && (l = x(c, n)), T = z(n, l && l.length - 1), k === F ? k = F + 1 : T > e ? F = n - 1 : k = n;
            }

            0 === F ? w("") : f && F === f.length - 1 || w(l || x(f || c, n));
          }

          c && c.splice(0, n);
          b.actualWidth = T;
          b.rotation = C;
          return O;
        },
        escapes: {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          "'": "&#39;",
          '"': "&quot;"
        },
        buildText: function buildText(b) {
          var c = b.element,
              p = this,
              e = p.forExport,
              m = f(b.textStr, "").toString(),
              l = -1 !== m.indexOf("<"),
              C = c.childNodes,
              k,
              v = A(c, "x"),
              w = b.styles,
              z = b.textWidth,
              O = w && w.lineHeight,
              q = w && w.textOutline,
              B = w && "ellipsis" === w.textOverflow,
              d = w && "nowrap" === w.whiteSpace,
              N = w && w.fontSize,
              X,
              h = C.length;
          w = z && !b.added && this.box;

          var L = function L(b) {
            var f;
            p.styledMode || (f = /(px|em)$/.test(b && b.style.fontSize) ? b.style.fontSize : N || p.style.fontSize || 12);
            return O ? a(O) : p.fontMetrics(f, b.getAttribute("style") ? b : c).h;
          },
              t = function t(b, a) {
            n(p.escapes, function (f, c) {
              a && -1 !== a.indexOf(f) || (b = b.toString().replace(new RegExp(f, "g"), c));
            });
            return b;
          },
              U = function U(b, a) {
            var f = b.indexOf("<");
            b = b.substring(f, b.indexOf(">") - f);
            f = b.indexOf(a + "=");
            if (-1 !== f && (f = f + a.length + 1, a = b.charAt(f), '"' === a || "'" === a)) return b = b.substring(f + 1), b.substring(0, b.indexOf(a));
          },
              P = /<br.*?>/g;

          var g = [m, B, d, O, q, N, z].join();

          if (g !== b.textCache) {
            for (b.textCache = g; h--;) {
              c.removeChild(C[h]);
            }

            l || q || B || z || -1 !== m.indexOf(" ") && (!d || P.test(m)) ? (w && w.appendChild(c), l ? (m = p.styledMode ? m.replace(/<(b|strong)>/g, '<span class="highcharts-strong">').replace(/<(i|em)>/g, '<span class="highcharts-emphasized">') : m.replace(/<(b|strong)>/g, '<span style="font-weight:bold">').replace(/<(i|em)>/g, '<span style="font-style:italic">'), m = m.replace(/<a/g, "<span").replace(/<\/(b|strong|i|em|a)>/g, "</span>").split(P)) : m = [m], m = m.filter(function (b) {
              return "" !== b;
            }), m.forEach(function (a, f) {
              var m = 0,
                  l = 0;
              a = a.replace(/^\s+|\s+$/g, "").replace(/<span/g, "|||<span").replace(/<\/span>/g, "</span>|||");
              var C = a.split("|||");
              C.forEach(function (a) {
                if ("" !== a || 1 === C.length) {
                  var n = {},
                      F = Q.createElementNS(p.SVG_NS, "tspan"),
                      w,
                      O;
                  (w = U(a, "class")) && A(F, "class", w);
                  if (w = U(a, "style")) w = w.replace(/(;| |^)color([ :])/, "$1fill$2"), A(F, "style", w);
                  (O = U(a, "href")) && !e && (A(F, "onclick", 'location.href="' + O + '"'), A(F, "class", "highcharts-anchor"), p.styledMode || J(F, {
                    cursor: "pointer"
                  }));
                  a = t(a.replace(/<[a-zA-Z\/](.|\n)*?>/g, "") || " ");

                  if (" " !== a) {
                    F.appendChild(Q.createTextNode(a));
                    m ? n.dx = 0 : f && null !== v && (n.x = v);
                    A(F, n);
                    c.appendChild(F);
                    !m && X && (!x && e && J(F, {
                      display: "block"
                    }), A(F, "dy", L(F)));

                    if (z) {
                      var q = a.replace(/([^\^])-/g, "$1- ").split(" ");
                      n = !d && (1 < C.length || f || 1 < q.length);
                      O = 0;
                      var T = L(F);
                      if (B) k = p.truncate(b, F, a, void 0, 0, Math.max(0, z - parseInt(N || 12, 10)), function (b, a) {
                        return b.substring(0, a) + "\u2026";
                      });else if (n) for (; q.length;) {
                        q.length && !d && 0 < O && (F = Q.createElementNS(R, "tspan"), A(F, {
                          dy: T,
                          x: v
                        }), w && A(F, "style", w), F.appendChild(Q.createTextNode(q.join(" ").replace(/- /g, "-"))), c.appendChild(F)), p.truncate(b, F, null, q, 0 === O ? l : 0, z, function (b, a) {
                          return q.slice(0, a).join(" ").replace(/- /g, "-");
                        }), l = b.actualWidth, O++;
                      }
                    }

                    m++;
                  }
                }
              });
              X = X || c.childNodes.length;
            }), B && k && b.attr("title", t(b.textStr, ["&lt;", "&gt;"])), w && w.removeChild(c), q && b.applyTextOutline && b.applyTextOutline(q)) : c.appendChild(Q.createTextNode(t(m)));
          }
        },
        getContrast: function getContrast(b) {
          b = u(b).rgba;
          b[0] *= 1;
          b[1] *= 1.2;
          b[2] *= .5;
          return 459 < b[0] + b[1] + b[2] ? "#000000" : "#FFFFFF";
        },
        button: function button(b, a, f, c, e, x, l, C, n, w) {
          var m = this.label(b, a, f, n, null, null, w, null, "button"),
              F = 0,
              v = this.styledMode;
          m.attr(k({
            padding: 8,
            r: 2
          }, e));

          if (!v) {
            e = k({
              fill: "#f7f7f7",
              stroke: "#cccccc",
              "stroke-width": 1,
              style: {
                color: "#333333",
                cursor: "pointer",
                fontWeight: "normal"
              }
            }, e);
            var z = e.style;
            delete e.style;
            x = k(e, {
              fill: "#e6e6e6"
            }, x);
            var O = x.style;
            delete x.style;
            l = k(e, {
              fill: "#e6ebf5",
              style: {
                color: "#000000",
                fontWeight: "bold"
              }
            }, l);
            var q = l.style;
            delete l.style;
            C = k(e, {
              style: {
                color: "#cccccc"
              }
            }, C);
            var R = C.style;
            delete C.style;
          }

          I(m.element, p ? "mouseover" : "mouseenter", function () {
            3 !== F && m.setState(1);
          });
          I(m.element, p ? "mouseout" : "mouseleave", function () {
            3 !== F && m.setState(F);
          });

          m.setState = function (b) {
            1 !== b && (m.state = F = b);
            m.removeClass(/highcharts-button-(normal|hover|pressed|disabled)/).addClass("highcharts-button-" + ["normal", "hover", "pressed", "disabled"][b || 0]);
            v || m.attr([e, x, l, C][b || 0]).css([z, O, q, R][b || 0]);
          };

          v || m.attr(e).css(h({
            cursor: "default"
          }, z));
          return m.on("click", function (b) {
            3 !== F && c.call(m, b);
          });
        },
        crispLine: function crispLine(b, a) {
          b[1] === b[4] && (b[1] = b[4] = Math.round(b[1]) - a % 2 / 2);
          b[2] === b[5] && (b[2] = b[5] = Math.round(b[2]) + a % 2 / 2);
          return b;
        },
        path: function path(b) {
          var a = this.styledMode ? {} : {
            fill: "none"
          };
          q(b) ? a.d = b : e(b) && h(a, b);
          return this.createElement("path").attr(a);
        },
        circle: function circle(b, a, f) {
          b = e(b) ? b : "undefined" === typeof b ? {} : {
            x: b,
            y: a,
            r: f
          };
          a = this.createElement("circle");

          a.xSetter = a.ySetter = function (b, a, f) {
            f.setAttribute("c" + a, b);
          };

          return a.attr(b);
        },
        arc: function arc(b, a, f, c, p, x) {
          e(b) ? (c = b, a = c.y, f = c.r, b = c.x) : c = {
            innerR: c,
            start: p,
            end: x
          };
          b = this.symbol("arc", b, a, f, f, c);
          b.r = f;
          return b;
        },
        rect: function rect(b, a, f, c, p, x) {
          p = e(b) ? b.r : p;
          var m = this.createElement("rect");
          b = e(b) ? b : "undefined" === typeof b ? {} : {
            x: b,
            y: a,
            width: Math.max(f, 0),
            height: Math.max(c, 0)
          };
          this.styledMode || ("undefined" !== typeof x && (b.strokeWidth = x, b = m.crisp(b)), b.fill = "none");
          p && (b.r = p);

          m.rSetter = function (b, a, f) {
            m.r = b;
            A(f, {
              rx: b,
              ry: b
            });
          };

          m.rGetter = function () {
            return m.r;
          };

          return m.attr(b);
        },
        setSize: function setSize(b, a, c) {
          var p = this.alignedObjects,
              e = p.length;
          this.width = b;
          this.height = a;

          for (this.boxWrapper.animate({
            width: b,
            height: a
          }, {
            step: function step() {
              this.attr({
                viewBox: "0 0 " + this.attr("width") + " " + this.attr("height")
              });
            },
            duration: f(c, !0) ? void 0 : 0
          }); e--;) {
            p[e].align();
          }
        },
        g: function g(b) {
          var a = this.createElement("g");
          return b ? a.attr({
            "class": "highcharts-" + b
          }) : a;
        },
        image: function image(b, a, f, c, p, e) {
          var x = {
            preserveAspectRatio: "none"
          },
              m = function m(b, a) {
            b.setAttributeNS ? b.setAttributeNS("http://www.w3.org/1999/xlink", "href", a) : b.setAttribute("hc-svg-href", a);
          },
              l = function l(a) {
            m(C.element, b);
            e.call(C, a);
          };

          1 < arguments.length && h(x, {
            x: a,
            y: f,
            width: c,
            height: p
          });
          var C = this.createElement("image").attr(x);
          e ? (m(C.element, "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="), x = new X.Image(), I(x, "load", l), x.src = b, x.complete && l({})) : m(C.element, b);
          return C;
        },
        symbol: function symbol(b, a, c, p, e, x) {
          var m = this,
              l = /^url\((.*?)\)$/,
              C = l.test(b),
              k = !C && (this.symbols[b] ? b : "circle"),
              n = k && this.symbols[k],
              F = y(a) && n && n.call(this.symbols, Math.round(a), Math.round(c), p, e, x);

          if (n) {
            var w = this.path(F);
            m.styledMode || w.attr("fill", "none");
            h(w, {
              symbolName: k,
              x: a,
              y: c,
              width: p,
              height: e
            });
            x && h(w, x);
          } else if (C) {
            var v = b.match(l)[1];
            w = this.image(v);
            w.imgwidth = f(O[v] && O[v].width, x && x.width);
            w.imgheight = f(O[v] && O[v].height, x && x.height);

            var z = function z() {
              w.attr({
                width: w.width,
                height: w.height
              });
            };

            ["width", "height"].forEach(function (b) {
              w[b + "Setter"] = function (b, a) {
                var f = {},
                    c = this["img" + a],
                    p = "width" === a ? "translateX" : "translateY";
                this[a] = b;
                y(c) && (x && "within" === x.backgroundSize && this.width && this.height && (c = Math.round(c * Math.min(this.width / this.imgwidth, this.height / this.imgheight))), this.element && this.element.setAttribute(a, c), this.alignByTranslate || (f[p] = ((this[a] || 0) - c) / 2, this.attr(f)));
              };
            });
            y(a) && w.attr({
              x: a,
              y: c
            });
            w.isImg = !0;
            y(w.imgwidth) && y(w.imgheight) ? z() : (w.attr({
              width: 0,
              height: 0
            }), G("img", {
              onload: function onload() {
                var b = B[m.chartIndex];
                0 === this.width && (J(this, {
                  position: "absolute",
                  top: "-999em"
                }), Q.body.appendChild(this));
                O[v] = {
                  width: this.width,
                  height: this.height
                };
                w.imgwidth = this.width;
                w.imgheight = this.height;
                w.element && z();
                this.parentNode && this.parentNode.removeChild(this);
                m.imgCount--;
                if (!m.imgCount && b && !b.hasLoaded) b.onload();
              },
              src: v
            }), this.imgCount++);
          }

          return w;
        },
        symbols: {
          circle: function circle(b, a, f, c) {
            return this.arc(b + f / 2, a + c / 2, f / 2, c / 2, {
              start: .5 * Math.PI,
              end: 2.5 * Math.PI,
              open: !1
            });
          },
          square: function square(b, a, f, c) {
            return ["M", b, a, "L", b + f, a, b + f, a + c, b, a + c, "Z"];
          },
          triangle: function triangle(b, a, f, c) {
            return ["M", b + f / 2, a, "L", b + f, a + c, b, a + c, "Z"];
          },
          "triangle-down": function triangleDown(b, a, f, c) {
            return ["M", b, a, "L", b + f, a, b + f / 2, a + c, "Z"];
          },
          diamond: function diamond(b, a, f, c) {
            return ["M", b + f / 2, a, "L", b + f, a + c / 2, b + f / 2, a + c, b, a + c / 2, "Z"];
          },
          arc: function arc(b, a, c, p, e) {
            var x = e.start,
                m = e.r || c,
                l = e.r || p || c,
                C = e.end - .001;
            c = e.innerR;
            p = f(e.open, .001 > Math.abs(e.end - e.start - 2 * Math.PI));
            var k = Math.cos(x),
                n = Math.sin(x),
                w = Math.cos(C);
            C = Math.sin(C);
            x = f(e.longArc, .001 > e.end - x - Math.PI ? 0 : 1);
            m = ["M", b + m * k, a + l * n, "A", m, l, 0, x, f(e.clockwise, 1), b + m * w, a + l * C];
            y(c) && m.push(p ? "M" : "L", b + c * w, a + c * C, "A", c, c, 0, x, y(e.clockwise) ? 1 - e.clockwise : 0, b + c * k, a + c * n);
            m.push(p ? "" : "Z");
            return m;
          },
          callout: function callout(b, a, f, c, p) {
            var e = Math.min(p && p.r || 0, f, c),
                x = e + 6,
                l = p && p.anchorX;
            p = p && p.anchorY;
            var m = ["M", b + e, a, "L", b + f - e, a, "C", b + f, a, b + f, a, b + f, a + e, "L", b + f, a + c - e, "C", b + f, a + c, b + f, a + c, b + f - e, a + c, "L", b + e, a + c, "C", b, a + c, b, a + c, b, a + c - e, "L", b, a + e, "C", b, a, b, a, b + e, a];
            l && l > f ? p > a + x && p < a + c - x ? m.splice(13, 3, "L", b + f, p - 6, b + f + 6, p, b + f, p + 6, b + f, a + c - e) : m.splice(13, 3, "L", b + f, c / 2, l, p, b + f, c / 2, b + f, a + c - e) : l && 0 > l ? p > a + x && p < a + c - x ? m.splice(33, 3, "L", b, p + 6, b - 6, p, b, p - 6, b, a + e) : m.splice(33, 3, "L", b, c / 2, l, p, b, c / 2, b, a + e) : p && p > c && l > b + x && l < b + f - x ? m.splice(23, 3, "L", l + 6, a + c, l, a + c + 6, l - 6, a + c, b + e, a + c) : p && 0 > p && l > b + x && l < b + f - x && m.splice(3, 3, "L", l - 6, a, l, a - 6, l + 6, a, f - e, a);
            return m;
          }
        },
        clipRect: function clipRect(b, a, f, c) {
          var p = w() + "-",
              e = this.createElement("clipPath").attr({
            id: p
          }).add(this.defs);
          b = this.rect(b, a, f, c, 0).add(e);
          b.id = p;
          b.clipPath = e;
          b.count = 0;
          return b;
        },
        text: function text(b, a, f, c) {
          var p = {};
          if (c && (this.allowHTML || !this.forExport)) return this.html(b, a, f);
          p.x = Math.round(a || 0);
          f && (p.y = Math.round(f));
          y(b) && (p.text = b);
          b = this.createElement("text").attr(p);
          c || (b.xSetter = function (b, a, f) {
            var c = f.getElementsByTagName("tspan"),
                p = f.getAttribute(a),
                e;

            for (e = 0; e < c.length; e++) {
              var x = c[e];
              x.getAttribute(a) === p && x.setAttribute(a, b);
            }

            f.setAttribute(a, b);
          });
          return b;
        },
        fontMetrics: function fontMetrics(b, f) {
          b = !this.styledMode && /px/.test(b) || !X.getComputedStyle ? b || f && f.style && f.style.fontSize || this.style && this.style.fontSize : f && U.prototype.getStyle.call(f, "font-size");
          b = /px/.test(b) ? a(b) : 12;
          f = 24 > b ? b + 3 : Math.round(1.2 * b);
          return {
            h: f,
            b: Math.round(.8 * f),
            f: b
          };
        },
        rotCorr: function rotCorr(b, a, f) {
          var c = b;
          a && f && (c = Math.max(c * Math.cos(a * L), 4));
          return {
            x: -b / 3 * Math.sin(a * L),
            y: c
          };
        },
        label: function label(b, a, f, c, p, e, x, C, n) {
          var m = this,
              w = m.styledMode,
              v = m.g("button" !== n && "label"),
              F = v.text = m.text("", 0, 0, x).attr({
            zIndex: 1
          }),
              z,
              O,
              q = 0,
              R = 3,
              B = 0,
              d,
              N,
              X,
              V,
              L,
              t = {},
              T,
              g,
              Q = /^url\((.*?)\)$/.test(c),
              H = w || Q,
              K = function K() {
            return w ? z.strokeWidth() % 2 / 2 : (T ? parseInt(T, 10) : 0) % 2 / 2;
          };

          n && v.addClass("highcharts-" + n);

          var ba = function ba() {
            var b = F.element.style,
                a = {};
            O = ("undefined" === typeof d || "undefined" === typeof N || L) && y(F.textStr) && F.getBBox();
            v.width = (d || O.width || 0) + 2 * R + B;
            v.height = (N || O.height || 0) + 2 * R;
            g = R + Math.min(m.fontMetrics(b && b.fontSize, F).b, O ? O.height : Infinity);
            H && (z || (v.box = z = m.symbols[c] || Q ? m.symbol(c) : m.rect(), z.addClass(("button" === n ? "" : "highcharts-label-box") + (n ? " highcharts-" + n + "-box" : "")), z.add(v), b = K(), a.x = b, a.y = (C ? -g : 0) + b), a.width = Math.round(v.width), a.height = Math.round(v.height), z.attr(h(a, t)), t = {});
          };

          var u = function u() {
            var b = B + R;
            var a = C ? 0 : g;
            y(d) && O && ("center" === L || "right" === L) && (b += {
              center: .5,
              right: 1
            }[L] * (d - O.width));
            if (b !== F.x || a !== F.y) F.attr("x", b), F.hasBoxWidthChanged && (O = F.getBBox(!0), ba()), "undefined" !== typeof a && F.attr("y", a);
            F.x = b;
            F.y = a;
          };

          var Y = function Y(b, a) {
            z ? z.attr(b, a) : t[b] = a;
          };

          v.onAdd = function () {
            F.add(v);
            v.attr({
              text: b || 0 === b ? b : "",
              x: a,
              y: f
            });
            z && y(p) && v.attr({
              anchorX: p,
              anchorY: e
            });
          };

          v.widthSetter = function (b) {
            d = P(b) ? b : null;
          };

          v.heightSetter = function (b) {
            N = b;
          };

          v["text-alignSetter"] = function (b) {
            L = b;
          };

          v.paddingSetter = function (b) {
            y(b) && b !== R && (R = v.padding = b, u());
          };

          v.paddingLeftSetter = function (b) {
            y(b) && b !== B && (B = b, u());
          };

          v.alignSetter = function (b) {
            b = {
              left: 0,
              center: .5,
              right: 1
            }[b];
            b !== q && (q = b, O && v.attr({
              x: X
            }));
          };

          v.textSetter = function (b) {
            "undefined" !== typeof b && F.attr({
              text: b
            });
            ba();
            u();
          };

          v["stroke-widthSetter"] = function (b, a) {
            b && (H = !0);
            T = this["stroke-width"] = b;
            Y(a, b);
          };

          w ? v.rSetter = function (b, a) {
            Y(a, b);
          } : v.strokeSetter = v.fillSetter = v.rSetter = function (b, a) {
            "r" !== a && ("fill" === a && b && (H = !0), v[a] = b);
            Y(a, b);
          };

          v.anchorXSetter = function (b, a) {
            p = v.anchorX = b;
            Y(a, Math.round(b) - K() - X);
          };

          v.anchorYSetter = function (b, a) {
            e = v.anchorY = b;
            Y(a, b - V);
          };

          v.xSetter = function (b) {
            v.x = b;
            q && (b -= q * ((d || O.width) + 2 * R), v["forceAnimate:x"] = !0);
            X = Math.round(b);
            v.attr("translateX", X);
          };

          v.ySetter = function (b) {
            V = v.y = Math.round(b);
            v.attr("translateY", V);
          };

          var D = v.css;
          x = {
            css: function css(b) {
              if (b) {
                var a = {};
                b = k(b);
                v.textProps.forEach(function (f) {
                  "undefined" !== typeof b[f] && (a[f] = b[f], delete b[f]);
                });
                F.css(a);
                "width" in a && ba();
                "fontSize" in a && (ba(), u());
              }

              return D.call(v, b);
            },
            getBBox: function getBBox() {
              return {
                width: O.width + 2 * R,
                height: O.height + 2 * R,
                x: O.x - R,
                y: O.y - R
              };
            },
            destroy: function destroy() {
              l(v.element, "mouseenter");
              l(v.element, "mouseleave");
              F && (F = F.destroy());
              z && (z = z.destroy());
              U.prototype.destroy.call(v);
              v = m = ba = u = Y = null;
            }
          };
          w || (x.shadow = function (b) {
            b && (ba(), z && z.shadow(b));
            return v;
          });
          return h(v, x);
        }
      });
      d.Renderer = g;
    });
    S(r, "parts/Html.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.attr,
          u = g.createElement,
          I = g.css,
          M = g.defined,
          E = g.extend,
          A = g.pick,
          G = g.pInt,
          J = d.isFirefox,
          y = d.isMS,
          t = d.isWebKit,
          D = d.SVGElement;
      g = d.SVGRenderer;
      var h = d.win;
      E(D.prototype, {
        htmlCss: function htmlCss(d) {
          var q = "SPAN" === this.element.tagName && d && "width" in d,
              N = A(q && d.width, void 0);

          if (q) {
            delete d.width;
            this.textWidth = N;
            var e = !0;
          }

          d && "ellipsis" === d.textOverflow && (d.whiteSpace = "nowrap", d.overflow = "hidden");
          this.styles = E(this.styles, d);
          I(this.element, d);
          e && this.htmlUpdateTransform();
          return this;
        },
        htmlGetBBox: function htmlGetBBox() {
          var d = this.element;
          return {
            x: d.offsetLeft,
            y: d.offsetTop,
            width: d.offsetWidth,
            height: d.offsetHeight
          };
        },
        htmlUpdateTransform: function htmlUpdateTransform() {
          if (this.added) {
            var d = this.renderer,
                q = this.element,
                h = this.translateX || 0,
                e = this.translateY || 0,
                c = this.x || 0,
                k = this.y || 0,
                n = this.textAlign || "left",
                f = {
              left: 0,
              center: .5,
              right: 1
            }[n],
                a = this.styles,
                l = a && a.whiteSpace;
            I(q, {
              marginLeft: h,
              marginTop: e
            });
            !d.styledMode && this.shadows && this.shadows.forEach(function (a) {
              I(a, {
                marginLeft: h + 1,
                marginTop: e + 1
              });
            });
            this.inverted && [].forEach.call(q.childNodes, function (a) {
              d.invertChild(a, q);
            });

            if ("SPAN" === q.tagName) {
              a = this.rotation;
              var v = this.textWidth && G(this.textWidth),
                  z = [a, n, q.innerHTML, this.textWidth, this.textAlign].join(),
                  w;
              (w = v !== this.oldTextWidth) && !(w = v > this.oldTextWidth) && ((w = this.textPxLength) || (I(q, {
                width: "",
                whiteSpace: l || "nowrap"
              }), w = q.offsetWidth), w = w > v);
              w && (/[ \-]/.test(q.textContent || q.innerText) || "ellipsis" === q.style.textOverflow) ? (I(q, {
                width: v + "px",
                display: "block",
                whiteSpace: l || "normal"
              }), this.oldTextWidth = v, this.hasBoxWidthChanged = !0) : this.hasBoxWidthChanged = !1;
              z !== this.cTT && (l = d.fontMetrics(q.style.fontSize, q).b, !M(a) || a === (this.oldRotation || 0) && n === this.oldAlign || this.setSpanRotation(a, f, l), this.getSpanCorrection(!M(a) && this.textPxLength || q.offsetWidth, l, f, a, n));
              I(q, {
                left: c + (this.xCorr || 0) + "px",
                top: k + (this.yCorr || 0) + "px"
              });
              this.cTT = z;
              this.oldRotation = a;
              this.oldAlign = n;
            }
          } else this.alignOnAdd = !0;
        },
        setSpanRotation: function setSpanRotation(d, q, h) {
          var e = {},
              c = this.renderer.getTransformKey();
          e[c] = e.transform = "rotate(" + d + "deg)";
          e[c + (J ? "Origin" : "-origin")] = e.transformOrigin = 100 * q + "% " + h + "px";
          I(this.element, e);
        },
        getSpanCorrection: function getSpanCorrection(d, q, h) {
          this.xCorr = -d * h;
          this.yCorr = -q;
        }
      });
      E(g.prototype, {
        getTransformKey: function getTransformKey() {
          return y && !/Edge/.test(h.navigator.userAgent) ? "-ms-transform" : t ? "-webkit-transform" : J ? "MozTransform" : h.opera ? "-o-transform" : "";
        },
        html: function html(d, q, h) {
          var e = this.createElement("span"),
              c = e.element,
              k = e.renderer,
              n = k.isSVG,
              f = function f(a, _f) {
            ["opacity", "visibility"].forEach(function (c) {
              a[c + "Setter"] = function (e, l, k) {
                var v = a.div ? a.div.style : _f;
                D.prototype[c + "Setter"].call(this, e, l, k);
                v && (v[l] = e);
              };
            });
            a.addedSetters = !0;
          };

          e.textSetter = function (a) {
            a !== c.innerHTML && (delete this.bBox, delete this.oldTextWidth);
            this.textStr = a;
            c.innerHTML = A(a, "");
            e.doTransform = !0;
          };

          n && f(e, e.element.style);

          e.xSetter = e.ySetter = e.alignSetter = e.rotationSetter = function (a, f) {
            "align" === f && (f = "textAlign");
            e[f] = a;
            e.doTransform = !0;
          };

          e.afterSetters = function () {
            this.doTransform && (this.htmlUpdateTransform(), this.doTransform = !1);
          };

          e.attr({
            text: d,
            x: Math.round(q),
            y: Math.round(h)
          }).css({
            position: "absolute"
          });
          k.styledMode || e.css({
            fontFamily: this.style.fontFamily,
            fontSize: this.style.fontSize
          });
          c.style.whiteSpace = "nowrap";
          e.css = e.htmlCss;
          n && (e.add = function (a) {
            var l = k.box.parentNode,
                v = [];

            if (this.parentGroup = a) {
              var n = a.div;

              if (!n) {
                for (; a;) {
                  v.push(a), a = a.parentGroup;
                }

                v.reverse().forEach(function (a) {
                  function c(f, c) {
                    a[c] = f;
                    "translateX" === c ? w.left = f + "px" : w.top = f + "px";
                    a.doTransform = !0;
                  }

                  var k = r(a.element, "class");
                  n = a.div = a.div || u("div", k ? {
                    className: k
                  } : void 0, {
                    position: "absolute",
                    left: (a.translateX || 0) + "px",
                    top: (a.translateY || 0) + "px",
                    display: a.display,
                    opacity: a.opacity,
                    pointerEvents: a.styles && a.styles.pointerEvents
                  }, n || l);
                  var w = n.style;
                  E(a, {
                    classSetter: function (a) {
                      return function (f) {
                        this.element.setAttribute("class", f);
                        a.className = f;
                      };
                    }(n),
                    on: function on() {
                      v[0].div && e.on.apply({
                        element: v[0].div
                      }, arguments);
                      return a;
                    },
                    translateXSetter: c,
                    translateYSetter: c
                  });
                  a.addedSetters || f(a);
                });
              }
            } else n = l;

            n.appendChild(c);
            e.added = !0;
            e.alignOnAdd && e.htmlUpdateTransform();
            return e;
          });
          return e;
        }
      });
    });
    S(r, "parts/Tick.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.clamp,
          u = g.correctFloat,
          I = g.defined,
          M = g.destroyObjectProperties,
          E = g.extend,
          A = g.isNumber,
          G = g.merge,
          J = g.objectEach,
          y = g.pick,
          t = d.fireEvent,
          D = d.deg2rad;

      g = function () {
        function h(d, q, h, e, c) {
          this.isNewLabel = this.isNew = !0;
          this.axis = d;
          this.pos = q;
          this.type = h || "";
          this.parameters = c || {};
          this.tickmarkOffset = this.parameters.tickmarkOffset;
          this.options = this.parameters.options;
          h || e || this.addLabel();
        }

        h.prototype.addLabel = function () {
          var d = this,
              q = d.axis,
              h = q.options,
              e = q.chart,
              c = q.categories,
              k = q.names,
              n = d.pos,
              f = y(d.options && d.options.labels, h.labels),
              a = q.tickPositions,
              l = n === a[0],
              v = n === a[a.length - 1];
          k = this.parameters.category || (c ? y(c[n], k[n], n) : n);
          var z = d.label;
          c = (!f.step || 1 === f.step) && 1 === q.tickInterval;
          a = a.info;
          var w, B;

          if (q.isDatetimeAxis && a) {
            var L = e.time.resolveDTLFormat(h.dateTimeLabelFormats[!h.grid && a.higherRanks[n] || a.unitName]);
            var t = L.main;
          }

          d.isFirst = l;
          d.isLast = v;
          d.formatCtx = {
            axis: q,
            chart: e,
            isFirst: l,
            isLast: v,
            dateTimeLabelFormat: t,
            tickPositionInfo: a,
            value: q.isLog ? u(q.lin2log(k)) : k,
            pos: n
          };
          h = q.labelFormatter.call(d.formatCtx, this.formatCtx);
          if (B = L && L.list) d.shortenLabel = function () {
            for (w = 0; w < B.length; w++) {
              if (z.attr({
                text: q.labelFormatter.call(E(d.formatCtx, {
                  dateTimeLabelFormat: B[w]
                }))
              }), z.getBBox().width < q.getSlotWidth(d) - 2 * y(f.padding, 5)) return;
            }

            z.attr({
              text: ""
            });
          };
          c && q._addedPlotLB && q.isXAxis && d.moveLabel(h, f);
          I(z) || d.movedLabel ? z && z.textStr !== h && !c && (!z.textWidth || f.style && f.style.width || z.styles.width || z.css({
            width: null
          }), z.attr({
            text: h
          }), z.textPxLength = z.getBBox().width) : (d.label = z = d.createLabel({
            x: 0,
            y: 0
          }, h, f), d.rotation = 0);
        };

        h.prototype.createLabel = function (d, q, h) {
          var e = this.axis,
              c = e.chart;
          if (d = I(q) && h.enabled ? c.renderer.text(q, d.x, d.y, h.useHTML).add(e.labelGroup) : null) c.styledMode || d.css(G(h.style)), d.textPxLength = d.getBBox().width;
          return d;
        };

        h.prototype.destroy = function () {
          M(this, this.axis);
        };

        h.prototype.getPosition = function (d, q, h, e) {
          var c = this.axis,
              k = c.chart,
              n = e && k.oldChartHeight || k.chartHeight;
          d = {
            x: d ? u(c.translate(q + h, null, null, e) + c.transB) : c.left + c.offset + (c.opposite ? (e && k.oldChartWidth || k.chartWidth) - c.right - c.left : 0),
            y: d ? n - c.bottom + c.offset - (c.opposite ? c.height : 0) : u(n - c.translate(q + h, null, null, e) - c.transB)
          };
          d.y = r(d.y, -1E5, 1E5);
          t(this, "afterGetPosition", {
            pos: d
          });
          return d;
        };

        h.prototype.getLabelPosition = function (d, q, h, e, c, k, n, f) {
          var a = this.axis,
              l = a.transA,
              v = a.isLinked && a.linkedParent ? a.linkedParent.reversed : a.reversed,
              z = a.staggerLines,
              w = a.tickRotCorr || {
            x: 0,
            y: 0
          },
              B = c.y,
              L = e || a.reserveSpaceDefault ? 0 : -a.labelOffset * ("center" === a.labelAlign ? .5 : 1),
              N = {};
          I(B) || (B = 0 === a.side ? h.rotation ? -8 : -h.getBBox().height : 2 === a.side ? w.y + 8 : Math.cos(h.rotation * D) * (w.y - h.getBBox(!1, 0).height / 2));
          d = d + c.x + L + w.x - (k && e ? k * l * (v ? -1 : 1) : 0);
          q = q + B - (k && !e ? k * l * (v ? 1 : -1) : 0);
          z && (h = n / (f || 1) % z, a.opposite && (h = z - h - 1), q += a.labelOffset / z * h);
          N.x = d;
          N.y = Math.round(q);
          t(this, "afterGetLabelPosition", {
            pos: N,
            tickmarkOffset: k,
            index: n
          });
          return N;
        };

        h.prototype.getLabelSize = function () {
          return this.label ? this.label.getBBox()[this.axis.horiz ? "height" : "width"] : 0;
        };

        h.prototype.getMarkPath = function (d, q, h, e, c, k) {
          return k.crispLine(["M", d, q, "L", d + (c ? 0 : -h), q + (c ? h : 0)], e);
        };

        h.prototype.handleOverflow = function (d) {
          var q = this.axis,
              h = q.options.labels,
              e = d.x,
              c = q.chart.chartWidth,
              k = q.chart.spacing,
              n = y(q.labelLeft, Math.min(q.pos, k[3]));
          k = y(q.labelRight, Math.max(q.isRadial ? 0 : q.pos + q.len, c - k[1]));
          var f = this.label,
              a = this.rotation,
              l = {
            left: 0,
            center: .5,
            right: 1
          }[q.labelAlign || f.attr("align")],
              v = f.getBBox().width,
              z = q.getSlotWidth(this),
              w = z,
              B = 1,
              L,
              t = {};
          if (a || "justify" !== y(h.overflow, "justify")) 0 > a && e - l * v < n ? L = Math.round(e / Math.cos(a * D) - n) : 0 < a && e + l * v > k && (L = Math.round((c - e) / Math.cos(a * D)));else if (c = e + (1 - l) * v, e - l * v < n ? w = d.x + w * (1 - l) - n : c > k && (w = k - d.x + w * l, B = -1), w = Math.min(z, w), w < z && "center" === q.labelAlign && (d.x += B * (z - w - l * (z - Math.min(v, w)))), v > w || q.autoRotation && (f.styles || {}).width) L = w;
          L && (this.shortenLabel ? this.shortenLabel() : (t.width = Math.floor(L), (h.style || {}).textOverflow || (t.textOverflow = "ellipsis"), f.css(t)));
        };

        h.prototype.moveLabel = function (d, q) {
          var h = this,
              e = h.label,
              c = !1,
              k = h.axis,
              n = k.reversed,
              f = k.chart.inverted;
          e && e.textStr === d ? (h.movedLabel = e, c = !0, delete h.label) : J(k.ticks, function (a) {
            c || a.isNew || a === h || !a.label || a.label.textStr !== d || (h.movedLabel = a.label, c = !0, a.labelPos = h.movedLabel.xy, delete a.label);
          });

          if (!c && (h.labelPos || e)) {
            var a = h.labelPos || e.xy;
            e = f ? a.x : n ? 0 : k.width + k.left;
            k = f ? n ? k.width + k.left : 0 : a.y;
            h.movedLabel = h.createLabel({
              x: e,
              y: k
            }, d, q);
            h.movedLabel && h.movedLabel.attr({
              opacity: 0
            });
          }
        };

        h.prototype.render = function (h, q, t) {
          var e = this.axis,
              c = e.horiz,
              k = this.pos,
              n = y(this.tickmarkOffset, e.tickmarkOffset);
          k = this.getPosition(c, k, n, q);
          n = k.x;
          var f = k.y;
          e = c && n === e.pos + e.len || !c && f === e.pos ? -1 : 1;
          t = y(t, 1);
          this.isActive = !0;
          this.renderGridLine(q, t, e);
          this.renderMark(k, t, e);
          this.renderLabel(k, q, t, h);
          this.isNew = !1;
          d.fireEvent(this, "afterRender");
        };

        h.prototype.renderGridLine = function (d, q, h) {
          var e = this.axis,
              c = e.options,
              k = this.gridLine,
              n = {},
              f = this.pos,
              a = this.type,
              l = y(this.tickmarkOffset, e.tickmarkOffset),
              v = e.chart.renderer,
              z = a ? a + "Grid" : "grid",
              w = c[z + "LineWidth"],
              B = c[z + "LineColor"];
          c = c[z + "LineDashStyle"];
          k || (e.chart.styledMode || (n.stroke = B, n["stroke-width"] = w, c && (n.dashstyle = c)), a || (n.zIndex = 1), d && (q = 0), this.gridLine = k = v.path().attr(n).addClass("highcharts-" + (a ? a + "-" : "") + "grid-line").add(e.gridGroup));
          if (k && (h = e.getPlotLinePath({
            value: f + l,
            lineWidth: k.strokeWidth() * h,
            force: "pass",
            old: d
          }))) k[d || this.isNew ? "attr" : "animate"]({
            d: h,
            opacity: q
          });
        };

        h.prototype.renderMark = function (d, q, h) {
          var e = this.axis,
              c = e.options,
              k = e.chart.renderer,
              n = this.type,
              f = n ? n + "Tick" : "tick",
              a = e.tickSize(f),
              l = this.mark,
              v = !l,
              z = d.x;
          d = d.y;
          var w = y(c[f + "Width"], !n && e.isXAxis ? 1 : 0);
          c = c[f + "Color"];
          a && (e.opposite && (a[0] = -a[0]), v && (this.mark = l = k.path().addClass("highcharts-" + (n ? n + "-" : "") + "tick").add(e.axisGroup), e.chart.styledMode || l.attr({
            stroke: c,
            "stroke-width": w
          })), l[v ? "attr" : "animate"]({
            d: this.getMarkPath(z, d, a[0], l.strokeWidth() * h, e.horiz, k),
            opacity: q
          }));
        };

        h.prototype.renderLabel = function (d, q, h, e) {
          var c = this.axis,
              k = c.horiz,
              n = c.options,
              f = this.label,
              a = n.labels,
              l = a.step;
          c = y(this.tickmarkOffset, c.tickmarkOffset);
          var v = !0,
              z = d.x;
          d = d.y;
          f && A(z) && (f.xy = d = this.getLabelPosition(z, d, f, k, a, c, e, l), this.isFirst && !this.isLast && !y(n.showFirstLabel, 1) || this.isLast && !this.isFirst && !y(n.showLastLabel, 1) ? v = !1 : !k || a.step || a.rotation || q || 0 === h || this.handleOverflow(d), l && e % l && (v = !1), v && A(d.y) ? (d.opacity = h, f[this.isNewLabel ? "attr" : "animate"](d), this.isNewLabel = !1) : (f.attr("y", -9999), this.isNewLabel = !0));
        };

        h.prototype.replaceMovedLabel = function () {
          var d = this.label,
              q = this.axis,
              h = q.reversed,
              e = this.axis.chart.inverted;

          if (d && !this.isNew) {
            var c = e ? d.xy.x : h ? q.left : q.width + q.left;
            h = e ? h ? q.width + q.top : q.top : d.xy.y;
            d.animate({
              x: c,
              y: h,
              opacity: 0
            }, void 0, d.destroy);
            delete this.label;
          }

          q.isDirty = !0;
          this.label = this.movedLabel;
          delete this.movedLabel;
        };

        return h;
      }();

      d.Tick = g;
      return d.Tick;
    });
    S(r, "parts/Time.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.defined,
          u = g.error,
          I = g.extend,
          M = g.isObject,
          E = g.merge,
          A = g.objectEach,
          G = g.pad,
          J = g.pick,
          y = g.splat,
          t = g.timeUnits,
          D = d.win;

      g = function () {
        function h(d) {
          this.options = {};
          this.variableTimezone = this.useUTC = !1;
          this.Date = D.Date;
          this.getTimezoneOffset = this.timezoneOffsetFunction();
          this.update(d);
        }

        h.prototype.get = function (d, q) {
          if (this.variableTimezone || this.timezoneOffset) {
            var h = q.getTime(),
                e = h - this.getTimezoneOffset(q);
            q.setTime(e);
            d = q["getUTC" + d]();
            q.setTime(h);
            return d;
          }

          return this.useUTC ? q["getUTC" + d]() : q["get" + d]();
        };

        h.prototype.set = function (d, q, h) {
          if (this.variableTimezone || this.timezoneOffset) {
            if ("Milliseconds" === d || "Seconds" === d || "Minutes" === d) return q["setUTC" + d](h);
            var e = this.getTimezoneOffset(q);
            e = q.getTime() - e;
            q.setTime(e);
            q["setUTC" + d](h);
            d = this.getTimezoneOffset(q);
            e = q.getTime() + d;
            return q.setTime(e);
          }

          return this.useUTC ? q["setUTC" + d](h) : q["set" + d](h);
        };

        h.prototype.update = function (d) {
          var q = J(d && d.useUTC, !0);
          this.options = d = E(!0, this.options || {}, d);
          this.Date = d.Date || D.Date || Date;
          this.timezoneOffset = (this.useUTC = q) && d.timezoneOffset;
          this.getTimezoneOffset = this.timezoneOffsetFunction();
          this.variableTimezone = !(q && !d.getTimezoneOffset && !d.timezone);
        };

        h.prototype.makeTime = function (h, q, t, e, c, k) {
          if (this.useUTC) {
            var n = this.Date.UTC.apply(0, arguments);
            var f = this.getTimezoneOffset(n);
            n += f;
            var a = this.getTimezoneOffset(n);
            f !== a ? n += a - f : f - 36E5 !== this.getTimezoneOffset(n - 36E5) || d.isSafari || (n -= 36E5);
          } else n = new this.Date(h, q, J(t, 1), J(e, 0), J(c, 0), J(k, 0)).getTime();

          return n;
        };

        h.prototype.timezoneOffsetFunction = function () {
          var d = this,
              q = this.options,
              h = D.moment;
          if (!this.useUTC) return function (e) {
            return 6E4 * new Date(e.toString()).getTimezoneOffset();
          };

          if (q.timezone) {
            if (h) return function (e) {
              return 6E4 * -h.tz(e, q.timezone).utcOffset();
            };
            u(25);
          }

          return this.useUTC && q.getTimezoneOffset ? function (e) {
            return 6E4 * q.getTimezoneOffset(e.valueOf());
          } : function () {
            return 6E4 * (d.timezoneOffset || 0);
          };
        };

        h.prototype.dateFormat = function (h, q, t) {
          var e;
          if (!r(q) || isNaN(q)) return (null === (e = d.defaultOptions.lang) || void 0 === e ? void 0 : e.invalidDate) || "";
          h = J(h, "%Y-%m-%d %H:%M:%S");
          var c = this;
          e = new this.Date(q);
          var k = this.get("Hours", e),
              n = this.get("Day", e),
              f = this.get("Date", e),
              a = this.get("Month", e),
              l = this.get("FullYear", e),
              v = d.defaultOptions.lang,
              z = null === v || void 0 === v ? void 0 : v.weekdays,
              w = null === v || void 0 === v ? void 0 : v.shortWeekdays;
          e = I({
            a: w ? w[n] : z[n].substr(0, 3),
            A: z[n],
            d: G(f),
            e: G(f, 2, " "),
            w: n,
            b: v.shortMonths[a],
            B: v.months[a],
            m: G(a + 1),
            o: a + 1,
            y: l.toString().substr(2, 2),
            Y: l,
            H: G(k),
            k: k,
            I: G(k % 12 || 12),
            l: k % 12 || 12,
            M: G(this.get("Minutes", e)),
            p: 12 > k ? "AM" : "PM",
            P: 12 > k ? "am" : "pm",
            S: G(e.getSeconds()),
            L: G(Math.floor(q % 1E3), 3)
          }, d.dateFormats);
          A(e, function (a, f) {
            for (; -1 !== h.indexOf("%" + f);) {
              h = h.replace("%" + f, "function" === typeof a ? a.call(c, q) : a);
            }
          });
          return t ? h.substr(0, 1).toUpperCase() + h.substr(1) : h;
        };

        h.prototype.resolveDTLFormat = function (d) {
          return M(d, !0) ? d : (d = y(d), {
            main: d[0],
            from: d[1],
            to: d[2]
          });
        };

        h.prototype.getTimeTicks = function (d, h, g, e) {
          var c = this,
              k = [],
              n = {};
          var f = new c.Date(h);
          var a = d.unitRange,
              l = d.count || 1,
              v;
          e = J(e, 1);

          if (r(h)) {
            c.set("Milliseconds", f, a >= t.second ? 0 : l * Math.floor(c.get("Milliseconds", f) / l));
            a >= t.second && c.set("Seconds", f, a >= t.minute ? 0 : l * Math.floor(c.get("Seconds", f) / l));
            a >= t.minute && c.set("Minutes", f, a >= t.hour ? 0 : l * Math.floor(c.get("Minutes", f) / l));
            a >= t.hour && c.set("Hours", f, a >= t.day ? 0 : l * Math.floor(c.get("Hours", f) / l));
            a >= t.day && c.set("Date", f, a >= t.month ? 1 : Math.max(1, l * Math.floor(c.get("Date", f) / l)));

            if (a >= t.month) {
              c.set("Month", f, a >= t.year ? 0 : l * Math.floor(c.get("Month", f) / l));
              var z = c.get("FullYear", f);
            }

            a >= t.year && c.set("FullYear", f, z - z % l);
            a === t.week && (z = c.get("Day", f), c.set("Date", f, c.get("Date", f) - z + e + (z < e ? -7 : 0)));
            z = c.get("FullYear", f);
            e = c.get("Month", f);
            var w = c.get("Date", f),
                q = c.get("Hours", f);
            h = f.getTime();
            c.variableTimezone && (v = g - h > 4 * t.month || c.getTimezoneOffset(h) !== c.getTimezoneOffset(g));
            h = f.getTime();

            for (f = 1; h < g;) {
              k.push(h), h = a === t.year ? c.makeTime(z + f * l, 0) : a === t.month ? c.makeTime(z, e + f * l) : !v || a !== t.day && a !== t.week ? v && a === t.hour && 1 < l ? c.makeTime(z, e, w, q + f * l) : h + a * l : c.makeTime(z, e, w + f * l * (a === t.day ? 1 : 7)), f++;
            }

            k.push(h);
            a <= t.hour && 1E4 > k.length && k.forEach(function (a) {
              0 === a % 18E5 && "000000000" === c.dateFormat("%H%M%S%L", a) && (n[a] = "day");
            });
          }

          k.info = I(d, {
            higherRanks: n,
            totalRange: a * l
          });
          return k;
        };

        h.defaultOptions = {
          Date: void 0,
          getTimezoneOffset: void 0,
          timezone: void 0,
          timezoneOffset: 0,
          useUTC: !0
        };
        return h;
      }();

      d.Time = g;
      return d.Time;
    });
    S(r, "parts/Options.js", [r["parts/Globals.js"], r["parts/Time.js"], r["parts/Color.js"], r["parts/Utilities.js"]], function (d, g, r, u) {
      r = r.parse;
      var I = u.merge;
      d.defaultOptions = {
        colors: "#7cb5ec #434348 #90ed7d #f7a35c #8085e9 #f15c80 #e4d354 #2b908f #f45b5b #91e8e1".split(" "),
        symbols: ["circle", "diamond", "square", "triangle", "triangle-down"],
        lang: {
          loading: "Loading...",
          months: "January February March April May June July August September October November December".split(" "),
          shortMonths: "Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec".split(" "),
          weekdays: "Sunday Monday Tuesday Wednesday Thursday Friday Saturday".split(" "),
          decimalPoint: ".",
          numericSymbols: "kMGTPE".split(""),
          resetZoom: "Reset zoom",
          resetZoomTitle: "Reset zoom level 1:1",
          thousandsSep: " "
        },
        global: {},
        time: g.defaultOptions,
        chart: {
          styledMode: !1,
          borderRadius: 0,
          colorCount: 10,
          defaultSeriesType: "line",
          ignoreHiddenSeries: !0,
          spacing: [10, 10, 15, 10],
          resetZoomButton: {
            theme: {
              zIndex: 6
            },
            position: {
              align: "right",
              x: -10,
              y: 10
            }
          },
          width: null,
          height: null,
          borderColor: "#335cad",
          backgroundColor: "#ffffff",
          plotBorderColor: "#cccccc"
        },
        title: {
          text: "Chart title",
          align: "center",
          margin: 15,
          widthAdjust: -44
        },
        subtitle: {
          text: "",
          align: "center",
          widthAdjust: -44
        },
        caption: {
          margin: 15,
          text: "",
          align: "left",
          verticalAlign: "bottom"
        },
        plotOptions: {},
        labels: {
          style: {
            position: "absolute",
            color: "#333333"
          }
        },
        legend: {
          enabled: !0,
          align: "center",
          alignColumns: !0,
          layout: "horizontal",
          labelFormatter: function labelFormatter() {
            return this.name;
          },
          borderColor: "#999999",
          borderRadius: 0,
          navigation: {
            activeColor: "#003399",
            inactiveColor: "#cccccc"
          },
          itemStyle: {
            color: "#333333",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "bold",
            textOverflow: "ellipsis"
          },
          itemHoverStyle: {
            color: "#000000"
          },
          itemHiddenStyle: {
            color: "#cccccc"
          },
          shadow: !1,
          itemCheckboxStyle: {
            position: "absolute",
            width: "13px",
            height: "13px"
          },
          squareSymbol: !0,
          symbolPadding: 5,
          verticalAlign: "bottom",
          x: 0,
          y: 0,
          title: {
            style: {
              fontWeight: "bold"
            }
          }
        },
        loading: {
          labelStyle: {
            fontWeight: "bold",
            position: "relative",
            top: "45%"
          },
          style: {
            position: "absolute",
            backgroundColor: "#ffffff",
            opacity: .5,
            textAlign: "center"
          }
        },
        tooltip: {
          enabled: !0,
          animation: d.svg,
          borderRadius: 3,
          dateTimeLabelFormats: {
            millisecond: "%A, %b %e, %H:%M:%S.%L",
            second: "%A, %b %e, %H:%M:%S",
            minute: "%A, %b %e, %H:%M",
            hour: "%A, %b %e, %H:%M",
            day: "%A, %b %e, %Y",
            week: "Week from %A, %b %e, %Y",
            month: "%B %Y",
            year: "%Y"
          },
          footerFormat: "",
          padding: 8,
          snap: d.isTouchDevice ? 25 : 10,
          headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
          pointFormat: "<span style=\"color:{point.color}\">\u25CF</span> {series.name}: <b>{point.y}</b><br/>",
          backgroundColor: r("#f7f7f7").setOpacity(.85).get(),
          borderWidth: 1,
          shadow: !0,
          style: {
            color: "#333333",
            cursor: "default",
            fontSize: "12px",
            whiteSpace: "nowrap"
          }
        },
        credits: {
          enabled: !0,
          href: "https://www.highcharts.com?credits",
          position: {
            align: "right",
            x: -10,
            verticalAlign: "bottom",
            y: -5
          },
          style: {
            cursor: "pointer",
            color: "#999999",
            fontSize: "9px"
          },
          text: "Highcharts.com"
        }
      };

      d.setOptions = function (g) {
        d.defaultOptions = I(!0, d.defaultOptions, g);
        (g.time || g.global) && d.time.update(I(d.defaultOptions.global, d.defaultOptions.time, g.global, g.time));
        return d.defaultOptions;
      };

      d.getOptions = function () {
        return d.defaultOptions;
      };

      d.defaultPlotOptions = d.defaultOptions.plotOptions;
      d.time = new g(I(d.defaultOptions.global, d.defaultOptions.time));

      d.dateFormat = function (g, u, A) {
        return d.time.dateFormat(g, u, A);
      };
    });
    S(r, "parts/Axis.js", [r["parts/Globals.js"], r["parts/Color.js"], r["parts/Tick.js"], r["parts/Utilities.js"]], function (d, g, r, u) {
      var I = g.parse,
          M = u.addEvent,
          E = u.animObject,
          A = u.arrayMax,
          G = u.arrayMin,
          J = u.clamp,
          y = u.correctFloat,
          t = u.defined,
          D = u.destroyObjectProperties,
          h = u.error,
          N = u.extend,
          q = u.fireEvent,
          P = u.format,
          e = u.getMagnitude,
          c = u.isArray,
          k = u.isFunction,
          n = u.isNumber,
          f = u.isString,
          a = u.merge,
          l = u.normalizeTickInterval,
          v = u.objectEach,
          z = u.pick,
          w = u.relativeLength,
          B = u.removeEvent,
          L = u.splat,
          Q = u.syncTimeout,
          H = d.defaultOptions,
          K = d.deg2rad;

      g = function g() {
        this.init.apply(this, arguments);
      };

      N(g.prototype, {
        defaultOptions: {
          dateTimeLabelFormats: {
            millisecond: {
              main: "%H:%M:%S.%L",
              range: !1
            },
            second: {
              main: "%H:%M:%S",
              range: !1
            },
            minute: {
              main: "%H:%M",
              range: !1
            },
            hour: {
              main: "%H:%M",
              range: !1
            },
            day: {
              main: "%e. %b"
            },
            week: {
              main: "%e. %b"
            },
            month: {
              main: "%b '%y"
            },
            year: {
              main: "%Y"
            }
          },
          endOnTick: !1,
          labels: {
            enabled: !0,
            indentation: 10,
            x: 0,
            style: {
              color: "#666666",
              cursor: "default",
              fontSize: "11px"
            }
          },
          maxPadding: .01,
          minorTickLength: 2,
          minorTickPosition: "outside",
          minPadding: .01,
          showEmpty: !0,
          startOfWeek: 1,
          startOnTick: !1,
          tickLength: 10,
          tickPixelInterval: 100,
          tickmarkPlacement: "between",
          tickPosition: "outside",
          title: {
            align: "middle",
            style: {
              color: "#666666"
            }
          },
          type: "linear",
          minorGridLineColor: "#f2f2f2",
          minorGridLineWidth: 1,
          minorTickColor: "#999999",
          lineColor: "#ccd6eb",
          lineWidth: 1,
          gridLineColor: "#e6e6e6",
          tickColor: "#ccd6eb"
        },
        defaultYAxisOptions: {
          endOnTick: !0,
          maxPadding: .05,
          minPadding: .05,
          tickPixelInterval: 72,
          showLastLabel: !0,
          labels: {
            x: -8
          },
          startOnTick: !0,
          title: {
            rotation: 270,
            text: "Values"
          },
          stackLabels: {
            allowOverlap: !1,
            enabled: !1,
            crop: !0,
            overflow: "justify",
            formatter: function formatter() {
              var a = this.axis.chart.numberFormatter;
              return a(this.total, -1);
            },
            style: {
              color: "#000000",
              fontSize: "11px",
              fontWeight: "bold",
              textOutline: "1px contrast"
            }
          },
          gridLineWidth: 1,
          lineWidth: 0
        },
        defaultLeftAxisOptions: {
          labels: {
            x: -15
          },
          title: {
            rotation: 270
          }
        },
        defaultRightAxisOptions: {
          labels: {
            x: 15
          },
          title: {
            rotation: 90
          }
        },
        defaultBottomAxisOptions: {
          labels: {
            autoRotation: [-45],
            x: 0
          },
          margin: 15,
          title: {
            rotation: 0
          }
        },
        defaultTopAxisOptions: {
          labels: {
            autoRotation: [-45],
            x: 0
          },
          margin: 15,
          title: {
            rotation: 0
          }
        },
        init: function init(a, b) {
          var f = b.isX,
              c = this;
          c.chart = a;
          c.horiz = a.inverted && !c.isZAxis ? !f : f;
          c.isXAxis = f;
          c.coll = c.coll || (f ? "xAxis" : "yAxis");
          q(this, "init", {
            userOptions: b
          });
          c.opposite = b.opposite;
          c.side = b.side || (c.horiz ? c.opposite ? 0 : 2 : c.opposite ? 1 : 3);
          c.setOptions(b);
          var p = this.options,
              e = p.type;
          c.labelFormatter = p.labels.formatter || c.defaultLabelFormatter;
          c.userOptions = b;
          c.minPixelPadding = 0;
          c.reversed = p.reversed;
          c.visible = !1 !== p.visible;
          c.zoomEnabled = !1 !== p.zoomEnabled;
          c.hasNames = "category" === e || !0 === p.categories;
          c.categories = p.categories || c.hasNames;
          c.names || (c.names = [], c.names.keys = {});
          c.plotLinesAndBandsGroups = {};
          c.isLog = "logarithmic" === e;
          c.isDatetimeAxis = "datetime" === e;
          c.positiveValuesOnly = c.isLog && !c.allowNegativeLog;
          c.isLinked = t(p.linkedTo);
          c.ticks = {};
          c.labelEdge = [];
          c.minorTicks = {};
          c.plotLinesAndBands = [];
          c.alternateBands = {};
          c.len = 0;
          c.minRange = c.userMinRange = p.minRange || p.maxZoom;
          c.range = p.range;
          c.offset = p.offset || 0;
          c.stacks = {};
          c.oldStacks = {};
          c.stacksTouched = 0;
          c.max = null;
          c.min = null;
          c.crosshair = z(p.crosshair, L(a.options.tooltip.crosshairs)[f ? 0 : 1], !1);
          b = c.options.events;
          -1 === a.axes.indexOf(c) && (f ? a.axes.splice(a.xAxis.length, 0, c) : a.axes.push(c), a[c.coll].push(c));
          c.series = c.series || [];
          a.inverted && !c.isZAxis && f && "undefined" === typeof c.reversed && (c.reversed = !0);
          v(b, function (b, a) {
            k(b) && M(c, a, b);
          });
          c.lin2log = p.linearToLogConverter || c.lin2log;
          c.isLog && (c.val2lin = c.log2lin, c.lin2val = c.lin2log);
          q(this, "afterInit");
        },
        setOptions: function setOptions(c) {
          this.options = a(this.defaultOptions, "yAxis" === this.coll && this.defaultYAxisOptions, [this.defaultTopAxisOptions, this.defaultRightAxisOptions, this.defaultBottomAxisOptions, this.defaultLeftAxisOptions][this.side], a(H[this.coll], c));
          q(this, "afterSetOptions", {
            userOptions: c
          });
        },
        defaultLabelFormatter: function defaultLabelFormatter() {
          var a = this.axis,
              b = this.value,
              c = a.chart.time,
              f = a.categories,
              e = this.dateTimeLabelFormat,
              l = H.lang,
              k = l.numericSymbols;
          l = l.numericSymbolMagnitude || 1E3;
          var v = k && k.length,
              m = a.options.labels.format;
          a = a.isLog ? Math.abs(b) : a.tickInterval;
          var n = this.chart,
              d = n.numberFormatter;
          if (m) var w = P(m, this, n);else if (f) w = b;else if (e) w = c.dateFormat(e, b);else if (v && 1E3 <= a) for (; v-- && "undefined" === typeof w;) {
            c = Math.pow(l, v + 1), a >= c && 0 === 10 * b % c && null !== k[v] && 0 !== b && (w = d(b / c, -1) + k[v]);
          }
          "undefined" === typeof w && (w = 1E4 <= Math.abs(b) ? d(b, -1) : d(b, -1, void 0, ""));
          return w;
        },
        getSeriesExtremes: function getSeriesExtremes() {
          var a = this,
              b = a.chart,
              c;
          q(this, "getSeriesExtremes", null, function () {
            a.hasVisibleSeries = !1;
            a.dataMin = a.dataMax = a.threshold = null;
            a.softThreshold = !a.isXAxis;
            a.buildStacks && a.buildStacks();
            a.series.forEach(function (f) {
              if (f.visible || !b.options.chart.ignoreHiddenSeries) {
                var e = f.options,
                    p = e.threshold;
                a.hasVisibleSeries = !0;
                a.positiveValuesOnly && 0 >= p && (p = null);

                if (a.isXAxis) {
                  if (e = f.xData, e.length) {
                    c = f.getXExtremes(e);
                    var x = c.min;
                    var l = c.max;
                    n(x) || x instanceof Date || (e = e.filter(n), c = f.getXExtremes(e), x = c.min, l = c.max);
                    e.length && (a.dataMin = Math.min(z(a.dataMin, x), x), a.dataMax = Math.max(z(a.dataMax, l), l));
                  }
                } else if (f.getExtremes(), l = f.dataMax, x = f.dataMin, t(x) && t(l) && (a.dataMin = Math.min(z(a.dataMin, x), x), a.dataMax = Math.max(z(a.dataMax, l), l)), t(p) && (a.threshold = p), !e.softThreshold || a.positiveValuesOnly) a.softThreshold = !1;
              }
            });
          });
          q(this, "afterGetSeriesExtremes");
        },
        translate: function translate(a, b, c, f, e, l) {
          var p = this.linkedParent || this,
              x = 1,
              m = 0,
              C = f ? p.oldTransA : p.transA;
          f = f ? p.oldMin : p.min;
          var k = p.minPixelPadding;
          e = (p.isOrdinal || p.isBroken || p.isLog && e) && p.lin2val;
          C || (C = p.transA);
          c && (x *= -1, m = p.len);
          p.reversed && (x *= -1, m -= x * (p.sector || p.len));
          b ? (a = (a * x + m - k) / C + f, e && (a = p.lin2val(a))) : (e && (a = p.val2lin(a)), a = n(f) ? x * (a - f) * C + m + x * k + (n(l) ? C * l : 0) : void 0);
          return a;
        },
        toPixels: function toPixels(a, b) {
          return this.translate(a, !1, !this.horiz, null, !0) + (b ? 0 : this.pos);
        },
        toValue: function toValue(a, b) {
          return this.translate(a - (b ? 0 : this.pos), !0, !this.horiz, null, !0);
        },
        getPlotLinePath: function getPlotLinePath(a) {
          var b = this,
              c = b.chart,
              f = b.left,
              e = b.top,
              p = a.old,
              l = a.value,
              k = a.translatedValue,
              m = a.lineWidth,
              v = a.force,
              d,
              w,
              F,
              h,
              B = p && c.oldChartHeight || c.chartHeight,
              L = p && c.oldChartWidth || c.chartWidth,
              t,
              g = b.transB,
              H = function H(b, a, c) {
            if ("pass" !== v && b < a || b > c) v ? b = J(b, a, c) : t = !0;
            return b;
          };

          a = {
            value: l,
            lineWidth: m,
            old: p,
            force: v,
            acrossPanes: a.acrossPanes,
            translatedValue: k
          };
          q(this, "getPlotLinePath", a, function (a) {
            k = z(k, b.translate(l, null, null, p));
            k = J(k, -1E5, 1E5);
            d = F = Math.round(k + g);
            w = h = Math.round(B - k - g);
            n(k) ? b.horiz ? (w = e, h = B - b.bottom, d = F = H(d, f, f + b.width)) : (d = f, F = L - b.right, w = h = H(w, e, e + b.height)) : (t = !0, v = !1);
            a.path = t && !v ? null : c.renderer.crispLine(["M", d, w, "L", F, h], m || 1);
          });
          return a.path;
        },
        getLinearTickPositions: function getLinearTickPositions(a, b, c) {
          var f = y(Math.floor(b / a) * a);
          c = y(Math.ceil(c / a) * a);
          var e = [],
              p;
          y(f + a) === f && (p = 20);
          if (this.single) return [b];

          for (b = f; b <= c;) {
            e.push(b);
            b = y(b + a, p);
            if (b === l) break;
            var l = b;
          }

          return e;
        },
        getMinorTickInterval: function getMinorTickInterval() {
          var a = this.options;
          return !0 === a.minorTicks ? z(a.minorTickInterval, "auto") : !1 === a.minorTicks ? null : a.minorTickInterval;
        },
        getMinorTickPositions: function getMinorTickPositions() {
          var a = this,
              b = a.options,
              c = a.tickPositions,
              f = a.minorTickInterval,
              e = [],
              l = a.pointRangePadding || 0,
              k = a.min - l;
          l = a.max + l;
          var v = l - k;
          if (v && v / f < a.len / 3) if (a.isLog) this.paddedTicks.forEach(function (b, c, p) {
            c && e.push.apply(e, a.getLogTickPositions(f, p[c - 1], p[c], !0));
          });else if (a.isDatetimeAxis && "auto" === this.getMinorTickInterval()) e = e.concat(a.getTimeTicks(a.normalizeTimeTickInterval(f), k, l, b.startOfWeek));else for (b = k + (c[0] - k) % f; b <= l && b !== e[0]; b += f) {
            e.push(b);
          }
          0 !== e.length && a.trimTicks(e);
          return e;
        },
        adjustForMinRange: function adjustForMinRange() {
          var a = this.options,
              b = this.min,
              c = this.max,
              f,
              e,
              l,
              k,
              v;
          this.isXAxis && "undefined" === typeof this.minRange && !this.isLog && (t(a.min) || t(a.max) ? this.minRange = null : (this.series.forEach(function (b) {
            k = b.xData;

            for (e = v = b.xIncrement ? 1 : k.length - 1; 0 < e; e--) {
              if (l = k[e] - k[e - 1], "undefined" === typeof f || l < f) f = l;
            }
          }), this.minRange = Math.min(5 * f, this.dataMax - this.dataMin)));

          if (c - b < this.minRange) {
            var m = this.dataMax - this.dataMin >= this.minRange;
            var n = this.minRange;
            var d = (n - c + b) / 2;
            d = [b - d, z(a.min, b - d)];
            m && (d[2] = this.isLog ? this.log2lin(this.dataMin) : this.dataMin);
            b = A(d);
            c = [b + n, z(a.max, b + n)];
            m && (c[2] = this.isLog ? this.log2lin(this.dataMax) : this.dataMax);
            c = G(c);
            c - b < n && (d[0] = c - n, d[1] = z(a.min, c - n), b = A(d));
          }

          this.min = b;
          this.max = c;
        },
        getClosest: function getClosest() {
          var a;
          this.categories ? a = 1 : this.series.forEach(function (b) {
            var c = b.closestPointRange,
                f = b.visible || !b.chart.options.chart.ignoreHiddenSeries;
            !b.noSharedTooltip && t(c) && f && (a = t(a) ? Math.min(a, c) : c);
          });
          return a;
        },
        nameToX: function nameToX(a) {
          var b = c(this.categories),
              f = b ? this.categories : this.names,
              e = a.options.x;
          a.series.requireSorting = !1;
          t(e) || (e = !1 === this.options.uniqueNames ? a.series.autoIncrement() : b ? f.indexOf(a.name) : z(f.keys[a.name], -1));

          if (-1 === e) {
            if (!b) var p = f.length;
          } else p = e;

          "undefined" !== typeof p && (this.names[p] = a.name, this.names.keys[a.name] = p);
          return p;
        },
        updateNames: function updateNames() {
          var a = this,
              b = this.names;
          0 < b.length && (Object.keys(b.keys).forEach(function (a) {
            delete b.keys[a];
          }), b.length = 0, this.minRange = this.userMinRange, (this.series || []).forEach(function (b) {
            b.xIncrement = null;
            if (!b.points || b.isDirtyData) a.max = Math.max(a.max, b.xData.length - 1), b.processData(), b.generatePoints();
            b.data.forEach(function (c, f) {
              if (c && c.options && "undefined" !== typeof c.name) {
                var e = a.nameToX(c);
                "undefined" !== typeof e && e !== c.x && (c.x = e, b.xData[f] = e);
              }
            });
          }));
        },
        setAxisTranslation: function setAxisTranslation(a) {
          var b = this,
              c = b.max - b.min,
              e = b.axisPointRange || 0,
              p = 0,
              l = 0,
              k = b.linkedParent,
              v = !!b.categories,
              m = b.transA,
              d = b.isXAxis;

          if (d || v || e) {
            var n = b.getClosest();
            k ? (p = k.minPointOffset, l = k.pointRangePadding) : b.series.forEach(function (a) {
              var c = v ? 1 : d ? z(a.options.pointRange, n, 0) : b.axisPointRange || 0,
                  x = a.options.pointPlacement;
              e = Math.max(e, c);
              if (!b.single || v) a = a.is("xrange") ? !d : d, p = Math.max(p, a && f(x) ? 0 : c / 2), l = Math.max(l, a && "on" === x ? 0 : c);
            });
            k = b.ordinalSlope && n ? b.ordinalSlope / n : 1;
            b.minPointOffset = p *= k;
            b.pointRangePadding = l *= k;
            b.pointRange = Math.min(e, b.single && v ? 1 : c);
            d && (b.closestPointRange = n);
          }

          a && (b.oldTransA = m);
          b.translationSlope = b.transA = m = b.staticScale || b.len / (c + l || 1);
          b.transB = b.horiz ? b.left : b.bottom;
          b.minPixelPadding = m * p;
          q(this, "afterSetAxisTranslation");
        },
        minFromRange: function minFromRange() {
          return this.max - this.range;
        },
        setTickInterval: function setTickInterval(a) {
          var b = this,
              c = b.chart,
              f = b.options,
              p = b.isLog,
              k = b.isDatetimeAxis,
              v = b.isXAxis,
              d = b.isLinked,
              m = f.maxPadding,
              w = f.minPadding,
              B = f.tickInterval,
              L = f.tickPixelInterval,
              F = b.categories,
              V = n(b.threshold) ? b.threshold : null,
              g = b.softThreshold;
          k || F || d || this.getTickAmount();
          var H = z(b.userMin, f.min);
          var K = z(b.userMax, f.max);

          if (d) {
            b.linkedParent = c[b.coll][f.linkedTo];
            var Q = b.linkedParent.getExtremes();
            b.min = z(Q.min, Q.dataMin);
            b.max = z(Q.max, Q.dataMax);
            f.type !== b.linkedParent.options.type && h(11, 1, c);
          } else {
            if (!g && t(V)) if (b.dataMin >= V) Q = V, w = 0;else if (b.dataMax <= V) {
              var u = V;
              m = 0;
            }
            b.min = z(H, Q, b.dataMin);
            b.max = z(K, u, b.dataMax);
          }

          p && (b.positiveValuesOnly && !a && 0 >= Math.min(b.min, z(b.dataMin, b.min)) && h(10, 1, c), b.min = y(b.log2lin(b.min), 16), b.max = y(b.log2lin(b.max), 16));
          b.range && t(b.max) && (b.userMin = b.min = H = Math.max(b.dataMin, b.minFromRange()), b.userMax = K = b.max, b.range = null);
          q(b, "foundExtremes");
          b.beforePadding && b.beforePadding();
          b.adjustForMinRange();
          !(F || b.axisPointRange || b.usePercentage || d) && t(b.min) && t(b.max) && (c = b.max - b.min) && (!t(H) && w && (b.min -= c * w), !t(K) && m && (b.max += c * m));
          n(b.userMin) || (n(f.softMin) && f.softMin < b.min && (b.min = H = f.softMin), n(f.floor) && (b.min = Math.max(b.min, f.floor)));
          n(b.userMax) || (n(f.softMax) && f.softMax > b.max && (b.max = K = f.softMax), n(f.ceiling) && (b.max = Math.min(b.max, f.ceiling)));
          g && t(b.dataMin) && (V = V || 0, !t(H) && b.min < V && b.dataMin >= V ? b.min = b.options.minRange ? Math.min(V, b.max - b.minRange) : V : !t(K) && b.max > V && b.dataMax <= V && (b.max = b.options.minRange ? Math.max(V, b.min + b.minRange) : V));
          b.tickInterval = b.min === b.max || "undefined" === typeof b.min || "undefined" === typeof b.max ? 1 : d && !B && L === b.linkedParent.options.tickPixelInterval ? B = b.linkedParent.tickInterval : z(B, this.tickAmount ? (b.max - b.min) / Math.max(this.tickAmount - 1, 1) : void 0, F ? 1 : (b.max - b.min) * L / Math.max(b.len, L));
          v && !a && b.series.forEach(function (a) {
            a.processData(b.min !== b.oldMin || b.max !== b.oldMax);
          });
          b.setAxisTranslation(!0);
          b.beforeSetTickPositions && b.beforeSetTickPositions();
          b.postProcessTickInterval && (b.tickInterval = b.postProcessTickInterval(b.tickInterval));
          b.pointRange && !B && (b.tickInterval = Math.max(b.pointRange, b.tickInterval));
          a = z(f.minTickInterval, b.isDatetimeAxis && b.closestPointRange);
          !B && b.tickInterval < a && (b.tickInterval = a);
          k || p || B || (b.tickInterval = l(b.tickInterval, null, e(b.tickInterval), z(f.allowDecimals, !(.5 < b.tickInterval && 5 > b.tickInterval && 1E3 < b.max && 9999 > b.max)), !!this.tickAmount));
          this.tickAmount || (b.tickInterval = b.unsquish());
          this.setTickPositions();
        },
        setTickPositions: function setTickPositions() {
          var a = this.options,
              b = a.tickPositions;
          var c = this.getMinorTickInterval();
          var f = a.tickPositioner,
              e = a.startOnTick,
              l = a.endOnTick;
          this.tickmarkOffset = this.categories && "between" === a.tickmarkPlacement && 1 === this.tickInterval ? .5 : 0;
          this.minorTickInterval = "auto" === c && this.tickInterval ? this.tickInterval / 5 : c;
          this.single = this.min === this.max && t(this.min) && !this.tickAmount && (parseInt(this.min, 10) === this.min || !1 !== a.allowDecimals);
          this.tickPositions = c = b && b.slice();
          !c && (!this.ordinalPositions && (this.max - this.min) / this.tickInterval > Math.max(2 * this.len, 200) ? (c = [this.min, this.max], h(19, !1, this.chart)) : c = this.isDatetimeAxis ? this.getTimeTicks(this.normalizeTimeTickInterval(this.tickInterval, a.units), this.min, this.max, a.startOfWeek, this.ordinalPositions, this.closestPointRange, !0) : this.isLog ? this.getLogTickPositions(this.tickInterval, this.min, this.max) : this.getLinearTickPositions(this.tickInterval, this.min, this.max), c.length > this.len && (c = [c[0], c.pop()], c[0] === c[1] && (c.length = 1)), this.tickPositions = c, f && (f = f.apply(this, [this.min, this.max]))) && (this.tickPositions = c = f);
          this.paddedTicks = c.slice(0);
          this.trimTicks(c, e, l);
          this.isLinked || (this.single && 2 > c.length && !this.categories && !this.series.some(function (b) {
            return b.is("heatmap") && "between" === b.options.pointPlacement;
          }) && (this.min -= .5, this.max += .5), b || f || this.adjustTickAmount());
          q(this, "afterSetTickPositions");
        },
        trimTicks: function trimTicks(a, b, c) {
          var f = a[0],
              e = a[a.length - 1],
              p = !this.isOrdinal && this.minPointOffset || 0;
          q(this, "trimTicks");

          if (!this.isLinked) {
            if (b && -Infinity !== f) this.min = f;else for (; this.min - p > a[0];) {
              a.shift();
            }
            if (c) this.max = e;else for (; this.max + p < a[a.length - 1];) {
              a.pop();
            }
            0 === a.length && t(f) && !this.options.tickPositions && a.push((e + f) / 2);
          }
        },
        alignToOthers: function alignToOthers() {
          var a = {},
              b,
              c = this.options;
          !1 === this.chart.options.chart.alignTicks || !1 === c.alignTicks || !1 === c.startOnTick || !1 === c.endOnTick || this.isLog || this.chart[this.coll].forEach(function (c) {
            var f = c.options;
            f = [c.horiz ? f.left : f.top, f.width, f.height, f.pane].join();
            c.series.length && (a[f] ? b = !0 : a[f] = 1);
          });
          return b;
        },
        getTickAmount: function getTickAmount() {
          var a = this.options,
              b = a.tickAmount,
              c = a.tickPixelInterval;
          !t(a.tickInterval) && this.len < c && !this.isRadial && !this.isLog && a.startOnTick && a.endOnTick && (b = 2);
          !b && this.alignToOthers() && (b = Math.ceil(this.len / c) + 1);
          4 > b && (this.finalTickAmt = b, b = 5);
          this.tickAmount = b;
        },
        adjustTickAmount: function adjustTickAmount() {
          var a = this.options,
              b = this.tickInterval,
              c = this.tickPositions,
              f = this.tickAmount,
              e = this.finalTickAmt,
              l = c && c.length,
              k = z(this.threshold, this.softThreshold ? 0 : null),
              v;

          if (this.hasData()) {
            if (l < f) {
              for (v = this.min; c.length < f;) {
                c.length % 2 || v === k ? c.push(y(c[c.length - 1] + b)) : c.unshift(y(c[0] - b));
              }

              this.transA *= (l - 1) / (f - 1);
              this.min = a.startOnTick ? c[0] : Math.min(this.min, c[0]);
              this.max = a.endOnTick ? c[c.length - 1] : Math.max(this.max, c[c.length - 1]);
            } else l > f && (this.tickInterval *= 2, this.setTickPositions());

            if (t(e)) {
              for (b = a = c.length; b--;) {
                (3 === e && 1 === b % 2 || 2 >= e && 0 < b && b < a - 1) && c.splice(b, 1);
              }

              this.finalTickAmt = void 0;
            }
          }
        },
        setScale: function setScale() {
          var a = this.series.some(function (b) {
            return b.isDirtyData || b.isDirty || b.xAxis && b.xAxis.isDirty;
          }),
              b;
          this.oldMin = this.min;
          this.oldMax = this.max;
          this.oldAxisLength = this.len;
          this.setAxisSize();
          (b = this.len !== this.oldAxisLength) || a || this.isLinked || this.forceRedraw || this.userMin !== this.oldUserMin || this.userMax !== this.oldUserMax || this.alignToOthers() ? (this.resetStacks && this.resetStacks(), this.forceRedraw = !1, this.getSeriesExtremes(), this.setTickInterval(), this.oldUserMin = this.userMin, this.oldUserMax = this.userMax, this.isDirty || (this.isDirty = b || this.min !== this.oldMin || this.max !== this.oldMax)) : this.cleanStacks && this.cleanStacks();
          q(this, "afterSetScale");
        },
        setExtremes: function setExtremes(a, b, c, f, e) {
          var p = this,
              l = p.chart;
          c = z(c, !0);
          p.series.forEach(function (b) {
            delete b.kdTree;
          });
          e = N(e, {
            min: a,
            max: b
          });
          q(p, "setExtremes", e, function () {
            p.userMin = a;
            p.userMax = b;
            p.eventArgs = e;
            c && l.redraw(f);
          });
        },
        zoom: function zoom(a, b) {
          var c = this.dataMin,
              f = this.dataMax,
              e = this.options,
              p = Math.min(c, z(e.min, c)),
              l = Math.max(f, z(e.max, f));
          a = {
            newMin: a,
            newMax: b
          };
          q(this, "zoom", a, function (b) {
            var a = b.newMin,
                e = b.newMax;
            if (a !== this.min || e !== this.max) this.allowZoomOutside || (t(c) && (a < p && (a = p), a > l && (a = l)), t(f) && (e < p && (e = p), e > l && (e = l))), this.displayBtn = "undefined" !== typeof a || "undefined" !== typeof e, this.setExtremes(a, e, !1, void 0, {
              trigger: "zoom"
            });
            b.zoomed = !0;
          });
          return a.zoomed;
        },
        setAxisSize: function setAxisSize() {
          var a = this.chart,
              b = this.options,
              c = b.offsets || [0, 0, 0, 0],
              f = this.horiz,
              e = this.width = Math.round(w(z(b.width, a.plotWidth - c[3] + c[1]), a.plotWidth)),
              l = this.height = Math.round(w(z(b.height, a.plotHeight - c[0] + c[2]), a.plotHeight)),
              k = this.top = Math.round(w(z(b.top, a.plotTop + c[0]), a.plotHeight, a.plotTop));
          b = this.left = Math.round(w(z(b.left, a.plotLeft + c[3]), a.plotWidth, a.plotLeft));
          this.bottom = a.chartHeight - l - k;
          this.right = a.chartWidth - e - b;
          this.len = Math.max(f ? e : l, 0);
          this.pos = f ? b : k;
        },
        getExtremes: function getExtremes() {
          var a = this.isLog;
          return {
            min: a ? y(this.lin2log(this.min)) : this.min,
            max: a ? y(this.lin2log(this.max)) : this.max,
            dataMin: this.dataMin,
            dataMax: this.dataMax,
            userMin: this.userMin,
            userMax: this.userMax
          };
        },
        getThreshold: function getThreshold(a) {
          var b = this.isLog,
              c = b ? this.lin2log(this.min) : this.min;
          b = b ? this.lin2log(this.max) : this.max;
          null === a || -Infinity === a ? a = c : Infinity === a ? a = b : c > a ? a = c : b < a && (a = b);
          return this.translate(a, 0, 1, 0, 1);
        },
        autoLabelAlign: function autoLabelAlign(a) {
          var b = (z(a, 0) - 90 * this.side + 720) % 360;
          a = {
            align: "center"
          };
          q(this, "autoLabelAlign", a, function (a) {
            15 < b && 165 > b ? a.align = "right" : 195 < b && 345 > b && (a.align = "left");
          });
          return a.align;
        },
        tickSize: function tickSize(a) {
          var b = this.options,
              c = b[a + "Length"],
              f = z(b[a + "Width"], "tick" === a && this.isXAxis && !this.categories ? 1 : 0);

          if (f && c) {
            "inside" === b[a + "Position"] && (c = -c);
            var e = [c, f];
          }

          a = {
            tickSize: e
          };
          q(this, "afterTickSize", a);
          return a.tickSize;
        },
        labelMetrics: function labelMetrics() {
          var a = this.tickPositions && this.tickPositions[0] || 0;
          return this.chart.renderer.fontMetrics(this.options.labels.style && this.options.labels.style.fontSize, this.ticks[a] && this.ticks[a].label);
        },
        unsquish: function unsquish() {
          var a = this.options.labels,
              b = this.horiz,
              c = this.tickInterval,
              f = c,
              e = this.len / (((this.categories ? 1 : 0) + this.max - this.min) / c),
              l,
              k = a.rotation,
              v = this.labelMetrics(),
              m,
              d = Number.MAX_VALUE,
              n,
              w = this.max - this.min,
              F = function F(b) {
            var a = b / (e || 1);
            a = 1 < a ? Math.ceil(a) : 1;
            a * c > w && Infinity !== b && Infinity !== e && w && (a = Math.ceil(w / c));
            return y(a * c);
          };

          b ? (n = !a.staggerLines && !a.step && (t(k) ? [k] : e < z(a.autoRotationLimit, 80) && a.autoRotation)) && n.forEach(function (b) {
            if (b === k || b && -90 <= b && 90 >= b) {
              m = F(Math.abs(v.h / Math.sin(K * b)));
              var a = m + Math.abs(b / 360);
              a < d && (d = a, l = b, f = m);
            }
          }) : a.step || (f = F(v.h));
          this.autoRotation = n;
          this.labelRotation = z(l, k);
          return f;
        },
        getSlotWidth: function getSlotWidth(a) {
          var b = this.chart,
              c = this.horiz,
              f = this.options.labels,
              e = Math.max(this.tickPositions.length - (this.categories ? 0 : 1), 1),
              l = b.margin[3];
          return a && a.slotWidth || c && 2 > (f.step || 0) && !f.rotation && (this.staggerLines || 1) * this.len / e || !c && (f.style && parseInt(f.style.width, 10) || l && l - b.spacing[3] || .33 * b.chartWidth);
        },
        renderUnsquish: function renderUnsquish() {
          var a = this.chart,
              b = a.renderer,
              c = this.tickPositions,
              e = this.ticks,
              l = this.options.labels,
              k = l && l.style || {},
              v = this.horiz,
              d = this.getSlotWidth(),
              m = Math.max(1, Math.round(d - 2 * (l.padding || 5))),
              n = {},
              w = this.labelMetrics(),
              h = l.style && l.style.textOverflow,
              F = 0;
          f(l.rotation) || (n.rotation = l.rotation || 0);
          c.forEach(function (b) {
            b = e[b];
            b.movedLabel && b.replaceMovedLabel();
            b && b.label && b.label.textPxLength > F && (F = b.label.textPxLength);
          });
          this.maxLabelLength = F;
          if (this.autoRotation) F > m && F > w.h ? n.rotation = this.labelRotation : this.labelRotation = 0;else if (d) {
            var z = m;

            if (!h) {
              var q = "clip";

              for (m = c.length; !v && m--;) {
                var B = c[m];
                if (B = e[B].label) B.styles && "ellipsis" === B.styles.textOverflow ? B.css({
                  textOverflow: "clip"
                }) : B.textPxLength > d && B.css({
                  width: d + "px"
                }), B.getBBox().height > this.len / c.length - (w.h - w.f) && (B.specificTextOverflow = "ellipsis");
              }
            }
          }
          n.rotation && (z = F > .5 * a.chartHeight ? .33 * a.chartHeight : F, h || (q = "ellipsis"));
          if (this.labelAlign = l.align || this.autoLabelAlign(this.labelRotation)) n.align = this.labelAlign;
          c.forEach(function (b) {
            var a = (b = e[b]) && b.label,
                c = k.width,
                f = {};
            a && (a.attr(n), b.shortenLabel ? b.shortenLabel() : z && !c && "nowrap" !== k.whiteSpace && (z < a.textPxLength || "SPAN" === a.element.tagName) ? (f.width = z, h || (f.textOverflow = a.specificTextOverflow || q), a.css(f)) : a.styles && a.styles.width && !f.width && !c && a.css({
              width: null
            }), delete a.specificTextOverflow, b.rotation = n.rotation);
          }, this);
          this.tickRotCorr = b.rotCorr(w.b, this.labelRotation || 0, 0 !== this.side);
        },
        hasData: function hasData() {
          return this.series.some(function (a) {
            return a.hasData();
          }) || this.options.showEmpty && t(this.min) && t(this.max);
        },
        addTitle: function addTitle(c) {
          var b = this.chart.renderer,
              f = this.horiz,
              e = this.opposite,
              l = this.options.title,
              p,
              k = this.chart.styledMode;
          this.axisTitle || ((p = l.textAlign) || (p = (f ? {
            low: "left",
            middle: "center",
            high: "right"
          } : {
            low: e ? "right" : "left",
            middle: "center",
            high: e ? "left" : "right"
          })[l.align]), this.axisTitle = b.text(l.text, 0, 0, l.useHTML).attr({
            zIndex: 7,
            rotation: l.rotation || 0,
            align: p
          }).addClass("highcharts-axis-title"), k || this.axisTitle.css(a(l.style)), this.axisTitle.add(this.axisGroup), this.axisTitle.isNew = !0);
          k || l.style.width || this.isRadial || this.axisTitle.css({
            width: this.len
          });
          this.axisTitle[c ? "show" : "hide"](c);
        },
        generateTick: function generateTick(a) {
          var b = this.ticks;
          b[a] ? b[a].addLabel() : b[a] = new r(this, a);
        },
        getOffset: function getOffset() {
          var a = this,
              b = a.chart,
              c = b.renderer,
              f = a.options,
              e = a.tickPositions,
              l = a.ticks,
              k = a.horiz,
              d = a.side,
              m = b.inverted && !a.isZAxis ? [1, 0, 3, 2][d] : d,
              n,
              w = 0,
              h = 0,
              F = f.title,
              B = f.labels,
              L = 0,
              g = b.axisOffset;
          b = b.clipOffset;
          var H = [-1, 1, 1, -1][d],
              K = f.className,
              Q = a.axisParent;
          var y = a.hasData();
          a.showAxis = n = y || z(f.showEmpty, !0);
          a.staggerLines = a.horiz && B.staggerLines;
          a.axisGroup || (a.gridGroup = c.g("grid").attr({
            zIndex: f.gridZIndex || 1
          }).addClass("highcharts-" + this.coll.toLowerCase() + "-grid " + (K || "")).add(Q), a.axisGroup = c.g("axis").attr({
            zIndex: f.zIndex || 2
          }).addClass("highcharts-" + this.coll.toLowerCase() + " " + (K || "")).add(Q), a.labelGroup = c.g("axis-labels").attr({
            zIndex: B.zIndex || 7
          }).addClass("highcharts-" + a.coll.toLowerCase() + "-labels " + (K || "")).add(Q));
          y || a.isLinked ? (e.forEach(function (b, c) {
            a.generateTick(b, c);
          }), a.renderUnsquish(), a.reserveSpaceDefault = 0 === d || 2 === d || {
            1: "left",
            3: "right"
          }[d] === a.labelAlign, z(B.reserveSpace, "center" === a.labelAlign ? !0 : null, a.reserveSpaceDefault) && e.forEach(function (b) {
            L = Math.max(l[b].getLabelSize(), L);
          }), a.staggerLines && (L *= a.staggerLines), a.labelOffset = L * (a.opposite ? -1 : 1)) : v(l, function (b, a) {
            b.destroy();
            delete l[a];
          });

          if (F && F.text && !1 !== F.enabled && (a.addTitle(n), n && !1 !== F.reserveSpace)) {
            a.titleOffset = w = a.axisTitle.getBBox()[k ? "height" : "width"];
            var u = F.offset;
            h = t(u) ? 0 : z(F.margin, k ? 5 : 10);
          }

          a.renderLine();
          a.offset = H * z(f.offset, g[d] ? g[d] + (f.margin || 0) : 0);
          a.tickRotCorr = a.tickRotCorr || {
            x: 0,
            y: 0
          };
          c = 0 === d ? -a.labelMetrics().h : 2 === d ? a.tickRotCorr.y : 0;
          h = Math.abs(L) + h;
          L && (h = h - c + H * (k ? z(B.y, a.tickRotCorr.y + 8 * H) : B.x));
          a.axisTitleMargin = z(u, h);
          a.getMaxLabelDimensions && (a.maxLabelDimensions = a.getMaxLabelDimensions(l, e));
          k = this.tickSize("tick");
          g[d] = Math.max(g[d], a.axisTitleMargin + w + H * a.offset, h, e && e.length && k ? k[0] + H * a.offset : 0);
          f = f.offset ? 0 : 2 * Math.floor(a.axisLine.strokeWidth() / 2);
          b[m] = Math.max(b[m], f);
          q(this, "afterGetOffset");
        },
        getLinePath: function getLinePath(a) {
          var b = this.chart,
              c = this.opposite,
              f = this.offset,
              e = this.horiz,
              l = this.left + (c ? this.width : 0) + f;
          f = b.chartHeight - this.bottom - (c ? this.height : 0) + f;
          c && (a *= -1);
          return b.renderer.crispLine(["M", e ? this.left : l, e ? f : this.top, "L", e ? b.chartWidth - this.right : l, e ? f : b.chartHeight - this.bottom], a);
        },
        renderLine: function renderLine() {
          this.axisLine || (this.axisLine = this.chart.renderer.path().addClass("highcharts-axis-line").add(this.axisGroup), this.chart.styledMode || this.axisLine.attr({
            stroke: this.options.lineColor,
            "stroke-width": this.options.lineWidth,
            zIndex: 7
          }));
        },
        getTitlePosition: function getTitlePosition() {
          var a = this.horiz,
              b = this.left,
              c = this.top,
              f = this.len,
              e = this.options.title,
              l = a ? b : c,
              k = this.opposite,
              d = this.offset,
              m = e.x || 0,
              v = e.y || 0,
              n = this.axisTitle,
              w = this.chart.renderer.fontMetrics(e.style && e.style.fontSize, n);
          n = Math.max(n.getBBox(null, 0).height - w.h - 1, 0);
          f = {
            low: l + (a ? 0 : f),
            middle: l + f / 2,
            high: l + (a ? f : 0)
          }[e.align];
          b = (a ? c + this.height : b) + (a ? 1 : -1) * (k ? -1 : 1) * this.axisTitleMargin + [-n, n, w.f, -n][this.side];
          a = {
            x: a ? f + m : b + (k ? this.width : 0) + d + m,
            y: a ? b + v - (k ? this.height : 0) + d : f + v
          };
          q(this, "afterGetTitlePosition", {
            titlePosition: a
          });
          return a;
        },
        renderMinorTick: function renderMinorTick(a) {
          var b = this.chart.hasRendered && n(this.oldMin),
              c = this.minorTicks;
          c[a] || (c[a] = new r(this, a, "minor"));
          b && c[a].isNew && c[a].render(null, !0);
          c[a].render(null, !1, 1);
        },
        renderTick: function renderTick(a, b) {
          var c = this.isLinked,
              f = this.ticks,
              e = this.chart.hasRendered && n(this.oldMin);
          if (!c || a >= this.min && a <= this.max) f[a] || (f[a] = new r(this, a)), e && f[a].isNew && f[a].render(b, !0, -1), f[a].render(b);
        },
        render: function render() {
          var a = this,
              b = a.chart,
              c = a.options,
              f = a.isLog,
              e = a.isLinked,
              l = a.tickPositions,
              k = a.axisTitle,
              w = a.ticks,
              m = a.minorTicks,
              h = a.alternateBands,
              z = c.stackLabels,
              B = c.alternateGridColor,
              F = a.tickmarkOffset,
              L = a.axisLine,
              t = a.showAxis,
              g = E(b.renderer.globalAnimation),
              H,
              K;
          a.labelEdge.length = 0;
          a.overlap = !1;
          [w, m, h].forEach(function (b) {
            v(b, function (b) {
              b.isActive = !1;
            });
          });
          if (a.hasData() || e) a.minorTickInterval && !a.categories && a.getMinorTickPositions().forEach(function (b) {
            a.renderMinorTick(b);
          }), l.length && (l.forEach(function (b, c) {
            a.renderTick(b, c);
          }), F && (0 === a.min || a.single) && (w[-1] || (w[-1] = new r(a, -1, null, !0)), w[-1].render(-1))), B && l.forEach(function (c, e) {
            K = "undefined" !== typeof l[e + 1] ? l[e + 1] + F : a.max - F;
            0 === e % 2 && c < a.max && K <= a.max + (b.polar ? -F : F) && (h[c] || (h[c] = new d.PlotLineOrBand(a)), H = c + F, h[c].options = {
              from: f ? a.lin2log(H) : H,
              to: f ? a.lin2log(K) : K,
              color: B
            }, h[c].render(), h[c].isActive = !0);
          }), a._addedPlotLB || ((c.plotLines || []).concat(c.plotBands || []).forEach(function (b) {
            a.addPlotBandOrLine(b);
          }), a._addedPlotLB = !0);
          [w, m, h].forEach(function (a) {
            var c,
                f = [],
                e = g.duration;
            v(a, function (b, a) {
              b.isActive || (b.render(a, !1, 0), b.isActive = !1, f.push(a));
            });
            Q(function () {
              for (c = f.length; c--;) {
                a[f[c]] && !a[f[c]].isActive && (a[f[c]].destroy(), delete a[f[c]]);
              }
            }, a !== h && b.hasRendered && e ? e : 0);
          });
          L && (L[L.isPlaced ? "animate" : "attr"]({
            d: this.getLinePath(L.strokeWidth())
          }), L.isPlaced = !0, L[t ? "show" : "hide"](t));
          k && t && (c = a.getTitlePosition(), n(c.y) ? (k[k.isNew ? "attr" : "animate"](c), k.isNew = !1) : (k.attr("y", -9999), k.isNew = !0));
          z && z.enabled && a.renderStackTotals();
          a.isDirty = !1;
          q(this, "afterRender");
        },
        redraw: function redraw() {
          this.visible && (this.render(), this.plotLinesAndBands.forEach(function (a) {
            a.render();
          }));
          this.series.forEach(function (a) {
            a.isDirty = !0;
          });
        },
        keepProps: "extKey hcEvents names series userMax userMin".split(" "),
        destroy: function destroy(a) {
          var b = this,
              c = b.stacks,
              f = b.plotLinesAndBands,
              e;
          q(this, "destroy", {
            keepEvents: a
          });
          a || B(b);
          v(c, function (b, a) {
            D(b);
            c[a] = null;
          });
          [b.ticks, b.minorTicks, b.alternateBands].forEach(function (b) {
            D(b);
          });
          if (f) for (a = f.length; a--;) {
            f[a].destroy();
          }
          "stackTotalGroup axisLine axisTitle axisGroup gridGroup labelGroup cross scrollbar".split(" ").forEach(function (a) {
            b[a] && (b[a] = b[a].destroy());
          });

          for (e in b.plotLinesAndBandsGroups) {
            b.plotLinesAndBandsGroups[e] = b.plotLinesAndBandsGroups[e].destroy();
          }

          v(b, function (a, c) {
            -1 === b.keepProps.indexOf(c) && delete b[c];
          });
        },
        drawCrosshair: function drawCrosshair(a, b) {
          var c = this.crosshair,
              f = z(c.snap, !0),
              e,
              l = this.cross,
              k = this.chart;
          q(this, "drawCrosshair", {
            e: a,
            point: b
          });
          a || (a = this.cross && this.cross.e);

          if (this.crosshair && !1 !== (t(b) || !f)) {
            f ? t(b) && (e = z("colorAxis" !== this.coll ? b.crosshairPos : null, this.isXAxis ? b.plotX : this.len - b.plotY)) : e = a && (this.horiz ? a.chartX - this.pos : this.len - a.chartY + this.pos);

            if (t(e)) {
              var d = {
                value: b && (this.isXAxis ? b.x : z(b.stackY, b.y)),
                translatedValue: e
              };
              k.polar && N(d, {
                isCrosshair: !0,
                chartX: a && a.chartX,
                chartY: a && a.chartY,
                point: b
              });
              d = this.getPlotLinePath(d) || null;
            }

            if (!t(d)) {
              this.hideCrosshair();
              return;
            }

            f = this.categories && !this.isRadial;
            l || (this.cross = l = k.renderer.path().addClass("highcharts-crosshair highcharts-crosshair-" + (f ? "category " : "thin ") + c.className).attr({
              zIndex: z(c.zIndex, 2)
            }).add(), k.styledMode || (l.attr({
              stroke: c.color || (f ? I("#ccd6eb").setOpacity(.25).get() : "#cccccc"),
              "stroke-width": z(c.width, 1)
            }).css({
              "pointer-events": "none"
            }), c.dashStyle && l.attr({
              dashstyle: c.dashStyle
            })));
            l.show().attr({
              d: d
            });
            f && !c.width && l.attr({
              "stroke-width": this.transA
            });
            this.cross.e = a;
          } else this.hideCrosshair();

          q(this, "afterDrawCrosshair", {
            e: a,
            point: b
          });
        },
        hideCrosshair: function hideCrosshair() {
          this.cross && this.cross.hide();
          q(this, "afterHideCrosshair");
        }
      });
      return d.Axis = g;
    });
    S(r, "parts/DateTimeAxis.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.getMagnitude,
          u = g.normalizeTickInterval,
          I = g.timeUnits;
      d = d.Axis;

      d.prototype.getTimeTicks = function () {
        return this.chart.time.getTimeTicks.apply(this.chart.time, arguments);
      };

      d.prototype.normalizeTimeTickInterval = function (d, g) {
        var A = g || [["millisecond", [1, 2, 5, 10, 20, 25, 50, 100, 200, 500]], ["second", [1, 2, 5, 10, 15, 30]], ["minute", [1, 2, 5, 10, 15, 30]], ["hour", [1, 2, 3, 4, 6, 8, 12]], ["day", [1, 2]], ["week", [1, 2]], ["month", [1, 2, 3, 4, 6]], ["year", null]];
        g = A[A.length - 1];
        var G = I[g[0]],
            E = g[1],
            y;

        for (y = 0; y < A.length && !(g = A[y], G = I[g[0]], E = g[1], A[y + 1] && d <= (G * E[E.length - 1] + I[A[y + 1][0]]) / 2); y++) {
        }

        G === I.year && d < 5 * G && (E = [1, 2, 5]);
        d = u(d / G, E, "year" === g[0] ? Math.max(r(d / G), 1) : 1);
        return {
          unitRange: G,
          count: d,
          unitName: g[0]
        };
      };
    });
    S(r, "parts/LogarithmicAxis.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.getMagnitude,
          u = g.normalizeTickInterval,
          I = g.pick;
      d = d.Axis;

      d.prototype.getLogTickPositions = function (d, g, A, G) {
        var E = this.options,
            y = this.len,
            t = [];
        G || (this._minorAutoInterval = null);
        if (.5 <= d) d = Math.round(d), t = this.getLinearTickPositions(d, g, A);else if (.08 <= d) {
          y = Math.floor(g);
          var D, h;

          for (E = .3 < d ? [1, 2, 4] : .15 < d ? [1, 2, 4, 6, 8] : [1, 2, 3, 4, 5, 6, 7, 8, 9]; y < A + 1 && !h; y++) {
            var N = E.length;

            for (D = 0; D < N && !h; D++) {
              var q = this.log2lin(this.lin2log(y) * E[D]);
              q > g && (!G || P <= A) && "undefined" !== typeof P && t.push(P);
              P > A && (h = !0);
              var P = q;
            }
          }
        } else g = this.lin2log(g), A = this.lin2log(A), d = G ? this.getMinorTickInterval() : E.tickInterval, d = I("auto" === d ? null : d, this._minorAutoInterval, E.tickPixelInterval / (G ? 5 : 1) * (A - g) / ((G ? y / this.tickPositions.length : y) || 1)), d = u(d, null, r(d)), t = this.getLinearTickPositions(d, g, A).map(this.log2lin), G || (this._minorAutoInterval = d / 5);
        G || (this.tickInterval = d);
        return t;
      };

      d.prototype.log2lin = function (d) {
        return Math.log(d) / Math.LN10;
      };

      d.prototype.lin2log = function (d) {
        return Math.pow(10, d);
      };
    });
    S(r, "parts/PlotLineOrBand.js", [r["parts/Globals.js"], r["parts/Axis.js"], r["parts/Utilities.js"]], function (d, g, r) {
      var u = r.arrayMax,
          I = r.arrayMin,
          M = r.defined,
          E = r.destroyObjectProperties,
          A = r.erase,
          G = r.extend,
          J = r.merge,
          y = r.objectEach,
          t = r.pick,
          D = function () {
        function h(d, h) {
          this.axis = d;
          h && (this.options = h, this.id = h.id);
        }

        h.prototype.render = function () {
          d.fireEvent(this, "render");
          var h = this,
              q = h.axis,
              g = q.horiz,
              e = h.options,
              c = e.label,
              k = h.label,
              n = e.to,
              f = e.from,
              a = e.value,
              l = M(f) && M(n),
              v = M(a),
              z = h.svgElem,
              w = !z,
              B = [],
              L = e.color,
              Q = t(e.zIndex, 0),
              H = e.events;
          B = {
            "class": "highcharts-plot-" + (l ? "band " : "line ") + (e.className || "")
          };
          var K = {},
              p = q.chart.renderer,
              b = l ? "bands" : "lines";
          q.isLog && (f = q.log2lin(f), n = q.log2lin(n), a = q.log2lin(a));
          q.chart.styledMode || (v ? (B.stroke = L || "#999999", B["stroke-width"] = t(e.width, 1), e.dashStyle && (B.dashstyle = e.dashStyle)) : l && (B.fill = L || "#e6ebf5", e.borderWidth && (B.stroke = e.borderColor, B["stroke-width"] = e.borderWidth)));
          K.zIndex = Q;
          b += "-" + Q;
          (L = q.plotLinesAndBandsGroups[b]) || (q.plotLinesAndBandsGroups[b] = L = p.g("plot-" + b).attr(K).add());
          w && (h.svgElem = z = p.path().attr(B).add(L));
          if (v) B = q.getPlotLinePath({
            value: a,
            lineWidth: z.strokeWidth(),
            acrossPanes: e.acrossPanes
          });else if (l) B = q.getPlotBandPath(f, n, e);else return;
          (w || !z.d) && B && B.length ? (z.attr({
            d: B
          }), H && y(H, function (b, a) {
            z.on(a, function (b) {
              H[a].apply(h, [b]);
            });
          })) : z && (B ? (z.show(!0), z.animate({
            d: B
          })) : z.d && (z.hide(), k && (h.label = k = k.destroy())));
          c && (M(c.text) || M(c.formatter)) && B && B.length && 0 < q.width && 0 < q.height && !B.isFlat ? (c = J({
            align: g && l && "center",
            x: g ? !l && 4 : 10,
            verticalAlign: !g && l && "middle",
            y: g ? l ? 16 : 10 : l ? 6 : -4,
            rotation: g && !l && 90
          }, c), this.renderLabel(c, B, l, Q)) : k && k.hide();
          return h;
        };

        h.prototype.renderLabel = function (d, h, g, e) {
          var c = this.label,
              k = this.axis.chart.renderer;
          c || (c = {
            align: d.textAlign || d.align,
            rotation: d.rotation,
            "class": "highcharts-plot-" + (g ? "band" : "line") + "-label " + (d.className || "")
          }, c.zIndex = e, e = this.getLabelText(d), this.label = c = k.text(e, 0, 0, d.useHTML).attr(c).add(), this.axis.chart.styledMode || c.css(d.style));
          k = h.xBounds || [h[1], h[4], g ? h[6] : h[1]];
          h = h.yBounds || [h[2], h[5], g ? h[7] : h[2]];
          g = I(k);
          e = I(h);
          c.align(d, !1, {
            x: g,
            y: e,
            width: u(k) - g,
            height: u(h) - e
          });
          c.show(!0);
        };

        h.prototype.getLabelText = function (d) {
          return M(d.formatter) ? d.formatter.call(this) : d.text;
        };

        h.prototype.destroy = function () {
          A(this.axis.plotLinesAndBands, this);
          delete this.axis;
          E(this);
        };

        return h;
      }();

      G(g.prototype, {
        getPlotBandPath: function getPlotBandPath(d, g) {
          var h = this.getPlotLinePath({
            value: g,
            force: !0,
            acrossPanes: this.options.acrossPanes
          }),
              t = this.getPlotLinePath({
            value: d,
            force: !0,
            acrossPanes: this.options.acrossPanes
          }),
              e = [],
              c = this.horiz,
              k = 1;
          d = d < this.min && g < this.min || d > this.max && g > this.max;

          if (t && h) {
            if (d) {
              var n = t.toString() === h.toString();
              k = 0;
            }

            for (d = 0; d < t.length; d += 6) {
              c && h[d + 1] === t[d + 1] ? (h[d + 1] += k, h[d + 4] += k) : c || h[d + 2] !== t[d + 2] || (h[d + 2] += k, h[d + 5] += k), e.push("M", t[d + 1], t[d + 2], "L", t[d + 4], t[d + 5], h[d + 4], h[d + 5], h[d + 1], h[d + 2], "z"), e.isFlat = n;
            }
          }

          return e;
        },
        addPlotBand: function addPlotBand(d) {
          return this.addPlotBandOrLine(d, "plotBands");
        },
        addPlotLine: function addPlotLine(d) {
          return this.addPlotBandOrLine(d, "plotLines");
        },
        addPlotBandOrLine: function addPlotBandOrLine(d, g) {
          var h = new D(this, d).render(),
              t = this.userOptions;

          if (h) {
            if (g) {
              var e = t[g] || [];
              e.push(d);
              t[g] = e;
            }

            this.plotLinesAndBands.push(h);
          }

          return h;
        },
        removePlotBandOrLine: function removePlotBandOrLine(d) {
          for (var h = this.plotLinesAndBands, q = this.options, g = this.userOptions, e = h.length; e--;) {
            h[e].id === d && h[e].destroy();
          }

          [q.plotLines || [], g.plotLines || [], q.plotBands || [], g.plotBands || []].forEach(function (c) {
            for (e = c.length; e--;) {
              c[e].id === d && A(c, c[e]);
            }
          });
        },
        removePlotBand: function removePlotBand(d) {
          this.removePlotBandOrLine(d);
        },
        removePlotLine: function removePlotLine(d) {
          this.removePlotBandOrLine(d);
        }
      });
      d.PlotLineOrBand = D;
      return d.PlotLineOrBand;
    });
    S(r, "parts/Tooltip.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.clamp,
          u = g.css,
          I = g.defined,
          M = g.discardElement,
          E = g.extend,
          A = g.format,
          G = g.isNumber,
          J = g.isString,
          y = g.merge,
          t = g.pick,
          D = g.splat,
          h = g.syncTimeout,
          N = g.timeUnits;

      var q = d.doc,
          P = function () {
        function e(c, e) {
          this.crosshairs = [];
          this.distance = 0;
          this.isHidden = !0;
          this.isSticky = !1;
          this.now = {};
          this.options = {};
          this.outside = !1;
          this.chart = c;
          this.init(c, e);
        }

        e.prototype.applyFilter = function () {
          var c = this.chart;
          c.renderer.definition({
            tagName: "filter",
            id: "drop-shadow-" + c.index,
            opacity: .5,
            children: [{
              tagName: "feGaussianBlur",
              "in": "SourceAlpha",
              stdDeviation: 1
            }, {
              tagName: "feOffset",
              dx: 1,
              dy: 1
            }, {
              tagName: "feComponentTransfer",
              children: [{
                tagName: "feFuncA",
                type: "linear",
                slope: .3
              }]
            }, {
              tagName: "feMerge",
              children: [{
                tagName: "feMergeNode"
              }, {
                tagName: "feMergeNode",
                "in": "SourceGraphic"
              }]
            }]
          });
          c.renderer.definition({
            tagName: "style",
            textContent: ".highcharts-tooltip-" + c.index + "{filter:url(#drop-shadow-" + c.index + ")}"
          });
        };

        e.prototype.bodyFormatter = function (c) {
          return c.map(function (c) {
            var e = c.series.tooltipOptions;
            return (e[(c.point.formatPrefix || "point") + "Formatter"] || c.point.tooltipFormatter).call(c.point, e[(c.point.formatPrefix || "point") + "Format"] || "");
          });
        };

        e.prototype.cleanSplit = function (c) {
          this.chart.series.forEach(function (e) {
            var d = e && e.tt;
            d && (!d.isActive || c ? e.tt = d.destroy() : d.isActive = !1);
          });
        };

        e.prototype.defaultFormatter = function (c) {
          var e = this.points || D(this);
          var d = [c.tooltipFooterHeaderFormatter(e[0])];
          d = d.concat(c.bodyFormatter(e));
          d.push(c.tooltipFooterHeaderFormatter(e[0], !0));
          return d;
        };

        e.prototype.destroy = function () {
          this.label && (this.label = this.label.destroy());
          this.split && this.tt && (this.cleanSplit(this.chart, !0), this.tt = this.tt.destroy());
          this.renderer && (this.renderer = this.renderer.destroy(), M(this.container));
          g.clearTimeout(this.hideTimer);
          g.clearTimeout(this.tooltipTimeout);
        };

        e.prototype.getAnchor = function (c, e) {
          var d = this.chart,
              f = d.pointer,
              a = d.inverted,
              l = d.plotTop,
              k = d.plotLeft,
              h = 0,
              w = 0,
              B,
              q;
          c = D(c);
          this.followPointer && e ? ("undefined" === typeof e.chartX && (e = f.normalize(e)), c = [e.chartX - k, e.chartY - l]) : c[0].tooltipPos ? c = c[0].tooltipPos : (c.forEach(function (c) {
            B = c.series.yAxis;
            q = c.series.xAxis;
            h += c.plotX + (!a && q ? q.left - k : 0);
            w += (c.plotLow ? (c.plotLow + c.plotHigh) / 2 : c.plotY) + (!a && B ? B.top - l : 0);
          }), h /= c.length, w /= c.length, c = [a ? d.plotWidth - w : h, this.shared && !a && 1 < c.length && e ? e.chartY - l : a ? d.plotHeight - h : w]);
          return c.map(Math.round);
        };

        e.prototype.getDateFormat = function (c, e, d, f) {
          var a = this.chart.time,
              l = a.dateFormat("%m-%d %H:%M:%S.%L", e),
              k = {
            millisecond: 15,
            second: 12,
            minute: 9,
            hour: 6,
            day: 3
          },
              n = "millisecond";

          for (w in N) {
            if (c === N.week && +a.dateFormat("%w", e) === d && "00:00:00.000" === l.substr(6)) {
              var w = "week";
              break;
            }

            if (N[w] > c) {
              w = n;
              break;
            }

            if (k[w] && l.substr(k[w]) !== "01-01 00:00:00.000".substr(k[w])) break;
            "week" !== w && (n = w);
          }

          if (w) var h = a.resolveDTLFormat(f[w]).main;
          return h;
        };

        e.prototype.getLabel = function () {
          var c,
              e = this,
              n = this.chart.renderer,
              f = this.chart.styledMode,
              a = this.options,
              l = "tooltip" + (I(a.className) ? " " + a.className : ""),
              v = (null === (c = a.style) || void 0 === c ? void 0 : c.pointerEvents) || (!this.followPointer && a.stickOnContact ? "auto" : "none"),
              h;

          c = function c() {
            e.inContact = !0;
          };

          var w = function w() {
            var a = e.chart.hoverSeries;
            e.inContact = !1;
            if (a && a.onMouseOut) a.onMouseOut();
          };

          if (!this.label) {
            this.outside && (this.container = h = d.doc.createElement("div"), h.className = "highcharts-tooltip-container", u(h, {
              position: "absolute",
              top: "1px",
              pointerEvents: v,
              zIndex: 3
            }), d.doc.body.appendChild(h), this.renderer = n = new d.Renderer(h, 0, 0, {}, void 0, void 0, n.styledMode));
            this.split ? this.label = n.g(l) : (this.label = n.label("", 0, 0, a.shape || "callout", null, null, a.useHTML, null, l).attr({
              padding: a.padding,
              r: a.borderRadius
            }), f || this.label.attr({
              fill: a.backgroundColor,
              "stroke-width": a.borderWidth
            }).css(a.style).css({
              pointerEvents: v
            }).shadow(a.shadow));
            f && (this.applyFilter(), this.label.addClass("highcharts-tooltip-" + this.chart.index));

            if (e.outside && !e.split) {
              var B = {
                x: this.label.xSetter,
                y: this.label.ySetter
              };

              this.label.xSetter = function (a, c) {
                B[c].call(this.label, e.distance);
                h.style.left = a + "px";
              };

              this.label.ySetter = function (a, c) {
                B[c].call(this.label, e.distance);
                h.style.top = a + "px";
              };
            }

            this.label.on("mouseenter", c).on("mouseleave", w).attr({
              zIndex: 8
            }).add();
          }

          return this.label;
        };

        e.prototype.getPosition = function (c, e, d) {
          var f = this.chart,
              a = this.distance,
              l = {},
              k = f.inverted && d.h || 0,
              n,
              w = this.outside,
              h = w ? q.documentElement.clientWidth - 2 * a : f.chartWidth,
              g = w ? Math.max(q.body.scrollHeight, q.documentElement.scrollHeight, q.body.offsetHeight, q.documentElement.offsetHeight, q.documentElement.clientHeight) : f.chartHeight,
              Q = f.pointer.getChartPosition(),
              H = f.containerScaling,
              K = function K(b) {
            return H ? b * H.scaleX : b;
          },
              p = function p(b) {
            return H ? b * H.scaleY : b;
          },
              b = function b(_b) {
            var l = "x" === _b;
            return [_b, l ? h : g, l ? c : e].concat(w ? [l ? K(c) : p(e), l ? Q.left - a + K(d.plotX + f.plotLeft) : Q.top - a + p(d.plotY + f.plotTop), 0, l ? h : g] : [l ? c : e, l ? d.plotX + f.plotLeft : d.plotY + f.plotTop, l ? f.plotLeft : f.plotTop, l ? f.plotLeft + f.plotWidth : f.plotTop + f.plotHeight]);
          },
              C = b("y"),
              x = b("x"),
              R = !this.followPointer && t(d.ttBelow, !f.inverted === !!d.negative),
              O = function O(b, c, f, e, d, m, v) {
            var n = "y" === b ? p(a) : K(a),
                w = (f - e) / 2,
                x = e < d - a,
                h = d + a + e < c,
                F = d - n - f + w;
            d = d + n - w;
            if (R && h) l[b] = d;else if (!R && x) l[b] = F;else if (x) l[b] = Math.min(v - e, 0 > F - k ? F : F - k);else if (h) l[b] = Math.max(m, d + k + f > c ? d : d + k);else return !1;
          },
              y = function y(b, c, f, e, d) {
            var k;
            d < a || d > c - a ? k = !1 : l[b] = d < f / 2 ? 1 : d > c - e / 2 ? c - e - 2 : d - f / 2;
            return k;
          },
              u = function u(b) {
            var a = C;
            C = x;
            x = a;
            n = b;
          },
              m = function m() {
            !1 !== O.apply(0, C) ? !1 !== y.apply(0, x) || n || (u(!0), m()) : n ? l.x = l.y = 0 : (u(!0), m());
          };

          (f.inverted || 1 < this.len) && u();
          m();
          return l;
        };

        e.prototype.getXDateFormat = function (c, e, d) {
          e = e.dateTimeLabelFormats;
          var f = d && d.closestPointRange;
          return (f ? this.getDateFormat(f, c.x, d.options.startOfWeek, e) : e.day) || e.year;
        };

        e.prototype.hide = function (c) {
          var e = this;
          g.clearTimeout(this.hideTimer);
          c = t(c, this.options.hideDelay, 500);
          this.isHidden || (this.hideTimer = h(function () {
            e.getLabel()[c ? "fadeOut" : "hide"]();
            e.isHidden = !0;
          }, c));
        };

        e.prototype.init = function (c, e) {
          this.chart = c;
          this.options = e;
          this.crosshairs = [];
          this.now = {
            x: 0,
            y: 0
          };
          this.isHidden = !0;
          this.split = e.split && !c.inverted && !c.polar;
          this.shared = e.shared || this.split;
          this.outside = t(e.outside, !(!c.scrollablePixelsX && !c.scrollablePixelsY));
        };

        e.prototype.isStickyOnContact = function () {
          return !(this.followPointer || !this.options.stickOnContact || !this.inContact);
        };

        e.prototype.move = function (c, e, d, f) {
          var a = this,
              l = a.now,
              k = !1 !== a.options.animation && !a.isHidden && (1 < Math.abs(c - l.x) || 1 < Math.abs(e - l.y)),
              n = a.followPointer || 1 < a.len;
          E(l, {
            x: k ? (2 * l.x + c) / 3 : c,
            y: k ? (l.y + e) / 2 : e,
            anchorX: n ? void 0 : k ? (2 * l.anchorX + d) / 3 : d,
            anchorY: n ? void 0 : k ? (l.anchorY + f) / 2 : f
          });
          a.getLabel().attr(l);
          a.drawTracker();
          k && (g.clearTimeout(this.tooltipTimeout), this.tooltipTimeout = setTimeout(function () {
            a && a.move(c, e, d, f);
          }, 32));
        };

        e.prototype.refresh = function (c, e) {
          var k = this.chart,
              f = this.options,
              a = c,
              l = {},
              v = [],
              h = f.formatter || this.defaultFormatter;
          l = this.shared;
          var w = k.styledMode;

          if (f.enabled) {
            g.clearTimeout(this.hideTimer);
            this.followPointer = D(a)[0].series.tooltipOptions.followPointer;
            var B = this.getAnchor(a, e);
            e = B[0];
            var q = B[1];
            !l || a.series && a.series.noSharedTooltip ? l = a.getLabelConfig() : (k.pointer.applyInactiveState(a), a.forEach(function (a) {
              a.setState("hover");
              v.push(a.getLabelConfig());
            }), l = {
              x: a[0].category,
              y: a[0].y
            }, l.points = v, a = a[0]);
            this.len = v.length;
            k = h.call(l, this);
            h = a.series;
            this.distance = t(h.tooltipOptions.distance, 16);
            !1 === k ? this.hide() : (this.split ? this.renderSplit(k, D(c)) : (c = this.getLabel(), f.style.width && !w || c.css({
              width: this.chart.spacingBox.width
            }), c.attr({
              text: k && k.join ? k.join("") : k
            }), c.removeClass(/highcharts-color-[\d]+/g).addClass("highcharts-color-" + t(a.colorIndex, h.colorIndex)), w || c.attr({
              stroke: f.borderColor || a.color || h.color || "#666666"
            }), this.updatePosition({
              plotX: e,
              plotY: q,
              negative: a.negative,
              ttBelow: a.ttBelow,
              h: B[2] || 0
            })), this.isHidden && this.label && this.label.attr({
              opacity: 1
            }).show(), this.isHidden = !1);
            d.fireEvent(this, "refresh");
          }
        };

        e.prototype.renderSplit = function (c, e) {
          function k(b, a, c, f, e) {
            void 0 === e && (e = !0);
            c ? (a = u ? 0 : A, b = r(b - f / 2, O.left, O.right - f)) : (a -= m, b = e ? b - f - C : b + C, b = r(b, e ? b : O.left, O.right));
            return {
              x: b,
              y: a
            };
          }

          var f = this,
              a = f.chart,
              l = f.chart,
              v = l.plotHeight,
              h = l.plotLeft,
              w = l.plotTop,
              B = l.pointer,
              q = l.renderer,
              g = l.scrollablePixelsY,
              H = void 0 === g ? 0 : g;
          g = l.scrollingContainer;
          g = void 0 === g ? {
            scrollLeft: 0,
            scrollTop: 0
          } : g;
          var K = g.scrollLeft,
              p = g.scrollTop,
              b = l.styledMode,
              C = f.distance,
              x = f.options,
              R = f.options.positioner,
              O = {
            left: K,
            right: K + l.chartWidth,
            top: p,
            bottom: p + l.chartHeight
          },
              y = f.getLabel(),
              u = !(!a.xAxis[0] || !a.xAxis[0].opposite),
              m = w + p,
              D = 0,
              A = v - H;
          J(c) && (c = [!1, c]);
          c = c.slice(0, e.length + 1).reduce(function (a, c, l) {
            if (!1 !== c && "" !== c) {
              l = e[l - 1] || {
                isHeader: !0,
                plotX: e[0].plotX,
                plotY: v,
                series: {}
              };
              var d = l.isHeader,
                  n = d ? f : l.series,
                  F = n.tt,
                  z = l.isHeader;
              var B = l.series;
              var g = "highcharts-color-" + t(l.colorIndex, B.colorIndex, "none");
              F || (F = {
                padding: x.padding,
                r: x.borderRadius
              }, b || (F.fill = x.backgroundColor, F["stroke-width"] = x.borderWidth), F = q.label("", 0, 0, x[z ? "headerShape" : "shape"] || "callout", void 0, void 0, x.useHTML).addClass((z ? "highcharts-tooltip-header " : "") + "highcharts-tooltip-box " + g).attr(F).add(y));
              F.isActive = !0;
              F.attr({
                text: c
              });
              b || F.css(x.style).shadow(x.shadow).attr({
                stroke: x.borderColor || l.color || B.color || "#333333"
              });
              c = n.tt = F;
              z = c.getBBox();
              n = z.width + c.strokeWidth();
              d && (D = z.height, A += D, u && (m -= D));
              B = l.plotX;
              B = void 0 === B ? 0 : B;
              g = l.plotY;
              g = void 0 === g ? 0 : g;
              var L = l.series;

              if (l.isHeader) {
                B = h + B;
                var K = w + v / 2;
              } else F = L.xAxis, L = L.yAxis, B = F.pos + r(B, -C, F.len + C), L.pos + g >= p + w && L.pos + g <= p + w + v - H && (K = L.pos + g);

              B = r(B, O.left - C, O.right + C);
              "number" === typeof K ? (z = z.height + 1, g = R ? R.call(f, n, z, l) : k(B, K, d, n), a.push({
                align: R ? 0 : void 0,
                anchorX: B,
                anchorY: K,
                boxWidth: n,
                point: l,
                rank: t(g.rank, d ? 1 : 0),
                size: z,
                target: g.y,
                tt: c,
                x: g.x
              })) : c.isActive = !1;
            }

            return a;
          }, []);
          !R && c.some(function (b) {
            return b.x < O.left;
          }) && (c = c.map(function (b) {
            var a = k(b.anchorX, b.anchorY, b.point.isHeader, b.boxWidth, !1);
            return E(b, {
              target: a.y,
              x: a.x
            });
          }));
          f.cleanSplit();
          d.distribute(c, A);
          c.forEach(function (b) {
            var a = b.pos;
            b.tt.attr({
              visibility: "undefined" === typeof a ? "hidden" : "inherit",
              x: b.x,
              y: a + m,
              anchorX: b.anchorX,
              anchorY: b.anchorY
            });
          });
          c = f.container;
          a = f.renderer;
          f.outside && c && a && (l = y.getBBox(), a.setSize(l.width + l.x, l.height + l.y, !1), B = B.getChartPosition(), c.style.left = B.left + "px", c.style.top = B.top + "px");
        };

        e.prototype.drawTracker = function () {
          if (this.followPointer || !this.options.stickOnContact) this.tracker && this.tracker.destroy();else {
            var c = this.chart,
                e = this.label,
                d = c.hoverPoint;

            if (e && d) {
              var f = {
                x: 0,
                y: 0,
                width: 0,
                height: 0
              };
              d = this.getAnchor(d);
              var a = e.getBBox();
              d[0] += c.plotLeft - e.translateX;
              d[1] += c.plotTop - e.translateY;
              f.x = Math.min(0, d[0]);
              f.y = Math.min(0, d[1]);
              f.width = 0 > d[0] ? Math.max(Math.abs(d[0]), a.width - d[0]) : Math.max(Math.abs(d[0]), a.width);
              f.height = 0 > d[1] ? Math.max(Math.abs(d[1]), a.height - Math.abs(d[1])) : Math.max(Math.abs(d[1]), a.height);
              this.tracker ? this.tracker.attr(f) : (this.tracker = e.renderer.rect(f).addClass("highcharts-tracker").add(e), c.styledMode || this.tracker.attr({
                fill: "rgba(0,0,0,0)"
              }));
            }
          }
        };

        e.prototype.styledModeFormat = function (c) {
          return c.replace('style="font-size: 10px"', 'class="highcharts-header"').replace(/style="color:{(point|series)\.color}"/g, 'class="highcharts-color-{$1.colorIndex}"');
        };

        e.prototype.tooltipFooterHeaderFormatter = function (c, e) {
          var k = e ? "footer" : "header",
              f = c.series,
              a = f.tooltipOptions,
              l = a.xDateFormat,
              v = f.xAxis,
              h = v && "datetime" === v.options.type && G(c.key),
              w = a[k + "Format"];
          e = {
            isFooter: e,
            labelConfig: c
          };
          d.fireEvent(this, "headerFormatter", e, function (e) {
            h && !l && (l = this.getXDateFormat(c, a, v));
            h && l && (c.point && c.point.tooltipDateKeys || ["key"]).forEach(function (a) {
              w = w.replace("{point." + a + "}", "{point." + a + ":" + l + "}");
            });
            f.chart.styledMode && (w = this.styledModeFormat(w));
            e.text = A(w, {
              point: c,
              series: f
            }, this.chart);
          });
          return e.text;
        };

        e.prototype.update = function (c) {
          this.destroy();
          y(!0, this.chart.options.tooltip.userOptions, c);
          this.init(this.chart, y(!0, this.options, c));
        };

        e.prototype.updatePosition = function (c) {
          var e = this.chart,
              d = e.pointer,
              f = this.getLabel(),
              a = c.plotX + e.plotLeft,
              l = c.plotY + e.plotTop;
          d = d.getChartPosition();
          c = (this.options.positioner || this.getPosition).call(this, f.width, f.height, c);

          if (this.outside) {
            var v = (this.options.borderWidth || 0) + 2 * this.distance;
            this.renderer.setSize(f.width + v, f.height + v, !1);
            if (e = e.containerScaling) u(this.container, {
              transform: "scale(" + e.scaleX + ", " + e.scaleY + ")"
            }), a *= e.scaleX, l *= e.scaleY;
            a += d.left - c.x;
            l += d.top - c.y;
          }

          this.move(Math.round(c.x), Math.round(c.y || 0), a, l);
        };

        return e;
      }();

      d.Tooltip = P;
      return d.Tooltip;
    });
    S(r, "parts/Pointer.js", [r["parts/Globals.js"], r["parts/Utilities.js"], r["parts/Tooltip.js"], r["parts/Color.js"]], function (d, g, r, u) {
      var I = g.addEvent,
          M = g.attr,
          E = g.css,
          A = g.defined,
          G = g.extend,
          J = g.find,
          y = g.fireEvent,
          t = g.isNumber,
          D = g.isObject,
          h = g.objectEach,
          N = g.offset,
          q = g.pick,
          P = g.splat,
          e = u.parse,
          c = d.charts,
          k = d.noop;

      g = function () {
        function n(c, a) {
          this.lastValidTouch = {};
          this.pinchDown = [];
          this.runChartClick = !1;
          this.chart = c;
          this.hasDragged = !1;
          this.options = a;

          this.unbindContainerMouseLeave = function () {};

          this.init(c, a);
        }

        n.prototype.applyInactiveState = function (c) {
          var a = [],
              f;
          (c || []).forEach(function (c) {
            f = c.series;
            a.push(f);
            f.linkedParent && a.push(f.linkedParent);
            f.linkedSeries && (a = a.concat(f.linkedSeries));
            f.navigatorSeries && a.push(f.navigatorSeries);
          });
          this.chart.series.forEach(function (c) {
            -1 === a.indexOf(c) ? c.setState("inactive", !0) : c.options.inactiveOtherPoints && c.setAllPointsToState("inactive");
          });
        };

        n.prototype.destroy = function () {
          var c = this;
          "undefined" !== typeof c.unDocMouseMove && c.unDocMouseMove();
          this.unbindContainerMouseLeave();
          d.chartCount || (d.unbindDocumentMouseUp && (d.unbindDocumentMouseUp = d.unbindDocumentMouseUp()), d.unbindDocumentTouchEnd && (d.unbindDocumentTouchEnd = d.unbindDocumentTouchEnd()));
          clearInterval(c.tooltipTimeout);
          h(c, function (a, f) {
            c[f] = null;
          });
        };

        n.prototype.drag = function (c) {
          var a = this.chart,
              f = a.options.chart,
              d = c.chartX,
              k = c.chartY,
              w = this.zoomHor,
              n = this.zoomVert,
              h = a.plotLeft,
              q = a.plotTop,
              g = a.plotWidth,
              t = a.plotHeight,
              p = this.selectionMarker,
              b = this.mouseDownX || 0,
              C = this.mouseDownY || 0,
              x = D(f.panning) ? f.panning && f.panning.enabled : f.panning,
              y = f.panKey && c[f.panKey + "Key"];
          if (!p || !p.touch) if (d < h ? d = h : d > h + g && (d = h + g), k < q ? k = q : k > q + t && (k = q + t), this.hasDragged = Math.sqrt(Math.pow(b - d, 2) + Math.pow(C - k, 2)), 10 < this.hasDragged) {
            var O = a.isInsidePlot(b - h, C - q);
            a.hasCartesianSeries && (this.zoomX || this.zoomY) && O && !y && !p && (this.selectionMarker = p = a.renderer.rect(h, q, w ? 1 : g, n ? 1 : t, 0).attr({
              "class": "highcharts-selection-marker",
              zIndex: 7
            }).add(), a.styledMode || p.attr({
              fill: f.selectionMarkerFill || e("#335cad").setOpacity(.25).get()
            }));
            p && w && (d -= b, p.attr({
              width: Math.abs(d),
              x: (0 < d ? 0 : d) + b
            }));
            p && n && (d = k - C, p.attr({
              height: Math.abs(d),
              y: (0 < d ? 0 : d) + C
            }));
            O && !p && x && a.pan(c, f.panning);
          }
        };

        n.prototype.dragStart = function (c) {
          var a = this.chart;
          a.mouseIsDown = c.type;
          a.cancelClick = !1;
          a.mouseDownX = this.mouseDownX = c.chartX;
          a.mouseDownY = this.mouseDownY = c.chartY;
        };

        n.prototype.drop = function (c) {
          var a = this,
              f = this.chart,
              e = this.hasPinched;

          if (this.selectionMarker) {
            var d = {
              originalEvent: c,
              xAxis: [],
              yAxis: []
            },
                k = this.selectionMarker,
                n = k.attr ? k.attr("x") : k.x,
                h = k.attr ? k.attr("y") : k.y,
                q = k.attr ? k.attr("width") : k.width,
                g = k.attr ? k.attr("height") : k.height,
                K;
            if (this.hasDragged || e) f.axes.forEach(function (f) {
              if (f.zoomEnabled && A(f.min) && (e || a[{
                xAxis: "zoomX",
                yAxis: "zoomY"
              }[f.coll]])) {
                var b = f.horiz,
                    l = "touchend" === c.type ? f.minPixelPadding : 0,
                    k = f.toValue((b ? n : h) + l);
                b = f.toValue((b ? n + q : h + g) - l);
                d[f.coll].push({
                  axis: f,
                  min: Math.min(k, b),
                  max: Math.max(k, b)
                });
                K = !0;
              }
            }), K && y(f, "selection", d, function (a) {
              f.zoom(G(a, e ? {
                animation: !1
              } : null));
            });
            t(f.index) && (this.selectionMarker = this.selectionMarker.destroy());
            e && this.scaleGroups();
          }

          f && t(f.index) && (E(f.container, {
            cursor: f._cursor
          }), f.cancelClick = 10 < this.hasDragged, f.mouseIsDown = this.hasDragged = this.hasPinched = !1, this.pinchDown = []);
        };

        n.prototype.findNearestKDPoint = function (c, a, e) {
          var f = this.chart,
              d = f.hoverPoint;
          f = f.tooltip;
          if (d && f && f.isStickyOnContact()) return d;
          var l;
          c.forEach(function (c) {
            var f = !(c.noSharedTooltip && a) && 0 > c.options.findNearestPointBy.indexOf("y");
            c = c.searchPoint(e, f);

            if ((f = D(c, !0)) && !(f = !D(l, !0))) {
              f = l.distX - c.distX;
              var d = l.dist - c.dist,
                  k = (c.series.group && c.series.group.zIndex) - (l.series.group && l.series.group.zIndex);
              f = 0 < (0 !== f && a ? f : 0 !== d ? d : 0 !== k ? k : l.series.index > c.series.index ? -1 : 1);
            }

            f && (l = c);
          });
          return l;
        };

        n.prototype.getChartCoordinatesFromPoint = function (c, a) {
          var f = c.series,
              e = f.xAxis;
          f = f.yAxis;
          var d = q(c.clientX, c.plotX),
              k = c.shapeArgs;
          if (e && f) return a ? {
            chartX: e.len + e.pos - d,
            chartY: f.len + f.pos - c.plotY
          } : {
            chartX: d + e.pos,
            chartY: c.plotY + f.pos
          };
          if (k && k.x && k.y) return {
            chartX: k.x,
            chartY: k.y
          };
        };

        n.prototype.getChartPosition = function () {
          return this.chartPosition || (this.chartPosition = N(this.chart.container));
        };

        n.prototype.getCoordinates = function (c) {
          var a = {
            xAxis: [],
            yAxis: []
          };
          this.chart.axes.forEach(function (f) {
            a[f.isXAxis ? "xAxis" : "yAxis"].push({
              axis: f,
              value: f.toValue(c[f.horiz ? "chartX" : "chartY"])
            });
          });
          return a;
        };

        n.prototype.getHoverData = function (c, a, e, d, k, n) {
          var f,
              l = [];
          d = !(!d || !c);
          var v = a && !a.stickyTracking,
              h = {
            chartX: n ? n.chartX : void 0,
            chartY: n ? n.chartY : void 0,
            shared: k
          };
          y(this, "beforeGetHoverData", h);
          v = v ? [a] : e.filter(function (a) {
            return h.filter ? h.filter(a) : a.visible && !(!k && a.directTouch) && q(a.options.enableMouseTracking, !0) && a.stickyTracking;
          });
          a = (f = d || !n ? c : this.findNearestKDPoint(v, k, n)) && f.series;
          f && (k && !a.noSharedTooltip ? (v = e.filter(function (a) {
            return h.filter ? h.filter(a) : a.visible && !(!k && a.directTouch) && q(a.options.enableMouseTracking, !0) && !a.noSharedTooltip;
          }), v.forEach(function (a) {
            var c = J(a.points, function (b) {
              return b.x === f.x && !b.isNull;
            });
            D(c) && (a.chart.isBoosting && (c = a.getPoint(c)), l.push(c));
          })) : l.push(f));
          h = {
            hoverPoint: f
          };
          y(this, "afterGetHoverData", h);
          return {
            hoverPoint: h.hoverPoint,
            hoverSeries: a,
            hoverPoints: l
          };
        };

        n.prototype.getPointFromEvent = function (c) {
          c = c.target;

          for (var a; c && !a;) {
            a = c.point, c = c.parentNode;
          }

          return a;
        };

        n.prototype.onTrackerMouseOut = function (c) {
          var a = this.chart.hoverSeries;
          c = c.relatedTarget || c.toElement;
          this.isDirectTouch = !1;
          if (!(!a || !c || a.stickyTracking || this.inClass(c, "highcharts-tooltip") || this.inClass(c, "highcharts-series-" + a.index) && this.inClass(c, "highcharts-tracker"))) a.onMouseOut();
        };

        n.prototype.inClass = function (c, a) {
          for (var f; c;) {
            if (f = M(c, "class")) {
              if (-1 !== f.indexOf(a)) return !0;
              if (-1 !== f.indexOf("highcharts-container")) return !1;
            }

            c = c.parentNode;
          }
        };

        n.prototype.init = function (c, a) {
          this.options = a;
          this.chart = c;
          this.runChartClick = a.chart.events && !!a.chart.events.click;
          this.pinchDown = [];
          this.lastValidTouch = {};
          r && (c.tooltip = new r(c, a.tooltip), this.followTouchMove = q(a.tooltip.followTouchMove, !0));
          this.setDOMEvents();
        };

        n.prototype.normalize = function (c, a) {
          var f = c.touches,
              e = f ? f.length ? f.item(0) : f.changedTouches[0] : c;
          a || (a = this.getChartPosition());
          f = e.pageX - a.left;
          a = e.pageY - a.top;
          if (e = this.chart.containerScaling) f /= e.scaleX, a /= e.scaleY;
          return G(c, {
            chartX: Math.round(f),
            chartY: Math.round(a)
          });
        };

        n.prototype.onContainerClick = function (c) {
          var a = this.chart,
              f = a.hoverPoint,
              e = a.plotLeft,
              d = a.plotTop;
          c = this.normalize(c);
          a.cancelClick || (f && this.inClass(c.target, "highcharts-tracker") ? (y(f.series, "click", G(c, {
            point: f
          })), a.hoverPoint && f.firePointEvent("click", c)) : (G(c, this.getCoordinates(c)), a.isInsidePlot(c.chartX - e, c.chartY - d) && y(a, "click", c)));
        };

        n.prototype.onContainerMouseDown = function (c) {
          c = this.normalize(c);
          2 !== c.button && (this.zoomOption(c), c.preventDefault && c.preventDefault(), this.dragStart(c));
        };

        n.prototype.onContainerMouseLeave = function (f) {
          var a = c[d.hoverChartIndex];
          a && (f.relatedTarget || f.toElement) && (a.pointer.reset(), a.pointer.chartPosition = void 0);
        };

        n.prototype.onContainerMouseMove = function (f) {
          var a = this.chart;
          A(d.hoverChartIndex) && c[d.hoverChartIndex] && c[d.hoverChartIndex].mouseIsDown || (d.hoverChartIndex = a.index);
          f = this.normalize(f);
          f.preventDefault || (f.returnValue = !1);
          "mousedown" === a.mouseIsDown && this.drag(f);
          a.openMenu || !this.inClass(f.target, "highcharts-tracker") && !a.isInsidePlot(f.chartX - a.plotLeft, f.chartY - a.plotTop) || this.runPointActions(f);
        };

        n.prototype.onDocumentTouchEnd = function (f) {
          c[d.hoverChartIndex] && c[d.hoverChartIndex].pointer.drop(f);
        };

        n.prototype.onContainerTouchMove = function (c) {
          this.touch(c);
        };

        n.prototype.onContainerTouchStart = function (c) {
          this.zoomOption(c);
          this.touch(c, !0);
        };

        n.prototype.onDocumentMouseMove = function (c) {
          var a = this.chart,
              f = this.chartPosition,
              e = a.tooltip;
          c = this.normalize(c, f);
          !f || e && e.isStickyOnContact() || a.isInsidePlot(c.chartX - a.plotLeft, c.chartY - a.plotTop) || this.inClass(c.target, "highcharts-tracker") || this.reset();
        };

        n.prototype.onDocumentMouseUp = function (f) {
          c[d.hoverChartIndex] && c[d.hoverChartIndex].pointer.drop(f);
        };

        n.prototype.pinch = function (c) {
          var a = this,
              f = a.chart,
              e = a.pinchDown,
              d = c.touches || [],
              n = d.length,
              h = a.lastValidTouch,
              g = a.hasZoom,
              t = a.selectionMarker,
              H = {},
              K = 1 === n && (a.inClass(c.target, "highcharts-tracker") && f.runTrackerClick || a.runChartClick),
              p = {};
          1 < n && (a.initiated = !0);
          g && a.initiated && !K && c.preventDefault();
          [].map.call(d, function (b) {
            return a.normalize(b);
          });
          "touchstart" === c.type ? ([].forEach.call(d, function (b, a) {
            e[a] = {
              chartX: b.chartX,
              chartY: b.chartY
            };
          }), h.x = [e[0].chartX, e[1] && e[1].chartX], h.y = [e[0].chartY, e[1] && e[1].chartY], f.axes.forEach(function (b) {
            if (b.zoomEnabled) {
              var a = f.bounds[b.horiz ? "h" : "v"],
                  c = b.minPixelPadding,
                  e = b.toPixels(Math.min(q(b.options.min, b.dataMin), b.dataMin)),
                  d = b.toPixels(Math.max(q(b.options.max, b.dataMax), b.dataMax)),
                  l = Math.max(e, d);
              a.min = Math.min(b.pos, Math.min(e, d) - c);
              a.max = Math.max(b.pos + b.len, l + c);
            }
          }), a.res = !0) : a.followTouchMove && 1 === n ? this.runPointActions(a.normalize(c)) : e.length && (t || (a.selectionMarker = t = G({
            destroy: k,
            touch: !0
          }, f.plotBox)), a.pinchTranslate(e, d, H, t, p, h), a.hasPinched = g, a.scaleGroups(H, p), a.res && (a.res = !1, this.reset(!1, 0)));
        };

        n.prototype.pinchTranslate = function (c, a, e, d, k, n) {
          this.zoomHor && this.pinchTranslateDirection(!0, c, a, e, d, k, n);
          this.zoomVert && this.pinchTranslateDirection(!1, c, a, e, d, k, n);
        };

        n.prototype.pinchTranslateDirection = function (c, a, e, d, k, n, h, q) {
          var f = this.chart,
              l = c ? "x" : "y",
              v = c ? "X" : "Y",
              p = "chart" + v,
              b = c ? "width" : "height",
              w = f["plot" + (c ? "Left" : "Top")],
              x,
              B,
              g = q || 1,
              z = f.inverted,
              t = f.bounds[c ? "h" : "v"],
              m = 1 === a.length,
              L = a[0][p],
              y = e[0][p],
              u = !m && a[1][p],
              F = !m && e[1][p];

          e = function e() {
            "number" === typeof F && 20 < Math.abs(L - u) && (g = q || Math.abs(y - F) / Math.abs(L - u));
            B = (w - y) / g + L;
            x = f["plot" + (c ? "Width" : "Height")] / g;
          };

          e();
          a = B;

          if (a < t.min) {
            a = t.min;
            var V = !0;
          } else a + x > t.max && (a = t.max - x, V = !0);

          V ? (y -= .8 * (y - h[l][0]), "number" === typeof F && (F -= .8 * (F - h[l][1])), e()) : h[l] = [y, F];
          z || (n[l] = B - w, n[b] = x);
          n = z ? 1 / g : g;
          k[b] = x;
          k[l] = a;
          d[z ? c ? "scaleY" : "scaleX" : "scale" + v] = g;
          d["translate" + v] = n * w + (y - n * L);
        };

        n.prototype.reset = function (c, a) {
          var f = this.chart,
              e = f.hoverSeries,
              d = f.hoverPoint,
              k = f.hoverPoints,
              n = f.tooltip,
              h = n && n.shared ? k : d;
          c && h && P(h).forEach(function (a) {
            a.series.isCartesian && "undefined" === typeof a.plotX && (c = !1);
          });
          if (c) n && h && P(h).length && (n.refresh(h), n.shared && k ? k.forEach(function (a) {
            a.setState(a.state, !0);
            a.series.isCartesian && (a.series.xAxis.crosshair && a.series.xAxis.drawCrosshair(null, a), a.series.yAxis.crosshair && a.series.yAxis.drawCrosshair(null, a));
          }) : d && (d.setState(d.state, !0), f.axes.forEach(function (a) {
            a.crosshair && d.series[a.coll] === a && a.drawCrosshair(null, d);
          })));else {
            if (d) d.onMouseOut();
            k && k.forEach(function (a) {
              a.setState();
            });
            if (e) e.onMouseOut();
            n && n.hide(a);
            this.unDocMouseMove && (this.unDocMouseMove = this.unDocMouseMove());
            f.axes.forEach(function (a) {
              a.hideCrosshair();
            });
            this.hoverX = f.hoverPoints = f.hoverPoint = null;
          }
        };

        n.prototype.runPointActions = function (f, a) {
          var e = this.chart,
              k = e.tooltip && e.tooltip.options.enabled ? e.tooltip : void 0,
              n = k ? k.shared : !1,
              h = a || e.hoverPoint,
              g = h && h.series || e.hoverSeries;
          g = this.getHoverData(h, g, e.series, (!f || "touchmove" !== f.type) && (!!a || g && g.directTouch && this.isDirectTouch), n, f);
          h = g.hoverPoint;
          var t = g.hoverPoints;
          a = (g = g.hoverSeries) && g.tooltipOptions.followPointer;
          n = n && g && !g.noSharedTooltip;

          if (h && (h !== e.hoverPoint || k && k.isHidden)) {
            (e.hoverPoints || []).forEach(function (a) {
              -1 === t.indexOf(a) && a.setState();
            });
            if (e.hoverSeries !== g) g.onMouseOver();
            this.applyInactiveState(t);
            (t || []).forEach(function (a) {
              a.setState("hover");
            });
            e.hoverPoint && e.hoverPoint.firePointEvent("mouseOut");
            if (!h.series) return;
            h.firePointEvent("mouseOver");
            e.hoverPoints = t;
            e.hoverPoint = h;
            k && k.refresh(n ? t : h, f);
          } else a && k && !k.isHidden && (h = k.getAnchor([{}], f), k.updatePosition({
            plotX: h[0],
            plotY: h[1]
          }));

          this.unDocMouseMove || (this.unDocMouseMove = I(e.container.ownerDocument, "mousemove", function (a) {
            var f = c[d.hoverChartIndex];
            if (f) f.pointer.onDocumentMouseMove(a);
          }));
          e.axes.forEach(function (a) {
            var c = q(a.crosshair.snap, !0),
                e = c ? J(t, function (c) {
              return c.series[a.coll] === a;
            }) : void 0;
            e || !c ? a.drawCrosshair(f, e) : a.hideCrosshair();
          });
        };

        n.prototype.scaleGroups = function (c, a) {
          var f = this.chart,
              e;
          f.series.forEach(function (d) {
            e = c || d.getPlotBox();
            d.xAxis && d.xAxis.zoomEnabled && d.group && (d.group.attr(e), d.markerGroup && (d.markerGroup.attr(e), d.markerGroup.clip(a ? f.clipRect : null)), d.dataLabelsGroup && d.dataLabelsGroup.attr(e));
          });
          f.clipRect.attr(a || f.clipBox);
        };

        n.prototype.setDOMEvents = function () {
          var c = this,
              a = c.chart.container,
              e = a.ownerDocument;

          a.onmousedown = function (a) {
            c.onContainerMouseDown(a);
          };

          a.onmousemove = function (a) {
            c.onContainerMouseMove(a);
          };

          a.onclick = function (a) {
            c.onContainerClick(a);
          };

          this.unbindContainerMouseLeave = I(a, "mouseleave", c.onContainerMouseLeave);
          d.unbindDocumentMouseUp || (d.unbindDocumentMouseUp = I(e, "mouseup", c.onDocumentMouseUp));
          d.hasTouch && (I(a, "touchstart", function (a) {
            c.onContainerTouchStart(a);
          }), I(a, "touchmove", function (a) {
            c.onContainerTouchMove(a);
          }), d.unbindDocumentTouchEnd || (d.unbindDocumentTouchEnd = I(e, "touchend", c.onDocumentTouchEnd)));
        };

        n.prototype.touch = function (c, a) {
          var f = this.chart,
              e;
          if (f.index !== d.hoverChartIndex) this.onContainerMouseLeave({
            relatedTarget: !0
          });
          d.hoverChartIndex = f.index;
          if (1 === c.touches.length) {
            if (c = this.normalize(c), (e = f.isInsidePlot(c.chartX - f.plotLeft, c.chartY - f.plotTop)) && !f.openMenu) {
              a && this.runPointActions(c);

              if ("touchmove" === c.type) {
                a = this.pinchDown;
                var k = a[0] ? 4 <= Math.sqrt(Math.pow(a[0].chartX - c.chartX, 2) + Math.pow(a[0].chartY - c.chartY, 2)) : !1;
              }

              q(k, !0) && this.pinch(c);
            } else a && this.reset();
          } else 2 === c.touches.length && this.pinch(c);
        };

        n.prototype.zoomOption = function (c) {
          var a = this.chart,
              f = a.options.chart,
              e = f.zoomType || "";
          a = a.inverted;
          /touch/.test(c.type) && (e = q(f.pinchType, e));
          this.zoomX = c = /x/.test(e);
          this.zoomY = e = /y/.test(e);
          this.zoomHor = c && !a || e && a;
          this.zoomVert = e && !a || c && a;
          this.hasZoom = c || e;
        };

        return n;
      }();

      d.Pointer = g;
      return d.Pointer;
    });
    S(r, "parts/MSPointer.js", [r["parts/Globals.js"], r["parts/Pointer.js"], r["parts/Utilities.js"]], function (d, g, r) {
      function u() {
        var d = [];

        d.item = function (d) {
          return this[d];
        };

        G(h, function (h) {
          d.push({
            pageX: h.pageX,
            pageY: h.pageY,
            target: h.target
          });
        });
        return d;
      }

      function I(h, g, e, c) {
        "touch" !== h.pointerType && h.pointerType !== h.MSPOINTER_TYPE_TOUCH || !y[d.hoverChartIndex] || (c(h), c = y[d.hoverChartIndex].pointer, c[g]({
          type: e,
          target: h.currentTarget,
          preventDefault: D,
          touches: u()
        }));
      }

      var M = this && this.__extends || function () {
        var _d = function d(h, e) {
          _d = Object.setPrototypeOf || {
            __proto__: []
          } instanceof Array && function (c, e) {
            c.__proto__ = e;
          } || function (c, e) {
            for (var d in e) {
              e.hasOwnProperty(d) && (c[d] = e[d]);
            }
          };

          return _d(h, e);
        };

        return function (h, e) {
          function c() {
            this.constructor = h;
          }

          _d(h, e);

          h.prototype = null === e ? Object.create(e) : (c.prototype = e.prototype, new c());
        };
      }(),
          E = r.addEvent,
          A = r.css,
          G = r.objectEach,
          J = r.removeEvent,
          y = d.charts,
          t = d.doc,
          D = d.noop,
          h = {},
          N = !!d.win.PointerEvent;

      return function (d) {
        function g() {
          return null !== d && d.apply(this, arguments) || this;
        }

        M(g, d);

        g.prototype.batchMSEvents = function (e) {
          e(this.chart.container, N ? "pointerdown" : "MSPointerDown", this.onContainerPointerDown);
          e(this.chart.container, N ? "pointermove" : "MSPointerMove", this.onContainerPointerMove);
          e(t, N ? "pointerup" : "MSPointerUp", this.onDocumentPointerUp);
        };

        g.prototype.destroy = function () {
          this.batchMSEvents(J);
          d.prototype.destroy.call(this);
        };

        g.prototype.init = function (e, c) {
          d.prototype.init.call(this, e, c);
          this.hasZoom && A(e.container, {
            "-ms-touch-action": "none",
            "touch-action": "none"
          });
        };

        g.prototype.onContainerPointerDown = function (e) {
          I(e, "onContainerTouchStart", "touchstart", function (c) {
            h[c.pointerId] = {
              pageX: c.pageX,
              pageY: c.pageY,
              target: c.currentTarget
            };
          });
        };

        g.prototype.onContainerPointerMove = function (e) {
          I(e, "onContainerTouchMove", "touchmove", function (c) {
            h[c.pointerId] = {
              pageX: c.pageX,
              pageY: c.pageY
            };
            h[c.pointerId].target || (h[c.pointerId].target = c.currentTarget);
          });
        };

        g.prototype.onDocumentPointerUp = function (e) {
          I(e, "onDocumentTouchEnd", "touchend", function (c) {
            delete h[c.pointerId];
          });
        };

        g.prototype.setDOMEvents = function () {
          d.prototype.setDOMEvents.call(this);
          (this.hasZoom || this.followTouchMove) && this.batchMSEvents(E);
        };

        return g;
      }(g);
    });
    S(r, "parts/Legend.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.addEvent,
          u = g.css,
          I = g.defined,
          M = g.discardElement,
          E = g.find,
          A = g.fireEvent,
          G = g.format,
          J = g.isNumber,
          y = g.merge,
          t = g.pick,
          D = g.relativeLength,
          h = g.setAnimation,
          N = g.stableSort,
          q = g.syncTimeout;
      g = g.wrap;

      var P = d.isFirefox,
          e = d.marginNames,
          c = d.win,
          k = function () {
        function c(c, a) {
          this.allItems = [];
          this.contentGroup = this.box = void 0;
          this.display = !1;
          this.group = void 0;
          this.offsetWidth = this.maxLegendWidth = this.maxItemWidth = this.legendWidth = this.legendHeight = this.lastLineHeight = this.lastItemY = this.itemY = this.itemX = this.itemMarginTop = this.itemMarginBottom = this.itemHeight = this.initialItemY = 0;
          this.options = {};
          this.padding = 0;
          this.pages = [];
          this.proximate = !1;
          this.scrollGroup = void 0;
          this.widthOption = this.totalItemWidth = this.titleHeight = this.symbolWidth = this.symbolHeight = 0;
          this.chart = c;
          this.init(c, a);
        }

        c.prototype.init = function (c, a) {
          this.chart = c;
          this.setOptions(a);
          a.enabled && (this.render(), r(this.chart, "endResize", function () {
            this.legend.positionCheckboxes();
          }), this.proximate ? this.unchartrender = r(this.chart, "render", function () {
            this.legend.proximatePositions();
            this.legend.positionItems();
          }) : this.unchartrender && this.unchartrender());
        };

        c.prototype.setOptions = function (c) {
          var a = t(c.padding, 8);
          this.options = c;
          this.chart.styledMode || (this.itemStyle = c.itemStyle, this.itemHiddenStyle = y(this.itemStyle, c.itemHiddenStyle));
          this.itemMarginTop = c.itemMarginTop || 0;
          this.itemMarginBottom = c.itemMarginBottom || 0;
          this.padding = a;
          this.initialItemY = a - 5;
          this.symbolWidth = t(c.symbolWidth, 16);
          this.pages = [];
          this.proximate = "proximate" === c.layout && !this.chart.inverted;
          this.baseline = void 0;
        };

        c.prototype.update = function (c, a) {
          var f = this.chart;
          this.setOptions(y(!0, this.options, c));
          this.destroy();
          f.isDirtyLegend = f.isDirtyBox = !0;
          t(a, !0) && f.redraw();
          A(this, "afterUpdate");
        };

        c.prototype.colorizeItem = function (c, a) {
          c.legendGroup[a ? "removeClass" : "addClass"]("highcharts-legend-item-hidden");

          if (!this.chart.styledMode) {
            var f = this.options,
                e = c.legendItem,
                d = c.legendLine,
                k = c.legendSymbol,
                h = this.itemHiddenStyle.color;
            f = a ? f.itemStyle.color : h;
            var n = a ? c.color || h : h,
                g = c.options && c.options.marker,
                q = {
              fill: n
            };
            e && e.css({
              fill: f,
              color: f
            });
            d && d.attr({
              stroke: n
            });
            k && (g && k.isMarker && (q = c.pointAttribs(), a || (q.stroke = q.fill = h)), k.attr(q));
          }

          A(this, "afterColorizeItem", {
            item: c,
            visible: a
          });
        };

        c.prototype.positionItems = function () {
          this.allItems.forEach(this.positionItem, this);
          this.chart.isResizing || this.positionCheckboxes();
        };

        c.prototype.positionItem = function (c) {
          var a = this.options,
              f = a.symbolPadding;
          a = !a.rtl;
          var e = c._legendItemPos,
              d = e[0];
          e = e[1];
          var k = c.checkbox;
          if ((c = c.legendGroup) && c.element) c[I(c.translateY) ? "animate" : "attr"]({
            translateX: a ? d : this.legendWidth - d - 2 * f - 4,
            translateY: e
          });
          k && (k.x = d, k.y = e);
        };

        c.prototype.destroyItem = function (c) {
          var a = c.checkbox;
          ["legendItem", "legendLine", "legendSymbol", "legendGroup"].forEach(function (a) {
            c[a] && (c[a] = c[a].destroy());
          });
          a && M(c.checkbox);
        };

        c.prototype.destroy = function () {
          function c(a) {
            this[a] && (this[a] = this[a].destroy());
          }

          this.getAllItems().forEach(function (a) {
            ["legendItem", "legendGroup"].forEach(c, a);
          });
          "clipRect up down pager nav box title group".split(" ").forEach(c, this);
          this.display = null;
        };

        c.prototype.positionCheckboxes = function () {
          var c = this.group && this.group.alignAttr,
              a = this.clipHeight || this.legendHeight,
              e = this.titleHeight;

          if (c) {
            var d = c.translateY;
            this.allItems.forEach(function (f) {
              var k = f.checkbox;

              if (k) {
                var l = d + e + k.y + (this.scrollOffset || 0) + 3;
                u(k, {
                  left: c.translateX + f.checkboxOffset + k.x - 20 + "px",
                  top: l + "px",
                  display: this.proximate || l > d - 6 && l < d + a - 6 ? "" : "none"
                });
              }
            }, this);
          }
        };

        c.prototype.renderTitle = function () {
          var c = this.options,
              a = this.padding,
              e = c.title,
              d = 0;
          e.text && (this.title || (this.title = this.chart.renderer.label(e.text, a - 3, a - 4, null, null, null, c.useHTML, null, "legend-title").attr({
            zIndex: 1
          }), this.chart.styledMode || this.title.css(e.style), this.title.add(this.group)), e.width || this.title.css({
            width: this.maxLegendWidth + "px"
          }), c = this.title.getBBox(), d = c.height, this.offsetWidth = c.width, this.contentGroup.attr({
            translateY: d
          }));
          this.titleHeight = d;
        };

        c.prototype.setText = function (c) {
          var a = this.options;
          c.legendItem.attr({
            text: a.labelFormat ? G(a.labelFormat, c, this.chart) : a.labelFormatter.call(c)
          });
        };

        c.prototype.renderItem = function (c) {
          var a = this.chart,
              e = a.renderer,
              f = this.options,
              d = this.symbolWidth,
              k = f.symbolPadding,
              h = this.itemStyle,
              n = this.itemHiddenStyle,
              g = "horizontal" === f.layout ? t(f.itemDistance, 20) : 0,
              q = !f.rtl,
              K = c.legendItem,
              p = !c.series,
              b = !p && c.series.drawLegendSymbol ? c.series : c,
              C = b.options;
          C = this.createCheckboxForItem && C && C.showCheckbox;
          g = d + k + g + (C ? 20 : 0);
          var x = f.useHTML,
              R = c.options.className;
          K || (c.legendGroup = e.g("legend-item").addClass("highcharts-" + b.type + "-series highcharts-color-" + c.colorIndex + (R ? " " + R : "") + (p ? " highcharts-series-" + c.index : "")).attr({
            zIndex: 1
          }).add(this.scrollGroup), c.legendItem = K = e.text("", q ? d + k : -k, this.baseline || 0, x), a.styledMode || K.css(y(c.visible ? h : n)), K.attr({
            align: q ? "left" : "right",
            zIndex: 2
          }).add(c.legendGroup), this.baseline || (this.fontMetrics = e.fontMetrics(a.styledMode ? 12 : h.fontSize, K), this.baseline = this.fontMetrics.f + 3 + this.itemMarginTop, K.attr("y", this.baseline)), this.symbolHeight = f.symbolHeight || this.fontMetrics.f, b.drawLegendSymbol(this, c), this.setItemEvents && this.setItemEvents(c, K, x));
          C && !c.checkbox && this.createCheckboxForItem && this.createCheckboxForItem(c);
          this.colorizeItem(c, c.visible);
          !a.styledMode && h.width || K.css({
            width: (f.itemWidth || this.widthOption || a.spacingBox.width) - g
          });
          this.setText(c);
          a = K.getBBox();
          c.itemWidth = c.checkboxOffset = f.itemWidth || c.legendItemWidth || a.width + g;
          this.maxItemWidth = Math.max(this.maxItemWidth, c.itemWidth);
          this.totalItemWidth += c.itemWidth;
          this.itemHeight = c.itemHeight = Math.round(c.legendItemHeight || a.height || this.symbolHeight);
        };

        c.prototype.layoutItem = function (c) {
          var a = this.options,
              e = this.padding,
              f = "horizontal" === a.layout,
              d = c.itemHeight,
              k = this.itemMarginBottom,
              h = this.itemMarginTop,
              n = f ? t(a.itemDistance, 20) : 0,
              g = this.maxLegendWidth;
          a = a.alignColumns && this.totalItemWidth > g ? this.maxItemWidth : c.itemWidth;
          f && this.itemX - e + a > g && (this.itemX = e, this.lastLineHeight && (this.itemY += h + this.lastLineHeight + k), this.lastLineHeight = 0);
          this.lastItemY = h + this.itemY + k;
          this.lastLineHeight = Math.max(d, this.lastLineHeight);
          c._legendItemPos = [this.itemX, this.itemY];
          f ? this.itemX += a : (this.itemY += h + d + k, this.lastLineHeight = d);
          this.offsetWidth = this.widthOption || Math.max((f ? this.itemX - e - (c.checkbox ? 0 : n) : a) + e, this.offsetWidth);
        };

        c.prototype.getAllItems = function () {
          var c = [];
          this.chart.series.forEach(function (a) {
            var e = a && a.options;
            a && t(e.showInLegend, I(e.linkedTo) ? !1 : void 0, !0) && (c = c.concat(a.legendItems || ("point" === e.legendType ? a.data : a)));
          });
          A(this, "afterGetAllItems", {
            allItems: c
          });
          return c;
        };

        c.prototype.getAlignment = function () {
          var c = this.options;
          return this.proximate ? c.align.charAt(0) + "tv" : c.floating ? "" : c.align.charAt(0) + c.verticalAlign.charAt(0) + c.layout.charAt(0);
        };

        c.prototype.adjustMargins = function (c, a) {
          var f = this.chart,
              d = this.options,
              k = this.getAlignment();
          k && [/(lth|ct|rth)/, /(rtv|rm|rbv)/, /(rbh|cb|lbh)/, /(lbv|lm|ltv)/].forEach(function (l, h) {
            l.test(k) && !I(c[h]) && (f[e[h]] = Math.max(f[e[h]], f.legend[(h + 1) % 2 ? "legendHeight" : "legendWidth"] + [1, -1, -1, 1][h] * d[h % 2 ? "x" : "y"] + t(d.margin, 12) + a[h] + (f.titleOffset[h] || 0)));
          });
        };

        c.prototype.proximatePositions = function () {
          var c = this.chart,
              a = [],
              e = "left" === this.options.align;
          this.allItems.forEach(function (f) {
            var d = e;

            if (f.yAxis && f.points) {
              f.xAxis.options.reversed && (d = !d);
              var k = E(d ? f.points : f.points.slice(0).reverse(), function (a) {
                return J(a.plotY);
              });
              d = this.itemMarginTop + f.legendItem.getBBox().height + this.itemMarginBottom;
              var l = f.yAxis.top - c.plotTop;
              f.visible ? (k = k ? k.plotY : f.yAxis.height, k += l - .3 * d) : k = l + f.yAxis.height;
              a.push({
                target: k,
                size: d,
                item: f
              });
            }
          }, this);
          d.distribute(a, c.plotHeight);
          a.forEach(function (a) {
            a.item._legendItemPos[1] = c.plotTop - c.spacing[0] + a.pos;
          });
        };

        c.prototype.render = function () {
          var c = this.chart,
              a = c.renderer,
              e = this.group,
              d,
              k = this.box,
              h = this.options,
              n = this.padding;
          this.itemX = n;
          this.itemY = this.initialItemY;
          this.lastItemY = this.offsetWidth = 0;
          this.widthOption = D(h.width, c.spacingBox.width - n);
          var g = c.spacingBox.width - 2 * n - h.x;
          -1 < ["rm", "lm"].indexOf(this.getAlignment().substring(0, 2)) && (g /= 2);
          this.maxLegendWidth = this.widthOption || g;
          e || (this.group = e = a.g("legend").attr({
            zIndex: 7
          }).add(), this.contentGroup = a.g().attr({
            zIndex: 1
          }).add(e), this.scrollGroup = a.g().add(this.contentGroup));
          this.renderTitle();
          g = this.getAllItems();
          N(g, function (a, c) {
            return (a.options && a.options.legendIndex || 0) - (c.options && c.options.legendIndex || 0);
          });
          h.reversed && g.reverse();
          this.allItems = g;
          this.display = d = !!g.length;
          this.itemHeight = this.totalItemWidth = this.maxItemWidth = this.lastLineHeight = 0;
          g.forEach(this.renderItem, this);
          g.forEach(this.layoutItem, this);
          g = (this.widthOption || this.offsetWidth) + n;
          var q = this.lastItemY + this.lastLineHeight + this.titleHeight;
          q = this.handleOverflow(q);
          q += n;
          k || (this.box = k = a.rect().addClass("highcharts-legend-box").attr({
            r: h.borderRadius
          }).add(e), k.isNew = !0);
          c.styledMode || k.attr({
            stroke: h.borderColor,
            "stroke-width": h.borderWidth || 0,
            fill: h.backgroundColor || "none"
          }).shadow(h.shadow);
          0 < g && 0 < q && (k[k.isNew ? "attr" : "animate"](k.crisp.call({}, {
            x: 0,
            y: 0,
            width: g,
            height: q
          }, k.strokeWidth())), k.isNew = !1);
          k[d ? "show" : "hide"]();
          c.styledMode && "none" === e.getStyle("display") && (g = q = 0);
          this.legendWidth = g;
          this.legendHeight = q;
          d && (a = c.spacingBox, k = a.y, /(lth|ct|rth)/.test(this.getAlignment()) && 0 < c.titleOffset[0] ? k += c.titleOffset[0] : /(lbh|cb|rbh)/.test(this.getAlignment()) && 0 < c.titleOffset[2] && (k -= c.titleOffset[2]), k !== a.y && (a = y(a, {
            y: k
          })), e.align(y(h, {
            width: g,
            height: q,
            verticalAlign: this.proximate ? "top" : h.verticalAlign
          }), !0, a));
          this.proximate || this.positionItems();
          A(this, "afterRender");
        };

        c.prototype.handleOverflow = function (c) {
          var a = this,
              e = this.chart,
              f = e.renderer,
              d = this.options,
              k = d.y,
              h = this.padding;
          k = e.spacingBox.height + ("top" === d.verticalAlign ? -k : k) - h;

          var n = d.maxHeight,
              g,
              q = this.clipRect,
              y = d.navigation,
              p = t(y.animation, !0),
              b = y.arrowSize || 12,
              C = this.nav,
              x = this.pages,
              R,
              O = this.allItems,
              u = function u(b) {
            "number" === typeof b ? q.attr({
              height: b
            }) : q && (a.clipRect = q.destroy(), a.contentGroup.clip());
            a.contentGroup.div && (a.contentGroup.div.style.clip = b ? "rect(" + h + "px,9999px," + (h + b) + "px,0)" : "auto");
          },
              D = function D(c) {
            a[c] = f.circle(0, 0, 1.3 * b).translate(b / 2, b / 2).add(C);
            e.styledMode || a[c].attr("fill", "rgba(0,0,0,0.0001)");
            return a[c];
          };

          "horizontal" !== d.layout || "middle" === d.verticalAlign || d.floating || (k /= 2);
          n && (k = Math.min(k, n));
          x.length = 0;
          c > k && !1 !== y.enabled ? (this.clipHeight = g = Math.max(k - 20 - this.titleHeight - h, 0), this.currentPage = t(this.currentPage, 1), this.fullHeight = c, O.forEach(function (b, a) {
            var c = b._legendItemPos[1],
                e = Math.round(b.legendItem.getBBox().height),
                f = x.length;
            if (!f || c - x[f - 1] > g && (R || c) !== x[f - 1]) x.push(R || c), f++;
            b.pageIx = f - 1;
            R && (O[a - 1].pageIx = f - 1);
            a === O.length - 1 && c + e - x[f - 1] > g && c !== R && (x.push(c), b.pageIx = f);
            c !== R && (R = c);
          }), q || (q = a.clipRect = f.clipRect(0, h, 9999, 0), a.contentGroup.clip(q)), u(g), C || (this.nav = C = f.g().attr({
            zIndex: 1
          }).add(this.group), this.up = f.symbol("triangle", 0, 0, b, b).add(C), D("upTracker").on("click", function () {
            a.scroll(-1, p);
          }), this.pager = f.text("", 15, 10).addClass("highcharts-legend-navigation"), e.styledMode || this.pager.css(y.style), this.pager.add(C), this.down = f.symbol("triangle-down", 0, 0, b, b).add(C), D("downTracker").on("click", function () {
            a.scroll(1, p);
          })), a.scroll(0), c = k) : C && (u(), this.nav = C.destroy(), this.scrollGroup.attr({
            translateY: 1
          }), this.clipHeight = 0);
          return c;
        };

        c.prototype.scroll = function (c, a) {
          var e = this,
              f = this.chart,
              k = this.pages,
              n = k.length,
              g = this.currentPage + c;
          c = this.clipHeight;
          var y = this.options.navigation,
              u = this.pager,
              H = this.padding;
          g > n && (g = n);
          0 < g && ("undefined" !== typeof a && h(a, f), this.nav.attr({
            translateX: H,
            translateY: c + this.padding + 7 + this.titleHeight,
            visibility: "visible"
          }), [this.up, this.upTracker].forEach(function (a) {
            a.attr({
              "class": 1 === g ? "highcharts-legend-nav-inactive" : "highcharts-legend-nav-active"
            });
          }), u.attr({
            text: g + "/" + n
          }), [this.down, this.downTracker].forEach(function (a) {
            a.attr({
              x: 18 + this.pager.getBBox().width,
              "class": g === n ? "highcharts-legend-nav-inactive" : "highcharts-legend-nav-active"
            });
          }, this), f.styledMode || (this.up.attr({
            fill: 1 === g ? y.inactiveColor : y.activeColor
          }), this.upTracker.css({
            cursor: 1 === g ? "default" : "pointer"
          }), this.down.attr({
            fill: g === n ? y.inactiveColor : y.activeColor
          }), this.downTracker.css({
            cursor: g === n ? "default" : "pointer"
          })), this.scrollOffset = -k[g - 1] + this.initialItemY, this.scrollGroup.animate({
            translateY: this.scrollOffset
          }), this.currentPage = g, this.positionCheckboxes(), a = d.animObject(t(a, f.renderer.globalAnimation, !0)), q(function () {
            A(e, "afterScroll", {
              currentPage: g
            });
          }, a.duration || 0));
        };

        return c;
      }();

      (/Trident\/7\.0/.test(c.navigator && c.navigator.userAgent) || P) && g(k.prototype, "positionItem", function (c, e) {
        var a = this,
            f = function f() {
          e._legendItemPos && c.call(a, e);
        };

        f();
        a.bubbleLegend || setTimeout(f);
      });
      d.Legend = k;
      return d.Legend;
    });
    S(r, "parts/Chart.js", [r["parts/Globals.js"], r["parts/Legend.js"], r["parts/MSPointer.js"], r["parts/Pointer.js"], r["parts/Time.js"], r["parts/Utilities.js"]], function (d, g, r, u, I, M) {
      var E = M.addEvent,
          A = M.animate,
          G = M.animObject,
          J = M.attr,
          y = M.createElement,
          t = M.css,
          D = M.defined,
          h = M.discardElement,
          N = M.erase,
          q = M.error,
          P = M.extend,
          e = M.find,
          c = M.fireEvent,
          k = M.getStyle,
          n = M.isArray,
          f = M.isFunction,
          a = M.isNumber,
          l = M.isObject,
          v = M.isString,
          z = M.merge,
          w = M.numberFormat,
          B = M.objectEach,
          L = M.pick,
          Q = M.pInt,
          H = M.relativeLength,
          K = M.removeEvent,
          p = M.setAnimation,
          b = M.splat,
          C = M.syncTimeout,
          x = M.uniqueKey,
          R = d.doc,
          O = d.Axis,
          X = d.defaultOptions,
          U = d.charts,
          m = d.marginNames,
          T = d.seriesTypes,
          Z = d.win,
          ca = d.Chart = function () {
        this.getArgs.apply(this, arguments);
      };

      d.chart = function (b, a, c) {
        return new ca(b, a, c);
      };

      P(ca.prototype, {
        callbacks: [],
        getArgs: function getArgs() {
          var b = [].slice.call(arguments);
          if (v(b[0]) || b[0].nodeName) this.renderTo = b.shift();
          this.init(b[0], b[1]);
        },
        init: function init(b, a) {
          var e,
              k = b.series,
              h = b.plotOptions || {};
          c(this, "init", {
            args: arguments
          }, function () {
            b.series = null;
            e = z(X, b);
            B(e.plotOptions, function (b, a) {
              l(b) && (b.tooltip = h[a] && z(h[a].tooltip) || void 0);
            });
            e.tooltip.userOptions = b.chart && b.chart.forExport && b.tooltip.userOptions || b.tooltip;
            e.series = b.series = k;
            this.userOptions = b;
            var m = e.chart,
                n = m.events;
            this.margin = [];
            this.spacing = [];
            this.bounds = {
              h: {},
              v: {}
            };
            this.labelCollectors = [];
            this.callback = a;
            this.isResizing = 0;
            this.options = e;
            this.axes = [];
            this.series = [];
            this.time = b.time && Object.keys(b.time).length ? new I(b.time) : d.time;
            this.numberFormatter = m.numberFormatter || w;
            this.styledMode = m.styledMode;
            this.hasCartesianSeries = m.showAxes;
            var p = this;
            p.index = U.length;
            U.push(p);
            d.chartCount++;
            n && B(n, function (b, a) {
              f(b) && E(p, a, b);
            });
            p.xAxis = [];
            p.yAxis = [];
            p.pointCount = p.colorCounter = p.symbolCounter = 0;
            c(p, "afterInit");
            p.firstRender();
          });
        },
        initSeries: function initSeries(b) {
          var a = this.options.chart;
          a = b.type || a.type || a.defaultSeriesType;
          var c = T[a];
          c || q(17, !0, this, {
            missingModuleFor: a
          });
          a = new c();
          a.init(this, b);
          return a;
        },
        setSeriesData: function setSeriesData() {
          this.getSeriesOrderByLinks().forEach(function (b) {
            b.points || b.data || !b.enabledDataSorting || b.setData(b.options.data, !1);
          });
        },
        getSeriesOrderByLinks: function getSeriesOrderByLinks() {
          return this.series.concat().sort(function (b, a) {
            return b.linkedSeries.length || a.linkedSeries.length ? a.linkedSeries.length - b.linkedSeries.length : 0;
          });
        },
        orderSeries: function orderSeries(b) {
          var a = this.series;

          for (b = b || 0; b < a.length; b++) {
            a[b] && (a[b].index = b, a[b].name = a[b].getName());
          }
        },
        isInsidePlot: function isInsidePlot(b, a, e) {
          var f = e ? a : b;
          b = e ? b : a;
          f = {
            x: f,
            y: b,
            isInsidePlot: 0 <= f && f <= this.plotWidth && 0 <= b && b <= this.plotHeight
          };
          c(this, "afterIsInsidePlot", f);
          return f.isInsidePlot;
        },
        redraw: function redraw(b) {
          c(this, "beforeRedraw");
          var a = this.axes,
              e = this.series,
              f = this.pointer,
              d = this.legend,
              k = this.userOptions.legend,
              l = this.isDirtyLegend,
              h = this.hasCartesianSeries,
              m = this.isDirtyBox,
              n = this.renderer,
              x = n.isHidden(),
              g = [];
          this.setResponsive && this.setResponsive(!1);
          p(this.hasRendered ? b : !1, this);
          x && this.temporaryDisplay();
          this.layOutTitles();

          for (b = e.length; b--;) {
            var w = e[b];

            if (w.options.stacking) {
              var C = !0;

              if (w.isDirty) {
                var v = !0;
                break;
              }
            }
          }

          if (v) for (b = e.length; b--;) {
            w = e[b], w.options.stacking && (w.isDirty = !0);
          }
          e.forEach(function (b) {
            b.isDirty && ("point" === b.options.legendType ? (b.updateTotals && b.updateTotals(), l = !0) : k && (k.labelFormatter || k.labelFormat) && (l = !0));
            b.isDirtyData && c(b, "updatedData");
          });
          l && d && d.options.enabled && (d.render(), this.isDirtyLegend = !1);
          C && this.getStacks();
          h && a.forEach(function (b) {
            b.updateNames();
            b.setScale();
          });
          this.getMargins();
          h && (a.forEach(function (b) {
            b.isDirty && (m = !0);
          }), a.forEach(function (b) {
            var a = b.min + "," + b.max;
            b.extKey !== a && (b.extKey = a, g.push(function () {
              c(b, "afterSetExtremes", P(b.eventArgs, b.getExtremes()));
              delete b.eventArgs;
            }));
            (m || C) && b.redraw();
          }));
          m && this.drawChartBox();
          c(this, "predraw");
          e.forEach(function (b) {
            (m || b.isDirty) && b.visible && b.redraw();
            b.isDirtyData = !1;
          });
          f && f.reset(!0);
          n.draw();
          c(this, "redraw");
          c(this, "render");
          x && this.temporaryDisplay(!0);
          g.forEach(function (b) {
            b.call();
          });
        },
        get: function get(b) {
          function a(a) {
            return a.id === b || a.options && a.options.id === b;
          }

          var c = this.series,
              f;
          var d = e(this.axes, a) || e(this.series, a);

          for (f = 0; !d && f < c.length; f++) {
            d = e(c[f].points || [], a);
          }

          return d;
        },
        getAxes: function getAxes() {
          var a = this,
              e = this.options,
              f = e.xAxis = b(e.xAxis || {});
          e = e.yAxis = b(e.yAxis || {});
          c(this, "getAxes");
          f.forEach(function (b, a) {
            b.index = a;
            b.isX = !0;
          });
          e.forEach(function (b, a) {
            b.index = a;
          });
          f.concat(e).forEach(function (b) {
            new O(a, b);
          });
          c(this, "afterGetAxes");
        },
        getSelectedPoints: function getSelectedPoints() {
          var b = [];
          this.series.forEach(function (a) {
            b = b.concat(a.getPointsCollection().filter(function (b) {
              return L(b.selectedStaging, b.selected);
            }));
          });
          return b;
        },
        getSelectedSeries: function getSelectedSeries() {
          return this.series.filter(function (b) {
            return b.selected;
          });
        },
        setTitle: function setTitle(b, a, c) {
          this.applyDescription("title", b);
          this.applyDescription("subtitle", a);
          this.applyDescription("caption", void 0);
          this.layOutTitles(c);
        },
        applyDescription: function applyDescription(b, a) {
          var c = this,
              e = "title" === b ? {
            color: "#333333",
            fontSize: this.options.isStock ? "16px" : "18px"
          } : {
            color: "#666666"
          };
          e = this.options[b] = z(!this.styledMode && {
            style: e
          }, this.options[b], a);
          var f = this[b];
          f && a && (this[b] = f = f.destroy());
          e && !f && (f = this.renderer.text(e.text, 0, 0, e.useHTML).attr({
            align: e.align,
            "class": "highcharts-" + b,
            zIndex: e.zIndex || 4
          }).add(), f.update = function (a) {
            c[{
              title: "setTitle",
              subtitle: "setSubtitle",
              caption: "setCaption"
            }[b]](a);
          }, this.styledMode || f.css(e.style), this[b] = f);
        },
        layOutTitles: function layOutTitles(b) {
          var a = [0, 0, 0],
              e = this.renderer,
              f = this.spacingBox;
          ["title", "subtitle", "caption"].forEach(function (b) {
            var c = this[b],
                d = this.options[b],
                k = d.verticalAlign || "top";
            b = "title" === b ? -3 : "top" === k ? a[0] + 2 : 0;

            if (c) {
              if (!this.styledMode) var l = d.style.fontSize;
              l = e.fontMetrics(l, c).b;
              c.css({
                width: (d.width || f.width + (d.widthAdjust || 0)) + "px"
              });
              var h = Math.round(c.getBBox(d.useHTML).height);
              c.align(P({
                y: "bottom" === k ? l : b + l,
                height: h
              }, d), !1, "spacingBox");
              d.floating || ("top" === k ? a[0] = Math.ceil(a[0] + h) : "bottom" === k && (a[2] = Math.ceil(a[2] + h)));
            }
          }, this);
          a[0] && "top" === (this.options.title.verticalAlign || "top") && (a[0] += this.options.title.margin);
          a[2] && "bottom" === this.options.caption.verticalAlign && (a[2] += this.options.caption.margin);
          var d = !this.titleOffset || this.titleOffset.join(",") !== a.join(",");
          this.titleOffset = a;
          c(this, "afterLayOutTitles");
          !this.isDirtyBox && d && (this.isDirtyBox = this.isDirtyLegend = d, this.hasRendered && L(b, !0) && this.isDirtyBox && this.redraw());
        },
        getChartSize: function getChartSize() {
          var b = this.options.chart,
              a = b.width;
          b = b.height;
          var c = this.renderTo;
          D(a) || (this.containerWidth = k(c, "width"));
          D(b) || (this.containerHeight = k(c, "height"));
          this.chartWidth = Math.max(0, a || this.containerWidth || 600);
          this.chartHeight = Math.max(0, H(b, this.chartWidth) || (1 < this.containerHeight ? this.containerHeight : 400));
        },
        temporaryDisplay: function temporaryDisplay(b) {
          var a = this.renderTo;
          if (b) for (; a && a.style;) {
            a.hcOrigStyle && (t(a, a.hcOrigStyle), delete a.hcOrigStyle), a.hcOrigDetached && (R.body.removeChild(a), a.hcOrigDetached = !1), a = a.parentNode;
          } else for (; a && a.style;) {
            R.body.contains(a) || a.parentNode || (a.hcOrigDetached = !0, R.body.appendChild(a));
            if ("none" === k(a, "display", !1) || a.hcOricDetached) a.hcOrigStyle = {
              display: a.style.display,
              height: a.style.height,
              overflow: a.style.overflow
            }, b = {
              display: "block",
              overflow: "hidden"
            }, a !== this.renderTo && (b.height = 0), t(a, b), a.offsetWidth || a.style.setProperty("display", "block", "important");
            a = a.parentNode;
            if (a === R.body) break;
          }
        },
        setClassName: function setClassName(b) {
          this.container.className = "highcharts-container " + (b || "");
        },
        getContainer: function getContainer() {
          var b = this.options,
              e = b.chart;
          var f = this.renderTo;
          var k = x(),
              l,
              h;
          f || (this.renderTo = f = e.renderTo);
          v(f) && (this.renderTo = f = R.getElementById(f));
          f || q(13, !0, this);
          var m = Q(J(f, "data-highcharts-chart"));
          a(m) && U[m] && U[m].hasRendered && U[m].destroy();
          J(f, "data-highcharts-chart", this.index);
          f.innerHTML = "";
          e.skipClone || f.offsetWidth || this.temporaryDisplay();
          this.getChartSize();
          m = this.chartWidth;
          var n = this.chartHeight;
          t(f, {
            overflow: "hidden"
          });
          this.styledMode || (l = P({
            position: "relative",
            overflow: "hidden",
            width: m + "px",
            height: n + "px",
            textAlign: "left",
            lineHeight: "normal",
            zIndex: 0,
            "-webkit-tap-highlight-color": "rgba(0,0,0,0)"
          }, e.style));
          this.container = f = y("div", {
            id: k
          }, l, f);
          this._cursor = f.style.cursor;
          this.renderer = new (d[e.renderer] || d.Renderer)(f, m, n, null, e.forExport, b.exporting && b.exporting.allowHTML, this.styledMode);
          p(void 0, this);
          this.setClassName(e.className);
          if (this.styledMode) for (h in b.defs) {
            this.renderer.definition(b.defs[h]);
          } else this.renderer.setStyle(e.style);
          this.renderer.chartIndex = this.index;
          c(this, "afterGetContainer");
        },
        getMargins: function getMargins(b) {
          var a = this.spacing,
              e = this.margin,
              f = this.titleOffset;
          this.resetMargins();
          f[0] && !D(e[0]) && (this.plotTop = Math.max(this.plotTop, f[0] + a[0]));
          f[2] && !D(e[2]) && (this.marginBottom = Math.max(this.marginBottom, f[2] + a[2]));
          this.legend && this.legend.display && this.legend.adjustMargins(e, a);
          c(this, "getMargins");
          b || this.getAxisMargins();
        },
        getAxisMargins: function getAxisMargins() {
          var b = this,
              a = b.axisOffset = [0, 0, 0, 0],
              c = b.colorAxis,
              e = b.margin,
              f = function f(b) {
            b.forEach(function (b) {
              b.visible && b.getOffset();
            });
          };

          b.hasCartesianSeries ? f(b.axes) : c && c.length && f(c);
          m.forEach(function (c, f) {
            D(e[f]) || (b[c] += a[f]);
          });
          b.setChartSize();
        },
        reflow: function reflow(b) {
          var a = this,
              c = a.options.chart,
              e = a.renderTo,
              f = D(c.width) && D(c.height),
              d = c.width || k(e, "width");
          c = c.height || k(e, "height");
          e = b ? b.target : Z;

          if (!f && !a.isPrinting && d && c && (e === Z || e === R)) {
            if (d !== a.containerWidth || c !== a.containerHeight) M.clearTimeout(a.reflowTimeout), a.reflowTimeout = C(function () {
              a.container && a.setSize(void 0, void 0, !1);
            }, b ? 100 : 0);
            a.containerWidth = d;
            a.containerHeight = c;
          }
        },
        setReflow: function setReflow(b) {
          var a = this;
          !1 === b || this.unbindReflow ? !1 === b && this.unbindReflow && (this.unbindReflow = this.unbindReflow()) : (this.unbindReflow = E(Z, "resize", function (b) {
            a.options && a.reflow(b);
          }), E(this, "destroy", this.unbindReflow));
        },
        setSize: function setSize(b, a, e) {
          var f = this,
              d = f.renderer;
          f.isResizing += 1;
          p(e, f);
          e = d.globalAnimation;
          f.oldChartHeight = f.chartHeight;
          f.oldChartWidth = f.chartWidth;
          "undefined" !== typeof b && (f.options.chart.width = b);
          "undefined" !== typeof a && (f.options.chart.height = a);
          f.getChartSize();
          f.styledMode || (e ? A : t)(f.container, {
            width: f.chartWidth + "px",
            height: f.chartHeight + "px"
          }, e);
          f.setChartSize(!0);
          d.setSize(f.chartWidth, f.chartHeight, e);
          f.axes.forEach(function (b) {
            b.isDirty = !0;
            b.setScale();
          });
          f.isDirtyLegend = !0;
          f.isDirtyBox = !0;
          f.layOutTitles();
          f.getMargins();
          f.redraw(e);
          f.oldChartHeight = null;
          c(f, "resize");
          C(function () {
            f && c(f, "endResize", null, function () {
              --f.isResizing;
            });
          }, G(e).duration || 0);
        },
        setChartSize: function setChartSize(b) {
          var a = this.inverted,
              e = this.renderer,
              f = this.chartWidth,
              d = this.chartHeight,
              k = this.options.chart,
              l = this.spacing,
              h = this.clipOffset,
              m,
              n,
              p,
              x;
          this.plotLeft = m = Math.round(this.plotLeft);
          this.plotTop = n = Math.round(this.plotTop);
          this.plotWidth = p = Math.max(0, Math.round(f - m - this.marginRight));
          this.plotHeight = x = Math.max(0, Math.round(d - n - this.marginBottom));
          this.plotSizeX = a ? x : p;
          this.plotSizeY = a ? p : x;
          this.plotBorderWidth = k.plotBorderWidth || 0;
          this.spacingBox = e.spacingBox = {
            x: l[3],
            y: l[0],
            width: f - l[3] - l[1],
            height: d - l[0] - l[2]
          };
          this.plotBox = e.plotBox = {
            x: m,
            y: n,
            width: p,
            height: x
          };
          f = 2 * Math.floor(this.plotBorderWidth / 2);
          a = Math.ceil(Math.max(f, h[3]) / 2);
          e = Math.ceil(Math.max(f, h[0]) / 2);
          this.clipBox = {
            x: a,
            y: e,
            width: Math.floor(this.plotSizeX - Math.max(f, h[1]) / 2 - a),
            height: Math.max(0, Math.floor(this.plotSizeY - Math.max(f, h[2]) / 2 - e))
          };
          b || this.axes.forEach(function (b) {
            b.setAxisSize();
            b.setAxisTranslation();
          });
          c(this, "afterSetChartSize", {
            skipAxes: b
          });
        },
        resetMargins: function resetMargins() {
          c(this, "resetMargins");
          var b = this,
              a = b.options.chart;
          ["margin", "spacing"].forEach(function (c) {
            var e = a[c],
                f = l(e) ? e : [e, e, e, e];
            ["Top", "Right", "Bottom", "Left"].forEach(function (e, d) {
              b[c][d] = L(a[c + e], f[d]);
            });
          });
          m.forEach(function (a, c) {
            b[a] = L(b.margin[c], b.spacing[c]);
          });
          b.axisOffset = [0, 0, 0, 0];
          b.clipOffset = [0, 0, 0, 0];
        },
        drawChartBox: function drawChartBox() {
          var b = this.options.chart,
              a = this.renderer,
              e = this.chartWidth,
              f = this.chartHeight,
              d = this.chartBackground,
              k = this.plotBackground,
              l = this.plotBorder,
              h = this.styledMode,
              m = this.plotBGImage,
              n = b.backgroundColor,
              p = b.plotBackgroundColor,
              x = b.plotBackgroundImage,
              g,
              w = this.plotLeft,
              C = this.plotTop,
              v = this.plotWidth,
              q = this.plotHeight,
              t = this.plotBox,
              B = this.clipRect,
              z = this.clipBox,
              O = "animate";
          d || (this.chartBackground = d = a.rect().addClass("highcharts-background").add(), O = "attr");
          if (h) var y = g = d.strokeWidth();else {
            y = b.borderWidth || 0;
            g = y + (b.shadow ? 8 : 0);
            n = {
              fill: n || "none"
            };
            if (y || d["stroke-width"]) n.stroke = b.borderColor, n["stroke-width"] = y;
            d.attr(n).shadow(b.shadow);
          }
          d[O]({
            x: g / 2,
            y: g / 2,
            width: e - g - y % 2,
            height: f - g - y % 2,
            r: b.borderRadius
          });
          O = "animate";
          k || (O = "attr", this.plotBackground = k = a.rect().addClass("highcharts-plot-background").add());
          k[O](t);
          h || (k.attr({
            fill: p || "none"
          }).shadow(b.plotShadow), x && (m ? (x !== m.attr("href") && m.attr("href", x), m.animate(t)) : this.plotBGImage = a.image(x, w, C, v, q).add()));
          B ? B.animate({
            width: z.width,
            height: z.height
          }) : this.clipRect = a.clipRect(z);
          O = "animate";
          l || (O = "attr", this.plotBorder = l = a.rect().addClass("highcharts-plot-border").attr({
            zIndex: 1
          }).add());
          h || l.attr({
            stroke: b.plotBorderColor,
            "stroke-width": b.plotBorderWidth || 0,
            fill: "none"
          });
          l[O](l.crisp({
            x: w,
            y: C,
            width: v,
            height: q
          }, -l.strokeWidth()));
          this.isDirtyBox = !1;
          c(this, "afterDrawChartBox");
        },
        propFromSeries: function propFromSeries() {
          var b = this,
              a = b.options.chart,
              c,
              e = b.options.series,
              f,
              d;
          ["inverted", "angular", "polar"].forEach(function (k) {
            c = T[a.type || a.defaultSeriesType];
            d = a[k] || c && c.prototype[k];

            for (f = e && e.length; !d && f--;) {
              (c = T[e[f].type]) && c.prototype[k] && (d = !0);
            }

            b[k] = d;
          });
        },
        linkSeries: function linkSeries() {
          var b = this,
              a = b.series;
          a.forEach(function (b) {
            b.linkedSeries.length = 0;
          });
          a.forEach(function (a) {
            var c = a.options.linkedTo;
            v(c) && (c = ":previous" === c ? b.series[a.index - 1] : b.get(c)) && c.linkedParent !== a && (c.linkedSeries.push(a), a.linkedParent = c, c.enabledDataSorting && a.setDataSortingOptions(), a.visible = L(a.options.visible, c.options.visible, a.visible));
          });
          c(this, "afterLinkSeries");
        },
        renderSeries: function renderSeries() {
          this.series.forEach(function (b) {
            b.translate();
            b.render();
          });
        },
        renderLabels: function renderLabels() {
          var b = this,
              a = b.options.labels;
          a.items && a.items.forEach(function (c) {
            var e = P(a.style, c.style),
                f = Q(e.left) + b.plotLeft,
                d = Q(e.top) + b.plotTop + 12;
            delete e.left;
            delete e.top;
            b.renderer.text(c.html, f, d).attr({
              zIndex: 2
            }).css(e).add();
          });
        },
        render: function render() {
          var b = this.axes,
              a = this.colorAxis,
              c = this.renderer,
              e = this.options,
              f = 0,
              d = function d(b) {
            b.forEach(function (b) {
              b.visible && b.render();
            });
          };

          this.setTitle();
          this.legend = new g(this, e.legend);
          this.getStacks && this.getStacks();
          this.getMargins(!0);
          this.setChartSize();
          e = this.plotWidth;
          b.some(function (b) {
            if (b.horiz && b.visible && b.options.labels.enabled && b.series.length) return f = 21, !0;
          });
          var k = this.plotHeight = Math.max(this.plotHeight - f, 0);
          b.forEach(function (b) {
            b.setScale();
          });
          this.getAxisMargins();
          var l = 1.1 < e / this.plotWidth;
          var h = 1.05 < k / this.plotHeight;
          if (l || h) b.forEach(function (b) {
            (b.horiz && l || !b.horiz && h) && b.setTickInterval(!0);
          }), this.getMargins();
          this.drawChartBox();
          this.hasCartesianSeries ? d(b) : a && a.length && d(a);
          this.seriesGroup || (this.seriesGroup = c.g("series-group").attr({
            zIndex: 3
          }).add());
          this.renderSeries();
          this.renderLabels();
          this.addCredits();
          this.setResponsive && this.setResponsive();
          this.updateContainerScaling();
          this.hasRendered = !0;
        },
        addCredits: function addCredits(b) {
          var a = this;
          b = z(!0, this.options.credits, b);
          b.enabled && !this.credits && (this.credits = this.renderer.text(b.text + (this.mapCredits || ""), 0, 0).addClass("highcharts-credits").on("click", function () {
            b.href && (Z.location.href = b.href);
          }).attr({
            align: b.position.align,
            zIndex: 8
          }), a.styledMode || this.credits.css(b.style), this.credits.add().align(b.position), this.credits.update = function (b) {
            a.credits = a.credits.destroy();
            a.addCredits(b);
          });
        },
        updateContainerScaling: function updateContainerScaling() {
          var b = this.container;

          if (b.offsetWidth && b.offsetHeight && b.getBoundingClientRect) {
            var a = b.getBoundingClientRect(),
                c = a.width / b.offsetWidth;
            b = a.height / b.offsetHeight;
            1 !== c || 1 !== b ? this.containerScaling = {
              scaleX: c,
              scaleY: b
            } : delete this.containerScaling;
          }
        },
        destroy: function destroy() {
          var b = this,
              a = b.axes,
              e = b.series,
              f = b.container,
              k,
              l = f && f.parentNode;
          c(b, "destroy");
          b.renderer.forExport ? N(U, b) : U[b.index] = void 0;
          d.chartCount--;
          b.renderTo.removeAttribute("data-highcharts-chart");
          K(b);

          for (k = a.length; k--;) {
            a[k] = a[k].destroy();
          }

          this.scroller && this.scroller.destroy && this.scroller.destroy();

          for (k = e.length; k--;) {
            e[k] = e[k].destroy();
          }

          "title subtitle chartBackground plotBackground plotBGImage plotBorder seriesGroup clipRect credits pointer rangeSelector legend resetZoomButton tooltip renderer".split(" ").forEach(function (a) {
            var c = b[a];
            c && c.destroy && (b[a] = c.destroy());
          });
          f && (f.innerHTML = "", K(f), l && h(f));
          B(b, function (a, c) {
            delete b[c];
          });
        },
        firstRender: function firstRender() {
          var b = this,
              a = b.options;

          if (!b.isReadyToRender || b.isReadyToRender()) {
            b.getContainer();
            b.resetMargins();
            b.setChartSize();
            b.propFromSeries();
            b.getAxes();
            (n(a.series) ? a.series : []).forEach(function (a) {
              b.initSeries(a);
            });
            b.linkSeries();
            b.setSeriesData();
            c(b, "beforeRender");
            u && (b.pointer = d.hasTouch || !Z.PointerEvent && !Z.MSPointerEvent ? new u(b, a) : new r(b, a));
            b.render();
            if (!b.renderer.imgCount && !b.hasLoaded) b.onload();
            b.temporaryDisplay(!0);
          }
        },
        onload: function onload() {
          this.callbacks.concat([this.callback]).forEach(function (b) {
            b && "undefined" !== typeof this.index && b.apply(this, [this]);
          }, this);
          c(this, "load");
          c(this, "render");
          D(this.index) && this.setReflow(this.options.chart.reflow);
          this.hasLoaded = !0;
        }
      });
    });
    S(r, "parts/ScrollablePlotArea.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.addEvent,
          u = g.createElement,
          I = g.pick,
          M = g.stop;
      g = d.Chart;
      r(g, "afterSetChartSize", function (g) {
        var u = this.options.chart.scrollablePlotArea,
            r = u && u.minWidth;
        u = u && u.minHeight;

        if (!this.renderer.forExport) {
          if (r) {
            if (this.scrollablePixelsX = r = Math.max(0, r - this.chartWidth)) {
              this.plotWidth += r;
              this.inverted ? (this.clipBox.height += r, this.plotBox.height += r) : (this.clipBox.width += r, this.plotBox.width += r);
              var E = {
                1: {
                  name: "right",
                  value: r
                }
              };
            }
          } else u && (this.scrollablePixelsY = r = Math.max(0, u - this.chartHeight)) && (this.plotHeight += r, this.inverted ? (this.clipBox.width += r, this.plotBox.width += r) : (this.clipBox.height += r, this.plotBox.height += r), E = {
            2: {
              name: "bottom",
              value: r
            }
          });

          E && !g.skipAxes && this.axes.forEach(function (g) {
            E[g.side] ? g.getPlotLinePath = function () {
              var t = E[g.side].name,
                  y = this[t];
              this[t] = y - E[g.side].value;
              var h = d.Axis.prototype.getPlotLinePath.apply(this, arguments);
              this[t] = y;
              return h;
            } : (g.setAxisSize(), g.setAxisTranslation());
          });
        }
      });
      r(g, "render", function () {
        this.scrollablePixelsX || this.scrollablePixelsY ? (this.setUpScrolling && this.setUpScrolling(), this.applyFixed()) : this.fixedDiv && this.applyFixed();
      });

      g.prototype.setUpScrolling = function () {
        var d = this,
            g = {
          WebkitOverflowScrolling: "touch",
          overflowX: "hidden",
          overflowY: "hidden"
        };
        this.scrollablePixelsX && (g.overflowX = "auto");
        this.scrollablePixelsY && (g.overflowY = "auto");
        this.scrollingContainer = u("div", {
          className: "highcharts-scrolling"
        }, g, this.renderTo);
        r(this.scrollingContainer, "scroll", function () {
          d.pointer && delete d.pointer.chartPosition;
        });
        this.innerContainer = u("div", {
          className: "highcharts-inner-container"
        }, null, this.scrollingContainer);
        this.innerContainer.appendChild(this.container);
        this.setUpScrolling = null;
      };

      g.prototype.moveFixedElements = function () {
        var d = this.container,
            g = this.fixedRenderer,
            u = ".highcharts-contextbutton .highcharts-credits .highcharts-legend .highcharts-legend-checkbox .highcharts-navigator-series .highcharts-navigator-xaxis .highcharts-navigator-yaxis .highcharts-navigator .highcharts-reset-zoom .highcharts-scrollbar .highcharts-subtitle .highcharts-title".split(" "),
            r;
        this.scrollablePixelsX && !this.inverted ? r = ".highcharts-yaxis" : this.scrollablePixelsX && this.inverted ? r = ".highcharts-xaxis" : this.scrollablePixelsY && !this.inverted ? r = ".highcharts-xaxis" : this.scrollablePixelsY && this.inverted && (r = ".highcharts-yaxis");
        u.push(r, r + "-labels");
        u.forEach(function (y) {
          [].forEach.call(d.querySelectorAll(y), function (d) {
            (d.namespaceURI === g.SVG_NS ? g.box : g.box.parentNode).appendChild(d);
            d.style.pointerEvents = "auto";
          });
        });
      };

      g.prototype.applyFixed = function () {
        var g,
            A = !this.fixedDiv,
            G = this.options.chart.scrollablePlotArea;
        A ? (this.fixedDiv = u("div", {
          className: "highcharts-fixed"
        }, {
          position: "absolute",
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 2
        }, null, !0), this.renderTo.insertBefore(this.fixedDiv, this.renderTo.firstChild), this.renderTo.style.overflow = "visible", this.fixedRenderer = g = new d.Renderer(this.fixedDiv, this.chartWidth, this.chartHeight), this.scrollableMask = g.path().attr({
          fill: this.options.chart.backgroundColor || "#fff",
          "fill-opacity": I(G.opacity, .85),
          zIndex: -1
        }).addClass("highcharts-scrollable-mask").add(), this.moveFixedElements(), r(this, "afterShowResetZoom", this.moveFixedElements), r(this, "afterLayOutTitles", this.moveFixedElements)) : this.fixedRenderer.setSize(this.chartWidth, this.chartHeight);
        g = this.chartWidth + (this.scrollablePixelsX || 0);
        var J = this.chartHeight + (this.scrollablePixelsY || 0);
        M(this.container);
        this.container.style.width = g + "px";
        this.container.style.height = J + "px";
        this.renderer.boxWrapper.attr({
          width: g,
          height: J,
          viewBox: [0, 0, g, J].join(" ")
        });
        this.chartBackground.attr({
          width: g,
          height: J
        });
        this.scrollablePixelsY && (this.scrollingContainer.style.height = this.chartHeight + "px");
        A && (G.scrollPositionX && (this.scrollingContainer.scrollLeft = this.scrollablePixelsX * G.scrollPositionX), G.scrollPositionY && (this.scrollingContainer.scrollTop = this.scrollablePixelsY * G.scrollPositionY));
        J = this.axisOffset;
        A = this.plotTop - J[0] - 1;
        G = this.plotLeft - J[3] - 1;
        g = this.plotTop + this.plotHeight + J[2] + 1;
        J = this.plotLeft + this.plotWidth + J[1] + 1;
        var y = this.plotLeft + this.plotWidth - (this.scrollablePixelsX || 0),
            t = this.plotTop + this.plotHeight - (this.scrollablePixelsY || 0);
        A = this.scrollablePixelsX ? ["M", 0, A, "L", this.plotLeft - 1, A, "L", this.plotLeft - 1, g, "L", 0, g, "Z", "M", y, A, "L", this.chartWidth, A, "L", this.chartWidth, g, "L", y, g, "Z"] : this.scrollablePixelsY ? ["M", G, 0, "L", G, this.plotTop - 1, "L", J, this.plotTop - 1, "L", J, 0, "Z", "M", G, t, "L", G, this.chartHeight, "L", J, this.chartHeight, "L", J, t, "Z"] : ["M", 0, 0];
        "adjustHeight" !== this.redrawTrigger && this.scrollableMask.attr({
          d: A
        });
      };
    });
    S(r, "mixins/legend-symbol.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.merge,
          u = g.pick;
      d.LegendSymbolMixin = {
        drawRectangle: function drawRectangle(d, g) {
          var r = d.symbolHeight,
              A = d.options.squareSymbol;
          g.legendSymbol = this.chart.renderer.rect(A ? (d.symbolWidth - r) / 2 : 0, d.baseline - r + 1, A ? r : d.symbolWidth, r, u(d.options.symbolRadius, r / 2)).addClass("highcharts-point").attr({
            zIndex: 3
          }).add(g.legendGroup);
        },
        drawLineMarker: function drawLineMarker(d) {
          var g = this.options,
              E = g.marker,
              A = d.symbolWidth,
              G = d.symbolHeight,
              J = G / 2,
              y = this.chart.renderer,
              t = this.legendGroup;
          d = d.baseline - Math.round(.3 * d.fontMetrics.b);
          var D = {};
          this.chart.styledMode || (D = {
            "stroke-width": g.lineWidth || 0
          }, g.dashStyle && (D.dashstyle = g.dashStyle));
          this.legendLine = y.path(["M", 0, d, "L", A, d]).addClass("highcharts-graph").attr(D).add(t);
          E && !1 !== E.enabled && A && (g = Math.min(u(E.radius, J), J), 0 === this.symbol.indexOf("url") && (E = r(E, {
            width: G,
            height: G
          }), g = 0), this.legendSymbol = E = y.symbol(this.symbol, A / 2 - g, d - g, 2 * g, 2 * g, E).addClass("highcharts-point").add(t), E.isMarker = !0);
        }
      };
      return d.LegendSymbolMixin;
    });
    S(r, "parts/Point.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {

      var r = g.animObject,
          u = g.defined,
          I = g.erase,
          M = g.extend,
          E = g.format,
          A = g.getNestedProperty,
          G = g.isArray,
          J = g.isNumber,
          y = g.isObject,
          t = g.syncTimeout,
          D = g.pick,
          h = g.removeEvent,
          N = g.uniqueKey,
          q = d.fireEvent;

      g = function () {
        function d() {
          this.colorIndex = this.category = void 0;
          this.formatPrefix = "point";
          this.id = void 0;
          this.isNull = !1;
          this.percentage = this.options = this.name = void 0;
          this.selected = !1;
          this.total = this.series = void 0;
          this.visible = !0;
          this.x = void 0;
        }

        d.prototype.animateBeforeDestroy = function () {
          var e = this,
              c = {
            x: e.startXPos,
            opacity: 0
          },
              d,
              h = e.getGraphicalProps();
          h.singular.forEach(function (f) {
            d = "dataLabel" === f;
            e[f] = e[f].animate(d ? {
              x: e[f].startXPos,
              y: e[f].startYPos,
              opacity: 0
            } : c);
          });
          h.plural.forEach(function (c) {
            e[c].forEach(function (a) {
              a.element && a.animate(M({
                x: e.startXPos
              }, a.startYPos ? {
                x: a.startXPos,
                y: a.startYPos
              } : {}));
            });
          });
        };

        d.prototype.applyOptions = function (e, c) {
          var k = this.series,
              h = k.options.pointValKey || k.pointValKey;
          e = d.prototype.optionsToObject.call(this, e);
          M(this, e);
          this.options = this.options ? M(this.options, e) : e;
          e.group && delete this.group;
          e.dataLabels && delete this.dataLabels;
          h && (this.y = d.prototype.getNestedProperty.call(this, h));
          this.formatPrefix = (this.isNull = D(this.isValid && !this.isValid(), null === this.x || !J(this.y))) ? "null" : "point";
          this.selected && (this.state = "select");
          "name" in this && "undefined" === typeof c && k.xAxis && k.xAxis.hasNames && (this.x = k.xAxis.nameToX(this));
          "undefined" === typeof this.x && k && (this.x = "undefined" === typeof c ? k.autoIncrement(this) : c);
          return this;
        };

        d.prototype.destroy = function () {
          function e() {
            if (c.graphic || c.dataLabel || c.dataLabels) h(c), c.destroyElements();

            for (l in c) {
              c[l] = null;
            }
          }

          var c = this,
              d = c.series,
              n = d.chart;
          d = d.options.dataSorting;
          var f = n.hoverPoints,
              a = r(c.series.chart.renderer.globalAnimation),
              l;
          c.legendItem && n.legend.destroyItem(c);
          f && (c.setState(), I(f, c), f.length || (n.hoverPoints = null));
          if (c === n.hoverPoint) c.onMouseOut();
          d && d.enabled ? (this.animateBeforeDestroy(), t(e, a.duration)) : e();
          n.pointCount--;
        };

        d.prototype.destroyElements = function (e) {
          var c = this;
          e = c.getGraphicalProps(e);
          e.singular.forEach(function (e) {
            c[e] = c[e].destroy();
          });
          e.plural.forEach(function (e) {
            c[e].forEach(function (c) {
              c.element && c.destroy();
            });
            delete c[e];
          });
        };

        d.prototype.firePointEvent = function (e, c, d) {
          var k = this,
              f = this.series.options;
          (f.point.events[e] || k.options && k.options.events && k.options.events[e]) && k.importEvents();
          "click" === e && f.allowPointSelect && (d = function d(a) {
            k.select && k.select(null, a.ctrlKey || a.metaKey || a.shiftKey);
          });
          q(k, e, c, d);
        };

        d.prototype.getClassName = function () {
          return "highcharts-point" + (this.selected ? " highcharts-point-select" : "") + (this.negative ? " highcharts-negative" : "") + (this.isNull ? " highcharts-null-point" : "") + ("undefined" !== typeof this.colorIndex ? " highcharts-color-" + this.colorIndex : "") + (this.options.className ? " " + this.options.className : "") + (this.zone && this.zone.className ? " " + this.zone.className.replace("highcharts-negative", "") : "");
        };

        d.prototype.getGraphicalProps = function (e) {
          var c = this,
              d = [],
              h,
              f = {
            singular: [],
            plural: []
          };
          e = e || {
            graphic: 1,
            dataLabel: 1
          };
          e.graphic && d.push("graphic", "shadowGroup");
          e.dataLabel && d.push("dataLabel", "dataLabelUpper", "connector");

          for (h = d.length; h--;) {
            var a = d[h];
            c[a] && f.singular.push(a);
          }

          ["dataLabel", "connector"].forEach(function (a) {
            var d = a + "s";
            e[a] && c[d] && f.plural.push(d);
          });
          return f;
        };

        d.prototype.getLabelConfig = function () {
          return {
            x: this.category,
            y: this.y,
            color: this.color,
            colorIndex: this.colorIndex,
            key: this.name || this.category,
            series: this.series,
            point: this,
            percentage: this.percentage,
            total: this.total || this.stackTotal
          };
        };

        d.prototype.getNestedProperty = function (e) {
          if (e) return 0 === e.indexOf("custom.") ? A(e, this.options) : this[e];
        };

        d.prototype.getZone = function () {
          var e = this.series,
              c = e.zones;
          e = e.zoneAxis || "y";
          var d = 0,
              h;

          for (h = c[d]; this[e] >= h.value;) {
            h = c[++d];
          }

          this.nonZonedColor || (this.nonZonedColor = this.color);
          this.color = h && h.color && !this.options.color ? h.color : this.nonZonedColor;
          return h;
        };

        d.prototype.hasNewShapeType = function () {
          return (this.graphic && (this.graphic.symbolName || this.graphic.element.nodeName)) !== this.shapeType;
        };

        d.prototype.init = function (e, c, d) {
          this.series = e;
          this.applyOptions(c, d);
          this.id = u(this.id) ? this.id : N();
          this.resolveColor();
          e.chart.pointCount++;
          q(this, "afterInit");
          return this;
        };

        d.prototype.optionsToObject = function (e) {
          var c = {},
              k = this.series,
              h = k.options.keys,
              f = h || k.pointArrayMap || ["y"],
              a = f.length,
              l = 0,
              g = 0;
          if (J(e) || null === e) c[f[0]] = e;else if (G(e)) for (!h && e.length > a && (k = typeof e[0], "string" === k ? c.name = e[0] : "number" === k && (c.x = e[0]), l++); g < a;) {
            h && "undefined" === typeof e[l] || (0 < f[g].indexOf(".") ? d.prototype.setNestedProperty(c, e[l], f[g]) : c[f[g]] = e[l]), l++, g++;
          } else "object" === typeof e && (c = e, e.dataLabels && (k._hasPointLabels = !0), e.marker && (k._hasPointMarkers = !0));
          return c;
        };

        d.prototype.resolveColor = function () {
          var e = this.series;
          var c = e.chart.options.chart.colorCount;
          var d = e.chart.styledMode;
          d || this.options.color || (this.color = e.color);
          e.options.colorByPoint ? (d || (c = e.options.colors || e.chart.options.colors, this.color = this.color || c[e.colorCounter], c = c.length), d = e.colorCounter, e.colorCounter++, e.colorCounter === c && (e.colorCounter = 0)) : d = e.colorIndex;
          this.colorIndex = D(this.colorIndex, d);
        };

        d.prototype.setNestedProperty = function (e, c, d) {
          d.split(".").reduce(function (e, f, a, d) {
            e[f] = d.length - 1 === a ? c : y(e[f], !0) ? e[f] : {};
            return e[f];
          }, e);
          return e;
        };

        d.prototype.tooltipFormatter = function (e) {
          var c = this.series,
              d = c.tooltipOptions,
              h = D(d.valueDecimals, ""),
              f = d.valuePrefix || "",
              a = d.valueSuffix || "";
          c.chart.styledMode && (e = c.chart.tooltip.styledModeFormat(e));
          (c.pointArrayMap || ["y"]).forEach(function (c) {
            c = "{point." + c;
            if (f || a) e = e.replace(RegExp(c + "}", "g"), f + c + "}" + a);
            e = e.replace(RegExp(c + "}", "g"), c + ":,." + h + "f}");
          });
          return E(e, {
            point: this,
            series: this.series
          }, c.chart);
        };

        return d;
      }();

      d.Point = g;
      return d.Point;
    });
    S(r, "parts/Series.js", [r["parts/Globals.js"], r["mixins/legend-symbol.js"], r["parts/Point.js"], r["parts/Utilities.js"]], function (d, g, r, u) {

      var I = u.addEvent,
          M = u.animObject,
          E = u.arrayMax,
          A = u.arrayMin,
          G = u.clamp,
          J = u.correctFloat,
          y = u.defined,
          t = u.erase,
          D = u.error,
          h = u.extend,
          N = u.find,
          q = u.fireEvent,
          P = u.getNestedProperty,
          e = u.isArray,
          c = u.isFunction,
          k = u.isNumber,
          n = u.isString,
          f = u.merge,
          a = u.objectEach,
          l = u.pick,
          v = u.removeEvent,
          z = u.seriesType,
          w = u.splat,
          B = u.syncTimeout,
          L = d.defaultOptions,
          Q = d.defaultPlotOptions,
          H = d.seriesTypes,
          K = d.SVGElement,
          p = d.win;
      d.Series = z("line", null, {
        lineWidth: 2,
        allowPointSelect: !1,
        showCheckbox: !1,
        animation: {
          duration: 1E3
        },
        events: {},
        marker: {
          enabledThreshold: 2,
          lineColor: "#ffffff",
          lineWidth: 0,
          radius: 4,
          states: {
            normal: {
              animation: !0
            },
            hover: {
              animation: {
                duration: 50
              },
              enabled: !0,
              radiusPlus: 2,
              lineWidthPlus: 1
            },
            select: {
              fillColor: "#cccccc",
              lineColor: "#000000",
              lineWidth: 2
            }
          }
        },
        point: {
          events: {}
        },
        dataLabels: {
          align: "center",
          formatter: function formatter() {
            var b = this.series.chart.numberFormatter;
            return "number" !== typeof this.y ? "" : b(this.y, -1);
          },
          padding: 5,
          style: {
            fontSize: "11px",
            fontWeight: "bold",
            color: "contrast",
            textOutline: "1px contrast"
          },
          verticalAlign: "bottom",
          x: 0,
          y: 0
        },
        cropThreshold: 300,
        opacity: 1,
        pointRange: 0,
        softThreshold: !0,
        states: {
          normal: {
            animation: !0
          },
          hover: {
            animation: {
              duration: 50
            },
            lineWidthPlus: 1,
            marker: {},
            halo: {
              size: 10,
              opacity: .25
            }
          },
          select: {
            animation: {
              duration: 0
            }
          },
          inactive: {
            animation: {
              duration: 50
            },
            opacity: .2
          }
        },
        stickyTracking: !0,
        turboThreshold: 1E3,
        findNearestPointBy: "x"
      }, {
        axisTypes: ["xAxis", "yAxis"],
        coll: "series",
        colorCounter: 0,
        cropShoulder: 1,
        directTouch: !1,
        eventsToUnbind: [],
        isCartesian: !0,
        parallelArrays: ["x", "y"],
        pointClass: r,
        requireSorting: !0,
        sorted: !0,
        init: function init(b, e) {
          q(this, "init", {
            options: e
          });
          var f = this,
              d = b.series,
              k;
          this.eventOptions = this.eventOptions || {};
          f.chart = b;
          f.options = e = f.setOptions(e);
          f.linkedSeries = [];
          f.bindAxes();
          h(f, {
            name: e.name,
            state: "",
            visible: !1 !== e.visible,
            selected: !0 === e.selected
          });
          var p = e.events;
          a(p, function (b, a) {
            c(b) && f.eventOptions[a] !== b && (c(f.eventOptions[a]) && v(f, a, f.eventOptions[a]), f.eventOptions[a] = b, I(f, a, b));
          });
          if (p && p.click || e.point && e.point.events && e.point.events.click || e.allowPointSelect) b.runTrackerClick = !0;
          f.getColor();
          f.getSymbol();
          f.parallelArrays.forEach(function (b) {
            f[b + "Data"] || (f[b + "Data"] = []);
          });
          f.isCartesian && (b.hasCartesianSeries = !0);
          d.length && (k = d[d.length - 1]);
          f._i = l(k && k._i, -1) + 1;
          b.orderSeries(this.insert(d));
          e.dataSorting && e.dataSorting.enabled ? f.setDataSortingOptions() : f.points || f.data || f.setData(e.data, !1);
          q(this, "afterInit");
        },
        is: function is(b) {
          return H[b] && this instanceof H[b];
        },
        insert: function insert(b) {
          var a = this.options.index,
              c;

          if (k(a)) {
            for (c = b.length; c--;) {
              if (a >= l(b[c].options.index, b[c]._i)) {
                b.splice(c + 1, 0, this);
                break;
              }
            }

            -1 === c && b.unshift(this);
            c += 1;
          } else b.push(this);

          return l(c, b.length - 1);
        },
        bindAxes: function bindAxes() {
          var b = this,
              a = b.options,
              c = b.chart,
              e;
          q(this, "bindAxes", null, function () {
            (b.axisTypes || []).forEach(function (f) {
              c[f].forEach(function (c) {
                e = c.options;
                if (a[f] === e.index || "undefined" !== typeof a[f] && a[f] === e.id || "undefined" === typeof a[f] && 0 === e.index) b.insert(c.series), b[f] = c, c.isDirty = !0;
              });
              b[f] || b.optionalAxis === f || D(18, !0, c);
            });
          });
          q(this, "afterBindAxes");
        },
        updateParallelArrays: function updateParallelArrays(b, a) {
          var c = b.series,
              e = arguments,
              f = k(a) ? function (e) {
            var f = "y" === e && c.toYData ? c.toYData(b) : b[e];
            c[e + "Data"][a] = f;
          } : function (b) {
            Array.prototype[a].apply(c[b + "Data"], Array.prototype.slice.call(e, 2));
          };
          c.parallelArrays.forEach(f);
        },
        hasData: function hasData() {
          return this.visible && "undefined" !== typeof this.dataMax && "undefined" !== typeof this.dataMin || this.visible && this.yData && 0 < this.yData.length;
        },
        autoIncrement: function autoIncrement() {
          var b = this.options,
              a = this.xIncrement,
              c,
              e = b.pointIntervalUnit,
              f = this.chart.time;
          a = l(a, b.pointStart, 0);
          this.pointInterval = c = l(this.pointInterval, b.pointInterval, 1);
          e && (b = new f.Date(a), "day" === e ? f.set("Date", b, f.get("Date", b) + c) : "month" === e ? f.set("Month", b, f.get("Month", b) + c) : "year" === e && f.set("FullYear", b, f.get("FullYear", b) + c), c = b.getTime() - a);
          this.xIncrement = a + c;
          return a;
        },
        setDataSortingOptions: function setDataSortingOptions() {
          var b = this.options;
          h(this, {
            requireSorting: !1,
            sorted: !1,
            enabledDataSorting: !0,
            allowDG: !1
          });
          y(b.pointRange) || (b.pointRange = 1);
        },
        setOptions: function setOptions(b) {
          var a = this.chart,
              c = a.options,
              e = c.plotOptions,
              d = a.userOptions || {};
          b = f(b);
          a = a.styledMode;
          var k = {
            plotOptions: e,
            userOptions: b
          };
          q(this, "setOptions", k);
          var h = k.plotOptions[this.type],
              m = d.plotOptions || {};
          this.userOptions = k.userOptions;
          d = f(h, e.series, d.plotOptions && d.plotOptions[this.type], b);
          this.tooltipOptions = f(L.tooltip, L.plotOptions.series && L.plotOptions.series.tooltip, L.plotOptions[this.type].tooltip, c.tooltip.userOptions, e.series && e.series.tooltip, e[this.type].tooltip, b.tooltip);
          this.stickyTracking = l(b.stickyTracking, m[this.type] && m[this.type].stickyTracking, m.series && m.series.stickyTracking, this.tooltipOptions.shared && !this.noSharedTooltip ? !0 : d.stickyTracking);
          null === h.marker && delete d.marker;
          this.zoneAxis = d.zoneAxis;
          c = this.zones = (d.zones || []).slice();
          !d.negativeColor && !d.negativeFillColor || d.zones || (e = {
            value: d[this.zoneAxis + "Threshold"] || d.threshold || 0,
            className: "highcharts-negative"
          }, a || (e.color = d.negativeColor, e.fillColor = d.negativeFillColor), c.push(e));
          c.length && y(c[c.length - 1].value) && c.push(a ? {} : {
            color: this.color,
            fillColor: this.fillColor
          });
          q(this, "afterSetOptions", {
            options: d
          });
          return d;
        },
        getName: function getName() {
          return l(this.options.name, "Series " + (this.index + 1));
        },
        getCyclic: function getCyclic(b, a, c) {
          var e = this.chart,
              f = this.userOptions,
              d = b + "Index",
              k = b + "Counter",
              h = c ? c.length : l(e.options.chart[b + "Count"], e[b + "Count"]);

          if (!a) {
            var p = l(f[d], f["_" + d]);
            y(p) || (e.series.length || (e[k] = 0), f["_" + d] = p = e[k] % h, e[k] += 1);
            c && (a = c[p]);
          }

          "undefined" !== typeof p && (this[d] = p);
          this[b] = a;
        },
        getColor: function getColor() {
          this.chart.styledMode ? this.getCyclic("color") : this.options.colorByPoint ? this.options.color = null : this.getCyclic("color", this.options.color || Q[this.type].color, this.chart.options.colors);
        },
        getPointsCollection: function getPointsCollection() {
          return (this.hasGroupedData ? this.points : this.data) || [];
        },
        getSymbol: function getSymbol() {
          this.getCyclic("symbol", this.options.marker.symbol, this.chart.options.symbols);
        },
        findPointIndex: function findPointIndex(b, a) {
          var c = b.id,
              e = b.x,
              f = this.points,
              d,
              h = this.options.dataSorting;
          if (c) var l = this.chart.get(c);else if (this.linkedParent || this.enabledDataSorting) {
            var p = h && h.matchByName ? "name" : "index";
            l = N(f, function (a) {
              return !a.touched && a[p] === b[p];
            });
            if (!l) return;
          }

          if (l) {
            var n = l && l.index;
            "undefined" !== typeof n && (d = !0);
          }

          "undefined" === typeof n && k(e) && (n = this.xData.indexOf(e, a));
          -1 !== n && "undefined" !== typeof n && this.cropped && (n = n >= this.cropStart ? n - this.cropStart : n);
          !d && f[n] && f[n].touched && (n = void 0);
          return n;
        },
        drawLegendSymbol: g.drawLineMarker,
        updateData: function updateData(b, a) {
          var c = this.options,
              e = c.dataSorting,
              f = this.points,
              d = [],
              h,
              l,
              n,
              p = this.requireSorting,
              g = b.length === f.length,
              w = !0;
          this.xIncrement = null;
          b.forEach(function (b, a) {
            var l = y(b) && this.pointClass.prototype.optionsToObject.call({
              series: this
            }, b) || {};
            var m = l.x;

            if (l.id || k(m)) {
              if (m = this.findPointIndex(l, n), -1 === m || "undefined" === typeof m ? d.push(b) : f[m] && b !== c.data[m] ? (f[m].update(b, !1, null, !1), f[m].touched = !0, p && (n = m + 1)) : f[m] && (f[m].touched = !0), !g || a !== m || e && e.enabled || this.hasDerivedData) h = !0;
            } else d.push(b);
          }, this);
          if (h) for (b = f.length; b--;) {
            (l = f[b]) && !l.touched && l.remove && l.remove(!1, a);
          } else !g || e && e.enabled ? w = !1 : (b.forEach(function (b, a) {
            f[a].update && b !== f[a].y && f[a].update(b, !1, null, !1);
          }), d.length = 0);
          f.forEach(function (b) {
            b && (b.touched = !1);
          });
          if (!w) return !1;
          d.forEach(function (b) {
            this.addPoint(b, !1, null, null, !1);
          }, this);
          null === this.xIncrement && this.xData && this.xData.length && (this.xIncrement = E(this.xData), this.autoIncrement());
          return !0;
        },
        setData: function setData(b, a, c, f) {
          var d = this,
              h = d.points,
              p = h && h.length || 0,
              m,
              g = d.options,
              w = d.chart,
              x = g.dataSorting,
              q = null,
              v = d.xAxis;
          q = g.turboThreshold;
          var C = this.xData,
              t = this.yData,
              B = (m = d.pointArrayMap) && m.length,
              z = g.keys,
              y = 0,
              L = 1,
              u;
          b = b || [];
          m = b.length;
          a = l(a, !0);
          x && x.enabled && (b = this.sortData(b));
          !1 !== f && m && p && !d.cropped && !d.hasGroupedData && d.visible && !d.isSeriesBoosting && (u = this.updateData(b, c));

          if (!u) {
            d.xIncrement = null;
            d.colorCounter = 0;
            this.parallelArrays.forEach(function (b) {
              d[b + "Data"].length = 0;
            });
            if (q && m > q) {
              if (q = d.getFirstValidPoint(b), k(q)) for (c = 0; c < m; c++) {
                C[c] = this.autoIncrement(), t[c] = b[c];
              } else if (e(q)) {
                if (B) for (c = 0; c < m; c++) {
                  f = b[c], C[c] = f[0], t[c] = f.slice(1, B + 1);
                } else for (z && (y = z.indexOf("x"), L = z.indexOf("y"), y = 0 <= y ? y : 0, L = 0 <= L ? L : 1), c = 0; c < m; c++) {
                  f = b[c], C[c] = f[y], t[c] = f[L];
                }
              } else D(12, !1, w);
            } else for (c = 0; c < m; c++) {
              "undefined" !== typeof b[c] && (f = {
                series: d
              }, d.pointClass.prototype.applyOptions.apply(f, [b[c]]), d.updateParallelArrays(f, c));
            }
            t && n(t[0]) && D(14, !0, w);
            d.data = [];
            d.options.data = d.userOptions.data = b;

            for (c = p; c--;) {
              h[c] && h[c].destroy && h[c].destroy();
            }

            v && (v.minRange = v.userMinRange);
            d.isDirty = w.isDirtyBox = !0;
            d.isDirtyData = !!h;
            c = !1;
          }

          "point" === g.legendType && (this.processData(), this.generatePoints());
          a && w.redraw(c);
        },
        sortData: function sortData(b) {
          var a = this,
              c = a.options.dataSorting.sortKey || "y",
              e = function e(b, a) {
            return y(a) && b.pointClass.prototype.optionsToObject.call({
              series: b
            }, a) || {};
          };

          b.forEach(function (c, f) {
            b[f] = e(a, c);
            b[f].index = f;
          }, this);
          b.concat().sort(function (b, a) {
            b = P(c, b);
            a = P(c, a);
            return a < b ? -1 : a > b ? 1 : 0;
          }).forEach(function (b, a) {
            b.x = a;
          }, this);
          a.linkedSeries && a.linkedSeries.forEach(function (a) {
            var c = a.options,
                f = c.data;
            c.dataSorting && c.dataSorting.enabled || !f || (f.forEach(function (c, d) {
              f[d] = e(a, c);
              b[d] && (f[d].x = b[d].x, f[d].index = d);
            }), a.setData(f, !1));
          });
          return b;
        },
        processData: function processData(b) {
          var a = this.xData,
              c = this.yData,
              e = a.length;
          var f = 0;
          var d = this.xAxis,
              k = this.options;
          var h = k.cropThreshold;
          var l = this.getExtremesFromAll || k.getExtremesFromAll,
              p = this.isCartesian;
          k = d && d.val2lin;
          var n = d && d.isLog,
              g = this.requireSorting;
          if (p && !this.isDirty && !d.isDirty && !this.yAxis.isDirty && !b) return !1;

          if (d) {
            b = d.getExtremes();
            var w = b.min;
            var q = b.max;
          }

          if (p && this.sorted && !l && (!h || e > h || this.forceCrop)) if (a[e - 1] < w || a[0] > q) a = [], c = [];else if (this.yData && (a[0] < w || a[e - 1] > q)) {
            f = this.cropData(this.xData, this.yData, w, q);
            a = f.xData;
            c = f.yData;
            f = f.start;
            var v = !0;
          }

          for (h = a.length || 1; --h;) {
            if (e = n ? k(a[h]) - k(a[h - 1]) : a[h] - a[h - 1], 0 < e && ("undefined" === typeof t || e < t)) var t = e;else 0 > e && g && (D(15, !1, this.chart), g = !1);
          }

          this.cropped = v;
          this.cropStart = f;
          this.processedXData = a;
          this.processedYData = c;
          this.closestPointRange = this.basePointRange = t;
        },
        cropData: function cropData(b, a, c, e, f) {
          var d = b.length,
              k = 0,
              h = d,
              p;
          f = l(f, this.cropShoulder);

          for (p = 0; p < d; p++) {
            if (b[p] >= c) {
              k = Math.max(0, p - f);
              break;
            }
          }

          for (c = p; c < d; c++) {
            if (b[c] > e) {
              h = c + f;
              break;
            }
          }

          return {
            xData: b.slice(k, h),
            yData: a.slice(k, h),
            start: k,
            end: h
          };
        },
        generatePoints: function generatePoints() {
          var b = this.options,
              a = b.data,
              c = this.data,
              e,
              f = this.processedXData,
              d = this.processedYData,
              k = this.pointClass,
              l = f.length,
              p = this.cropStart || 0,
              n = this.hasGroupedData;
          b = b.keys;
          var g = [],
              v;
          c || n || (c = [], c.length = a.length, c = this.data = c);
          b && n && (this.options.keys = !1);

          for (v = 0; v < l; v++) {
            var t = p + v;

            if (n) {
              var B = new k().init(this, [f[v]].concat(w(d[v])));
              B.dataGroup = this.groupMap[v];
              B.dataGroup.options && (B.options = B.dataGroup.options, h(B, B.dataGroup.options), delete B.dataLabels);
            } else (B = c[t]) || "undefined" === typeof a[t] || (c[t] = B = new k().init(this, a[t], f[v]));

            B && (B.index = t, g[v] = B);
          }

          this.options.keys = b;
          if (c && (l !== (e = c.length) || n)) for (v = 0; v < e; v++) {
            v !== p || n || (v += l), c[v] && (c[v].destroyElements(), c[v].plotX = void 0);
          }
          this.data = c;
          this.points = g;
          q(this, "afterGeneratePoints");
        },
        getXExtremes: function getXExtremes(b) {
          return {
            min: A(b),
            max: E(b)
          };
        },
        getExtremes: function getExtremes(b) {
          var a = this.xAxis,
              c = this.yAxis,
              f = this.processedXData || this.xData,
              d = [],
              h = 0,
              l = 0;
          var m = 0;
          var p = this.requireSorting ? this.cropShoulder : 0,
              n = c ? c.positiveValuesOnly : !1,
              g;
          b = b || this.stackedYData || this.processedYData || [];
          c = b.length;
          a && (m = a.getExtremes(), l = m.min, m = m.max);

          for (g = 0; g < c; g++) {
            var w = f[g];
            var v = b[g];
            var t = (k(v) || e(v)) && (v.length || 0 < v || !n);
            w = this.getExtremesFromAll || this.options.getExtremesFromAll || this.cropped || !a || (f[g + p] || w) >= l && (f[g - p] || w) <= m;
            if (t && w) if (t = v.length) for (; t--;) {
              k(v[t]) && (d[h++] = v[t]);
            } else d[h++] = v;
          }

          this.dataMin = A(d);
          this.dataMax = E(d);
          q(this, "afterGetExtremes");
        },
        getFirstValidPoint: function getFirstValidPoint(b) {
          for (var a = null, c = b.length, e = 0; null === a && e < c;) {
            a = b[e], e++;
          }

          return a;
        },
        translate: function translate() {
          this.processedXData || this.processData();
          this.generatePoints();
          var b = this.options,
              a = b.stacking,
              c = this.xAxis,
              f = c.categories,
              d = this.enabledDataSorting,
              h = this.yAxis,
              p = this.points,
              m = p.length,
              n = !!this.modifyValue,
              g,
              w = this.pointPlacementToXValue(),
              v = !!w,
              t = b.threshold,
              B = b.startFromThreshold ? t : 0,
              z,
              L = this.zoneAxis || "y",
              u = Number.MAX_VALUE;

          for (g = 0; g < m; g++) {
            var K = p[g],
                r = K.x,
                H = K.y,
                D = K.low,
                A = a && h.stacks[(this.negStacks && H < (B ? 0 : t) ? "-" : "") + this.stackKey];
            h.positiveValuesOnly && null !== H && 0 >= H && (K.isNull = !0);
            K.plotX = z = J(G(c.translate(r, 0, 0, 0, 1, w, "flags" === this.type), -1E5, 1E5));

            if (a && this.visible && A && A[r]) {
              var N = this.getStackIndicator(N, r, this.index);

              if (!K.isNull) {
                var Q = A[r];
                var E = Q.points[N.key];
              }
            }

            e(E) && (D = E[0], H = E[1], D === B && N.key === A[r].base && (D = l(k(t) && t, h.min)), h.positiveValuesOnly && 0 >= D && (D = null), K.total = K.stackTotal = Q.total, K.percentage = Q.total && K.y / Q.total * 100, K.stackY = H, this.irregularWidths || Q.setOffset(this.pointXOffset || 0, this.barW || 0));
            K.yBottom = y(D) ? G(h.translate(D, 0, 1, 0, 1), -1E5, 1E5) : null;
            n && (H = this.modifyValue(H, K));
            K.plotY = "number" === typeof H && Infinity !== H ? G(h.translate(H, 0, 1, 0, 1), -1E5, 1E5) : void 0;
            K.isInside = this.isPointInside(K);
            K.clientX = v ? J(c.translate(r, 0, 0, 0, 1, w)) : z;
            K.negative = K[L] < (b[L + "Threshold"] || t || 0);
            K.category = f && "undefined" !== typeof f[K.x] ? f[K.x] : K.x;

            if (!K.isNull && !1 !== K.visible) {
              "undefined" !== typeof P && (u = Math.min(u, Math.abs(z - P)));
              var P = z;
            }

            K.zone = this.zones.length && K.getZone();
            !K.graphic && this.group && d && (K.isNew = !0);
          }

          this.closestPointRangePx = u;
          q(this, "afterTranslate");
        },
        getValidPoints: function getValidPoints(b, a, c) {
          var e = this.chart;
          return (b || this.points || []).filter(function (b) {
            return a && !e.isInsidePlot(b.plotX, b.plotY, e.inverted) ? !1 : !1 !== b.visible && (c || !b.isNull);
          });
        },
        getClipBox: function getClipBox(b, a) {
          var c = this.options,
              e = this.chart,
              f = e.inverted,
              d = this.xAxis,
              k = d && this.yAxis;
          b && !1 === c.clip && k ? b = f ? {
            y: -e.chartWidth + k.len + k.pos,
            height: e.chartWidth,
            width: e.chartHeight,
            x: -e.chartHeight + d.len + d.pos
          } : {
            y: -k.pos,
            height: e.chartHeight,
            width: e.chartWidth,
            x: -d.pos
          } : (b = this.clipBox || e.clipBox, a && (b.width = e.plotSizeX, b.x = 0));
          return a ? {
            width: b.width,
            x: b.x
          } : b;
        },
        setClip: function setClip(b) {
          var a = this.chart,
              c = this.options,
              e = a.renderer,
              f = a.inverted,
              d = this.clipBox,
              k = this.getClipBox(b),
              h = this.sharedClipKey || ["_sharedClip", b && b.duration, b && b.easing, k.height, c.xAxis, c.yAxis].join(),
              l = a[h],
              p = a[h + "m"];
          b && (k.width = 0, f && (k.x = a.plotHeight + (!1 !== c.clip ? 0 : a.plotTop)));
          l ? a.hasLoaded || l.attr(k) : (b && (a[h + "m"] = p = e.clipRect(f ? a.plotSizeX + 99 : -99, f ? -a.plotLeft : -a.plotTop, 99, f ? a.chartWidth : a.chartHeight)), a[h] = l = e.clipRect(k), l.count = {
            length: 0
          });
          b && !l.count[this.index] && (l.count[this.index] = !0, l.count.length += 1);
          if (!1 !== c.clip || b) this.group.clip(b || d ? l : a.clipRect), this.markerGroup.clip(p), this.sharedClipKey = h;
          b || (l.count[this.index] && (delete l.count[this.index], --l.count.length), 0 === l.count.length && h && a[h] && (d || (a[h] = a[h].destroy()), a[h + "m"] && (a[h + "m"] = a[h + "m"].destroy())));
        },
        animate: function animate(b) {
          var a = this.chart,
              c = M(this.options.animation);
          if (!a.hasRendered) if (b) this.setClip(c);else {
            var e = this.sharedClipKey;
            b = a[e];
            var f = this.getClipBox(c, !0);
            b && b.animate(f, c);
            a[e + "m"] && a[e + "m"].animate({
              width: f.width + 99,
              x: f.x - (a.inverted ? 0 : 99)
            }, c);
          }
        },
        afterAnimate: function afterAnimate() {
          this.setClip();
          q(this, "afterAnimate");
          this.finishedAnimating = !0;
        },
        drawPoints: function drawPoints() {
          var b = this.points,
              a = this.chart,
              c,
              e,
              f = this.options.marker,
              d = this[this.specialGroup] || this.markerGroup,
              k = this.xAxis,
              h = l(f.enabled, !k || k.isRadial ? !0 : null, this.closestPointRangePx >= f.enabledThreshold * f.radius);
          if (!1 !== f.enabled || this._hasPointMarkers) for (c = 0; c < b.length; c++) {
            var p = b[c];
            var n = (e = p.graphic) ? "animate" : "attr";
            var g = p.marker || {};
            var w = !!p.marker;

            if ((h && "undefined" === typeof g.enabled || g.enabled) && !p.isNull && !1 !== p.visible) {
              var v = l(g.symbol, this.symbol);
              var q = this.markerAttribs(p, p.selected && "select");
              this.enabledDataSorting && (p.startXPos = k.reversed ? -q.width : k.width);
              var t = !1 !== p.isInside;
              e ? e[t ? "show" : "hide"](t).animate(q) : t && (0 < q.width || p.hasImage) && (p.graphic = e = a.renderer.symbol(v, q.x, q.y, q.width, q.height, w ? g : f).add(d), this.enabledDataSorting && a.hasRendered && (e.attr({
                x: p.startXPos
              }), n = "animate"));
              e && "animate" === n && e[t ? "show" : "hide"](t).animate(q);
              if (e && !a.styledMode) e[n](this.pointAttribs(p, p.selected && "select"));
              e && e.addClass(p.getClassName(), !0);
            } else e && (p.graphic = e.destroy());
          }
        },
        markerAttribs: function markerAttribs(b, a) {
          var c = this.options.marker,
              e = b.marker || {},
              f = e.symbol || c.symbol,
              d = l(e.radius, c.radius);
          a && (c = c.states[a], a = e.states && e.states[a], d = l(a && a.radius, c && c.radius, d + (c && c.radiusPlus || 0)));
          b.hasImage = f && 0 === f.indexOf("url");
          b.hasImage && (d = 0);
          b = {
            x: Math.floor(b.plotX) - d,
            y: b.plotY - d
          };
          d && (b.width = b.height = 2 * d);
          return b;
        },
        pointAttribs: function pointAttribs(b, a) {
          var c = this.options.marker,
              e = b && b.options,
              f = e && e.marker || {},
              d = this.color,
              k = e && e.color,
              h = b && b.color;
          e = l(f.lineWidth, c.lineWidth);
          var p = b && b.zone && b.zone.color;
          b = 1;
          d = k || p || h || d;
          k = f.fillColor || c.fillColor || d;
          d = f.lineColor || c.lineColor || d;
          a = a || "normal";
          c = c.states[a];
          a = f.states && f.states[a] || {};
          e = l(a.lineWidth, c.lineWidth, e + l(a.lineWidthPlus, c.lineWidthPlus, 0));
          k = a.fillColor || c.fillColor || k;
          d = a.lineColor || c.lineColor || d;
          b = l(a.opacity, c.opacity, b);
          return {
            stroke: d,
            "stroke-width": e,
            fill: k,
            opacity: b
          };
        },
        destroy: function destroy(b) {
          var c = this,
              e = c.chart,
              f = /AppleWebKit\/533/.test(p.navigator.userAgent),
              d,
              k,
              h = c.data || [],
              l,
              n;
          q(c, "destroy");
          this.removeEvents(b);
          (c.axisTypes || []).forEach(function (b) {
            (n = c[b]) && n.series && (t(n.series, c), n.isDirty = n.forceRedraw = !0);
          });
          c.legendItem && c.chart.legend.destroyItem(c);

          for (k = h.length; k--;) {
            (l = h[k]) && l.destroy && l.destroy();
          }

          c.points = null;
          u.clearTimeout(c.animationTimeout);
          a(c, function (b, a) {
            b instanceof K && !b.survive && (d = f && "group" === a ? "hide" : "destroy", b[d]());
          });
          e.hoverSeries === c && (e.hoverSeries = null);
          t(e.series, c);
          e.orderSeries();
          a(c, function (a, e) {
            b && "hcEvents" === e || delete c[e];
          });
        },
        getGraphPath: function getGraphPath(b, a, c) {
          var e = this,
              f = e.options,
              d = f.step,
              k,
              h = [],
              l = [],
              p;
          b = b || e.points;
          (k = b.reversed) && b.reverse();
          (d = {
            right: 1,
            center: 2
          }[d] || d && 3) && k && (d = 4 - d);
          b = this.getValidPoints(b, !1, !(f.connectNulls && !a && !c));
          b.forEach(function (k, n) {
            var m = k.plotX,
                g = k.plotY,
                w = b[n - 1];
            (k.leftCliff || w && w.rightCliff) && !c && (p = !0);
            k.isNull && !y(a) && 0 < n ? p = !f.connectNulls : k.isNull && !a ? p = !0 : (0 === n || p ? n = ["M", k.plotX, k.plotY] : e.getPointSpline ? n = e.getPointSpline(b, k, n) : d ? (n = 1 === d ? ["L", w.plotX, g] : 2 === d ? ["L", (w.plotX + m) / 2, w.plotY, "L", (w.plotX + m) / 2, g] : ["L", m, w.plotY], n.push("L", m, g)) : n = ["L", m, g], l.push(k.x), d && (l.push(k.x), 2 === d && l.push(k.x)), h.push.apply(h, n), p = !1);
          });
          h.xMap = l;
          return e.graphPath = h;
        },
        drawGraph: function drawGraph() {
          var b = this,
              a = this.options,
              c = (this.gappedPath || this.getGraphPath).call(this),
              e = this.chart.styledMode,
              f = [["graph", "highcharts-graph"]];
          e || f[0].push(a.lineColor || this.color || "#cccccc", a.dashStyle);
          f = b.getZonesGraphs(f);
          f.forEach(function (f, d) {
            var k = f[0],
                h = b[k],
                l = h ? "animate" : "attr";
            h ? (h.endX = b.preventGraphAnimation ? null : c.xMap, h.animate({
              d: c
            })) : c.length && (b[k] = h = b.chart.renderer.path(c).addClass(f[1]).attr({
              zIndex: 1
            }).add(b.group));
            h && !e && (k = {
              stroke: f[2],
              "stroke-width": a.lineWidth,
              fill: b.fillGraph && b.color || "none"
            }, f[3] ? k.dashstyle = f[3] : "square" !== a.linecap && (k["stroke-linecap"] = k["stroke-linejoin"] = "round"), h[l](k).shadow(2 > d && a.shadow));
            h && (h.startX = c.xMap, h.isArea = c.isArea);
          });
        },
        getZonesGraphs: function getZonesGraphs(a) {
          this.zones.forEach(function (b, c) {
            c = ["zone-graph-" + c, "highcharts-graph highcharts-zone-graph-" + c + " " + (b.className || "")];
            this.chart.styledMode || c.push(b.color || this.color, b.dashStyle || this.options.dashStyle);
            a.push(c);
          }, this);
          return a;
        },
        applyZones: function applyZones() {
          var a = this,
              c = this.chart,
              e = c.renderer,
              f = this.zones,
              d,
              k,
              h = this.clips || [],
              p,
              n = this.graph,
              g = this.area,
              w = Math.max(c.chartWidth, c.chartHeight),
              v = this[(this.zoneAxis || "y") + "Axis"],
              q = c.inverted,
              t,
              B,
              z,
              y = !1;

          if (f.length && (n || g) && v && "undefined" !== typeof v.min) {
            var K = v.reversed;
            var L = v.horiz;
            n && !this.showLine && n.hide();
            g && g.hide();
            var u = v.getExtremes();
            f.forEach(function (b, f) {
              d = K ? L ? c.plotWidth : 0 : L ? 0 : v.toPixels(u.min) || 0;
              d = G(l(k, d), 0, w);
              k = G(Math.round(v.toPixels(l(b.value, u.max), !0) || 0), 0, w);
              y && (d = k = v.toPixels(u.max));
              t = Math.abs(d - k);
              B = Math.min(d, k);
              z = Math.max(d, k);
              v.isXAxis ? (p = {
                x: q ? z : B,
                y: 0,
                width: t,
                height: w
              }, L || (p.x = c.plotHeight - p.x)) : (p = {
                x: 0,
                y: q ? z : B,
                width: w,
                height: t
              }, L && (p.y = c.plotWidth - p.y));
              q && e.isVML && (p = v.isXAxis ? {
                x: 0,
                y: K ? B : z,
                height: p.width,
                width: c.chartWidth
              } : {
                x: p.y - c.plotLeft - c.spacingBox.x,
                y: 0,
                width: p.height,
                height: c.chartHeight
              });
              h[f] ? h[f].animate(p) : h[f] = e.clipRect(p);
              n && a["zone-graph-" + f].clip(h[f]);
              g && a["zone-area-" + f].clip(h[f]);
              y = b.value > u.max;
              a.resetZones && 0 === k && (k = void 0);
            });
            this.clips = h;
          } else a.visible && (n && n.show(!0), g && g.show(!0));
        },
        invertGroups: function invertGroups(a) {
          function b() {
            ["group", "markerGroup"].forEach(function (b) {
              c[b] && (e.renderer.isVML && c[b].attr({
                width: c.yAxis.len,
                height: c.xAxis.len
              }), c[b].width = c.yAxis.len, c[b].height = c.xAxis.len, c[b].invert(c.isRadialSeries ? !1 : a));
            });
          }

          var c = this,
              e = c.chart;
          c.xAxis && (c.eventsToUnbind.push(I(e, "resize", b)), b(), c.invertGroups = b);
        },
        plotGroup: function plotGroup(a, c, e, f, d) {
          var b = this[a],
              k = !b;
          k && (this[a] = b = this.chart.renderer.g().attr({
            zIndex: f || .1
          }).add(d));
          b.addClass("highcharts-" + c + " highcharts-series-" + this.index + " highcharts-" + this.type + "-series " + (y(this.colorIndex) ? "highcharts-color-" + this.colorIndex + " " : "") + (this.options.className || "") + (b.hasClass("highcharts-tracker") ? " highcharts-tracker" : ""), !0);
          b.attr({
            visibility: e
          })[k ? "attr" : "animate"](this.getPlotBox());
          return b;
        },
        getPlotBox: function getPlotBox() {
          var a = this.chart,
              c = this.xAxis,
              e = this.yAxis;
          a.inverted && (c = e, e = this.xAxis);
          return {
            translateX: c ? c.left : a.plotLeft,
            translateY: e ? e.top : a.plotTop,
            scaleX: 1,
            scaleY: 1
          };
        },
        removeEvents: function removeEvents(a) {
          a ? this.eventsToUnbind.length && (this.eventsToUnbind.forEach(function (a) {
            a();
          }), this.eventsToUnbind.length = 0) : v(this);
        },
        render: function render() {
          var a = this,
              c = a.chart,
              e = a.options,
              f = !a.finishedAnimating && c.renderer.isSVG && M(e.animation).duration,
              d = a.visible ? "inherit" : "hidden",
              k = e.zIndex,
              h = a.hasRendered,
              l = c.seriesGroup,
              p = c.inverted;
          q(this, "render");
          var n = a.plotGroup("group", "series", d, k, l);
          a.markerGroup = a.plotGroup("markerGroup", "markers", d, k, l);
          f && a.animate && a.animate(!0);
          n.inverted = a.isCartesian || a.invertable ? p : !1;
          a.drawGraph && (a.drawGraph(), a.applyZones());
          a.visible && a.drawPoints();
          a.drawDataLabels && a.drawDataLabels();
          a.redrawPoints && a.redrawPoints();
          a.drawTracker && !1 !== a.options.enableMouseTracking && a.drawTracker();
          a.invertGroups(p);
          !1 === e.clip || a.sharedClipKey || h || n.clip(c.clipRect);
          f && a.animate && a.animate();
          h || (a.animationTimeout = B(function () {
            a.afterAnimate();
          }, f || 0));
          a.isDirty = !1;
          a.hasRendered = !0;
          q(a, "afterRender");
        },
        redraw: function redraw() {
          var a = this.chart,
              c = this.isDirty || this.isDirtyData,
              e = this.group,
              f = this.xAxis,
              d = this.yAxis;
          e && (a.inverted && e.attr({
            width: a.plotWidth,
            height: a.plotHeight
          }), e.animate({
            translateX: l(f && f.left, a.plotLeft),
            translateY: l(d && d.top, a.plotTop)
          }));
          this.translate();
          this.render();
          c && delete this.kdTree;
        },
        kdAxisArray: ["clientX", "plotY"],
        searchPoint: function searchPoint(a, c) {
          var b = this.xAxis,
              e = this.yAxis,
              f = this.chart.inverted;
          return this.searchKDTree({
            clientX: f ? b.len - a.chartY + b.pos : a.chartX - b.pos,
            plotY: f ? e.len - a.chartX + e.pos : a.chartY - e.pos
          }, c, a);
        },
        buildKDTree: function buildKDTree(a) {
          function b(a, e, f) {
            var d;

            if (d = a && a.length) {
              var k = c.kdAxisArray[e % f];
              a.sort(function (a, b) {
                return a[k] - b[k];
              });
              d = Math.floor(d / 2);
              return {
                point: a[d],
                left: b(a.slice(0, d), e + 1, f),
                right: b(a.slice(d + 1), e + 1, f)
              };
            }
          }

          this.buildingKdTree = !0;
          var c = this,
              e = -1 < c.options.findNearestPointBy.indexOf("y") ? 2 : 1;
          delete c.kdTree;
          B(function () {
            c.kdTree = b(c.getValidPoints(null, !c.directTouch), e, e);
            c.buildingKdTree = !1;
          }, c.options.kdNow || a && "touchstart" === a.type ? 0 : 1);
        },
        searchKDTree: function searchKDTree(a, c, e) {
          function b(a, c, e, l) {
            var p = c.point,
                n = f.kdAxisArray[e % l],
                g = p;
            var m = y(a[d]) && y(p[d]) ? Math.pow(a[d] - p[d], 2) : null;
            var w = y(a[k]) && y(p[k]) ? Math.pow(a[k] - p[k], 2) : null;
            w = (m || 0) + (w || 0);
            p.dist = y(w) ? Math.sqrt(w) : Number.MAX_VALUE;
            p.distX = y(m) ? Math.sqrt(m) : Number.MAX_VALUE;
            n = a[n] - p[n];
            w = 0 > n ? "left" : "right";
            m = 0 > n ? "right" : "left";
            c[w] && (w = b(a, c[w], e + 1, l), g = w[h] < g[h] ? w : p);
            c[m] && Math.sqrt(n * n) < g[h] && (a = b(a, c[m], e + 1, l), g = a[h] < g[h] ? a : g);
            return g;
          }

          var f = this,
              d = this.kdAxisArray[0],
              k = this.kdAxisArray[1],
              h = c ? "distX" : "dist";
          c = -1 < f.options.findNearestPointBy.indexOf("y") ? 2 : 1;
          this.kdTree || this.buildingKdTree || this.buildKDTree(e);
          if (this.kdTree) return b(a, this.kdTree, c, c);
        },
        pointPlacementToXValue: function pointPlacementToXValue() {
          var a = this.options,
              c = a.pointRange,
              e = this.xAxis;
          a = a.pointPlacement;
          "between" === a && (a = e.reversed ? -.5 : .5);
          return k(a) ? a * l(c, e.pointRange) : 0;
        },
        isPointInside: function isPointInside(a) {
          return "undefined" !== typeof a.plotY && "undefined" !== typeof a.plotX && 0 <= a.plotY && a.plotY <= this.yAxis.len && 0 <= a.plotX && a.plotX <= this.xAxis.len;
        }
      });
    });
    S(r, "parts/Stacking.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.correctFloat,
          u = g.defined,
          I = g.destroyObjectProperties,
          M = g.format,
          E = g.objectEach,
          A = g.pick;
      g = d.Axis;
      var G = d.Chart,
          J = d.Series;

      d.StackItem = function (d, g, u, h, r) {
        var q = d.chart.inverted;
        this.axis = d;
        this.isNegative = u;
        this.options = g = g || {};
        this.x = h;
        this.total = null;
        this.points = {};
        this.stack = r;
        this.rightCliff = this.leftCliff = 0;
        this.alignOptions = {
          align: g.align || (q ? u ? "left" : "right" : "center"),
          verticalAlign: g.verticalAlign || (q ? "middle" : u ? "bottom" : "top"),
          y: g.y,
          x: g.x
        };
        this.textAlign = g.textAlign || (q ? u ? "right" : "left" : "center");
      };

      d.StackItem.prototype = {
        destroy: function destroy() {
          I(this, this.axis);
        },
        render: function render(d) {
          var g = this.axis.chart,
              y = this.options,
              h = y.format;
          h = h ? M(h, this, g) : y.formatter.call(this);
          this.label ? this.label.attr({
            text: h,
            visibility: "hidden"
          }) : (this.label = g.renderer.label(h, null, null, y.shape, null, null, y.useHTML, !1, "stack-labels"), h = {
            text: h,
            rotation: y.rotation,
            padding: A(y.padding, 5),
            visibility: "hidden"
          }, this.label.attr(h), g.styledMode || this.label.css(y.style), this.label.added || this.label.add(d));
          this.label.labelrank = g.plotHeight;
        },
        setOffset: function setOffset(d, g, r, h, N) {
          var q = this.axis,
              t = q.chart;
          h = q.translate(q.usePercentage ? 100 : h ? h : this.total, 0, 0, 0, 1);
          r = q.translate(r ? r : 0);
          r = u(h) && Math.abs(h - r);
          d = A(N, t.xAxis[0].translate(this.x)) + d;
          q = u(h) && this.getStackBox(t, this, d, h, g, r, q);
          g = this.label;
          r = this.isNegative;
          d = "justify" === A(this.options.overflow, "justify");
          var e = this.textAlign;
          g && q && (N = g.getBBox(), h = g.padding, e = "left" === e ? t.inverted ? -h : h : "right" === e ? N.width : t.inverted && "center" === e ? N.width / 2 : t.inverted ? r ? N.width + h : -h : N.width / 2, r = t.inverted ? N.height / 2 : r ? -h : N.height, this.alignOptions.x = A(this.options.x, 0), this.alignOptions.y = A(this.options.y, 0), q.x -= e, q.y -= r, g.align(this.alignOptions, null, q), t.isInsidePlot(g.alignAttr.x + e - this.alignOptions.x, g.alignAttr.y + r - this.alignOptions.y) ? g.show() : (g.alignAttr.y = -9999, d = !1), d && J.prototype.justifyDataLabel.call(this.axis, g, this.alignOptions, g.alignAttr, N, q), g.attr({
            x: g.alignAttr.x,
            y: g.alignAttr.y
          }), A(!d && this.options.crop, !0) && ((t = t.isInsidePlot(g.x - h + g.width, g.y) && t.isInsidePlot(g.x + h, g.y)) || g.hide()));
        },
        getStackBox: function getStackBox(d, g, r, h, u, q, A) {
          var e = g.axis.reversed,
              c = d.inverted;
          d = A.height + A.pos - (c ? d.plotLeft : d.plotTop);
          g = g.isNegative && !e || !g.isNegative && e;
          return {
            x: c ? g ? h : h - q : r,
            y: c ? d - r - u : g ? d - h - q : d - h,
            width: c ? q : u,
            height: c ? u : q
          };
        }
      };

      G.prototype.getStacks = function () {
        var d = this,
            g = d.inverted;
        d.yAxis.forEach(function (d) {
          d.stacks && d.hasVisibleSeries && (d.oldStacks = d.stacks);
        });
        d.series.forEach(function (t) {
          var h = t.xAxis && t.xAxis.options || {};
          !t.options.stacking || !0 !== t.visible && !1 !== d.options.chart.ignoreHiddenSeries || (t.stackKey = [t.type, A(t.options.stack, ""), g ? h.top : h.left, g ? h.height : h.width].join());
        });
      };

      g.prototype.buildStacks = function () {
        var g = this.series,
            t = A(this.options.reversedStacks, !0),
            r = g.length,
            h;

        if (!this.isXAxis) {
          this.usePercentage = !1;

          for (h = r; h--;) {
            var u = g[t ? h : r - h - 1];
            u.setStackedPoints();
          }

          for (h = 0; h < r; h++) {
            g[h].modifyStacks();
          }

          d.fireEvent(this, "afterBuildStacks");
        }
      };

      g.prototype.renderStackTotals = function () {
        var d = this.chart,
            g = d.renderer,
            r = this.stacks,
            h = this.stackTotalGroup;
        h || (this.stackTotalGroup = h = g.g("stack-labels").attr({
          visibility: "visible",
          zIndex: 6
        }).add());
        h.translate(d.plotLeft, d.plotTop);
        E(r, function (d) {
          E(d, function (d) {
            d.render(h);
          });
        });
      };

      g.prototype.resetStacks = function () {
        var d = this,
            g = d.stacks;
        d.isXAxis || E(g, function (g) {
          E(g, function (h, t) {
            h.touched < d.stacksTouched ? (h.destroy(), delete g[t]) : (h.total = null, h.cumulative = null);
          });
        });
      };

      g.prototype.cleanStacks = function () {
        if (!this.isXAxis) {
          if (this.oldStacks) var d = this.stacks = this.oldStacks;
          E(d, function (d) {
            E(d, function (d) {
              d.cumulative = d.total;
            });
          });
        }
      };

      J.prototype.setStackedPoints = function () {
        if (this.options.stacking && (!0 === this.visible || !1 === this.chart.options.chart.ignoreHiddenSeries)) {
          var g = this.processedXData,
              t = this.processedYData,
              D = [],
              h = t.length,
              N = this.options,
              q = N.threshold,
              G = A(N.startFromThreshold && q, 0),
              e = N.stack;
          N = N.stacking;
          var c = this.stackKey,
              k = "-" + c,
              n = this.negStacks,
              f = this.yAxis,
              a = f.stacks,
              l = f.oldStacks,
              v,
              z;
          f.stacksTouched += 1;

          for (z = 0; z < h; z++) {
            var w = g[z];
            var B = t[z];
            var L = this.getStackIndicator(L, w, this.index);
            var Q = L.key;
            var H = (v = n && B < (G ? 0 : q)) ? k : c;
            a[H] || (a[H] = {});
            a[H][w] || (l[H] && l[H][w] ? (a[H][w] = l[H][w], a[H][w].total = null) : a[H][w] = new d.StackItem(f, f.options.stackLabels, v, w, e));
            H = a[H][w];
            null !== B ? (H.points[Q] = H.points[this.index] = [A(H.cumulative, G)], u(H.cumulative) || (H.base = Q), H.touched = f.stacksTouched, 0 < L.index && !1 === this.singleStacks && (H.points[Q][0] = H.points[this.index + "," + w + ",0"][0])) : H.points[Q] = H.points[this.index] = null;
            "percent" === N ? (v = v ? c : k, n && a[v] && a[v][w] ? (v = a[v][w], H.total = v.total = Math.max(v.total, H.total) + Math.abs(B) || 0) : H.total = r(H.total + (Math.abs(B) || 0))) : H.total = r(H.total + (B || 0));
            H.cumulative = A(H.cumulative, G) + (B || 0);
            null !== B && (H.points[Q].push(H.cumulative), D[z] = H.cumulative);
          }

          "percent" === N && (f.usePercentage = !0);
          this.stackedYData = D;
          f.oldStacks = {};
        }
      };

      J.prototype.modifyStacks = function () {
        var d = this,
            g = d.stackKey,
            r = d.yAxis.stacks,
            h = d.processedXData,
            u,
            q = d.options.stacking;
        d[q + "Stacker"] && [g, "-" + g].forEach(function (g) {
          for (var e = h.length, c, k; e--;) {
            if (c = h[e], u = d.getStackIndicator(u, c, d.index, g), k = (c = r[g] && r[g][c]) && c.points[u.key]) d[q + "Stacker"](k, c, e);
          }
        });
      };

      J.prototype.percentStacker = function (d, g, u) {
        g = g.total ? 100 / g.total : 0;
        d[0] = r(d[0] * g);
        d[1] = r(d[1] * g);
        this.stackedYData[u] = d[1];
      };

      J.prototype.getStackIndicator = function (d, g, r, h) {
        !u(d) || d.x !== g || h && d.key !== h ? d = {
          x: g,
          index: 0,
          key: h
        } : d.index++;
        d.key = [r, g, d.index].join();
        return d;
      };
    });
    S(r, "parts/Dynamics.js", [r["parts/Globals.js"], r["parts/Point.js"], r["parts/Time.js"], r["parts/Utilities.js"]], function (d, g, r, u) {
      var I = u.addEvent,
          M = u.animate,
          E = u.createElement,
          A = u.css,
          G = u.defined,
          J = u.erase,
          y = u.error,
          t = u.extend,
          D = u.fireEvent,
          h = u.isArray,
          N = u.isNumber,
          q = u.isObject,
          P = u.isString,
          e = u.merge,
          c = u.objectEach,
          k = u.pick,
          n = u.relativeLength,
          f = u.setAnimation,
          a = u.splat,
          l = d.Axis;
      u = d.Chart;
      var v = d.Series,
          z = d.seriesTypes;

      d.cleanRecursively = function (a, e) {
        var f = {};
        c(a, function (c, k) {
          if (q(a[k], !0) && !a.nodeType && e[k]) c = d.cleanRecursively(a[k], e[k]), Object.keys(c).length && (f[k] = c);else if (q(a[k]) || a[k] !== e[k]) f[k] = a[k];
        });
        return f;
      };

      t(u.prototype, {
        addSeries: function addSeries(a, c, e) {
          var f,
              d = this;
          a && (c = k(c, !0), D(d, "addSeries", {
            options: a
          }, function () {
            f = d.initSeries(a);
            d.isDirtyLegend = !0;
            d.linkSeries();
            f.enabledDataSorting && f.setData(a.data, !1);
            D(d, "afterAddSeries", {
              series: f
            });
            c && d.redraw(e);
          }));
          return f;
        },
        addAxis: function addAxis(a, c, e, f) {
          return this.createAxis(c ? "xAxis" : "yAxis", {
            axis: a,
            redraw: e,
            animation: f
          });
        },
        addColorAxis: function addColorAxis(a, c, e) {
          return this.createAxis("colorAxis", {
            axis: a,
            redraw: c,
            animation: e
          });
        },
        createAxis: function createAxis(c, f) {
          var h = this.options,
              g = "colorAxis" === c,
              n = f.redraw,
              w = f.animation;
          f = e(f.axis, {
            index: this[c].length,
            isX: "xAxis" === c
          });
          var p = g ? new d.ColorAxis(this, f) : new l(this, f);
          h[c] = a(h[c] || {});
          h[c].push(f);
          g && (this.isDirtyLegend = !0, this.axes.forEach(function (a) {
            a.series = [];
          }), this.series.forEach(function (a) {
            a.bindAxes();
            a.isDirtyData = !0;
          }));
          k(n, !0) && this.redraw(w);
          return p;
        },
        showLoading: function showLoading(a) {
          var c = this,
              e = c.options,
              f = c.loadingDiv,
              d = e.loading,
              h = function h() {
            f && A(f, {
              left: c.plotLeft + "px",
              top: c.plotTop + "px",
              width: c.plotWidth + "px",
              height: c.plotHeight + "px"
            });
          };

          f || (c.loadingDiv = f = E("div", {
            className: "highcharts-loading highcharts-loading-hidden"
          }, null, c.container), c.loadingSpan = E("span", {
            className: "highcharts-loading-inner"
          }, null, f), I(c, "redraw", h));
          f.className = "highcharts-loading";
          c.loadingSpan.innerHTML = k(a, e.lang.loading, "");
          c.styledMode || (A(f, t(d.style, {
            zIndex: 10
          })), A(c.loadingSpan, d.labelStyle), c.loadingShown || (A(f, {
            opacity: 0,
            display: ""
          }), M(f, {
            opacity: d.style.opacity || .5
          }, {
            duration: d.showDuration || 0
          })));
          c.loadingShown = !0;
          h();
        },
        hideLoading: function hideLoading() {
          var a = this.options,
              c = this.loadingDiv;
          c && (c.className = "highcharts-loading highcharts-loading-hidden", this.styledMode || M(c, {
            opacity: 0
          }, {
            duration: a.loading.hideDuration || 100,
            complete: function complete() {
              A(c, {
                display: "none"
              });
            }
          }));
          this.loadingShown = !1;
        },
        propsRequireDirtyBox: "backgroundColor borderColor borderWidth borderRadius plotBackgroundColor plotBackgroundImage plotBorderColor plotBorderWidth plotShadow shadow".split(" "),
        propsRequireReflow: "margin marginTop marginRight marginBottom marginLeft spacing spacingTop spacingRight spacingBottom spacingLeft".split(" "),
        propsRequireUpdateSeries: "chart.inverted chart.polar chart.ignoreHiddenSeries chart.type colors plotOptions time tooltip".split(" "),
        collectionsWithUpdate: ["xAxis", "yAxis", "zAxis", "series"],
        update: function update(f, h, l, g) {
          var v = this,
              w = {
            credits: "addCredits",
            title: "setTitle",
            subtitle: "setSubtitle",
            caption: "setCaption"
          },
              p,
              b,
              q,
              t = f.isResponsiveOptions,
              z = [];
          D(v, "update", {
            options: f
          });
          t || v.setResponsive(!1, !0);
          f = d.cleanRecursively(f, v.options);
          e(!0, v.userOptions, f);

          if (p = f.chart) {
            e(!0, v.options.chart, p);
            "className" in p && v.setClassName(p.className);
            "reflow" in p && v.setReflow(p.reflow);

            if ("inverted" in p || "polar" in p || "type" in p) {
              v.propFromSeries();
              var B = !0;
            }

            "alignTicks" in p && (B = !0);
            c(p, function (a, c) {
              -1 !== v.propsRequireUpdateSeries.indexOf("chart." + c) && (b = !0);
              -1 !== v.propsRequireDirtyBox.indexOf(c) && (v.isDirtyBox = !0);
              t || -1 === v.propsRequireReflow.indexOf(c) || (q = !0);
            });
            !v.styledMode && "style" in p && v.renderer.setStyle(p.style);
          }

          !v.styledMode && f.colors && (this.options.colors = f.colors);
          f.plotOptions && e(!0, this.options.plotOptions, f.plotOptions);
          f.time && this.time === d.time && (this.time = new r(f.time));
          c(f, function (a, c) {
            if (v[c] && "function" === typeof v[c].update) v[c].update(a, !1);else if ("function" === typeof v[w[c]]) v[w[c]](a);
            "chart" !== c && -1 !== v.propsRequireUpdateSeries.indexOf(c) && (b = !0);
          });
          this.collectionsWithUpdate.forEach(function (b) {
            if (f[b]) {
              if ("series" === b) {
                var c = [];
                v[b].forEach(function (a, b) {
                  a.options.isInternal || c.push(k(a.options.index, b));
                });
              }

              a(f[b]).forEach(function (a, e) {
                (e = G(a.id) && v.get(a.id) || v[b][c ? c[e] : e]) && e.coll === b && (e.update(a, !1), l && (e.touched = !0));
                !e && l && v.collectionsWithInit[b] && (v.collectionsWithInit[b][0].apply(v, [a].concat(v.collectionsWithInit[b][1] || []).concat([!1])).touched = !0);
              });
              l && v[b].forEach(function (a) {
                a.touched || a.options.isInternal ? delete a.touched : z.push(a);
              });
            }
          });
          z.forEach(function (a) {
            a.remove && a.remove(!1);
          });
          B && v.axes.forEach(function (a) {
            a.update({}, !1);
          });
          b && v.getSeriesOrderByLinks().forEach(function (a) {
            a.chart && a.update({}, !1);
          }, this);
          f.loading && e(!0, v.options.loading, f.loading);
          B = p && p.width;
          p = p && p.height;
          P(p) && (p = n(p, B || v.chartWidth));
          q || N(B) && B !== v.chartWidth || N(p) && p !== v.chartHeight ? v.setSize(B, p, g) : k(h, !0) && v.redraw(g);
          D(v, "afterUpdate", {
            options: f,
            redraw: h,
            animation: g
          });
        },
        setSubtitle: function setSubtitle(a, c) {
          this.applyDescription("subtitle", a);
          this.layOutTitles(c);
        },
        setCaption: function setCaption(a, c) {
          this.applyDescription("caption", a);
          this.layOutTitles(c);
        }
      });
      u.prototype.collectionsWithInit = {
        xAxis: [u.prototype.addAxis, [!0]],
        yAxis: [u.prototype.addAxis, [!1]],
        series: [u.prototype.addSeries]
      };
      t(g.prototype, {
        update: function update(a, c, e, f) {
          function d() {
            h.applyOptions(a);
            var f = b && h.hasDummyGraphic;
            f = null === h.y ? !f : f;
            b && f && (h.graphic = b.destroy(), delete h.hasDummyGraphic);
            q(a, !0) && (b && b.element && a && a.marker && "undefined" !== typeof a.marker.symbol && (h.graphic = b.destroy()), a && a.dataLabels && h.dataLabel && (h.dataLabel = h.dataLabel.destroy()), h.connector && (h.connector = h.connector.destroy()));
            g = h.index;
            l.updateParallelArrays(h, g);
            v.data[g] = q(v.data[g], !0) || q(a, !0) ? h.options : k(a, v.data[g]);
            l.isDirty = l.isDirtyData = !0;
            !l.fixedBox && l.hasCartesianSeries && (n.isDirtyBox = !0);
            "point" === v.legendType && (n.isDirtyLegend = !0);
            c && n.redraw(e);
          }

          var h = this,
              l = h.series,
              b = h.graphic,
              g,
              n = l.chart,
              v = l.options;
          c = k(c, !0);
          !1 === f ? d() : h.firePointEvent("update", {
            options: a
          }, d);
        },
        remove: function remove(a, c) {
          this.series.removePoint(this.series.data.indexOf(this), a, c);
        }
      });
      t(v.prototype, {
        addPoint: function addPoint(a, c, e, f, d) {
          var h = this.options,
              l = this.data,
              b = this.chart,
              g = this.xAxis;
          g = g && g.hasNames && g.names;
          var n = h.data,
              v = this.xData,
              q;
          c = k(c, !0);
          var w = {
            series: this
          };
          this.pointClass.prototype.applyOptions.apply(w, [a]);
          var t = w.x;
          var m = v.length;
          if (this.requireSorting && t < v[m - 1]) for (q = !0; m && v[m - 1] > t;) {
            m--;
          }
          this.updateParallelArrays(w, "splice", m, 0, 0);
          this.updateParallelArrays(w, m);
          g && w.name && (g[t] = w.name);
          n.splice(m, 0, a);
          q && (this.data.splice(m, 0, null), this.processData());
          "point" === h.legendType && this.generatePoints();
          e && (l[0] && l[0].remove ? l[0].remove(!1) : (l.shift(), this.updateParallelArrays(w, "shift"), n.shift()));
          !1 !== d && D(this, "addPoint", {
            point: w
          });
          this.isDirtyData = this.isDirty = !0;
          c && b.redraw(f);
        },
        removePoint: function removePoint(a, c, e) {
          var d = this,
              h = d.data,
              l = h[a],
              g = d.points,
              b = d.chart,
              n = function n() {
            g && g.length === h.length && g.splice(a, 1);
            h.splice(a, 1);
            d.options.data.splice(a, 1);
            d.updateParallelArrays(l || {
              series: d
            }, "splice", a, 1);
            l && l.destroy();
            d.isDirty = !0;
            d.isDirtyData = !0;
            c && b.redraw();
          };

          f(e, b);
          c = k(c, !0);
          l ? l.firePointEvent("remove", null, n) : n();
        },
        remove: function remove(a, c, e, f) {
          function d() {
            h.destroy(f);
            h.remove = null;
            l.isDirtyLegend = l.isDirtyBox = !0;
            l.linkSeries();
            k(a, !0) && l.redraw(c);
          }

          var h = this,
              l = h.chart;
          !1 !== e ? D(h, "remove", null, d) : d();
        },
        update: function update(a, c) {
          a = d.cleanRecursively(a, this.userOptions);
          D(this, "update", {
            options: a
          });
          var f = this,
              h = f.chart,
              l = f.userOptions,
              g = f.initialType || f.type,
              n = a.type || l.type || h.options.chart.type,
              b = !(this.hasDerivedData || a.dataGrouping || n && n !== this.type || "undefined" !== typeof a.pointStart || a.pointInterval || a.pointIntervalUnit || a.keys),
              v = z[g].prototype,
              q,
              w = ["group", "markerGroup", "dataLabelsGroup", "transformGroup"],
              r = ["eventOptions", "navigatorSeries", "baseSeries"],
              B = f.finishedAnimating && {
            animation: !1
          },
              u = {};
          b && (r.push("data", "isDirtyData", "points", "processedXData", "processedYData", "xIncrement", "_hasPointMarkers", "_hasPointLabels", "mapMap", "mapData", "minY", "maxY", "minX", "maxX"), !1 !== a.visible && r.push("area", "graph"), f.parallelArrays.forEach(function (a) {
            r.push(a + "Data");
          }), a.data && (a.dataSorting && t(f.options.dataSorting, a.dataSorting), this.setData(a.data, !1)));
          a = e(l, B, {
            index: "undefined" === typeof l.index ? f.index : l.index,
            pointStart: k(l.pointStart, f.xData[0])
          }, !b && {
            data: f.options.data
          }, a);
          b && a.data && (a.data = f.options.data);
          r = w.concat(r);
          r.forEach(function (a) {
            r[a] = f[a];
            delete f[a];
          });
          f.remove(!1, null, !1, !0);

          for (q in v) {
            f[q] = void 0;
          }

          z[n || g] ? t(f, z[n || g].prototype) : y(17, !0, h, {
            missingModuleFor: n || g
          });
          r.forEach(function (a) {
            f[a] = r[a];
          });
          f.init(h, a);

          if (b && this.points) {
            var m = f.options;
            !1 === m.visible ? (u.graphic = 1, u.dataLabel = 1) : f._hasPointLabels || (n = m.marker, v = m.dataLabels, n && (!1 === n.enabled || "symbol" in n) && (u.graphic = 1), v && !1 === v.enabled && (u.dataLabel = 1));
            this.points.forEach(function (a) {
              a && a.series && (a.resolveColor(), Object.keys(u).length && a.destroyElements(u), !1 === m.showInLegend && a.legendItem && h.legend.destroyItem(a));
            }, this);
          }

          a.zIndex !== l.zIndex && w.forEach(function (b) {
            f[b] && f[b].attr({
              zIndex: a.zIndex
            });
          });
          f.initialType = g;
          h.linkSeries();
          D(this, "afterUpdate");
          k(c, !0) && h.redraw(b ? void 0 : !1);
        },
        setName: function setName(a) {
          this.name = this.options.name = this.userOptions.name = a;
          this.chart.isDirtyLegend = !0;
        }
      });
      t(l.prototype, {
        update: function update(a, f) {
          var d = this.chart,
              h = a && a.events || {};
          a = e(this.userOptions, a);
          d.options[this.coll].indexOf && (d.options[this.coll][d.options[this.coll].indexOf(this.userOptions)] = a);
          c(d.options[this.coll].events, function (a, c) {
            "undefined" === typeof h[c] && (h[c] = void 0);
          });
          this.destroy(!0);
          this.init(d, t(a, {
            events: h
          }));
          d.isDirtyBox = !0;
          k(f, !0) && d.redraw();
        },
        remove: function remove(a) {
          for (var c = this.chart, e = this.coll, f = this.series, d = f.length; d--;) {
            f[d] && f[d].remove(!1);
          }

          J(c.axes, this);
          J(c[e], this);
          h(c.options[e]) ? c.options[e].splice(this.options.index, 1) : delete c.options[e];
          c[e].forEach(function (a, c) {
            a.options.index = a.userOptions.index = c;
          });
          this.destroy();
          c.isDirtyBox = !0;
          k(a, !0) && c.redraw();
        },
        setTitle: function setTitle(a, c) {
          this.update({
            title: a
          }, c);
        },
        setCategories: function setCategories(a, c) {
          this.update({
            categories: a
          }, c);
        }
      });
    });
    S(r, "parts/AreaSeries.js", [r["parts/Globals.js"], r["parts/Color.js"], r["mixins/legend-symbol.js"], r["parts/Utilities.js"]], function (d, g, r, u) {
      var I = g.parse,
          M = u.objectEach,
          E = u.pick;
      g = u.seriesType;
      var A = d.Series;
      g("area", "line", {
        softThreshold: !1,
        threshold: 0
      }, {
        singleStacks: !1,
        getStackPoints: function getStackPoints(d) {
          var g = [],
              r = [],
              t = this.xAxis,
              u = this.yAxis,
              h = u.stacks[this.stackKey],
              A = {},
              q = this.index,
              G = u.series,
              e = G.length,
              c = E(u.options.reversedStacks, !0) ? 1 : -1,
              k;
          d = d || this.points;

          if (this.options.stacking) {
            for (k = 0; k < d.length; k++) {
              d[k].leftNull = d[k].rightNull = void 0, A[d[k].x] = d[k];
            }

            M(h, function (c, a) {
              null !== c.total && r.push(a);
            });
            r.sort(function (c, a) {
              return c - a;
            });
            var n = G.map(function (c) {
              return c.visible;
            });
            r.forEach(function (f, a) {
              var d = 0,
                  v,
                  z;
              if (A[f] && !A[f].isNull) g.push(A[f]), [-1, 1].forEach(function (d) {
                var l = 1 === d ? "rightNull" : "leftNull",
                    g = 0,
                    w = h[r[a + d]];
                if (w) for (k = q; 0 <= k && k < e;) {
                  v = w.points[k], v || (k === q ? A[f][l] = !0 : n[k] && (z = h[f].points[k]) && (g -= z[1] - z[0])), k += c;
                }
                A[f][1 === d ? "rightCliff" : "leftCliff"] = g;
              });else {
                for (k = q; 0 <= k && k < e;) {
                  if (v = h[f].points[k]) {
                    d = v[1];
                    break;
                  }

                  k += c;
                }

                d = u.translate(d, 0, 1, 0, 1);
                g.push({
                  isNull: !0,
                  plotX: t.translate(f, 0, 0, 0, 1),
                  x: f,
                  plotY: d,
                  yBottom: d
                });
              }
            });
          }

          return g;
        },
        getGraphPath: function getGraphPath(d) {
          var g = A.prototype.getGraphPath,
              r = this.options,
              t = r.stacking,
              u = this.yAxis,
              h,
              N = [],
              q = [],
              G = this.index,
              e = u.stacks[this.stackKey],
              c = r.threshold,
              k = Math.round(u.getThreshold(r.threshold));
          r = E(r.connectNulls, "percent" === t);

          var n = function n(f, h, l) {
            var g = d[f];
            f = t && e[g.x].points[G];
            var n = g[l + "Null"] || 0;
            l = g[l + "Cliff"] || 0;
            g = !0;

            if (l || n) {
              var v = (n ? f[0] : f[1]) + l;
              var w = f[0] + l;
              g = !!n;
            } else !t && d[h] && d[h].isNull && (v = w = c);

            "undefined" !== typeof v && (q.push({
              plotX: a,
              plotY: null === v ? k : u.getThreshold(v),
              isNull: g,
              isCliff: !0
            }), N.push({
              plotX: a,
              plotY: null === w ? k : u.getThreshold(w),
              doCurve: !1
            }));
          };

          d = d || this.points;
          t && (d = this.getStackPoints(d));

          for (h = 0; h < d.length; h++) {
            t || (d[h].leftCliff = d[h].rightCliff = d[h].leftNull = d[h].rightNull = void 0);
            var f = d[h].isNull;
            var a = E(d[h].rectPlotX, d[h].plotX);
            var l = E(d[h].yBottom, k);
            if (!f || r) r || n(h, h - 1, "left"), f && !t && r || (q.push(d[h]), N.push({
              x: h,
              plotX: a,
              plotY: l
            })), r || n(h, h + 1, "right");
          }

          h = g.call(this, q, !0, !0);
          N.reversed = !0;
          f = g.call(this, N, !0, !0);
          f.length && (f[0] = "L");
          f = h.concat(f);
          g = g.call(this, q, !1, r);
          f.xMap = h.xMap;
          this.areaPath = f;
          return g;
        },
        drawGraph: function drawGraph() {
          this.areaPath = [];
          A.prototype.drawGraph.apply(this);
          var d = this,
              g = this.areaPath,
              r = this.options,
              t = [["area", "highcharts-area", this.color, r.fillColor]];
          this.zones.forEach(function (g, h) {
            t.push(["zone-area-" + h, "highcharts-area highcharts-zone-area-" + h + " " + g.className, g.color || d.color, g.fillColor || r.fillColor]);
          });
          t.forEach(function (t) {
            var h = t[0],
                u = d[h],
                q = u ? "animate" : "attr",
                y = {};
            u ? (u.endX = d.preventGraphAnimation ? null : g.xMap, u.animate({
              d: g
            })) : (y.zIndex = 0, u = d[h] = d.chart.renderer.path(g).addClass(t[1]).add(d.group), u.isArea = !0);
            d.chart.styledMode || (y.fill = E(t[3], I(t[2]).setOpacity(E(r.fillOpacity, .75)).get()));
            u[q](y);
            u.startX = g.xMap;
            u.shiftUnit = r.step ? 2 : 1;
          });
        },
        drawLegendSymbol: r.drawRectangle
      });
    });
    S(r, "parts/SplineSeries.js", [r["parts/Utilities.js"]], function (d) {
      var g = d.pick;
      d = d.seriesType;
      d("spline", "line", {}, {
        getPointSpline: function getPointSpline(d, r, I) {
          var u = r.plotX,
              E = r.plotY,
              A = d[I - 1];
          I = d[I + 1];

          if (A && !A.isNull && !1 !== A.doCurve && !r.isCliff && I && !I.isNull && !1 !== I.doCurve && !r.isCliff) {
            d = A.plotY;
            var G = I.plotX;
            I = I.plotY;
            var J = 0;
            var y = (1.5 * u + A.plotX) / 2.5;
            var t = (1.5 * E + d) / 2.5;
            G = (1.5 * u + G) / 2.5;
            var D = (1.5 * E + I) / 2.5;
            G !== y && (J = (D - t) * (G - u) / (G - y) + E - D);
            t += J;
            D += J;
            t > d && t > E ? (t = Math.max(d, E), D = 2 * E - t) : t < d && t < E && (t = Math.min(d, E), D = 2 * E - t);
            D > I && D > E ? (D = Math.max(I, E), t = 2 * E - D) : D < I && D < E && (D = Math.min(I, E), t = 2 * E - D);
            r.rightContX = G;
            r.rightContY = D;
          }

          r = ["C", g(A.rightContX, A.plotX), g(A.rightContY, A.plotY), g(y, u), g(t, E), u, E];
          A.rightContX = A.rightContY = null;
          return r;
        }
      });
    });
    S(r, "parts/AreaSplineSeries.js", [r["parts/Globals.js"], r["mixins/legend-symbol.js"], r["parts/Utilities.js"]], function (d, g, r) {
      r = r.seriesType;
      var u = d.seriesTypes.area.prototype;
      r("areaspline", "spline", d.defaultPlotOptions.area, {
        getStackPoints: u.getStackPoints,
        getGraphPath: u.getGraphPath,
        drawGraph: u.drawGraph,
        drawLegendSymbol: g.drawRectangle
      });
    });
    S(r, "parts/ColumnSeries.js", [r["parts/Globals.js"], r["parts/Color.js"], r["mixins/legend-symbol.js"], r["parts/Utilities.js"]], function (d, g, r, u) {

      var I = g.parse,
          M = u.animObject,
          E = u.clamp,
          A = u.defined,
          G = u.extend,
          J = u.isNumber,
          y = u.merge,
          t = u.pick;
      g = u.seriesType;
      var D = d.Series;
      g("column", "line", {
        borderRadius: 0,
        crisp: !0,
        groupPadding: .2,
        marker: null,
        pointPadding: .1,
        minPointLength: 0,
        cropThreshold: 50,
        pointRange: null,
        states: {
          hover: {
            halo: !1,
            brightness: .1
          },
          select: {
            color: "#cccccc",
            borderColor: "#000000"
          }
        },
        dataLabels: {
          align: null,
          verticalAlign: null,
          y: null
        },
        softThreshold: !1,
        startFromThreshold: !0,
        stickyTracking: !1,
        tooltip: {
          distance: 6
        },
        threshold: 0,
        borderColor: "#ffffff"
      }, {
        cropShoulder: 0,
        directTouch: !0,
        trackerGroups: ["group", "dataLabelsGroup"],
        negStacks: !0,
        init: function init() {
          D.prototype.init.apply(this, arguments);
          var d = this,
              g = d.chart;
          g.hasRendered && g.series.forEach(function (h) {
            h.type === d.type && (h.isDirty = !0);
          });
        },
        getColumnMetrics: function getColumnMetrics() {
          var d = this,
              g = d.options,
              q = d.xAxis,
              r = d.yAxis,
              e = q.options.reversedStacks;
          e = q.reversed && !e || !q.reversed && e;
          var c,
              k = {},
              n = 0;
          !1 === g.grouping ? n = 1 : d.chart.series.forEach(function (a) {
            var e = a.yAxis,
                f = a.options;

            if (a.type === d.type && (a.visible || !d.chart.options.chart.ignoreHiddenSeries) && r.len === e.len && r.pos === e.pos) {
              if (f.stacking) {
                c = a.stackKey;
                "undefined" === typeof k[c] && (k[c] = n++);
                var h = k[c];
              } else !1 !== f.grouping && (h = n++);

              a.columnIndex = h;
            }
          });
          var f = Math.min(Math.abs(q.transA) * (q.ordinalSlope || g.pointRange || q.closestPointRange || q.tickInterval || 1), q.len),
              a = f * g.groupPadding,
              l = (f - 2 * a) / (n || 1);
          g = Math.min(g.maxPointWidth || q.len, t(g.pointWidth, l * (1 - 2 * g.pointPadding)));
          d.columnMetrics = {
            width: g,
            offset: (l - g) / 2 + (a + ((d.columnIndex || 0) + (e ? 1 : 0)) * l - f / 2) * (e ? -1 : 1)
          };
          return d.columnMetrics;
        },
        crispCol: function crispCol(d, g, q, t) {
          var e = this.chart,
              c = this.borderWidth,
              k = -(c % 2 ? .5 : 0);
          c = c % 2 ? .5 : 1;
          e.inverted && e.renderer.isVML && (c += 1);
          this.options.crisp && (q = Math.round(d + q) + k, d = Math.round(d) + k, q -= d);
          t = Math.round(g + t) + c;
          k = .5 >= Math.abs(g) && .5 < t;
          g = Math.round(g) + c;
          t -= g;
          k && t && (--g, t += 1);
          return {
            x: d,
            y: g,
            width: q,
            height: t
          };
        },
        translate: function translate() {
          var d = this,
              g = d.chart,
              q = d.options,
              r = d.dense = 2 > d.closestPointRange * d.xAxis.transA;
          r = d.borderWidth = t(q.borderWidth, r ? 0 : 1);
          var e = d.xAxis,
              c = d.yAxis,
              k = q.threshold,
              n = d.translatedThreshold = c.getThreshold(k),
              f = t(q.minPointLength, 5),
              a = d.getColumnMetrics(),
              l = a.width,
              v = d.barW = Math.max(l, 1 + 2 * r),
              z = d.pointXOffset = a.offset,
              w = d.dataMin,
              u = d.dataMax;
          g.inverted && (n -= .5);
          q.pointPadding && (v = Math.ceil(v));
          D.prototype.translate.apply(d);
          d.points.forEach(function (a) {
            var h = t(a.yBottom, n),
                q = 999 + Math.abs(h),
                r = l,
                p = a.plotX;
            q = E(a.plotY, -q, c.len + q);
            var b = a.plotX + z,
                B = v,
                x = Math.min(q, h),
                y = Math.max(q, h) - x;

            if (f && Math.abs(y) < f) {
              y = f;
              var L = !c.reversed && !a.negative || c.reversed && a.negative;
              a.y === k && d.dataMax <= k && c.min < k && w !== u && (L = !L);
              x = Math.abs(x - n) > f ? h - f : n - (L ? f : 0);
            }

            A(a.options.pointWidth) && (r = B = Math.ceil(a.options.pointWidth), b -= Math.round((r - l) / 2));
            a.barX = b;
            a.pointWidth = r;
            a.tooltipPos = g.inverted ? [c.len + c.pos - g.plotLeft - q, e.len + e.pos - g.plotTop - (p || 0) - z - B / 2, y] : [b + B / 2, q + c.pos - g.plotTop, y];
            a.shapeType = d.pointClass.prototype.shapeType || "rect";
            a.shapeArgs = d.crispCol.apply(d, a.isNull ? [b, n, B, 0] : [b, x, B, y]);
          });
        },
        getSymbol: d.noop,
        drawLegendSymbol: r.drawRectangle,
        drawGraph: function drawGraph() {
          this.group[this.dense ? "addClass" : "removeClass"]("highcharts-dense-data");
        },
        pointAttribs: function pointAttribs(d, g) {
          var h = this.options,
              r = this.pointAttrToOptions || {};
          var e = r.stroke || "borderColor";
          var c = r["stroke-width"] || "borderWidth",
              k = d && d.color || this.color,
              n = d && d[e] || h[e] || this.color || k,
              f = d && d[c] || h[c] || this[c] || 0;
          r = d && d.options.dashStyle || h.dashStyle;
          var a = t(d && d.opacity, h.opacity, 1);

          if (d && this.zones.length) {
            var l = d.getZone();
            k = d.options.color || l && (l.color || d.nonZonedColor) || this.color;
            l && (n = l.borderColor || n, r = l.dashStyle || r, f = l.borderWidth || f);
          }

          g && d && (d = y(h.states[g], d.options.states && d.options.states[g] || {}), g = d.brightness, k = d.color || "undefined" !== typeof g && I(k).brighten(d.brightness).get() || k, n = d[e] || n, f = d[c] || f, r = d.dashStyle || r, a = t(d.opacity, a));
          e = {
            fill: k,
            stroke: n,
            "stroke-width": f,
            opacity: a
          };
          r && (e.dashstyle = r);
          return e;
        },
        drawPoints: function drawPoints() {
          var d = this,
              g = this.chart,
              q = d.options,
              t = g.renderer,
              e = q.animationLimit || 250,
              c;
          d.points.forEach(function (k) {
            var h = k.graphic,
                f = !!h,
                a = h && g.pointCount < e ? "animate" : "attr";

            if (J(k.plotY) && null !== k.y) {
              c = k.shapeArgs;
              h && k.hasNewShapeType() && (h = h.destroy());
              d.enabledDataSorting && (k.startXPos = d.xAxis.reversed ? -(c ? c.width : 0) : d.xAxis.width);
              h || (k.graphic = h = t[k.shapeType](c).add(k.group || d.group)) && d.enabledDataSorting && g.hasRendered && g.pointCount < e && (h.attr({
                x: k.startXPos
              }), f = !0, a = "animate");
              if (h && f) h[a](y(c));
              if (q.borderRadius) h[a]({
                r: q.borderRadius
              });
              g.styledMode || h[a](d.pointAttribs(k, k.selected && "select")).shadow(!1 !== k.allowShadow && q.shadow, null, q.stacking && !q.borderRadius);
              h.addClass(k.getClassName(), !0);
            } else h && (k.graphic = h.destroy());
          });
        },
        animate: function animate(d) {
          var h = this,
              g = this.yAxis,
              t = h.options,
              e = this.chart.inverted,
              c = {},
              k = e ? "translateX" : "translateY";
          if (d) c.scaleY = .001, d = E(g.toPixels(t.threshold), g.pos, g.pos + g.len), e ? c.translateX = d - g.len : c.translateY = d, h.clipBox && h.setClip(), h.group.attr(c);else {
            var n = h.group.attr(k);
            h.group.animate({
              scaleY: 1
            }, G(M(h.options.animation), {
              step: function step(e, a) {
                h.group && (c[k] = n + a.pos * (g.pos - n), h.group.attr(c));
              }
            }));
          }
        },
        remove: function remove() {
          var d = this,
              g = d.chart;
          g.hasRendered && g.series.forEach(function (h) {
            h.type === d.type && (h.isDirty = !0);
          });
          D.prototype.remove.apply(d, arguments);
        }
      });
    });
    S(r, "parts/BarSeries.js", [r["parts/Utilities.js"]], function (d) {
      d = d.seriesType;
      d("bar", "column", null, {
        inverted: !0
      });
    });
    S(r, "parts/ScatterSeries.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.addEvent;
      g = g.seriesType;
      var u = d.Series;
      g("scatter", "line", {
        lineWidth: 0,
        findNearestPointBy: "xy",
        jitter: {
          x: 0,
          y: 0
        },
        marker: {
          enabled: !0
        },
        tooltip: {
          headerFormat: "<span style=\"color:{point.color}\">\u25CF</span> <span style=\"font-size: 10px\"> {series.name}</span><br/>",
          pointFormat: "x: <b>{point.x}</b><br/>y: <b>{point.y}</b><br/>"
        }
      }, {
        sorted: !1,
        requireSorting: !1,
        noSharedTooltip: !0,
        trackerGroups: ["group", "markerGroup", "dataLabelsGroup"],
        takeOrdinalPosition: !1,
        drawGraph: function drawGraph() {
          this.options.lineWidth && u.prototype.drawGraph.call(this);
        },
        applyJitter: function applyJitter() {
          var d = this,
              g = this.options.jitter,
              r = this.points.length;
          g && this.points.forEach(function (u, E) {
            ["x", "y"].forEach(function (A, y) {
              var t = "plot" + A.toUpperCase();

              if (g[A] && !u.isNull) {
                var D = d[A + "Axis"];
                var h = g[A] * D.transA;

                if (D && !D.isLog) {
                  var G = Math.max(0, u[t] - h);
                  D = Math.min(D.len, u[t] + h);
                  y = 1E4 * Math.sin(E + y * r);
                  u[t] = G + (D - G) * (y - Math.floor(y));
                  "x" === A && (u.clientX = u.plotX);
                }
              }
            });
          });
        }
      });
      r(u, "afterTranslate", function () {
        this.applyJitter && this.applyJitter();
      });
    });
    S(r, "mixins/centered-series.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.isNumber,
          u = g.pick,
          I = g.relativeLength,
          M = d.deg2rad;
      d.CenteredSeriesMixin = {
        getCenter: function getCenter() {
          var d = this.options,
              g = this.chart,
              r = 2 * (d.slicedOffset || 0),
              J = g.plotWidth - 2 * r,
              y = g.plotHeight - 2 * r,
              t = d.center,
              D = Math.min(J, y),
              h = d.size,
              N = d.innerSize || 0;
          "string" === typeof h && (h = parseFloat(h));
          "string" === typeof N && (N = parseFloat(N));
          d = [u(t[0], "50%"), u(t[1], "50%"), u(h && 0 > h ? void 0 : d.size, "100%"), u(N && 0 > N ? void 0 : d.innerSize || 0, "0%")];
          g.angular && (d[3] = 0);

          for (t = 0; 4 > t; ++t) {
            h = d[t], g = 2 > t || 2 === t && /%$/.test(h), d[t] = I(h, [J, y, D, d[2]][t]) + (g ? r : 0);
          }

          d[3] > d[2] && (d[3] = d[2]);
          return d;
        },
        getStartAndEndRadians: function getStartAndEndRadians(d, g) {
          d = r(d) ? d : 0;
          g = r(g) && g > d && 360 > g - d ? g : d + 360;
          return {
            start: M * (d + -90),
            end: M * (g + -90)
          };
        }
      };
    });
    S(r, "parts/PieSeries.js", [r["parts/Globals.js"], r["mixins/legend-symbol.js"], r["parts/Point.js"], r["parts/Utilities.js"]], function (d, g, r, u) {
      var I = u.addEvent,
          M = u.clamp,
          E = u.defined,
          A = u.fireEvent,
          G = u.isNumber,
          J = u.merge,
          y = u.pick,
          t = u.relativeLength,
          D = u.seriesType,
          h = u.setAnimation;
      u = d.CenteredSeriesMixin;
      var N = u.getStartAndEndRadians,
          q = d.noop,
          P = d.Series;
      D("pie", "line", {
        center: [null, null],
        clip: !1,
        colorByPoint: !0,
        dataLabels: {
          allowOverlap: !0,
          connectorPadding: 5,
          connectorShape: "fixedOffset",
          crookDistance: "70%",
          distance: 30,
          enabled: !0,
          formatter: function formatter() {
            return this.point.isNull ? void 0 : this.point.name;
          },
          softConnector: !0,
          x: 0
        },
        fillColor: void 0,
        ignoreHiddenPoint: !0,
        inactiveOtherPoints: !0,
        legendType: "point",
        marker: null,
        size: null,
        showInLegend: !1,
        slicedOffset: 10,
        stickyTracking: !1,
        tooltip: {
          followPointer: !0
        },
        borderColor: "#ffffff",
        borderWidth: 1,
        lineWidth: void 0,
        states: {
          hover: {
            brightness: .1
          }
        }
      }, {
        isCartesian: !1,
        requireSorting: !1,
        directTouch: !0,
        noSharedTooltip: !0,
        trackerGroups: ["group", "dataLabelsGroup"],
        axisTypes: [],
        pointAttribs: d.seriesTypes.column.prototype.pointAttribs,
        animate: function animate(e) {
          var c = this,
              d = c.points,
              g = c.startAngleRad;
          e || d.forEach(function (e) {
            var a = e.graphic,
                f = e.shapeArgs;
            a && f && (a.attr({
              r: y(e.startR, c.center && c.center[3] / 2),
              start: g,
              end: g
            }), a.animate({
              r: f.r,
              start: f.start,
              end: f.end
            }, c.options.animation));
          });
        },
        hasData: function hasData() {
          return !!this.processedXData.length;
        },
        updateTotals: function updateTotals() {
          var e,
              c = 0,
              d = this.points,
              g = d.length,
              f = this.options.ignoreHiddenPoint;

          for (e = 0; e < g; e++) {
            var a = d[e];
            c += f && !a.visible ? 0 : a.isNull ? 0 : a.y;
          }

          this.total = c;

          for (e = 0; e < g; e++) {
            a = d[e], a.percentage = 0 < c && (a.visible || !f) ? a.y / c * 100 : 0, a.total = c;
          }
        },
        generatePoints: function generatePoints() {
          P.prototype.generatePoints.call(this);
          this.updateTotals();
        },
        getX: function getX(e, c, d) {
          var k = this.center,
              f = this.radii ? this.radii[d.index] : k[2] / 2;
          e = Math.asin(M((e - k[1]) / (f + d.labelDistance), -1, 1));
          return k[0] + (c ? -1 : 1) * Math.cos(e) * (f + d.labelDistance) + (0 < d.labelDistance ? (c ? -1 : 1) * this.options.dataLabels.padding : 0);
        },
        translate: function translate(e) {
          this.generatePoints();
          var c = 0,
              d = this.options,
              g = d.slicedOffset,
              f = g + (d.borderWidth || 0),
              a = N(d.startAngle, d.endAngle),
              h = this.startAngleRad = a.start;
          a = (this.endAngleRad = a.end) - h;
          var v = this.points,
              q = d.dataLabels.distance;
          d = d.ignoreHiddenPoint;
          var w,
              r = v.length;
          e || (this.center = e = this.getCenter());

          for (w = 0; w < r; w++) {
            var u = v[w];
            var D = h + c * a;
            if (!d || u.visible) c += u.percentage / 100;
            var H = h + c * a;
            u.shapeType = "arc";
            u.shapeArgs = {
              x: e[0],
              y: e[1],
              r: e[2] / 2,
              innerR: e[3] / 2,
              start: Math.round(1E3 * D) / 1E3,
              end: Math.round(1E3 * H) / 1E3
            };
            u.labelDistance = y(u.options.dataLabels && u.options.dataLabels.distance, q);
            u.labelDistance = t(u.labelDistance, u.shapeArgs.r);
            this.maxLabelDistance = Math.max(this.maxLabelDistance || 0, u.labelDistance);
            H = (H + D) / 2;
            H > 1.5 * Math.PI ? H -= 2 * Math.PI : H < -Math.PI / 2 && (H += 2 * Math.PI);
            u.slicedTranslation = {
              translateX: Math.round(Math.cos(H) * g),
              translateY: Math.round(Math.sin(H) * g)
            };
            var K = Math.cos(H) * e[2] / 2;
            var p = Math.sin(H) * e[2] / 2;
            u.tooltipPos = [e[0] + .7 * K, e[1] + .7 * p];
            u.half = H < -Math.PI / 2 || H > Math.PI / 2 ? 1 : 0;
            u.angle = H;
            D = Math.min(f, u.labelDistance / 5);
            u.labelPosition = {
              natural: {
                x: e[0] + K + Math.cos(H) * u.labelDistance,
                y: e[1] + p + Math.sin(H) * u.labelDistance
              },
              "final": {},
              alignment: 0 > u.labelDistance ? "center" : u.half ? "right" : "left",
              connectorPosition: {
                breakAt: {
                  x: e[0] + K + Math.cos(H) * D,
                  y: e[1] + p + Math.sin(H) * D
                },
                touchingSliceAt: {
                  x: e[0] + K,
                  y: e[1] + p
                }
              }
            };
          }

          A(this, "afterTranslate");
        },
        drawEmpty: function drawEmpty() {
          var e = this.options;

          if (0 === this.total) {
            var c = this.center[0];
            var d = this.center[1];
            this.graph || (this.graph = this.chart.renderer.circle(c, d, 0).addClass("highcharts-graph").add(this.group));
            this.graph.animate({
              "stroke-width": e.borderWidth,
              cx: c,
              cy: d,
              r: this.center[2] / 2,
              fill: e.fillColor || "none",
              stroke: e.color || "#cccccc"
            }, this.options.animation);
          } else this.graph && (this.graph = this.graph.destroy());
        },
        redrawPoints: function redrawPoints() {
          var e = this,
              c = e.chart,
              d = c.renderer,
              g,
              f,
              a,
              h,
              v = e.options.shadow;
          this.drawEmpty();
          !v || e.shadowGroup || c.styledMode || (e.shadowGroup = d.g("shadow").attr({
            zIndex: -1
          }).add(e.group));
          e.points.forEach(function (k) {
            var l = {};
            f = k.graphic;

            if (!k.isNull && f) {
              h = k.shapeArgs;
              g = k.getTranslate();

              if (!c.styledMode) {
                var n = k.shadowGroup;
                v && !n && (n = k.shadowGroup = d.g("shadow").add(e.shadowGroup));
                n && n.attr(g);
                a = e.pointAttribs(k, k.selected && "select");
              }

              k.delayedRendering ? (f.setRadialReference(e.center).attr(h).attr(g), c.styledMode || f.attr(a).attr({
                "stroke-linejoin": "round"
              }).shadow(v, n), k.delayedRendering = !1) : (f.setRadialReference(e.center), c.styledMode || J(!0, l, a), J(!0, l, h, g), f.animate(l));
              f.attr({
                visibility: k.visible ? "inherit" : "hidden"
              });
              f.addClass(k.getClassName());
            } else f && (k.graphic = f.destroy());
          });
        },
        drawPoints: function drawPoints() {
          var e = this.chart.renderer;
          this.points.forEach(function (c) {
            c.graphic && c.hasNewShapeType() && (c.graphic = c.graphic.destroy());
            c.graphic || (c.graphic = e[c.shapeType](c.shapeArgs).add(c.series.group), c.delayedRendering = !0);
          });
        },
        searchPoint: q,
        sortByAngle: function sortByAngle(e, c) {
          e.sort(function (e, d) {
            return "undefined" !== typeof e.angle && (d.angle - e.angle) * c;
          });
        },
        drawLegendSymbol: g.drawRectangle,
        getCenter: u.getCenter,
        getSymbol: q,
        drawGraph: null
      }, {
        init: function init() {
          r.prototype.init.apply(this, arguments);
          var e = this;
          e.name = y(e.name, "Slice");

          var c = function c(_c) {
            e.slice("select" === _c.type);
          };

          I(e, "select", c);
          I(e, "unselect", c);
          return e;
        },
        isValid: function isValid() {
          return G(this.y) && 0 <= this.y;
        },
        setVisible: function setVisible(e, c) {
          var d = this,
              g = d.series,
              f = g.chart,
              a = g.options.ignoreHiddenPoint;
          c = y(c, a);
          e !== d.visible && (d.visible = d.options.visible = e = "undefined" === typeof e ? !d.visible : e, g.options.data[g.data.indexOf(d)] = d.options, ["graphic", "dataLabel", "connector", "shadowGroup"].forEach(function (a) {
            if (d[a]) d[a][e ? "show" : "hide"](!0);
          }), d.legendItem && f.legend.colorizeItem(d, e), e || "hover" !== d.state || d.setState(""), a && (g.isDirty = !0), c && f.redraw());
        },
        slice: function slice(e, c, d) {
          var g = this.series;
          h(d, g.chart);
          y(c, !0);
          this.sliced = this.options.sliced = E(e) ? e : !this.sliced;
          g.options.data[g.data.indexOf(this)] = this.options;
          this.graphic.animate(this.getTranslate());
          this.shadowGroup && this.shadowGroup.animate(this.getTranslate());
        },
        getTranslate: function getTranslate() {
          return this.sliced ? this.slicedTranslation : {
            translateX: 0,
            translateY: 0
          };
        },
        haloPath: function haloPath(e) {
          var c = this.shapeArgs;
          return this.sliced || !this.visible ? [] : this.series.chart.renderer.symbols.arc(c.x, c.y, c.r + e, c.r + e, {
            innerR: c.r - 1,
            start: c.start,
            end: c.end
          });
        },
        connectorShapes: {
          fixedOffset: function fixedOffset(e, c, d) {
            var g = c.breakAt;
            c = c.touchingSliceAt;
            return ["M", e.x, e.y].concat(d.softConnector ? ["C", e.x + ("left" === e.alignment ? -5 : 5), e.y, 2 * g.x - c.x, 2 * g.y - c.y, g.x, g.y] : ["L", g.x, g.y]).concat(["L", c.x, c.y]);
          },
          straight: function straight(e, c) {
            c = c.touchingSliceAt;
            return ["M", e.x, e.y, "L", c.x, c.y];
          },
          crookedLine: function crookedLine(e, c, d) {
            c = c.touchingSliceAt;
            var g = this.series,
                f = g.center[0],
                a = g.chart.plotWidth,
                k = g.chart.plotLeft;
            g = e.alignment;
            var h = this.shapeArgs.r;
            d = t(d.crookDistance, 1);
            d = "left" === g ? f + h + (a + k - f - h) * (1 - d) : k + (f - h) * d;
            f = ["L", d, e.y];
            if ("left" === g ? d > e.x || d < c.x : d < e.x || d > c.x) f = [];
            return ["M", e.x, e.y].concat(f).concat(["L", c.x, c.y]);
          }
        },
        getConnectorPath: function getConnectorPath() {
          var e = this.labelPosition,
              c = this.series.options.dataLabels,
              d = c.connectorShape,
              g = this.connectorShapes;
          g[d] && (d = g[d]);
          return d.call(this, {
            x: e["final"].x,
            y: e["final"].y,
            alignment: e.alignment
          }, e.connectorPosition, c);
        }
      });
    });
    S(r, "parts/DataLabels.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.animObject,
          u = g.arrayMax,
          I = g.clamp,
          M = g.defined,
          E = g.extend,
          A = g.format,
          G = g.isArray,
          J = g.merge,
          y = g.objectEach,
          t = g.pick,
          D = g.relativeLength,
          h = g.splat,
          N = g.stableSort;
      g = d.noop;
      var q = d.Series,
          P = d.seriesTypes;

      d.distribute = function (e, c, g) {
        function h(a, c) {
          return a.target - c.target;
        }

        var f,
            a = !0,
            k = e,
            v = [];
        var q = 0;
        var w = k.reducedLen || c;

        for (f = e.length; f--;) {
          q += e[f].size;
        }

        if (q > w) {
          N(e, function (a, c) {
            return (c.rank || 0) - (a.rank || 0);
          });

          for (q = f = 0; q <= w;) {
            q += e[f].size, f++;
          }

          v = e.splice(f - 1, e.length);
        }

        N(e, h);

        for (e = e.map(function (a) {
          return {
            size: a.size,
            targets: [a.target],
            align: t(a.align, .5)
          };
        }); a;) {
          for (f = e.length; f--;) {
            a = e[f], q = (Math.min.apply(0, a.targets) + Math.max.apply(0, a.targets)) / 2, a.pos = I(q - a.size * a.align, 0, c - a.size);
          }

          f = e.length;

          for (a = !1; f--;) {
            0 < f && e[f - 1].pos + e[f - 1].size > e[f].pos && (e[f - 1].size += e[f].size, e[f - 1].targets = e[f - 1].targets.concat(e[f].targets), e[f - 1].align = .5, e[f - 1].pos + e[f - 1].size > c && (e[f - 1].pos = c - e[f - 1].size), e.splice(f, 1), a = !0);
          }
        }

        k.push.apply(k, v);
        f = 0;
        e.some(function (a) {
          var e = 0;
          if (a.targets.some(function () {
            k[f].pos = a.pos + e;
            if ("undefined" !== typeof g && Math.abs(k[f].pos - k[f].target) > g) return k.slice(0, f + 1).forEach(function (a) {
              delete a.pos;
            }), k.reducedLen = (k.reducedLen || c) - .1 * c, k.reducedLen > .1 * c && d.distribute(k, c, g), !0;
            e += k[f].size;
            f++;
          })) return !0;
        });
        N(k, h);
      };

      q.prototype.drawDataLabels = function () {
        function e(a, c) {
          var b = c.filter;
          return b ? (c = b.operator, a = a[b.property], b = b.value, ">" === c && a > b || "<" === c && a < b || ">=" === c && a >= b || "<=" === c && a <= b || "==" === c && a == b || "===" === c && a === b ? !0 : !1) : !0;
        }

        function c(a, c) {
          var b = [],
              e;
          if (G(a) && !G(c)) b = a.map(function (a) {
            return J(a, c);
          });else if (G(c) && !G(a)) b = c.map(function (b) {
            return J(a, b);
          });else if (G(a) || G(c)) for (e = Math.max(a.length, c.length); e--;) {
            b[e] = J(a[e], c[e]);
          } else b = J(a, c);
          return b;
        }

        var g = this,
            n = g.chart,
            f = g.options,
            a = f.dataLabels,
            l = g.points,
            v,
            q = g.hasRendered || 0,
            w = r(f.animation).duration,
            u = Math.min(w, 200),
            L = !n.renderer.forExport && t(a.defer, 0 < u),
            D = n.renderer;
        a = c(c(n.options.plotOptions && n.options.plotOptions.series && n.options.plotOptions.series.dataLabels, n.options.plotOptions && n.options.plotOptions[g.type] && n.options.plotOptions[g.type].dataLabels), a);
        d.fireEvent(this, "drawDataLabels");

        if (G(a) || a.enabled || g._hasPointLabels) {
          var H = g.plotGroup("dataLabelsGroup", "data-labels", L && !q ? "hidden" : "inherit", a.zIndex || 6);
          L && (H.attr({
            opacity: +q
          }), q || setTimeout(function () {
            var a = g.dataLabelsGroup;
            a && (g.visible && H.show(!0), a[f.animation ? "animate" : "attr"]({
              opacity: 1
            }, {
              duration: u
            }));
          }, w - u));
          l.forEach(function (d) {
            v = h(c(a, d.dlOptions || d.options && d.options.dataLabels));
            v.forEach(function (a, b) {
              var c = a.enabled && (!d.isNull || d.dataLabelOnNull) && e(d, a),
                  h = d.dataLabels ? d.dataLabels[b] : d.dataLabel,
                  k = d.connectors ? d.connectors[b] : d.connector,
                  l = t(a.distance, d.labelDistance),
                  p = !h;

              if (c) {
                var v = d.getLabelConfig();
                var m = t(a[d.formatPrefix + "Format"], a.format);
                v = M(m) ? A(m, v, n) : (a[d.formatPrefix + "Formatter"] || a.formatter).call(v, a);
                m = a.style;
                var q = a.rotation;
                n.styledMode || (m.color = t(a.color, m.color, g.color, "#000000"), "contrast" === m.color ? (d.contrastColor = D.getContrast(d.color || g.color), m.color = !M(l) && a.inside || 0 > l || f.stacking ? d.contrastColor : "#000000") : delete d.contrastColor, f.cursor && (m.cursor = f.cursor));
                var w = {
                  r: a.borderRadius || 0,
                  rotation: q,
                  padding: a.padding,
                  zIndex: 1
                };
                n.styledMode || (w.fill = a.backgroundColor, w.stroke = a.borderColor, w["stroke-width"] = a.borderWidth);
                y(w, function (a, b) {
                  "undefined" === typeof a && delete w[b];
                });
              }

              !h || c && M(v) ? c && M(v) && (h ? w.text = v : (d.dataLabels = d.dataLabels || [], h = d.dataLabels[b] = q ? D.text(v, 0, -9999, a.useHTML).addClass("highcharts-data-label") : D.label(v, 0, -9999, a.shape, null, null, a.useHTML, null, "data-label"), b || (d.dataLabel = h), h.addClass(" highcharts-data-label-color-" + d.colorIndex + " " + (a.className || "") + (a.useHTML ? " highcharts-tracker" : ""))), h.options = a, h.attr(w), n.styledMode || h.css(m).shadow(a.shadow), h.added || h.add(H), a.textPath && !a.useHTML && (h.setTextPath(d.getDataLabelPath && d.getDataLabelPath(h) || d.graphic, a.textPath), d.dataLabelPath && !a.textPath.enabled && (d.dataLabelPath = d.dataLabelPath.destroy())), g.alignDataLabel(d, h, a, null, p)) : (d.dataLabel = d.dataLabel && d.dataLabel.destroy(), d.dataLabels && (1 === d.dataLabels.length ? delete d.dataLabels : delete d.dataLabels[b]), b || delete d.dataLabel, k && (d.connector = d.connector.destroy(), d.connectors && (1 === d.connectors.length ? delete d.connectors : delete d.connectors[b])));
            });
          });
        }

        d.fireEvent(this, "afterDrawDataLabels");
      };

      q.prototype.alignDataLabel = function (d, c, g, h, f) {
        var a = this,
            e = this.chart,
            k = this.isCartesian && e.inverted,
            n = this.enabledDataSorting,
            q = t(d.dlBox && d.dlBox.centerX, d.plotX, -9999),
            r = t(d.plotY, -9999),
            u = c.getBBox(),
            y = g.rotation,
            A = g.align,
            K = e.isInsidePlot(q, Math.round(r), k),
            p = "justify" === t(g.overflow, n ? "none" : "justify"),
            b = this.visible && !1 !== d.visible && (d.series.forceDL || n && !p || K || g.inside && h && e.isInsidePlot(q, k ? h.x + 1 : h.y + h.height - 1, k));

        var C = function C(b) {
          n && a.xAxis && !p && a.setDataLabelStartPos(d, c, f, K, b);
        };

        if (b) {
          var x = e.renderer.fontMetrics(e.styledMode ? void 0 : g.style.fontSize, c).b;
          h = E({
            x: k ? this.yAxis.len - r : q,
            y: Math.round(k ? this.xAxis.len - q : r),
            width: 0,
            height: 0
          }, h);
          E(g, {
            width: u.width,
            height: u.height
          });
          y ? (p = !1, q = e.renderer.rotCorr(x, y), q = {
            x: h.x + g.x + h.width / 2 + q.x,
            y: h.y + g.y + {
              top: 0,
              middle: .5,
              bottom: 1
            }[g.verticalAlign] * h.height
          }, C(q), c[f ? "attr" : "animate"](q).attr({
            align: A
          }), C = (y + 720) % 360, C = 180 < C && 360 > C, "left" === A ? q.y -= C ? u.height : 0 : "center" === A ? (q.x -= u.width / 2, q.y -= u.height / 2) : "right" === A && (q.x -= u.width, q.y -= C ? 0 : u.height), c.placed = !0, c.alignAttr = q) : (C(h), c.align(g, null, h), q = c.alignAttr);
          p && 0 <= h.height ? this.justifyDataLabel(c, g, q, u, h, f) : t(g.crop, !0) && (b = e.isInsidePlot(q.x, q.y) && e.isInsidePlot(q.x + u.width, q.y + u.height));
          if (g.shape && !y) c[f ? "attr" : "animate"]({
            anchorX: k ? e.plotWidth - d.plotY : d.plotX,
            anchorY: k ? e.plotHeight - d.plotX : d.plotY
          });
        }

        f && n && (c.placed = !1);
        b || n && !p || (c.hide(!0), c.placed = !1);
      };

      q.prototype.setDataLabelStartPos = function (d, c, g, h, f) {
        var a = this.chart,
            e = a.inverted,
            k = this.xAxis,
            n = k.reversed,
            q = e ? c.height / 2 : c.width / 2;
        d = (d = d.pointWidth) ? d / 2 : 0;
        k = e ? f.x : n ? -q - d : k.width - q + d;
        f = e ? n ? this.yAxis.height - q + d : -q - d : f.y;
        c.startXPos = k;
        c.startYPos = f;
        h ? "hidden" === c.visibility && (c.show(), c.attr({
          opacity: 0
        }).animate({
          opacity: 1
        })) : c.attr({
          opacity: 1
        }).animate({
          opacity: 0
        }, void 0, c.hide);
        a.hasRendered && (g && c.attr({
          x: c.startXPos,
          y: c.startYPos
        }), c.placed = !0);
      };

      q.prototype.justifyDataLabel = function (d, c, g, h, f, a) {
        var e = this.chart,
            k = c.align,
            n = c.verticalAlign,
            q = d.box ? 0 : d.padding || 0;
        var r = g.x + q;

        if (0 > r) {
          "right" === k ? (c.align = "left", c.inside = !0) : c.x = -r;
          var t = !0;
        }

        r = g.x + h.width - q;
        r > e.plotWidth && ("left" === k ? (c.align = "right", c.inside = !0) : c.x = e.plotWidth - r, t = !0);
        r = g.y + q;
        0 > r && ("bottom" === n ? (c.verticalAlign = "top", c.inside = !0) : c.y = -r, t = !0);
        r = g.y + h.height - q;
        r > e.plotHeight && ("top" === n ? (c.verticalAlign = "bottom", c.inside = !0) : c.y = e.plotHeight - r, t = !0);
        t && (d.placed = !a, d.align(c, null, f));
        return t;
      };

      P.pie && (P.pie.prototype.dataLabelPositioners = {
        radialDistributionY: function radialDistributionY(d) {
          return d.top + d.distributeBox.pos;
        },
        radialDistributionX: function radialDistributionX(d, c, g, h) {
          return d.getX(g < c.top + 2 || g > c.bottom - 2 ? h : g, c.half, c);
        },
        justify: function justify(d, c, g) {
          return g[0] + (d.half ? -1 : 1) * (c + d.labelDistance);
        },
        alignToPlotEdges: function alignToPlotEdges(d, c, g, h) {
          d = d.getBBox().width;
          return c ? d + h : g - d - h;
        },
        alignToConnectors: function alignToConnectors(d, c, g, h) {
          var e = 0,
              a;
          d.forEach(function (c) {
            a = c.dataLabel.getBBox().width;
            a > e && (e = a);
          });
          return c ? e + h : g - e - h;
        }
      }, P.pie.prototype.drawDataLabels = function () {
        var e = this,
            c = e.data,
            g,
            h = e.chart,
            f = e.options.dataLabels || {},
            a = f.connectorPadding,
            l,
            v = h.plotWidth,
            r = h.plotHeight,
            w = h.plotLeft,
            y = Math.round(h.chartWidth / 3),
            A,
            D = e.center,
            H = D[2] / 2,
            K = D[1],
            p,
            b,
            C,
            x,
            G = [[], []],
            E,
            N,
            I,
            m,
            P = [0, 0, 0, 0],
            S = e.dataLabelPositioners,
            W;
        e.visible && (f.enabled || e._hasPointLabels) && (c.forEach(function (a) {
          a.dataLabel && a.visible && a.dataLabel.shortened && (a.dataLabel.attr({
            width: "auto"
          }).css({
            width: "auto",
            textOverflow: "clip"
          }), a.dataLabel.shortened = !1);
        }), q.prototype.drawDataLabels.apply(e), c.forEach(function (a) {
          a.dataLabel && (a.visible ? (G[a.half].push(a), a.dataLabel._pos = null, !M(f.style.width) && !M(a.options.dataLabels && a.options.dataLabels.style && a.options.dataLabels.style.width) && a.dataLabel.getBBox().width > y && (a.dataLabel.css({
            width: .7 * y
          }), a.dataLabel.shortened = !0)) : (a.dataLabel = a.dataLabel.destroy(), a.dataLabels && 1 === a.dataLabels.length && delete a.dataLabels));
        }), G.forEach(function (c, k) {
          var l = c.length,
              n = [],
              q;

          if (l) {
            e.sortByAngle(c, k - .5);

            if (0 < e.maxLabelDistance) {
              var u = Math.max(0, K - H - e.maxLabelDistance);
              var z = Math.min(K + H + e.maxLabelDistance, h.plotHeight);
              c.forEach(function (a) {
                0 < a.labelDistance && a.dataLabel && (a.top = Math.max(0, K - H - a.labelDistance), a.bottom = Math.min(K + H + a.labelDistance, h.plotHeight), q = a.dataLabel.getBBox().height || 21, a.distributeBox = {
                  target: a.labelPosition.natural.y - a.top + q / 2,
                  size: q,
                  rank: a.y
                }, n.push(a.distributeBox));
              });
              u = z + q - u;
              d.distribute(n, u, u / 5);
            }

            for (m = 0; m < l; m++) {
              g = c[m];
              C = g.labelPosition;
              p = g.dataLabel;
              I = !1 === g.visible ? "hidden" : "inherit";
              N = u = C.natural.y;
              n && M(g.distributeBox) && ("undefined" === typeof g.distributeBox.pos ? I = "hidden" : (x = g.distributeBox.size, N = S.radialDistributionY(g)));
              delete g.positionIndex;
              if (f.justify) E = S.justify(g, H, D);else switch (f.alignTo) {
                case "connectors":
                  E = S.alignToConnectors(c, k, v, w);
                  break;

                case "plotEdges":
                  E = S.alignToPlotEdges(p, k, v, w);
                  break;

                default:
                  E = S.radialDistributionX(e, g, N, u);
              }
              p._attr = {
                visibility: I,
                align: C.alignment
              };
              W = g.options.dataLabels || {};
              p._pos = {
                x: E + t(W.x, f.x) + ({
                  left: a,
                  right: -a
                }[C.alignment] || 0),
                y: N + t(W.y, f.y) - 10
              };
              C["final"].x = E;
              C["final"].y = N;
              t(f.crop, !0) && (b = p.getBBox().width, u = null, E - b < a && 1 === k ? (u = Math.round(b - E + a), P[3] = Math.max(u, P[3])) : E + b > v - a && 0 === k && (u = Math.round(E + b - v + a), P[1] = Math.max(u, P[1])), 0 > N - x / 2 ? P[0] = Math.max(Math.round(-N + x / 2), P[0]) : N + x / 2 > r && (P[2] = Math.max(Math.round(N + x / 2 - r), P[2])), p.sideOverflow = u);
            }
          }
        }), 0 === u(P) || this.verifyDataLabelOverflow(P)) && (this.placeDataLabels(), this.points.forEach(function (a) {
          W = J(f, a.options.dataLabels);

          if (l = t(W.connectorWidth, 1)) {
            var b;
            A = a.connector;

            if ((p = a.dataLabel) && p._pos && a.visible && 0 < a.labelDistance) {
              I = p._attr.visibility;
              if (b = !A) a.connector = A = h.renderer.path().addClass("highcharts-data-label-connector  highcharts-color-" + a.colorIndex + (a.className ? " " + a.className : "")).add(e.dataLabelsGroup), h.styledMode || A.attr({
                "stroke-width": l,
                stroke: W.connectorColor || a.color || "#666666"
              });
              A[b ? "attr" : "animate"]({
                d: a.getConnectorPath()
              });
              A.attr("visibility", I);
            } else A && (a.connector = A.destroy());
          }
        }));
      }, P.pie.prototype.placeDataLabels = function () {
        this.points.forEach(function (d) {
          var c = d.dataLabel,
              e;
          c && d.visible && ((e = c._pos) ? (c.sideOverflow && (c._attr.width = Math.max(c.getBBox().width - c.sideOverflow, 0), c.css({
            width: c._attr.width + "px",
            textOverflow: (this.options.dataLabels.style || {}).textOverflow || "ellipsis"
          }), c.shortened = !0), c.attr(c._attr), c[c.moved ? "animate" : "attr"](e), c.moved = !0) : c && c.attr({
            y: -9999
          }));
          delete d.distributeBox;
        }, this);
      }, P.pie.prototype.alignDataLabel = g, P.pie.prototype.verifyDataLabelOverflow = function (d) {
        var c = this.center,
            e = this.options,
            g = e.center,
            f = e.minSize || 80,
            a = null !== e.size;

        if (!a) {
          if (null !== g[0]) var h = Math.max(c[2] - Math.max(d[1], d[3]), f);else h = Math.max(c[2] - d[1] - d[3], f), c[0] += (d[3] - d[1]) / 2;
          null !== g[1] ? h = I(h, f, c[2] - Math.max(d[0], d[2])) : (h = I(h, f, c[2] - d[0] - d[2]), c[1] += (d[0] - d[2]) / 2);
          h < c[2] ? (c[2] = h, c[3] = Math.min(D(e.innerSize || 0, h), h), this.translate(c), this.drawDataLabels && this.drawDataLabels()) : a = !0;
        }

        return a;
      });
      P.column && (P.column.prototype.alignDataLabel = function (d, c, g, h, f) {
        var a = this.chart.inverted,
            e = d.series,
            k = d.dlBox || d.shapeArgs,
            n = t(d.below, d.plotY > t(this.translatedThreshold, e.yAxis.len)),
            r = t(g.inside, !!this.options.stacking);
        k && (h = J(k), 0 > h.y && (h.height += h.y, h.y = 0), k = h.y + h.height - e.yAxis.len, 0 < k && k < h.height && (h.height -= k), a && (h = {
          x: e.yAxis.len - h.y - h.height,
          y: e.xAxis.len - h.x - h.width,
          width: h.height,
          height: h.width
        }), r || (a ? (h.x += n ? 0 : h.width, h.width = 0) : (h.y += n ? h.height : 0, h.height = 0)));
        g.align = t(g.align, !a || r ? "center" : n ? "right" : "left");
        g.verticalAlign = t(g.verticalAlign, a || r ? "middle" : n ? "top" : "bottom");
        q.prototype.alignDataLabel.call(this, d, c, g, h, f);
        g.inside && d.contrastColor && c.css({
          color: d.contrastColor
        });
      });
    });
    S(r, "modules/overlapping-datalabels.src.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.addEvent,
          u = g.fireEvent,
          I = g.isArray,
          M = g.objectEach,
          E = g.pick;
      d = d.Chart;
      r(d, "render", function () {
        var d = [];
        (this.labelCollectors || []).forEach(function (g) {
          d = d.concat(g());
        });
        (this.yAxis || []).forEach(function (g) {
          g.options.stackLabels && !g.options.stackLabels.allowOverlap && M(g.stacks, function (g) {
            M(g, function (g) {
              d.push(g.label);
            });
          });
        });
        (this.series || []).forEach(function (g) {
          var r = g.options.dataLabels;
          g.visible && (!1 !== r.enabled || g._hasPointLabels) && (g.nodes || g.points).forEach(function (g) {
            g.visible && (I(g.dataLabels) ? g.dataLabels : g.dataLabel ? [g.dataLabel] : []).forEach(function (r) {
              var t = r.options;
              r.labelrank = E(t.labelrank, g.labelrank, g.shapeArgs && g.shapeArgs.height);
              t.allowOverlap || d.push(r);
            });
          });
        });
        this.hideOverlappingLabels(d);
      });

      d.prototype.hideOverlappingLabels = function (d) {
        var g = this,
            r = d.length,
            y = g.renderer,
            t,
            A,
            h,
            E = !1;

        var q = function q(c) {
          var d = c.box ? 0 : c.padding || 0;
          var e = 0;

          if (c && (!c.alignAttr || c.placed)) {
            var f = c.alignAttr || {
              x: c.attr("x"),
              y: c.attr("y")
            };
            var a = c.parentGroup;
            c.width || (e = c.getBBox(), c.width = e.width, c.height = e.height, e = y.fontMetrics(null, c.element).h);
            return {
              x: f.x + (a.translateX || 0) + d,
              y: f.y + (a.translateY || 0) + d - e,
              width: c.width - 2 * d,
              height: c.height - 2 * d
            };
          }
        };

        for (A = 0; A < r; A++) {
          if (t = d[A]) t.oldOpacity = t.opacity, t.newOpacity = 1, t.absoluteBox = q(t);
        }

        d.sort(function (c, d) {
          return (d.labelrank || 0) - (c.labelrank || 0);
        });

        for (A = 0; A < r; A++) {
          var I = (q = d[A]) && q.absoluteBox;

          for (t = A + 1; t < r; ++t) {
            var e = (h = d[t]) && h.absoluteBox;
            !I || !e || q === h || 0 === q.newOpacity || 0 === h.newOpacity || e.x > I.x + I.width || e.x + e.width < I.x || e.y > I.y + I.height || e.y + e.height < I.y || ((q.labelrank < h.labelrank ? q : h).newOpacity = 0);
          }
        }

        d.forEach(function (c) {
          var d;

          if (c) {
            var e = c.newOpacity;
            c.oldOpacity !== e && (c.alignAttr && c.placed ? (e ? c.show(!0) : d = function d() {
              c.hide(!0);
              c.placed = !1;
            }, E = !0, c.alignAttr.opacity = e, c[c.isOld ? "animate" : "attr"](c.alignAttr, null, d), u(g, "afterHideOverlappingLabel")) : c.attr({
              opacity: e
            }));
            c.isOld = !0;
          }
        });
        E && u(g, "afterHideAllOverlappingLabels");
      };
    });
    S(r, "parts/Interaction.js", [r["parts/Globals.js"], r["parts/Legend.js"], r["parts/Point.js"], r["parts/Utilities.js"]], function (d, g, r, u) {
      var I = u.addEvent,
          M = u.createElement,
          E = u.css,
          A = u.defined,
          G = u.extend,
          J = u.fireEvent,
          y = u.isArray,
          t = u.isFunction,
          D = u.isObject,
          h = u.merge,
          N = u.objectEach,
          q = u.pick;
      u = d.Chart;
      var P = d.defaultOptions,
          e = d.defaultPlotOptions,
          c = d.hasTouch,
          k = d.Series,
          n = d.seriesTypes,
          f = d.svg;
      d = d.TrackerMixin = {
        drawTrackerPoint: function drawTrackerPoint() {
          var a = this,
              d = a.chart,
              e = d.pointer,
              f = function f(a) {
            var c = e.getPointFromEvent(a);
            "undefined" !== typeof c && (e.isDirectTouch = !0, c.onMouseOver(a));
          },
              g;

          a.points.forEach(function (a) {
            g = y(a.dataLabels) ? a.dataLabels : a.dataLabel ? [a.dataLabel] : [];
            a.graphic && (a.graphic.element.point = a);
            g.forEach(function (c) {
              c.div ? c.div.point = a : c.element.point = a;
            });
          });
          a._hasTracking || (a.trackerGroups.forEach(function (g) {
            if (a[g]) {
              a[g].addClass("highcharts-tracker").on("mouseover", f).on("mouseout", function (a) {
                e.onTrackerMouseOut(a);
              });
              if (c) a[g].on("touchstart", f);
              !d.styledMode && a.options.cursor && a[g].css(E).css({
                cursor: a.options.cursor
              });
            }
          }), a._hasTracking = !0);
          J(this, "afterDrawTracker");
        },
        drawTrackerGraph: function drawTrackerGraph() {
          var a = this,
              d = a.options,
              e = d.trackByArea,
              g = [].concat(e ? a.areaPath : a.graphPath),
              h = g.length,
              k = a.chart,
              n = k.pointer,
              q = k.renderer,
              r = k.options.tooltip.snap,
              t = a.tracker,
              p,
              b = function b() {
            if (k.hoverSeries !== a) a.onMouseOver();
          },
              u = "rgba(192,192,192," + (f ? .0001 : .002) + ")";

          if (h && !e) for (p = h + 1; p--;) {
            "M" === g[p] && g.splice(p + 1, 0, g[p + 1] - r, g[p + 2], "L"), (p && "M" === g[p] || p === h) && g.splice(p, 0, "L", g[p - 2] + r, g[p - 1]);
          }
          t ? t.attr({
            d: g
          }) : a.graph && (a.tracker = q.path(g).attr({
            visibility: a.visible ? "visible" : "hidden",
            zIndex: 2
          }).addClass(e ? "highcharts-tracker-area" : "highcharts-tracker-line").add(a.group), k.styledMode || a.tracker.attr({
            "stroke-linejoin": "round",
            stroke: u,
            fill: e ? u : "none",
            "stroke-width": a.graph.strokeWidth() + (e ? 0 : 2 * r)
          }), [a.tracker, a.markerGroup].forEach(function (a) {
            a.addClass("highcharts-tracker").on("mouseover", b).on("mouseout", function (a) {
              n.onTrackerMouseOut(a);
            });
            d.cursor && !k.styledMode && a.css({
              cursor: d.cursor
            });
            if (c) a.on("touchstart", b);
          }));
          J(this, "afterDrawTracker");
        }
      };
      n.column && (n.column.prototype.drawTracker = d.drawTrackerPoint);
      n.pie && (n.pie.prototype.drawTracker = d.drawTrackerPoint);
      n.scatter && (n.scatter.prototype.drawTracker = d.drawTrackerPoint);
      G(g.prototype, {
        setItemEvents: function setItemEvents(a, c, d) {
          var e = this,
              f = e.chart.renderer.boxWrapper,
              g = a instanceof r,
              k = "highcharts-legend-" + (g ? "point" : "series") + "-active",
              l = e.chart.styledMode;
          (d ? [c, a.legendSymbol] : [a.legendGroup]).forEach(function (d) {
            if (d) d.on("mouseover", function () {
              a.visible && e.allItems.forEach(function (c) {
                a !== c && c.setState("inactive", !g);
              });
              a.setState("hover");
              a.visible && f.addClass(k);
              l || c.css(e.options.itemHoverStyle);
            }).on("mouseout", function () {
              e.chart.styledMode || c.css(h(a.visible ? e.itemStyle : e.itemHiddenStyle));
              e.allItems.forEach(function (c) {
                a !== c && c.setState("", !g);
              });
              f.removeClass(k);
              a.setState();
            }).on("click", function (c) {
              var d = function d() {
                a.setVisible && a.setVisible();
                e.allItems.forEach(function (b) {
                  a !== b && b.setState(a.visible ? "inactive" : "", !g);
                });
              };

              f.removeClass(k);
              c = {
                browserEvent: c
              };
              a.firePointEvent ? a.firePointEvent("legendItemClick", c, d) : J(a, "legendItemClick", c, d);
            });
          });
        },
        createCheckboxForItem: function createCheckboxForItem(a) {
          a.checkbox = M("input", {
            type: "checkbox",
            className: "highcharts-legend-checkbox",
            checked: a.selected,
            defaultChecked: a.selected
          }, this.options.itemCheckboxStyle, this.chart.container);
          I(a.checkbox, "click", function (c) {
            J(a.series || a, "checkboxClick", {
              checked: c.target.checked,
              item: a
            }, function () {
              a.select();
            });
          });
        }
      });
      G(u.prototype, {
        showResetZoom: function showResetZoom() {
          function a() {
            c.zoomOut();
          }

          var c = this,
              d = P.lang,
              e = c.options.chart.resetZoomButton,
              f = e.theme,
              g = f.states,
              h = "chart" === e.relativeTo || "spaceBox" === e.relativeTo ? null : "plotBox";
          J(this, "beforeShowResetZoom", null, function () {
            c.resetZoomButton = c.renderer.button(d.resetZoom, null, null, a, f, g && g.hover).attr({
              align: e.position.align,
              title: d.resetZoomTitle
            }).addClass("highcharts-reset-zoom").add().align(e.position, !1, h);
          });
          J(this, "afterShowResetZoom");
        },
        zoomOut: function zoomOut() {
          J(this, "selection", {
            resetSelection: !0
          }, this.zoom);
        },
        zoom: function zoom(a) {
          var c = this,
              d,
              e = c.pointer,
              f = !1,
              g = c.inverted ? e.mouseDownX : e.mouseDownY;
          !a || a.resetSelection ? (c.axes.forEach(function (a) {
            d = a.zoom();
          }), e.initiated = !1) : a.xAxis.concat(a.yAxis).forEach(function (a) {
            var h = a.axis,
                k = c.inverted ? h.left : h.top,
                l = c.inverted ? k + h.width : k + h.height,
                b = h.isXAxis,
                n = !1;
            if (!b && g >= k && g <= l || b || !A(g)) n = !0;
            e[b ? "zoomX" : "zoomY"] && n && (d = h.zoom(a.min, a.max), h.displayBtn && (f = !0));
          });
          var h = c.resetZoomButton;
          f && !h ? c.showResetZoom() : !f && D(h) && (c.resetZoomButton = h.destroy());
          d && c.redraw(q(c.options.chart.animation, a && a.animation, 100 > c.pointCount));
        },
        pan: function pan(a, c) {
          var d = this,
              e = d.hoverPoints,
              f = d.options.chart,
              g;
          c = "object" === typeof c ? c : {
            enabled: c,
            type: "x"
          };
          f && f.panning && (f.panning = c);
          var h = c.type;
          J(this, "pan", {
            originalEvent: a
          }, function () {
            e && e.forEach(function (a) {
              a.setState();
            });
            var c = [1];
            "xy" === h ? c = [1, 0] : "y" === h && (c = [0]);
            c.forEach(function (c) {
              var e = d[c ? "xAxis" : "yAxis"][0],
                  f = e.options,
                  b = e.horiz,
                  h = a[b ? "chartX" : "chartY"];
              b = b ? "mouseDownX" : "mouseDownY";
              var k = d[b],
                  l = (e.pointRange || 0) / 2,
                  n = e.reversed && !d.inverted || !e.reversed && d.inverted ? -1 : 1,
                  q = e.getExtremes(),
                  v = e.toValue(k - h, !0) + l * n;
              n = e.toValue(k + e.len - h, !0) - l * n;
              var m = n < v;
              k = m ? n : v;
              v = m ? v : n;
              n = Math.min(q.dataMin, l ? q.min : e.toValue(e.toPixels(q.min) - e.minPixelPadding));
              l = Math.max(q.dataMax, l ? q.max : e.toValue(e.toPixels(q.max) + e.minPixelPadding));

              if (!f.ordinal) {
                c && (f = n - k, 0 < f && (v += f, k = n), f = v - l, 0 < f && (v = l, k -= f));
                if (e.series.length && k !== q.min && v !== q.max && c || e.panningState && k >= e.panningState.startMin && v <= e.panningState.startMax) e.setExtremes(k, v, !1, !1, {
                  trigger: "pan"
                }), g = !0;
                d[b] = h;
              }
            });
            g && d.redraw(!1);
            E(d.container, {
              cursor: "move"
            });
          });
        }
      });
      G(r.prototype, {
        select: function select(a, c) {
          var d = this,
              e = d.series,
              f = e.chart;
          this.selectedStaging = a = q(a, !d.selected);
          d.firePointEvent(a ? "select" : "unselect", {
            accumulate: c
          }, function () {
            d.selected = d.options.selected = a;
            e.options.data[e.data.indexOf(d)] = d.options;
            d.setState(a && "select");
            c || f.getSelectedPoints().forEach(function (a) {
              var c = a.series;
              a.selected && a !== d && (a.selected = a.options.selected = !1, c.options.data[c.data.indexOf(a)] = a.options, a.setState(f.hoverPoints && c.options.inactiveOtherPoints ? "inactive" : ""), a.firePointEvent("unselect"));
            });
          });
          delete this.selectedStaging;
        },
        onMouseOver: function onMouseOver(a) {
          var c = this.series.chart,
              d = c.pointer;
          a = a ? d.normalize(a) : d.getChartCoordinatesFromPoint(this, c.inverted);
          d.runPointActions(a, this);
        },
        onMouseOut: function onMouseOut() {
          var a = this.series.chart;
          this.firePointEvent("mouseOut");
          this.series.options.inactiveOtherPoints || (a.hoverPoints || []).forEach(function (a) {
            a.setState();
          });
          a.hoverPoints = a.hoverPoint = null;
        },
        importEvents: function importEvents() {
          if (!this.hasImportedEvents) {
            var a = this,
                c = h(a.series.options.point, a.options).events;
            a.events = c;
            N(c, function (c, d) {
              t(c) && I(a, d, c);
            });
            this.hasImportedEvents = !0;
          }
        },
        setState: function setState(a, c) {
          var d = this.series,
              f = this.state,
              g = d.options.states[a || "normal"] || {},
              h = e[d.type].marker && d.options.marker,
              k = h && !1 === h.enabled,
              l = h && h.states && h.states[a || "normal"] || {},
              n = !1 === l.enabled,
              r = d.stateMarkerGraphic,
              p = this.marker || {},
              b = d.chart,
              t = d.halo,
              u,
              y = h && d.markerAttribs;
          a = a || "";

          if (!(a === this.state && !c || this.selected && "select" !== a || !1 === g.enabled || a && (n || k && !1 === l.enabled) || a && p.states && p.states[a] && !1 === p.states[a].enabled)) {
            this.state = a;
            y && (u = d.markerAttribs(this, a));

            if (this.graphic) {
              f && this.graphic.removeClass("highcharts-point-" + f);
              a && this.graphic.addClass("highcharts-point-" + a);

              if (!b.styledMode) {
                var A = d.pointAttribs(this, a);
                var D = q(b.options.chart.animation, g.animation);
                d.options.inactiveOtherPoints && ((this.dataLabels || []).forEach(function (a) {
                  a && a.animate({
                    opacity: A.opacity
                  }, D);
                }), this.connector && this.connector.animate({
                  opacity: A.opacity
                }, D));
                this.graphic.animate(A, D);
              }

              u && this.graphic.animate(u, q(b.options.chart.animation, l.animation, h.animation));
              r && r.hide();
            } else {
              if (a && l) {
                f = p.symbol || d.symbol;
                r && r.currentSymbol !== f && (r = r.destroy());
                if (u) if (r) r[c ? "animate" : "attr"]({
                  x: u.x,
                  y: u.y
                });else f && (d.stateMarkerGraphic = r = b.renderer.symbol(f, u.x, u.y, u.width, u.height).add(d.markerGroup), r.currentSymbol = f);
                !b.styledMode && r && r.attr(d.pointAttribs(this, a));
              }

              r && (r[a && this.isInside ? "show" : "hide"](), r.element.point = this);
            }

            a = g.halo;
            g = (r = this.graphic || r) && r.visibility || "inherit";
            a && a.size && r && "hidden" !== g && !this.isCluster ? (t || (d.halo = t = b.renderer.path().add(r.parentGroup)), t.show()[c ? "animate" : "attr"]({
              d: this.haloPath(a.size)
            }), t.attr({
              "class": "highcharts-halo highcharts-color-" + q(this.colorIndex, d.colorIndex) + (this.className ? " " + this.className : ""),
              visibility: g,
              zIndex: -1
            }), t.point = this, b.styledMode || t.attr(G({
              fill: this.color || d.color,
              "fill-opacity": a.opacity
            }, a.attributes))) : t && t.point && t.point.haloPath && t.animate({
              d: t.point.haloPath(0)
            }, null, t.hide);
            J(this, "afterSetState");
          }
        },
        haloPath: function haloPath(a) {
          return this.series.chart.renderer.symbols.circle(Math.floor(this.plotX) - a, this.plotY - a, 2 * a, 2 * a);
        }
      });
      G(k.prototype, {
        onMouseOver: function onMouseOver() {
          var a = this.chart,
              c = a.hoverSeries;
          if (c && c !== this) c.onMouseOut();
          this.options.events.mouseOver && J(this, "mouseOver");
          this.setState("hover");
          a.hoverSeries = this;
        },
        onMouseOut: function onMouseOut() {
          var a = this.options,
              c = this.chart,
              d = c.tooltip,
              e = c.hoverPoint;
          c.hoverSeries = null;
          if (e) e.onMouseOut();
          this && a.events.mouseOut && J(this, "mouseOut");
          !d || this.stickyTracking || d.shared && !this.noSharedTooltip || d.hide();
          c.series.forEach(function (a) {
            a.setState("", !0);
          });
        },
        setState: function setState(a, c) {
          var d = this,
              e = d.options,
              f = d.graph,
              g = e.inactiveOtherPoints,
              h = e.states,
              k = e.lineWidth,
              l = e.opacity,
              n = q(h[a || "normal"] && h[a || "normal"].animation, d.chart.options.chart.animation);
          e = 0;
          a = a || "";

          if (d.state !== a && ([d.group, d.markerGroup, d.dataLabelsGroup].forEach(function (c) {
            c && (d.state && c.removeClass("highcharts-series-" + d.state), a && c.addClass("highcharts-series-" + a));
          }), d.state = a, !d.chart.styledMode)) {
            if (h[a] && !1 === h[a].enabled) return;
            a && (k = h[a].lineWidth || k + (h[a].lineWidthPlus || 0), l = q(h[a].opacity, l));
            if (f && !f.dashstyle) for (h = {
              "stroke-width": k
            }, f.animate(h, n); d["zone-graph-" + e];) {
              d["zone-graph-" + e].attr(h), e += 1;
            }
            g || [d.group, d.markerGroup, d.dataLabelsGroup, d.labelBySeries].forEach(function (a) {
              a && a.animate({
                opacity: l
              }, n);
            });
          }

          c && g && d.points && d.setAllPointsToState(a);
        },
        setAllPointsToState: function setAllPointsToState(a) {
          this.points.forEach(function (c) {
            c.setState && c.setState(a);
          });
        },
        setVisible: function setVisible(a, c) {
          var d = this,
              e = d.chart,
              f = d.legendItem,
              g = e.options.chart.ignoreHiddenSeries,
              h = d.visible;
          var k = (d.visible = a = d.options.visible = d.userOptions.visible = "undefined" === typeof a ? !h : a) ? "show" : "hide";
          ["group", "dataLabelsGroup", "markerGroup", "tracker", "tt"].forEach(function (a) {
            if (d[a]) d[a][k]();
          });
          if (e.hoverSeries === d || (e.hoverPoint && e.hoverPoint.series) === d) d.onMouseOut();
          f && e.legend.colorizeItem(d, a);
          d.isDirty = !0;
          d.options.stacking && e.series.forEach(function (a) {
            a.options.stacking && a.visible && (a.isDirty = !0);
          });
          d.linkedSeries.forEach(function (c) {
            c.setVisible(a, !1);
          });
          g && (e.isDirtyBox = !0);
          J(d, k);
          !1 !== c && e.redraw();
        },
        show: function show() {
          this.setVisible(!0);
        },
        hide: function hide() {
          this.setVisible(!1);
        },
        select: function select(a) {
          this.selected = a = this.options.selected = "undefined" === typeof a ? !this.selected : a;
          this.checkbox && (this.checkbox.checked = a);
          J(this, a ? "select" : "unselect");
        },
        drawTracker: d.drawTrackerGraph
      });
    });
    S(r, "parts/Responsive.js", [r["parts/Globals.js"], r["parts/Utilities.js"]], function (d, g) {
      var r = g.find,
          u = g.isArray,
          I = g.isObject,
          M = g.merge,
          E = g.objectEach,
          A = g.pick,
          G = g.splat,
          J = g.uniqueKey;
      d = d.Chart;

      d.prototype.setResponsive = function (d, g) {
        var t = this.options.responsive,
            h = [],
            u = this.currentResponsive;
        !g && t && t.rules && t.rules.forEach(function (d) {
          "undefined" === typeof d._id && (d._id = J());
          this.matchResponsiveRule(d, h);
        }, this);
        g = M.apply(0, h.map(function (d) {
          return r(t.rules, function (g) {
            return g._id === d;
          }).chartOptions;
        }));
        g.isResponsiveOptions = !0;
        h = h.toString() || void 0;
        h !== (u && u.ruleIds) && (u && this.update(u.undoOptions, d, !0), h ? (u = this.currentOptions(g), u.isResponsiveOptions = !0, this.currentResponsive = {
          ruleIds: h,
          mergedOptions: g,
          undoOptions: u
        }, this.update(g, d, !0)) : this.currentResponsive = void 0);
      };

      d.prototype.matchResponsiveRule = function (d, g) {
        var r = d.condition;
        (r.callback || function () {
          return this.chartWidth <= A(r.maxWidth, Number.MAX_VALUE) && this.chartHeight <= A(r.maxHeight, Number.MAX_VALUE) && this.chartWidth >= A(r.minWidth, 0) && this.chartHeight >= A(r.minHeight, 0);
        }).call(this) && g.push(d._id);
      };

      d.prototype.currentOptions = function (d) {
        function g(d, h, t, e) {
          var c;
          E(d, function (d, n) {
            if (!e && -1 < r.collectionsWithUpdate.indexOf(n)) for (d = G(d), t[n] = [], c = 0; c < d.length; c++) {
              h[n][c] && (t[n][c] = {}, g(d[c], h[n][c], t[n][c], e + 1));
            } else I(d) ? (t[n] = u(d) ? [] : {}, g(d, h[n] || {}, t[n], e + 1)) : t[n] = "undefined" === typeof h[n] ? null : h[n];
          });
        }

        var r = this,
            h = {};
        g(d, this.options, h, 0);
        return h;
      };
    });
    S(r, "masters/highcharts.src.js", [r["parts/Globals.js"]], function (d) {
      return d;
    });
    r["masters/highcharts.src.js"]._modules = r;
    return r["masters/highcharts.src.js"];
  });
});

var highchartsGantt_src = createCommonjsModule(function (module) {

  (function (root, factory) {
    if ( module.exports) {
      factory['default'] = factory;
      module.exports = root.document ? factory(root) : factory;
    } else {
      if (root.Highcharts) {
        root.Highcharts.error(16, true);
      }

      root.Highcharts = factory(root);
    }
  })(typeof window !== 'undefined' ? window : commonjsGlobal, function (win) {
    var _modules = {};

    function _registerModule(obj, path, args, fn) {
      if (!obj.hasOwnProperty(path)) {
        obj[path] = fn.apply(null, args);
      }
    }

    _registerModule(_modules, 'parts/Globals.js', [], function () {
      var glob = typeof win !== 'undefined' ? win : typeof window !== 'undefined' ? window : {},
          doc = glob.document,
          SVG_NS = 'http://www.w3.org/2000/svg',
          userAgent = glob.navigator && glob.navigator.userAgent || '',
          svg = doc && doc.createElementNS && !!doc.createElementNS(SVG_NS, 'svg').createSVGRect,
          isMS = /(edge|msie|trident)/i.test(userAgent) && !glob.opera,
          isFirefox = userAgent.indexOf('Firefox') !== -1,
          isChrome = userAgent.indexOf('Chrome') !== -1,
          hasBidiBug = isFirefox && parseInt(userAgent.split('Firefox/')[1], 10) < 4;
      var H = {
        product: 'Highcharts',
        version: '8.0.4',
        deg2rad: Math.PI * 2 / 360,
        doc: doc,
        hasBidiBug: hasBidiBug,
        hasTouch: !!glob.TouchEvent,
        isMS: isMS,
        isWebKit: userAgent.indexOf('AppleWebKit') !== -1,
        isFirefox: isFirefox,
        isChrome: isChrome,
        isSafari: !isChrome && userAgent.indexOf('Safari') !== -1,
        isTouchDevice: /(Mobile|Android|Windows Phone)/.test(userAgent),
        SVG_NS: SVG_NS,
        chartCount: 0,
        seriesTypes: {},
        symbolSizes: {},
        svg: svg,
        win: glob,
        marginNames: ['plotTop', 'marginRight', 'marginBottom', 'plotLeft'],
        noop: function noop() {},
        charts: [],
        dateFormats: {}
      };
      return H;
    });

    _registerModule(_modules, 'parts/Utilities.js', [_modules['parts/Globals.js']], function (H) {
      H.timers = [];
      var charts = H.charts,
          doc = H.doc,
          win = H.win;

      var error = H.error = function (code, stop, chart, params) {
        var isCode = isNumber(code),
            message = isCode ? "Highcharts error #" + code + ": www.highcharts.com/errors/" + code + "/" : code.toString(),
            defaultHandler = function defaultHandler() {
          if (stop) {
            throw new Error(message);
          }

          if (win.console) {
            console.log(message);
          }
        };

        if (typeof params !== 'undefined') {
          var additionalMessages_1 = '';

          if (isCode) {
            message += '?';
          }

          H.objectEach(params, function (value, key) {
            additionalMessages_1 += '\n' + key + ': ' + value;

            if (isCode) {
              message += encodeURI(key) + '=' + encodeURI(value);
            }
          });
          message += additionalMessages_1;
        }

        if (chart) {
          H.fireEvent(chart, 'displayError', {
            code: code,
            message: message,
            params: params
          }, defaultHandler);
        } else {
          defaultHandler();
        }
      };

      var Fx = function () {
        function Fx(elem, options, prop) {
          this.options = options;
          this.elem = elem;
          this.prop = prop;
        }

        Fx.prototype.dSetter = function () {
          var start = this.paths[0],
              end = this.paths[1],
              ret = [],
              now = this.now,
              i = start.length,
              startVal;

          if (now === 1) {
            ret = this.toD;
          } else if (i === end.length && now < 1) {
            while (i--) {
              startVal = parseFloat(start[i]);
              ret[i] = isNaN(startVal) || end[i - 4] === 'A' || end[i - 5] === 'A' ? end[i] : now * parseFloat('' + (end[i] - startVal)) + startVal;
            }
          } else {
            ret = end;
          }

          this.elem.attr('d', ret, null, true);
        };

        Fx.prototype.update = function () {
          var elem = this.elem,
              prop = this.prop,
              now = this.now,
              step = this.options.step;

          if (this[prop + 'Setter']) {
            this[prop + 'Setter']();
          } else if (elem.attr) {
            if (elem.element) {
              elem.attr(prop, now, null, true);
            }
          } else {
            elem.style[prop] = now + this.unit;
          }

          if (step) {
            step.call(elem, now, this);
          }
        };

        Fx.prototype.run = function (from, to, unit) {
          var self = this,
              options = self.options,
              timer = function timer(gotoEnd) {
            return timer.stopped ? false : self.step(gotoEnd);
          },
              requestAnimationFrame = win.requestAnimationFrame || function (step) {
            setTimeout(step, 13);
          },
              step = function step() {
            for (var i = 0; i < H.timers.length; i++) {
              if (!H.timers[i]()) {
                H.timers.splice(i--, 1);
              }
            }

            if (H.timers.length) {
              requestAnimationFrame(step);
            }
          };

          if (from === to && !this.elem['forceAnimate:' + this.prop]) {
            delete options.curAnim[this.prop];

            if (options.complete && Object.keys(options.curAnim).length === 0) {
              options.complete.call(this.elem);
            }
          } else {
            this.startTime = +new Date();
            this.start = from;
            this.end = to;
            this.unit = unit;
            this.now = this.start;
            this.pos = 0;
            timer.elem = this.elem;
            timer.prop = this.prop;

            if (timer() && H.timers.push(timer) === 1) {
              requestAnimationFrame(step);
            }
          }
        };

        Fx.prototype.step = function (gotoEnd) {
          var t = +new Date(),
              ret,
              done,
              options = this.options,
              elem = this.elem,
              complete = options.complete,
              duration = options.duration,
              curAnim = options.curAnim;

          if (elem.attr && !elem.element) {
            ret = false;
          } else if (gotoEnd || t >= duration + this.startTime) {
            this.now = this.end;
            this.pos = 1;
            this.update();
            curAnim[this.prop] = true;
            done = true;
            objectEach(curAnim, function (val) {
              if (val !== true) {
                done = false;
              }
            });

            if (done && complete) {
              complete.call(elem);
            }

            ret = false;
          } else {
            this.pos = options.easing((t - this.startTime) / duration);
            this.now = this.start + (this.end - this.start) * this.pos;
            this.update();
            ret = true;
          }

          return ret;
        };

        Fx.prototype.initPath = function (elem, fromD, toD) {
          fromD = fromD || '';
          var shift,
              startX = elem.startX,
              endX = elem.endX,
              bezier = fromD.indexOf('C') > -1,
              numParams = bezier ? 7 : 3,
              fullLength,
              slice,
              i,
              start = fromD.split(' '),
              end = toD.slice(),
              isArea = elem.isArea,
              positionFactor = isArea ? 2 : 1,
              reverse;

          function sixify(arr) {
            var isOperator, nextIsOperator;
            i = arr.length;

            while (i--) {
              isOperator = arr[i] === 'M' || arr[i] === 'L';
              nextIsOperator = /[a-zA-Z]/.test(arr[i + 3]);

              if (isOperator && nextIsOperator) {
                arr.splice(i + 1, 0, arr[i + 1], arr[i + 2], arr[i + 1], arr[i + 2]);
              }
            }
          }

          function insertSlice(arr, subArr, index) {
            [].splice.apply(arr, [index, 0].concat(subArr));
          }

          function prepend(arr, other) {
            while (arr.length < fullLength) {
              arr[0] = other[fullLength - arr.length];
              insertSlice(arr, arr.slice(0, numParams), 0);

              if (isArea) {
                insertSlice(arr, arr.slice(arr.length - numParams), arr.length);
                i--;
              }
            }

            arr[0] = 'M';
          }

          function append(arr, other) {
            var i = (fullLength - arr.length) / numParams;

            while (i > 0 && i--) {
              slice = arr.slice().splice(arr.length / positionFactor - numParams, numParams * positionFactor);
              slice[0] = other[fullLength - numParams - i * numParams];

              if (bezier) {
                slice[numParams - 6] = slice[numParams - 2];
                slice[numParams - 5] = slice[numParams - 1];
              }

              insertSlice(arr, slice, arr.length / positionFactor);

              if (isArea) {
                i--;
              }
            }
          }

          if (bezier) {
            sixify(start);
            sixify(end);
          }

          if (startX && endX) {
            for (i = 0; i < startX.length; i++) {
              if (startX[i] === endX[0]) {
                shift = i;
                break;
              } else if (startX[0] === endX[endX.length - startX.length + i]) {
                shift = i;
                reverse = true;
                break;
              } else if (startX[startX.length - 1] === endX[endX.length - startX.length + i]) {
                shift = startX.length - i;
                break;
              }
            }

            if (typeof shift === 'undefined') {
              start = [];
            }
          }

          if (start.length && isNumber(shift)) {
            fullLength = end.length + shift * positionFactor * numParams;

            if (!reverse) {
              prepend(end, start);
              append(start, end);
            } else {
              prepend(start, end);
              append(end, start);
            }
          }

          return [start, end];
        };

        Fx.prototype.fillSetter = function () {
          H.Fx.prototype.strokeSetter.apply(this, arguments);
        };

        Fx.prototype.strokeSetter = function () {
          this.elem.attr(this.prop, H.color(this.start).tweenTo(H.color(this.end), this.pos), null, true);
        };

        return Fx;
      }();

      H.Fx = Fx;

      function merge() {
        var i,
            args = arguments,
            len,
            ret = {},
            doCopy = function doCopy(copy, original) {
          if (typeof copy !== 'object') {
            copy = {};
          }

          objectEach(original, function (value, key) {
            if (isObject(value, true) && !isClass(value) && !isDOMElement(value)) {
              copy[key] = doCopy(copy[key] || {}, value);
            } else {
              copy[key] = original[key];
            }
          });
          return copy;
        };

        if (args[0] === true) {
          ret = args[1];
          args = Array.prototype.slice.call(args, 2);
        }

        len = args.length;

        for (i = 0; i < len; i++) {
          ret = doCopy(ret, args[i]);
        }

        return ret;
      }

      H.merge = merge;

      function clamp(value, min, max) {
        return value > min ? value < max ? value : max : min;
      }

      var pInt = H.pInt = function pInt(s, mag) {
        return parseInt(s, mag || 10);
      };

      var isString = H.isString = function isString(s) {
        return typeof s === 'string';
      };

      var isArray = H.isArray = function isArray(obj) {
        var str = Object.prototype.toString.call(obj);
        return str === '[object Array]' || str === '[object Array Iterator]';
      };

      var isObject = H.isObject = function isObject(obj, strict) {
        return !!obj && typeof obj === 'object' && (!strict || !isArray(obj));
      };

      var isDOMElement = H.isDOMElement = function isDOMElement(obj) {
        return isObject(obj) && typeof obj.nodeType === 'number';
      };

      var isClass = H.isClass = function isClass(obj) {
        var c = obj && obj.constructor;
        return !!(isObject(obj, true) && !isDOMElement(obj) && c && c.name && c.name !== 'Object');
      };

      var isNumber = H.isNumber = function isNumber(n) {
        return typeof n === 'number' && !isNaN(n) && n < Infinity && n > -Infinity;
      };

      var erase = H.erase = function erase(arr, item) {
        var i = arr.length;

        while (i--) {
          if (arr[i] === item) {
            arr.splice(i, 1);
            break;
          }
        }
      };

      var defined = H.defined = function defined(obj) {
        return typeof obj !== 'undefined' && obj !== null;
      };

      function attr(elem, prop, value) {
        var ret;

        if (isString(prop)) {
          if (defined(value)) {
            elem.setAttribute(prop, value);
          } else if (elem && elem.getAttribute) {
            ret = elem.getAttribute(prop);

            if (!ret && prop === 'class') {
              ret = elem.getAttribute(prop + 'Name');
            }
          }
        } else {
          objectEach(prop, function (val, key) {
            elem.setAttribute(key, val);
          });
        }

        return ret;
      }

      H.attr = attr;

      var splat = H.splat = function splat(obj) {
        return isArray(obj) ? obj : [obj];
      };

      var syncTimeout = H.syncTimeout = function syncTimeout(fn, delay, context) {
        if (delay > 0) {
          return setTimeout(fn, delay, context);
        }

        fn.call(0, context);
        return -1;
      };

      var internalClearTimeout = H.clearTimeout = function (id) {
        if (defined(id)) {
          clearTimeout(id);
        }
      };

      var extend = H.extend = function extend(a, b) {
        var n;

        if (!a) {
          a = {};
        }

        for (n in b) {
          a[n] = b[n];
        }

        return a;
      };

      function pick() {
        var args = arguments;
        var length = args.length;

        for (var i = 0; i < length; i++) {
          var arg = args[i];

          if (typeof arg !== 'undefined' && arg !== null) {
            return arg;
          }
        }
      }

      H.pick = pick;

      var css = H.css = function css(el, styles) {
        if (H.isMS && !H.svg) {
          if (styles && typeof styles.opacity !== 'undefined') {
            styles.filter = 'alpha(opacity=' + styles.opacity * 100 + ')';
          }
        }

        extend(el.style, styles);
      };

      var createElement = H.createElement = function createElement(tag, attribs, styles, parent, nopad) {
        var el = doc.createElement(tag);

        if (attribs) {
          extend(el, attribs);
        }

        if (nopad) {
          css(el, {
            padding: '0',
            border: 'none',
            margin: '0'
          });
        }

        if (styles) {
          css(el, styles);
        }

        if (parent) {
          parent.appendChild(el);
        }

        return el;
      };

      var extendClass = H.extendClass = function extendClass(parent, members) {
        var obj = function obj() {};

        obj.prototype = new parent();
        extend(obj.prototype, members);
        return obj;
      };

      var pad = H.pad = function pad(number, length, padder) {
        return new Array((length || 2) + 1 - String(number).replace('-', '').length).join(padder || '0') + number;
      };

      var relativeLength = H.relativeLength = function relativeLength(value, base, offset) {
        return /%$/.test(value) ? base * parseFloat(value) / 100 + (offset || 0) : parseFloat(value);
      };

      var wrap = H.wrap = function wrap(obj, method, func) {
        var proceed = obj[method];

        obj[method] = function () {
          var args = Array.prototype.slice.call(arguments),
              outerArgs = arguments,
              ctx = this,
              ret;

          ctx.proceed = function () {
            proceed.apply(ctx, arguments.length ? arguments : outerArgs);
          };

          args.unshift(proceed);
          ret = func.apply(this, args);
          ctx.proceed = null;
          return ret;
        };
      };

      var format = H.format = function (str, ctx, chart) {
        var splitter = '{',
            isInside = false,
            segment,
            valueAndFormat,
            ret = [],
            val,
            index;
        var floatRegex = /f$/;
        var decRegex = /\.([0-9])/;
        var lang = H.defaultOptions.lang;
        var time = chart && chart.time || H.time;
        var numberFormatter = chart && chart.numberFormatter || numberFormat;

        while (str) {
          index = str.indexOf(splitter);

          if (index === -1) {
            break;
          }

          segment = str.slice(0, index);

          if (isInside) {
            valueAndFormat = segment.split(':');
            val = getNestedProperty(valueAndFormat.shift() || '', ctx);

            if (valueAndFormat.length && typeof val === 'number') {
              segment = valueAndFormat.join(':');

              if (floatRegex.test(segment)) {
                var decimals = parseInt((segment.match(decRegex) || ['', '-1'])[1], 10);

                if (val !== null) {
                  val = numberFormatter(val, decimals, lang.decimalPoint, segment.indexOf(',') > -1 ? lang.thousandsSep : '');
                }
              } else {
                val = time.dateFormat(segment, val);
              }
            }

            ret.push(val);
          } else {
            ret.push(segment);
          }

          str = str.slice(index + 1);
          isInside = !isInside;
          splitter = isInside ? '}' : '{';
        }

        ret.push(str);
        return ret.join('');
      };

      var getMagnitude = H.getMagnitude = function (num) {
        return Math.pow(10, Math.floor(Math.log(num) / Math.LN10));
      };

      var normalizeTickInterval = H.normalizeTickInterval = function (interval, multiples, magnitude, allowDecimals, hasTickAmount) {
        var normalized,
            i,
            retInterval = interval;
        magnitude = pick(magnitude, 1);
        normalized = interval / magnitude;

        if (!multiples) {
          multiples = hasTickAmount ? [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10] : [1, 2, 2.5, 5, 10];

          if (allowDecimals === false) {
            if (magnitude === 1) {
              multiples = multiples.filter(function (num) {
                return num % 1 === 0;
              });
            } else if (magnitude <= 0.1) {
              multiples = [1 / magnitude];
            }
          }
        }

        for (i = 0; i < multiples.length; i++) {
          retInterval = multiples[i];

          if (hasTickAmount && retInterval * magnitude >= interval || !hasTickAmount && normalized <= (multiples[i] + (multiples[i + 1] || multiples[i])) / 2) {
            break;
          }
        }

        retInterval = correctFloat(retInterval * magnitude, -Math.round(Math.log(0.001) / Math.LN10));
        return retInterval;
      };

      var stableSort = H.stableSort = function stableSort(arr, sortFunction) {
        var length = arr.length,
            sortValue,
            i;

        for (i = 0; i < length; i++) {
          arr[i].safeI = i;
        }

        arr.sort(function (a, b) {
          sortValue = sortFunction(a, b);
          return sortValue === 0 ? a.safeI - b.safeI : sortValue;
        });

        for (i = 0; i < length; i++) {
          delete arr[i].safeI;
        }
      };

      var arrayMin = H.arrayMin = function arrayMin(data) {
        var i = data.length,
            min = data[0];

        while (i--) {
          if (data[i] < min) {
            min = data[i];
          }
        }

        return min;
      };

      var arrayMax = H.arrayMax = function arrayMax(data) {
        var i = data.length,
            max = data[0];

        while (i--) {
          if (data[i] > max) {
            max = data[i];
          }
        }

        return max;
      };

      var destroyObjectProperties = H.destroyObjectProperties = function destroyObjectProperties(obj, except) {
        objectEach(obj, function (val, n) {
          if (val && val !== except && val.destroy) {
            val.destroy();
          }

          delete obj[n];
        });
      };

      var discardElement = H.discardElement = function discardElement(element) {
        var garbageBin = H.garbageBin;

        if (!garbageBin) {
          garbageBin = createElement('div');
        }

        if (element) {
          garbageBin.appendChild(element);
        }

        garbageBin.innerHTML = '';
      };

      var correctFloat = H.correctFloat = function correctFloat(num, prec) {
        return parseFloat(num.toPrecision(prec || 14));
      };

      var setAnimation = H.setAnimation = function setAnimation(animation, chart) {
        chart.renderer.globalAnimation = pick(animation, chart.options.chart.animation, true);
      };

      var animObject = H.animObject = function animObject(animation) {
        return isObject(animation) ? merge(animation) : {
          duration: animation ? 500 : 0
        };
      };

      var timeUnits = H.timeUnits = {
        millisecond: 1,
        second: 1000,
        minute: 60000,
        hour: 3600000,
        day: 24 * 3600000,
        week: 7 * 24 * 3600000,
        month: 28 * 24 * 3600000,
        year: 364 * 24 * 3600000
      };

      var numberFormat = H.numberFormat = function numberFormat(number, decimals, decimalPoint, thousandsSep) {
        number = +number || 0;
        decimals = +decimals;
        var lang = H.defaultOptions.lang,
            origDec = (number.toString().split('.')[1] || '').split('e')[0].length,
            strinteger,
            thousands,
            ret,
            roundedNumber,
            exponent = number.toString().split('e'),
            fractionDigits;

        if (decimals === -1) {
          decimals = Math.min(origDec, 20);
        } else if (!isNumber(decimals)) {
          decimals = 2;
        } else if (decimals && exponent[1] && exponent[1] < 0) {
          fractionDigits = decimals + +exponent[1];

          if (fractionDigits >= 0) {
            exponent[0] = (+exponent[0]).toExponential(fractionDigits).split('e')[0];
            decimals = fractionDigits;
          } else {
            exponent[0] = exponent[0].split('.')[0] || 0;

            if (decimals < 20) {
              number = (exponent[0] * Math.pow(10, exponent[1])).toFixed(decimals);
            } else {
              number = 0;
            }

            exponent[1] = 0;
          }
        }

        roundedNumber = (Math.abs(exponent[1] ? exponent[0] : number) + Math.pow(10, -Math.max(decimals, origDec) - 1)).toFixed(decimals);
        strinteger = String(pInt(roundedNumber));
        thousands = strinteger.length > 3 ? strinteger.length % 3 : 0;
        decimalPoint = pick(decimalPoint, lang.decimalPoint);
        thousandsSep = pick(thousandsSep, lang.thousandsSep);
        ret = number < 0 ? '-' : '';
        ret += thousands ? strinteger.substr(0, thousands) + thousandsSep : '';
        ret += strinteger.substr(thousands).replace(/(\d{3})(?=\d)/g, '$1' + thousandsSep);

        if (decimals) {
          ret += decimalPoint + roundedNumber.slice(-decimals);
        }

        if (exponent[1] && +ret !== 0) {
          ret += 'e' + exponent[1];
        }

        return ret;
      };

      Math.easeInOutSine = function (pos) {
        return -0.5 * (Math.cos(Math.PI * pos) - 1);
      };

      function getNestedProperty(path, obj) {
        if (!path) {
          return obj;
        }

        var pathElements = path.split('.').reverse();
        var subProperty = obj;

        if (pathElements.length === 1) {
          return subProperty[path];
        }

        var pathElement = pathElements.pop();

        while (typeof pathElement !== 'undefined' && typeof subProperty !== 'undefined' && subProperty !== null) {
          subProperty = subProperty[pathElement];
          pathElement = pathElements.pop();
        }

        return subProperty;
      }

      var getStyle = H.getStyle = function (el, prop, toInt) {
        var style;

        if (prop === 'width') {
          var offsetWidth = Math.min(el.offsetWidth, el.scrollWidth);
          var boundingClientRectWidth = el.getBoundingClientRect && el.getBoundingClientRect().width;

          if (boundingClientRectWidth < offsetWidth && boundingClientRectWidth >= offsetWidth - 1) {
            offsetWidth = Math.floor(boundingClientRectWidth);
          }

          return Math.max(0, offsetWidth - H.getStyle(el, 'padding-left') - H.getStyle(el, 'padding-right'));
        }

        if (prop === 'height') {
          return Math.max(0, Math.min(el.offsetHeight, el.scrollHeight) - H.getStyle(el, 'padding-top') - H.getStyle(el, 'padding-bottom'));
        }

        if (!win.getComputedStyle) {
          error(27, true);
        }

        style = win.getComputedStyle(el, undefined);

        if (style) {
          style = style.getPropertyValue(prop);

          if (pick(toInt, prop !== 'opacity')) {
            style = pInt(style);
          }
        }

        return style;
      };

      var inArray = H.inArray = function (item, arr, fromIndex) {
        return arr.indexOf(item, fromIndex);
      };

      var find = H.find = Array.prototype.find ? function (arr, callback) {
        return arr.find(callback);
      } : function (arr, callback) {
        var i,
            length = arr.length;

        for (i = 0; i < length; i++) {
          if (callback(arr[i], i)) {
            return arr[i];
          }
        }
      };
      H.keys = Object.keys;

      var offset = H.offset = function offset(el) {
        var docElem = doc.documentElement,
            box = el.parentElement || el.parentNode ? el.getBoundingClientRect() : {
          top: 0,
          left: 0
        };
        return {
          top: box.top + (win.pageYOffset || docElem.scrollTop) - (docElem.clientTop || 0),
          left: box.left + (win.pageXOffset || docElem.scrollLeft) - (docElem.clientLeft || 0)
        };
      };

      var stop = H.stop = function (el, prop) {
        var i = H.timers.length;

        while (i--) {
          if (H.timers[i].elem === el && (!prop || prop === H.timers[i].prop)) {
            H.timers[i].stopped = true;
          }
        }
      };

      var objectEach = H.objectEach = function objectEach(obj, fn, ctx) {
        for (var key in obj) {
          if (Object.hasOwnProperty.call(obj, key)) {
            fn.call(ctx || obj[key], obj[key], key, obj);
          }
        }
      };

      objectEach({
        map: 'map',
        each: 'forEach',
        grep: 'filter',
        reduce: 'reduce',
        some: 'some'
      }, function (val, key) {
        H[key] = function (arr) {
          return Array.prototype[val].apply(arr, [].slice.call(arguments, 1));
        };
      });

      var addEvent = H.addEvent = function (el, type, fn, options) {
        if (options === void 0) {
          options = {};
        }

        var events,
            addEventListener = el.addEventListener || H.addEventListenerPolyfill;

        if (typeof el === 'function' && el.prototype) {
          events = el.prototype.protoEvents = el.prototype.protoEvents || {};
        } else {
          events = el.hcEvents = el.hcEvents || {};
        }

        if (H.Point && el instanceof H.Point && el.series && el.series.chart) {
          el.series.chart.runTrackerClick = true;
        }

        if (addEventListener) {
          addEventListener.call(el, type, fn, false);
        }

        if (!events[type]) {
          events[type] = [];
        }

        var eventObject = {
          fn: fn,
          order: typeof options.order === 'number' ? options.order : Infinity
        };
        events[type].push(eventObject);
        events[type].sort(function (a, b) {
          return a.order - b.order;
        });
        return function () {
          removeEvent(el, type, fn);
        };
      };

      var removeEvent = H.removeEvent = function removeEvent(el, type, fn) {
        var events;

        function removeOneEvent(type, fn) {
          var removeEventListener = el.removeEventListener || H.removeEventListenerPolyfill;

          if (removeEventListener) {
            removeEventListener.call(el, type, fn, false);
          }
        }

        function removeAllEvents(eventCollection) {
          var types, len;

          if (!el.nodeName) {
            return;
          }

          if (type) {
            types = {};
            types[type] = true;
          } else {
            types = eventCollection;
          }

          objectEach(types, function (val, n) {
            if (eventCollection[n]) {
              len = eventCollection[n].length;

              while (len--) {
                removeOneEvent(n, eventCollection[n][len].fn);
              }
            }
          });
        }

        ['protoEvents', 'hcEvents'].forEach(function (coll, i) {
          var eventElem = i ? el : el.prototype;
          var eventCollection = eventElem && eventElem[coll];

          if (eventCollection) {
            if (type) {
              events = eventCollection[type] || [];

              if (fn) {
                eventCollection[type] = events.filter(function (obj) {
                  return fn !== obj.fn;
                });
                removeOneEvent(type, fn);
              } else {
                removeAllEvents(eventCollection);
                eventCollection[type] = [];
              }
            } else {
              removeAllEvents(eventCollection);
              eventElem[coll] = {};
            }
          }
        });
      };

      var fireEvent = H.fireEvent = function (el, type, eventArguments, defaultFunction) {
        var e, i;
        eventArguments = eventArguments || {};

        if (doc.createEvent && (el.dispatchEvent || el.fireEvent)) {
          e = doc.createEvent('Events');
          e.initEvent(type, true, true);
          extend(e, eventArguments);

          if (el.dispatchEvent) {
            el.dispatchEvent(e);
          } else {
            el.fireEvent(type, e);
          }
        } else {
          if (!eventArguments.target) {
            extend(eventArguments, {
              preventDefault: function preventDefault() {
                eventArguments.defaultPrevented = true;
              },
              target: el,
              type: type
            });
          }

          var fireInOrder = function fireInOrder(protoEvents, hcEvents) {
            if (protoEvents === void 0) {
              protoEvents = [];
            }

            if (hcEvents === void 0) {
              hcEvents = [];
            }

            var iA = 0;
            var iB = 0;
            var length = protoEvents.length + hcEvents.length;

            for (i = 0; i < length; i++) {
              var obj = !protoEvents[iA] ? hcEvents[iB++] : !hcEvents[iB] ? protoEvents[iA++] : protoEvents[iA].order <= hcEvents[iB].order ? protoEvents[iA++] : hcEvents[iB++];

              if (obj.fn.call(el, eventArguments) === false) {
                eventArguments.preventDefault();
              }
            }
          };

          fireInOrder(el.protoEvents && el.protoEvents[type], el.hcEvents && el.hcEvents[type]);
        }

        if (defaultFunction && !eventArguments.defaultPrevented) {
          defaultFunction.call(el, eventArguments);
        }
      };

      var animate = H.animate = function (el, params, opt) {
        var start,
            unit = '',
            end,
            fx,
            args;

        if (!isObject(opt)) {
          args = arguments;
          opt = {
            duration: args[2],
            easing: args[3],
            complete: args[4]
          };
        }

        if (!isNumber(opt.duration)) {
          opt.duration = 400;
        }

        opt.easing = typeof opt.easing === 'function' ? opt.easing : Math[opt.easing] || Math.easeInOutSine;
        opt.curAnim = merge(params);
        objectEach(params, function (val, prop) {
          stop(el, prop);
          fx = new Fx(el, opt, prop);
          end = null;

          if (prop === 'd') {
            fx.paths = fx.initPath(el, el.d, params.d);
            fx.toD = params.d;
            start = 0;
            end = 1;
          } else if (el.attr) {
            start = el.attr(prop);
          } else {
            start = parseFloat(getStyle(el, prop)) || 0;

            if (prop !== 'opacity') {
              unit = 'px';
            }
          }

          if (!end) {
            end = val;
          }

          if (end && end.match && end.match('px')) {
            end = end.replace(/px/g, '');
          }

          fx.run(start, end, unit);
        });
      };

      var seriesType = H.seriesType = function (type, parent, options, props, pointProps) {
        var defaultOptions = H.getOptions(),
            seriesTypes = H.seriesTypes;
        defaultOptions.plotOptions[type] = merge(defaultOptions.plotOptions[parent], options);
        seriesTypes[type] = extendClass(seriesTypes[parent] || function () {}, props);
        seriesTypes[type].prototype.type = type;

        if (pointProps) {
          seriesTypes[type].prototype.pointClass = extendClass(H.Point, pointProps);
        }

        return seriesTypes[type];
      };

      var uniqueKey = H.uniqueKey = function () {
        var uniqueKeyHash = Math.random().toString(36).substring(2, 9),
            idCounter = 0;
        return function () {
          return 'highcharts-' + uniqueKeyHash + '-' + idCounter++;
        };
      }();

      var isFunction = H.isFunction = function (obj) {
        return typeof obj === 'function';
      };

      if (win.jQuery) {
        win.jQuery.fn.highcharts = function () {
          var args = [].slice.call(arguments);

          if (this[0]) {
            if (args[0]) {
              new H[isString(args[0]) ? args.shift() : 'Chart'](this[0], args[0], args[1]);
              return this;
            }

            return charts[attr(this[0], 'data-highcharts-chart')];
          }
        };
      }

      var utilitiesModule = {
        Fx: Fx,
        addEvent: addEvent,
        animate: animate,
        animObject: animObject,
        arrayMax: arrayMax,
        arrayMin: arrayMin,
        attr: attr,
        clamp: clamp,
        clearTimeout: internalClearTimeout,
        correctFloat: correctFloat,
        createElement: createElement,
        css: css,
        defined: defined,
        destroyObjectProperties: destroyObjectProperties,
        discardElement: discardElement,
        erase: erase,
        error: error,
        extend: extend,
        extendClass: extendClass,
        find: find,
        fireEvent: fireEvent,
        format: format,
        getMagnitude: getMagnitude,
        getNestedProperty: getNestedProperty,
        getStyle: getStyle,
        inArray: inArray,
        isArray: isArray,
        isClass: isClass,
        isDOMElement: isDOMElement,
        isFunction: isFunction,
        isNumber: isNumber,
        isObject: isObject,
        isString: isString,
        merge: merge,
        normalizeTickInterval: normalizeTickInterval,
        numberFormat: numberFormat,
        objectEach: objectEach,
        offset: offset,
        pad: pad,
        pick: pick,
        pInt: pInt,
        relativeLength: relativeLength,
        removeEvent: removeEvent,
        seriesType: seriesType,
        setAnimation: setAnimation,
        splat: splat,
        stableSort: stableSort,
        stop: stop,
        syncTimeout: syncTimeout,
        timeUnits: timeUnits,
        uniqueKey: uniqueKey,
        wrap: wrap
      };
      return utilitiesModule;
    });

    _registerModule(_modules, 'parts/Color.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var isNumber = U.isNumber,
          merge = U.merge,
          pInt = U.pInt;

      var Color = function () {
        function Color(input) {
          this.parsers = [{
            regex: /rgba\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]?(?:\.[0-9]+)?)\s*\)/,
            parse: function parse(result) {
              return [pInt(result[1]), pInt(result[2]), pInt(result[3]), parseFloat(result[4], 10)];
            }
          }, {
            regex: /rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)/,
            parse: function parse(result) {
              return [pInt(result[1]), pInt(result[2]), pInt(result[3]), 1];
            }
          }];
          this.rgba = [];

          if (!(this instanceof Color)) {
            return new Color(input);
          }

          this.init(input);
        }

        Color.parse = function (input) {
          return new Color(input);
        };

        Color.prototype.init = function (input) {
          var result, rgba, i, parser, len;
          this.input = input = Color.names[input && input.toLowerCase ? input.toLowerCase() : ''] || input;

          if (input && input.stops) {
            this.stops = input.stops.map(function (stop) {
              return new Color(stop[1]);
            });
          } else {
            if (input && input.charAt && input.charAt() === '#') {
              len = input.length;
              input = parseInt(input.substr(1), 16);

              if (len === 7) {
                rgba = [(input & 0xFF0000) >> 16, (input & 0xFF00) >> 8, input & 0xFF, 1];
              } else if (len === 4) {
                rgba = [(input & 0xF00) >> 4 | (input & 0xF00) >> 8, (input & 0xF0) >> 4 | input & 0xF0, (input & 0xF) << 4 | input & 0xF, 1];
              }
            }

            if (!rgba) {
              i = this.parsers.length;

              while (i-- && !rgba) {
                parser = this.parsers[i];
                result = parser.regex.exec(input);

                if (result) {
                  rgba = parser.parse(result);
                }
              }
            }
          }

          this.rgba = rgba || [];
        };

        Color.prototype.get = function (format) {
          var input = this.input,
              rgba = this.rgba,
              ret;

          if (typeof this.stops !== 'undefined') {
            ret = merge(input);
            ret.stops = [].concat(ret.stops);
            this.stops.forEach(function (stop, i) {
              ret.stops[i] = [ret.stops[i][0], stop.get(format)];
            });
          } else if (rgba && isNumber(rgba[0])) {
            if (format === 'rgb' || !format && rgba[3] === 1) {
              ret = 'rgb(' + rgba[0] + ',' + rgba[1] + ',' + rgba[2] + ')';
            } else if (format === 'a') {
              ret = rgba[3];
            } else {
              ret = 'rgba(' + rgba.join(',') + ')';
            }
          } else {
            ret = input;
          }

          return ret;
        };

        Color.prototype.brighten = function (alpha) {
          var i,
              rgba = this.rgba;

          if (this.stops) {
            this.stops.forEach(function (stop) {
              stop.brighten(alpha);
            });
          } else if (isNumber(alpha) && alpha !== 0) {
            for (i = 0; i < 3; i++) {
              rgba[i] += pInt(alpha * 255);

              if (rgba[i] < 0) {
                rgba[i] = 0;
              }

              if (rgba[i] > 255) {
                rgba[i] = 255;
              }
            }
          }

          return this;
        };

        Color.prototype.setOpacity = function (alpha) {
          this.rgba[3] = alpha;
          return this;
        };

        Color.prototype.tweenTo = function (to, pos) {
          var fromRgba = this.rgba,
              toRgba = to.rgba,
              hasAlpha,
              ret;

          if (!toRgba.length || !fromRgba || !fromRgba.length) {
            ret = to.input || 'none';
          } else {
            hasAlpha = toRgba[3] !== 1 || fromRgba[3] !== 1;
            ret = (hasAlpha ? 'rgba(' : 'rgb(') + Math.round(toRgba[0] + (fromRgba[0] - toRgba[0]) * (1 - pos)) + ',' + Math.round(toRgba[1] + (fromRgba[1] - toRgba[1]) * (1 - pos)) + ',' + Math.round(toRgba[2] + (fromRgba[2] - toRgba[2]) * (1 - pos)) + (hasAlpha ? ',' + (toRgba[3] + (fromRgba[3] - toRgba[3]) * (1 - pos)) : '') + ')';
          }

          return ret;
        };

        Color.names = {
          white: '#ffffff',
          black: '#000000'
        };
        return Color;
      }();

      H.Color = Color;
      H.color = Color.parse;
      return H.Color;
    });

    _registerModule(_modules, 'parts/SvgRenderer.js', [_modules['parts/Globals.js'], _modules['parts/Color.js'], _modules['parts/Utilities.js']], function (H, Color, U) {
      var color = Color.parse;
      var addEvent = U.addEvent,
          _animate = U.animate,
          animObject = U.animObject,
          attr = U.attr,
          createElement = U.createElement,
          _css = U.css,
          defined = U.defined,
          destroyObjectProperties = U.destroyObjectProperties,
          erase = U.erase,
          extend = U.extend,
          inArray = U.inArray,
          isArray = U.isArray,
          isNumber = U.isNumber,
          isObject = U.isObject,
          isString = U.isString,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick,
          pInt = U.pInt,
          removeEvent = U.removeEvent,
          splat = U.splat,
          stop = U.stop,
          uniqueKey = U.uniqueKey;
      var SVGElement,
          SVGRenderer,
          charts = H.charts,
          deg2rad = H.deg2rad,
          doc = H.doc,
          hasTouch = H.hasTouch,
          isFirefox = H.isFirefox,
          isMS = H.isMS,
          isWebKit = H.isWebKit,
          noop = H.noop,
          svg = H.svg,
          SVG_NS = H.SVG_NS,
          symbolSizes = H.symbolSizes,
          win = H.win;

      SVGElement = H.SVGElement = function () {
        return this;
      };

      extend(SVGElement.prototype, {
        opacity: 1,
        SVG_NS: SVG_NS,
        textProps: ['direction', 'fontSize', 'fontWeight', 'fontFamily', 'fontStyle', 'color', 'lineHeight', 'width', 'textAlign', 'textDecoration', 'textOverflow', 'textOutline', 'cursor'],
        init: function init(renderer, nodeName) {
          this.element = nodeName === 'span' ? createElement(nodeName) : doc.createElementNS(this.SVG_NS, nodeName);
          this.renderer = renderer;
          H.fireEvent(this, 'afterInit');
        },
        animate: function animate(params, options, complete) {
          var animOptions = animObject(pick(options, this.renderer.globalAnimation, true));

          if (pick(doc.hidden, doc.msHidden, doc.webkitHidden, false)) {
            animOptions.duration = 0;
          }

          if (animOptions.duration !== 0) {
            if (complete) {
              animOptions.complete = complete;
            }

            _animate(this, params, animOptions);
          } else {
            this.attr(params, void 0, complete);
            objectEach(params, function (val, prop) {
              if (animOptions.step) {
                animOptions.step.call(this, val, {
                  prop: prop,
                  pos: 1
                });
              }
            }, this);
          }

          return this;
        },
        complexColor: function complexColor(colorOptions, prop, elem) {
          var renderer = this.renderer,
              colorObject,
              gradName,
              gradAttr,
              radAttr,
              gradients,
              gradientObject,
              stops,
              stopColor,
              stopOpacity,
              radialReference,
              id,
              key = [],
              value;
          H.fireEvent(this.renderer, 'complexColor', {
            args: arguments
          }, function () {
            if (colorOptions.radialGradient) {
              gradName = 'radialGradient';
            } else if (colorOptions.linearGradient) {
              gradName = 'linearGradient';
            }

            if (gradName) {
              gradAttr = colorOptions[gradName];
              gradients = renderer.gradients;
              stops = colorOptions.stops;
              radialReference = elem.radialReference;

              if (isArray(gradAttr)) {
                colorOptions[gradName] = gradAttr = {
                  x1: gradAttr[0],
                  y1: gradAttr[1],
                  x2: gradAttr[2],
                  y2: gradAttr[3],
                  gradientUnits: 'userSpaceOnUse'
                };
              }

              if (gradName === 'radialGradient' && radialReference && !defined(gradAttr.gradientUnits)) {
                radAttr = gradAttr;
                gradAttr = merge(gradAttr, renderer.getRadialAttr(radialReference, radAttr), {
                  gradientUnits: 'userSpaceOnUse'
                });
              }

              objectEach(gradAttr, function (val, n) {
                if (n !== 'id') {
                  key.push(n, val);
                }
              });
              objectEach(stops, function (val) {
                key.push(val);
              });
              key = key.join(',');

              if (gradients[key]) {
                id = gradients[key].attr('id');
              } else {
                gradAttr.id = id = uniqueKey();
                gradients[key] = gradientObject = renderer.createElement(gradName).attr(gradAttr).add(renderer.defs);
                gradientObject.radAttr = radAttr;
                gradientObject.stops = [];
                stops.forEach(function (stop) {
                  var stopObject;

                  if (stop[1].indexOf('rgba') === 0) {
                    colorObject = color(stop[1]);
                    stopColor = colorObject.get('rgb');
                    stopOpacity = colorObject.get('a');
                  } else {
                    stopColor = stop[1];
                    stopOpacity = 1;
                  }

                  stopObject = renderer.createElement('stop').attr({
                    offset: stop[0],
                    'stop-color': stopColor,
                    'stop-opacity': stopOpacity
                  }).add(gradientObject);
                  gradientObject.stops.push(stopObject);
                });
              }

              value = 'url(' + renderer.url + '#' + id + ')';
              elem.setAttribute(prop, value);
              elem.gradient = key;

              colorOptions.toString = function () {
                return value;
              };
            }
          });
        },
        applyTextOutline: function applyTextOutline(textOutline) {
          var elem = this.element,
              tspans,
              hasContrast = textOutline.indexOf('contrast') !== -1,
              styles = {},
              color,
              strokeWidth,
              firstRealChild;

          if (hasContrast) {
            styles.textOutline = textOutline = textOutline.replace(/contrast/g, this.renderer.getContrast(elem.style.fill));
          }

          textOutline = textOutline.split(' ');
          color = textOutline[textOutline.length - 1];
          strokeWidth = textOutline[0];

          if (strokeWidth && strokeWidth !== 'none' && H.svg) {
            this.fakeTS = true;
            tspans = [].slice.call(elem.getElementsByTagName('tspan'));
            this.ySetter = this.xSetter;
            strokeWidth = strokeWidth.replace(/(^[\d\.]+)(.*?)$/g, function (match, digit, unit) {
              return 2 * digit + unit;
            });
            this.removeTextOutline(tspans);
            var isRTL_1 = elem.textContent ? /^[\u0591-\u065F\u066A-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(elem.textContent) : false;
            firstRealChild = elem.firstChild;
            tspans.forEach(function (tspan, y) {
              var clone;

              if (y === 0) {
                tspan.setAttribute('x', elem.getAttribute('x'));
                y = elem.getAttribute('y');
                tspan.setAttribute('y', y || 0);

                if (y === null) {
                  elem.setAttribute('y', 0);
                }
              }

              clone = tspan.cloneNode(true);
              attr(isRTL_1 && !isFirefox ? tspan : clone, {
                'class': 'highcharts-text-outline',
                fill: color,
                stroke: color,
                'stroke-width': strokeWidth,
                'stroke-linejoin': 'round'
              });
              elem.insertBefore(clone, firstRealChild);
            });

            if (isRTL_1 && isFirefox && tspans[0]) {
              var whitespace = tspans[0].cloneNode(true);
              whitespace.textContent = ' ';
              elem.insertBefore(whitespace, firstRealChild);
            }
          }
        },
        removeTextOutline: function removeTextOutline(tspans) {
          var i = tspans.length,
              tspan;

          while (i--) {
            tspan = tspans[i];

            if (tspan.getAttribute('class') === 'highcharts-text-outline') {
              erase(tspans, this.element.removeChild(tspan));
            }
          }
        },
        symbolCustomAttribs: ['x', 'y', 'width', 'height', 'r', 'start', 'end', 'innerR', 'anchorX', 'anchorY', 'rounded'],
        attr: function attr(hash, val, complete, continueAnimation) {
          var key,
              element = this.element,
              hasSetSymbolSize,
              ret = this,
              skipAttr,
              setter,
              symbolCustomAttribs = this.symbolCustomAttribs;

          if (typeof hash === 'string' && typeof val !== 'undefined') {
            key = hash;
            hash = {};
            hash[key] = val;
          }

          if (typeof hash === 'string') {
            ret = (this[hash + 'Getter'] || this._defaultGetter).call(this, hash, element);
          } else {
            objectEach(hash, function eachAttribute(val, key) {
              skipAttr = false;

              if (!continueAnimation) {
                stop(this, key);
              }

              if (this.symbolName && inArray(key, symbolCustomAttribs) !== -1) {
                if (!hasSetSymbolSize) {
                  this.symbolAttr(hash);
                  hasSetSymbolSize = true;
                }

                skipAttr = true;
              }

              if (this.rotation && (key === 'x' || key === 'y')) {
                this.doTransform = true;
              }

              if (!skipAttr) {
                setter = this[key + 'Setter'] || this._defaultSetter;
                setter.call(this, val, key, element);

                if (!this.styledMode && this.shadows && /^(width|height|visibility|x|y|d|transform|cx|cy|r)$/.test(key)) {
                  this.updateShadows(key, val, setter);
                }
              }
            }, this);
            this.afterSetters();
          }

          if (complete) {
            complete.call(this);
          }

          return ret;
        },
        afterSetters: function afterSetters() {
          if (this.doTransform) {
            this.updateTransform();
            this.doTransform = false;
          }
        },
        updateShadows: function updateShadows(key, value, setter) {
          var shadows = this.shadows,
              i = shadows.length;

          while (i--) {
            setter.call(shadows[i], key === 'height' ? Math.max(value - (shadows[i].cutHeight || 0), 0) : key === 'd' ? this.d : value, key, shadows[i]);
          }
        },
        addClass: function addClass(className, replace) {
          var currentClassName = replace ? '' : this.attr('class') || '';
          className = (className || '').split(/ /g).reduce(function (newClassName, name) {
            if (currentClassName.indexOf(name) === -1) {
              newClassName.push(name);
            }

            return newClassName;
          }, currentClassName ? [currentClassName] : []).join(' ');

          if (className !== currentClassName) {
            this.attr('class', className);
          }

          return this;
        },
        hasClass: function hasClass(className) {
          return (this.attr('class') || '').split(' ').indexOf(className) !== -1;
        },
        removeClass: function removeClass(className) {
          return this.attr('class', (this.attr('class') || '').replace(isString(className) ? new RegExp(" ?" + className + " ?") : className, ''));
        },
        symbolAttr: function symbolAttr(hash) {
          var wrapper = this;
          ['x', 'y', 'r', 'start', 'end', 'width', 'height', 'innerR', 'anchorX', 'anchorY', 'clockwise'].forEach(function (key) {
            wrapper[key] = pick(hash[key], wrapper[key]);
          });
          wrapper.attr({
            d: wrapper.renderer.symbols[wrapper.symbolName](wrapper.x, wrapper.y, wrapper.width, wrapper.height, wrapper)
          });
        },
        clip: function clip(clipRect) {
          return this.attr('clip-path', clipRect ? 'url(' + this.renderer.url + '#' + clipRect.id + ')' : 'none');
        },
        crisp: function crisp(rect, strokeWidth) {
          var wrapper = this,
              normalizer;
          strokeWidth = strokeWidth || rect.strokeWidth || 0;
          normalizer = Math.round(strokeWidth) % 2 / 2;
          rect.x = Math.floor(rect.x || wrapper.x || 0) + normalizer;
          rect.y = Math.floor(rect.y || wrapper.y || 0) + normalizer;
          rect.width = Math.floor((rect.width || wrapper.width || 0) - 2 * normalizer);
          rect.height = Math.floor((rect.height || wrapper.height || 0) - 2 * normalizer);

          if (defined(rect.strokeWidth)) {
            rect.strokeWidth = strokeWidth;
          }

          return rect;
        },
        css: function css(styles) {
          var oldStyles = this.styles,
              newStyles = {},
              elem = this.element,
              textWidth,
              serializedCss = '',
              hyphenate,
              hasNew = !oldStyles,
              svgPseudoProps = ['textOutline', 'textOverflow', 'width'];

          if (styles && styles.color) {
            styles.fill = styles.color;
          }

          if (oldStyles) {
            objectEach(styles, function (style, n) {
              if (style !== oldStyles[n]) {
                newStyles[n] = style;
                hasNew = true;
              }
            });
          }

          if (hasNew) {
            if (oldStyles) {
              styles = extend(oldStyles, newStyles);
            }

            if (styles) {
              if (styles.width === null || styles.width === 'auto') {
                delete this.textWidth;
              } else if (elem.nodeName.toLowerCase() === 'text' && styles.width) {
                textWidth = this.textWidth = pInt(styles.width);
              }
            }

            this.styles = styles;

            if (textWidth && !svg && this.renderer.forExport) {
              delete styles.width;
            }

            if (elem.namespaceURI === this.SVG_NS) {
              hyphenate = function hyphenate(a, b) {
                return '-' + b.toLowerCase();
              };

              objectEach(styles, function (style, n) {
                if (svgPseudoProps.indexOf(n) === -1) {
                  serializedCss += n.replace(/([A-Z])/g, hyphenate) + ':' + style + ';';
                }
              });

              if (serializedCss) {
                attr(elem, 'style', serializedCss);
              }
            } else {
              _css(elem, styles);
            }

            if (this.added) {
              if (this.element.nodeName === 'text') {
                this.renderer.buildText(this);
              }

              if (styles && styles.textOutline) {
                this.applyTextOutline(styles.textOutline);
              }
            }
          }

          return this;
        },
        getStyle: function getStyle(prop) {
          return win.getComputedStyle(this.element || this, '').getPropertyValue(prop);
        },
        strokeWidth: function strokeWidth() {
          if (!this.renderer.styledMode) {
            return this['stroke-width'] || 0;
          }

          var val = this.getStyle('stroke-width'),
              ret = 0,
              dummy;

          if (val.indexOf('px') === val.length - 2) {
            ret = pInt(val);
          } else if (val !== '') {
            dummy = doc.createElementNS(SVG_NS, 'rect');
            attr(dummy, {
              width: val,
              'stroke-width': 0
            });
            this.element.parentNode.appendChild(dummy);
            ret = dummy.getBBox().width;
            dummy.parentNode.removeChild(dummy);
          }

          return ret;
        },
        on: function on(eventType, handler) {
          var svgElement = this,
              element = svgElement.element,
              touchStartPos,
              touchEventFired;

          if (hasTouch && eventType === 'click') {
            element.ontouchstart = function (e) {
              touchStartPos = {
                clientX: e.touches[0].clientX,
                clientY: e.touches[0].clientY
              };
            };

            element.ontouchend = function (e) {
              var hasMoved = touchStartPos.clientX ? Math.sqrt(Math.pow(touchStartPos.clientX - e.changedTouches[0].clientX, 2) + Math.pow(touchStartPos.clientY - e.changedTouches[0].clientY, 2)) >= 4 : false;

              if (!hasMoved) {
                handler.call(element, e);
              }

              touchEventFired = true;
              e.preventDefault();
            };

            element.onclick = function (e) {
              if (!touchEventFired) {
                handler.call(element, e);
              }
            };
          } else {
            element['on' + eventType] = handler;
          }

          return this;
        },
        setRadialReference: function setRadialReference(coordinates) {
          var existingGradient = this.renderer.gradients[this.element.gradient];
          this.element.radialReference = coordinates;

          if (existingGradient && existingGradient.radAttr) {
            existingGradient.animate(this.renderer.getRadialAttr(coordinates, existingGradient.radAttr));
          }

          return this;
        },
        translate: function translate(x, y) {
          return this.attr({
            translateX: x,
            translateY: y
          });
        },
        invert: function invert(inverted) {
          var wrapper = this;
          wrapper.inverted = inverted;
          wrapper.updateTransform();
          return wrapper;
        },
        updateTransform: function updateTransform() {
          var wrapper = this,
              translateX = wrapper.translateX || 0,
              translateY = wrapper.translateY || 0,
              scaleX = wrapper.scaleX,
              scaleY = wrapper.scaleY,
              inverted = wrapper.inverted,
              rotation = wrapper.rotation,
              matrix = wrapper.matrix,
              element = wrapper.element,
              transform;

          if (inverted) {
            translateX += wrapper.width;
            translateY += wrapper.height;
          }

          transform = ['translate(' + translateX + ',' + translateY + ')'];

          if (defined(matrix)) {
            transform.push('matrix(' + matrix.join(',') + ')');
          }

          if (inverted) {
            transform.push('rotate(90) scale(-1,1)');
          } else if (rotation) {
            transform.push('rotate(' + rotation + ' ' + pick(this.rotationOriginX, element.getAttribute('x'), 0) + ' ' + pick(this.rotationOriginY, element.getAttribute('y') || 0) + ')');
          }

          if (defined(scaleX) || defined(scaleY)) {
            transform.push('scale(' + pick(scaleX, 1) + ' ' + pick(scaleY, 1) + ')');
          }

          if (transform.length) {
            element.setAttribute('transform', transform.join(' '));
          }
        },
        toFront: function toFront() {
          var element = this.element;
          element.parentNode.appendChild(element);
          return this;
        },
        align: function align(alignOptions, alignByTranslate, box) {
          var align,
              vAlign,
              x,
              y,
              attribs = {},
              alignTo,
              renderer = this.renderer,
              alignedObjects = renderer.alignedObjects,
              alignFactor,
              vAlignFactor;

          if (alignOptions) {
            this.alignOptions = alignOptions;
            this.alignByTranslate = alignByTranslate;

            if (!box || isString(box)) {
              this.alignTo = alignTo = box || 'renderer';
              erase(alignedObjects, this);
              alignedObjects.push(this);
              box = null;
            }
          } else {
            alignOptions = this.alignOptions;
            alignByTranslate = this.alignByTranslate;
            alignTo = this.alignTo;
          }

          box = pick(box, renderer[alignTo], renderer);
          align = alignOptions.align;
          vAlign = alignOptions.verticalAlign;
          x = (box.x || 0) + (alignOptions.x || 0);
          y = (box.y || 0) + (alignOptions.y || 0);

          if (align === 'right') {
            alignFactor = 1;
          } else if (align === 'center') {
            alignFactor = 2;
          }

          if (alignFactor) {
            x += (box.width - (alignOptions.width || 0)) / alignFactor;
          }

          attribs[alignByTranslate ? 'translateX' : 'x'] = Math.round(x);

          if (vAlign === 'bottom') {
            vAlignFactor = 1;
          } else if (vAlign === 'middle') {
            vAlignFactor = 2;
          }

          if (vAlignFactor) {
            y += (box.height - (alignOptions.height || 0)) / vAlignFactor;
          }

          attribs[alignByTranslate ? 'translateY' : 'y'] = Math.round(y);
          this[this.placed ? 'animate' : 'attr'](attribs);
          this.placed = true;
          this.alignAttr = attribs;
          return this;
        },
        getBBox: function getBBox(reload, rot) {
          var wrapper = this,
              bBox,
              renderer = wrapper.renderer,
              width,
              height,
              element = wrapper.element,
              styles = wrapper.styles,
              fontSize,
              textStr = wrapper.textStr,
              toggleTextShadowShim,
              cache = renderer.cache,
              cacheKeys = renderer.cacheKeys,
              isSVG = element.namespaceURI === wrapper.SVG_NS,
              cacheKey;
          var rotation = pick(rot, wrapper.rotation, 0);
          fontSize = renderer.styledMode ? element && SVGElement.prototype.getStyle.call(element, 'font-size') : styles && styles.fontSize;

          if (defined(textStr)) {
            cacheKey = textStr.toString();

            if (cacheKey.indexOf('<') === -1) {
              cacheKey = cacheKey.replace(/[0-9]/g, '0');
            }

            cacheKey += ['', rotation, fontSize, wrapper.textWidth, styles && styles.textOverflow].join(',');
          }

          if (cacheKey && !reload) {
            bBox = cache[cacheKey];
          }

          if (!bBox) {
            if (isSVG || renderer.forExport) {
              try {
                toggleTextShadowShim = this.fakeTS && function (display) {
                  [].forEach.call(element.querySelectorAll('.highcharts-text-outline'), function (tspan) {
                    tspan.style.display = display;
                  });
                };

                if (toggleTextShadowShim) {
                  toggleTextShadowShim('none');
                }

                bBox = element.getBBox ? extend({}, element.getBBox()) : {
                  width: element.offsetWidth,
                  height: element.offsetHeight
                };

                if (toggleTextShadowShim) {
                  toggleTextShadowShim('');
                }
              } catch (e) {
              }

              if (!bBox || bBox.width < 0) {
                bBox = {
                  width: 0,
                  height: 0
                };
              }
            } else {
              bBox = wrapper.htmlGetBBox();
            }

            if (renderer.isSVG) {
              width = bBox.width;
              height = bBox.height;

              if (isSVG) {
                bBox.height = height = {
                  '11px,17': 14,
                  '13px,20': 16
                }[styles && styles.fontSize + ',' + Math.round(height)] || height;
              }

              if (rotation) {
                var rad = rotation * deg2rad;
                bBox.width = Math.abs(height * Math.sin(rad)) + Math.abs(width * Math.cos(rad));
                bBox.height = Math.abs(height * Math.cos(rad)) + Math.abs(width * Math.sin(rad));
              }
            }

            if (cacheKey && bBox.height > 0) {
              while (cacheKeys.length > 250) {
                delete cache[cacheKeys.shift()];
              }

              if (!cache[cacheKey]) {
                cacheKeys.push(cacheKey);
              }

              cache[cacheKey] = bBox;
            }
          }

          return bBox;
        },
        show: function show(inherit) {
          return this.attr({
            visibility: inherit ? 'inherit' : 'visible'
          });
        },
        hide: function hide(hideByTranslation) {
          if (hideByTranslation) {
            this.attr({
              y: -9999
            });
          } else {
            this.attr({
              visibility: 'hidden'
            });
          }

          return this;
        },
        fadeOut: function fadeOut(duration) {
          var elemWrapper = this;
          elemWrapper.animate({
            opacity: 0
          }, {
            duration: duration || 150,
            complete: function complete() {
              elemWrapper.attr({
                y: -9999
              });
            }
          });
        },
        add: function add(parent) {
          var renderer = this.renderer,
              element = this.element,
              inserted;

          if (parent) {
            this.parentGroup = parent;
          }

          this.parentInverted = parent && parent.inverted;

          if (typeof this.textStr !== 'undefined') {
            renderer.buildText(this);
          }

          this.added = true;

          if (!parent || parent.handleZ || this.zIndex) {
            inserted = this.zIndexSetter();
          }

          if (!inserted) {
            (parent ? parent.element : renderer.box).appendChild(element);
          }

          if (this.onAdd) {
            this.onAdd();
          }

          return this;
        },
        safeRemoveChild: function safeRemoveChild(element) {
          var parentNode = element.parentNode;

          if (parentNode) {
            parentNode.removeChild(element);
          }
        },
        destroy: function destroy() {
          var wrapper = this,
              element = wrapper.element || {},
              renderer = wrapper.renderer,
              parentToClean = renderer.isSVG && element.nodeName === 'SPAN' && wrapper.parentGroup,
              grandParent,
              ownerSVGElement = element.ownerSVGElement,
              i,
              clipPath = wrapper.clipPath;
          element.onclick = element.onmouseout = element.onmouseover = element.onmousemove = element.point = null;
          stop(wrapper);

          if (clipPath && ownerSVGElement) {
            [].forEach.call(ownerSVGElement.querySelectorAll('[clip-path],[CLIP-PATH]'), function (el) {
              var clipPathAttr = el.getAttribute('clip-path');

              if (clipPathAttr.indexOf(clipPath.element.id) > -1) {
                el.removeAttribute('clip-path');
              }
            });
            wrapper.clipPath = clipPath.destroy();
          }

          if (wrapper.stops) {
            for (i = 0; i < wrapper.stops.length; i++) {
              wrapper.stops[i] = wrapper.stops[i].destroy();
            }

            wrapper.stops = null;
          }

          wrapper.safeRemoveChild(element);

          if (!renderer.styledMode) {
            wrapper.destroyShadows();
          }

          while (parentToClean && parentToClean.div && parentToClean.div.childNodes.length === 0) {
            grandParent = parentToClean.parentGroup;
            wrapper.safeRemoveChild(parentToClean.div);
            delete parentToClean.div;
            parentToClean = grandParent;
          }

          if (wrapper.alignTo) {
            erase(renderer.alignedObjects, wrapper);
          }

          objectEach(wrapper, function (val, key) {
            if (wrapper[key] && wrapper[key].parentGroup === wrapper && wrapper[key].destroy) {
              wrapper[key].destroy();
            }

            delete wrapper[key];
          });
          return;
        },
        shadow: function shadow(shadowOptions, group, cutOff) {
          var shadows = [],
              i,
              shadow,
              element = this.element,
              strokeWidth,
              shadowWidth,
              shadowElementOpacity,
              transform;

          if (!shadowOptions) {
            this.destroyShadows();
          } else if (!this.shadows) {
            shadowWidth = pick(shadowOptions.width, 3);
            shadowElementOpacity = (shadowOptions.opacity || 0.15) / shadowWidth;
            transform = this.parentInverted ? '(-1,-1)' : '(' + pick(shadowOptions.offsetX, 1) + ', ' + pick(shadowOptions.offsetY, 1) + ')';

            for (i = 1; i <= shadowWidth; i++) {
              shadow = element.cloneNode(0);
              strokeWidth = shadowWidth * 2 + 1 - 2 * i;
              attr(shadow, {
                stroke: shadowOptions.color || '#000000',
                'stroke-opacity': shadowElementOpacity * i,
                'stroke-width': strokeWidth,
                transform: 'translate' + transform,
                fill: 'none'
              });
              shadow.setAttribute('class', (shadow.getAttribute('class') || '') + ' highcharts-shadow');

              if (cutOff) {
                attr(shadow, 'height', Math.max(attr(shadow, 'height') - strokeWidth, 0));
                shadow.cutHeight = strokeWidth;
              }

              if (group) {
                group.element.appendChild(shadow);
              } else if (element.parentNode) {
                element.parentNode.insertBefore(shadow, element);
              }

              shadows.push(shadow);
            }

            this.shadows = shadows;
          }

          return this;
        },
        destroyShadows: function destroyShadows() {
          (this.shadows || []).forEach(function (shadow) {
            this.safeRemoveChild(shadow);
          }, this);
          this.shadows = void 0;
        },
        xGetter: function xGetter(key) {
          if (this.element.nodeName === 'circle') {
            if (key === 'x') {
              key = 'cx';
            } else if (key === 'y') {
              key = 'cy';
            }
          }

          return this._defaultGetter(key);
        },
        _defaultGetter: function _defaultGetter(key) {
          var ret = pick(this[key + 'Value'], this[key], this.element ? this.element.getAttribute(key) : null, 0);

          if (/^[\-0-9\.]+$/.test(ret)) {
            ret = parseFloat(ret);
          }

          return ret;
        },
        dSetter: function dSetter(value, key, element) {
          if (value && value.join) {
            value = value.join(' ');
          }

          if (/(NaN| {2}|^$)/.test(value)) {
            value = 'M 0 0';
          }

          if (this[key] !== value) {
            element.setAttribute(key, value);
            this[key] = value;
          }
        },
        dashstyleSetter: function dashstyleSetter(value) {
          var i,
              strokeWidth = this['stroke-width'];

          if (strokeWidth === 'inherit') {
            strokeWidth = 1;
          }

          value = value && value.toLowerCase();

          if (value) {
            value = value.replace('shortdashdotdot', '3,1,1,1,1,1,').replace('shortdashdot', '3,1,1,1').replace('shortdot', '1,1,').replace('shortdash', '3,1,').replace('longdash', '8,3,').replace(/dot/g, '1,3,').replace('dash', '4,3,').replace(/,$/, '').split(',');
            i = value.length;

            while (i--) {
              value[i] = pInt(value[i]) * strokeWidth;
            }

            value = value.join(',').replace(/NaN/g, 'none');
            this.element.setAttribute('stroke-dasharray', value);
          }
        },
        alignSetter: function alignSetter(value) {
          var convert = {
            left: 'start',
            center: 'middle',
            right: 'end'
          };

          if (convert[value]) {
            this.alignValue = value;
            this.element.setAttribute('text-anchor', convert[value]);
          }
        },
        opacitySetter: function opacitySetter(value, key, element) {
          this[key] = value;
          element.setAttribute(key, value);
        },
        titleSetter: function titleSetter(value) {
          var titleNode = this.element.getElementsByTagName('title')[0];

          if (!titleNode) {
            titleNode = doc.createElementNS(this.SVG_NS, 'title');
            this.element.appendChild(titleNode);
          }

          if (titleNode.firstChild) {
            titleNode.removeChild(titleNode.firstChild);
          }

          titleNode.appendChild(doc.createTextNode(String(pick(value, '')).replace(/<[^>]*>/g, '').replace(/&lt;/g, '<').replace(/&gt;/g, '>')));
        },
        textSetter: function textSetter(value) {
          if (value !== this.textStr) {
            delete this.bBox;
            delete this.textPxLength;
            this.textStr = value;

            if (this.added) {
              this.renderer.buildText(this);
            }
          }
        },
        setTextPath: function setTextPath(path, textPathOptions) {
          var elem = this.element,
              attribsMap = {
            textAnchor: 'text-anchor'
          },
              attrs,
              adder = false,
              textPathElement,
              textPathId,
              textPathWrapper = this.textPathWrapper,
              tspans,
              firstTime = !textPathWrapper;
          textPathOptions = merge(true, {
            enabled: true,
            attributes: {
              dy: -5,
              startOffset: '50%',
              textAnchor: 'middle'
            }
          }, textPathOptions);
          attrs = textPathOptions.attributes;

          if (path && textPathOptions && textPathOptions.enabled) {
            if (textPathWrapper && textPathWrapper.element.parentNode === null) {
              firstTime = true;
              textPathWrapper = textPathWrapper.destroy();
            } else if (textPathWrapper) {
              this.removeTextOutline.call(textPathWrapper.parentGroup, [].slice.call(elem.getElementsByTagName('tspan')));
            }

            if (this.options && this.options.padding) {
              attrs.dx = -this.options.padding;
            }

            if (!textPathWrapper) {
              this.textPathWrapper = textPathWrapper = this.renderer.createElement('textPath');
              adder = true;
            }

            textPathElement = textPathWrapper.element;
            textPathId = path.element.getAttribute('id');

            if (!textPathId) {
              path.element.setAttribute('id', textPathId = uniqueKey());
            }

            if (firstTime) {
              tspans = elem.getElementsByTagName('tspan');

              while (tspans.length) {
                tspans[0].setAttribute('y', 0);

                if (isNumber(attrs.dx)) {
                  tspans[0].setAttribute('x', -attrs.dx);
                }

                textPathElement.appendChild(tspans[0]);
              }
            }

            if (adder) {
              textPathWrapper.add({
                element: this.text ? this.text.element : elem
              });
            }

            textPathElement.setAttributeNS('http://www.w3.org/1999/xlink', 'href', this.renderer.url + '#' + textPathId);

            if (defined(attrs.dy)) {
              textPathElement.parentNode.setAttribute('dy', attrs.dy);
              delete attrs.dy;
            }

            if (defined(attrs.dx)) {
              textPathElement.parentNode.setAttribute('dx', attrs.dx);
              delete attrs.dx;
            }

            objectEach(attrs, function (val, key) {
              textPathElement.setAttribute(attribsMap[key] || key, val);
            });
            elem.removeAttribute('transform');
            this.removeTextOutline.call(textPathWrapper, [].slice.call(elem.getElementsByTagName('tspan')));

            if (this.text && !this.renderer.styledMode) {
              this.attr({
                fill: 'none',
                'stroke-width': 0
              });
            }

            this.updateTransform = noop;
            this.applyTextOutline = noop;
          } else if (textPathWrapper) {
            delete this.updateTransform;
            delete this.applyTextOutline;
            this.destroyTextPath(elem, path);
            this.updateTransform();

            if (this.options.rotation) {
              this.applyTextOutline(this.options.style.textOutline);
            }
          }

          return this;
        },
        destroyTextPath: function destroyTextPath(elem, path) {
          var tspans,
              textElement = elem.getElementsByTagName('text')[0];

          if (textElement) {
            textElement.removeAttribute('dx');
            textElement.removeAttribute('dy');
            path.element.setAttribute('id', '');

            if (textElement.getElementsByTagName('textPath').length) {
              tspans = this.textPathWrapper.element.childNodes;

              while (tspans.length) {
                textElement.appendChild(tspans[0]);
              }

              textElement.removeChild(this.textPathWrapper.element);
            }
          } else if (elem.getAttribute('dx') || elem.getAttribute('dy')) {
            elem.removeAttribute('dx');
            elem.removeAttribute('dy');
          }

          this.textPathWrapper = this.textPathWrapper.destroy();
        },
        fillSetter: function fillSetter(value, key, element) {
          if (typeof value === 'string') {
            element.setAttribute(key, value);
          } else if (value) {
            this.complexColor(value, key, element);
          }
        },
        visibilitySetter: function visibilitySetter(value, key, element) {
          if (value === 'inherit') {
            element.removeAttribute(key);
          } else if (this[key] !== value) {
            element.setAttribute(key, value);
          }

          this[key] = value;
        },
        zIndexSetter: function zIndexSetter(value, key) {
          var renderer = this.renderer,
              parentGroup = this.parentGroup,
              parentWrapper = parentGroup || renderer,
              parentNode = parentWrapper.element || renderer.box,
              childNodes,
              otherElement,
              otherZIndex,
              element = this.element,
              inserted = false,
              undefinedOtherZIndex,
              svgParent = parentNode === renderer.box,
              run = this.added,
              i;

          if (defined(value)) {
            element.setAttribute('data-z-index', value);
            value = +value;

            if (this[key] === value) {
              run = false;
            }
          } else if (defined(this[key])) {
            element.removeAttribute('data-z-index');
          }

          this[key] = value;

          if (run) {
            value = this.zIndex;

            if (value && parentGroup) {
              parentGroup.handleZ = true;
            }

            childNodes = parentNode.childNodes;

            for (i = childNodes.length - 1; i >= 0 && !inserted; i--) {
              otherElement = childNodes[i];
              otherZIndex = otherElement.getAttribute('data-z-index');
              undefinedOtherZIndex = !defined(otherZIndex);

              if (otherElement !== element) {
                if (value < 0 && undefinedOtherZIndex && !svgParent && !i) {
                  parentNode.insertBefore(element, childNodes[i]);
                  inserted = true;
                } else if (pInt(otherZIndex) <= value || undefinedOtherZIndex && (!defined(value) || value >= 0)) {
                  parentNode.insertBefore(element, childNodes[i + 1] || null);
                  inserted = true;
                }
              }
            }

            if (!inserted) {
              parentNode.insertBefore(element, childNodes[svgParent ? 3 : 0] || null);
              inserted = true;
            }
          }

          return inserted;
        },
        _defaultSetter: function _defaultSetter(value, key, element) {
          element.setAttribute(key, value);
        }
      });
      SVGElement.prototype.yGetter = SVGElement.prototype.xGetter;

      SVGElement.prototype.translateXSetter = SVGElement.prototype.translateYSetter = SVGElement.prototype.rotationSetter = SVGElement.prototype.verticalAlignSetter = SVGElement.prototype.rotationOriginXSetter = SVGElement.prototype.rotationOriginYSetter = SVGElement.prototype.scaleXSetter = SVGElement.prototype.scaleYSetter = SVGElement.prototype.matrixSetter = function (value, key) {
        this[key] = value;
        this.doTransform = true;
      };

      SVGElement.prototype['stroke-widthSetter'] = SVGElement.prototype.strokeSetter = function (value, key, element) {
        this[key] = value;

        if (this.stroke && this['stroke-width']) {
          SVGElement.prototype.fillSetter.call(this, this.stroke, 'stroke', element);
          element.setAttribute('stroke-width', this['stroke-width']);
          this.hasStroke = true;
        } else if (key === 'stroke-width' && value === 0 && this.hasStroke) {
          element.removeAttribute('stroke');
          this.hasStroke = false;
        } else if (this.renderer.styledMode && this['stroke-width']) {
          element.setAttribute('stroke-width', this['stroke-width']);
          this.hasStroke = true;
        }
      };

      SVGRenderer = H.SVGRenderer = function () {
        this.init.apply(this, arguments);
      };

      extend(SVGRenderer.prototype, {
        Element: SVGElement,
        SVG_NS: SVG_NS,
        init: function init(container, width, height, style, forExport, allowHTML, styledMode) {
          var renderer = this,
              boxWrapper,
              element,
              desc;
          boxWrapper = renderer.createElement('svg').attr({
            version: '1.1',
            'class': 'highcharts-root'
          });

          if (!styledMode) {
            boxWrapper.css(this.getStyle(style));
          }

          element = boxWrapper.element;
          container.appendChild(element);
          attr(container, 'dir', 'ltr');

          if (container.innerHTML.indexOf('xmlns') === -1) {
            attr(element, 'xmlns', this.SVG_NS);
          }

          renderer.isSVG = true;
          this.box = element;
          this.boxWrapper = boxWrapper;
          renderer.alignedObjects = [];
          this.url = (isFirefox || isWebKit) && doc.getElementsByTagName('base').length ? win.location.href.split('#')[0].replace(/<[^>]*>/g, '').replace(/([\('\)])/g, '\\$1').replace(/ /g, '%20') : '';
          desc = this.createElement('desc').add();
          desc.element.appendChild(doc.createTextNode('Created with Highcharts 8.0.4'));
          renderer.defs = this.createElement('defs').add();
          renderer.allowHTML = allowHTML;
          renderer.forExport = forExport;
          renderer.styledMode = styledMode;
          renderer.gradients = {};
          renderer.cache = {};
          renderer.cacheKeys = [];
          renderer.imgCount = 0;
          renderer.setSize(width, height, false);
          var subPixelFix, rect;

          if (isFirefox && container.getBoundingClientRect) {
            subPixelFix = function subPixelFix() {
              _css(container, {
                left: 0,
                top: 0
              });

              rect = container.getBoundingClientRect();

              _css(container, {
                left: Math.ceil(rect.left) - rect.left + 'px',
                top: Math.ceil(rect.top) - rect.top + 'px'
              });
            };

            subPixelFix();
            renderer.unSubPixelFix = addEvent(win, 'resize', subPixelFix);
          }
        },
        definition: function definition(def) {
          var ren = this;

          function recurse(config, parent) {
            var ret;
            splat(config).forEach(function (item) {
              var node = ren.createElement(item.tagName),
                  attr = {};
              objectEach(item, function (val, key) {
                if (key !== 'tagName' && key !== 'children' && key !== 'textContent') {
                  attr[key] = val;
                }
              });
              node.attr(attr);
              node.add(parent || ren.defs);

              if (item.textContent) {
                node.element.appendChild(doc.createTextNode(item.textContent));
              }

              recurse(item.children || [], node);
              ret = node;
            });
            return ret;
          }

          return recurse(def);
        },
        getStyle: function getStyle(style) {
          this.style = extend({
            fontFamily: '"Lucida Grande", "Lucida Sans Unicode", ' + 'Arial, Helvetica, sans-serif',
            fontSize: '12px'
          }, style);
          return this.style;
        },
        setStyle: function setStyle(style) {
          this.boxWrapper.css(this.getStyle(style));
        },
        isHidden: function isHidden() {
          return !this.boxWrapper.getBBox().width;
        },
        destroy: function destroy() {
          var renderer = this,
              rendererDefs = renderer.defs;
          renderer.box = null;
          renderer.boxWrapper = renderer.boxWrapper.destroy();
          destroyObjectProperties(renderer.gradients || {});
          renderer.gradients = null;

          if (rendererDefs) {
            renderer.defs = rendererDefs.destroy();
          }

          if (renderer.unSubPixelFix) {
            renderer.unSubPixelFix();
          }

          renderer.alignedObjects = null;
          return null;
        },
        createElement: function createElement(nodeName) {
          var wrapper = new this.Element();
          wrapper.init(this, nodeName);
          return wrapper;
        },
        draw: noop,
        getRadialAttr: function getRadialAttr(radialReference, gradAttr) {
          return {
            cx: radialReference[0] - radialReference[2] / 2 + gradAttr.cx * radialReference[2],
            cy: radialReference[1] - radialReference[2] / 2 + gradAttr.cy * radialReference[2],
            r: gradAttr.r * radialReference[2]
          };
        },
        truncate: function truncate(wrapper, tspan, text, words, startAt, width, getString) {
          var renderer = this,
              rotation = wrapper.rotation,
              str,
              minIndex = words ? 1 : 0,
              maxIndex = (text || words).length,
              currentIndex = maxIndex,
              lengths = [],
              updateTSpan = function updateTSpan(s) {
            if (tspan.firstChild) {
              tspan.removeChild(tspan.firstChild);
            }

            if (s) {
              tspan.appendChild(doc.createTextNode(s));
            }
          },
              getSubStringLength = function getSubStringLength(charEnd, concatenatedEnd) {
            var end = concatenatedEnd || charEnd;

            if (typeof lengths[end] === 'undefined') {
              if (tspan.getSubStringLength) {
                try {
                  lengths[end] = startAt + tspan.getSubStringLength(0, words ? end + 1 : end);
                } catch (e) {
                }
              } else if (renderer.getSpanWidth) {
                updateTSpan(getString(text || words, charEnd));
                lengths[end] = startAt + renderer.getSpanWidth(wrapper, tspan);
              }
            }

            return lengths[end];
          },
              actualWidth,
              truncated;

          wrapper.rotation = 0;
          actualWidth = getSubStringLength(tspan.textContent.length);
          truncated = startAt + actualWidth > width;

          if (truncated) {
            while (minIndex <= maxIndex) {
              currentIndex = Math.ceil((minIndex + maxIndex) / 2);

              if (words) {
                str = getString(words, currentIndex);
              }

              actualWidth = getSubStringLength(currentIndex, str && str.length - 1);

              if (minIndex === maxIndex) {
                minIndex = maxIndex + 1;
              } else if (actualWidth > width) {
                maxIndex = currentIndex - 1;
              } else {
                minIndex = currentIndex;
              }
            }

            if (maxIndex === 0) {
              updateTSpan('');
            } else if (!(text && maxIndex === text.length - 1)) {
              updateTSpan(str || getString(text || words, currentIndex));
            }
          }

          if (words) {
            words.splice(0, currentIndex);
          }

          wrapper.actualWidth = actualWidth;
          wrapper.rotation = rotation;
          return truncated;
        },
        escapes: {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          "'": '&#39;',
          '"': '&quot;'
        },
        buildText: function buildText(wrapper) {
          var textNode = wrapper.element,
              renderer = this,
              forExport = renderer.forExport,
              textStr = pick(wrapper.textStr, '').toString(),
              hasMarkup = textStr.indexOf('<') !== -1,
              lines,
              childNodes = textNode.childNodes,
              truncated,
              parentX = attr(textNode, 'x'),
              textStyles = wrapper.styles,
              width = wrapper.textWidth,
              textLineHeight = textStyles && textStyles.lineHeight,
              textOutline = textStyles && textStyles.textOutline,
              ellipsis = textStyles && textStyles.textOverflow === 'ellipsis',
              noWrap = textStyles && textStyles.whiteSpace === 'nowrap',
              fontSize = textStyles && textStyles.fontSize,
              textCache,
              isSubsequentLine,
              i = childNodes.length,
              tempParent = width && !wrapper.added && this.box,
              getLineHeight = function getLineHeight(tspan) {
            var fontSizeStyle;

            if (!renderer.styledMode) {
              fontSizeStyle = /(px|em)$/.test(tspan && tspan.style.fontSize) ? tspan.style.fontSize : fontSize || renderer.style.fontSize || 12;
            }

            return textLineHeight ? pInt(textLineHeight) : renderer.fontMetrics(fontSizeStyle, tspan.getAttribute('style') ? tspan : textNode).h;
          },
              unescapeEntities = function unescapeEntities(inputStr, except) {
            objectEach(renderer.escapes, function (value, key) {
              if (!except || except.indexOf(value) === -1) {
                inputStr = inputStr.toString().replace(new RegExp(value, 'g'), key);
              }
            });
            return inputStr;
          },
              parseAttribute = function parseAttribute(s, attr) {
            var start, delimiter;
            start = s.indexOf('<');
            s = s.substring(start, s.indexOf('>') - start);
            start = s.indexOf(attr + '=');

            if (start !== -1) {
              start = start + attr.length + 1;
              delimiter = s.charAt(start);

              if (delimiter === '"' || delimiter === "'") {
                s = s.substring(start + 1);
                return s.substring(0, s.indexOf(delimiter));
              }
            }
          };

          var regexMatchBreaks = /<br.*?>/g;
          textCache = [textStr, ellipsis, noWrap, textLineHeight, textOutline, fontSize, width].join(',');

          if (textCache === wrapper.textCache) {
            return;
          }

          wrapper.textCache = textCache;

          while (i--) {
            textNode.removeChild(childNodes[i]);
          }

          if (!hasMarkup && !textOutline && !ellipsis && !width && (textStr.indexOf(' ') === -1 || noWrap && !regexMatchBreaks.test(textStr))) {
            textNode.appendChild(doc.createTextNode(unescapeEntities(textStr)));
          } else {
            if (tempParent) {
              tempParent.appendChild(textNode);
            }

            if (hasMarkup) {
              lines = renderer.styledMode ? textStr.replace(/<(b|strong)>/g, '<span class="highcharts-strong">').replace(/<(i|em)>/g, '<span class="highcharts-emphasized">') : textStr.replace(/<(b|strong)>/g, '<span style="font-weight:bold">').replace(/<(i|em)>/g, '<span style="font-style:italic">');
              lines = lines.replace(/<a/g, '<span').replace(/<\/(b|strong|i|em|a)>/g, '</span>').split(regexMatchBreaks);
            } else {
              lines = [textStr];
            }

            lines = lines.filter(function (line) {
              return line !== '';
            });
            lines.forEach(function (line, lineNo) {
              var spans,
                  spanNo = 0,
                  lineLength = 0;
              line = line.replace(/^\s+|\s+$/g, '').replace(/<span/g, '|||<span').replace(/<\/span>/g, '</span>|||');
              spans = line.split('|||');
              spans.forEach(function buildTextSpans(span) {
                if (span !== '' || spans.length === 1) {
                  var attributes = {},
                      tspan = doc.createElementNS(renderer.SVG_NS, 'tspan'),
                      classAttribute,
                      styleAttribute,
                      hrefAttribute;
                  classAttribute = parseAttribute(span, 'class');

                  if (classAttribute) {
                    attr(tspan, 'class', classAttribute);
                  }

                  styleAttribute = parseAttribute(span, 'style');

                  if (styleAttribute) {
                    styleAttribute = styleAttribute.replace(/(;| |^)color([ :])/, '$1fill$2');
                    attr(tspan, 'style', styleAttribute);
                  }

                  hrefAttribute = parseAttribute(span, 'href');

                  if (hrefAttribute && !forExport) {
                    attr(tspan, 'onclick', 'location.href=\"' + hrefAttribute + '\"');
                    attr(tspan, 'class', 'highcharts-anchor');

                    if (!renderer.styledMode) {
                      _css(tspan, {
                        cursor: 'pointer'
                      });
                    }
                  }

                  span = unescapeEntities(span.replace(/<[a-zA-Z\/](.|\n)*?>/g, '') || ' ');

                  if (span !== ' ') {
                    tspan.appendChild(doc.createTextNode(span));

                    if (!spanNo) {
                      if (lineNo && parentX !== null) {
                        attributes.x = parentX;
                      }
                    } else {
                      attributes.dx = 0;
                    }

                    attr(tspan, attributes);
                    textNode.appendChild(tspan);

                    if (!spanNo && isSubsequentLine) {
                      if (!svg && forExport) {
                        _css(tspan, {
                          display: 'block'
                        });
                      }

                      attr(tspan, 'dy', getLineHeight(tspan));
                    }

                    if (width) {
                      var words = span.replace(/([^\^])-/g, '$1- ').split(' '),
                          hasWhiteSpace = !noWrap && (spans.length > 1 || lineNo || words.length > 1),
                          wrapLineNo = 0,
                          dy = getLineHeight(tspan);

                      if (ellipsis) {
                        truncated = renderer.truncate(wrapper, tspan, span, void 0, 0, Math.max(0, width - parseInt(fontSize || 12, 10)), function (text, currentIndex) {
                          return text.substring(0, currentIndex) + "\u2026";
                        });
                      } else if (hasWhiteSpace) {
                        while (words.length) {
                          if (words.length && !noWrap && wrapLineNo > 0) {
                            tspan = doc.createElementNS(SVG_NS, 'tspan');
                            attr(tspan, {
                              dy: dy,
                              x: parentX
                            });

                            if (styleAttribute) {
                              attr(tspan, 'style', styleAttribute);
                            }

                            tspan.appendChild(doc.createTextNode(words.join(' ').replace(/- /g, '-')));
                            textNode.appendChild(tspan);
                          }

                          renderer.truncate(wrapper, tspan, null, words, wrapLineNo === 0 ? lineLength : 0, width, function (text, currentIndex) {
                            return words.slice(0, currentIndex).join(' ').replace(/- /g, '-');
                          });
                          lineLength = wrapper.actualWidth;
                          wrapLineNo++;
                        }
                      }
                    }

                    spanNo++;
                  }
                }
              });
              isSubsequentLine = isSubsequentLine || textNode.childNodes.length;
            });

            if (ellipsis && truncated) {
              wrapper.attr('title', unescapeEntities(wrapper.textStr, ['&lt;', '&gt;']));
            }

            if (tempParent) {
              tempParent.removeChild(textNode);
            }

            if (textOutline && wrapper.applyTextOutline) {
              wrapper.applyTextOutline(textOutline);
            }
          }
        },
        getContrast: function getContrast(rgba) {
          rgba = color(rgba).rgba;
          rgba[0] *= 1;
          rgba[1] *= 1.2;
          rgba[2] *= 0.5;
          return rgba[0] + rgba[1] + rgba[2] > 1.8 * 255 ? '#000000' : '#FFFFFF';
        },
        button: function button(text, x, y, callback, normalState, hoverState, pressedState, disabledState, shape, useHTML) {
          var label = this.label(text, x, y, shape, null, null, useHTML, null, 'button'),
              curState = 0,
              styledMode = this.styledMode;
          label.attr(merge({
            padding: 8,
            r: 2
          }, normalState));

          if (!styledMode) {
            var normalStyle, hoverStyle, pressedStyle, disabledStyle;
            normalState = merge({
              fill: '#f7f7f7',
              stroke: '#cccccc',
              'stroke-width': 1,
              style: {
                color: '#333333',
                cursor: 'pointer',
                fontWeight: 'normal'
              }
            }, normalState);
            normalStyle = normalState.style;
            delete normalState.style;
            hoverState = merge(normalState, {
              fill: '#e6e6e6'
            }, hoverState);
            hoverStyle = hoverState.style;
            delete hoverState.style;
            pressedState = merge(normalState, {
              fill: '#e6ebf5',
              style: {
                color: '#000000',
                fontWeight: 'bold'
              }
            }, pressedState);
            pressedStyle = pressedState.style;
            delete pressedState.style;
            disabledState = merge(normalState, {
              style: {
                color: '#cccccc'
              }
            }, disabledState);
            disabledStyle = disabledState.style;
            delete disabledState.style;
          }

          addEvent(label.element, isMS ? 'mouseover' : 'mouseenter', function () {
            if (curState !== 3) {
              label.setState(1);
            }
          });
          addEvent(label.element, isMS ? 'mouseout' : 'mouseleave', function () {
            if (curState !== 3) {
              label.setState(curState);
            }
          });

          label.setState = function (state) {
            if (state !== 1) {
              label.state = curState = state;
            }

            label.removeClass(/highcharts-button-(normal|hover|pressed|disabled)/).addClass('highcharts-button-' + ['normal', 'hover', 'pressed', 'disabled'][state || 0]);

            if (!styledMode) {
              label.attr([normalState, hoverState, pressedState, disabledState][state || 0]).css([normalStyle, hoverStyle, pressedStyle, disabledStyle][state || 0]);
            }
          };

          if (!styledMode) {
            label.attr(normalState).css(extend({
              cursor: 'default'
            }, normalStyle));
          }

          return label.on('click', function (e) {
            if (curState !== 3) {
              callback.call(label, e);
            }
          });
        },
        crispLine: function crispLine(points, width) {
          if (points[1] === points[4]) {
            points[1] = points[4] = Math.round(points[1]) - width % 2 / 2;
          }

          if (points[2] === points[5]) {
            points[2] = points[5] = Math.round(points[2]) + width % 2 / 2;
          }

          return points;
        },
        path: function path(_path) {
          var attribs = this.styledMode ? {} : {
            fill: 'none'
          };

          if (isArray(_path)) {
            attribs.d = _path;
          } else if (isObject(_path)) {
            extend(attribs, _path);
          }

          return this.createElement('path').attr(attribs);
        },
        circle: function circle(x, y, r) {
          var attribs = isObject(x) ? x : typeof x === 'undefined' ? {} : {
            x: x,
            y: y,
            r: r
          },
              wrapper = this.createElement('circle');

          wrapper.xSetter = wrapper.ySetter = function (value, key, element) {
            element.setAttribute('c' + key, value);
          };

          return wrapper.attr(attribs);
        },
        arc: function arc(x, y, r, innerR, start, end) {
          var arc, options;

          if (isObject(x)) {
            options = x;
            y = options.y;
            r = options.r;
            innerR = options.innerR;
            start = options.start;
            end = options.end;
            x = options.x;
          } else {
            options = {
              innerR: innerR,
              start: start,
              end: end
            };
          }

          arc = this.symbol('arc', x, y, r, r, options);
          arc.r = r;
          return arc;
        },
        rect: function rect(x, y, width, height, r, strokeWidth) {
          r = isObject(x) ? x.r : r;
          var wrapper = this.createElement('rect'),
              attribs = isObject(x) ? x : typeof x === 'undefined' ? {} : {
            x: x,
            y: y,
            width: Math.max(width, 0),
            height: Math.max(height, 0)
          };

          if (!this.styledMode) {
            if (typeof strokeWidth !== 'undefined') {
              attribs.strokeWidth = strokeWidth;
              attribs = wrapper.crisp(attribs);
            }

            attribs.fill = 'none';
          }

          if (r) {
            attribs.r = r;
          }

          wrapper.rSetter = function (value, key, element) {
            wrapper.r = value;
            attr(element, {
              rx: value,
              ry: value
            });
          };

          wrapper.rGetter = function () {
            return wrapper.r;
          };

          return wrapper.attr(attribs);
        },
        setSize: function setSize(width, height, animate) {
          var renderer = this,
              alignedObjects = renderer.alignedObjects,
              i = alignedObjects.length;
          renderer.width = width;
          renderer.height = height;
          renderer.boxWrapper.animate({
            width: width,
            height: height
          }, {
            step: function step() {
              this.attr({
                viewBox: '0 0 ' + this.attr('width') + ' ' + this.attr('height')
              });
            },
            duration: pick(animate, true) ? void 0 : 0
          });

          while (i--) {
            alignedObjects[i].align();
          }
        },
        g: function g(name) {
          var elem = this.createElement('g');
          return name ? elem.attr({
            'class': 'highcharts-' + name
          }) : elem;
        },
        image: function image(src, x, y, width, height, onload) {
          var attribs = {
            preserveAspectRatio: 'none'
          },
              elemWrapper,
              dummy,
              setSVGImageSource = function setSVGImageSource(el, src) {
            if (el.setAttributeNS) {
              el.setAttributeNS('http://www.w3.org/1999/xlink', 'href', src);
            } else {
              el.setAttribute('hc-svg-href', src);
            }
          },
              onDummyLoad = function onDummyLoad(e) {
            setSVGImageSource(elemWrapper.element, src);
            onload.call(elemWrapper, e);
          };

          if (arguments.length > 1) {
            extend(attribs, {
              x: x,
              y: y,
              width: width,
              height: height
            });
          }

          elemWrapper = this.createElement('image').attr(attribs);

          if (onload) {
            setSVGImageSource(elemWrapper.element, 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==');
            dummy = new win.Image();
            addEvent(dummy, 'load', onDummyLoad);
            dummy.src = src;

            if (dummy.complete) {
              onDummyLoad({});
            }
          } else {
            setSVGImageSource(elemWrapper.element, src);
          }

          return elemWrapper;
        },
        symbol: function symbol(_symbol, x, y, width, height, options) {
          var ren = this,
              obj,
              imageRegex = /^url\((.*?)\)$/,
              isImage = imageRegex.test(_symbol),
              sym = !isImage && (this.symbols[_symbol] ? _symbol : 'circle'),
              symbolFn = sym && this.symbols[sym],
              path = defined(x) && symbolFn && symbolFn.call(this.symbols, Math.round(x), Math.round(y), width, height, options),
              imageSrc,
              centerImage;

          if (symbolFn) {
            obj = this.path(path);

            if (!ren.styledMode) {
              obj.attr('fill', 'none');
            }

            extend(obj, {
              symbolName: sym,
              x: x,
              y: y,
              width: width,
              height: height
            });

            if (options) {
              extend(obj, options);
            }
          } else if (isImage) {
            imageSrc = _symbol.match(imageRegex)[1];
            obj = this.image(imageSrc);
            obj.imgwidth = pick(symbolSizes[imageSrc] && symbolSizes[imageSrc].width, options && options.width);
            obj.imgheight = pick(symbolSizes[imageSrc] && symbolSizes[imageSrc].height, options && options.height);

            centerImage = function centerImage() {
              obj.attr({
                width: obj.width,
                height: obj.height
              });
            };

            ['width', 'height'].forEach(function (key) {
              obj[key + 'Setter'] = function (value, key) {
                var attribs = {},
                    imgSize = this['img' + key],
                    trans = key === 'width' ? 'translateX' : 'translateY';
                this[key] = value;

                if (defined(imgSize)) {
                  if (options && options.backgroundSize === 'within' && this.width && this.height) {
                    imgSize = Math.round(imgSize * Math.min(this.width / this.imgwidth, this.height / this.imgheight));
                  }

                  if (this.element) {
                    this.element.setAttribute(key, imgSize);
                  }

                  if (!this.alignByTranslate) {
                    attribs[trans] = ((this[key] || 0) - imgSize) / 2;
                    this.attr(attribs);
                  }
                }
              };
            });

            if (defined(x)) {
              obj.attr({
                x: x,
                y: y
              });
            }

            obj.isImg = true;

            if (defined(obj.imgwidth) && defined(obj.imgheight)) {
              centerImage();
            } else {
              obj.attr({
                width: 0,
                height: 0
              });
              createElement('img', {
                onload: function onload() {
                  var chart = charts[ren.chartIndex];

                  if (this.width === 0) {
                    _css(this, {
                      position: 'absolute',
                      top: '-999em'
                    });

                    doc.body.appendChild(this);
                  }

                  symbolSizes[imageSrc] = {
                    width: this.width,
                    height: this.height
                  };
                  obj.imgwidth = this.width;
                  obj.imgheight = this.height;

                  if (obj.element) {
                    centerImage();
                  }

                  if (this.parentNode) {
                    this.parentNode.removeChild(this);
                  }

                  ren.imgCount--;

                  if (!ren.imgCount && chart && !chart.hasLoaded) {
                    chart.onload();
                  }
                },
                src: imageSrc
              });
              this.imgCount++;
            }
          }

          return obj;
        },
        symbols: {
          circle: function circle(x, y, w, h) {
            return this.arc(x + w / 2, y + h / 2, w / 2, h / 2, {
              start: Math.PI * 0.5,
              end: Math.PI * 2.5,
              open: false
            });
          },
          square: function square(x, y, w, h) {
            return ['M', x, y, 'L', x + w, y, x + w, y + h, x, y + h, 'Z'];
          },
          triangle: function triangle(x, y, w, h) {
            return ['M', x + w / 2, y, 'L', x + w, y + h, x, y + h, 'Z'];
          },
          'triangle-down': function triangleDown(x, y, w, h) {
            return ['M', x, y, 'L', x + w, y, x + w / 2, y + h, 'Z'];
          },
          diamond: function diamond(x, y, w, h) {
            return ['M', x + w / 2, y, 'L', x + w, y + h / 2, x + w / 2, y + h, x, y + h / 2, 'Z'];
          },
          arc: function arc(x, y, w, h, options) {
            var start = options.start,
                rx = options.r || w,
                ry = options.r || h || w,
                proximity = 0.001,
                fullCircle = Math.abs(options.end - options.start - 2 * Math.PI) < proximity,
                end = options.end - proximity,
                innerRadius = options.innerR,
                open = pick(options.open, fullCircle),
                cosStart = Math.cos(start),
                sinStart = Math.sin(start),
                cosEnd = Math.cos(end),
                sinEnd = Math.sin(end),
                longArc = pick(options.longArc, options.end - start - Math.PI < proximity ? 0 : 1),
                arc;
            arc = ['M', x + rx * cosStart, y + ry * sinStart, 'A', rx, ry, 0, longArc, pick(options.clockwise, 1), x + rx * cosEnd, y + ry * sinEnd];

            if (defined(innerRadius)) {
              arc.push(open ? 'M' : 'L', x + innerRadius * cosEnd, y + innerRadius * sinEnd, 'A', innerRadius, innerRadius, 0, longArc, defined(options.clockwise) ? 1 - options.clockwise : 0, x + innerRadius * cosStart, y + innerRadius * sinStart);
            }

            arc.push(open ? '' : 'Z');
            return arc;
          },
          callout: function callout(x, y, w, h, options) {
            var arrowLength = 6,
                halfDistance = 6,
                r = Math.min(options && options.r || 0, w, h),
                safeDistance = r + halfDistance,
                anchorX = options && options.anchorX,
                anchorY = options && options.anchorY,
                path;
            path = ['M', x + r, y, 'L', x + w - r, y, 'C', x + w, y, x + w, y, x + w, y + r, 'L', x + w, y + h - r, 'C', x + w, y + h, x + w, y + h, x + w - r, y + h, 'L', x + r, y + h, 'C', x, y + h, x, y + h, x, y + h - r, 'L', x, y + r, 'C', x, y, x, y, x + r, y];

            if (anchorX && anchorX > w) {
              if (anchorY > y + safeDistance && anchorY < y + h - safeDistance) {
                path.splice(13, 3, 'L', x + w, anchorY - halfDistance, x + w + arrowLength, anchorY, x + w, anchorY + halfDistance, x + w, y + h - r);
              } else {
                path.splice(13, 3, 'L', x + w, h / 2, anchorX, anchorY, x + w, h / 2, x + w, y + h - r);
              }
            } else if (anchorX && anchorX < 0) {
              if (anchorY > y + safeDistance && anchorY < y + h - safeDistance) {
                path.splice(33, 3, 'L', x, anchorY + halfDistance, x - arrowLength, anchorY, x, anchorY - halfDistance, x, y + r);
              } else {
                path.splice(33, 3, 'L', x, h / 2, anchorX, anchorY, x, h / 2, x, y + r);
              }
            } else if (anchorY && anchorY > h && anchorX > x + safeDistance && anchorX < x + w - safeDistance) {
              path.splice(23, 3, 'L', anchorX + halfDistance, y + h, anchorX, y + h + arrowLength, anchorX - halfDistance, y + h, x + r, y + h);
            } else if (anchorY && anchorY < 0 && anchorX > x + safeDistance && anchorX < x + w - safeDistance) {
              path.splice(3, 3, 'L', anchorX - halfDistance, y, anchorX, y - arrowLength, anchorX + halfDistance, y, w - r, y);
            }

            return path;
          }
        },
        clipRect: function clipRect(x, y, width, height) {
          var wrapper,
              id = uniqueKey() + '-',
              clipPath = this.createElement('clipPath').attr({
            id: id
          }).add(this.defs);
          wrapper = this.rect(x, y, width, height, 0).add(clipPath);
          wrapper.id = id;
          wrapper.clipPath = clipPath;
          wrapper.count = 0;
          return wrapper;
        },
        text: function text(str, x, y, useHTML) {
          var renderer = this,
              wrapper,
              attribs = {};

          if (useHTML && (renderer.allowHTML || !renderer.forExport)) {
            return renderer.html(str, x, y);
          }

          attribs.x = Math.round(x || 0);

          if (y) {
            attribs.y = Math.round(y);
          }

          if (defined(str)) {
            attribs.text = str;
          }

          wrapper = renderer.createElement('text').attr(attribs);

          if (!useHTML) {
            wrapper.xSetter = function (value, key, element) {
              var tspans = element.getElementsByTagName('tspan'),
                  tspan,
                  parentVal = element.getAttribute(key),
                  i;

              for (i = 0; i < tspans.length; i++) {
                tspan = tspans[i];

                if (tspan.getAttribute(key) === parentVal) {
                  tspan.setAttribute(key, value);
                }
              }

              element.setAttribute(key, value);
            };
          }

          return wrapper;
        },
        fontMetrics: function fontMetrics(fontSize, elem) {
          var lineHeight, baseline;

          if ((this.styledMode || !/px/.test(fontSize)) && win.getComputedStyle) {
              fontSize = elem && SVGElement.prototype.getStyle.call(elem, 'font-size');
            } else {
            fontSize = fontSize || elem && elem.style && elem.style.fontSize || this.style && this.style.fontSize;
          }

          if (/px/.test(fontSize)) {
            fontSize = pInt(fontSize);
          } else {
            fontSize = 12;
          }

          lineHeight = fontSize < 24 ? fontSize + 3 : Math.round(fontSize * 1.2);
          baseline = Math.round(lineHeight * 0.8);
          return {
            h: lineHeight,
            b: baseline,
            f: fontSize
          };
        },
        rotCorr: function rotCorr(baseline, rotation, alterY) {
          var y = baseline;

          if (rotation && alterY) {
            y = Math.max(y * Math.cos(rotation * deg2rad), 4);
          }

          return {
            x: -baseline / 3 * Math.sin(rotation * deg2rad),
            y: y
          };
        },
        label: function label(str, x, y, shape, anchorX, anchorY, useHTML, baseline, className) {
          var renderer = this,
              styledMode = renderer.styledMode,
              wrapper = renderer.g(className !== 'button' && 'label'),
              text = wrapper.text = renderer.text('', 0, 0, useHTML).attr({
            zIndex: 1
          }),
              box,
              bBox,
              alignFactor = 0,
              padding = 3,
              paddingLeft = 0,
              width,
              height,
              wrapperX,
              wrapperY,
              textAlign,
              deferredAttr = {},
              strokeWidth,
              baselineOffset,
              hasBGImage = /^url\((.*?)\)$/.test(shape),
              needsBox = styledMode || hasBGImage,
              getCrispAdjust = function getCrispAdjust() {
            return styledMode ? box.strokeWidth() % 2 / 2 : (strokeWidth ? parseInt(strokeWidth, 10) : 0) % 2 / 2;
          },
              updateBoxSize,
              updateTextPadding,
              boxAttr;

          if (className) {
            wrapper.addClass('highcharts-' + className);
          }

          updateBoxSize = function updateBoxSize() {
            var style = text.element.style,
                crispAdjust,
                attribs = {};
            bBox = (typeof width === 'undefined' || typeof height === 'undefined' || textAlign) && defined(text.textStr) && text.getBBox();
            wrapper.width = (width || bBox.width || 0) + 2 * padding + paddingLeft;
            wrapper.height = (height || bBox.height || 0) + 2 * padding;
            baselineOffset = padding + Math.min(renderer.fontMetrics(style && style.fontSize, text).b, bBox ? bBox.height : Infinity);

            if (needsBox) {
              if (!box) {
                wrapper.box = box = renderer.symbols[shape] || hasBGImage ? renderer.symbol(shape) : renderer.rect();
                box.addClass((className === 'button' ? '' : 'highcharts-label-box') + (className ? ' highcharts-' + className + '-box' : ''));
                box.add(wrapper);
                crispAdjust = getCrispAdjust();
                attribs.x = crispAdjust;
                attribs.y = (baseline ? -baselineOffset : 0) + crispAdjust;
              }

              attribs.width = Math.round(wrapper.width);
              attribs.height = Math.round(wrapper.height);
              box.attr(extend(attribs, deferredAttr));
              deferredAttr = {};
            }
          };

          updateTextPadding = function updateTextPadding() {
            var textX = paddingLeft + padding,
                textY;
            textY = baseline ? 0 : baselineOffset;

            if (defined(width) && bBox && (textAlign === 'center' || textAlign === 'right')) {
              textX += {
                center: 0.5,
                right: 1
              }[textAlign] * (width - bBox.width);
            }

            if (textX !== text.x || textY !== text.y) {
              text.attr('x', textX);

              if (text.hasBoxWidthChanged) {
                bBox = text.getBBox(true);
                updateBoxSize();
              }

              if (typeof textY !== 'undefined') {
                text.attr('y', textY);
              }
            }

            text.x = textX;
            text.y = textY;
          };

          boxAttr = function boxAttr(key, value) {
            if (box) {
              box.attr(key, value);
            } else {
              deferredAttr[key] = value;
            }
          };

          wrapper.onAdd = function () {
            text.add(wrapper);
            wrapper.attr({
              text: str || str === 0 ? str : '',
              x: x,
              y: y
            });

            if (box && defined(anchorX)) {
              wrapper.attr({
                anchorX: anchorX,
                anchorY: anchorY
              });
            }
          };

          wrapper.widthSetter = function (value) {
            width = isNumber(value) ? value : null;
          };

          wrapper.heightSetter = function (value) {
            height = value;
          };

          wrapper['text-alignSetter'] = function (value) {
            textAlign = value;
          };

          wrapper.paddingSetter = function (value) {
            if (defined(value) && value !== padding) {
              padding = wrapper.padding = value;
              updateTextPadding();
            }
          };

          wrapper.paddingLeftSetter = function (value) {
            if (defined(value) && value !== paddingLeft) {
              paddingLeft = value;
              updateTextPadding();
            }
          };

          wrapper.alignSetter = function (value) {
            value = {
              left: 0,
              center: 0.5,
              right: 1
            }[value];

            if (value !== alignFactor) {
              alignFactor = value;

              if (bBox) {
                wrapper.attr({
                  x: wrapperX
                });
              }
            }
          };

          wrapper.textSetter = function (value) {
            if (typeof value !== 'undefined') {
              text.attr({
                text: value
              });
            }

            updateBoxSize();
            updateTextPadding();
          };

          wrapper['stroke-widthSetter'] = function (value, key) {
            if (value) {
              needsBox = true;
            }

            strokeWidth = this['stroke-width'] = value;
            boxAttr(key, value);
          };

          if (styledMode) {
            wrapper.rSetter = function (value, key) {
              boxAttr(key, value);
            };
          } else {
            wrapper.strokeSetter = wrapper.fillSetter = wrapper.rSetter = function (value, key) {
              if (key !== 'r') {
                if (key === 'fill' && value) {
                  needsBox = true;
                }

                wrapper[key] = value;
              }

              boxAttr(key, value);
            };
          }

          wrapper.anchorXSetter = function (value, key) {
            anchorX = wrapper.anchorX = value;
            boxAttr(key, Math.round(value) - getCrispAdjust() - wrapperX);
          };

          wrapper.anchorYSetter = function (value, key) {
            anchorY = wrapper.anchorY = value;
            boxAttr(key, value - wrapperY);
          };

          wrapper.xSetter = function (value) {
            wrapper.x = value;

            if (alignFactor) {
              value -= alignFactor * ((width || bBox.width) + 2 * padding);
              wrapper['forceAnimate:x'] = true;
            }

            wrapperX = Math.round(value);
            wrapper.attr('translateX', wrapperX);
          };

          wrapper.ySetter = function (value) {
            wrapperY = wrapper.y = Math.round(value);
            wrapper.attr('translateY', wrapperY);
          };

          var baseCss = wrapper.css;
          var wrapperExtension = {
            css: function css(styles) {
              if (styles) {
                var textStyles = {};
                styles = merge(styles);
                wrapper.textProps.forEach(function (prop) {
                  if (typeof styles[prop] !== 'undefined') {
                    textStyles[prop] = styles[prop];
                    delete styles[prop];
                  }
                });
                text.css(textStyles);

                if ('width' in textStyles) {
                  updateBoxSize();
                }

                if ('fontSize' in textStyles) {
                  updateBoxSize();
                  updateTextPadding();
                }
              }

              return baseCss.call(wrapper, styles);
            },
            getBBox: function getBBox() {
              return {
                width: bBox.width + 2 * padding,
                height: bBox.height + 2 * padding,
                x: bBox.x - padding,
                y: bBox.y - padding
              };
            },
            destroy: function destroy() {
              removeEvent(wrapper.element, 'mouseenter');
              removeEvent(wrapper.element, 'mouseleave');

              if (text) {
                text = text.destroy();
              }

              if (box) {
                box = box.destroy();
              }

              SVGElement.prototype.destroy.call(wrapper);
              wrapper = renderer = updateBoxSize = updateTextPadding = boxAttr = null;
            }
          };

          if (!styledMode) {
            wrapperExtension.shadow = function (b) {
              if (b) {
                updateBoxSize();

                if (box) {
                  box.shadow(b);
                }
              }

              return wrapper;
            };
          }

          return extend(wrapper, wrapperExtension);
        }
      });
      H.Renderer = SVGRenderer;
    });

    _registerModule(_modules, 'parts/Html.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var attr = U.attr,
          createElement = U.createElement,
          css = U.css,
          defined = U.defined,
          extend = U.extend,
          pick = U.pick,
          pInt = U.pInt;
      var isFirefox = H.isFirefox,
          isMS = H.isMS,
          isWebKit = H.isWebKit,
          SVGElement = H.SVGElement,
          SVGRenderer = H.SVGRenderer,
          win = H.win;
      extend(SVGElement.prototype, {
        htmlCss: function htmlCss(styles) {
          var wrapper = this,
              element = wrapper.element,
              isSettingWidth = element.tagName === 'SPAN' && styles && 'width' in styles,
              textWidth = pick(isSettingWidth && styles.width, void 0),
              doTransform;

          if (isSettingWidth) {
            delete styles.width;
            wrapper.textWidth = textWidth;
            doTransform = true;
          }

          if (styles && styles.textOverflow === 'ellipsis') {
            styles.whiteSpace = 'nowrap';
            styles.overflow = 'hidden';
          }

          wrapper.styles = extend(wrapper.styles, styles);
          css(wrapper.element, styles);

          if (doTransform) {
            wrapper.htmlUpdateTransform();
          }

          return wrapper;
        },
        htmlGetBBox: function htmlGetBBox() {
          var wrapper = this,
              element = wrapper.element;
          return {
            x: element.offsetLeft,
            y: element.offsetTop,
            width: element.offsetWidth,
            height: element.offsetHeight
          };
        },
        htmlUpdateTransform: function htmlUpdateTransform() {
          if (!this.added) {
            this.alignOnAdd = true;
            return;
          }

          var wrapper = this,
              renderer = wrapper.renderer,
              elem = wrapper.element,
              translateX = wrapper.translateX || 0,
              translateY = wrapper.translateY || 0,
              x = wrapper.x || 0,
              y = wrapper.y || 0,
              align = wrapper.textAlign || 'left',
              alignCorrection = {
            left: 0,
            center: 0.5,
            right: 1
          }[align],
              styles = wrapper.styles,
              whiteSpace = styles && styles.whiteSpace;

          function getTextPxLength() {
            css(elem, {
              width: '',
              whiteSpace: whiteSpace || 'nowrap'
            });
            return elem.offsetWidth;
          }

          css(elem, {
            marginLeft: translateX,
            marginTop: translateY
          });

          if (!renderer.styledMode && wrapper.shadows) {
            wrapper.shadows.forEach(function (shadow) {
              css(shadow, {
                marginLeft: translateX + 1,
                marginTop: translateY + 1
              });
            });
          }

          if (wrapper.inverted) {
            [].forEach.call(elem.childNodes, function (child) {
              renderer.invertChild(child, elem);
            });
          }

          if (elem.tagName === 'SPAN') {
            var rotation = wrapper.rotation,
                baseline,
                textWidth = wrapper.textWidth && pInt(wrapper.textWidth),
                currentTextTransform = [rotation, align, elem.innerHTML, wrapper.textWidth, wrapper.textAlign].join(',');

            if (textWidth !== wrapper.oldTextWidth && (textWidth > wrapper.oldTextWidth || (wrapper.textPxLength || getTextPxLength()) > textWidth) && (/[ \-]/.test(elem.textContent || elem.innerText) || elem.style.textOverflow === 'ellipsis')) {
              css(elem, {
                width: textWidth + 'px',
                display: 'block',
                whiteSpace: whiteSpace || 'normal'
              });
              wrapper.oldTextWidth = textWidth;
              wrapper.hasBoxWidthChanged = true;
            } else {
              wrapper.hasBoxWidthChanged = false;
            }

            if (currentTextTransform !== wrapper.cTT) {
              baseline = renderer.fontMetrics(elem.style.fontSize, elem).b;

              if (defined(rotation) && (rotation !== (wrapper.oldRotation || 0) || align !== wrapper.oldAlign)) {
                wrapper.setSpanRotation(rotation, alignCorrection, baseline);
              }

              wrapper.getSpanCorrection(!defined(rotation) && wrapper.textPxLength || elem.offsetWidth, baseline, alignCorrection, rotation, align);
            }

            css(elem, {
              left: x + (wrapper.xCorr || 0) + 'px',
              top: y + (wrapper.yCorr || 0) + 'px'
            });
            wrapper.cTT = currentTextTransform;
            wrapper.oldRotation = rotation;
            wrapper.oldAlign = align;
          }
        },
        setSpanRotation: function setSpanRotation(rotation, alignCorrection, baseline) {
          var rotationStyle = {},
              cssTransformKey = this.renderer.getTransformKey();
          rotationStyle[cssTransformKey] = rotationStyle.transform = 'rotate(' + rotation + 'deg)';
          rotationStyle[cssTransformKey + (isFirefox ? 'Origin' : '-origin')] = rotationStyle.transformOrigin = alignCorrection * 100 + '% ' + baseline + 'px';
          css(this.element, rotationStyle);
        },
        getSpanCorrection: function getSpanCorrection(width, baseline, alignCorrection) {
          this.xCorr = -width * alignCorrection;
          this.yCorr = -baseline;
        }
      });
      extend(SVGRenderer.prototype, {
        getTransformKey: function getTransformKey() {
          return isMS && !/Edge/.test(win.navigator.userAgent) ? '-ms-transform' : isWebKit ? '-webkit-transform' : isFirefox ? 'MozTransform' : win.opera ? '-o-transform' : '';
        },
        html: function html(str, x, y) {
          var wrapper = this.createElement('span'),
              element = wrapper.element,
              renderer = wrapper.renderer,
              isSVG = renderer.isSVG,
              addSetters = function addSetters(gWrapper, style) {
            ['opacity', 'visibility'].forEach(function (prop) {
              gWrapper[prop + 'Setter'] = function (value, key, elem) {
                var styleObject = gWrapper.div ? gWrapper.div.style : style;
                SVGElement.prototype[prop + 'Setter'].call(this, value, key, elem);

                if (styleObject) {
                  styleObject[key] = value;
                }
              };
            });
            gWrapper.addedSetters = true;
          };

          wrapper.textSetter = function (value) {
            if (value !== element.innerHTML) {
              delete this.bBox;
              delete this.oldTextWidth;
            }

            this.textStr = value;
            element.innerHTML = pick(value, '');
            wrapper.doTransform = true;
          };

          if (isSVG) {
            addSetters(wrapper, wrapper.element.style);
          }

          wrapper.xSetter = wrapper.ySetter = wrapper.alignSetter = wrapper.rotationSetter = function (value, key) {
            if (key === 'align') {
              key = 'textAlign';
            }

            wrapper[key] = value;
            wrapper.doTransform = true;
          };

          wrapper.afterSetters = function () {
            if (this.doTransform) {
              this.htmlUpdateTransform();
              this.doTransform = false;
            }
          };

          wrapper.attr({
            text: str,
            x: Math.round(x),
            y: Math.round(y)
          }).css({
            position: 'absolute'
          });

          if (!renderer.styledMode) {
            wrapper.css({
              fontFamily: this.style.fontFamily,
              fontSize: this.style.fontSize
            });
          }

          element.style.whiteSpace = 'nowrap';
          wrapper.css = wrapper.htmlCss;

          if (isSVG) {
            wrapper.add = function (svgGroupWrapper) {
              var htmlGroup,
                  container = renderer.box.parentNode,
                  parentGroup,
                  parents = [];
              this.parentGroup = svgGroupWrapper;

              if (svgGroupWrapper) {
                htmlGroup = svgGroupWrapper.div;

                if (!htmlGroup) {
                  parentGroup = svgGroupWrapper;

                  while (parentGroup) {
                    parents.push(parentGroup);
                    parentGroup = parentGroup.parentGroup;
                  }

                  parents.reverse().forEach(function (parentGroup) {
                    var htmlGroupStyle,
                        cls = attr(parentGroup.element, 'class');

                    function translateSetter(value, key) {
                      parentGroup[key] = value;

                      if (key === 'translateX') {
                        htmlGroupStyle.left = value + 'px';
                      } else {
                        htmlGroupStyle.top = value + 'px';
                      }

                      parentGroup.doTransform = true;
                    }

                    htmlGroup = parentGroup.div = parentGroup.div || createElement('div', cls ? {
                      className: cls
                    } : void 0, {
                      position: 'absolute',
                      left: (parentGroup.translateX || 0) + 'px',
                      top: (parentGroup.translateY || 0) + 'px',
                      display: parentGroup.display,
                      opacity: parentGroup.opacity,
                      pointerEvents: parentGroup.styles && parentGroup.styles.pointerEvents
                    }, htmlGroup || container);
                    htmlGroupStyle = htmlGroup.style;
                    extend(parentGroup, {
                      classSetter: function (htmlGroup) {
                        return function (value) {
                          this.element.setAttribute('class', value);
                          htmlGroup.className = value;
                        };
                      }(htmlGroup),
                      on: function on() {
                        if (parents[0].div) {
                          wrapper.on.apply({
                            element: parents[0].div
                          }, arguments);
                        }

                        return parentGroup;
                      },
                      translateXSetter: translateSetter,
                      translateYSetter: translateSetter
                    });

                    if (!parentGroup.addedSetters) {
                      addSetters(parentGroup);
                    }
                  });
                }
              } else {
                htmlGroup = container;
              }

              htmlGroup.appendChild(element);
              wrapper.added = true;

              if (wrapper.alignOnAdd) {
                wrapper.htmlUpdateTransform();
              }

              return wrapper;
            };
          }

          return wrapper;
        }
      });
    });

    _registerModule(_modules, 'parts/Tick.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var clamp = U.clamp,
          correctFloat = U.correctFloat,
          defined = U.defined,
          destroyObjectProperties = U.destroyObjectProperties,
          extend = U.extend,
          isNumber = U.isNumber,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick;
      var fireEvent = H.fireEvent,
          deg2rad = H.deg2rad;

      var Tick = function () {
        function Tick(axis, pos, type, noLabel, parameters) {
          this.isNew = true;
          this.isNewLabel = true;
          this.axis = axis;
          this.pos = pos;
          this.type = type || '';
          this.parameters = parameters || {};
          this.tickmarkOffset = this.parameters.tickmarkOffset;
          this.options = this.parameters.options;

          if (!type && !noLabel) {
            this.addLabel();
          }
        }

        Tick.prototype.addLabel = function () {
          var tick = this,
              axis = tick.axis,
              options = axis.options,
              chart = axis.chart,
              categories = axis.categories,
              names = axis.names,
              pos = tick.pos,
              labelOptions = pick(tick.options && tick.options.labels, options.labels),
              str,
              tickPositions = axis.tickPositions,
              isFirst = pos === tickPositions[0],
              isLast = pos === tickPositions[tickPositions.length - 1],
              value = this.parameters.category || (categories ? pick(categories[pos], names[pos], pos) : pos),
              label = tick.label,
              animateLabels = (!labelOptions.step || labelOptions.step === 1) && axis.tickInterval === 1,
              tickPositionInfo = tickPositions.info,
              dateTimeLabelFormat,
              dateTimeLabelFormats,
              i,
              list;

          if (axis.isDatetimeAxis && tickPositionInfo) {
            dateTimeLabelFormats = chart.time.resolveDTLFormat(options.dateTimeLabelFormats[!options.grid && tickPositionInfo.higherRanks[pos] || tickPositionInfo.unitName]);
            dateTimeLabelFormat = dateTimeLabelFormats.main;
          }

          tick.isFirst = isFirst;
          tick.isLast = isLast;
          tick.formatCtx = {
            axis: axis,
            chart: chart,
            isFirst: isFirst,
            isLast: isLast,
            dateTimeLabelFormat: dateTimeLabelFormat,
            tickPositionInfo: tickPositionInfo,
            value: axis.isLog ? correctFloat(axis.lin2log(value)) : value,
            pos: pos
          };
          str = axis.labelFormatter.call(tick.formatCtx, this.formatCtx);
          list = dateTimeLabelFormats && dateTimeLabelFormats.list;

          if (list) {
            tick.shortenLabel = function () {
              for (i = 0; i < list.length; i++) {
                label.attr({
                  text: axis.labelFormatter.call(extend(tick.formatCtx, {
                    dateTimeLabelFormat: list[i]
                  }))
                });

                if (label.getBBox().width < axis.getSlotWidth(tick) - 2 * pick(labelOptions.padding, 5)) {
                  return;
                }
              }

              label.attr({
                text: ''
              });
            };
          }

          if (animateLabels && axis._addedPlotLB && axis.isXAxis) {
            tick.moveLabel(str, labelOptions);
          }

          if (!defined(label) && !tick.movedLabel) {
            tick.label = label = tick.createLabel({
              x: 0,
              y: 0
            }, str, labelOptions);
            tick.rotation = 0;
          } else if (label && label.textStr !== str && !animateLabels) {
            if (label.textWidth && !(labelOptions.style && labelOptions.style.width) && !label.styles.width) {
              label.css({
                width: null
              });
            }

            label.attr({
              text: str
            });
            label.textPxLength = label.getBBox().width;
          }
        };

        Tick.prototype.createLabel = function (xy, str, labelOptions) {
          var axis = this.axis,
              chart = axis.chart,
              label = defined(str) && labelOptions.enabled ? chart.renderer.text(str, xy.x, xy.y, labelOptions.useHTML).add(axis.labelGroup) : null;

          if (label) {
            if (!chart.styledMode) {
              label.css(merge(labelOptions.style));
            }

            label.textPxLength = label.getBBox().width;
          }

          return label;
        };

        Tick.prototype.destroy = function () {
          destroyObjectProperties(this, this.axis);
        };

        Tick.prototype.getPosition = function (horiz, tickPos, tickmarkOffset, old) {
          var axis = this.axis,
              chart = axis.chart,
              cHeight = old && chart.oldChartHeight || chart.chartHeight,
              pos;
          pos = {
            x: horiz ? correctFloat(axis.translate(tickPos + tickmarkOffset, null, null, old) + axis.transB) : axis.left + axis.offset + (axis.opposite ? (old && chart.oldChartWidth || chart.chartWidth) - axis.right - axis.left : 0),
            y: horiz ? cHeight - axis.bottom + axis.offset - (axis.opposite ? axis.height : 0) : correctFloat(cHeight - axis.translate(tickPos + tickmarkOffset, null, null, old) - axis.transB)
          };
          pos.y = clamp(pos.y, -1e5, 1e5);
          fireEvent(this, 'afterGetPosition', {
            pos: pos
          });
          return pos;
        };

        Tick.prototype.getLabelPosition = function (x, y, label, horiz, labelOptions, tickmarkOffset, index, step) {
          var axis = this.axis,
              transA = axis.transA,
              reversed = axis.isLinked && axis.linkedParent ? axis.linkedParent.reversed : axis.reversed,
              staggerLines = axis.staggerLines,
              rotCorr = axis.tickRotCorr || {
            x: 0,
            y: 0
          },
              yOffset = labelOptions.y,
              labelOffsetCorrection = !horiz && !axis.reserveSpaceDefault ? -axis.labelOffset * (axis.labelAlign === 'center' ? 0.5 : 1) : 0,
              line,
              pos = {};

          if (!defined(yOffset)) {
            if (axis.side === 0) {
              yOffset = label.rotation ? -8 : -label.getBBox().height;
            } else if (axis.side === 2) {
              yOffset = rotCorr.y + 8;
            } else {
              yOffset = Math.cos(label.rotation * deg2rad) * (rotCorr.y - label.getBBox(false, 0).height / 2);
            }
          }

          x = x + labelOptions.x + labelOffsetCorrection + rotCorr.x - (tickmarkOffset && horiz ? tickmarkOffset * transA * (reversed ? -1 : 1) : 0);
          y = y + yOffset - (tickmarkOffset && !horiz ? tickmarkOffset * transA * (reversed ? 1 : -1) : 0);

          if (staggerLines) {
            line = index / (step || 1) % staggerLines;

            if (axis.opposite) {
              line = staggerLines - line - 1;
            }

            y += line * (axis.labelOffset / staggerLines);
          }

          pos.x = x;
          pos.y = Math.round(y);
          fireEvent(this, 'afterGetLabelPosition', {
            pos: pos,
            tickmarkOffset: tickmarkOffset,
            index: index
          });
          return pos;
        };

        Tick.prototype.getLabelSize = function () {
          return this.label ? this.label.getBBox()[this.axis.horiz ? 'height' : 'width'] : 0;
        };

        Tick.prototype.getMarkPath = function (x, y, tickLength, tickWidth, horiz, renderer) {
          return renderer.crispLine(['M', x, y, 'L', x + (horiz ? 0 : -tickLength), y + (horiz ? tickLength : 0)], tickWidth);
        };

        Tick.prototype.handleOverflow = function (xy) {
          var tick = this,
              axis = this.axis,
              labelOptions = axis.options.labels,
              pxPos = xy.x,
              chartWidth = axis.chart.chartWidth,
              spacing = axis.chart.spacing,
              leftBound = pick(axis.labelLeft, Math.min(axis.pos, spacing[3])),
              rightBound = pick(axis.labelRight, Math.max(!axis.isRadial ? axis.pos + axis.len : 0, chartWidth - spacing[1])),
              label = this.label,
              rotation = this.rotation,
              factor = {
            left: 0,
            center: 0.5,
            right: 1
          }[axis.labelAlign || label.attr('align')],
              labelWidth = label.getBBox().width,
              slotWidth = axis.getSlotWidth(tick),
              modifiedSlotWidth = slotWidth,
              xCorrection = factor,
              goRight = 1,
              leftPos,
              rightPos,
              textWidth,
              css = {};

          if (!rotation && pick(labelOptions.overflow, 'justify') === 'justify') {
            leftPos = pxPos - factor * labelWidth;
            rightPos = pxPos + (1 - factor) * labelWidth;

            if (leftPos < leftBound) {
              modifiedSlotWidth = xy.x + modifiedSlotWidth * (1 - factor) - leftBound;
            } else if (rightPos > rightBound) {
              modifiedSlotWidth = rightBound - xy.x + modifiedSlotWidth * factor;
              goRight = -1;
            }

            modifiedSlotWidth = Math.min(slotWidth, modifiedSlotWidth);

            if (modifiedSlotWidth < slotWidth && axis.labelAlign === 'center') {
              xy.x += goRight * (slotWidth - modifiedSlotWidth - xCorrection * (slotWidth - Math.min(labelWidth, modifiedSlotWidth)));
            }

            if (labelWidth > modifiedSlotWidth || axis.autoRotation && (label.styles || {}).width) {
              textWidth = modifiedSlotWidth;
            }
          } else if (rotation < 0 && pxPos - factor * labelWidth < leftBound) {
            textWidth = Math.round(pxPos / Math.cos(rotation * deg2rad) - leftBound);
          } else if (rotation > 0 && pxPos + factor * labelWidth > rightBound) {
            textWidth = Math.round((chartWidth - pxPos) / Math.cos(rotation * deg2rad));
          }

          if (textWidth) {
            if (tick.shortenLabel) {
              tick.shortenLabel();
            } else {
              css.width = Math.floor(textWidth);

              if (!(labelOptions.style || {}).textOverflow) {
                css.textOverflow = 'ellipsis';
              }

              label.css(css);
            }
          }
        };

        Tick.prototype.moveLabel = function (str, labelOptions) {
          var tick = this,
              label = tick.label,
              moved = false,
              xAxis = tick.axis,
              chart = xAxis.chart,
              labelPos,
              reversed = xAxis.reversed,
              inverted = chart.inverted,
              xPos,
              yPos;

          if (label && label.textStr === str) {
            tick.movedLabel = label;
            moved = true;
            delete tick.label;
          } else {
            objectEach(xAxis.ticks, function (currentTick) {
              if (!moved && !currentTick.isNew && currentTick !== tick && currentTick.label && currentTick.label.textStr === str) {
                tick.movedLabel = currentTick.label;
                moved = true;
                currentTick.labelPos = tick.movedLabel.xy;
                delete currentTick.label;
              }
            });
          }

          if (!moved && (tick.labelPos || label)) {
            labelPos = tick.labelPos || label.xy;
            xPos = inverted ? labelPos.x : reversed ? 0 : xAxis.width + xAxis.left;
            yPos = inverted ? reversed ? xAxis.width + xAxis.left : 0 : labelPos.y;
            tick.movedLabel = tick.createLabel({
              x: xPos,
              y: yPos
            }, str, labelOptions);

            if (tick.movedLabel) {
              tick.movedLabel.attr({
                opacity: 0
              });
            }
          }
        };

        Tick.prototype.render = function (index, old, opacity) {
          var tick = this,
              axis = tick.axis,
              horiz = axis.horiz,
              pos = tick.pos,
              tickmarkOffset = pick(tick.tickmarkOffset, axis.tickmarkOffset),
              xy = tick.getPosition(horiz, pos, tickmarkOffset, old),
              x = xy.x,
              y = xy.y,
              reverseCrisp = horiz && x === axis.pos + axis.len || !horiz && y === axis.pos ? -1 : 1;
          opacity = pick(opacity, 1);
          this.isActive = true;
          this.renderGridLine(old, opacity, reverseCrisp);
          this.renderMark(xy, opacity, reverseCrisp);
          this.renderLabel(xy, old, opacity, index);
          tick.isNew = false;
          H.fireEvent(this, 'afterRender');
        };

        Tick.prototype.renderGridLine = function (old, opacity, reverseCrisp) {
          var tick = this,
              axis = tick.axis,
              options = axis.options,
              gridLine = tick.gridLine,
              gridLinePath,
              attribs = {},
              pos = tick.pos,
              type = tick.type,
              tickmarkOffset = pick(tick.tickmarkOffset, axis.tickmarkOffset),
              renderer = axis.chart.renderer,
              gridPrefix = type ? type + 'Grid' : 'grid',
              gridLineWidth = options[gridPrefix + 'LineWidth'],
              gridLineColor = options[gridPrefix + 'LineColor'],
              dashStyle = options[gridPrefix + 'LineDashStyle'];

          if (!gridLine) {
            if (!axis.chart.styledMode) {
              attribs.stroke = gridLineColor;
              attribs['stroke-width'] = gridLineWidth;

              if (dashStyle) {
                attribs.dashstyle = dashStyle;
              }
            }

            if (!type) {
              attribs.zIndex = 1;
            }

            if (old) {
              opacity = 0;
            }

            tick.gridLine = gridLine = renderer.path().attr(attribs).addClass('highcharts-' + (type ? type + '-' : '') + 'grid-line').add(axis.gridGroup);
          }

          if (gridLine) {
            gridLinePath = axis.getPlotLinePath({
              value: pos + tickmarkOffset,
              lineWidth: gridLine.strokeWidth() * reverseCrisp,
              force: 'pass',
              old: old
            });

            if (gridLinePath) {
              gridLine[old || tick.isNew ? 'attr' : 'animate']({
                d: gridLinePath,
                opacity: opacity
              });
            }
          }
        };

        Tick.prototype.renderMark = function (xy, opacity, reverseCrisp) {
          var tick = this,
              axis = tick.axis,
              options = axis.options,
              renderer = axis.chart.renderer,
              type = tick.type,
              tickPrefix = type ? type + 'Tick' : 'tick',
              tickSize = axis.tickSize(tickPrefix),
              mark = tick.mark,
              isNewMark = !mark,
              x = xy.x,
              y = xy.y,
              tickWidth = pick(options[tickPrefix + 'Width'], !type && axis.isXAxis ? 1 : 0),
              tickColor = options[tickPrefix + 'Color'];

          if (tickSize) {
            if (axis.opposite) {
              tickSize[0] = -tickSize[0];
            }

            if (isNewMark) {
              tick.mark = mark = renderer.path().addClass('highcharts-' + (type ? type + '-' : '') + 'tick').add(axis.axisGroup);

              if (!axis.chart.styledMode) {
                mark.attr({
                  stroke: tickColor,
                  'stroke-width': tickWidth
                });
              }
            }

            mark[isNewMark ? 'attr' : 'animate']({
              d: tick.getMarkPath(x, y, tickSize[0], mark.strokeWidth() * reverseCrisp, axis.horiz, renderer),
              opacity: opacity
            });
          }
        };

        Tick.prototype.renderLabel = function (xy, old, opacity, index) {
          var tick = this,
              axis = tick.axis,
              horiz = axis.horiz,
              options = axis.options,
              label = tick.label,
              labelOptions = options.labels,
              step = labelOptions.step,
              tickmarkOffset = pick(tick.tickmarkOffset, axis.tickmarkOffset),
              show = true,
              x = xy.x,
              y = xy.y;

          if (label && isNumber(x)) {
            label.xy = xy = tick.getLabelPosition(x, y, label, horiz, labelOptions, tickmarkOffset, index, step);

            if (tick.isFirst && !tick.isLast && !pick(options.showFirstLabel, 1) || tick.isLast && !tick.isFirst && !pick(options.showLastLabel, 1)) {
              show = false;
            } else if (horiz && !labelOptions.step && !labelOptions.rotation && !old && opacity !== 0) {
              tick.handleOverflow(xy);
            }

            if (step && index % step) {
              show = false;
            }

            if (show && isNumber(xy.y)) {
              xy.opacity = opacity;
              label[tick.isNewLabel ? 'attr' : 'animate'](xy);
              tick.isNewLabel = false;
            } else {
              label.attr('y', -9999);
              tick.isNewLabel = true;
            }
          }
        };

        Tick.prototype.replaceMovedLabel = function () {
          var tick = this,
              label = tick.label,
              axis = tick.axis,
              reversed = axis.reversed,
              chart = tick.axis.chart,
              inverted = chart.inverted,
              x,
              y;

          if (label && !tick.isNew) {
            x = inverted ? label.xy.x : reversed ? axis.left : axis.width + axis.left;
            y = inverted ? reversed ? axis.width + axis.top : axis.top : label.xy.y;
            label.animate({
              x: x,
              y: y,
              opacity: 0
            }, void 0, label.destroy);
            delete tick.label;
          }

          axis.isDirty = true;
          tick.label = tick.movedLabel;
          delete tick.movedLabel;
        };

        return Tick;
      }();

      H.Tick = Tick;
      return H.Tick;
    });

    _registerModule(_modules, 'parts/Time.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (Highcharts, U) {
      var defined = U.defined,
          error = U.error,
          extend = U.extend,
          isObject = U.isObject,
          merge = U.merge,
          objectEach = U.objectEach,
          pad = U.pad,
          pick = U.pick,
          splat = U.splat,
          timeUnits = U.timeUnits;
      var H = Highcharts,
          win = H.win;

      var Time = function () {
        function Time(options) {
          this.options = {};
          this.useUTC = false;
          this.variableTimezone = false;
          this.Date = win.Date;
          this.getTimezoneOffset = this.timezoneOffsetFunction();
          this.update(options);
        }

        Time.prototype.get = function (unit, date) {
          if (this.variableTimezone || this.timezoneOffset) {
            var realMs = date.getTime();
            var ms = realMs - this.getTimezoneOffset(date);
            date.setTime(ms);
            var ret = date['getUTC' + unit]();
            date.setTime(realMs);
            return ret;
          }

          if (this.useUTC) {
            return date['getUTC' + unit]();
          }

          return date['get' + unit]();
        };

        Time.prototype.set = function (unit, date, value) {
          if (this.variableTimezone || this.timezoneOffset) {
            if (unit === 'Milliseconds' || unit === 'Seconds' || unit === 'Minutes') {
              return date['setUTC' + unit](value);
            }

            var offset = this.getTimezoneOffset(date);
            var ms = date.getTime() - offset;
            date.setTime(ms);
            date['setUTC' + unit](value);
            var newOffset = this.getTimezoneOffset(date);
            ms = date.getTime() + newOffset;
            return date.setTime(ms);
          }

          if (this.useUTC) {
            return date['setUTC' + unit](value);
          }

          return date['set' + unit](value);
        };

        Time.prototype.update = function (options) {
          var useUTC = pick(options && options.useUTC, true);
          this.options = options = merge(true, this.options || {}, options);
          this.Date = options.Date || win.Date || Date;
          this.useUTC = useUTC;
          this.timezoneOffset = useUTC && options.timezoneOffset;
          this.getTimezoneOffset = this.timezoneOffsetFunction();
          this.variableTimezone = !!(!useUTC || options.getTimezoneOffset || options.timezone);
        };

        Time.prototype.makeTime = function (year, month, date, hours, minutes, seconds) {
          var d, offset, newOffset;

          if (this.useUTC) {
            d = this.Date.UTC.apply(0, arguments);
            offset = this.getTimezoneOffset(d);
            d += offset;
            newOffset = this.getTimezoneOffset(d);

            if (offset !== newOffset) {
              d += newOffset - offset;
            } else if (offset - 36e5 === this.getTimezoneOffset(d - 36e5) && !H.isSafari) {
              d -= 36e5;
            }
          } else {
            d = new this.Date(year, month, pick(date, 1), pick(hours, 0), pick(minutes, 0), pick(seconds, 0)).getTime();
          }

          return d;
        };

        Time.prototype.timezoneOffsetFunction = function () {
          var time = this,
              options = this.options,
              moment = win.moment;

          if (!this.useUTC) {
            return function (timestamp) {
              return new Date(timestamp.toString()).getTimezoneOffset() * 60000;
            };
          }

          if (options.timezone) {
            if (!moment) {
              error(25);
            } else {
              return function (timestamp) {
                return -moment.tz(timestamp, options.timezone).utcOffset() * 60000;
              };
            }
          }

          if (this.useUTC && options.getTimezoneOffset) {
            return function (timestamp) {
              return options.getTimezoneOffset(timestamp.valueOf()) * 60000;
            };
          }

          return function () {
            return (time.timezoneOffset || 0) * 60000;
          };
        };

        Time.prototype.dateFormat = function (format, timestamp, capitalize) {
          var _a;

          if (!defined(timestamp) || isNaN(timestamp)) {
            return ((_a = H.defaultOptions.lang) === null || _a === void 0 ? void 0 : _a.invalidDate) || '';
          }

          format = pick(format, '%Y-%m-%d %H:%M:%S');
          var time = this,
              date = new this.Date(timestamp),
              hours = this.get('Hours', date),
              day = this.get('Day', date),
              dayOfMonth = this.get('Date', date),
              month = this.get('Month', date),
              fullYear = this.get('FullYear', date),
              lang = H.defaultOptions.lang,
              langWeekdays = lang === null || lang === void 0 ? void 0 : lang.weekdays,
              shortWeekdays = lang === null || lang === void 0 ? void 0 : lang.shortWeekdays,
              replacements = extend({
            a: shortWeekdays ? shortWeekdays[day] : langWeekdays[day].substr(0, 3),
            A: langWeekdays[day],
            d: pad(dayOfMonth),
            e: pad(dayOfMonth, 2, ' '),
            w: day,
            b: lang.shortMonths[month],
            B: lang.months[month],
            m: pad(month + 1),
            o: month + 1,
            y: fullYear.toString().substr(2, 2),
            Y: fullYear,
            H: pad(hours),
            k: hours,
            I: pad(hours % 12 || 12),
            l: hours % 12 || 12,
            M: pad(this.get('Minutes', date)),
            p: hours < 12 ? 'AM' : 'PM',
            P: hours < 12 ? 'am' : 'pm',
            S: pad(date.getSeconds()),
            L: pad(Math.floor(timestamp % 1000), 3)
          }, H.dateFormats);
          objectEach(replacements, function (val, key) {
            while (format.indexOf('%' + key) !== -1) {
              format = format.replace('%' + key, typeof val === 'function' ? val.call(time, timestamp) : val);
            }
          });
          return capitalize ? format.substr(0, 1).toUpperCase() + format.substr(1) : format;
        };

        Time.prototype.resolveDTLFormat = function (f) {
          if (!isObject(f, true)) {
            f = splat(f);
            return {
              main: f[0],
              from: f[1],
              to: f[2]
            };
          }

          return f;
        };

        Time.prototype.getTimeTicks = function (normalizedInterval, min, max, startOfWeek) {
          var time = this,
              Date = time.Date,
              tickPositions = [],
              i,
              higherRanks = {},
              minYear,
              minDate = new Date(min),
              interval = normalizedInterval.unitRange,
              count = normalizedInterval.count || 1,
              variableDayLength,
              minDay;
          startOfWeek = pick(startOfWeek, 1);

          if (defined(min)) {
            time.set('Milliseconds', minDate, interval >= timeUnits.second ? 0 : count * Math.floor(time.get('Milliseconds', minDate) / count));

            if (interval >= timeUnits.second) {
              time.set('Seconds', minDate, interval >= timeUnits.minute ? 0 : count * Math.floor(time.get('Seconds', minDate) / count));
            }

            if (interval >= timeUnits.minute) {
              time.set('Minutes', minDate, interval >= timeUnits.hour ? 0 : count * Math.floor(time.get('Minutes', minDate) / count));
            }

            if (interval >= timeUnits.hour) {
              time.set('Hours', minDate, interval >= timeUnits.day ? 0 : count * Math.floor(time.get('Hours', minDate) / count));
            }

            if (interval >= timeUnits.day) {
              time.set('Date', minDate, interval >= timeUnits.month ? 1 : Math.max(1, count * Math.floor(time.get('Date', minDate) / count)));
            }

            if (interval >= timeUnits.month) {
              time.set('Month', minDate, interval >= timeUnits.year ? 0 : count * Math.floor(time.get('Month', minDate) / count));
              minYear = time.get('FullYear', minDate);
            }

            if (interval >= timeUnits.year) {
              minYear -= minYear % count;
              time.set('FullYear', minDate, minYear);
            }

            if (interval === timeUnits.week) {
              minDay = time.get('Day', minDate);
              time.set('Date', minDate, time.get('Date', minDate) - minDay + startOfWeek + (minDay < startOfWeek ? -7 : 0));
            }

            minYear = time.get('FullYear', minDate);
            var minMonth = time.get('Month', minDate),
                minDateDate = time.get('Date', minDate),
                minHours = time.get('Hours', minDate);
            min = minDate.getTime();

            if (time.variableTimezone) {
              variableDayLength = max - min > 4 * timeUnits.month || time.getTimezoneOffset(min) !== time.getTimezoneOffset(max);
            }

            var t = minDate.getTime();
            i = 1;

            while (t < max) {
              tickPositions.push(t);

              if (interval === timeUnits.year) {
                t = time.makeTime(minYear + i * count, 0);
              } else if (interval === timeUnits.month) {
                t = time.makeTime(minYear, minMonth + i * count);
              } else if (variableDayLength && (interval === timeUnits.day || interval === timeUnits.week)) {
                t = time.makeTime(minYear, minMonth, minDateDate + i * count * (interval === timeUnits.day ? 1 : 7));
              } else if (variableDayLength && interval === timeUnits.hour && count > 1) {
                t = time.makeTime(minYear, minMonth, minDateDate, minHours + i * count);
              } else {
                t += interval * count;
              }

              i++;
            }

            tickPositions.push(t);

            if (interval <= timeUnits.hour && tickPositions.length < 10000) {
              tickPositions.forEach(function (t) {
                if (t % 1800000 === 0 && time.dateFormat('%H%M%S%L', t) === '000000000') {
                  higherRanks[t] = 'day';
                }
              });
            }
          }

          tickPositions.info = extend(normalizedInterval, {
            higherRanks: higherRanks,
            totalRange: interval * count
          });
          return tickPositions;
        };

        Time.defaultOptions = {
          Date: void 0,
          getTimezoneOffset: void 0,
          timezone: void 0,
          timezoneOffset: 0,
          useUTC: true
        };
        return Time;
      }();

      H.Time = Time;
      return H.Time;
    });

    _registerModule(_modules, 'parts/Options.js', [_modules['parts/Globals.js'], _modules['parts/Time.js'], _modules['parts/Color.js'], _modules['parts/Utilities.js']], function (H, Time, Color, U) {
      var color = Color.parse;
      var merge = U.merge;
      var isTouchDevice = H.isTouchDevice,
          svg = H.svg;
      H.defaultOptions = {
        colors: '#7cb5ec #434348 #90ed7d #f7a35c #8085e9 #f15c80 #e4d354 #2b908f #f45b5b #91e8e1'.split(' '),
        symbols: ['circle', 'diamond', 'square', 'triangle', 'triangle-down'],
        lang: {
          loading: 'Loading...',
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          decimalPoint: '.',
          numericSymbols: ['k', 'M', 'G', 'T', 'P', 'E'],
          resetZoom: 'Reset zoom',
          resetZoomTitle: 'Reset zoom level 1:1',
          thousandsSep: ' '
        },
        global: {},
        time: Time.defaultOptions,
        chart: {
          styledMode: false,
          borderRadius: 0,
          colorCount: 10,
          defaultSeriesType: 'line',
          ignoreHiddenSeries: true,
          spacing: [10, 10, 15, 10],
          resetZoomButton: {
            theme: {
              zIndex: 6
            },
            position: {
              align: 'right',
              x: -10,
              y: 10
            }
          },
          width: null,
          height: null,
          borderColor: '#335cad',
          backgroundColor: '#ffffff',
          plotBorderColor: '#cccccc'
        },
        title: {
          text: 'Chart title',
          align: 'center',
          margin: 15,
          widthAdjust: -44
        },
        subtitle: {
          text: '',
          align: 'center',
          widthAdjust: -44
        },
        caption: {
          margin: 15,
          text: '',
          align: 'left',
          verticalAlign: 'bottom'
        },
        plotOptions: {},
        labels: {
          style: {
            position: 'absolute',
            color: '#333333'
          }
        },
        legend: {
          enabled: true,
          align: 'center',
          alignColumns: true,
          layout: 'horizontal',
          labelFormatter: function labelFormatter() {
            return this.name;
          },
          borderColor: '#999999',
          borderRadius: 0,
          navigation: {
            activeColor: '#003399',
            inactiveColor: '#cccccc'
          },
          itemStyle: {
            color: '#333333',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 'bold',
            textOverflow: 'ellipsis'
          },
          itemHoverStyle: {
            color: '#000000'
          },
          itemHiddenStyle: {
            color: '#cccccc'
          },
          shadow: false,
          itemCheckboxStyle: {
            position: 'absolute',
            width: '13px',
            height: '13px'
          },
          squareSymbol: true,
          symbolPadding: 5,
          verticalAlign: 'bottom',
          x: 0,
          y: 0,
          title: {
            style: {
              fontWeight: 'bold'
            }
          }
        },
        loading: {
          labelStyle: {
            fontWeight: 'bold',
            position: 'relative',
            top: '45%'
          },
          style: {
            position: 'absolute',
            backgroundColor: '#ffffff',
            opacity: 0.5,
            textAlign: 'center'
          }
        },
        tooltip: {
          enabled: true,
          animation: svg,
          borderRadius: 3,
          dateTimeLabelFormats: {
            millisecond: '%A, %b %e, %H:%M:%S.%L',
            second: '%A, %b %e, %H:%M:%S',
            minute: '%A, %b %e, %H:%M',
            hour: '%A, %b %e, %H:%M',
            day: '%A, %b %e, %Y',
            week: 'Week from %A, %b %e, %Y',
            month: '%B %Y',
            year: '%Y'
          },
          footerFormat: '',
          padding: 8,
          snap: isTouchDevice ? 25 : 10,
          headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
          pointFormat: "<span style=\"color:{point.color}\">\u25CF</span> {series.name}: <b>{point.y}</b><br/>",
          backgroundColor: color('#f7f7f7').setOpacity(0.85).get(),
          borderWidth: 1,
          shadow: true,
          style: {
            color: '#333333',
            cursor: 'default',
            fontSize: '12px',
            whiteSpace: 'nowrap'
          }
        },
        credits: {
          enabled: true,
          href: 'https://www.highcharts.com?credits',
          position: {
            align: 'right',
            x: -10,
            verticalAlign: 'bottom',
            y: -5
          },
          style: {
            cursor: 'pointer',
            color: '#999999',
            fontSize: '9px'
          },
          text: 'Highcharts.com'
        }
      };

      H.setOptions = function (options) {
        H.defaultOptions = merge(true, H.defaultOptions, options);

        if (options.time || options.global) {
          H.time.update(merge(H.defaultOptions.global, H.defaultOptions.time, options.global, options.time));
        }

        return H.defaultOptions;
      };

      H.getOptions = function () {
        return H.defaultOptions;
      };

      H.defaultPlotOptions = H.defaultOptions.plotOptions;
      H.time = new Time(merge(H.defaultOptions.global, H.defaultOptions.time));

      H.dateFormat = function (format, timestamp, capitalize) {
        return H.time.dateFormat(format, timestamp, capitalize);
      };
    });

    _registerModule(_modules, 'parts/Axis.js', [_modules['parts/Globals.js'], _modules['parts/Color.js'], _modules['parts/Tick.js'], _modules['parts/Utilities.js']], function (H, Color, Tick, U) {
      var color = Color.parse;
      var addEvent = U.addEvent,
          animObject = U.animObject,
          arrayMax = U.arrayMax,
          arrayMin = U.arrayMin,
          clamp = U.clamp,
          correctFloat = U.correctFloat,
          defined = U.defined,
          destroyObjectProperties = U.destroyObjectProperties,
          error = U.error,
          extend = U.extend,
          fireEvent = U.fireEvent,
          format = U.format,
          getMagnitude = U.getMagnitude,
          isArray = U.isArray,
          isFunction = U.isFunction,
          isNumber = U.isNumber,
          isString = U.isString,
          merge = U.merge,
          normalizeTickInterval = U.normalizeTickInterval,
          objectEach = U.objectEach,
          pick = U.pick,
          relativeLength = U.relativeLength,
          removeEvent = U.removeEvent,
          splat = U.splat,
          syncTimeout = U.syncTimeout;
      var defaultOptions = H.defaultOptions,
          deg2rad = H.deg2rad;

      var Axis = function Axis() {
        this.init.apply(this, arguments);
      };

      extend(Axis.prototype, {
        defaultOptions: {
          dateTimeLabelFormats: {
            millisecond: {
              main: '%H:%M:%S.%L',
              range: false
            },
            second: {
              main: '%H:%M:%S',
              range: false
            },
            minute: {
              main: '%H:%M',
              range: false
            },
            hour: {
              main: '%H:%M',
              range: false
            },
            day: {
              main: '%e. %b'
            },
            week: {
              main: '%e. %b'
            },
            month: {
              main: '%b \'%y'
            },
            year: {
              main: '%Y'
            }
          },
          endOnTick: false,
          labels: {
            enabled: true,
            indentation: 10,
            x: 0,
            style: {
              color: '#666666',
              cursor: 'default',
              fontSize: '11px'
            }
          },
          maxPadding: 0.01,
          minorTickLength: 2,
          minorTickPosition: 'outside',
          minPadding: 0.01,
          showEmpty: true,
          startOfWeek: 1,
          startOnTick: false,
          tickLength: 10,
          tickPixelInterval: 100,
          tickmarkPlacement: 'between',
          tickPosition: 'outside',
          title: {
            align: 'middle',
            style: {
              color: '#666666'
            }
          },
          type: 'linear',
          minorGridLineColor: '#f2f2f2',
          minorGridLineWidth: 1,
          minorTickColor: '#999999',
          lineColor: '#ccd6eb',
          lineWidth: 1,
          gridLineColor: '#e6e6e6',
          tickColor: '#ccd6eb'
        },
        defaultYAxisOptions: {
          endOnTick: true,
          maxPadding: 0.05,
          minPadding: 0.05,
          tickPixelInterval: 72,
          showLastLabel: true,
          labels: {
            x: -8
          },
          startOnTick: true,
          title: {
            rotation: 270,
            text: 'Values'
          },
          stackLabels: {
            allowOverlap: false,
            enabled: false,
            crop: true,
            overflow: 'justify',
            formatter: function formatter() {
              var numberFormatter = this.axis.chart.numberFormatter;
              return numberFormatter(this.total, -1);
            },
            style: {
              color: '#000000',
              fontSize: '11px',
              fontWeight: 'bold',
              textOutline: '1px contrast'
            }
          },
          gridLineWidth: 1,
          lineWidth: 0
        },
        defaultLeftAxisOptions: {
          labels: {
            x: -15
          },
          title: {
            rotation: 270
          }
        },
        defaultRightAxisOptions: {
          labels: {
            x: 15
          },
          title: {
            rotation: 90
          }
        },
        defaultBottomAxisOptions: {
          labels: {
            autoRotation: [-45],
            x: 0
          },
          margin: 15,
          title: {
            rotation: 0
          }
        },
        defaultTopAxisOptions: {
          labels: {
            autoRotation: [-45],
            x: 0
          },
          margin: 15,
          title: {
            rotation: 0
          }
        },
        init: function init(chart, userOptions) {
          var isXAxis = userOptions.isX,
              axis = this;
          axis.chart = chart;
          axis.horiz = chart.inverted && !axis.isZAxis ? !isXAxis : isXAxis;
          axis.isXAxis = isXAxis;
          axis.coll = axis.coll || (isXAxis ? 'xAxis' : 'yAxis');
          fireEvent(this, 'init', {
            userOptions: userOptions
          });
          axis.opposite = userOptions.opposite;
          axis.side = userOptions.side || (axis.horiz ? axis.opposite ? 0 : 2 : axis.opposite ? 1 : 3);
          axis.setOptions(userOptions);
          var options = this.options,
              type = options.type,
              isDatetimeAxis = type === 'datetime';
          axis.labelFormatter = options.labels.formatter || axis.defaultLabelFormatter;
          axis.userOptions = userOptions;
          axis.minPixelPadding = 0;
          axis.reversed = options.reversed;
          axis.visible = options.visible !== false;
          axis.zoomEnabled = options.zoomEnabled !== false;
          axis.hasNames = type === 'category' || options.categories === true;
          axis.categories = options.categories || axis.hasNames;

          if (!axis.names) {
            axis.names = [];
            axis.names.keys = {};
          }

          axis.plotLinesAndBandsGroups = {};
          axis.isLog = type === 'logarithmic';
          axis.isDatetimeAxis = isDatetimeAxis;
          axis.positiveValuesOnly = axis.isLog && !axis.allowNegativeLog;
          axis.isLinked = defined(options.linkedTo);
          axis.ticks = {};
          axis.labelEdge = [];
          axis.minorTicks = {};
          axis.plotLinesAndBands = [];
          axis.alternateBands = {};
          axis.len = 0;
          axis.minRange = axis.userMinRange = options.minRange || options.maxZoom;
          axis.range = options.range;
          axis.offset = options.offset || 0;
          axis.stacks = {};
          axis.oldStacks = {};
          axis.stacksTouched = 0;
          axis.max = null;
          axis.min = null;
          axis.crosshair = pick(options.crosshair, splat(chart.options.tooltip.crosshairs)[isXAxis ? 0 : 1], false);
          var events = axis.options.events;

          if (chart.axes.indexOf(axis) === -1) {
            if (isXAxis) {
              chart.axes.splice(chart.xAxis.length, 0, axis);
            } else {
              chart.axes.push(axis);
            }

            chart[axis.coll].push(axis);
          }

          axis.series = axis.series || [];

          if (chart.inverted && !axis.isZAxis && isXAxis && typeof axis.reversed === 'undefined') {
            axis.reversed = true;
          }

          objectEach(events, function (event, eventType) {
            if (isFunction(event)) {
              addEvent(axis, eventType, event);
            }
          });
          axis.lin2log = options.linearToLogConverter || axis.lin2log;

          if (axis.isLog) {
            axis.val2lin = axis.log2lin;
            axis.lin2val = axis.lin2log;
          }

          fireEvent(this, 'afterInit');
        },
        setOptions: function setOptions(userOptions) {
          this.options = merge(this.defaultOptions, this.coll === 'yAxis' && this.defaultYAxisOptions, [this.defaultTopAxisOptions, this.defaultRightAxisOptions, this.defaultBottomAxisOptions, this.defaultLeftAxisOptions][this.side], merge(defaultOptions[this.coll], userOptions));
          fireEvent(this, 'afterSetOptions', {
            userOptions: userOptions
          });
        },
        defaultLabelFormatter: function defaultLabelFormatter() {
          var axis = this.axis,
              value = this.value,
              time = axis.chart.time,
              categories = axis.categories,
              dateTimeLabelFormat = this.dateTimeLabelFormat,
              lang = defaultOptions.lang,
              numericSymbols = lang.numericSymbols,
              numSymMagnitude = lang.numericSymbolMagnitude || 1000,
              i = numericSymbols && numericSymbols.length,
              multi,
              ret,
              formatOption = axis.options.labels.format,
              numericSymbolDetector = axis.isLog ? Math.abs(value) : axis.tickInterval;
          var chart = this.chart;
          var numberFormatter = chart.numberFormatter;

          if (formatOption) {
            ret = format(formatOption, this, chart);
          } else if (categories) {
            ret = value;
          } else if (dateTimeLabelFormat) {
            ret = time.dateFormat(dateTimeLabelFormat, value);
          } else if (i && numericSymbolDetector >= 1000) {
            while (i-- && typeof ret === 'undefined') {
              multi = Math.pow(numSymMagnitude, i + 1);

              if (numericSymbolDetector >= multi && value * 10 % multi === 0 && numericSymbols[i] !== null && value !== 0) {
                ret = numberFormatter(value / multi, -1) + numericSymbols[i];
              }
            }
          }

          if (typeof ret === 'undefined') {
            if (Math.abs(value) >= 10000) {
              ret = numberFormatter(value, -1);
            } else {
              ret = numberFormatter(value, -1, void 0, '');
            }
          }

          return ret;
        },
        getSeriesExtremes: function getSeriesExtremes() {
          var axis = this,
              chart = axis.chart,
              xExtremes;
          fireEvent(this, 'getSeriesExtremes', null, function () {
            axis.hasVisibleSeries = false;
            axis.dataMin = axis.dataMax = axis.threshold = null;
            axis.softThreshold = !axis.isXAxis;

            if (axis.buildStacks) {
              axis.buildStacks();
            }

            axis.series.forEach(function (series) {
              if (series.visible || !chart.options.chart.ignoreHiddenSeries) {
                var seriesOptions = series.options,
                    xData,
                    threshold = seriesOptions.threshold,
                    seriesDataMin,
                    seriesDataMax;
                axis.hasVisibleSeries = true;

                if (axis.positiveValuesOnly && threshold <= 0) {
                  threshold = null;
                }

                if (axis.isXAxis) {
                  xData = series.xData;

                  if (xData.length) {
                    xExtremes = series.getXExtremes(xData);
                    seriesDataMin = xExtremes.min;
                    seriesDataMax = xExtremes.max;

                    if (!isNumber(seriesDataMin) && !(seriesDataMin instanceof Date)) {
                      xData = xData.filter(isNumber);
                      xExtremes = series.getXExtremes(xData);
                      seriesDataMin = xExtremes.min;
                      seriesDataMax = xExtremes.max;
                    }

                    if (xData.length) {
                      axis.dataMin = Math.min(pick(axis.dataMin, seriesDataMin), seriesDataMin);
                      axis.dataMax = Math.max(pick(axis.dataMax, seriesDataMax), seriesDataMax);
                    }
                  }
                } else {
                  series.getExtremes();
                  seriesDataMax = series.dataMax;
                  seriesDataMin = series.dataMin;

                  if (defined(seriesDataMin) && defined(seriesDataMax)) {
                    axis.dataMin = Math.min(pick(axis.dataMin, seriesDataMin), seriesDataMin);
                    axis.dataMax = Math.max(pick(axis.dataMax, seriesDataMax), seriesDataMax);
                  }

                  if (defined(threshold)) {
                    axis.threshold = threshold;
                  }

                  if (!seriesOptions.softThreshold || axis.positiveValuesOnly) {
                    axis.softThreshold = false;
                  }
                }
              }
            });
          });
          fireEvent(this, 'afterGetSeriesExtremes');
        },
        translate: function translate(val, backwards, cvsCoord, old, handleLog, pointPlacement) {
          var axis = this.linkedParent || this,
              sign = 1,
              cvsOffset = 0,
              localA = old ? axis.oldTransA : axis.transA,
              localMin = old ? axis.oldMin : axis.min,
              returnValue = 0,
              minPixelPadding = axis.minPixelPadding,
              doPostTranslate = (axis.isOrdinal || axis.isBroken || axis.isLog && handleLog) && axis.lin2val;

          if (!localA) {
            localA = axis.transA;
          }

          if (cvsCoord) {
            sign *= -1;
            cvsOffset = axis.len;
          }

          if (axis.reversed) {
            sign *= -1;
            cvsOffset -= sign * (axis.sector || axis.len);
          }

          if (backwards) {
            val = val * sign + cvsOffset;
            val -= minPixelPadding;
            returnValue = val / localA + localMin;

            if (doPostTranslate) {
              returnValue = axis.lin2val(returnValue);
            }
          } else {
            if (doPostTranslate) {
              val = axis.val2lin(val);
            }

            returnValue = isNumber(localMin) ? sign * (val - localMin) * localA + cvsOffset + sign * minPixelPadding + (isNumber(pointPlacement) ? localA * pointPlacement : 0) : void 0;
          }

          return returnValue;
        },
        toPixels: function toPixels(value, paneCoordinates) {
          return this.translate(value, false, !this.horiz, null, true) + (paneCoordinates ? 0 : this.pos);
        },
        toValue: function toValue(pixel, paneCoordinates) {
          return this.translate(pixel - (paneCoordinates ? 0 : this.pos), true, !this.horiz, null, true);
        },
        getPlotLinePath: function getPlotLinePath(options) {
          var axis = this,
              chart = axis.chart,
              axisLeft = axis.left,
              axisTop = axis.top,
              old = options.old,
              value = options.value,
              translatedValue = options.translatedValue,
              lineWidth = options.lineWidth,
              force = options.force,
              x1,
              y1,
              x2,
              y2,
              cHeight = old && chart.oldChartHeight || chart.chartHeight,
              cWidth = old && chart.oldChartWidth || chart.chartWidth,
              skip,
              transB = axis.transB,
              evt,
              between = function between(x, a, b) {
            if (force !== 'pass' && x < a || x > b) {
              if (force) {
                x = clamp(x, a, b);
              } else {
                skip = true;
              }
            }

            return x;
          };

          evt = {
            value: value,
            lineWidth: lineWidth,
            old: old,
            force: force,
            acrossPanes: options.acrossPanes,
            translatedValue: translatedValue
          };
          fireEvent(this, 'getPlotLinePath', evt, function (e) {
            translatedValue = pick(translatedValue, axis.translate(value, null, null, old));
            translatedValue = clamp(translatedValue, -1e5, 1e5);
            x1 = x2 = Math.round(translatedValue + transB);
            y1 = y2 = Math.round(cHeight - translatedValue - transB);

            if (!isNumber(translatedValue)) {
              skip = true;
              force = false;
            } else if (axis.horiz) {
              y1 = axisTop;
              y2 = cHeight - axis.bottom;
              x1 = x2 = between(x1, axisLeft, axisLeft + axis.width);
            } else {
              x1 = axisLeft;
              x2 = cWidth - axis.right;
              y1 = y2 = between(y1, axisTop, axisTop + axis.height);
            }

            e.path = skip && !force ? null : chart.renderer.crispLine(['M', x1, y1, 'L', x2, y2], lineWidth || 1);
          });
          return evt.path;
        },
        getLinearTickPositions: function getLinearTickPositions(tickInterval, min, max) {
          var pos,
              lastPos,
              roundedMin = correctFloat(Math.floor(min / tickInterval) * tickInterval),
              roundedMax = correctFloat(Math.ceil(max / tickInterval) * tickInterval),
              tickPositions = [],
              precision;

          if (correctFloat(roundedMin + tickInterval) === roundedMin) {
            precision = 20;
          }

          if (this.single) {
            return [min];
          }

          pos = roundedMin;

          while (pos <= roundedMax) {
            tickPositions.push(pos);
            pos = correctFloat(pos + tickInterval, precision);

            if (pos === lastPos) {
              break;
            }

            lastPos = pos;
          }

          return tickPositions;
        },
        getMinorTickInterval: function getMinorTickInterval() {
          var options = this.options;

          if (options.minorTicks === true) {
            return pick(options.minorTickInterval, 'auto');
          }

          if (options.minorTicks === false) {
            return null;
          }

          return options.minorTickInterval;
        },
        getMinorTickPositions: function getMinorTickPositions() {
          var axis = this,
              options = axis.options,
              tickPositions = axis.tickPositions,
              minorTickInterval = axis.minorTickInterval,
              minorTickPositions = [],
              pos,
              pointRangePadding = axis.pointRangePadding || 0,
              min = axis.min - pointRangePadding,
              max = axis.max + pointRangePadding,
              range = max - min;

          if (range && range / minorTickInterval < axis.len / 3) {
            if (axis.isLog) {
              this.paddedTicks.forEach(function (pos, i, paddedTicks) {
                if (i) {
                  minorTickPositions.push.apply(minorTickPositions, axis.getLogTickPositions(minorTickInterval, paddedTicks[i - 1], paddedTicks[i], true));
                }
              });
            } else if (axis.isDatetimeAxis && this.getMinorTickInterval() === 'auto') {
              minorTickPositions = minorTickPositions.concat(axis.getTimeTicks(axis.normalizeTimeTickInterval(minorTickInterval), min, max, options.startOfWeek));
            } else {
              for (pos = min + (tickPositions[0] - min) % minorTickInterval; pos <= max; pos += minorTickInterval) {
                if (pos === minorTickPositions[0]) {
                  break;
                }

                minorTickPositions.push(pos);
              }
            }
          }

          if (minorTickPositions.length !== 0) {
            axis.trimTicks(minorTickPositions);
          }

          return minorTickPositions;
        },
        adjustForMinRange: function adjustForMinRange() {
          var axis = this,
              options = axis.options,
              min = axis.min,
              max = axis.max,
              zoomOffset,
              spaceAvailable,
              closestDataRange,
              i,
              distance,
              xData,
              loopLength,
              minArgs,
              maxArgs,
              minRange;

          if (axis.isXAxis && typeof axis.minRange === 'undefined' && !axis.isLog) {
            if (defined(options.min) || defined(options.max)) {
              axis.minRange = null;
            } else {
              axis.series.forEach(function (series) {
                xData = series.xData;
                loopLength = series.xIncrement ? 1 : xData.length - 1;

                for (i = loopLength; i > 0; i--) {
                  distance = xData[i] - xData[i - 1];

                  if (typeof closestDataRange === 'undefined' || distance < closestDataRange) {
                    closestDataRange = distance;
                  }
                }
              });
              axis.minRange = Math.min(closestDataRange * 5, axis.dataMax - axis.dataMin);
            }
          }

          if (max - min < axis.minRange) {
            spaceAvailable = axis.dataMax - axis.dataMin >= axis.minRange;
            minRange = axis.minRange;
            zoomOffset = (minRange - max + min) / 2;
            minArgs = [min - zoomOffset, pick(options.min, min - zoomOffset)];

            if (spaceAvailable) {
              minArgs[2] = axis.isLog ? axis.log2lin(axis.dataMin) : axis.dataMin;
            }

            min = arrayMax(minArgs);
            maxArgs = [min + minRange, pick(options.max, min + minRange)];

            if (spaceAvailable) {
              maxArgs[2] = axis.isLog ? axis.log2lin(axis.dataMax) : axis.dataMax;
            }

            max = arrayMin(maxArgs);

            if (max - min < minRange) {
              minArgs[0] = max - minRange;
              minArgs[1] = pick(options.min, max - minRange);
              min = arrayMax(minArgs);
            }
          }

          axis.min = min;
          axis.max = max;
        },
        getClosest: function getClosest() {
          var ret;

          if (this.categories) {
            ret = 1;
          } else {
            this.series.forEach(function (series) {
              var seriesClosest = series.closestPointRange,
                  visible = series.visible || !series.chart.options.chart.ignoreHiddenSeries;

              if (!series.noSharedTooltip && defined(seriesClosest) && visible) {
                ret = defined(ret) ? Math.min(ret, seriesClosest) : seriesClosest;
              }
            });
          }

          return ret;
        },
        nameToX: function nameToX(point) {
          var explicitCategories = isArray(this.categories),
              names = explicitCategories ? this.categories : this.names,
              nameX = point.options.x,
              x;
          point.series.requireSorting = false;

          if (!defined(nameX)) {
            nameX = this.options.uniqueNames === false ? point.series.autoIncrement() : explicitCategories ? names.indexOf(point.name) : pick(names.keys[point.name], -1);
          }

          if (nameX === -1) {
            if (!explicitCategories) {
              x = names.length;
            }
          } else {
            x = nameX;
          }

          if (typeof x !== 'undefined') {
            this.names[x] = point.name;
            this.names.keys[point.name] = x;
          }

          return x;
        },
        updateNames: function updateNames() {
          var axis = this,
              names = this.names,
              i = names.length;

          if (i > 0) {
            Object.keys(names.keys).forEach(function (key) {
              delete names.keys[key];
            });
            names.length = 0;
            this.minRange = this.userMinRange;
            (this.series || []).forEach(function (series) {
              series.xIncrement = null;

              if (!series.points || series.isDirtyData) {
                axis.max = Math.max(axis.max, series.xData.length - 1);
                series.processData();
                series.generatePoints();
              }

              series.data.forEach(function (point, i) {
                var x;

                if (point && point.options && typeof point.name !== 'undefined') {
                    x = axis.nameToX(point);

                    if (typeof x !== 'undefined' && x !== point.x) {
                      point.x = x;
                      series.xData[i] = x;
                    }
                  }
              });
            });
          }
        },
        setAxisTranslation: function setAxisTranslation(saveOld) {
          var axis = this,
              range = axis.max - axis.min,
              pointRange = axis.axisPointRange || 0,
              closestPointRange,
              minPointOffset = 0,
              pointRangePadding = 0,
              linkedParent = axis.linkedParent,
              ordinalCorrection,
              hasCategories = !!axis.categories,
              transA = axis.transA,
              isXAxis = axis.isXAxis;

          if (isXAxis || hasCategories || pointRange) {
            closestPointRange = axis.getClosest();

            if (linkedParent) {
              minPointOffset = linkedParent.minPointOffset;
              pointRangePadding = linkedParent.pointRangePadding;
            } else {
              axis.series.forEach(function (series) {
                var seriesPointRange = hasCategories ? 1 : isXAxis ? pick(series.options.pointRange, closestPointRange, 0) : axis.axisPointRange || 0,
                    pointPlacement = series.options.pointPlacement;
                pointRange = Math.max(pointRange, seriesPointRange);

                if (!axis.single || hasCategories) {
                  var isPointPlacementAxis = series.is('xrange') ? !isXAxis : isXAxis;
                  minPointOffset = Math.max(minPointOffset, isPointPlacementAxis && isString(pointPlacement) ? 0 : seriesPointRange / 2);
                  pointRangePadding = Math.max(pointRangePadding, isPointPlacementAxis && pointPlacement === 'on' ? 0 : seriesPointRange);
                }
              });
            }

            ordinalCorrection = axis.ordinalSlope && closestPointRange ? axis.ordinalSlope / closestPointRange : 1;
            axis.minPointOffset = minPointOffset = minPointOffset * ordinalCorrection;
            axis.pointRangePadding = pointRangePadding = pointRangePadding * ordinalCorrection;
            axis.pointRange = Math.min(pointRange, axis.single && hasCategories ? 1 : range);

            if (isXAxis) {
              axis.closestPointRange = closestPointRange;
            }
          }

          if (saveOld) {
            axis.oldTransA = transA;
          }

          axis.translationSlope = axis.transA = transA = axis.staticScale || axis.len / (range + pointRangePadding || 1);
          axis.transB = axis.horiz ? axis.left : axis.bottom;
          axis.minPixelPadding = transA * minPointOffset;
          fireEvent(this, 'afterSetAxisTranslation');
        },
        minFromRange: function minFromRange() {
          return this.max - this.range;
        },
        setTickInterval: function setTickInterval(secondPass) {
          var axis = this,
              chart = axis.chart,
              options = axis.options,
              isLog = axis.isLog,
              isDatetimeAxis = axis.isDatetimeAxis,
              isXAxis = axis.isXAxis,
              isLinked = axis.isLinked,
              maxPadding = options.maxPadding,
              minPadding = options.minPadding,
              length,
              linkedParentExtremes,
              tickIntervalOption = options.tickInterval,
              minTickInterval,
              tickPixelIntervalOption = options.tickPixelInterval,
              categories = axis.categories,
              threshold = isNumber(axis.threshold) ? axis.threshold : null,
              softThreshold = axis.softThreshold,
              thresholdMin,
              thresholdMax,
              hardMin,
              hardMax;

          if (!isDatetimeAxis && !categories && !isLinked) {
            this.getTickAmount();
          }

          hardMin = pick(axis.userMin, options.min);
          hardMax = pick(axis.userMax, options.max);

          if (isLinked) {
            axis.linkedParent = chart[axis.coll][options.linkedTo];
            linkedParentExtremes = axis.linkedParent.getExtremes();
            axis.min = pick(linkedParentExtremes.min, linkedParentExtremes.dataMin);
            axis.max = pick(linkedParentExtremes.max, linkedParentExtremes.dataMax);

            if (options.type !== axis.linkedParent.options.type) {
              error(11, 1, chart);
            }
          } else {
            if (!softThreshold && defined(threshold)) {
              if (axis.dataMin >= threshold) {
                thresholdMin = threshold;
                minPadding = 0;
              } else if (axis.dataMax <= threshold) {
                thresholdMax = threshold;
                maxPadding = 0;
              }
            }

            axis.min = pick(hardMin, thresholdMin, axis.dataMin);
            axis.max = pick(hardMax, thresholdMax, axis.dataMax);
          }

          if (isLog) {
            if (axis.positiveValuesOnly && !secondPass && Math.min(axis.min, pick(axis.dataMin, axis.min)) <= 0) {
              error(10, 1, chart);
            }

            axis.min = correctFloat(axis.log2lin(axis.min), 16);
            axis.max = correctFloat(axis.log2lin(axis.max), 16);
          }

          if (axis.range && defined(axis.max)) {
            axis.userMin = axis.min = hardMin = Math.max(axis.dataMin, axis.minFromRange());
            axis.userMax = hardMax = axis.max;
            axis.range = null;
          }

          fireEvent(axis, 'foundExtremes');

          if (axis.beforePadding) {
            axis.beforePadding();
          }

          axis.adjustForMinRange();

          if (!categories && !axis.axisPointRange && !axis.usePercentage && !isLinked && defined(axis.min) && defined(axis.max)) {
            length = axis.max - axis.min;

            if (length) {
              if (!defined(hardMin) && minPadding) {
                axis.min -= length * minPadding;
              }

              if (!defined(hardMax) && maxPadding) {
                axis.max += length * maxPadding;
              }
            }
          }

          if (!isNumber(axis.userMin)) {
            if (isNumber(options.softMin) && options.softMin < axis.min) {
              axis.min = hardMin = options.softMin;
            }

            if (isNumber(options.floor)) {
              axis.min = Math.max(axis.min, options.floor);
            }
          }

          if (!isNumber(axis.userMax)) {
            if (isNumber(options.softMax) && options.softMax > axis.max) {
              axis.max = hardMax = options.softMax;
            }

            if (isNumber(options.ceiling)) {
              axis.max = Math.min(axis.max, options.ceiling);
            }
          }

          if (softThreshold && defined(axis.dataMin)) {
            threshold = threshold || 0;

            if (!defined(hardMin) && axis.min < threshold && axis.dataMin >= threshold) {
              axis.min = axis.options.minRange ? Math.min(threshold, axis.max - axis.minRange) : threshold;
            } else if (!defined(hardMax) && axis.max > threshold && axis.dataMax <= threshold) {
              axis.max = axis.options.minRange ? Math.max(threshold, axis.min + axis.minRange) : threshold;
            }
          }

          if (axis.min === axis.max || typeof axis.min === 'undefined' || typeof axis.max === 'undefined') {
            axis.tickInterval = 1;
          } else if (isLinked && !tickIntervalOption && tickPixelIntervalOption === axis.linkedParent.options.tickPixelInterval) {
            axis.tickInterval = tickIntervalOption = axis.linkedParent.tickInterval;
          } else {
            axis.tickInterval = pick(tickIntervalOption, this.tickAmount ? (axis.max - axis.min) / Math.max(this.tickAmount - 1, 1) : void 0, categories ? 1 : (axis.max - axis.min) * tickPixelIntervalOption / Math.max(axis.len, tickPixelIntervalOption));
          }

          if (isXAxis && !secondPass) {
            axis.series.forEach(function (series) {
              series.processData(axis.min !== axis.oldMin || axis.max !== axis.oldMax);
            });
          }

          axis.setAxisTranslation(true);

          if (axis.beforeSetTickPositions) {
            axis.beforeSetTickPositions();
          }

          if (axis.postProcessTickInterval) {
            axis.tickInterval = axis.postProcessTickInterval(axis.tickInterval);
          }

          if (axis.pointRange && !tickIntervalOption) {
            axis.tickInterval = Math.max(axis.pointRange, axis.tickInterval);
          }

          minTickInterval = pick(options.minTickInterval, axis.isDatetimeAxis && axis.closestPointRange);

          if (!tickIntervalOption && axis.tickInterval < minTickInterval) {
            axis.tickInterval = minTickInterval;
          }

          if (!isDatetimeAxis && !isLog && !tickIntervalOption) {
            axis.tickInterval = normalizeTickInterval(axis.tickInterval, null, getMagnitude(axis.tickInterval), pick(options.allowDecimals, !(axis.tickInterval > 0.5 && axis.tickInterval < 5 && axis.max > 1000 && axis.max < 9999)), !!this.tickAmount);
          }

          if (!this.tickAmount) {
            axis.tickInterval = axis.unsquish();
          }

          this.setTickPositions();
        },
        setTickPositions: function setTickPositions() {
          var options = this.options,
              tickPositions,
              tickPositionsOption = options.tickPositions,
              minorTickIntervalOption = this.getMinorTickInterval(),
              tickPositioner = options.tickPositioner,
              startOnTick = options.startOnTick,
              endOnTick = options.endOnTick;
          this.tickmarkOffset = this.categories && options.tickmarkPlacement === 'between' && this.tickInterval === 1 ? 0.5 : 0;
          this.minorTickInterval = minorTickIntervalOption === 'auto' && this.tickInterval ? this.tickInterval / 5 : minorTickIntervalOption;
          this.single = this.min === this.max && defined(this.min) && !this.tickAmount && (parseInt(this.min, 10) === this.min || options.allowDecimals !== false);
          this.tickPositions = tickPositions = tickPositionsOption && tickPositionsOption.slice();

          if (!tickPositions) {
            if (!this.ordinalPositions && (this.max - this.min) / this.tickInterval > Math.max(2 * this.len, 200)) {
              tickPositions = [this.min, this.max];
              error(19, false, this.chart);
            } else if (this.isDatetimeAxis) {
              tickPositions = this.getTimeTicks(this.normalizeTimeTickInterval(this.tickInterval, options.units), this.min, this.max, options.startOfWeek, this.ordinalPositions, this.closestPointRange, true);
            } else if (this.isLog) {
              tickPositions = this.getLogTickPositions(this.tickInterval, this.min, this.max);
            } else {
              tickPositions = this.getLinearTickPositions(this.tickInterval, this.min, this.max);
            }

            if (tickPositions.length > this.len) {
              tickPositions = [tickPositions[0], tickPositions.pop()];

              if (tickPositions[0] === tickPositions[1]) {
                tickPositions.length = 1;
              }
            }

            this.tickPositions = tickPositions;

            if (tickPositioner) {
              tickPositioner = tickPositioner.apply(this, [this.min, this.max]);

              if (tickPositioner) {
                this.tickPositions = tickPositions = tickPositioner;
              }
            }
          }

          this.paddedTicks = tickPositions.slice(0);
          this.trimTicks(tickPositions, startOnTick, endOnTick);

          if (!this.isLinked) {
            if (this.single && tickPositions.length < 2 && !this.categories && !this.series.some(function (s) {
              return s.is('heatmap') && s.options.pointPlacement === 'between';
            })) {
              this.min -= 0.5;
              this.max += 0.5;
            }

            if (!tickPositionsOption && !tickPositioner) {
              this.adjustTickAmount();
            }
          }

          fireEvent(this, 'afterSetTickPositions');
        },
        trimTicks: function trimTicks(tickPositions, startOnTick, endOnTick) {
          var roundedMin = tickPositions[0],
              roundedMax = tickPositions[tickPositions.length - 1],
              minPointOffset = !this.isOrdinal && this.minPointOffset || 0;
          fireEvent(this, 'trimTicks');

          if (!this.isLinked) {
            if (startOnTick && roundedMin !== -Infinity) {
              this.min = roundedMin;
            } else {
              while (this.min - minPointOffset > tickPositions[0]) {
                tickPositions.shift();
              }
            }

            if (endOnTick) {
              this.max = roundedMax;
            } else {
              while (this.max + minPointOffset < tickPositions[tickPositions.length - 1]) {
                tickPositions.pop();
              }
            }

            if (tickPositions.length === 0 && defined(roundedMin) && !this.options.tickPositions) {
              tickPositions.push((roundedMax + roundedMin) / 2);
            }
          }
        },
        alignToOthers: function alignToOthers() {
          var others = {},
              hasOther,
              options = this.options;

          if (this.chart.options.chart.alignTicks !== false && options.alignTicks !== false && options.startOnTick !== false && options.endOnTick !== false && !this.isLog) {
            this.chart[this.coll].forEach(function (axis) {
              var otherOptions = axis.options,
                  horiz = axis.horiz,
                  key = [horiz ? otherOptions.left : otherOptions.top, otherOptions.width, otherOptions.height, otherOptions.pane].join(',');

              if (axis.series.length) {
                if (others[key]) {
                  hasOther = true;
                } else {
                  others[key] = 1;
                }
              }
            });
          }

          return hasOther;
        },
        getTickAmount: function getTickAmount() {
          var options = this.options,
              tickAmount = options.tickAmount,
              tickPixelInterval = options.tickPixelInterval;

          if (!defined(options.tickInterval) && this.len < tickPixelInterval && !this.isRadial && !this.isLog && options.startOnTick && options.endOnTick) {
            tickAmount = 2;
          }

          if (!tickAmount && this.alignToOthers()) {
            tickAmount = Math.ceil(this.len / tickPixelInterval) + 1;
          }

          if (tickAmount < 4) {
            this.finalTickAmt = tickAmount;
            tickAmount = 5;
          }

          this.tickAmount = tickAmount;
        },
        adjustTickAmount: function adjustTickAmount() {
          var axis = this,
              axisOptions = axis.options,
              tickInterval = axis.tickInterval,
              tickPositions = axis.tickPositions,
              tickAmount = axis.tickAmount,
              finalTickAmt = axis.finalTickAmt,
              currentTickAmount = tickPositions && tickPositions.length,
              threshold = pick(axis.threshold, axis.softThreshold ? 0 : null),
              min,
              len,
              i;

          if (axis.hasData()) {
            if (currentTickAmount < tickAmount) {
              min = axis.min;

              while (tickPositions.length < tickAmount) {
                if (tickPositions.length % 2 || min === threshold) {
                  tickPositions.push(correctFloat(tickPositions[tickPositions.length - 1] + tickInterval));
                } else {
                  tickPositions.unshift(correctFloat(tickPositions[0] - tickInterval));
                }
              }

              axis.transA *= (currentTickAmount - 1) / (tickAmount - 1);
              axis.min = axisOptions.startOnTick ? tickPositions[0] : Math.min(axis.min, tickPositions[0]);
              axis.max = axisOptions.endOnTick ? tickPositions[tickPositions.length - 1] : Math.max(axis.max, tickPositions[tickPositions.length - 1]);
            } else if (currentTickAmount > tickAmount) {
              axis.tickInterval *= 2;
              axis.setTickPositions();
            }

            if (defined(finalTickAmt)) {
              i = len = tickPositions.length;

              while (i--) {
                if (finalTickAmt === 3 && i % 2 === 1 || finalTickAmt <= 2 && i > 0 && i < len - 1) {
                  tickPositions.splice(i, 1);
                }
              }

              axis.finalTickAmt = void 0;
            }
          }
        },
        setScale: function setScale() {
          var axis = this,
              isDirtyData = axis.series.some(function (series) {
            return series.isDirtyData || series.isDirty || series.xAxis && series.xAxis.isDirty;
          }),
              isDirtyAxisLength;
          axis.oldMin = axis.min;
          axis.oldMax = axis.max;
          axis.oldAxisLength = axis.len;
          axis.setAxisSize();
          isDirtyAxisLength = axis.len !== axis.oldAxisLength;

          if (isDirtyAxisLength || isDirtyData || axis.isLinked || axis.forceRedraw || axis.userMin !== axis.oldUserMin || axis.userMax !== axis.oldUserMax || axis.alignToOthers()) {
            if (axis.resetStacks) {
              axis.resetStacks();
            }

            axis.forceRedraw = false;
            axis.getSeriesExtremes();
            axis.setTickInterval();
            axis.oldUserMin = axis.userMin;
            axis.oldUserMax = axis.userMax;

            if (!axis.isDirty) {
              axis.isDirty = isDirtyAxisLength || axis.min !== axis.oldMin || axis.max !== axis.oldMax;
            }
          } else if (axis.cleanStacks) {
            axis.cleanStacks();
          }

          fireEvent(this, 'afterSetScale');
        },
        setExtremes: function setExtremes(newMin, newMax, redraw, animation, eventArguments) {
          var axis = this,
              chart = axis.chart;
          redraw = pick(redraw, true);
          axis.series.forEach(function (serie) {
            delete serie.kdTree;
          });
          eventArguments = extend(eventArguments, {
            min: newMin,
            max: newMax
          });
          fireEvent(axis, 'setExtremes', eventArguments, function () {
            axis.userMin = newMin;
            axis.userMax = newMax;
            axis.eventArgs = eventArguments;

            if (redraw) {
              chart.redraw(animation);
            }
          });
        },
        zoom: function zoom(newMin, newMax) {
          var dataMin = this.dataMin,
              dataMax = this.dataMax,
              options = this.options,
              min = Math.min(dataMin, pick(options.min, dataMin)),
              max = Math.max(dataMax, pick(options.max, dataMax)),
              evt = {
            newMin: newMin,
            newMax: newMax
          };
          fireEvent(this, 'zoom', evt, function (e) {
            var newMin = e.newMin,
                newMax = e.newMax;

            if (newMin !== this.min || newMax !== this.max) {
              if (!this.allowZoomOutside) {
                if (defined(dataMin)) {
                  if (newMin < min) {
                    newMin = min;
                  }

                  if (newMin > max) {
                    newMin = max;
                  }
                }

                if (defined(dataMax)) {
                  if (newMax < min) {
                    newMax = min;
                  }

                  if (newMax > max) {
                    newMax = max;
                  }
                }
              }

              this.displayBtn = typeof newMin !== 'undefined' || typeof newMax !== 'undefined';
              this.setExtremes(newMin, newMax, false, void 0, {
                trigger: 'zoom'
              });
            }

            e.zoomed = true;
          });
          return evt.zoomed;
        },
        setAxisSize: function setAxisSize() {
          var chart = this.chart,
              options = this.options,
              offsets = options.offsets || [0, 0, 0, 0],
              horiz = this.horiz,
              width = this.width = Math.round(relativeLength(pick(options.width, chart.plotWidth - offsets[3] + offsets[1]), chart.plotWidth)),
              height = this.height = Math.round(relativeLength(pick(options.height, chart.plotHeight - offsets[0] + offsets[2]), chart.plotHeight)),
              top = this.top = Math.round(relativeLength(pick(options.top, chart.plotTop + offsets[0]), chart.plotHeight, chart.plotTop)),
              left = this.left = Math.round(relativeLength(pick(options.left, chart.plotLeft + offsets[3]), chart.plotWidth, chart.plotLeft));
          this.bottom = chart.chartHeight - height - top;
          this.right = chart.chartWidth - width - left;
          this.len = Math.max(horiz ? width : height, 0);
          this.pos = horiz ? left : top;
        },
        getExtremes: function getExtremes() {
          var axis = this,
              isLog = axis.isLog;
          return {
            min: isLog ? correctFloat(axis.lin2log(axis.min)) : axis.min,
            max: isLog ? correctFloat(axis.lin2log(axis.max)) : axis.max,
            dataMin: axis.dataMin,
            dataMax: axis.dataMax,
            userMin: axis.userMin,
            userMax: axis.userMax
          };
        },
        getThreshold: function getThreshold(threshold) {
          var axis = this,
              isLog = axis.isLog,
              realMin = isLog ? axis.lin2log(axis.min) : axis.min,
              realMax = isLog ? axis.lin2log(axis.max) : axis.max;

          if (threshold === null || threshold === -Infinity) {
            threshold = realMin;
          } else if (threshold === Infinity) {
            threshold = realMax;
          } else if (realMin > threshold) {
            threshold = realMin;
          } else if (realMax < threshold) {
            threshold = realMax;
          }

          return axis.translate(threshold, 0, 1, 0, 1);
        },
        autoLabelAlign: function autoLabelAlign(rotation) {
          var angle = (pick(rotation, 0) - this.side * 90 + 720) % 360,
              evt = {
            align: 'center'
          };
          fireEvent(this, 'autoLabelAlign', evt, function (e) {
            if (angle > 15 && angle < 165) {
              e.align = 'right';
            } else if (angle > 195 && angle < 345) {
              e.align = 'left';
            }
          });
          return evt.align;
        },
        tickSize: function tickSize(prefix) {
          var options = this.options,
              tickLength = options[prefix + 'Length'],
              tickWidth = pick(options[prefix + 'Width'], prefix === 'tick' && this.isXAxis && !this.categories ? 1 : 0),
              e,
              tickSize;

          if (tickWidth && tickLength) {
            if (options[prefix + 'Position'] === 'inside') {
              tickLength = -tickLength;
            }

            tickSize = [tickLength, tickWidth];
          }

          e = {
            tickSize: tickSize
          };
          fireEvent(this, 'afterTickSize', e);
          return e.tickSize;
        },
        labelMetrics: function labelMetrics() {
          var index = this.tickPositions && this.tickPositions[0] || 0;
          return this.chart.renderer.fontMetrics(this.options.labels.style && this.options.labels.style.fontSize, this.ticks[index] && this.ticks[index].label);
        },
        unsquish: function unsquish() {
          var labelOptions = this.options.labels,
              horiz = this.horiz,
              tickInterval = this.tickInterval,
              newTickInterval = tickInterval,
              slotSize = this.len / (((this.categories ? 1 : 0) + this.max - this.min) / tickInterval),
              rotation,
              rotationOption = labelOptions.rotation,
              labelMetrics = this.labelMetrics(),
              step,
              bestScore = Number.MAX_VALUE,
              autoRotation,
              range = this.max - this.min,
              getStep = function getStep(spaceNeeded) {
            var step = spaceNeeded / (slotSize || 1);
            step = step > 1 ? Math.ceil(step) : 1;

            if (step * tickInterval > range && spaceNeeded !== Infinity && slotSize !== Infinity && range) {
              step = Math.ceil(range / tickInterval);
            }

            return correctFloat(step * tickInterval);
          };

          if (horiz) {
            autoRotation = !labelOptions.staggerLines && !labelOptions.step && (defined(rotationOption) ? [rotationOption] : slotSize < pick(labelOptions.autoRotationLimit, 80) && labelOptions.autoRotation);

            if (autoRotation) {
              autoRotation.forEach(function (rot) {
                var score;

                if (rot === rotationOption || rot && rot >= -90 && rot <= 90) {
                  step = getStep(Math.abs(labelMetrics.h / Math.sin(deg2rad * rot)));
                  score = step + Math.abs(rot / 360);

                  if (score < bestScore) {
                    bestScore = score;
                    rotation = rot;
                    newTickInterval = step;
                  }
                }
              });
            }
          } else if (!labelOptions.step) {
            newTickInterval = getStep(labelMetrics.h);
          }

          this.autoRotation = autoRotation;
          this.labelRotation = pick(rotation, rotationOption);
          return newTickInterval;
        },
        getSlotWidth: function getSlotWidth(tick) {
          var chart = this.chart,
              horiz = this.horiz,
              labelOptions = this.options.labels,
              slotCount = Math.max(this.tickPositions.length - (this.categories ? 0 : 1), 1),
              marginLeft = chart.margin[3];
          return tick && tick.slotWidth || horiz && (labelOptions.step || 0) < 2 && !labelOptions.rotation && (this.staggerLines || 1) * this.len / slotCount || !horiz && (labelOptions.style && parseInt(labelOptions.style.width, 10) || marginLeft && marginLeft - chart.spacing[3] || chart.chartWidth * 0.33);
        },
        renderUnsquish: function renderUnsquish() {
          var chart = this.chart,
              renderer = chart.renderer,
              tickPositions = this.tickPositions,
              ticks = this.ticks,
              labelOptions = this.options.labels,
              labelStyleOptions = labelOptions && labelOptions.style || {},
              horiz = this.horiz,
              slotWidth = this.getSlotWidth(),
              innerWidth = Math.max(1, Math.round(slotWidth - 2 * (labelOptions.padding || 5))),
              attr = {},
              labelMetrics = this.labelMetrics(),
              textOverflowOption = labelOptions.style && labelOptions.style.textOverflow,
              commonWidth,
              commonTextOverflow,
              maxLabelLength = 0,
              label,
              i,
              pos;

          if (!isString(labelOptions.rotation)) {
            attr.rotation = labelOptions.rotation || 0;
          }

          tickPositions.forEach(function (tick) {
            tick = ticks[tick];

            if (tick.movedLabel) {
              tick.replaceMovedLabel();
            }

            if (tick && tick.label && tick.label.textPxLength > maxLabelLength) {
              maxLabelLength = tick.label.textPxLength;
            }
          });
          this.maxLabelLength = maxLabelLength;

          if (this.autoRotation) {
            if (maxLabelLength > innerWidth && maxLabelLength > labelMetrics.h) {
              attr.rotation = this.labelRotation;
            } else {
              this.labelRotation = 0;
            }
          } else if (slotWidth) {
            commonWidth = innerWidth;

            if (!textOverflowOption) {
              commonTextOverflow = 'clip';
              i = tickPositions.length;

              while (!horiz && i--) {
                pos = tickPositions[i];
                label = ticks[pos].label;

                if (label) {
                  if (label.styles && label.styles.textOverflow === 'ellipsis') {
                    label.css({
                      textOverflow: 'clip'
                    });
                  } else if (label.textPxLength > slotWidth) {
                    label.css({
                      width: slotWidth + 'px'
                    });
                  }

                  if (label.getBBox().height > this.len / tickPositions.length - (labelMetrics.h - labelMetrics.f)) {
                    label.specificTextOverflow = 'ellipsis';
                  }
                }
              }
            }
          }

          if (attr.rotation) {
            commonWidth = maxLabelLength > chart.chartHeight * 0.5 ? chart.chartHeight * 0.33 : maxLabelLength;

            if (!textOverflowOption) {
              commonTextOverflow = 'ellipsis';
            }
          }

          this.labelAlign = labelOptions.align || this.autoLabelAlign(this.labelRotation);

          if (this.labelAlign) {
            attr.align = this.labelAlign;
          }

          tickPositions.forEach(function (pos) {
            var tick = ticks[pos],
                label = tick && tick.label,
                widthOption = labelStyleOptions.width,
                css = {};

            if (label) {
              label.attr(attr);

              if (tick.shortenLabel) {
                tick.shortenLabel();
              } else if (commonWidth && !widthOption && labelStyleOptions.whiteSpace !== 'nowrap' && (commonWidth < label.textPxLength || label.element.tagName === 'SPAN')) {
                css.width = commonWidth;

                if (!textOverflowOption) {
                  css.textOverflow = label.specificTextOverflow || commonTextOverflow;
                }

                label.css(css);
              } else if (label.styles && label.styles.width && !css.width && !widthOption) {
                label.css({
                  width: null
                });
              }

              delete label.specificTextOverflow;
              tick.rotation = attr.rotation;
            }
          }, this);
          this.tickRotCorr = renderer.rotCorr(labelMetrics.b, this.labelRotation || 0, this.side !== 0);
        },
        hasData: function hasData() {
          return this.series.some(function (s) {
            return s.hasData();
          }) || this.options.showEmpty && defined(this.min) && defined(this.max);
        },
        addTitle: function addTitle(display) {
          var axis = this,
              renderer = axis.chart.renderer,
              horiz = axis.horiz,
              opposite = axis.opposite,
              options = axis.options,
              axisTitleOptions = options.title,
              textAlign,
              styledMode = axis.chart.styledMode;

          if (!axis.axisTitle) {
            textAlign = axisTitleOptions.textAlign;

            if (!textAlign) {
              textAlign = (horiz ? {
                low: 'left',
                middle: 'center',
                high: 'right'
              } : {
                low: opposite ? 'right' : 'left',
                middle: 'center',
                high: opposite ? 'left' : 'right'
              })[axisTitleOptions.align];
            }

            axis.axisTitle = renderer.text(axisTitleOptions.text, 0, 0, axisTitleOptions.useHTML).attr({
              zIndex: 7,
              rotation: axisTitleOptions.rotation || 0,
              align: textAlign
            }).addClass('highcharts-axis-title');

            if (!styledMode) {
              axis.axisTitle.css(merge(axisTitleOptions.style));
            }

            axis.axisTitle.add(axis.axisGroup);
            axis.axisTitle.isNew = true;
          }

          if (!styledMode && !axisTitleOptions.style.width && !axis.isRadial) {
            axis.axisTitle.css({
              width: axis.len
            });
          }

          axis.axisTitle[display ? 'show' : 'hide'](display);
        },
        generateTick: function generateTick(pos) {
          var ticks = this.ticks;

          if (!ticks[pos]) {
            ticks[pos] = new Tick(this, pos);
          } else {
            ticks[pos].addLabel();
          }
        },
        getOffset: function getOffset() {
          var axis = this,
              chart = axis.chart,
              renderer = chart.renderer,
              options = axis.options,
              tickPositions = axis.tickPositions,
              ticks = axis.ticks,
              horiz = axis.horiz,
              side = axis.side,
              invertedSide = chart.inverted && !axis.isZAxis ? [1, 0, 3, 2][side] : side,
              hasData,
              showAxis,
              titleOffset = 0,
              titleOffsetOption,
              titleMargin = 0,
              axisTitleOptions = options.title,
              labelOptions = options.labels,
              labelOffset = 0,
              labelOffsetPadded,
              axisOffset = chart.axisOffset,
              clipOffset = chart.clipOffset,
              clip,
              directionFactor = [-1, 1, 1, -1][side],
              className = options.className,
              axisParent = axis.axisParent,
              lineHeightCorrection,
              tickSize;
          hasData = axis.hasData();
          axis.showAxis = showAxis = hasData || pick(options.showEmpty, true);
          axis.staggerLines = axis.horiz && labelOptions.staggerLines;

          if (!axis.axisGroup) {
            axis.gridGroup = renderer.g('grid').attr({
              zIndex: options.gridZIndex || 1
            }).addClass('highcharts-' + this.coll.toLowerCase() + '-grid ' + (className || '')).add(axisParent);
            axis.axisGroup = renderer.g('axis').attr({
              zIndex: options.zIndex || 2
            }).addClass('highcharts-' + this.coll.toLowerCase() + ' ' + (className || '')).add(axisParent);
            axis.labelGroup = renderer.g('axis-labels').attr({
              zIndex: labelOptions.zIndex || 7
            }).addClass('highcharts-' + axis.coll.toLowerCase() + '-labels ' + (className || '')).add(axisParent);
          }

          if (hasData || axis.isLinked) {
            tickPositions.forEach(function (pos, i) {
              axis.generateTick(pos, i);
            });
            axis.renderUnsquish();
            axis.reserveSpaceDefault = side === 0 || side === 2 || {
              1: 'left',
              3: 'right'
            }[side] === axis.labelAlign;

            if (pick(labelOptions.reserveSpace, axis.labelAlign === 'center' ? true : null, axis.reserveSpaceDefault)) {
              tickPositions.forEach(function (pos) {
                labelOffset = Math.max(ticks[pos].getLabelSize(), labelOffset);
              });
            }

            if (axis.staggerLines) {
              labelOffset *= axis.staggerLines;
            }

            axis.labelOffset = labelOffset * (axis.opposite ? -1 : 1);
          } else {
            objectEach(ticks, function (tick, n) {
              tick.destroy();
              delete ticks[n];
            });
          }

          if (axisTitleOptions && axisTitleOptions.text && axisTitleOptions.enabled !== false) {
            axis.addTitle(showAxis);

            if (showAxis && axisTitleOptions.reserveSpace !== false) {
              axis.titleOffset = titleOffset = axis.axisTitle.getBBox()[horiz ? 'height' : 'width'];
              titleOffsetOption = axisTitleOptions.offset;
              titleMargin = defined(titleOffsetOption) ? 0 : pick(axisTitleOptions.margin, horiz ? 5 : 10);
            }
          }

          axis.renderLine();
          axis.offset = directionFactor * pick(options.offset, axisOffset[side] ? axisOffset[side] + (options.margin || 0) : 0);
          axis.tickRotCorr = axis.tickRotCorr || {
            x: 0,
            y: 0
          };

          if (side === 0) {
            lineHeightCorrection = -axis.labelMetrics().h;
          } else if (side === 2) {
            lineHeightCorrection = axis.tickRotCorr.y;
          } else {
            lineHeightCorrection = 0;
          }

          labelOffsetPadded = Math.abs(labelOffset) + titleMargin;

          if (labelOffset) {
            labelOffsetPadded -= lineHeightCorrection;
            labelOffsetPadded += directionFactor * (horiz ? pick(labelOptions.y, axis.tickRotCorr.y + directionFactor * 8) : labelOptions.x);
          }

          axis.axisTitleMargin = pick(titleOffsetOption, labelOffsetPadded);

          if (axis.getMaxLabelDimensions) {
            axis.maxLabelDimensions = axis.getMaxLabelDimensions(ticks, tickPositions);
          }

          tickSize = this.tickSize('tick');
          axisOffset[side] = Math.max(axisOffset[side], axis.axisTitleMargin + titleOffset + directionFactor * axis.offset, labelOffsetPadded, tickPositions && tickPositions.length && tickSize ? tickSize[0] + directionFactor * axis.offset : 0);
          clip = options.offset ? 0 : Math.floor(axis.axisLine.strokeWidth() / 2) * 2;
          clipOffset[invertedSide] = Math.max(clipOffset[invertedSide], clip);
          fireEvent(this, 'afterGetOffset');
        },
        getLinePath: function getLinePath(lineWidth) {
          var chart = this.chart,
              opposite = this.opposite,
              offset = this.offset,
              horiz = this.horiz,
              lineLeft = this.left + (opposite ? this.width : 0) + offset,
              lineTop = chart.chartHeight - this.bottom - (opposite ? this.height : 0) + offset;

          if (opposite) {
            lineWidth *= -1;
          }

          return chart.renderer.crispLine(['M', horiz ? this.left : lineLeft, horiz ? lineTop : this.top, 'L', horiz ? chart.chartWidth - this.right : lineLeft, horiz ? lineTop : chart.chartHeight - this.bottom], lineWidth);
        },
        renderLine: function renderLine() {
          if (!this.axisLine) {
            this.axisLine = this.chart.renderer.path().addClass('highcharts-axis-line').add(this.axisGroup);

            if (!this.chart.styledMode) {
              this.axisLine.attr({
                stroke: this.options.lineColor,
                'stroke-width': this.options.lineWidth,
                zIndex: 7
              });
            }
          }
        },
        getTitlePosition: function getTitlePosition() {
          var horiz = this.horiz,
              axisLeft = this.left,
              axisTop = this.top,
              axisLength = this.len,
              axisTitleOptions = this.options.title,
              margin = horiz ? axisLeft : axisTop,
              opposite = this.opposite,
              offset = this.offset,
              xOption = axisTitleOptions.x || 0,
              yOption = axisTitleOptions.y || 0,
              axisTitle = this.axisTitle,
              fontMetrics = this.chart.renderer.fontMetrics(axisTitleOptions.style && axisTitleOptions.style.fontSize, axisTitle),
              textHeightOvershoot = Math.max(axisTitle.getBBox(null, 0).height - fontMetrics.h - 1, 0),
              alongAxis = {
            low: margin + (horiz ? 0 : axisLength),
            middle: margin + axisLength / 2,
            high: margin + (horiz ? axisLength : 0)
          }[axisTitleOptions.align],
              offAxis = (horiz ? axisTop + this.height : axisLeft) + (horiz ? 1 : -1) * (opposite ? -1 : 1) * this.axisTitleMargin + [-textHeightOvershoot, textHeightOvershoot, fontMetrics.f, -textHeightOvershoot][this.side],
              titlePosition = {
            x: horiz ? alongAxis + xOption : offAxis + (opposite ? this.width : 0) + offset + xOption,
            y: horiz ? offAxis + yOption - (opposite ? this.height : 0) + offset : alongAxis + yOption
          };
          fireEvent(this, 'afterGetTitlePosition', {
            titlePosition: titlePosition
          });
          return titlePosition;
        },
        renderMinorTick: function renderMinorTick(pos) {
          var slideInTicks = this.chart.hasRendered && isNumber(this.oldMin),
              minorTicks = this.minorTicks;

          if (!minorTicks[pos]) {
            minorTicks[pos] = new Tick(this, pos, 'minor');
          }

          if (slideInTicks && minorTicks[pos].isNew) {
            minorTicks[pos].render(null, true);
          }

          minorTicks[pos].render(null, false, 1);
        },
        renderTick: function renderTick(pos, i) {
          var isLinked = this.isLinked,
              ticks = this.ticks,
              slideInTicks = this.chart.hasRendered && isNumber(this.oldMin);

          if (!isLinked || pos >= this.min && pos <= this.max) {
            if (!ticks[pos]) {
              ticks[pos] = new Tick(this, pos);
            }

            if (slideInTicks && ticks[pos].isNew) {
              ticks[pos].render(i, true, -1);
            }

            ticks[pos].render(i);
          }
        },
        render: function render() {
          var axis = this,
              chart = axis.chart,
              renderer = chart.renderer,
              options = axis.options,
              isLog = axis.isLog,
              isLinked = axis.isLinked,
              tickPositions = axis.tickPositions,
              axisTitle = axis.axisTitle,
              ticks = axis.ticks,
              minorTicks = axis.minorTicks,
              alternateBands = axis.alternateBands,
              stackLabelOptions = options.stackLabels,
              alternateGridColor = options.alternateGridColor,
              tickmarkOffset = axis.tickmarkOffset,
              axisLine = axis.axisLine,
              showAxis = axis.showAxis,
              animation = animObject(renderer.globalAnimation),
              from,
              to;
          axis.labelEdge.length = 0;
          axis.overlap = false;
          [ticks, minorTicks, alternateBands].forEach(function (coll) {
            objectEach(coll, function (tick) {
              tick.isActive = false;
            });
          });

          if (axis.hasData() || isLinked) {
            if (axis.minorTickInterval && !axis.categories) {
              axis.getMinorTickPositions().forEach(function (pos) {
                axis.renderMinorTick(pos);
              });
            }

            if (tickPositions.length) {
              tickPositions.forEach(function (pos, i) {
                axis.renderTick(pos, i);
              });

              if (tickmarkOffset && (axis.min === 0 || axis.single)) {
                if (!ticks[-1]) {
                  ticks[-1] = new Tick(axis, -1, null, true);
                }

                ticks[-1].render(-1);
              }
            }

            if (alternateGridColor) {
              tickPositions.forEach(function (pos, i) {
                to = typeof tickPositions[i + 1] !== 'undefined' ? tickPositions[i + 1] + tickmarkOffset : axis.max - tickmarkOffset;

                if (i % 2 === 0 && pos < axis.max && to <= axis.max + (chart.polar ? -tickmarkOffset : tickmarkOffset)) {
                  if (!alternateBands[pos]) {
                    alternateBands[pos] = new H.PlotLineOrBand(axis);
                  }

                  from = pos + tickmarkOffset;
                  alternateBands[pos].options = {
                    from: isLog ? axis.lin2log(from) : from,
                    to: isLog ? axis.lin2log(to) : to,
                    color: alternateGridColor
                  };
                  alternateBands[pos].render();
                  alternateBands[pos].isActive = true;
                }
              });
            }

            if (!axis._addedPlotLB) {
              (options.plotLines || []).concat(options.plotBands || []).forEach(function (plotLineOptions) {
                axis.addPlotBandOrLine(plotLineOptions);
              });
              axis._addedPlotLB = true;
            }
          }

          [ticks, minorTicks, alternateBands].forEach(function (coll) {
            var i,
                forDestruction = [],
                delay = animation.duration,
                destroyInactiveItems = function destroyInactiveItems() {
              i = forDestruction.length;

              while (i--) {
                if (coll[forDestruction[i]] && !coll[forDestruction[i]].isActive) {
                  coll[forDestruction[i]].destroy();
                  delete coll[forDestruction[i]];
                }
              }
            };

            objectEach(coll, function (tick, pos) {
              if (!tick.isActive) {
                tick.render(pos, false, 0);
                tick.isActive = false;
                forDestruction.push(pos);
              }
            });
            syncTimeout(destroyInactiveItems, coll === alternateBands || !chart.hasRendered || !delay ? 0 : delay);
          });

          if (axisLine) {
            axisLine[axisLine.isPlaced ? 'animate' : 'attr']({
              d: this.getLinePath(axisLine.strokeWidth())
            });
            axisLine.isPlaced = true;
            axisLine[showAxis ? 'show' : 'hide'](showAxis);
          }

          if (axisTitle && showAxis) {
            var titleXy = axis.getTitlePosition();

            if (isNumber(titleXy.y)) {
              axisTitle[axisTitle.isNew ? 'attr' : 'animate'](titleXy);
              axisTitle.isNew = false;
            } else {
              axisTitle.attr('y', -9999);
              axisTitle.isNew = true;
            }
          }

          if (stackLabelOptions && stackLabelOptions.enabled) {
            axis.renderStackTotals();
          }

          axis.isDirty = false;
          fireEvent(this, 'afterRender');
        },
        redraw: function redraw() {
          if (this.visible) {
            this.render();
            this.plotLinesAndBands.forEach(function (plotLine) {
              plotLine.render();
            });
          }

          this.series.forEach(function (series) {
            series.isDirty = true;
          });
        },
        keepProps: ['extKey', 'hcEvents', 'names', 'series', 'userMax', 'userMin'],
        destroy: function destroy(keepEvents) {
          var axis = this,
              stacks = axis.stacks,
              plotLinesAndBands = axis.plotLinesAndBands,
              plotGroup,
              i;
          fireEvent(this, 'destroy', {
            keepEvents: keepEvents
          });

          if (!keepEvents) {
            removeEvent(axis);
          }

          objectEach(stacks, function (stack, stackKey) {
            destroyObjectProperties(stack);
            stacks[stackKey] = null;
          });
          [axis.ticks, axis.minorTicks, axis.alternateBands].forEach(function (coll) {
            destroyObjectProperties(coll);
          });

          if (plotLinesAndBands) {
            i = plotLinesAndBands.length;

            while (i--) {
              plotLinesAndBands[i].destroy();
            }
          }

          ['stackTotalGroup', 'axisLine', 'axisTitle', 'axisGroup', 'gridGroup', 'labelGroup', 'cross', 'scrollbar'].forEach(function (prop) {
            if (axis[prop]) {
              axis[prop] = axis[prop].destroy();
            }
          });

          for (plotGroup in axis.plotLinesAndBandsGroups) {
            axis.plotLinesAndBandsGroups[plotGroup] = axis.plotLinesAndBandsGroups[plotGroup].destroy();
          }

          objectEach(axis, function (val, key) {
            if (axis.keepProps.indexOf(key) === -1) {
              delete axis[key];
            }
          });
        },
        drawCrosshair: function drawCrosshair(e, point) {
          var path,
              options = this.crosshair,
              snap = pick(options.snap, true),
              pos,
              categorized,
              graphic = this.cross,
              crossOptions,
              chart = this.chart;
          fireEvent(this, 'drawCrosshair', {
            e: e,
            point: point
          });

          if (!e) {
            e = this.cross && this.cross.e;
          }

          if (!this.crosshair || (defined(point) || !snap) === false) {
            this.hideCrosshair();
          } else {
            if (!snap) {
              pos = e && (this.horiz ? e.chartX - this.pos : this.len - e.chartY + this.pos);
            } else if (defined(point)) {
              pos = pick(this.coll !== 'colorAxis' ? point.crosshairPos : null, this.isXAxis ? point.plotX : this.len - point.plotY);
            }

            if (defined(pos)) {
              crossOptions = {
                value: point && (this.isXAxis ? point.x : pick(point.stackY, point.y)),
                translatedValue: pos
              };

              if (chart.polar) {
                extend(crossOptions, {
                  isCrosshair: true,
                  chartX: e && e.chartX,
                  chartY: e && e.chartY,
                  point: point
                });
              }

              path = this.getPlotLinePath(crossOptions) || null;
            }

            if (!defined(path)) {
              this.hideCrosshair();
              return;
            }

            categorized = this.categories && !this.isRadial;

            if (!graphic) {
              this.cross = graphic = chart.renderer.path().addClass('highcharts-crosshair highcharts-crosshair-' + (categorized ? 'category ' : 'thin ') + options.className).attr({
                zIndex: pick(options.zIndex, 2)
              }).add();

              if (!chart.styledMode) {
                graphic.attr({
                  stroke: options.color || (categorized ? color('#ccd6eb').setOpacity(0.25).get() : '#cccccc'),
                  'stroke-width': pick(options.width, 1)
                }).css({
                  'pointer-events': 'none'
                });

                if (options.dashStyle) {
                  graphic.attr({
                    dashstyle: options.dashStyle
                  });
                }
              }
            }

            graphic.show().attr({
              d: path
            });

            if (categorized && !options.width) {
              graphic.attr({
                'stroke-width': this.transA
              });
            }

            this.cross.e = e;
          }

          fireEvent(this, 'afterDrawCrosshair', {
            e: e,
            point: point
          });
        },
        hideCrosshair: function hideCrosshair() {
          if (this.cross) {
            this.cross.hide();
          }

          fireEvent(this, 'afterHideCrosshair');
        }
      });
      H.Axis = Axis;
      return Axis;
    });

    _registerModule(_modules, 'parts/DateTimeAxis.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var getMagnitude = U.getMagnitude,
          normalizeTickInterval = U.normalizeTickInterval,
          timeUnits = U.timeUnits;
      var Axis = H.Axis;

      Axis.prototype.getTimeTicks = function () {
        return this.chart.time.getTimeTicks.apply(this.chart.time, arguments);
      };

      Axis.prototype.normalizeTimeTickInterval = function (tickInterval, unitsOption) {
        var units = unitsOption || [['millisecond', [1, 2, 5, 10, 20, 25, 50, 100, 200, 500]], ['second', [1, 2, 5, 10, 15, 30]], ['minute', [1, 2, 5, 10, 15, 30]], ['hour', [1, 2, 3, 4, 6, 8, 12]], ['day', [1, 2]], ['week', [1, 2]], ['month', [1, 2, 3, 4, 6]], ['year', null]],
            unit = units[units.length - 1],
            interval = timeUnits[unit[0]],
            multiples = unit[1],
            count,
            i;

        for (i = 0; i < units.length; i++) {
          unit = units[i];
          interval = timeUnits[unit[0]];
          multiples = unit[1];

          if (units[i + 1]) {
            var lessThan = (interval * multiples[multiples.length - 1] + timeUnits[units[i + 1][0]]) / 2;

            if (tickInterval <= lessThan) {
              break;
            }
          }
        }

        if (interval === timeUnits.year && tickInterval < 5 * interval) {
          multiples = [1, 2, 5];
        }

        count = normalizeTickInterval(tickInterval / interval, multiples, unit[0] === 'year' ? Math.max(getMagnitude(tickInterval / interval), 1) : 1);
        return {
          unitRange: interval,
          count: count,
          unitName: unit[0]
        };
      };
    });

    _registerModule(_modules, 'parts/LogarithmicAxis.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var getMagnitude = U.getMagnitude,
          normalizeTickInterval = U.normalizeTickInterval,
          pick = U.pick;
      var Axis = H.Axis;

      Axis.prototype.getLogTickPositions = function (interval, min, max, minor) {
        var axis = this,
            options = axis.options,
            axisLength = axis.len,
            positions = [];

        if (!minor) {
          axis._minorAutoInterval = null;
        }

        if (interval >= 0.5) {
          interval = Math.round(interval);
          positions = axis.getLinearTickPositions(interval, min, max);
        } else if (interval >= 0.08) {
          var roundedMin = Math.floor(min),
              intermediate,
              i,
              j,
              len,
              pos,
              lastPos,
              break2;

          if (interval > 0.3) {
            intermediate = [1, 2, 4];
          } else if (interval > 0.15) {
            intermediate = [1, 2, 4, 6, 8];
          } else {
            intermediate = [1, 2, 3, 4, 5, 6, 7, 8, 9];
          }

          for (i = roundedMin; i < max + 1 && !break2; i++) {
            len = intermediate.length;

            for (j = 0; j < len && !break2; j++) {
              pos = axis.log2lin(axis.lin2log(i) * intermediate[j]);

              if (pos > min && (!minor || lastPos <= max) && typeof lastPos !== 'undefined') {
                positions.push(lastPos);
              }

              if (lastPos > max) {
                break2 = true;
              }

              lastPos = pos;
            }
          }
        } else {
          var realMin = axis.lin2log(min),
              realMax = axis.lin2log(max),
              tickIntervalOption = minor ? this.getMinorTickInterval() : options.tickInterval,
              filteredTickIntervalOption = tickIntervalOption === 'auto' ? null : tickIntervalOption,
              tickPixelIntervalOption = options.tickPixelInterval / (minor ? 5 : 1),
              totalPixelLength = minor ? axisLength / axis.tickPositions.length : axisLength;
          interval = pick(filteredTickIntervalOption, axis._minorAutoInterval, (realMax - realMin) * tickPixelIntervalOption / (totalPixelLength || 1));
          interval = normalizeTickInterval(interval, null, getMagnitude(interval));
          positions = axis.getLinearTickPositions(interval, realMin, realMax).map(axis.log2lin);

          if (!minor) {
            axis._minorAutoInterval = interval / 5;
          }
        }

        if (!minor) {
          axis.tickInterval = interval;
        }

        return positions;
      };

      Axis.prototype.log2lin = function (num) {
        return Math.log(num) / Math.LN10;
      };

      Axis.prototype.lin2log = function (num) {
        return Math.pow(10, num);
      };
    });

    _registerModule(_modules, 'parts/PlotLineOrBand.js', [_modules['parts/Globals.js'], _modules['parts/Axis.js'], _modules['parts/Utilities.js']], function (H, Axis, U) {
      var arrayMax = U.arrayMax,
          arrayMin = U.arrayMin,
          defined = U.defined,
          destroyObjectProperties = U.destroyObjectProperties,
          erase = U.erase,
          extend = U.extend,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick;

      var PlotLineOrBand = function () {
        function PlotLineOrBand(axis, options) {
          this.axis = axis;

          if (options) {
            this.options = options;
            this.id = options.id;
          }
        }

        PlotLineOrBand.prototype.render = function () {
          H.fireEvent(this, 'render');
          var plotLine = this,
              axis = plotLine.axis,
              horiz = axis.horiz,
              options = plotLine.options,
              optionsLabel = options.label,
              label = plotLine.label,
              to = options.to,
              from = options.from,
              value = options.value,
              isBand = defined(from) && defined(to),
              isLine = defined(value),
              svgElem = plotLine.svgElem,
              isNew = !svgElem,
              path = [],
              color = options.color,
              zIndex = pick(options.zIndex, 0),
              events = options.events,
              attribs = {
            'class': 'highcharts-plot-' + (isBand ? 'band ' : 'line ') + (options.className || '')
          },
              groupAttribs = {},
              renderer = axis.chart.renderer,
              groupName = isBand ? 'bands' : 'lines',
              group;

          if (axis.isLog) {
            from = axis.log2lin(from);
            to = axis.log2lin(to);
            value = axis.log2lin(value);
          }

          if (!axis.chart.styledMode) {
            if (isLine) {
              attribs.stroke = color || '#999999';
              attribs['stroke-width'] = pick(options.width, 1);

              if (options.dashStyle) {
                attribs.dashstyle = options.dashStyle;
              }
            } else if (isBand) {
              attribs.fill = color || '#e6ebf5';

              if (options.borderWidth) {
                attribs.stroke = options.borderColor;
                attribs['stroke-width'] = options.borderWidth;
              }
            }
          }

          groupAttribs.zIndex = zIndex;
          groupName += '-' + zIndex;
          group = axis.plotLinesAndBandsGroups[groupName];

          if (!group) {
            axis.plotLinesAndBandsGroups[groupName] = group = renderer.g('plot-' + groupName).attr(groupAttribs).add();
          }

          if (isNew) {
            plotLine.svgElem = svgElem = renderer.path().attr(attribs).add(group);
          }

          if (isLine) {
            path = axis.getPlotLinePath({
              value: value,
              lineWidth: svgElem.strokeWidth(),
              acrossPanes: options.acrossPanes
            });
          } else if (isBand) {
            path = axis.getPlotBandPath(from, to, options);
          } else {
            return;
          }

          if ((isNew || !svgElem.d) && path && path.length) {
            svgElem.attr({
              d: path
            });

            if (events) {
              objectEach(events, function (event, eventType) {
                svgElem.on(eventType, function (e) {
                  events[eventType].apply(plotLine, [e]);
                });
              });
            }
          } else if (svgElem) {
            if (path) {
              svgElem.show(true);
              svgElem.animate({
                d: path
              });
            } else if (svgElem.d) {
              svgElem.hide();

              if (label) {
                plotLine.label = label = label.destroy();
              }
            }
          }

          if (optionsLabel && (defined(optionsLabel.text) || defined(optionsLabel.formatter)) && path && path.length && axis.width > 0 && axis.height > 0 && !path.isFlat) {
            optionsLabel = merge({
              align: horiz && isBand && 'center',
              x: horiz ? !isBand && 4 : 10,
              verticalAlign: !horiz && isBand && 'middle',
              y: horiz ? isBand ? 16 : 10 : isBand ? 6 : -4,
              rotation: horiz && !isBand && 90
            }, optionsLabel);
            this.renderLabel(optionsLabel, path, isBand, zIndex);
          } else if (label) {
            label.hide();
          }

          return plotLine;
        };

        PlotLineOrBand.prototype.renderLabel = function (optionsLabel, path, isBand, zIndex) {
          var plotLine = this,
              label = plotLine.label,
              renderer = plotLine.axis.chart.renderer,
              attribs,
              xBounds,
              yBounds,
              x,
              y,
              labelText;

          if (!label) {
            attribs = {
              align: optionsLabel.textAlign || optionsLabel.align,
              rotation: optionsLabel.rotation,
              'class': 'highcharts-plot-' + (isBand ? 'band' : 'line') + '-label ' + (optionsLabel.className || '')
            };
            attribs.zIndex = zIndex;
            labelText = this.getLabelText(optionsLabel);
            plotLine.label = label = renderer.text(labelText, 0, 0, optionsLabel.useHTML).attr(attribs).add();

            if (!this.axis.chart.styledMode) {
              label.css(optionsLabel.style);
            }
          }

          xBounds = path.xBounds || [path[1], path[4], isBand ? path[6] : path[1]];
          yBounds = path.yBounds || [path[2], path[5], isBand ? path[7] : path[2]];
          x = arrayMin(xBounds);
          y = arrayMin(yBounds);
          label.align(optionsLabel, false, {
            x: x,
            y: y,
            width: arrayMax(xBounds) - x,
            height: arrayMax(yBounds) - y
          });
          label.show(true);
        };

        PlotLineOrBand.prototype.getLabelText = function (optionsLabel) {
          return defined(optionsLabel.formatter) ? optionsLabel.formatter.call(this) : optionsLabel.text;
        };

        PlotLineOrBand.prototype.destroy = function () {
          erase(this.axis.plotLinesAndBands, this);
          delete this.axis;
          destroyObjectProperties(this);
        };

        return PlotLineOrBand;
      }();

      extend(Axis.prototype, {
        getPlotBandPath: function getPlotBandPath(from, to) {
          var toPath = this.getPlotLinePath({
            value: to,
            force: true,
            acrossPanes: this.options.acrossPanes
          }),
              path = this.getPlotLinePath({
            value: from,
            force: true,
            acrossPanes: this.options.acrossPanes
          }),
              result = [],
              i,
              horiz = this.horiz,
              plus = 1,
              isFlat,
              outside = from < this.min && to < this.min || from > this.max && to > this.max;

          if (path && toPath) {
            if (outside) {
              isFlat = path.toString() === toPath.toString();
              plus = 0;
            }

            for (i = 0; i < path.length; i += 6) {
              if (horiz && toPath[i + 1] === path[i + 1]) {
                toPath[i + 1] += plus;
                toPath[i + 4] += plus;
              } else if (!horiz && toPath[i + 2] === path[i + 2]) {
                toPath[i + 2] += plus;
                toPath[i + 5] += plus;
              }

              result.push('M', path[i + 1], path[i + 2], 'L', path[i + 4], path[i + 5], toPath[i + 4], toPath[i + 5], toPath[i + 1], toPath[i + 2], 'z');
              result.isFlat = isFlat;
            }
          } else {
            path = null;
          }

          return result;
        },
        addPlotBand: function addPlotBand(options) {
          return this.addPlotBandOrLine(options, 'plotBands');
        },
        addPlotLine: function addPlotLine(options) {
          return this.addPlotBandOrLine(options, 'plotLines');
        },
        addPlotBandOrLine: function addPlotBandOrLine(options, coll) {
          var obj = new PlotLineOrBand(this, options).render(),
              userOptions = this.userOptions;

          if (obj) {
            if (coll) {
              var updatedOptions = userOptions[coll] || [];
              updatedOptions.push(options);
              userOptions[coll] = updatedOptions;
            }

            this.plotLinesAndBands.push(obj);
          }

          return obj;
        },
        removePlotBandOrLine: function removePlotBandOrLine(id) {
          var plotLinesAndBands = this.plotLinesAndBands,
              options = this.options,
              userOptions = this.userOptions,
              i = plotLinesAndBands.length;

          while (i--) {
            if (plotLinesAndBands[i].id === id) {
              plotLinesAndBands[i].destroy();
            }
          }

          [options.plotLines || [], userOptions.plotLines || [], options.plotBands || [], userOptions.plotBands || []].forEach(function (arr) {
            i = arr.length;

            while (i--) {
              if (arr[i].id === id) {
                erase(arr, arr[i]);
              }
            }
          });
        },
        removePlotBand: function removePlotBand(id) {
          this.removePlotBandOrLine(id);
        },
        removePlotLine: function removePlotLine(id) {
          this.removePlotBandOrLine(id);
        }
      });
      H.PlotLineOrBand = PlotLineOrBand;
      return H.PlotLineOrBand;
    });

    _registerModule(_modules, 'parts/Tooltip.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var clamp = U.clamp,
          css = U.css,
          defined = U.defined,
          discardElement = U.discardElement,
          extend = U.extend,
          format = U.format,
          isNumber = U.isNumber,
          isString = U.isString,
          merge = U.merge,
          pick = U.pick,
          splat = U.splat,
          syncTimeout = U.syncTimeout,
          timeUnits = U.timeUnits;
      var doc = H.doc;

      var Tooltip = function () {
        function Tooltip(chart, options) {
          this.crosshairs = [];
          this.distance = 0;
          this.isHidden = true;
          this.isSticky = false;
          this.now = {};
          this.options = {};
          this.outside = false;
          this.chart = chart;
          this.init(chart, options);
        }

        Tooltip.prototype.applyFilter = function () {
          var chart = this.chart;
          chart.renderer.definition({
            tagName: 'filter',
            id: 'drop-shadow-' + chart.index,
            opacity: 0.5,
            children: [{
              tagName: 'feGaussianBlur',
              'in': 'SourceAlpha',
              stdDeviation: 1
            }, {
              tagName: 'feOffset',
              dx: 1,
              dy: 1
            }, {
              tagName: 'feComponentTransfer',
              children: [{
                tagName: 'feFuncA',
                type: 'linear',
                slope: 0.3
              }]
            }, {
              tagName: 'feMerge',
              children: [{
                tagName: 'feMergeNode'
              }, {
                tagName: 'feMergeNode',
                'in': 'SourceGraphic'
              }]
            }]
          });
          chart.renderer.definition({
            tagName: 'style',
            textContent: '.highcharts-tooltip-' + chart.index + '{' + 'filter:url(#drop-shadow-' + chart.index + ')' + '}'
          });
        };

        Tooltip.prototype.bodyFormatter = function (items) {
          return items.map(function (item) {
            var tooltipOptions = item.series.tooltipOptions;
            return (tooltipOptions[(item.point.formatPrefix || 'point') + 'Formatter'] || item.point.tooltipFormatter).call(item.point, tooltipOptions[(item.point.formatPrefix || 'point') + 'Format'] || '');
          });
        };

        Tooltip.prototype.cleanSplit = function (force) {
          this.chart.series.forEach(function (series) {
            var tt = series && series.tt;

            if (tt) {
              if (!tt.isActive || force) {
                series.tt = tt.destroy();
              } else {
                tt.isActive = false;
              }
            }
          });
        };

        Tooltip.prototype.defaultFormatter = function (tooltip) {
          var items = this.points || splat(this),
              s;
          s = [tooltip.tooltipFooterHeaderFormatter(items[0])];
          s = s.concat(tooltip.bodyFormatter(items));
          s.push(tooltip.tooltipFooterHeaderFormatter(items[0], true));
          return s;
        };

        Tooltip.prototype.destroy = function () {
          if (this.label) {
            this.label = this.label.destroy();
          }

          if (this.split && this.tt) {
            this.cleanSplit(this.chart, true);
            this.tt = this.tt.destroy();
          }

          if (this.renderer) {
            this.renderer = this.renderer.destroy();
            discardElement(this.container);
          }

          U.clearTimeout(this.hideTimer);
          U.clearTimeout(this.tooltipTimeout);
        };

        Tooltip.prototype.getAnchor = function (points, mouseEvent) {
          var ret,
              chart = this.chart,
              pointer = chart.pointer,
              inverted = chart.inverted,
              plotTop = chart.plotTop,
              plotLeft = chart.plotLeft,
              plotX = 0,
              plotY = 0,
              yAxis,
              xAxis;
          points = splat(points);

          if (this.followPointer && mouseEvent) {
            if (typeof mouseEvent.chartX === 'undefined') {
              mouseEvent = pointer.normalize(mouseEvent);
            }

            ret = [mouseEvent.chartX - plotLeft, mouseEvent.chartY - plotTop];
          } else if (points[0].tooltipPos) {
            ret = points[0].tooltipPos;
          } else {
            points.forEach(function (point) {
              yAxis = point.series.yAxis;
              xAxis = point.series.xAxis;
              plotX += point.plotX + (!inverted && xAxis ? xAxis.left - plotLeft : 0);
              plotY += (point.plotLow ? (point.plotLow + point.plotHigh) / 2 : point.plotY) + (!inverted && yAxis ? yAxis.top - plotTop : 0);
            });
            plotX /= points.length;
            plotY /= points.length;
            ret = [inverted ? chart.plotWidth - plotY : plotX, this.shared && !inverted && points.length > 1 && mouseEvent ? mouseEvent.chartY - plotTop : inverted ? chart.plotHeight - plotX : plotY];
          }

          return ret.map(Math.round);
        };

        Tooltip.prototype.getDateFormat = function (range, date, startOfWeek, dateTimeLabelFormats) {
          var time = this.chart.time,
              dateStr = time.dateFormat('%m-%d %H:%M:%S.%L', date),
              format,
              n,
              blank = '01-01 00:00:00.000',
              strpos = {
            millisecond: 15,
            second: 12,
            minute: 9,
            hour: 6,
            day: 3
          },
              lastN = 'millisecond';

          for (n in timeUnits) {
            if (range === timeUnits.week && +time.dateFormat('%w', date) === startOfWeek && dateStr.substr(6) === blank.substr(6)) {
              n = 'week';
              break;
            }

            if (timeUnits[n] > range) {
              n = lastN;
              break;
            }

            if (strpos[n] && dateStr.substr(strpos[n]) !== blank.substr(strpos[n])) {
              break;
            }

            if (n !== 'week') {
              lastN = n;
            }
          }

          if (n) {
            format = time.resolveDTLFormat(dateTimeLabelFormats[n]).main;
          }

          return format;
        };

        Tooltip.prototype.getLabel = function () {
          var _a;

          var tooltip = this,
              renderer = this.chart.renderer,
              styledMode = this.chart.styledMode,
              options = this.options,
              className = 'tooltip' + (defined(options.className) ? ' ' + options.className : ''),
              pointerEvents = ((_a = options.style) === null || _a === void 0 ? void 0 : _a.pointerEvents) || (!this.followPointer && options.stickOnContact ? 'auto' : 'none'),
              container,
              set,
              onMouseEnter = function onMouseEnter() {
            tooltip.inContact = true;
          },
              onMouseLeave = function onMouseLeave() {
            var series = tooltip.chart.hoverSeries;
            tooltip.inContact = false;

            if (series && series.onMouseOut) {
              series.onMouseOut();
            }
          };

          if (!this.label) {
            if (this.outside) {
              this.container = container = H.doc.createElement('div');
              container.className = 'highcharts-tooltip-container';
              css(container, {
                position: 'absolute',
                top: '1px',
                pointerEvents: pointerEvents,
                zIndex: 3
              });
              H.doc.body.appendChild(container);
              this.renderer = renderer = new H.Renderer(container, 0, 0, {}, void 0, void 0, renderer.styledMode);
            }

            if (this.split) {
              this.label = renderer.g(className);
            } else {
              this.label = renderer.label('', 0, 0, options.shape || 'callout', null, null, options.useHTML, null, className).attr({
                padding: options.padding,
                r: options.borderRadius
              });

              if (!styledMode) {
                this.label.attr({
                  fill: options.backgroundColor,
                  'stroke-width': options.borderWidth
                }).css(options.style).css({
                  pointerEvents: pointerEvents
                }).shadow(options.shadow);
              }
            }

            if (styledMode) {
              this.applyFilter();
              this.label.addClass('highcharts-tooltip-' + this.chart.index);
            }

            if (tooltip.outside && !tooltip.split) {
              set = {
                x: this.label.xSetter,
                y: this.label.ySetter
              };

              this.label.xSetter = function (value, key) {
                set[key].call(this.label, tooltip.distance);
                container.style.left = value + 'px';
              };

              this.label.ySetter = function (value, key) {
                set[key].call(this.label, tooltip.distance);
                container.style.top = value + 'px';
              };
            }

            this.label.on('mouseenter', onMouseEnter).on('mouseleave', onMouseLeave).attr({
              zIndex: 8
            }).add();
          }

          return this.label;
        };

        Tooltip.prototype.getPosition = function (boxWidth, boxHeight, point) {
          var chart = this.chart,
              distance = this.distance,
              ret = {},
              h = chart.inverted && point.h || 0,
              swapped,
              outside = this.outside,
              outerWidth = outside ? doc.documentElement.clientWidth - 2 * distance : chart.chartWidth,
              outerHeight = outside ? Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight, doc.body.offsetHeight, doc.documentElement.offsetHeight, doc.documentElement.clientHeight) : chart.chartHeight,
              chartPosition = chart.pointer.getChartPosition(),
              containerScaling = chart.containerScaling,
              scaleX = function scaleX(val) {
            return containerScaling ? val * containerScaling.scaleX : val;
          },
              scaleY = function scaleY(val) {
            return containerScaling ? val * containerScaling.scaleY : val;
          },
              buildDimensionArray = function buildDimensionArray(dim) {
            var isX = dim === 'x';
            return [dim, isX ? outerWidth : outerHeight, isX ? boxWidth : boxHeight].concat(outside ? [isX ? scaleX(boxWidth) : scaleY(boxHeight), isX ? chartPosition.left - distance + scaleX(point.plotX + chart.plotLeft) : chartPosition.top - distance + scaleY(point.plotY + chart.plotTop), 0, isX ? outerWidth : outerHeight] : [isX ? boxWidth : boxHeight, isX ? point.plotX + chart.plotLeft : point.plotY + chart.plotTop, isX ? chart.plotLeft : chart.plotTop, isX ? chart.plotLeft + chart.plotWidth : chart.plotTop + chart.plotHeight]);
          },
              first = buildDimensionArray('y'),
              second = buildDimensionArray('x'),
              preferFarSide = !this.followPointer && pick(point.ttBelow, !chart.inverted === !!point.negative),
              firstDimension = function firstDimension(dim, outerSize, innerSize, scaledInnerSize, point, min, max) {
            var scaledDist = dim === 'y' ? scaleY(distance) : scaleX(distance),
                scaleDiff = (innerSize - scaledInnerSize) / 2,
                roomLeft = scaledInnerSize < point - distance,
                roomRight = point + distance + scaledInnerSize < outerSize,
                alignedLeft = point - scaledDist - innerSize + scaleDiff,
                alignedRight = point + scaledDist - scaleDiff;

            if (preferFarSide && roomRight) {
              ret[dim] = alignedRight;
            } else if (!preferFarSide && roomLeft) {
              ret[dim] = alignedLeft;
            } else if (roomLeft) {
              ret[dim] = Math.min(max - scaledInnerSize, alignedLeft - h < 0 ? alignedLeft : alignedLeft - h);
            } else if (roomRight) {
              ret[dim] = Math.max(min, alignedRight + h + innerSize > outerSize ? alignedRight : alignedRight + h);
            } else {
              return false;
            }
          },
              secondDimension = function secondDimension(dim, outerSize, innerSize, scaledInnerSize, point) {
            var retVal;

            if (point < distance || point > outerSize - distance) {
              retVal = false;
            } else if (point < innerSize / 2) {
              ret[dim] = 1;
            } else if (point > outerSize - scaledInnerSize / 2) {
              ret[dim] = outerSize - scaledInnerSize - 2;
            } else {
              ret[dim] = point - innerSize / 2;
            }

            return retVal;
          },
              swap = function swap(count) {
            var temp = first;
            first = second;
            second = temp;
            swapped = count;
          },
              run = function run() {
            if (firstDimension.apply(0, first) !== false) {
              if (secondDimension.apply(0, second) === false && !swapped) {
                swap(true);
                run();
              }
            } else if (!swapped) {
              swap(true);
              run();
            } else {
              ret.x = ret.y = 0;
            }
          };

          if (chart.inverted || this.len > 1) {
            swap();
          }

          run();
          return ret;
        };

        Tooltip.prototype.getXDateFormat = function (point, options, xAxis) {
          var xDateFormat,
              dateTimeLabelFormats = options.dateTimeLabelFormats,
              closestPointRange = xAxis && xAxis.closestPointRange;

          if (closestPointRange) {
            xDateFormat = this.getDateFormat(closestPointRange, point.x, xAxis.options.startOfWeek, dateTimeLabelFormats);
          } else {
            xDateFormat = dateTimeLabelFormats.day;
          }

          return xDateFormat || dateTimeLabelFormats.year;
        };

        Tooltip.prototype.hide = function (delay) {
          var tooltip = this;
          U.clearTimeout(this.hideTimer);
          delay = pick(delay, this.options.hideDelay, 500);

          if (!this.isHidden) {
            this.hideTimer = syncTimeout(function () {
              tooltip.getLabel()[delay ? 'fadeOut' : 'hide']();
              tooltip.isHidden = true;
            }, delay);
          }
        };

        Tooltip.prototype.init = function (chart, options) {
          this.chart = chart;
          this.options = options;
          this.crosshairs = [];
          this.now = {
            x: 0,
            y: 0
          };
          this.isHidden = true;
          this.split = options.split && !chart.inverted && !chart.polar;
          this.shared = options.shared || this.split;
          this.outside = pick(options.outside, Boolean(chart.scrollablePixelsX || chart.scrollablePixelsY));
        };

        Tooltip.prototype.isStickyOnContact = function () {
          return !!(!this.followPointer && this.options.stickOnContact && this.inContact);
        };

        Tooltip.prototype.move = function (x, y, anchorX, anchorY) {
          var tooltip = this,
              now = tooltip.now,
              animate = tooltip.options.animation !== false && !tooltip.isHidden && (Math.abs(x - now.x) > 1 || Math.abs(y - now.y) > 1),
              skipAnchor = tooltip.followPointer || tooltip.len > 1;
          extend(now, {
            x: animate ? (2 * now.x + x) / 3 : x,
            y: animate ? (now.y + y) / 2 : y,
            anchorX: skipAnchor ? void 0 : animate ? (2 * now.anchorX + anchorX) / 3 : anchorX,
            anchorY: skipAnchor ? void 0 : animate ? (now.anchorY + anchorY) / 2 : anchorY
          });
          tooltip.getLabel().attr(now);
          tooltip.drawTracker();

          if (animate) {
            U.clearTimeout(this.tooltipTimeout);
            this.tooltipTimeout = setTimeout(function () {
              if (tooltip) {
                tooltip.move(x, y, anchorX, anchorY);
              }
            }, 32);
          }
        };

        Tooltip.prototype.refresh = function (pointOrPoints, mouseEvent) {
          var tooltip = this,
              chart = this.chart,
              options = tooltip.options,
              x,
              y,
              point = pointOrPoints,
              anchor,
              textConfig = {},
              text,
              pointConfig = [],
              formatter = options.formatter || tooltip.defaultFormatter,
              shared = tooltip.shared,
              currentSeries,
              styledMode = chart.styledMode;

          if (!options.enabled) {
            return;
          }

          U.clearTimeout(this.hideTimer);
          tooltip.followPointer = splat(point)[0].series.tooltipOptions.followPointer;
          anchor = tooltip.getAnchor(point, mouseEvent);
          x = anchor[0];
          y = anchor[1];

          if (shared && !(point.series && point.series.noSharedTooltip)) {
            chart.pointer.applyInactiveState(point);
            point.forEach(function (item) {
              item.setState('hover');
              pointConfig.push(item.getLabelConfig());
            });
            textConfig = {
              x: point[0].category,
              y: point[0].y
            };
            textConfig.points = pointConfig;
            point = point[0];
          } else {
            textConfig = point.getLabelConfig();
          }

          this.len = pointConfig.length;
          text = formatter.call(textConfig, tooltip);
          currentSeries = point.series;
          this.distance = pick(currentSeries.tooltipOptions.distance, 16);

          if (text === false) {
            this.hide();
          } else {
            if (tooltip.split) {
              this.renderSplit(text, splat(pointOrPoints));
            } else {
              var label = tooltip.getLabel();

              if (!options.style.width || styledMode) {
                label.css({
                  width: this.chart.spacingBox.width
                });
              }

              label.attr({
                text: text && text.join ? text.join('') : text
              });
              label.removeClass(/highcharts-color-[\d]+/g).addClass('highcharts-color-' + pick(point.colorIndex, currentSeries.colorIndex));

              if (!styledMode) {
                label.attr({
                  stroke: options.borderColor || point.color || currentSeries.color || '#666666'
                });
              }

              tooltip.updatePosition({
                plotX: x,
                plotY: y,
                negative: point.negative,
                ttBelow: point.ttBelow,
                h: anchor[2] || 0
              });
            }

            if (tooltip.isHidden && tooltip.label) {
              tooltip.label.attr({
                opacity: 1
              }).show();
            }

            tooltip.isHidden = false;
          }

          H.fireEvent(this, 'refresh');
        };

        Tooltip.prototype.renderSplit = function (labels, points) {
          var tooltip = this;

          var chart = tooltip.chart,
              _a = tooltip.chart,
              chartWidth = _a.chartWidth,
              chartHeight = _a.chartHeight,
              plotHeight = _a.plotHeight,
              plotLeft = _a.plotLeft,
              plotTop = _a.plotTop,
              pointer = _a.pointer,
              ren = _a.renderer,
              _b = _a.scrollablePixelsY,
              scrollablePixelsY = _b === void 0 ? 0 : _b,
              _c = _a.scrollingContainer,
              _d = _c === void 0 ? {
            scrollLeft: 0,
            scrollTop: 0
          } : _c,
              scrollLeft = _d.scrollLeft,
              scrollTop = _d.scrollTop,
              styledMode = _a.styledMode,
              distance = tooltip.distance,
              options = tooltip.options,
              positioner = tooltip.options.positioner;

          var bounds = {
            left: scrollLeft,
            right: scrollLeft + chartWidth,
            top: scrollTop,
            bottom: scrollTop + chartHeight
          };
          var tooltipLabel = tooltip.getLabel();
          var headerTop = Boolean(chart.xAxis[0] && chart.xAxis[0].opposite);
          var distributionBoxTop = plotTop + scrollTop;
          var headerHeight = 0;
          var adjustedPlotHeight = plotHeight - scrollablePixelsY;

          function getAnchor(point) {
            var isHeader = point.isHeader,
                _a = point.plotX,
                plotX = _a === void 0 ? 0 : _a,
                _b = point.plotY,
                plotY = _b === void 0 ? 0 : _b,
                series = point.series;
            var anchorX;
            var anchorY;

            if (isHeader) {
              anchorX = plotLeft + plotX;
              anchorY = plotTop + plotHeight / 2;
            } else {
              var xAxis = series.xAxis,
                  yAxis = series.yAxis;
              anchorX = xAxis.pos + clamp(plotX, -distance, xAxis.len + distance);

              if (yAxis.pos + plotY >= scrollTop + plotTop && yAxis.pos + plotY <= scrollTop + plotTop + plotHeight - scrollablePixelsY) {
                anchorY = yAxis.pos + plotY;
              }
            }

            anchorX = clamp(anchorX, bounds.left - distance, bounds.right + distance);
            return {
              anchorX: anchorX,
              anchorY: anchorY
            };
          }

          function defaultPositioner(anchorX, anchorY, isHeader, boxWidth, alignedLeft) {
            if (alignedLeft === void 0) {
              alignedLeft = true;
            }

            var y;
            var x;

            if (isHeader) {
              y = headerTop ? 0 : adjustedPlotHeight;
              x = clamp(anchorX - boxWidth / 2, bounds.left, bounds.right - boxWidth);
            } else {
              y = anchorY - distributionBoxTop;
              x = alignedLeft ? anchorX - boxWidth - distance : anchorX + distance;
              x = clamp(x, alignedLeft ? x : bounds.left, bounds.right);
            }

            return {
              x: x,
              y: y
            };
          }

          function updatePartialTooltip(partialTooltip, point, str) {
            var tt = partialTooltip;
            var isHeader = point.isHeader,
                series = point.series;
            var colorClass = 'highcharts-color-' + pick(point.colorIndex, series.colorIndex, 'none');

            if (!tt) {
              var attribs = {
                padding: options.padding,
                r: options.borderRadius
              };

              if (!styledMode) {
                attribs.fill = options.backgroundColor;
                attribs['stroke-width'] = options.borderWidth;
              }

              tt = ren.label('', 0, 0, options[isHeader ? 'headerShape' : 'shape'] || 'callout', void 0, void 0, options.useHTML).addClass((isHeader ? 'highcharts-tooltip-header ' : '') + 'highcharts-tooltip-box ' + colorClass).attr(attribs).add(tooltipLabel);
            }

            tt.isActive = true;
            tt.attr({
              text: str
            });

            if (!styledMode) {
              tt.css(options.style).shadow(options.shadow).attr({
                stroke: options.borderColor || point.color || series.color || '#333333'
              });
            }

            return tt;
          }

          if (isString(labels)) {
            labels = [false, labels];
          }

          var boxes = labels.slice(0, points.length + 1).reduce(function (boxes, str, i) {
            if (str !== false && str !== '') {
              var point = points[i - 1] || {
                isHeader: true,
                plotX: points[0].plotX,
                plotY: plotHeight,
                series: {}
              };
              var isHeader = point.isHeader;
              var owner = isHeader ? tooltip : point.series;
              var tt = owner.tt = updatePartialTooltip(owner.tt, point, str);
              var bBox = tt.getBBox();
              var boxWidth = bBox.width + tt.strokeWidth();

              if (isHeader) {
                headerHeight = bBox.height;
                adjustedPlotHeight += headerHeight;

                if (headerTop) {
                  distributionBoxTop -= headerHeight;
                }
              }

              var _a = getAnchor(point),
                  anchorX = _a.anchorX,
                  anchorY = _a.anchorY;

              if (typeof anchorY === 'number') {
                var size = bBox.height + 1;
                var boxPosition = positioner ? positioner.call(tooltip, boxWidth, size, point) : defaultPositioner(anchorX, anchorY, isHeader, boxWidth);
                boxes.push({
                  align: positioner ? 0 : void 0,
                  anchorX: anchorX,
                  anchorY: anchorY,
                  boxWidth: boxWidth,
                  point: point,
                  rank: pick(boxPosition.rank, isHeader ? 1 : 0),
                  size: size,
                  target: boxPosition.y,
                  tt: tt,
                  x: boxPosition.x
                });
              } else {
                tt.isActive = false;
              }
            }

            return boxes;
          }, []);

          if (!positioner && boxes.some(function (box) {
            return box.x < bounds.left;
          })) {
            boxes = boxes.map(function (box) {
              var _a = defaultPositioner(box.anchorX, box.anchorY, box.point.isHeader, box.boxWidth, false),
                  x = _a.x,
                  y = _a.y;

              return extend(box, {
                target: y,
                x: x
              });
            });
          }

          tooltip.cleanSplit();
          H.distribute(boxes, adjustedPlotHeight);
          boxes.forEach(function (box) {
            var anchorX = box.anchorX,
                anchorY = box.anchorY,
                pos = box.pos,
                x = box.x;
            box.tt.attr({
              visibility: typeof pos === 'undefined' ? 'hidden' : 'inherit',
              x: x,
              y: pos + distributionBoxTop,
              anchorX: anchorX,
              anchorY: anchorY
            });
          });
          var container = tooltip.container,
              outside = tooltip.outside,
              renderer = tooltip.renderer;

          if (outside && container && renderer) {
            var _e = tooltipLabel.getBBox(),
                width = _e.width,
                height = _e.height,
                x = _e.x,
                y = _e.y;

            renderer.setSize(width + x, height + y, false);
            var chartPosition = pointer.getChartPosition();
            container.style.left = chartPosition.left + 'px';
            container.style.top = chartPosition.top + 'px';
          }
        };

        Tooltip.prototype.drawTracker = function () {
          var tooltip = this;

          if (tooltip.followPointer || !tooltip.options.stickOnContact) {
            if (tooltip.tracker) {
              tooltip.tracker.destroy();
            }

            return;
          }

          var chart = tooltip.chart;
          var label = tooltip.label;
          var point = chart.hoverPoint;

          if (!label || !point) {
            return;
          }

          var box = {
            x: 0,
            y: 0,
            width: 0,
            height: 0
          };
          var anchorPos = this.getAnchor(point);
          var labelBBox = label.getBBox();
          anchorPos[0] += chart.plotLeft - label.translateX;
          anchorPos[1] += chart.plotTop - label.translateY;
          box.x = Math.min(0, anchorPos[0]);
          box.y = Math.min(0, anchorPos[1]);
          box.width = anchorPos[0] < 0 ? Math.max(Math.abs(anchorPos[0]), labelBBox.width - anchorPos[0]) : Math.max(Math.abs(anchorPos[0]), labelBBox.width);
          box.height = anchorPos[1] < 0 ? Math.max(Math.abs(anchorPos[1]), labelBBox.height - Math.abs(anchorPos[1])) : Math.max(Math.abs(anchorPos[1]), labelBBox.height);

          if (tooltip.tracker) {
            tooltip.tracker.attr(box);
          } else {
            tooltip.tracker = label.renderer.rect(box).addClass('highcharts-tracker').add(label);

            if (!chart.styledMode) {
              tooltip.tracker.attr({
                fill: 'rgba(0,0,0,0)'
              });
            }
          }
        };

        Tooltip.prototype.styledModeFormat = function (formatString) {
          return formatString.replace('style="font-size: 10px"', 'class="highcharts-header"').replace(/style="color:{(point|series)\.color}"/g, 'class="highcharts-color-{$1.colorIndex}"');
        };

        Tooltip.prototype.tooltipFooterHeaderFormatter = function (labelConfig, isFooter) {
          var footOrHead = isFooter ? 'footer' : 'header',
              series = labelConfig.series,
              tooltipOptions = series.tooltipOptions,
              xDateFormat = tooltipOptions.xDateFormat,
              xAxis = series.xAxis,
              isDateTime = xAxis && xAxis.options.type === 'datetime' && isNumber(labelConfig.key),
              formatString = tooltipOptions[footOrHead + 'Format'],
              evt = {
            isFooter: isFooter,
            labelConfig: labelConfig
          };
          H.fireEvent(this, 'headerFormatter', evt, function (e) {
            if (isDateTime && !xDateFormat) {
              xDateFormat = this.getXDateFormat(labelConfig, tooltipOptions, xAxis);
            }

            if (isDateTime && xDateFormat) {
              (labelConfig.point && labelConfig.point.tooltipDateKeys || ['key']).forEach(function (key) {
                formatString = formatString.replace('{point.' + key + '}', '{point.' + key + ':' + xDateFormat + '}');
              });
            }

            if (series.chart.styledMode) {
              formatString = this.styledModeFormat(formatString);
            }

            e.text = format(formatString, {
              point: labelConfig,
              series: series
            }, this.chart);
          });
          return evt.text;
        };

        Tooltip.prototype.update = function (options) {
          this.destroy();
          merge(true, this.chart.options.tooltip.userOptions, options);
          this.init(this.chart, merge(true, this.options, options));
        };

        Tooltip.prototype.updatePosition = function (point) {
          var chart = this.chart,
              pointer = chart.pointer,
              label = this.getLabel(),
              pos,
              anchorX = point.plotX + chart.plotLeft,
              anchorY = point.plotY + chart.plotTop,
              pad;
          var chartPosition = pointer.getChartPosition();
          pos = (this.options.positioner || this.getPosition).call(this, label.width, label.height, point);

          if (this.outside) {
            pad = (this.options.borderWidth || 0) + 2 * this.distance;
            this.renderer.setSize(label.width + pad, label.height + pad, false);
            var containerScaling = chart.containerScaling;

            if (containerScaling) {
              css(this.container, {
                transform: "scale(" + containerScaling.scaleX + ", " + containerScaling.scaleY + ")"
              });
              anchorX *= containerScaling.scaleX;
              anchorY *= containerScaling.scaleY;
            }

            anchorX += chartPosition.left - pos.x;
            anchorY += chartPosition.top - pos.y;
          }

          this.move(Math.round(pos.x), Math.round(pos.y || 0), anchorX, anchorY);
        };

        return Tooltip;
      }();

      H.Tooltip = Tooltip;
      return H.Tooltip;
    });

    _registerModule(_modules, 'parts/Pointer.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js'], _modules['parts/Tooltip.js'], _modules['parts/Color.js']], function (Highcharts, U, Tooltip, Color) {
      var addEvent = U.addEvent,
          attr = U.attr,
          css = U.css,
          defined = U.defined,
          extend = U.extend,
          find = U.find,
          fireEvent = U.fireEvent,
          isNumber = U.isNumber,
          isObject = U.isObject,
          objectEach = U.objectEach,
          offset = U.offset,
          pick = U.pick,
          splat = U.splat;
      var color = Color.parse;
      var H = Highcharts,
          charts = H.charts,
          noop = H.noop;

      var Pointer = function () {
        function Pointer(chart, options) {
          this.lastValidTouch = {};
          this.pinchDown = [];
          this.runChartClick = false;
          this.chart = chart;
          this.hasDragged = false;
          this.options = options;

          this.unbindContainerMouseLeave = function () {};

          this.init(chart, options);
        }

        Pointer.prototype.applyInactiveState = function (points) {
          var activeSeries = [],
              series;
          (points || []).forEach(function (item) {
            series = item.series;
            activeSeries.push(series);

            if (series.linkedParent) {
              activeSeries.push(series.linkedParent);
            }

            if (series.linkedSeries) {
              activeSeries = activeSeries.concat(series.linkedSeries);
            }

            if (series.navigatorSeries) {
              activeSeries.push(series.navigatorSeries);
            }
          });
          this.chart.series.forEach(function (inactiveSeries) {
            if (activeSeries.indexOf(inactiveSeries) === -1) {
              inactiveSeries.setState('inactive', true);
            } else if (inactiveSeries.options.inactiveOtherPoints) {
              inactiveSeries.setAllPointsToState('inactive');
            }
          });
        };

        Pointer.prototype.destroy = function () {
          var pointer = this;

          if (typeof pointer.unDocMouseMove !== 'undefined') {
            pointer.unDocMouseMove();
          }

          this.unbindContainerMouseLeave();

          if (!H.chartCount) {
            if (H.unbindDocumentMouseUp) {
              H.unbindDocumentMouseUp = H.unbindDocumentMouseUp();
            }

            if (H.unbindDocumentTouchEnd) {
              H.unbindDocumentTouchEnd = H.unbindDocumentTouchEnd();
            }
          }

          clearInterval(pointer.tooltipTimeout);
          objectEach(pointer, function (val, prop) {
            pointer[prop] = null;
          });
        };

        Pointer.prototype.drag = function (e) {
          var chart = this.chart,
              chartOptions = chart.options.chart,
              chartX = e.chartX,
              chartY = e.chartY,
              zoomHor = this.zoomHor,
              zoomVert = this.zoomVert,
              plotLeft = chart.plotLeft,
              plotTop = chart.plotTop,
              plotWidth = chart.plotWidth,
              plotHeight = chart.plotHeight,
              clickedInside,
              size,
              selectionMarker = this.selectionMarker,
              mouseDownX = this.mouseDownX || 0,
              mouseDownY = this.mouseDownY || 0,
              panningEnabled = isObject(chartOptions.panning) ? chartOptions.panning && chartOptions.panning.enabled : chartOptions.panning,
              panKey = chartOptions.panKey && e[chartOptions.panKey + 'Key'];

          if (selectionMarker && selectionMarker.touch) {
            return;
          }

          if (chartX < plotLeft) {
            chartX = plotLeft;
          } else if (chartX > plotLeft + plotWidth) {
            chartX = plotLeft + plotWidth;
          }

          if (chartY < plotTop) {
            chartY = plotTop;
          } else if (chartY > plotTop + plotHeight) {
            chartY = plotTop + plotHeight;
          }

          this.hasDragged = Math.sqrt(Math.pow(mouseDownX - chartX, 2) + Math.pow(mouseDownY - chartY, 2));

          if (this.hasDragged > 10) {
            clickedInside = chart.isInsidePlot(mouseDownX - plotLeft, mouseDownY - plotTop);

            if (chart.hasCartesianSeries && (this.zoomX || this.zoomY) && clickedInside && !panKey) {
              if (!selectionMarker) {
                this.selectionMarker = selectionMarker = chart.renderer.rect(plotLeft, plotTop, zoomHor ? 1 : plotWidth, zoomVert ? 1 : plotHeight, 0).attr({
                  'class': 'highcharts-selection-marker',
                  zIndex: 7
                }).add();

                if (!chart.styledMode) {
                  selectionMarker.attr({
                    fill: chartOptions.selectionMarkerFill || color('#335cad').setOpacity(0.25).get()
                  });
                }
              }
            }

            if (selectionMarker && zoomHor) {
              size = chartX - mouseDownX;
              selectionMarker.attr({
                width: Math.abs(size),
                x: (size > 0 ? 0 : size) + mouseDownX
              });
            }

            if (selectionMarker && zoomVert) {
              size = chartY - mouseDownY;
              selectionMarker.attr({
                height: Math.abs(size),
                y: (size > 0 ? 0 : size) + mouseDownY
              });
            }

            if (clickedInside && !selectionMarker && panningEnabled) {
              chart.pan(e, chartOptions.panning);
            }
          }
        };

        Pointer.prototype.dragStart = function (e) {
          var chart = this.chart;
          chart.mouseIsDown = e.type;
          chart.cancelClick = false;
          chart.mouseDownX = this.mouseDownX = e.chartX;
          chart.mouseDownY = this.mouseDownY = e.chartY;
        };

        Pointer.prototype.drop = function (e) {
          var pointer = this,
              chart = this.chart,
              hasPinched = this.hasPinched;

          if (this.selectionMarker) {
            var selectionData = {
              originalEvent: e,
              xAxis: [],
              yAxis: []
            },
                selectionBox = this.selectionMarker,
                selectionLeft = selectionBox.attr ? selectionBox.attr('x') : selectionBox.x,
                selectionTop = selectionBox.attr ? selectionBox.attr('y') : selectionBox.y,
                selectionWidth = selectionBox.attr ? selectionBox.attr('width') : selectionBox.width,
                selectionHeight = selectionBox.attr ? selectionBox.attr('height') : selectionBox.height,
                runZoom;

            if (this.hasDragged || hasPinched) {
              chart.axes.forEach(function (axis) {
                if (axis.zoomEnabled && defined(axis.min) && (hasPinched || pointer[{
                  xAxis: 'zoomX',
                  yAxis: 'zoomY'
                }[axis.coll]])) {
                  var horiz = axis.horiz,
                      minPixelPadding = e.type === 'touchend' ? axis.minPixelPadding : 0,
                      selectionMin = axis.toValue((horiz ? selectionLeft : selectionTop) + minPixelPadding),
                      selectionMax = axis.toValue((horiz ? selectionLeft + selectionWidth : selectionTop + selectionHeight) - minPixelPadding);
                  selectionData[axis.coll].push({
                    axis: axis,
                    min: Math.min(selectionMin, selectionMax),
                    max: Math.max(selectionMin, selectionMax)
                  });
                  runZoom = true;
                }
              });

              if (runZoom) {
                fireEvent(chart, 'selection', selectionData, function (args) {
                  chart.zoom(extend(args, hasPinched ? {
                    animation: false
                  } : null));
                });
              }
            }

            if (isNumber(chart.index)) {
              this.selectionMarker = this.selectionMarker.destroy();
            }

            if (hasPinched) {
              this.scaleGroups();
            }
          }

          if (chart && isNumber(chart.index)) {
            css(chart.container, {
              cursor: chart._cursor
            });
            chart.cancelClick = this.hasDragged > 10;
            chart.mouseIsDown = this.hasDragged = this.hasPinched = false;
            this.pinchDown = [];
          }
        };

        Pointer.prototype.findNearestKDPoint = function (series, shared, e) {
          var chart = this.chart;
          var hoverPoint = chart.hoverPoint;
          var tooltip = chart.tooltip;

          if (hoverPoint && tooltip && tooltip.isStickyOnContact()) {
            return hoverPoint;
          }

          var closest;

          function sort(p1, p2) {
            var isCloserX = p1.distX - p2.distX,
                isCloser = p1.dist - p2.dist,
                isAbove = (p2.series.group && p2.series.group.zIndex) - (p1.series.group && p1.series.group.zIndex),
                result;

            if (isCloserX !== 0 && shared) {
              result = isCloserX;
            } else if (isCloser !== 0) {
              result = isCloser;
            } else if (isAbove !== 0) {
              result = isAbove;
            } else {
              result = p1.series.index > p2.series.index ? -1 : 1;
            }

            return result;
          }

          series.forEach(function (s) {
            var noSharedTooltip = s.noSharedTooltip && shared,
                compareX = !noSharedTooltip && s.options.findNearestPointBy.indexOf('y') < 0,
                point = s.searchPoint(e, compareX);

            if (isObject(point, true) && (!isObject(closest, true) || sort(closest, point) > 0)) {
              closest = point;
            }
          });
          return closest;
        };

        Pointer.prototype.getChartCoordinatesFromPoint = function (point, inverted) {
          var series = point.series,
              xAxis = series.xAxis,
              yAxis = series.yAxis,
              plotX = pick(point.clientX, point.plotX),
              shapeArgs = point.shapeArgs;

          if (xAxis && yAxis) {
            return inverted ? {
              chartX: xAxis.len + xAxis.pos - plotX,
              chartY: yAxis.len + yAxis.pos - point.plotY
            } : {
              chartX: plotX + xAxis.pos,
              chartY: point.plotY + yAxis.pos
            };
          }

          if (shapeArgs && shapeArgs.x && shapeArgs.y) {
            return {
              chartX: shapeArgs.x,
              chartY: shapeArgs.y
            };
          }
        };

        Pointer.prototype.getChartPosition = function () {
          return this.chartPosition || (this.chartPosition = offset(this.chart.container));
        };

        Pointer.prototype.getCoordinates = function (e) {
          var coordinates = {
            xAxis: [],
            yAxis: []
          };
          this.chart.axes.forEach(function (axis) {
            coordinates[axis.isXAxis ? 'xAxis' : 'yAxis'].push({
              axis: axis,
              value: axis.toValue(e[axis.horiz ? 'chartX' : 'chartY'])
            });
          });
          return coordinates;
        };

        Pointer.prototype.getHoverData = function (existingHoverPoint, existingHoverSeries, series, isDirectTouch, shared, e) {
          var hoverPoint,
              hoverPoints = [],
              hoverSeries = existingHoverSeries,
              useExisting = !!(isDirectTouch && existingHoverPoint),
              notSticky = hoverSeries && !hoverSeries.stickyTracking,
              searchSeries,
              eventArgs = {
            chartX: e ? e.chartX : void 0,
            chartY: e ? e.chartY : void 0,
            shared: shared
          },
              filter = function filter(s) {
            return s.visible && !(!shared && s.directTouch) && pick(s.options.enableMouseTracking, true);
          };

          fireEvent(this, 'beforeGetHoverData', eventArgs);
          searchSeries = notSticky ? [hoverSeries] : series.filter(function (s) {
            return eventArgs.filter ? eventArgs.filter(s) : filter(s) && s.stickyTracking;
          });
          hoverPoint = useExisting || !e ? existingHoverPoint : this.findNearestKDPoint(searchSeries, shared, e);
          hoverSeries = hoverPoint && hoverPoint.series;

          if (hoverPoint) {
            if (shared && !hoverSeries.noSharedTooltip) {
              searchSeries = series.filter(function (s) {
                return eventArgs.filter ? eventArgs.filter(s) : filter(s) && !s.noSharedTooltip;
              });
              searchSeries.forEach(function (s) {
                var point = find(s.points, function (p) {
                  return p.x === hoverPoint.x && !p.isNull;
                });

                if (isObject(point)) {
                  if (s.chart.isBoosting) {
                    point = s.getPoint(point);
                  }

                  hoverPoints.push(point);
                }
              });
            } else {
              hoverPoints.push(hoverPoint);
            }
          }

          eventArgs = {
            hoverPoint: hoverPoint
          };
          fireEvent(this, 'afterGetHoverData', eventArgs);
          return {
            hoverPoint: eventArgs.hoverPoint,
            hoverSeries: hoverSeries,
            hoverPoints: hoverPoints
          };
        };

        Pointer.prototype.getPointFromEvent = function (e) {
          var target = e.target,
              point;

          while (target && !point) {
            point = target.point;
            target = target.parentNode;
          }

          return point;
        };

        Pointer.prototype.onTrackerMouseOut = function (e) {
          var chart = this.chart;
          var series = chart.hoverSeries;
          var relatedTarget = e.relatedTarget || e.toElement;
          this.isDirectTouch = false;

          if (series && relatedTarget && !series.stickyTracking && !this.inClass(relatedTarget, 'highcharts-tooltip') && (!this.inClass(relatedTarget, 'highcharts-series-' + series.index) || !this.inClass(relatedTarget, 'highcharts-tracker'))) {
            series.onMouseOut();
          }
        };

        Pointer.prototype.inClass = function (element, className) {
          var elemClassName;

          while (element) {
            elemClassName = attr(element, 'class');

            if (elemClassName) {
              if (elemClassName.indexOf(className) !== -1) {
                return true;
              }

              if (elemClassName.indexOf('highcharts-container') !== -1) {
                return false;
              }
            }

            element = element.parentNode;
          }
        };

        Pointer.prototype.init = function (chart, options) {
          this.options = options;
          this.chart = chart;
          this.runChartClick = options.chart.events && !!options.chart.events.click;
          this.pinchDown = [];
          this.lastValidTouch = {};

          if (Tooltip) {
            chart.tooltip = new Tooltip(chart, options.tooltip);
            this.followTouchMove = pick(options.tooltip.followTouchMove, true);
          }

          this.setDOMEvents();
        };

        Pointer.prototype.normalize = function (e, chartPosition) {
          var touches = e.touches;
          var ePos = touches ? touches.length ? touches.item(0) : touches.changedTouches[0] : e;

          if (!chartPosition) {
            chartPosition = this.getChartPosition();
          }

          var chartX = ePos.pageX - chartPosition.left,
              chartY = ePos.pageY - chartPosition.top;
          var containerScaling = this.chart.containerScaling;

          if (containerScaling) {
            chartX /= containerScaling.scaleX;
            chartY /= containerScaling.scaleY;
          }

          return extend(e, {
            chartX: Math.round(chartX),
            chartY: Math.round(chartY)
          });
        };

        Pointer.prototype.onContainerClick = function (e) {
          var chart = this.chart,
              hoverPoint = chart.hoverPoint,
              plotLeft = chart.plotLeft,
              plotTop = chart.plotTop;
          e = this.normalize(e);

          if (!chart.cancelClick) {
            if (hoverPoint && this.inClass(e.target, 'highcharts-tracker')) {
              fireEvent(hoverPoint.series, 'click', extend(e, {
                point: hoverPoint
              }));

              if (chart.hoverPoint) {
                hoverPoint.firePointEvent('click', e);
              }
            } else {
              extend(e, this.getCoordinates(e));

              if (chart.isInsidePlot(e.chartX - plotLeft, e.chartY - plotTop)) {
                fireEvent(chart, 'click', e);
              }
            }
          }
        };

        Pointer.prototype.onContainerMouseDown = function (e) {
          e = this.normalize(e);

          if (e.button !== 2) {
            this.zoomOption(e);

            if (e.preventDefault) {
              e.preventDefault();
            }

            this.dragStart(e);
          }
        };

        Pointer.prototype.onContainerMouseLeave = function (e) {
          var chart = charts[H.hoverChartIndex];

          if (chart && (e.relatedTarget || e.toElement)) {
            chart.pointer.reset();
            chart.pointer.chartPosition = void 0;
          }
        };

        Pointer.prototype.onContainerMouseMove = function (e) {
          var chart = this.chart;

          if (!defined(H.hoverChartIndex) || !charts[H.hoverChartIndex] || !charts[H.hoverChartIndex].mouseIsDown) {
            H.hoverChartIndex = chart.index;
          }

          e = this.normalize(e);

          if (!e.preventDefault) {
            e.returnValue = false;
          }

          if (chart.mouseIsDown === 'mousedown') {
            this.drag(e);
          }

          if (!chart.openMenu && (this.inClass(e.target, 'highcharts-tracker') || chart.isInsidePlot(e.chartX - chart.plotLeft, e.chartY - chart.plotTop))) {
            this.runPointActions(e);
          }
        };

        Pointer.prototype.onDocumentTouchEnd = function (e) {
          if (charts[H.hoverChartIndex]) {
            charts[H.hoverChartIndex].pointer.drop(e);
          }
        };

        Pointer.prototype.onContainerTouchMove = function (e) {
          this.touch(e);
        };

        Pointer.prototype.onContainerTouchStart = function (e) {
          this.zoomOption(e);
          this.touch(e, true);
        };

        Pointer.prototype.onDocumentMouseMove = function (e) {
          var chart = this.chart;
          var chartPosition = this.chartPosition;
          var tooltip = chart.tooltip;
          e = this.normalize(e, chartPosition);

          if (chartPosition && (!tooltip || !tooltip.isStickyOnContact()) && !chart.isInsidePlot(e.chartX - chart.plotLeft, e.chartY - chart.plotTop) && !this.inClass(e.target, 'highcharts-tracker')) {
            this.reset();
          }
        };

        Pointer.prototype.onDocumentMouseUp = function (e) {
          if (charts[H.hoverChartIndex]) {
            charts[H.hoverChartIndex].pointer.drop(e);
          }
        };

        Pointer.prototype.pinch = function (e) {
          var self = this,
              chart = self.chart,
              pinchDown = self.pinchDown,
              touches = e.touches || [],
              touchesLength = touches.length,
              lastValidTouch = self.lastValidTouch,
              hasZoom = self.hasZoom,
              selectionMarker = self.selectionMarker,
              transform = {},
              fireClickEvent = touchesLength === 1 && (self.inClass(e.target, 'highcharts-tracker') && chart.runTrackerClick || self.runChartClick),
              clip = {};

          if (touchesLength > 1) {
            self.initiated = true;
          }

          if (hasZoom && self.initiated && !fireClickEvent) {
            e.preventDefault();
          }

          [].map.call(touches, function (e) {
            return self.normalize(e);
          });

          if (e.type === 'touchstart') {
            [].forEach.call(touches, function (e, i) {
              pinchDown[i] = {
                chartX: e.chartX,
                chartY: e.chartY
              };
            });
            lastValidTouch.x = [pinchDown[0].chartX, pinchDown[1] && pinchDown[1].chartX];
            lastValidTouch.y = [pinchDown[0].chartY, pinchDown[1] && pinchDown[1].chartY];
            chart.axes.forEach(function (axis) {
              if (axis.zoomEnabled) {
                var bounds = chart.bounds[axis.horiz ? 'h' : 'v'],
                    minPixelPadding = axis.minPixelPadding,
                    min = axis.toPixels(Math.min(pick(axis.options.min, axis.dataMin), axis.dataMin)),
                    max = axis.toPixels(Math.max(pick(axis.options.max, axis.dataMax), axis.dataMax)),
                    absMin = Math.min(min, max),
                    absMax = Math.max(min, max);
                bounds.min = Math.min(axis.pos, absMin - minPixelPadding);
                bounds.max = Math.max(axis.pos + axis.len, absMax + minPixelPadding);
              }
            });
            self.res = true;
          } else if (self.followTouchMove && touchesLength === 1) {
            this.runPointActions(self.normalize(e));
          } else if (pinchDown.length) {
            if (!selectionMarker) {
              self.selectionMarker = selectionMarker = extend({
                destroy: noop,
                touch: true
              }, chart.plotBox);
            }

            self.pinchTranslate(pinchDown, touches, transform, selectionMarker, clip, lastValidTouch);
            self.hasPinched = hasZoom;
            self.scaleGroups(transform, clip);

            if (self.res) {
              self.res = false;
              this.reset(false, 0);
            }
          }
        };

        Pointer.prototype.pinchTranslate = function (pinchDown, touches, transform, selectionMarker, clip, lastValidTouch) {
          if (this.zoomHor) {
            this.pinchTranslateDirection(true, pinchDown, touches, transform, selectionMarker, clip, lastValidTouch);
          }

          if (this.zoomVert) {
            this.pinchTranslateDirection(false, pinchDown, touches, transform, selectionMarker, clip, lastValidTouch);
          }
        };

        Pointer.prototype.pinchTranslateDirection = function (horiz, pinchDown, touches, transform, selectionMarker, clip, lastValidTouch, forcedScale) {
          var chart = this.chart,
              xy = horiz ? 'x' : 'y',
              XY = horiz ? 'X' : 'Y',
              sChartXY = 'chart' + XY,
              wh = horiz ? 'width' : 'height',
              plotLeftTop = chart['plot' + (horiz ? 'Left' : 'Top')],
              selectionWH,
              selectionXY,
              clipXY,
              scale = forcedScale || 1,
              inverted = chart.inverted,
              bounds = chart.bounds[horiz ? 'h' : 'v'],
              singleTouch = pinchDown.length === 1,
              touch0Start = pinchDown[0][sChartXY],
              touch0Now = touches[0][sChartXY],
              touch1Start = !singleTouch && pinchDown[1][sChartXY],
              touch1Now = !singleTouch && touches[1][sChartXY],
              outOfBounds,
              transformScale,
              scaleKey,
              setScale = function setScale() {
            if (typeof touch1Now === 'number' && Math.abs(touch0Start - touch1Start) > 20) {
              scale = forcedScale || Math.abs(touch0Now - touch1Now) / Math.abs(touch0Start - touch1Start);
            }

            clipXY = (plotLeftTop - touch0Now) / scale + touch0Start;
            selectionWH = chart['plot' + (horiz ? 'Width' : 'Height')] / scale;
          };

          setScale();
          selectionXY = clipXY;

          if (selectionXY < bounds.min) {
            selectionXY = bounds.min;
            outOfBounds = true;
          } else if (selectionXY + selectionWH > bounds.max) {
            selectionXY = bounds.max - selectionWH;
            outOfBounds = true;
          }

          if (outOfBounds) {
            touch0Now -= 0.8 * (touch0Now - lastValidTouch[xy][0]);

            if (typeof touch1Now === 'number') {
              touch1Now -= 0.8 * (touch1Now - lastValidTouch[xy][1]);
            }

            setScale();
          } else {
            lastValidTouch[xy] = [touch0Now, touch1Now];
          }

          if (!inverted) {
            clip[xy] = clipXY - plotLeftTop;
            clip[wh] = selectionWH;
          }

          scaleKey = inverted ? horiz ? 'scaleY' : 'scaleX' : 'scale' + XY;
          transformScale = inverted ? 1 / scale : scale;
          selectionMarker[wh] = selectionWH;
          selectionMarker[xy] = selectionXY;
          transform[scaleKey] = scale;
          transform['translate' + XY] = transformScale * plotLeftTop + (touch0Now - transformScale * touch0Start);
        };

        Pointer.prototype.reset = function (allowMove, delay) {
          var pointer = this,
              chart = pointer.chart,
              hoverSeries = chart.hoverSeries,
              hoverPoint = chart.hoverPoint,
              hoverPoints = chart.hoverPoints,
              tooltip = chart.tooltip,
              tooltipPoints = tooltip && tooltip.shared ? hoverPoints : hoverPoint;

          if (allowMove && tooltipPoints) {
            splat(tooltipPoints).forEach(function (point) {
              if (point.series.isCartesian && typeof point.plotX === 'undefined') {
                allowMove = false;
              }
            });
          }

          if (allowMove) {
            if (tooltip && tooltipPoints && splat(tooltipPoints).length) {
              tooltip.refresh(tooltipPoints);

              if (tooltip.shared && hoverPoints) {
                hoverPoints.forEach(function (point) {
                  point.setState(point.state, true);

                  if (point.series.isCartesian) {
                    if (point.series.xAxis.crosshair) {
                      point.series.xAxis.drawCrosshair(null, point);
                    }

                    if (point.series.yAxis.crosshair) {
                      point.series.yAxis.drawCrosshair(null, point);
                    }
                  }
                });
              } else if (hoverPoint) {
                hoverPoint.setState(hoverPoint.state, true);
                chart.axes.forEach(function (axis) {
                  if (axis.crosshair && hoverPoint.series[axis.coll] === axis) {
                    axis.drawCrosshair(null, hoverPoint);
                  }
                });
              }
            }
          } else {
            if (hoverPoint) {
              hoverPoint.onMouseOut();
            }

            if (hoverPoints) {
              hoverPoints.forEach(function (point) {
                point.setState();
              });
            }

            if (hoverSeries) {
              hoverSeries.onMouseOut();
            }

            if (tooltip) {
              tooltip.hide(delay);
            }

            if (pointer.unDocMouseMove) {
              pointer.unDocMouseMove = pointer.unDocMouseMove();
            }

            chart.axes.forEach(function (axis) {
              axis.hideCrosshair();
            });
            pointer.hoverX = chart.hoverPoints = chart.hoverPoint = null;
          }
        };

        Pointer.prototype.runPointActions = function (e, p) {
          var pointer = this,
              chart = pointer.chart,
              series = chart.series,
              tooltip = chart.tooltip && chart.tooltip.options.enabled ? chart.tooltip : void 0,
              shared = tooltip ? tooltip.shared : false,
              hoverPoint = p || chart.hoverPoint,
              hoverSeries = hoverPoint && hoverPoint.series || chart.hoverSeries,
              isDirectTouch = (!e || e.type !== 'touchmove') && (!!p || hoverSeries && hoverSeries.directTouch && pointer.isDirectTouch),
              hoverData = this.getHoverData(hoverPoint, hoverSeries, series, isDirectTouch, shared, e),
              useSharedTooltip,
              followPointer,
              anchor,
              points;
          hoverPoint = hoverData.hoverPoint;
          points = hoverData.hoverPoints;
          hoverSeries = hoverData.hoverSeries;
          followPointer = hoverSeries && hoverSeries.tooltipOptions.followPointer;
          useSharedTooltip = shared && hoverSeries && !hoverSeries.noSharedTooltip;

          if (hoverPoint && (hoverPoint !== chart.hoverPoint || tooltip && tooltip.isHidden)) {
            (chart.hoverPoints || []).forEach(function (p) {
              if (points.indexOf(p) === -1) {
                p.setState();
              }
            });

            if (chart.hoverSeries !== hoverSeries) {
              hoverSeries.onMouseOver();
            }

            pointer.applyInactiveState(points);
            (points || []).forEach(function (p) {
              p.setState('hover');
            });

            if (chart.hoverPoint) {
              chart.hoverPoint.firePointEvent('mouseOut');
            }

            if (!hoverPoint.series) {
              return;
            }

            hoverPoint.firePointEvent('mouseOver');
            chart.hoverPoints = points;
            chart.hoverPoint = hoverPoint;

            if (tooltip) {
              tooltip.refresh(useSharedTooltip ? points : hoverPoint, e);
            }
          } else if (followPointer && tooltip && !tooltip.isHidden) {
            anchor = tooltip.getAnchor([{}], e);
            tooltip.updatePosition({
              plotX: anchor[0],
              plotY: anchor[1]
            });
          }

          if (!pointer.unDocMouseMove) {
            pointer.unDocMouseMove = addEvent(chart.container.ownerDocument, 'mousemove', function (e) {
              var chart = charts[H.hoverChartIndex];

              if (chart) {
                chart.pointer.onDocumentMouseMove(e);
              }
            });
          }

          chart.axes.forEach(function drawAxisCrosshair(axis) {
            var snap = pick(axis.crosshair.snap, true),
                point = !snap ? void 0 : find(points, function (p) {
              return p.series[axis.coll] === axis;
            });

            if (point || !snap) {
              axis.drawCrosshair(e, point);
            } else {
              axis.hideCrosshair();
            }
          });
        };

        Pointer.prototype.scaleGroups = function (attribs, clip) {
          var chart = this.chart,
              seriesAttribs;
          chart.series.forEach(function (series) {
            seriesAttribs = attribs || series.getPlotBox();

            if (series.xAxis && series.xAxis.zoomEnabled && series.group) {
              series.group.attr(seriesAttribs);

              if (series.markerGroup) {
                series.markerGroup.attr(seriesAttribs);
                series.markerGroup.clip(clip ? chart.clipRect : null);
              }

              if (series.dataLabelsGroup) {
                series.dataLabelsGroup.attr(seriesAttribs);
              }
            }
          });
          chart.clipRect.attr(clip || chart.clipBox);
        };

        Pointer.prototype.setDOMEvents = function () {
          var pointer = this,
              container = pointer.chart.container,
              ownerDoc = container.ownerDocument;

          container.onmousedown = function (e) {
            pointer.onContainerMouseDown(e);
          };

          container.onmousemove = function (e) {
            pointer.onContainerMouseMove(e);
          };

          container.onclick = function (e) {
            pointer.onContainerClick(e);
          };

          this.unbindContainerMouseLeave = addEvent(container, 'mouseleave', pointer.onContainerMouseLeave);

          if (!H.unbindDocumentMouseUp) {
            H.unbindDocumentMouseUp = addEvent(ownerDoc, 'mouseup', pointer.onDocumentMouseUp);
          }

          if (H.hasTouch) {
            addEvent(container, 'touchstart', function (e) {
              pointer.onContainerTouchStart(e);
            });
            addEvent(container, 'touchmove', function (e) {
              pointer.onContainerTouchMove(e);
            });

            if (!H.unbindDocumentTouchEnd) {
              H.unbindDocumentTouchEnd = addEvent(ownerDoc, 'touchend', pointer.onDocumentTouchEnd);
            }
          }
        };

        Pointer.prototype.touch = function (e, start) {
          var chart = this.chart,
              hasMoved,
              pinchDown,
              isInside;

          if (chart.index !== H.hoverChartIndex) {
            this.onContainerMouseLeave({
              relatedTarget: true
            });
          }

          H.hoverChartIndex = chart.index;

          if (e.touches.length === 1) {
            e = this.normalize(e);
            isInside = chart.isInsidePlot(e.chartX - chart.plotLeft, e.chartY - chart.plotTop);

            if (isInside && !chart.openMenu) {
              if (start) {
                this.runPointActions(e);
              }

              if (e.type === 'touchmove') {
                pinchDown = this.pinchDown;
                hasMoved = pinchDown[0] ? Math.sqrt(Math.pow(pinchDown[0].chartX - e.chartX, 2) + Math.pow(pinchDown[0].chartY - e.chartY, 2)) >= 4 : false;
              }

              if (pick(hasMoved, true)) {
                this.pinch(e);
              }
            } else if (start) {
              this.reset();
            }
          } else if (e.touches.length === 2) {
            this.pinch(e);
          }
        };

        Pointer.prototype.zoomOption = function (e) {
          var chart = this.chart,
              options = chart.options.chart,
              zoomType = options.zoomType || '',
              inverted = chart.inverted,
              zoomX,
              zoomY;

          if (/touch/.test(e.type)) {
            zoomType = pick(options.pinchType, zoomType);
          }

          this.zoomX = zoomX = /x/.test(zoomType);
          this.zoomY = zoomY = /y/.test(zoomType);
          this.zoomHor = zoomX && !inverted || zoomY && inverted;
          this.zoomVert = zoomY && !inverted || zoomX && inverted;
          this.hasZoom = zoomX || zoomY;
        };

        return Pointer;
      }();

      H.Pointer = Pointer;
      return H.Pointer;
    });

    _registerModule(_modules, 'parts/MSPointer.js', [_modules['parts/Globals.js'], _modules['parts/Pointer.js'], _modules['parts/Utilities.js']], function (H, Pointer, U) {
      var __extends = this && this.__extends || function () {
        var _extendStatics = function extendStatics(d, b) {
          _extendStatics = Object.setPrototypeOf || {
            __proto__: []
          } instanceof Array && function (d, b) {
            d.__proto__ = b;
          } || function (d, b) {
            for (var p in b) {
              if (b.hasOwnProperty(p)) d[p] = b[p];
            }
          };

          return _extendStatics(d, b);
        };

        return function (d, b) {
          _extendStatics(d, b);

          function __() {
            this.constructor = d;
          }

          d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
        };
      }();

      var addEvent = U.addEvent,
          css = U.css,
          objectEach = U.objectEach,
          removeEvent = U.removeEvent;
      var charts = H.charts,
          doc = H.doc,
          noop = H.noop,
          win = H.win;
      var touches = {};
      var hasPointerEvent = !!win.PointerEvent;

      function getWebkitTouches() {
        var fake = [];

        fake.item = function (i) {
          return this[i];
        };

        objectEach(touches, function (touch) {
          fake.push({
            pageX: touch.pageX,
            pageY: touch.pageY,
            target: touch.target
          });
        });
        return fake;
      }

      function translateMSPointer(e, method, wktype, func) {
        var p;

        if ((e.pointerType === 'touch' || e.pointerType === e.MSPOINTER_TYPE_TOUCH) && charts[H.hoverChartIndex]) {
          func(e);
          p = charts[H.hoverChartIndex].pointer;
          p[method]({
            type: wktype,
            target: e.currentTarget,
            preventDefault: noop,
            touches: getWebkitTouches()
          });
        }
      }

      var MSPointer = function (_super) {
        __extends(MSPointer, _super);

        function MSPointer() {
          return _super !== null && _super.apply(this, arguments) || this;
        }

        MSPointer.prototype.batchMSEvents = function (fn) {
          fn(this.chart.container, hasPointerEvent ? 'pointerdown' : 'MSPointerDown', this.onContainerPointerDown);
          fn(this.chart.container, hasPointerEvent ? 'pointermove' : 'MSPointerMove', this.onContainerPointerMove);
          fn(doc, hasPointerEvent ? 'pointerup' : 'MSPointerUp', this.onDocumentPointerUp);
        };

        MSPointer.prototype.destroy = function () {
          this.batchMSEvents(removeEvent);

          _super.prototype.destroy.call(this);
        };

        MSPointer.prototype.init = function (chart, options) {
          _super.prototype.init.call(this, chart, options);

          if (this.hasZoom) {
            css(chart.container, {
              '-ms-touch-action': 'none',
              'touch-action': 'none'
            });
          }
        };

        MSPointer.prototype.onContainerPointerDown = function (e) {
          translateMSPointer(e, 'onContainerTouchStart', 'touchstart', function (e) {
            touches[e.pointerId] = {
              pageX: e.pageX,
              pageY: e.pageY,
              target: e.currentTarget
            };
          });
        };

        MSPointer.prototype.onContainerPointerMove = function (e) {
          translateMSPointer(e, 'onContainerTouchMove', 'touchmove', function (e) {
            touches[e.pointerId] = {
              pageX: e.pageX,
              pageY: e.pageY
            };

            if (!touches[e.pointerId].target) {
              touches[e.pointerId].target = e.currentTarget;
            }
          });
        };

        MSPointer.prototype.onDocumentPointerUp = function (e) {
          translateMSPointer(e, 'onDocumentTouchEnd', 'touchend', function (e) {
            delete touches[e.pointerId];
          });
        };

        MSPointer.prototype.setDOMEvents = function () {
          _super.prototype.setDOMEvents.call(this);

          if (this.hasZoom || this.followTouchMove) {
            this.batchMSEvents(addEvent);
          }
        };

        return MSPointer;
      }(Pointer);

      return MSPointer;
    });

    _registerModule(_modules, 'parts/Legend.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var addEvent = U.addEvent,
          css = U.css,
          defined = U.defined,
          discardElement = U.discardElement,
          find = U.find,
          fireEvent = U.fireEvent,
          format = U.format,
          isNumber = U.isNumber,
          merge = U.merge,
          pick = U.pick,
          relativeLength = U.relativeLength,
          setAnimation = U.setAnimation,
          stableSort = U.stableSort,
          syncTimeout = U.syncTimeout,
          wrap = U.wrap;
      var isFirefox = H.isFirefox,
          marginNames = H.marginNames,
          win = H.win;

      var Legend = function () {
        function Legend(chart, options) {
          this.allItems = [];
          this.box = void 0;
          this.contentGroup = void 0;
          this.display = false;
          this.group = void 0;
          this.initialItemY = 0;
          this.itemHeight = 0;
          this.itemMarginBottom = 0;
          this.itemMarginTop = 0;
          this.itemX = 0;
          this.itemY = 0;
          this.lastItemY = 0;
          this.lastLineHeight = 0;
          this.legendHeight = 0;
          this.legendWidth = 0;
          this.maxItemWidth = 0;
          this.maxLegendWidth = 0;
          this.offsetWidth = 0;
          this.options = {};
          this.padding = 0;
          this.pages = [];
          this.proximate = false;
          this.scrollGroup = void 0;
          this.symbolHeight = 0;
          this.symbolWidth = 0;
          this.titleHeight = 0;
          this.totalItemWidth = 0;
          this.widthOption = 0;
          this.chart = chart;
          this.init(chart, options);
        }

        Legend.prototype.init = function (chart, options) {
          this.chart = chart;
          this.setOptions(options);

          if (options.enabled) {
            this.render();
            addEvent(this.chart, 'endResize', function () {
              this.legend.positionCheckboxes();
            });

            if (this.proximate) {
              this.unchartrender = addEvent(this.chart, 'render', function () {
                this.legend.proximatePositions();
                this.legend.positionItems();
              });
            } else if (this.unchartrender) {
              this.unchartrender();
            }
          }
        };

        Legend.prototype.setOptions = function (options) {
          var padding = pick(options.padding, 8);
          this.options = options;

          if (!this.chart.styledMode) {
            this.itemStyle = options.itemStyle;
            this.itemHiddenStyle = merge(this.itemStyle, options.itemHiddenStyle);
          }

          this.itemMarginTop = options.itemMarginTop || 0;
          this.itemMarginBottom = options.itemMarginBottom || 0;
          this.padding = padding;
          this.initialItemY = padding - 5;
          this.symbolWidth = pick(options.symbolWidth, 16);
          this.pages = [];
          this.proximate = options.layout === 'proximate' && !this.chart.inverted;
          this.baseline = void 0;
        };

        Legend.prototype.update = function (options, redraw) {
          var chart = this.chart;
          this.setOptions(merge(true, this.options, options));
          this.destroy();
          chart.isDirtyLegend = chart.isDirtyBox = true;

          if (pick(redraw, true)) {
            chart.redraw();
          }

          fireEvent(this, 'afterUpdate');
        };

        Legend.prototype.colorizeItem = function (item, visible) {
          item.legendGroup[visible ? 'removeClass' : 'addClass']('highcharts-legend-item-hidden');

          if (!this.chart.styledMode) {
            var legend = this,
                options = legend.options,
                legendItem = item.legendItem,
                legendLine = item.legendLine,
                legendSymbol = item.legendSymbol,
                hiddenColor = legend.itemHiddenStyle.color,
                textColor = visible ? options.itemStyle.color : hiddenColor,
                symbolColor = visible ? item.color || hiddenColor : hiddenColor,
                markerOptions = item.options && item.options.marker,
                symbolAttr = {
              fill: symbolColor
            };

            if (legendItem) {
              legendItem.css({
                fill: textColor,
                color: textColor
              });
            }

            if (legendLine) {
              legendLine.attr({
                stroke: symbolColor
              });
            }

            if (legendSymbol) {
              if (markerOptions && legendSymbol.isMarker) {
                symbolAttr = item.pointAttribs();

                if (!visible) {
                  symbolAttr.stroke = symbolAttr.fill = hiddenColor;
                }
              }

              legendSymbol.attr(symbolAttr);
            }
          }

          fireEvent(this, 'afterColorizeItem', {
            item: item,
            visible: visible
          });
        };

        Legend.prototype.positionItems = function () {
          this.allItems.forEach(this.positionItem, this);

          if (!this.chart.isResizing) {
            this.positionCheckboxes();
          }
        };

        Legend.prototype.positionItem = function (item) {
          var legend = this,
              options = legend.options,
              symbolPadding = options.symbolPadding,
              ltr = !options.rtl,
              legendItemPos = item._legendItemPos,
              itemX = legendItemPos[0],
              itemY = legendItemPos[1],
              checkbox = item.checkbox,
              legendGroup = item.legendGroup;

          if (legendGroup && legendGroup.element) {
            legendGroup[defined(legendGroup.translateY) ? 'animate' : 'attr']({
              translateX: ltr ? itemX : legend.legendWidth - itemX - 2 * symbolPadding - 4,
              translateY: itemY
            });
          }

          if (checkbox) {
            checkbox.x = itemX;
            checkbox.y = itemY;
          }
        };

        Legend.prototype.destroyItem = function (item) {
          var checkbox = item.checkbox;
          ['legendItem', 'legendLine', 'legendSymbol', 'legendGroup'].forEach(function (key) {
            if (item[key]) {
              item[key] = item[key].destroy();
            }
          });

          if (checkbox) {
            discardElement(item.checkbox);
          }
        };

        Legend.prototype.destroy = function () {
          function destroyItems(key) {
            if (this[key]) {
              this[key] = this[key].destroy();
            }
          }

          this.getAllItems().forEach(function (item) {
            ['legendItem', 'legendGroup'].forEach(destroyItems, item);
          });
          ['clipRect', 'up', 'down', 'pager', 'nav', 'box', 'title', 'group'].forEach(destroyItems, this);
          this.display = null;
        };

        Legend.prototype.positionCheckboxes = function () {
          var alignAttr = this.group && this.group.alignAttr,
              translateY,
              clipHeight = this.clipHeight || this.legendHeight,
              titleHeight = this.titleHeight;

          if (alignAttr) {
            translateY = alignAttr.translateY;
            this.allItems.forEach(function (item) {
              var checkbox = item.checkbox,
                  top;

              if (checkbox) {
                top = translateY + titleHeight + checkbox.y + (this.scrollOffset || 0) + 3;
                css(checkbox, {
                  left: alignAttr.translateX + item.checkboxOffset + checkbox.x - 20 + 'px',
                  top: top + 'px',
                  display: this.proximate || top > translateY - 6 && top < translateY + clipHeight - 6 ? '' : 'none'
                });
              }
            }, this);
          }
        };

        Legend.prototype.renderTitle = function () {
          var options = this.options,
              padding = this.padding,
              titleOptions = options.title,
              titleHeight = 0,
              bBox;

          if (titleOptions.text) {
            if (!this.title) {
              this.title = this.chart.renderer.label(titleOptions.text, padding - 3, padding - 4, null, null, null, options.useHTML, null, 'legend-title').attr({
                zIndex: 1
              });

              if (!this.chart.styledMode) {
                this.title.css(titleOptions.style);
              }

              this.title.add(this.group);
            }

            if (!titleOptions.width) {
              this.title.css({
                width: this.maxLegendWidth + 'px'
              });
            }

            bBox = this.title.getBBox();
            titleHeight = bBox.height;
            this.offsetWidth = bBox.width;
            this.contentGroup.attr({
              translateY: titleHeight
            });
          }

          this.titleHeight = titleHeight;
        };

        Legend.prototype.setText = function (item) {
          var options = this.options;
          item.legendItem.attr({
            text: options.labelFormat ? format(options.labelFormat, item, this.chart) : options.labelFormatter.call(item)
          });
        };

        Legend.prototype.renderItem = function (item) {
          var legend = this,
              chart = legend.chart,
              renderer = chart.renderer,
              options = legend.options,
              horizontal = options.layout === 'horizontal',
              symbolWidth = legend.symbolWidth,
              symbolPadding = options.symbolPadding,
              itemStyle = legend.itemStyle,
              itemHiddenStyle = legend.itemHiddenStyle,
              itemDistance = horizontal ? pick(options.itemDistance, 20) : 0,
              ltr = !options.rtl,
              bBox,
              li = item.legendItem,
              isSeries = !item.series,
              series = !isSeries && item.series.drawLegendSymbol ? item.series : item,
              seriesOptions = series.options,
              showCheckbox = legend.createCheckboxForItem && seriesOptions && seriesOptions.showCheckbox,
              itemExtraWidth = symbolWidth + symbolPadding + itemDistance + (showCheckbox ? 20 : 0),
              useHTML = options.useHTML,
              itemClassName = item.options.className;

          if (!li) {
            item.legendGroup = renderer.g('legend-item').addClass('highcharts-' + series.type + '-series ' + 'highcharts-color-' + item.colorIndex + (itemClassName ? ' ' + itemClassName : '') + (isSeries ? ' highcharts-series-' + item.index : '')).attr({
              zIndex: 1
            }).add(legend.scrollGroup);
            item.legendItem = li = renderer.text('', ltr ? symbolWidth + symbolPadding : -symbolPadding, legend.baseline || 0, useHTML);

            if (!chart.styledMode) {
              li.css(merge(item.visible ? itemStyle : itemHiddenStyle));
            }

            li.attr({
              align: ltr ? 'left' : 'right',
              zIndex: 2
            }).add(item.legendGroup);

            if (!legend.baseline) {
              legend.fontMetrics = renderer.fontMetrics(chart.styledMode ? 12 : itemStyle.fontSize, li);
              legend.baseline = legend.fontMetrics.f + 3 + legend.itemMarginTop;
              li.attr('y', legend.baseline);
            }

            legend.symbolHeight = options.symbolHeight || legend.fontMetrics.f;
            series.drawLegendSymbol(legend, item);

            if (legend.setItemEvents) {
              legend.setItemEvents(item, li, useHTML);
            }
          }

          if (showCheckbox && !item.checkbox && legend.createCheckboxForItem) {
            legend.createCheckboxForItem(item);
          }

          legend.colorizeItem(item, item.visible);

          if (chart.styledMode || !itemStyle.width) {
            li.css({
              width: (options.itemWidth || legend.widthOption || chart.spacingBox.width) - itemExtraWidth
            });
          }

          legend.setText(item);
          bBox = li.getBBox();
          item.itemWidth = item.checkboxOffset = options.itemWidth || item.legendItemWidth || bBox.width + itemExtraWidth;
          legend.maxItemWidth = Math.max(legend.maxItemWidth, item.itemWidth);
          legend.totalItemWidth += item.itemWidth;
          legend.itemHeight = item.itemHeight = Math.round(item.legendItemHeight || bBox.height || legend.symbolHeight);
        };

        Legend.prototype.layoutItem = function (item) {
          var options = this.options,
              padding = this.padding,
              horizontal = options.layout === 'horizontal',
              itemHeight = item.itemHeight,
              itemMarginBottom = this.itemMarginBottom,
              itemMarginTop = this.itemMarginTop,
              itemDistance = horizontal ? pick(options.itemDistance, 20) : 0,
              maxLegendWidth = this.maxLegendWidth,
              itemWidth = options.alignColumns && this.totalItemWidth > maxLegendWidth ? this.maxItemWidth : item.itemWidth;

          if (horizontal && this.itemX - padding + itemWidth > maxLegendWidth) {
            this.itemX = padding;

            if (this.lastLineHeight) {
              this.itemY += itemMarginTop + this.lastLineHeight + itemMarginBottom;
            }

            this.lastLineHeight = 0;
          }

          this.lastItemY = itemMarginTop + this.itemY + itemMarginBottom;
          this.lastLineHeight = Math.max(itemHeight, this.lastLineHeight);
          item._legendItemPos = [this.itemX, this.itemY];

          if (horizontal) {
            this.itemX += itemWidth;
          } else {
            this.itemY += itemMarginTop + itemHeight + itemMarginBottom;
            this.lastLineHeight = itemHeight;
          }

          this.offsetWidth = this.widthOption || Math.max((horizontal ? this.itemX - padding - (item.checkbox ? 0 : itemDistance) : itemWidth) + padding, this.offsetWidth);
        };

        Legend.prototype.getAllItems = function () {
          var allItems = [];
          this.chart.series.forEach(function (series) {
            var seriesOptions = series && series.options;

            if (series && pick(seriesOptions.showInLegend, !defined(seriesOptions.linkedTo) ? void 0 : false, true)) {
              allItems = allItems.concat(series.legendItems || (seriesOptions.legendType === 'point' ? series.data : series));
            }
          });
          fireEvent(this, 'afterGetAllItems', {
            allItems: allItems
          });
          return allItems;
        };

        Legend.prototype.getAlignment = function () {
          var options = this.options;

          if (this.proximate) {
            return options.align.charAt(0) + 'tv';
          }

          return options.floating ? '' : options.align.charAt(0) + options.verticalAlign.charAt(0) + options.layout.charAt(0);
        };

        Legend.prototype.adjustMargins = function (margin, spacing) {
          var chart = this.chart,
              options = this.options,
              alignment = this.getAlignment();

          if (alignment) {
            [/(lth|ct|rth)/, /(rtv|rm|rbv)/, /(rbh|cb|lbh)/, /(lbv|lm|ltv)/].forEach(function (alignments, side) {
              if (alignments.test(alignment) && !defined(margin[side])) {
                chart[marginNames[side]] = Math.max(chart[marginNames[side]], chart.legend[(side + 1) % 2 ? 'legendHeight' : 'legendWidth'] + [1, -1, -1, 1][side] * options[side % 2 ? 'x' : 'y'] + pick(options.margin, 12) + spacing[side] + (chart.titleOffset[side] || 0));
              }
            });
          }
        };

        Legend.prototype.proximatePositions = function () {
          var chart = this.chart,
              boxes = [],
              alignLeft = this.options.align === 'left';
          this.allItems.forEach(function (item) {
            var lastPoint,
                height,
                useFirstPoint = alignLeft,
                target,
                top;

            if (item.yAxis && item.points) {
              if (item.xAxis.options.reversed) {
                useFirstPoint = !useFirstPoint;
              }

              lastPoint = find(useFirstPoint ? item.points : item.points.slice(0).reverse(), function (item) {
                return isNumber(item.plotY);
              });
              height = this.itemMarginTop + item.legendItem.getBBox().height + this.itemMarginBottom;
              top = item.yAxis.top - chart.plotTop;

              if (item.visible) {
                target = lastPoint ? lastPoint.plotY : item.yAxis.height;
                target += top - 0.3 * height;
              } else {
                target = top + item.yAxis.height;
              }

              boxes.push({
                target: target,
                size: height,
                item: item
              });
            }
          }, this);
          H.distribute(boxes, chart.plotHeight);
          boxes.forEach(function (box) {
            box.item._legendItemPos[1] = chart.plotTop - chart.spacing[0] + box.pos;
          });
        };

        Legend.prototype.render = function () {
          var legend = this,
              chart = legend.chart,
              renderer = chart.renderer,
              legendGroup = legend.group,
              allItems,
              display,
              legendWidth,
              legendHeight,
              box = legend.box,
              options = legend.options,
              padding = legend.padding,
              allowedWidth;
          legend.itemX = padding;
          legend.itemY = legend.initialItemY;
          legend.offsetWidth = 0;
          legend.lastItemY = 0;
          legend.widthOption = relativeLength(options.width, chart.spacingBox.width - padding);
          allowedWidth = chart.spacingBox.width - 2 * padding - options.x;

          if (['rm', 'lm'].indexOf(legend.getAlignment().substring(0, 2)) > -1) {
            allowedWidth /= 2;
          }

          legend.maxLegendWidth = legend.widthOption || allowedWidth;

          if (!legendGroup) {
            legend.group = legendGroup = renderer.g('legend').attr({
              zIndex: 7
            }).add();
            legend.contentGroup = renderer.g().attr({
              zIndex: 1
            }).add(legendGroup);
            legend.scrollGroup = renderer.g().add(legend.contentGroup);
          }

          legend.renderTitle();
          allItems = legend.getAllItems();
          stableSort(allItems, function (a, b) {
            return (a.options && a.options.legendIndex || 0) - (b.options && b.options.legendIndex || 0);
          });

          if (options.reversed) {
            allItems.reverse();
          }

          legend.allItems = allItems;
          legend.display = display = !!allItems.length;
          legend.lastLineHeight = 0;
          legend.maxItemWidth = 0;
          legend.totalItemWidth = 0;
          legend.itemHeight = 0;
          allItems.forEach(legend.renderItem, legend);
          allItems.forEach(legend.layoutItem, legend);
          legendWidth = (legend.widthOption || legend.offsetWidth) + padding;
          legendHeight = legend.lastItemY + legend.lastLineHeight + legend.titleHeight;
          legendHeight = legend.handleOverflow(legendHeight);
          legendHeight += padding;

          if (!box) {
            legend.box = box = renderer.rect().addClass('highcharts-legend-box').attr({
              r: options.borderRadius
            }).add(legendGroup);
            box.isNew = true;
          }

          if (!chart.styledMode) {
            box.attr({
              stroke: options.borderColor,
              'stroke-width': options.borderWidth || 0,
              fill: options.backgroundColor || 'none'
            }).shadow(options.shadow);
          }

          if (legendWidth > 0 && legendHeight > 0) {
            box[box.isNew ? 'attr' : 'animate'](box.crisp.call({}, {
              x: 0,
              y: 0,
              width: legendWidth,
              height: legendHeight
            }, box.strokeWidth()));
            box.isNew = false;
          }

          box[display ? 'show' : 'hide']();

          if (chart.styledMode && legendGroup.getStyle('display') === 'none') {
            legendWidth = legendHeight = 0;
          }

          legend.legendWidth = legendWidth;
          legend.legendHeight = legendHeight;

          if (display) {
            var alignTo = chart.spacingBox;
            var y = alignTo.y;

            if (/(lth|ct|rth)/.test(legend.getAlignment()) && chart.titleOffset[0] > 0) {
              y += chart.titleOffset[0];
            } else if (/(lbh|cb|rbh)/.test(legend.getAlignment()) && chart.titleOffset[2] > 0) {
              y -= chart.titleOffset[2];
            }

            if (y !== alignTo.y) {
              alignTo = merge(alignTo, {
                y: y
              });
            }

            legendGroup.align(merge(options, {
              width: legendWidth,
              height: legendHeight,
              verticalAlign: this.proximate ? 'top' : options.verticalAlign
            }), true, alignTo);
          }

          if (!this.proximate) {
            this.positionItems();
          }

          fireEvent(this, 'afterRender');
        };

        Legend.prototype.handleOverflow = function (legendHeight) {
          var legend = this,
              chart = this.chart,
              renderer = chart.renderer,
              options = this.options,
              optionsY = options.y,
              alignTop = options.verticalAlign === 'top',
              padding = this.padding,
              spaceHeight = chart.spacingBox.height + (alignTop ? -optionsY : optionsY) - padding,
              maxHeight = options.maxHeight,
              clipHeight,
              clipRect = this.clipRect,
              navOptions = options.navigation,
              animation = pick(navOptions.animation, true),
              arrowSize = navOptions.arrowSize || 12,
              nav = this.nav,
              pages = this.pages,
              lastY,
              allItems = this.allItems,
              clipToHeight = function clipToHeight(height) {
            if (typeof height === 'number') {
              clipRect.attr({
                height: height
              });
            } else if (clipRect) {
              legend.clipRect = clipRect.destroy();
              legend.contentGroup.clip();
            }

            if (legend.contentGroup.div) {
              legend.contentGroup.div.style.clip = height ? 'rect(' + padding + 'px,9999px,' + (padding + height) + 'px,0)' : 'auto';
            }
          },
              addTracker = function addTracker(key) {
            legend[key] = renderer.circle(0, 0, arrowSize * 1.3).translate(arrowSize / 2, arrowSize / 2).add(nav);

            if (!chart.styledMode) {
              legend[key].attr('fill', 'rgba(0,0,0,0.0001)');
            }

            return legend[key];
          };

          if (options.layout === 'horizontal' && options.verticalAlign !== 'middle' && !options.floating) {
            spaceHeight /= 2;
          }

          if (maxHeight) {
            spaceHeight = Math.min(spaceHeight, maxHeight);
          }

          pages.length = 0;

          if (legendHeight > spaceHeight && navOptions.enabled !== false) {
            this.clipHeight = clipHeight = Math.max(spaceHeight - 20 - this.titleHeight - padding, 0);
            this.currentPage = pick(this.currentPage, 1);
            this.fullHeight = legendHeight;
            allItems.forEach(function (item, i) {
              var y = item._legendItemPos[1],
                  h = Math.round(item.legendItem.getBBox().height),
                  len = pages.length;

              if (!len || y - pages[len - 1] > clipHeight && (lastY || y) !== pages[len - 1]) {
                pages.push(lastY || y);
                len++;
              }

              item.pageIx = len - 1;

              if (lastY) {
                allItems[i - 1].pageIx = len - 1;
              }

              if (i === allItems.length - 1 && y + h - pages[len - 1] > clipHeight && y !== lastY) {
                  pages.push(y);
                  item.pageIx = len;
                }

              if (y !== lastY) {
                lastY = y;
              }
            });

            if (!clipRect) {
              clipRect = legend.clipRect = renderer.clipRect(0, padding, 9999, 0);
              legend.contentGroup.clip(clipRect);
            }

            clipToHeight(clipHeight);

            if (!nav) {
              this.nav = nav = renderer.g().attr({
                zIndex: 1
              }).add(this.group);
              this.up = renderer.symbol('triangle', 0, 0, arrowSize, arrowSize).add(nav);
              addTracker('upTracker').on('click', function () {
                legend.scroll(-1, animation);
              });
              this.pager = renderer.text('', 15, 10).addClass('highcharts-legend-navigation');

              if (!chart.styledMode) {
                this.pager.css(navOptions.style);
              }

              this.pager.add(nav);
              this.down = renderer.symbol('triangle-down', 0, 0, arrowSize, arrowSize).add(nav);
              addTracker('downTracker').on('click', function () {
                legend.scroll(1, animation);
              });
            }

            legend.scroll(0);
            legendHeight = spaceHeight;
          } else if (nav) {
            clipToHeight();
            this.nav = nav.destroy();
            this.scrollGroup.attr({
              translateY: 1
            });
            this.clipHeight = 0;
          }

          return legendHeight;
        };

        Legend.prototype.scroll = function (scrollBy, animation) {
          var _this = this;

          var chart = this.chart,
              pages = this.pages,
              pageCount = pages.length,
              currentPage = this.currentPage + scrollBy,
              clipHeight = this.clipHeight,
              navOptions = this.options.navigation,
              pager = this.pager,
              padding = this.padding;

          if (currentPage > pageCount) {
            currentPage = pageCount;
          }

          if (currentPage > 0) {
            if (typeof animation !== 'undefined') {
              setAnimation(animation, chart);
            }

            this.nav.attr({
              translateX: padding,
              translateY: clipHeight + this.padding + 7 + this.titleHeight,
              visibility: 'visible'
            });
            [this.up, this.upTracker].forEach(function (elem) {
              elem.attr({
                'class': currentPage === 1 ? 'highcharts-legend-nav-inactive' : 'highcharts-legend-nav-active'
              });
            });
            pager.attr({
              text: currentPage + '/' + pageCount
            });
            [this.down, this.downTracker].forEach(function (elem) {
              elem.attr({
                x: 18 + this.pager.getBBox().width,
                'class': currentPage === pageCount ? 'highcharts-legend-nav-inactive' : 'highcharts-legend-nav-active'
              });
            }, this);

            if (!chart.styledMode) {
              this.up.attr({
                fill: currentPage === 1 ? navOptions.inactiveColor : navOptions.activeColor
              });
              this.upTracker.css({
                cursor: currentPage === 1 ? 'default' : 'pointer'
              });
              this.down.attr({
                fill: currentPage === pageCount ? navOptions.inactiveColor : navOptions.activeColor
              });
              this.downTracker.css({
                cursor: currentPage === pageCount ? 'default' : 'pointer'
              });
            }

            this.scrollOffset = -pages[currentPage - 1] + this.initialItemY;
            this.scrollGroup.animate({
              translateY: this.scrollOffset
            });
            this.currentPage = currentPage;
            this.positionCheckboxes();
            var animOptions = H.animObject(pick(animation, chart.renderer.globalAnimation, true));
            syncTimeout(function () {
              fireEvent(_this, 'afterScroll', {
                currentPage: currentPage
              });
            }, animOptions.duration || 0);
          }
        };

        return Legend;
      }();

      if (/Trident\/7\.0/.test(win.navigator && win.navigator.userAgent) || isFirefox) {
        wrap(Legend.prototype, 'positionItem', function (proceed, item) {
          var legend = this,
              runPositionItem = function runPositionItem() {
            if (item._legendItemPos) {
              proceed.call(legend, item);
            }
          };

          runPositionItem();

          if (!legend.bubbleLegend) {
            setTimeout(runPositionItem);
          }
        });
      }

      H.Legend = Legend;
      return H.Legend;
    });

    _registerModule(_modules, 'parts/Chart.js', [_modules['parts/Globals.js'], _modules['parts/Legend.js'], _modules['parts/MSPointer.js'], _modules['parts/Pointer.js'], _modules['parts/Time.js'], _modules['parts/Utilities.js']], function (H, Legend, MSPointer, Pointer, Time, U) {
      var addEvent = U.addEvent,
          animate = U.animate,
          animObject = U.animObject,
          attr = U.attr,
          createElement = U.createElement,
          css = U.css,
          defined = U.defined,
          discardElement = U.discardElement,
          erase = U.erase,
          error = U.error,
          extend = U.extend,
          find = U.find,
          fireEvent = U.fireEvent,
          getStyle = U.getStyle,
          isArray = U.isArray,
          isFunction = U.isFunction,
          isNumber = U.isNumber,
          isObject = U.isObject,
          isString = U.isString,
          merge = U.merge,
          numberFormat = U.numberFormat,
          objectEach = U.objectEach,
          pick = U.pick,
          pInt = U.pInt,
          relativeLength = U.relativeLength,
          removeEvent = U.removeEvent,
          setAnimation = U.setAnimation,
          splat = U.splat,
          syncTimeout = U.syncTimeout,
          uniqueKey = U.uniqueKey;
      var doc = H.doc,
          Axis = H.Axis,
          defaultOptions = H.defaultOptions,
          charts = H.charts,
          marginNames = H.marginNames,
          seriesTypes = H.seriesTypes,
          win = H.win;

      var Chart = H.Chart = function () {
        this.getArgs.apply(this, arguments);
      };

      H.chart = function (a, b, c) {
        return new Chart(a, b, c);
      };

      extend(Chart.prototype, {
        callbacks: [],
        getArgs: function getArgs() {
          var args = [].slice.call(arguments);

          if (isString(args[0]) || args[0].nodeName) {
            this.renderTo = args.shift();
          }

          this.init(args[0], args[1]);
        },
        init: function init(userOptions, callback) {
          var options,
              seriesOptions = userOptions.series,
              userPlotOptions = userOptions.plotOptions || {};
          fireEvent(this, 'init', {
            args: arguments
          }, function () {
            userOptions.series = null;
            options = merge(defaultOptions, userOptions);
            objectEach(options.plotOptions, function (typeOptions, type) {
              if (isObject(typeOptions)) {
                typeOptions.tooltip = userPlotOptions[type] && merge(userPlotOptions[type].tooltip) || void 0;
              }
            });
            options.tooltip.userOptions = userOptions.chart && userOptions.chart.forExport && userOptions.tooltip.userOptions || userOptions.tooltip;
            options.series = userOptions.series = seriesOptions;
            this.userOptions = userOptions;
            var optionsChart = options.chart;
            var chartEvents = optionsChart.events;
            this.margin = [];
            this.spacing = [];
            this.bounds = {
              h: {},
              v: {}
            };
            this.labelCollectors = [];
            this.callback = callback;
            this.isResizing = 0;
            this.options = options;
            this.axes = [];
            this.series = [];
            this.time = userOptions.time && Object.keys(userOptions.time).length ? new Time(userOptions.time) : H.time;
            this.numberFormatter = optionsChart.numberFormatter || numberFormat;
            this.styledMode = optionsChart.styledMode;
            this.hasCartesianSeries = optionsChart.showAxes;
            var chart = this;
            chart.index = charts.length;
            charts.push(chart);
            H.chartCount++;

            if (chartEvents) {
              objectEach(chartEvents, function (event, eventType) {
                if (isFunction(event)) {
                  addEvent(chart, eventType, event);
                }
              });
            }

            chart.xAxis = [];
            chart.yAxis = [];
            chart.pointCount = chart.colorCounter = chart.symbolCounter = 0;
            fireEvent(chart, 'afterInit');
            chart.firstRender();
          });
        },
        initSeries: function initSeries(options) {
          var chart = this,
              optionsChart = chart.options.chart,
              type = options.type || optionsChart.type || optionsChart.defaultSeriesType,
              series,
              Constr = seriesTypes[type];

          if (!Constr) {
            error(17, true, chart, {
              missingModuleFor: type
            });
          }

          series = new Constr();
          series.init(this, options);
          return series;
        },
        setSeriesData: function setSeriesData() {
          this.getSeriesOrderByLinks().forEach(function (series) {
            if (!series.points && !series.data && series.enabledDataSorting) {
              series.setData(series.options.data, false);
            }
          });
        },
        getSeriesOrderByLinks: function getSeriesOrderByLinks() {
          return this.series.concat().sort(function (a, b) {
            if (a.linkedSeries.length || b.linkedSeries.length) {
              return b.linkedSeries.length - a.linkedSeries.length;
            }

            return 0;
          });
        },
        orderSeries: function orderSeries(fromIndex) {
          var series = this.series,
              i = fromIndex || 0;

          for (; i < series.length; i++) {
            if (series[i]) {
              series[i].index = i;
              series[i].name = series[i].getName();
            }
          }
        },
        isInsidePlot: function isInsidePlot(plotX, plotY, inverted) {
          var x = inverted ? plotY : plotX,
              y = inverted ? plotX : plotY,
              e = {
            x: x,
            y: y,
            isInsidePlot: x >= 0 && x <= this.plotWidth && y >= 0 && y <= this.plotHeight
          };
          fireEvent(this, 'afterIsInsidePlot', e);
          return e.isInsidePlot;
        },
        redraw: function redraw(animation) {
          fireEvent(this, 'beforeRedraw');
          var chart = this,
              axes = chart.axes,
              series = chart.series,
              pointer = chart.pointer,
              legend = chart.legend,
              legendUserOptions = chart.userOptions.legend,
              redrawLegend = chart.isDirtyLegend,
              hasStackedSeries,
              hasDirtyStacks,
              hasCartesianSeries = chart.hasCartesianSeries,
              isDirtyBox = chart.isDirtyBox,
              i,
              serie,
              renderer = chart.renderer,
              isHiddenChart = renderer.isHidden(),
              afterRedraw = [];

          if (chart.setResponsive) {
            chart.setResponsive(false);
          }

          setAnimation(chart.hasRendered ? animation : false, chart);

          if (isHiddenChart) {
            chart.temporaryDisplay();
          }

          chart.layOutTitles();
          i = series.length;

          while (i--) {
            serie = series[i];

            if (serie.options.stacking) {
              hasStackedSeries = true;

              if (serie.isDirty) {
                hasDirtyStacks = true;
                break;
              }
            }
          }

          if (hasDirtyStacks) {
            i = series.length;

            while (i--) {
              serie = series[i];

              if (serie.options.stacking) {
                serie.isDirty = true;
              }
            }
          }

          series.forEach(function (serie) {
            if (serie.isDirty) {
              if (serie.options.legendType === 'point') {
                if (serie.updateTotals) {
                  serie.updateTotals();
                }

                redrawLegend = true;
              } else if (legendUserOptions && (legendUserOptions.labelFormatter || legendUserOptions.labelFormat)) {
                redrawLegend = true;
              }
            }

            if (serie.isDirtyData) {
              fireEvent(serie, 'updatedData');
            }
          });

          if (redrawLegend && legend && legend.options.enabled) {
            legend.render();
            chart.isDirtyLegend = false;
          }

          if (hasStackedSeries) {
            chart.getStacks();
          }

          if (hasCartesianSeries) {
            axes.forEach(function (axis) {
              axis.updateNames();
              axis.setScale();
            });
          }

          chart.getMargins();

          if (hasCartesianSeries) {
            axes.forEach(function (axis) {
              if (axis.isDirty) {
                isDirtyBox = true;
              }
            });
            axes.forEach(function (axis) {
              var key = axis.min + ',' + axis.max;

              if (axis.extKey !== key) {
                axis.extKey = key;
                afterRedraw.push(function () {
                  fireEvent(axis, 'afterSetExtremes', extend(axis.eventArgs, axis.getExtremes()));
                  delete axis.eventArgs;
                });
              }

              if (isDirtyBox || hasStackedSeries) {
                axis.redraw();
              }
            });
          }

          if (isDirtyBox) {
            chart.drawChartBox();
          }

          fireEvent(chart, 'predraw');
          series.forEach(function (serie) {
            if ((isDirtyBox || serie.isDirty) && serie.visible) {
              serie.redraw();
            }

            serie.isDirtyData = false;
          });

          if (pointer) {
            pointer.reset(true);
          }

          renderer.draw();
          fireEvent(chart, 'redraw');
          fireEvent(chart, 'render');

          if (isHiddenChart) {
            chart.temporaryDisplay(true);
          }

          afterRedraw.forEach(function (callback) {
            callback.call();
          });
        },
        get: function get(id) {
          var ret,
              series = this.series,
              i;

          function itemById(item) {
            return item.id === id || item.options && item.options.id === id;
          }

          ret = find(this.axes, itemById) || find(this.series, itemById);

          for (i = 0; !ret && i < series.length; i++) {
            ret = find(series[i].points || [], itemById);
          }

          return ret;
        },
        getAxes: function getAxes() {
          var chart = this,
              options = this.options,
              xAxisOptions = options.xAxis = splat(options.xAxis || {}),
              yAxisOptions = options.yAxis = splat(options.yAxis || {}),
              optionsArray;
          fireEvent(this, 'getAxes');
          xAxisOptions.forEach(function (axis, i) {
            axis.index = i;
            axis.isX = true;
          });
          yAxisOptions.forEach(function (axis, i) {
            axis.index = i;
          });
          optionsArray = xAxisOptions.concat(yAxisOptions);
          optionsArray.forEach(function (axisOptions) {
            new Axis(chart, axisOptions);
          });
          fireEvent(this, 'afterGetAxes');
        },
        getSelectedPoints: function getSelectedPoints() {
          var points = [];
          this.series.forEach(function (serie) {
            points = points.concat(serie.getPointsCollection().filter(function (point) {
              return pick(point.selectedStaging, point.selected);
            }));
          });
          return points;
        },
        getSelectedSeries: function getSelectedSeries() {
          return this.series.filter(function (serie) {
            return serie.selected;
          });
        },
        setTitle: function setTitle(titleOptions, subtitleOptions, redraw) {
          this.applyDescription('title', titleOptions);
          this.applyDescription('subtitle', subtitleOptions);
          this.applyDescription('caption', void 0);
          this.layOutTitles(redraw);
        },
        applyDescription: function applyDescription(name, explicitOptions) {
          var chart = this;
          var style = name === 'title' ? {
            color: '#333333',
            fontSize: this.options.isStock ? '16px' : '18px'
          } : {
            color: '#666666'
          };
          var options = this.options[name] = merge(!this.styledMode && {
            style: style
          }, this.options[name], explicitOptions);
          var elem = this[name];

          if (elem && explicitOptions) {
            this[name] = elem = elem.destroy();
          }

          if (options && !elem) {
            elem = this.renderer.text(options.text, 0, 0, options.useHTML).attr({
              align: options.align,
              'class': 'highcharts-' + name,
              zIndex: options.zIndex || 4
            }).add();

            elem.update = function (updateOptions) {
              var fn = {
                title: 'setTitle',
                subtitle: 'setSubtitle',
                caption: 'setCaption'
              }[name];
              chart[fn](updateOptions);
            };

            if (!this.styledMode) {
              elem.css(options.style);
            }

            this[name] = elem;
          }
        },
        layOutTitles: function layOutTitles(redraw) {
          var titleOffset = [0, 0, 0],
              requiresDirtyBox,
              renderer = this.renderer,
              spacingBox = this.spacingBox;
          ['title', 'subtitle', 'caption'].forEach(function (key) {
            var title = this[key],
                titleOptions = this.options[key],
                verticalAlign = titleOptions.verticalAlign || 'top',
                offset = key === 'title' ? -3 : verticalAlign === 'top' ? titleOffset[0] + 2 : 0,
                titleSize,
                height;

            if (title) {
              if (!this.styledMode) {
                titleSize = titleOptions.style.fontSize;
              }

              titleSize = renderer.fontMetrics(titleSize, title).b;
              title.css({
                width: (titleOptions.width || spacingBox.width + (titleOptions.widthAdjust || 0)) + 'px'
              });
              height = Math.round(title.getBBox(titleOptions.useHTML).height);
              title.align(extend({
                y: verticalAlign === 'bottom' ? titleSize : offset + titleSize,
                height: height
              }, titleOptions), false, 'spacingBox');

              if (!titleOptions.floating) {
                if (verticalAlign === 'top') {
                  titleOffset[0] = Math.ceil(titleOffset[0] + height);
                } else if (verticalAlign === 'bottom') {
                  titleOffset[2] = Math.ceil(titleOffset[2] + height);
                }
              }
            }
          }, this);

          if (titleOffset[0] && (this.options.title.verticalAlign || 'top') === 'top') {
            titleOffset[0] += this.options.title.margin;
          }

          if (titleOffset[2] && this.options.caption.verticalAlign === 'bottom') {
            titleOffset[2] += this.options.caption.margin;
          }

          requiresDirtyBox = !this.titleOffset || this.titleOffset.join(',') !== titleOffset.join(',');
          this.titleOffset = titleOffset;
          fireEvent(this, 'afterLayOutTitles');

          if (!this.isDirtyBox && requiresDirtyBox) {
            this.isDirtyBox = this.isDirtyLegend = requiresDirtyBox;

            if (this.hasRendered && pick(redraw, true) && this.isDirtyBox) {
              this.redraw();
            }
          }
        },
        getChartSize: function getChartSize() {
          var chart = this,
              optionsChart = chart.options.chart,
              widthOption = optionsChart.width,
              heightOption = optionsChart.height,
              renderTo = chart.renderTo;

          if (!defined(widthOption)) {
            chart.containerWidth = getStyle(renderTo, 'width');
          }

          if (!defined(heightOption)) {
            chart.containerHeight = getStyle(renderTo, 'height');
          }

          chart.chartWidth = Math.max(0, widthOption || chart.containerWidth || 600);
          chart.chartHeight = Math.max(0, relativeLength(heightOption, chart.chartWidth) || (chart.containerHeight > 1 ? chart.containerHeight : 400));
        },
        temporaryDisplay: function temporaryDisplay(revert) {
          var node = this.renderTo,
              tempStyle;

          if (!revert) {
            while (node && node.style) {
              if (!doc.body.contains(node) && !node.parentNode) {
                node.hcOrigDetached = true;
                doc.body.appendChild(node);
              }

              if (getStyle(node, 'display', false) === 'none' || node.hcOricDetached) {
                node.hcOrigStyle = {
                  display: node.style.display,
                  height: node.style.height,
                  overflow: node.style.overflow
                };
                tempStyle = {
                  display: 'block',
                  overflow: 'hidden'
                };

                if (node !== this.renderTo) {
                  tempStyle.height = 0;
                }

                css(node, tempStyle);

                if (!node.offsetWidth) {
                  node.style.setProperty('display', 'block', 'important');
                }
              }

              node = node.parentNode;

              if (node === doc.body) {
                break;
              }
            }
          } else {
            while (node && node.style) {
              if (node.hcOrigStyle) {
                css(node, node.hcOrigStyle);
                delete node.hcOrigStyle;
              }

              if (node.hcOrigDetached) {
                doc.body.removeChild(node);
                node.hcOrigDetached = false;
              }

              node = node.parentNode;
            }
          }
        },
        setClassName: function setClassName(className) {
          this.container.className = 'highcharts-container ' + (className || '');
        },
        getContainer: function getContainer() {
          var chart = this,
              container,
              options = chart.options,
              optionsChart = options.chart,
              chartWidth,
              chartHeight,
              renderTo = chart.renderTo,
              indexAttrName = 'data-highcharts-chart',
              oldChartIndex,
              Ren,
              containerId = uniqueKey(),
              containerStyle,
              key;

          if (!renderTo) {
            chart.renderTo = renderTo = optionsChart.renderTo;
          }

          if (isString(renderTo)) {
            chart.renderTo = renderTo = doc.getElementById(renderTo);
          }

          if (!renderTo) {
            error(13, true, chart);
          }

          oldChartIndex = pInt(attr(renderTo, indexAttrName));

          if (isNumber(oldChartIndex) && charts[oldChartIndex] && charts[oldChartIndex].hasRendered) {
            charts[oldChartIndex].destroy();
          }

          attr(renderTo, indexAttrName, chart.index);
          renderTo.innerHTML = '';

          if (!optionsChart.skipClone && !renderTo.offsetWidth) {
            chart.temporaryDisplay();
          }

          chart.getChartSize();
          chartWidth = chart.chartWidth;
          chartHeight = chart.chartHeight;
          css(renderTo, {
            overflow: 'hidden'
          });

          if (!chart.styledMode) {
            containerStyle = extend({
              position: 'relative',
              overflow: 'hidden',
              width: chartWidth + 'px',
              height: chartHeight + 'px',
              textAlign: 'left',
              lineHeight: 'normal',
              zIndex: 0,
              '-webkit-tap-highlight-color': 'rgba(0,0,0,0)'
            }, optionsChart.style);
          }

          container = createElement('div', {
            id: containerId
          }, containerStyle, renderTo);
          chart.container = container;
          chart._cursor = container.style.cursor;
          Ren = H[optionsChart.renderer] || H.Renderer;
          chart.renderer = new Ren(container, chartWidth, chartHeight, null, optionsChart.forExport, options.exporting && options.exporting.allowHTML, chart.styledMode);
          setAnimation(void 0, chart);
          chart.setClassName(optionsChart.className);

          if (!chart.styledMode) {
            chart.renderer.setStyle(optionsChart.style);
          } else {
            for (key in options.defs) {
              this.renderer.definition(options.defs[key]);
            }
          }

          chart.renderer.chartIndex = chart.index;
          fireEvent(this, 'afterGetContainer');
        },
        getMargins: function getMargins(skipAxes) {
          var _a = this,
              spacing = _a.spacing,
              margin = _a.margin,
              titleOffset = _a.titleOffset;

          this.resetMargins();

          if (titleOffset[0] && !defined(margin[0])) {
            this.plotTop = Math.max(this.plotTop, titleOffset[0] + spacing[0]);
          }

          if (titleOffset[2] && !defined(margin[2])) {
            this.marginBottom = Math.max(this.marginBottom, titleOffset[2] + spacing[2]);
          }

          if (this.legend && this.legend.display) {
            this.legend.adjustMargins(margin, spacing);
          }

          fireEvent(this, 'getMargins');

          if (!skipAxes) {
            this.getAxisMargins();
          }
        },
        getAxisMargins: function getAxisMargins() {
          var chart = this,
              axisOffset = chart.axisOffset = [0, 0, 0, 0],
              colorAxis = chart.colorAxis,
              margin = chart.margin,
              getOffset = function getOffset(axes) {
            axes.forEach(function (axis) {
              if (axis.visible) {
                axis.getOffset();
              }
            });
          };

          if (chart.hasCartesianSeries) {
            getOffset(chart.axes);
          } else if (colorAxis && colorAxis.length) {
            getOffset(colorAxis);
          }

          marginNames.forEach(function (m, side) {
            if (!defined(margin[side])) {
              chart[m] += axisOffset[side];
            }
          });
          chart.setChartSize();
        },
        reflow: function reflow(e) {
          var chart = this,
              optionsChart = chart.options.chart,
              renderTo = chart.renderTo,
              hasUserSize = defined(optionsChart.width) && defined(optionsChart.height),
              width = optionsChart.width || getStyle(renderTo, 'width'),
              height = optionsChart.height || getStyle(renderTo, 'height'),
              target = e ? e.target : win;

          if (!hasUserSize && !chart.isPrinting && width && height && (target === win || target === doc)) {
            if (width !== chart.containerWidth || height !== chart.containerHeight) {
              U.clearTimeout(chart.reflowTimeout);
              chart.reflowTimeout = syncTimeout(function () {
                if (chart.container) {
                  chart.setSize(void 0, void 0, false);
                }
              }, e ? 100 : 0);
            }

            chart.containerWidth = width;
            chart.containerHeight = height;
          }
        },
        setReflow: function setReflow(reflow) {
          var chart = this;

          if (reflow !== false && !this.unbindReflow) {
            this.unbindReflow = addEvent(win, 'resize', function (e) {
              if (chart.options) {
                chart.reflow(e);
              }
            });
            addEvent(this, 'destroy', this.unbindReflow);
          } else if (reflow === false && this.unbindReflow) {
            this.unbindReflow = this.unbindReflow();
          }
        },
        setSize: function setSize(width, height, animation) {
          var chart = this,
              renderer = chart.renderer,
              globalAnimation;
          chart.isResizing += 1;
          setAnimation(animation, chart);
          globalAnimation = renderer.globalAnimation;
          chart.oldChartHeight = chart.chartHeight;
          chart.oldChartWidth = chart.chartWidth;

          if (typeof width !== 'undefined') {
            chart.options.chart.width = width;
          }

          if (typeof height !== 'undefined') {
            chart.options.chart.height = height;
          }

          chart.getChartSize();

          if (!chart.styledMode) {
            (globalAnimation ? animate : css)(chart.container, {
              width: chart.chartWidth + 'px',
              height: chart.chartHeight + 'px'
            }, globalAnimation);
          }

          chart.setChartSize(true);
          renderer.setSize(chart.chartWidth, chart.chartHeight, globalAnimation);
          chart.axes.forEach(function (axis) {
            axis.isDirty = true;
            axis.setScale();
          });
          chart.isDirtyLegend = true;
          chart.isDirtyBox = true;
          chart.layOutTitles();
          chart.getMargins();
          chart.redraw(globalAnimation);
          chart.oldChartHeight = null;
          fireEvent(chart, 'resize');
          syncTimeout(function () {
            if (chart) {
              fireEvent(chart, 'endResize', null, function () {
                chart.isResizing -= 1;
              });
            }
          }, animObject(globalAnimation).duration || 0);
        },
        setChartSize: function setChartSize(skipAxes) {
          var chart = this,
              inverted = chart.inverted,
              renderer = chart.renderer,
              chartWidth = chart.chartWidth,
              chartHeight = chart.chartHeight,
              optionsChart = chart.options.chart,
              spacing = chart.spacing,
              clipOffset = chart.clipOffset,
              clipX,
              clipY,
              plotLeft,
              plotTop,
              plotWidth,
              plotHeight,
              plotBorderWidth;
          chart.plotLeft = plotLeft = Math.round(chart.plotLeft);
          chart.plotTop = plotTop = Math.round(chart.plotTop);
          chart.plotWidth = plotWidth = Math.max(0, Math.round(chartWidth - plotLeft - chart.marginRight));
          chart.plotHeight = plotHeight = Math.max(0, Math.round(chartHeight - plotTop - chart.marginBottom));
          chart.plotSizeX = inverted ? plotHeight : plotWidth;
          chart.plotSizeY = inverted ? plotWidth : plotHeight;
          chart.plotBorderWidth = optionsChart.plotBorderWidth || 0;
          chart.spacingBox = renderer.spacingBox = {
            x: spacing[3],
            y: spacing[0],
            width: chartWidth - spacing[3] - spacing[1],
            height: chartHeight - spacing[0] - spacing[2]
          };
          chart.plotBox = renderer.plotBox = {
            x: plotLeft,
            y: plotTop,
            width: plotWidth,
            height: plotHeight
          };
          plotBorderWidth = 2 * Math.floor(chart.plotBorderWidth / 2);
          clipX = Math.ceil(Math.max(plotBorderWidth, clipOffset[3]) / 2);
          clipY = Math.ceil(Math.max(plotBorderWidth, clipOffset[0]) / 2);
          chart.clipBox = {
            x: clipX,
            y: clipY,
            width: Math.floor(chart.plotSizeX - Math.max(plotBorderWidth, clipOffset[1]) / 2 - clipX),
            height: Math.max(0, Math.floor(chart.plotSizeY - Math.max(plotBorderWidth, clipOffset[2]) / 2 - clipY))
          };

          if (!skipAxes) {
            chart.axes.forEach(function (axis) {
              axis.setAxisSize();
              axis.setAxisTranslation();
            });
          }

          fireEvent(chart, 'afterSetChartSize', {
            skipAxes: skipAxes
          });
        },
        resetMargins: function resetMargins() {
          fireEvent(this, 'resetMargins');
          var chart = this,
              chartOptions = chart.options.chart;
          ['margin', 'spacing'].forEach(function splashArrays(target) {
            var value = chartOptions[target],
                values = isObject(value) ? value : [value, value, value, value];
            ['Top', 'Right', 'Bottom', 'Left'].forEach(function (sideName, side) {
              chart[target][side] = pick(chartOptions[target + sideName], values[side]);
            });
          });
          marginNames.forEach(function (m, side) {
            chart[m] = pick(chart.margin[side], chart.spacing[side]);
          });
          chart.axisOffset = [0, 0, 0, 0];
          chart.clipOffset = [0, 0, 0, 0];
        },
        drawChartBox: function drawChartBox() {
          var chart = this,
              optionsChart = chart.options.chart,
              renderer = chart.renderer,
              chartWidth = chart.chartWidth,
              chartHeight = chart.chartHeight,
              chartBackground = chart.chartBackground,
              plotBackground = chart.plotBackground,
              plotBorder = chart.plotBorder,
              chartBorderWidth,
              styledMode = chart.styledMode,
              plotBGImage = chart.plotBGImage,
              chartBackgroundColor = optionsChart.backgroundColor,
              plotBackgroundColor = optionsChart.plotBackgroundColor,
              plotBackgroundImage = optionsChart.plotBackgroundImage,
              mgn,
              bgAttr,
              plotLeft = chart.plotLeft,
              plotTop = chart.plotTop,
              plotWidth = chart.plotWidth,
              plotHeight = chart.plotHeight,
              plotBox = chart.plotBox,
              clipRect = chart.clipRect,
              clipBox = chart.clipBox,
              verb = 'animate';

          if (!chartBackground) {
            chart.chartBackground = chartBackground = renderer.rect().addClass('highcharts-background').add();
            verb = 'attr';
          }

          if (!styledMode) {
            chartBorderWidth = optionsChart.borderWidth || 0;
            mgn = chartBorderWidth + (optionsChart.shadow ? 8 : 0);
            bgAttr = {
              fill: chartBackgroundColor || 'none'
            };

            if (chartBorderWidth || chartBackground['stroke-width']) {
              bgAttr.stroke = optionsChart.borderColor;
              bgAttr['stroke-width'] = chartBorderWidth;
            }

            chartBackground.attr(bgAttr).shadow(optionsChart.shadow);
          } else {
            chartBorderWidth = mgn = chartBackground.strokeWidth();
          }

          chartBackground[verb]({
            x: mgn / 2,
            y: mgn / 2,
            width: chartWidth - mgn - chartBorderWidth % 2,
            height: chartHeight - mgn - chartBorderWidth % 2,
            r: optionsChart.borderRadius
          });
          verb = 'animate';

          if (!plotBackground) {
            verb = 'attr';
            chart.plotBackground = plotBackground = renderer.rect().addClass('highcharts-plot-background').add();
          }

          plotBackground[verb](plotBox);

          if (!styledMode) {
            plotBackground.attr({
              fill: plotBackgroundColor || 'none'
            }).shadow(optionsChart.plotShadow);

            if (plotBackgroundImage) {
              if (!plotBGImage) {
                chart.plotBGImage = renderer.image(plotBackgroundImage, plotLeft, plotTop, plotWidth, plotHeight).add();
              } else {
                if (plotBackgroundImage !== plotBGImage.attr('href')) {
                  plotBGImage.attr('href', plotBackgroundImage);
                }

                plotBGImage.animate(plotBox);
              }
            }
          }

          if (!clipRect) {
            chart.clipRect = renderer.clipRect(clipBox);
          } else {
            clipRect.animate({
              width: clipBox.width,
              height: clipBox.height
            });
          }

          verb = 'animate';

          if (!plotBorder) {
            verb = 'attr';
            chart.plotBorder = plotBorder = renderer.rect().addClass('highcharts-plot-border').attr({
              zIndex: 1
            }).add();
          }

          if (!styledMode) {
            plotBorder.attr({
              stroke: optionsChart.plotBorderColor,
              'stroke-width': optionsChart.plotBorderWidth || 0,
              fill: 'none'
            });
          }

          plotBorder[verb](plotBorder.crisp({
            x: plotLeft,
            y: plotTop,
            width: plotWidth,
            height: plotHeight
          }, -plotBorder.strokeWidth()));
          chart.isDirtyBox = false;
          fireEvent(this, 'afterDrawChartBox');
        },
        propFromSeries: function propFromSeries() {
          var chart = this,
              optionsChart = chart.options.chart,
              klass,
              seriesOptions = chart.options.series,
              i,
              value;
          ['inverted', 'angular', 'polar'].forEach(function (key) {
            klass = seriesTypes[optionsChart.type || optionsChart.defaultSeriesType];
            value = optionsChart[key] || klass && klass.prototype[key];
            i = seriesOptions && seriesOptions.length;

            while (!value && i--) {
              klass = seriesTypes[seriesOptions[i].type];

              if (klass && klass.prototype[key]) {
                value = true;
              }
            }

            chart[key] = value;
          });
        },
        linkSeries: function linkSeries() {
          var chart = this,
              chartSeries = chart.series;
          chartSeries.forEach(function (series) {
            series.linkedSeries.length = 0;
          });
          chartSeries.forEach(function (series) {
            var linkedTo = series.options.linkedTo;

            if (isString(linkedTo)) {
              if (linkedTo === ':previous') {
                linkedTo = chart.series[series.index - 1];
              } else {
                linkedTo = chart.get(linkedTo);
              }

              if (linkedTo && linkedTo.linkedParent !== series) {
                linkedTo.linkedSeries.push(series);
                series.linkedParent = linkedTo;

                if (linkedTo.enabledDataSorting) {
                  series.setDataSortingOptions();
                }

                series.visible = pick(series.options.visible, linkedTo.options.visible, series.visible);
              }
            }
          });
          fireEvent(this, 'afterLinkSeries');
        },
        renderSeries: function renderSeries() {
          this.series.forEach(function (serie) {
            serie.translate();
            serie.render();
          });
        },
        renderLabels: function renderLabels() {
          var chart = this,
              labels = chart.options.labels;

          if (labels.items) {
            labels.items.forEach(function (label) {
              var style = extend(labels.style, label.style),
                  x = pInt(style.left) + chart.plotLeft,
                  y = pInt(style.top) + chart.plotTop + 12;
              delete style.left;
              delete style.top;
              chart.renderer.text(label.html, x, y).attr({
                zIndex: 2
              }).css(style).add();
            });
          }
        },
        render: function render() {
          var chart = this,
              axes = chart.axes,
              colorAxis = chart.colorAxis,
              renderer = chart.renderer,
              options = chart.options,
              correction = 0,
              tempWidth,
              tempHeight,
              redoHorizontal,
              redoVertical,
              renderAxes = function renderAxes(axes) {
            axes.forEach(function (axis) {
              if (axis.visible) {
                axis.render();
              }
            });
          };

          chart.setTitle();
          chart.legend = new Legend(chart, options.legend);

          if (chart.getStacks) {
            chart.getStacks();
          }

          chart.getMargins(true);
          chart.setChartSize();
          tempWidth = chart.plotWidth;
          axes.some(function (axis) {
            if (axis.horiz && axis.visible && axis.options.labels.enabled && axis.series.length) {
              correction = 21;
              return true;
            }
          });
          chart.plotHeight = Math.max(chart.plotHeight - correction, 0);
          tempHeight = chart.plotHeight;
          axes.forEach(function (axis) {
            axis.setScale();
          });
          chart.getAxisMargins();
          redoHorizontal = tempWidth / chart.plotWidth > 1.1;
          redoVertical = tempHeight / chart.plotHeight > 1.05;

          if (redoHorizontal || redoVertical) {
            axes.forEach(function (axis) {
              if (axis.horiz && redoHorizontal || !axis.horiz && redoVertical) {
                axis.setTickInterval(true);
              }
            });
            chart.getMargins();
          }

          chart.drawChartBox();

          if (chart.hasCartesianSeries) {
            renderAxes(axes);
          } else if (colorAxis && colorAxis.length) {
            renderAxes(colorAxis);
          }

          if (!chart.seriesGroup) {
            chart.seriesGroup = renderer.g('series-group').attr({
              zIndex: 3
            }).add();
          }

          chart.renderSeries();
          chart.renderLabels();
          chart.addCredits();

          if (chart.setResponsive) {
            chart.setResponsive();
          }

          chart.updateContainerScaling();
          chart.hasRendered = true;
        },
        addCredits: function addCredits(credits) {
          var chart = this;
          credits = merge(true, this.options.credits, credits);

          if (credits.enabled && !this.credits) {
            this.credits = this.renderer.text(credits.text + (this.mapCredits || ''), 0, 0).addClass('highcharts-credits').on('click', function () {
              if (credits.href) {
                win.location.href = credits.href;
              }
            }).attr({
              align: credits.position.align,
              zIndex: 8
            });

            if (!chart.styledMode) {
              this.credits.css(credits.style);
            }

            this.credits.add().align(credits.position);

            this.credits.update = function (options) {
              chart.credits = chart.credits.destroy();
              chart.addCredits(options);
            };
          }
        },
        updateContainerScaling: function updateContainerScaling() {
          var container = this.container;

          if (container.offsetWidth && container.offsetHeight && container.getBoundingClientRect) {
            var bb = container.getBoundingClientRect(),
                scaleX = bb.width / container.offsetWidth,
                scaleY = bb.height / container.offsetHeight;

            if (scaleX !== 1 || scaleY !== 1) {
              this.containerScaling = {
                scaleX: scaleX,
                scaleY: scaleY
              };
            } else {
              delete this.containerScaling;
            }
          }
        },
        destroy: function destroy() {
          var chart = this,
              axes = chart.axes,
              series = chart.series,
              container = chart.container,
              i,
              parentNode = container && container.parentNode;
          fireEvent(chart, 'destroy');

          if (chart.renderer.forExport) {
            erase(charts, chart);
          } else {
            charts[chart.index] = void 0;
          }

          H.chartCount--;
          chart.renderTo.removeAttribute('data-highcharts-chart');
          removeEvent(chart);
          i = axes.length;

          while (i--) {
            axes[i] = axes[i].destroy();
          }

          if (this.scroller && this.scroller.destroy) {
            this.scroller.destroy();
          }

          i = series.length;

          while (i--) {
            series[i] = series[i].destroy();
          }

          ['title', 'subtitle', 'chartBackground', 'plotBackground', 'plotBGImage', 'plotBorder', 'seriesGroup', 'clipRect', 'credits', 'pointer', 'rangeSelector', 'legend', 'resetZoomButton', 'tooltip', 'renderer'].forEach(function (name) {
            var prop = chart[name];

            if (prop && prop.destroy) {
              chart[name] = prop.destroy();
            }
          });

          if (container) {
            container.innerHTML = '';
            removeEvent(container);

            if (parentNode) {
              discardElement(container);
            }
          }

          objectEach(chart, function (val, key) {
            delete chart[key];
          });
        },
        firstRender: function firstRender() {
          var chart = this,
              options = chart.options;

          if (chart.isReadyToRender && !chart.isReadyToRender()) {
            return;
          }

          chart.getContainer();
          chart.resetMargins();
          chart.setChartSize();
          chart.propFromSeries();
          chart.getAxes();
          (isArray(options.series) ? options.series : []).forEach(function (serieOptions) {
            chart.initSeries(serieOptions);
          });
          chart.linkSeries();
          chart.setSeriesData();
          fireEvent(chart, 'beforeRender');

          if (Pointer) {
            if (!H.hasTouch && (win.PointerEvent || win.MSPointerEvent)) {
              chart.pointer = new MSPointer(chart, options);
            } else {
              chart.pointer = new Pointer(chart, options);
            }
          }

          chart.render();

          if (!chart.renderer.imgCount && !chart.hasLoaded) {
            chart.onload();
          }

          chart.temporaryDisplay(true);
        },
        onload: function onload() {
          this.callbacks.concat([this.callback]).forEach(function (fn) {
            if (fn && typeof this.index !== 'undefined') {
              fn.apply(this, [this]);
            }
          }, this);
          fireEvent(this, 'load');
          fireEvent(this, 'render');

          if (defined(this.index)) {
            this.setReflow(this.options.chart.reflow);
          }

          this.hasLoaded = true;
        }
      });
    });

    _registerModule(_modules, 'parts/ScrollablePlotArea.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var addEvent = U.addEvent,
          createElement = U.createElement,
          pick = U.pick,
          stop = U.stop;
      var Chart = H.Chart;
      addEvent(Chart, 'afterSetChartSize', function (e) {
        var scrollablePlotArea = this.options.chart.scrollablePlotArea,
            scrollableMinWidth = scrollablePlotArea && scrollablePlotArea.minWidth,
            scrollableMinHeight = scrollablePlotArea && scrollablePlotArea.minHeight,
            scrollablePixelsX,
            scrollablePixelsY,
            corrections;

        if (!this.renderer.forExport) {
          if (scrollableMinWidth) {
            this.scrollablePixelsX = scrollablePixelsX = Math.max(0, scrollableMinWidth - this.chartWidth);

            if (scrollablePixelsX) {
              this.plotWidth += scrollablePixelsX;

              if (this.inverted) {
                this.clipBox.height += scrollablePixelsX;
                this.plotBox.height += scrollablePixelsX;
              } else {
                this.clipBox.width += scrollablePixelsX;
                this.plotBox.width += scrollablePixelsX;
              }

              corrections = {
                1: {
                  name: 'right',
                  value: scrollablePixelsX
                }
              };
            }
          } else if (scrollableMinHeight) {
            this.scrollablePixelsY = scrollablePixelsY = Math.max(0, scrollableMinHeight - this.chartHeight);

            if (scrollablePixelsY) {
              this.plotHeight += scrollablePixelsY;

              if (this.inverted) {
                this.clipBox.width += scrollablePixelsY;
                this.plotBox.width += scrollablePixelsY;
              } else {
                this.clipBox.height += scrollablePixelsY;
                this.plotBox.height += scrollablePixelsY;
              }

              corrections = {
                2: {
                  name: 'bottom',
                  value: scrollablePixelsY
                }
              };
            }
          }

          if (corrections && !e.skipAxes) {
            this.axes.forEach(function (axis) {
              if (corrections[axis.side]) {
                axis.getPlotLinePath = function () {
                  var marginName = corrections[axis.side].name,
                      correctionValue = corrections[axis.side].value,
                      margin = this[marginName],
                      path;
                  this[marginName] = margin - correctionValue;
                  path = H.Axis.prototype.getPlotLinePath.apply(this, arguments);
                  this[marginName] = margin;
                  return path;
                };
              } else {
                axis.setAxisSize();
                axis.setAxisTranslation();
              }
            });
          }
        }
      });
      addEvent(Chart, 'render', function () {
        if (this.scrollablePixelsX || this.scrollablePixelsY) {
          if (this.setUpScrolling) {
            this.setUpScrolling();
          }

          this.applyFixed();
        } else if (this.fixedDiv) {
          this.applyFixed();
        }
      });

      Chart.prototype.setUpScrolling = function () {
        var _this = this;

        var attribs = {
          WebkitOverflowScrolling: 'touch',
          overflowX: 'hidden',
          overflowY: 'hidden'
        };

        if (this.scrollablePixelsX) {
          attribs.overflowX = 'auto';
        }

        if (this.scrollablePixelsY) {
          attribs.overflowY = 'auto';
        }

        this.scrollingContainer = createElement('div', {
          'className': 'highcharts-scrolling'
        }, attribs, this.renderTo);
        addEvent(this.scrollingContainer, 'scroll', function () {
          if (_this.pointer) {
            delete _this.pointer.chartPosition;
          }
        });
        this.innerContainer = createElement('div', {
          'className': 'highcharts-inner-container'
        }, null, this.scrollingContainer);
        this.innerContainer.appendChild(this.container);
        this.setUpScrolling = null;
      };

      Chart.prototype.moveFixedElements = function () {
        var container = this.container,
            fixedRenderer = this.fixedRenderer,
            fixedSelectors = ['.highcharts-contextbutton', '.highcharts-credits', '.highcharts-legend', '.highcharts-legend-checkbox', '.highcharts-navigator-series', '.highcharts-navigator-xaxis', '.highcharts-navigator-yaxis', '.highcharts-navigator', '.highcharts-reset-zoom', '.highcharts-scrollbar', '.highcharts-subtitle', '.highcharts-title'],
            axisClass;

        if (this.scrollablePixelsX && !this.inverted) {
          axisClass = '.highcharts-yaxis';
        } else if (this.scrollablePixelsX && this.inverted) {
          axisClass = '.highcharts-xaxis';
        } else if (this.scrollablePixelsY && !this.inverted) {
          axisClass = '.highcharts-xaxis';
        } else if (this.scrollablePixelsY && this.inverted) {
          axisClass = '.highcharts-yaxis';
        }

        fixedSelectors.push(axisClass, axisClass + '-labels');
        fixedSelectors.forEach(function (className) {
          [].forEach.call(container.querySelectorAll(className), function (elem) {
            (elem.namespaceURI === fixedRenderer.SVG_NS ? fixedRenderer.box : fixedRenderer.box.parentNode).appendChild(elem);
            elem.style.pointerEvents = 'auto';
          });
        });
      };

      Chart.prototype.applyFixed = function () {
        var fixedRenderer,
            scrollableWidth,
            scrollableHeight,
            firstTime = !this.fixedDiv,
            scrollableOptions = this.options.chart.scrollablePlotArea;

        if (firstTime) {
          this.fixedDiv = createElement('div', {
            className: 'highcharts-fixed'
          }, {
            position: 'absolute',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 2
          }, null, true);
          this.renderTo.insertBefore(this.fixedDiv, this.renderTo.firstChild);
          this.renderTo.style.overflow = 'visible';
          this.fixedRenderer = fixedRenderer = new H.Renderer(this.fixedDiv, this.chartWidth, this.chartHeight);
          this.scrollableMask = fixedRenderer.path().attr({
            fill: this.options.chart.backgroundColor || '#fff',
            'fill-opacity': pick(scrollableOptions.opacity, 0.85),
            zIndex: -1
          }).addClass('highcharts-scrollable-mask').add();
          this.moveFixedElements();
          addEvent(this, 'afterShowResetZoom', this.moveFixedElements);
          addEvent(this, 'afterLayOutTitles', this.moveFixedElements);
        } else {
          this.fixedRenderer.setSize(this.chartWidth, this.chartHeight);
        }

        scrollableWidth = this.chartWidth + (this.scrollablePixelsX || 0);
        scrollableHeight = this.chartHeight + (this.scrollablePixelsY || 0);
        stop(this.container);
        this.container.style.width = scrollableWidth + 'px';
        this.container.style.height = scrollableHeight + 'px';
        this.renderer.boxWrapper.attr({
          width: scrollableWidth,
          height: scrollableHeight,
          viewBox: [0, 0, scrollableWidth, scrollableHeight].join(' ')
        });
        this.chartBackground.attr({
          width: scrollableWidth,
          height: scrollableHeight
        });

        if (this.scrollablePixelsY) {
          this.scrollingContainer.style.height = this.chartHeight + 'px';
        }

        if (firstTime) {
          if (scrollableOptions.scrollPositionX) {
            this.scrollingContainer.scrollLeft = this.scrollablePixelsX * scrollableOptions.scrollPositionX;
          }

          if (scrollableOptions.scrollPositionY) {
            this.scrollingContainer.scrollTop = this.scrollablePixelsY * scrollableOptions.scrollPositionY;
          }
        }

        var axisOffset = this.axisOffset,
            maskTop = this.plotTop - axisOffset[0] - 1,
            maskLeft = this.plotLeft - axisOffset[3] - 1,
            maskBottom = this.plotTop + this.plotHeight + axisOffset[2] + 1,
            maskRight = this.plotLeft + this.plotWidth + axisOffset[1] + 1,
            maskPlotRight = this.plotLeft + this.plotWidth - (this.scrollablePixelsX || 0),
            maskPlotBottom = this.plotTop + this.plotHeight - (this.scrollablePixelsY || 0),
            d;

        if (this.scrollablePixelsX) {
          d = ['M', 0, maskTop, 'L', this.plotLeft - 1, maskTop, 'L', this.plotLeft - 1, maskBottom, 'L', 0, maskBottom, 'Z', 'M', maskPlotRight, maskTop, 'L', this.chartWidth, maskTop, 'L', this.chartWidth, maskBottom, 'L', maskPlotRight, maskBottom, 'Z'];
        } else if (this.scrollablePixelsY) {
          d = ['M', maskLeft, 0, 'L', maskLeft, this.plotTop - 1, 'L', maskRight, this.plotTop - 1, 'L', maskRight, 0, 'Z', 'M', maskLeft, maskPlotBottom, 'L', maskLeft, this.chartHeight, 'L', maskRight, this.chartHeight, 'L', maskRight, maskPlotBottom, 'Z'];
        } else {
          d = ['M', 0, 0];
        }

        if (this.redrawTrigger !== 'adjustHeight') {
          this.scrollableMask.attr({
            d: d
          });
        }
      };
    });

    _registerModule(_modules, 'mixins/legend-symbol.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var merge = U.merge,
          pick = U.pick;
      H.LegendSymbolMixin = {
        drawRectangle: function drawRectangle(legend, item) {
          var options = legend.options,
              symbolHeight = legend.symbolHeight,
              square = options.squareSymbol,
              symbolWidth = square ? symbolHeight : legend.symbolWidth;
          item.legendSymbol = this.chart.renderer.rect(square ? (legend.symbolWidth - symbolHeight) / 2 : 0, legend.baseline - symbolHeight + 1, symbolWidth, symbolHeight, pick(legend.options.symbolRadius, symbolHeight / 2)).addClass('highcharts-point').attr({
            zIndex: 3
          }).add(item.legendGroup);
        },
        drawLineMarker: function drawLineMarker(legend) {
          var options = this.options,
              markerOptions = options.marker,
              radius,
              legendSymbol,
              symbolWidth = legend.symbolWidth,
              symbolHeight = legend.symbolHeight,
              generalRadius = symbolHeight / 2,
              renderer = this.chart.renderer,
              legendItemGroup = this.legendGroup,
              verticalCenter = legend.baseline - Math.round(legend.fontMetrics.b * 0.3),
              attr = {};

          if (!this.chart.styledMode) {
            attr = {
              'stroke-width': options.lineWidth || 0
            };

            if (options.dashStyle) {
              attr.dashstyle = options.dashStyle;
            }
          }

          this.legendLine = renderer.path(['M', 0, verticalCenter, 'L', symbolWidth, verticalCenter]).addClass('highcharts-graph').attr(attr).add(legendItemGroup);

          if (markerOptions && markerOptions.enabled !== false && symbolWidth) {
            radius = Math.min(pick(markerOptions.radius, generalRadius), generalRadius);

            if (this.symbol.indexOf('url') === 0) {
              markerOptions = merge(markerOptions, {
                width: symbolHeight,
                height: symbolHeight
              });
              radius = 0;
            }

            this.legendSymbol = legendSymbol = renderer.symbol(this.symbol, symbolWidth / 2 - radius, verticalCenter - radius, 2 * radius, 2 * radius, markerOptions).addClass('highcharts-point').add(legendItemGroup);
            legendSymbol.isMarker = true;
          }
        }
      };
      return H.LegendSymbolMixin;
    });

    _registerModule(_modules, 'parts/Point.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (Highcharts, U) {

      var animObject = U.animObject,
          defined = U.defined,
          erase = U.erase,
          extend = U.extend,
          format = U.format,
          getNestedProperty = U.getNestedProperty,
          isArray = U.isArray,
          isNumber = U.isNumber,
          isObject = U.isObject,
          syncTimeout = U.syncTimeout,
          pick = U.pick,
          removeEvent = U.removeEvent,
          uniqueKey = U.uniqueKey;
      var H = Highcharts,
          fireEvent = H.fireEvent;

      var Point = function () {
        function Point() {
          this.category = void 0;
          this.colorIndex = void 0;
          this.formatPrefix = 'point';
          this.id = void 0;
          this.isNull = false;
          this.name = void 0;
          this.options = void 0;
          this.percentage = void 0;
          this.selected = false;
          this.series = void 0;
          this.total = void 0;
          this.visible = true;
          this.x = void 0;
        }

        Point.prototype.animateBeforeDestroy = function () {
          var point = this,
              animateParams = {
            x: point.startXPos,
            opacity: 0
          },
              isDataLabel,
              graphicalProps = point.getGraphicalProps();
          graphicalProps.singular.forEach(function (prop) {
            isDataLabel = prop === 'dataLabel';
            point[prop] = point[prop].animate(isDataLabel ? {
              x: point[prop].startXPos,
              y: point[prop].startYPos,
              opacity: 0
            } : animateParams);
          });
          graphicalProps.plural.forEach(function (plural) {
            point[plural].forEach(function (item) {
              if (item.element) {
                item.animate(extend({
                  x: point.startXPos
                }, item.startYPos ? {
                  x: item.startXPos,
                  y: item.startYPos
                } : {}));
              }
            });
          });
        };

        Point.prototype.applyOptions = function (options, x) {
          var point = this,
              series = point.series,
              pointValKey = series.options.pointValKey || series.pointValKey;
          options = Point.prototype.optionsToObject.call(this, options);
          extend(point, options);
          point.options = point.options ? extend(point.options, options) : options;

          if (options.group) {
            delete point.group;
          }

          if (options.dataLabels) {
            delete point.dataLabels;
          }

          if (pointValKey) {
            point.y = Point.prototype.getNestedProperty.call(point, pointValKey);
          }

          point.isNull = pick(point.isValid && !point.isValid(), point.x === null || !isNumber(point.y));
          point.formatPrefix = point.isNull ? 'null' : 'point';

          if (point.selected) {
            point.state = 'select';
          }

          if ('name' in point && typeof x === 'undefined' && series.xAxis && series.xAxis.hasNames) {
            point.x = series.xAxis.nameToX(point);
          }

          if (typeof point.x === 'undefined' && series) {
            if (typeof x === 'undefined') {
              point.x = series.autoIncrement(point);
            } else {
              point.x = x;
            }
          }

          return point;
        };

        Point.prototype.destroy = function () {
          var point = this,
              series = point.series,
              chart = series.chart,
              dataSorting = series.options.dataSorting,
              hoverPoints = chart.hoverPoints,
              globalAnimation = point.series.chart.renderer.globalAnimation,
              animation = animObject(globalAnimation),
              prop;

          function destroyPoint() {
            if (point.graphic || point.dataLabel || point.dataLabels) {
              removeEvent(point);
              point.destroyElements();
            }

            for (prop in point) {
              point[prop] = null;
            }
          }

          if (point.legendItem) {
            chart.legend.destroyItem(point);
          }

          if (hoverPoints) {
            point.setState();
            erase(hoverPoints, point);

            if (!hoverPoints.length) {
              chart.hoverPoints = null;
            }
          }

          if (point === chart.hoverPoint) {
            point.onMouseOut();
          }

          if (!dataSorting || !dataSorting.enabled) {
            destroyPoint();
          } else {
            this.animateBeforeDestroy();
            syncTimeout(destroyPoint, animation.duration);
          }

          chart.pointCount--;
        };

        Point.prototype.destroyElements = function (kinds) {
          var point = this,
              props = point.getGraphicalProps(kinds);
          props.singular.forEach(function (prop) {
            point[prop] = point[prop].destroy();
          });
          props.plural.forEach(function (plural) {
            point[plural].forEach(function (item) {
              if (item.element) {
                item.destroy();
              }
            });
            delete point[plural];
          });
        };

        Point.prototype.firePointEvent = function (eventType, eventArgs, defaultFunction) {
          var point = this,
              series = this.series,
              seriesOptions = series.options;

          if (seriesOptions.point.events[eventType] || point.options && point.options.events && point.options.events[eventType]) {
            point.importEvents();
          }

          if (eventType === 'click' && seriesOptions.allowPointSelect) {
            defaultFunction = function defaultFunction(event) {
              if (point.select) {
                point.select(null, event.ctrlKey || event.metaKey || event.shiftKey);
              }
            };
          }

          fireEvent(point, eventType, eventArgs, defaultFunction);
        };

        Point.prototype.getClassName = function () {
          var point = this;
          return 'highcharts-point' + (point.selected ? ' highcharts-point-select' : '') + (point.negative ? ' highcharts-negative' : '') + (point.isNull ? ' highcharts-null-point' : '') + (typeof point.colorIndex !== 'undefined' ? ' highcharts-color-' + point.colorIndex : '') + (point.options.className ? ' ' + point.options.className : '') + (point.zone && point.zone.className ? ' ' + point.zone.className.replace('highcharts-negative', '') : '');
        };

        Point.prototype.getGraphicalProps = function (kinds) {
          var point = this,
              props = [],
              prop,
              i,
              graphicalProps = {
            singular: [],
            plural: []
          };
          kinds = kinds || {
            graphic: 1,
            dataLabel: 1
          };

          if (kinds.graphic) {
            props.push('graphic', 'shadowGroup');
          }

          if (kinds.dataLabel) {
            props.push('dataLabel', 'dataLabelUpper', 'connector');
          }

          i = props.length;

          while (i--) {
            prop = props[i];

            if (point[prop]) {
              graphicalProps.singular.push(prop);
            }
          }

          ['dataLabel', 'connector'].forEach(function (prop) {
            var plural = prop + 's';

            if (kinds[prop] && point[plural]) {
              graphicalProps.plural.push(plural);
            }
          });
          return graphicalProps;
        };

        Point.prototype.getLabelConfig = function () {
          return {
            x: this.category,
            y: this.y,
            color: this.color,
            colorIndex: this.colorIndex,
            key: this.name || this.category,
            series: this.series,
            point: this,
            percentage: this.percentage,
            total: this.total || this.stackTotal
          };
        };

        Point.prototype.getNestedProperty = function (key) {
          if (!key) {
            return;
          }

          if (key.indexOf('custom.') === 0) {
            return getNestedProperty(key, this.options);
          }

          return this[key];
        };

        Point.prototype.getZone = function () {
          var series = this.series,
              zones = series.zones,
              zoneAxis = series.zoneAxis || 'y',
              i = 0,
              zone;
          zone = zones[i];

          while (this[zoneAxis] >= zone.value) {
            zone = zones[++i];
          }

          if (!this.nonZonedColor) {
            this.nonZonedColor = this.color;
          }

          if (zone && zone.color && !this.options.color) {
            this.color = zone.color;
          } else {
            this.color = this.nonZonedColor;
          }

          return zone;
        };

        Point.prototype.hasNewShapeType = function () {
          var point = this;
          var oldShapeType = point.graphic && (point.graphic.symbolName || point.graphic.element.nodeName);
          return oldShapeType !== this.shapeType;
        };

        Point.prototype.init = function (series, options, x) {
          this.series = series;
          this.applyOptions(options, x);
          this.id = defined(this.id) ? this.id : uniqueKey();
          this.resolveColor();
          series.chart.pointCount++;
          fireEvent(this, 'afterInit');
          return this;
        };

        Point.prototype.optionsToObject = function (options) {
          var ret = {},
              series = this.series,
              keys = series.options.keys,
              pointArrayMap = keys || series.pointArrayMap || ['y'],
              valueCount = pointArrayMap.length,
              firstItemType,
              i = 0,
              j = 0;

          if (isNumber(options) || options === null) {
            ret[pointArrayMap[0]] = options;
          } else if (isArray(options)) {
            if (!keys && options.length > valueCount) {
              firstItemType = typeof options[0];

              if (firstItemType === 'string') {
                ret.name = options[0];
              } else if (firstItemType === 'number') {
                ret.x = options[0];
              }

              i++;
            }

            while (j < valueCount) {
              if (!keys || typeof options[i] !== 'undefined') {
                if (pointArrayMap[j].indexOf('.') > 0) {
                  Point.prototype.setNestedProperty(ret, options[i], pointArrayMap[j]);
                } else {
                  ret[pointArrayMap[j]] = options[i];
                }
              }

              i++;
              j++;
            }
          } else if (typeof options === 'object') {
            ret = options;

            if (options.dataLabels) {
              series._hasPointLabels = true;
            }

            if (options.marker) {
              series._hasPointMarkers = true;
            }
          }

          return ret;
        };

        Point.prototype.resolveColor = function () {
          var series = this.series,
              colors,
              optionsChart = series.chart.options.chart,
              colorCount = optionsChart.colorCount,
              styledMode = series.chart.styledMode,
              colorIndex;

          if (!styledMode && !this.options.color) {
            this.color = series.color;
          }

          if (series.options.colorByPoint) {
            if (!styledMode) {
              colors = series.options.colors || series.chart.options.colors;
              this.color = this.color || colors[series.colorCounter];
              colorCount = colors.length;
            }

            colorIndex = series.colorCounter;
            series.colorCounter++;

            if (series.colorCounter === colorCount) {
              series.colorCounter = 0;
            }
          } else {
            colorIndex = series.colorIndex;
          }

          this.colorIndex = pick(this.colorIndex, colorIndex);
        };

        Point.prototype.setNestedProperty = function (object, value, key) {
          var nestedKeys = key.split('.');
          nestedKeys.reduce(function (result, key, i, arr) {
            var isLastKey = arr.length - 1 === i;
            result[key] = isLastKey ? value : isObject(result[key], true) ? result[key] : {};
            return result[key];
          }, object);
          return object;
        };

        Point.prototype.tooltipFormatter = function (pointFormat) {
          var series = this.series,
              seriesTooltipOptions = series.tooltipOptions,
              valueDecimals = pick(seriesTooltipOptions.valueDecimals, ''),
              valuePrefix = seriesTooltipOptions.valuePrefix || '',
              valueSuffix = seriesTooltipOptions.valueSuffix || '';

          if (series.chart.styledMode) {
            pointFormat = series.chart.tooltip.styledModeFormat(pointFormat);
          }

          (series.pointArrayMap || ['y']).forEach(function (key) {
            key = '{point.' + key;

            if (valuePrefix || valueSuffix) {
              pointFormat = pointFormat.replace(RegExp(key + '}', 'g'), valuePrefix + key + '}' + valueSuffix);
            }

            pointFormat = pointFormat.replace(RegExp(key + '}', 'g'), key + ':,.' + valueDecimals + 'f}');
          });
          return format(pointFormat, {
            point: this,
            series: this.series
          }, series.chart);
        };

        return Point;
      }();

      H.Point = Point;
      return H.Point;
    });

    _registerModule(_modules, 'parts/Series.js', [_modules['parts/Globals.js'], _modules['mixins/legend-symbol.js'], _modules['parts/Point.js'], _modules['parts/Utilities.js']], function (H, LegendSymbolMixin, Point, U) {

      var addEvent = U.addEvent,
          animObject = U.animObject,
          arrayMax = U.arrayMax,
          arrayMin = U.arrayMin,
          clamp = U.clamp,
          correctFloat = U.correctFloat,
          defined = U.defined,
          erase = U.erase,
          error = U.error,
          extend = U.extend,
          find = U.find,
          fireEvent = U.fireEvent,
          getNestedProperty = U.getNestedProperty,
          isArray = U.isArray,
          isFunction = U.isFunction,
          isNumber = U.isNumber,
          isString = U.isString,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick,
          removeEvent = U.removeEvent,
          seriesType = U.seriesType,
          splat = U.splat,
          syncTimeout = U.syncTimeout;
      var defaultOptions = H.defaultOptions,
          defaultPlotOptions = H.defaultPlotOptions,
          seriesTypes = H.seriesTypes,
          SVGElement = H.SVGElement,
          win = H.win;
      H.Series = seriesType('line', null, {
        lineWidth: 2,
        allowPointSelect: false,
        showCheckbox: false,
        animation: {
          duration: 1000
        },
        events: {},
        marker: {
          enabledThreshold: 2,
          lineColor: '#ffffff',
          lineWidth: 0,
          radius: 4,
          states: {
            normal: {
              animation: true
            },
            hover: {
              animation: {
                duration: 50
              },
              enabled: true,
              radiusPlus: 2,
              lineWidthPlus: 1
            },
            select: {
              fillColor: '#cccccc',
              lineColor: '#000000',
              lineWidth: 2
            }
          }
        },
        point: {
          events: {}
        },
        dataLabels: {
          align: 'center',
          formatter: function formatter() {
            var numberFormatter = this.series.chart.numberFormatter;
            return typeof this.y !== 'number' ? '' : numberFormatter(this.y, -1);
          },
          padding: 5,
          style: {
            fontSize: '11px',
            fontWeight: 'bold',
            color: 'contrast',
            textOutline: '1px contrast'
          },
          verticalAlign: 'bottom',
          x: 0,
          y: 0
        },
        cropThreshold: 300,
        opacity: 1,
        pointRange: 0,
        softThreshold: true,
        states: {
          normal: {
            animation: true
          },
          hover: {
            animation: {
              duration: 50
            },
            lineWidthPlus: 1,
            marker: {},
            halo: {
              size: 10,
              opacity: 0.25
            }
          },
          select: {
            animation: {
              duration: 0
            }
          },
          inactive: {
            animation: {
              duration: 50
            },
            opacity: 0.2
          }
        },
        stickyTracking: true,
        turboThreshold: 1000,
        findNearestPointBy: 'x'
      }, {
        axisTypes: ['xAxis', 'yAxis'],
        coll: 'series',
        colorCounter: 0,
        cropShoulder: 1,
        directTouch: false,
        eventsToUnbind: [],
        isCartesian: true,
        parallelArrays: ['x', 'y'],
        pointClass: Point,
        requireSorting: true,
        sorted: true,
        init: function init(chart, options) {
          fireEvent(this, 'init', {
            options: options
          });
          var series = this,
              events,
              chartSeries = chart.series,
              lastSeries;
          this.eventOptions = this.eventOptions || {};
          series.chart = chart;
          series.options = options = series.setOptions(options);
          series.linkedSeries = [];
          series.bindAxes();
          extend(series, {
            name: options.name,
            state: '',
            visible: options.visible !== false,
            selected: options.selected === true
          });
          events = options.events;
          objectEach(events, function (event, eventType) {
            if (isFunction(event)) {
              if (series.eventOptions[eventType] !== event) {
                if (isFunction(series.eventOptions[eventType])) {
                  removeEvent(series, eventType, series.eventOptions[eventType]);
                }

                series.eventOptions[eventType] = event;
                addEvent(series, eventType, event);
              }
            }
          });

          if (events && events.click || options.point && options.point.events && options.point.events.click || options.allowPointSelect) {
            chart.runTrackerClick = true;
          }

          series.getColor();
          series.getSymbol();
          series.parallelArrays.forEach(function (key) {
            if (!series[key + 'Data']) {
              series[key + 'Data'] = [];
            }
          });

          if (series.isCartesian) {
            chart.hasCartesianSeries = true;
          }

          if (chartSeries.length) {
            lastSeries = chartSeries[chartSeries.length - 1];
          }

          series._i = pick(lastSeries && lastSeries._i, -1) + 1;
          chart.orderSeries(this.insert(chartSeries));

          if (options.dataSorting && options.dataSorting.enabled) {
            series.setDataSortingOptions();
          } else if (!series.points && !series.data) {
            series.setData(options.data, false);
          }

          fireEvent(this, 'afterInit');
        },
        is: function is(type) {
          return seriesTypes[type] && this instanceof seriesTypes[type];
        },
        insert: function insert(collection) {
          var indexOption = this.options.index,
              i;

          if (isNumber(indexOption)) {
            i = collection.length;

            while (i--) {
              if (indexOption >= pick(collection[i].options.index, collection[i]._i)) {
                collection.splice(i + 1, 0, this);
                break;
              }
            }

            if (i === -1) {
              collection.unshift(this);
            }

            i = i + 1;
          } else {
            collection.push(this);
          }

          return pick(i, collection.length - 1);
        },
        bindAxes: function bindAxes() {
          var series = this,
              seriesOptions = series.options,
              chart = series.chart,
              axisOptions;
          fireEvent(this, 'bindAxes', null, function () {
            (series.axisTypes || []).forEach(function (AXIS) {
              chart[AXIS].forEach(function (axis) {
                axisOptions = axis.options;

                if (seriesOptions[AXIS] === axisOptions.index || typeof seriesOptions[AXIS] !== 'undefined' && seriesOptions[AXIS] === axisOptions.id || typeof seriesOptions[AXIS] === 'undefined' && axisOptions.index === 0) {
                  series.insert(axis.series);
                  series[AXIS] = axis;
                  axis.isDirty = true;
                }
              });

              if (!series[AXIS] && series.optionalAxis !== AXIS) {
                error(18, true, chart);
              }
            });
          });
          fireEvent(this, 'afterBindAxes');
        },
        updateParallelArrays: function updateParallelArrays(point, i) {
          var series = point.series,
              args = arguments,
              fn = isNumber(i) ? function (key) {
            var val = key === 'y' && series.toYData ? series.toYData(point) : point[key];
            series[key + 'Data'][i] = val;
          } : function (key) {
            Array.prototype[i].apply(series[key + 'Data'], Array.prototype.slice.call(args, 2));
          };
          series.parallelArrays.forEach(fn);
        },
        hasData: function hasData() {
          return this.visible && typeof this.dataMax !== 'undefined' && typeof this.dataMin !== 'undefined' || this.visible && this.yData && this.yData.length > 0;
        },
        autoIncrement: function autoIncrement() {
          var options = this.options,
              xIncrement = this.xIncrement,
              date,
              pointInterval,
              pointIntervalUnit = options.pointIntervalUnit,
              time = this.chart.time;
          xIncrement = pick(xIncrement, options.pointStart, 0);
          this.pointInterval = pointInterval = pick(this.pointInterval, options.pointInterval, 1);

          if (pointIntervalUnit) {
            date = new time.Date(xIncrement);

            if (pointIntervalUnit === 'day') {
              time.set('Date', date, time.get('Date', date) + pointInterval);
            } else if (pointIntervalUnit === 'month') {
              time.set('Month', date, time.get('Month', date) + pointInterval);
            } else if (pointIntervalUnit === 'year') {
              time.set('FullYear', date, time.get('FullYear', date) + pointInterval);
            }

            pointInterval = date.getTime() - xIncrement;
          }

          this.xIncrement = xIncrement + pointInterval;
          return xIncrement;
        },
        setDataSortingOptions: function setDataSortingOptions() {
          var options = this.options;
          extend(this, {
            requireSorting: false,
            sorted: false,
            enabledDataSorting: true,
            allowDG: false
          });

          if (!defined(options.pointRange)) {
            options.pointRange = 1;
          }
        },
        setOptions: function setOptions(itemOptions) {
          var chart = this.chart,
              chartOptions = chart.options,
              plotOptions = chartOptions.plotOptions,
              userOptions = chart.userOptions || {},
              seriesUserOptions = merge(itemOptions),
              options,
              zones,
              zone,
              styledMode = chart.styledMode,
              e = {
            plotOptions: plotOptions,
            userOptions: seriesUserOptions
          };
          fireEvent(this, 'setOptions', e);
          var typeOptions = e.plotOptions[this.type],
              userPlotOptions = userOptions.plotOptions || {};
          this.userOptions = e.userOptions;
          options = merge(typeOptions, plotOptions.series, userOptions.plotOptions && userOptions.plotOptions[this.type], seriesUserOptions);
          this.tooltipOptions = merge(defaultOptions.tooltip, defaultOptions.plotOptions.series && defaultOptions.plotOptions.series.tooltip, defaultOptions.plotOptions[this.type].tooltip, chartOptions.tooltip.userOptions, plotOptions.series && plotOptions.series.tooltip, plotOptions[this.type].tooltip, seriesUserOptions.tooltip);
          this.stickyTracking = pick(seriesUserOptions.stickyTracking, userPlotOptions[this.type] && userPlotOptions[this.type].stickyTracking, userPlotOptions.series && userPlotOptions.series.stickyTracking, this.tooltipOptions.shared && !this.noSharedTooltip ? true : options.stickyTracking);

          if (typeOptions.marker === null) {
            delete options.marker;
          }

          this.zoneAxis = options.zoneAxis;
          zones = this.zones = (options.zones || []).slice();

          if ((options.negativeColor || options.negativeFillColor) && !options.zones) {
            zone = {
              value: options[this.zoneAxis + 'Threshold'] || options.threshold || 0,
              className: 'highcharts-negative'
            };

            if (!styledMode) {
              zone.color = options.negativeColor;
              zone.fillColor = options.negativeFillColor;
            }

            zones.push(zone);
          }

          if (zones.length) {
            if (defined(zones[zones.length - 1].value)) {
              zones.push(styledMode ? {} : {
                color: this.color,
                fillColor: this.fillColor
              });
            }
          }

          fireEvent(this, 'afterSetOptions', {
            options: options
          });
          return options;
        },
        getName: function getName() {
          return pick(this.options.name, 'Series ' + (this.index + 1));
        },
        getCyclic: function getCyclic(prop, value, defaults) {
          var i,
              chart = this.chart,
              userOptions = this.userOptions,
              indexName = prop + 'Index',
              counterName = prop + 'Counter',
              len = defaults ? defaults.length : pick(chart.options.chart[prop + 'Count'], chart[prop + 'Count']),
              setting;

          if (!value) {
            setting = pick(userOptions[indexName], userOptions['_' + indexName]);

            if (defined(setting)) {
              i = setting;
            } else {
              if (!chart.series.length) {
                chart[counterName] = 0;
              }

              userOptions['_' + indexName] = i = chart[counterName] % len;
              chart[counterName] += 1;
            }

            if (defaults) {
              value = defaults[i];
            }
          }

          if (typeof i !== 'undefined') {
            this[indexName] = i;
          }

          this[prop] = value;
        },
        getColor: function getColor() {
          if (this.chart.styledMode) {
            this.getCyclic('color');
          } else if (this.options.colorByPoint) {
            this.options.color = null;
          } else {
            this.getCyclic('color', this.options.color || defaultPlotOptions[this.type].color, this.chart.options.colors);
          }
        },
        getPointsCollection: function getPointsCollection() {
          return (this.hasGroupedData ? this.points : this.data) || [];
        },
        getSymbol: function getSymbol() {
          var seriesMarkerOption = this.options.marker;
          this.getCyclic('symbol', seriesMarkerOption.symbol, this.chart.options.symbols);
        },
        findPointIndex: function findPointIndex(optionsObject, fromIndex) {
          var id = optionsObject.id,
              x = optionsObject.x,
              oldData = this.points,
              matchingPoint,
              matchedById,
              pointIndex,
              matchKey,
              dataSorting = this.options.dataSorting;

          if (id) {
            matchingPoint = this.chart.get(id);
          } else if (this.linkedParent || this.enabledDataSorting) {
            matchKey = dataSorting && dataSorting.matchByName ? 'name' : 'index';
            matchingPoint = find(oldData, function (oldPoint) {
              return !oldPoint.touched && oldPoint[matchKey] === optionsObject[matchKey];
            });

            if (!matchingPoint) {
              return void 0;
            }
          }

          if (matchingPoint) {
            pointIndex = matchingPoint && matchingPoint.index;

            if (typeof pointIndex !== 'undefined') {
              matchedById = true;
            }
          }

          if (typeof pointIndex === 'undefined' && isNumber(x)) {
            pointIndex = this.xData.indexOf(x, fromIndex);
          }

          if (pointIndex !== -1 && typeof pointIndex !== 'undefined' && this.cropped) {
            pointIndex = pointIndex >= this.cropStart ? pointIndex - this.cropStart : pointIndex;
          }

          if (!matchedById && oldData[pointIndex] && oldData[pointIndex].touched) {
            pointIndex = void 0;
          }

          return pointIndex;
        },
        drawLegendSymbol: LegendSymbolMixin.drawLineMarker,
        updateData: function updateData(data, animation) {
          var options = this.options,
              dataSorting = options.dataSorting,
              oldData = this.points,
              pointsToAdd = [],
              hasUpdatedByKey,
              i,
              point,
              lastIndex,
              requireSorting = this.requireSorting,
              equalLength = data.length === oldData.length,
              succeeded = true;
          this.xIncrement = null;
          data.forEach(function (pointOptions, i) {
            var id,
                x,
                pointIndex,
                optionsObject = defined(pointOptions) && this.pointClass.prototype.optionsToObject.call({
              series: this
            }, pointOptions) || {};
            x = optionsObject.x;
            id = optionsObject.id;

            if (id || isNumber(x)) {
              pointIndex = this.findPointIndex(optionsObject, lastIndex);

              if (pointIndex === -1 || typeof pointIndex === 'undefined') {
                pointsToAdd.push(pointOptions);
              } else if (oldData[pointIndex] && pointOptions !== options.data[pointIndex]) {
                oldData[pointIndex].update(pointOptions, false, null, false);
                oldData[pointIndex].touched = true;

                if (requireSorting) {
                  lastIndex = pointIndex + 1;
                }
              } else if (oldData[pointIndex]) {
                oldData[pointIndex].touched = true;
              }

              if (!equalLength || i !== pointIndex || dataSorting && dataSorting.enabled || this.hasDerivedData) {
                hasUpdatedByKey = true;
              }
            } else {
              pointsToAdd.push(pointOptions);
            }
          }, this);

          if (hasUpdatedByKey) {
            i = oldData.length;

            while (i--) {
              point = oldData[i];

              if (point && !point.touched && point.remove) {
                point.remove(false, animation);
              }
            }
          } else if (equalLength && (!dataSorting || !dataSorting.enabled)) {
            data.forEach(function (point, i) {
              if (oldData[i].update && point !== oldData[i].y) {
                oldData[i].update(point, false, null, false);
              }
            });
            pointsToAdd.length = 0;
          } else {
            succeeded = false;
          }

          oldData.forEach(function (point) {
            if (point) {
              point.touched = false;
            }
          });

          if (!succeeded) {
            return false;
          }

          pointsToAdd.forEach(function (point) {
            this.addPoint(point, false, null, null, false);
          }, this);

          if (this.xIncrement === null && this.xData && this.xData.length) {
            this.xIncrement = arrayMax(this.xData);
            this.autoIncrement();
          }

          return true;
        },
        setData: function setData(data, redraw, animation, updatePoints) {
          var series = this,
              oldData = series.points,
              oldDataLength = oldData && oldData.length || 0,
              dataLength,
              options = series.options,
              chart = series.chart,
              dataSorting = options.dataSorting,
              firstPoint = null,
              xAxis = series.xAxis,
              i,
              turboThreshold = options.turboThreshold,
              pt,
              xData = this.xData,
              yData = this.yData,
              pointArrayMap = series.pointArrayMap,
              valueCount = pointArrayMap && pointArrayMap.length,
              keys = options.keys,
              indexOfX = 0,
              indexOfY = 1,
              updatedData;
          data = data || [];
          dataLength = data.length;
          redraw = pick(redraw, true);

          if (dataSorting && dataSorting.enabled) {
            data = this.sortData(data);
          }

          if (updatePoints !== false && dataLength && oldDataLength && !series.cropped && !series.hasGroupedData && series.visible && !series.isSeriesBoosting) {
            updatedData = this.updateData(data, animation);
          }

          if (!updatedData) {
            series.xIncrement = null;
            series.colorCounter = 0;
            this.parallelArrays.forEach(function (key) {
              series[key + 'Data'].length = 0;
            });

            if (turboThreshold && dataLength > turboThreshold) {
              firstPoint = series.getFirstValidPoint(data);

              if (isNumber(firstPoint)) {
                for (i = 0; i < dataLength; i++) {
                  xData[i] = this.autoIncrement();
                  yData[i] = data[i];
                }
              } else if (isArray(firstPoint)) {
                if (valueCount) {
                  for (i = 0; i < dataLength; i++) {
                    pt = data[i];
                    xData[i] = pt[0];
                    yData[i] = pt.slice(1, valueCount + 1);
                  }
                } else {
                  if (keys) {
                    indexOfX = keys.indexOf('x');
                    indexOfY = keys.indexOf('y');
                    indexOfX = indexOfX >= 0 ? indexOfX : 0;
                    indexOfY = indexOfY >= 0 ? indexOfY : 1;
                  }

                  for (i = 0; i < dataLength; i++) {
                    pt = data[i];
                    xData[i] = pt[indexOfX];
                    yData[i] = pt[indexOfY];
                  }
                }
              } else {
                error(12, false, chart);
              }
            } else {
              for (i = 0; i < dataLength; i++) {
                if (typeof data[i] !== 'undefined') {
                  pt = {
                    series: series
                  };
                  series.pointClass.prototype.applyOptions.apply(pt, [data[i]]);
                  series.updateParallelArrays(pt, i);
                }
              }
            }

            if (yData && isString(yData[0])) {
              error(14, true, chart);
            }

            series.data = [];
            series.options.data = series.userOptions.data = data;
            i = oldDataLength;

            while (i--) {
              if (oldData[i] && oldData[i].destroy) {
                oldData[i].destroy();
              }
            }

            if (xAxis) {
              xAxis.minRange = xAxis.userMinRange;
            }

            series.isDirty = chart.isDirtyBox = true;
            series.isDirtyData = !!oldData;
            animation = false;
          }

          if (options.legendType === 'point') {
            this.processData();
            this.generatePoints();
          }

          if (redraw) {
            chart.redraw(animation);
          }
        },
        sortData: function sortData(data) {
          var series = this,
              options = series.options,
              dataSorting = options.dataSorting,
              sortKey = dataSorting.sortKey || 'y',
              sortedData,
              getPointOptionsObject = function getPointOptionsObject(series, pointOptions) {
            return defined(pointOptions) && series.pointClass.prototype.optionsToObject.call({
              series: series
            }, pointOptions) || {};
          };

          data.forEach(function (pointOptions, i) {
            data[i] = getPointOptionsObject(series, pointOptions);
            data[i].index = i;
          }, this);
          sortedData = data.concat().sort(function (a, b) {
            var aValue = getNestedProperty(sortKey, a);
            var bValue = getNestedProperty(sortKey, b);
            return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
          });
          sortedData.forEach(function (point, i) {
            point.x = i;
          }, this);

          if (series.linkedSeries) {
            series.linkedSeries.forEach(function (linkedSeries) {
              var options = linkedSeries.options,
                  seriesData = options.data;

              if ((!options.dataSorting || !options.dataSorting.enabled) && seriesData) {
                seriesData.forEach(function (pointOptions, i) {
                  seriesData[i] = getPointOptionsObject(linkedSeries, pointOptions);

                  if (data[i]) {
                    seriesData[i].x = data[i].x;
                    seriesData[i].index = i;
                  }
                });
                linkedSeries.setData(seriesData, false);
              }
            });
          }

          return data;
        },
        processData: function processData(force) {
          var series = this,
              processedXData = series.xData,
              processedYData = series.yData,
              dataLength = processedXData.length,
              croppedData,
              cropStart = 0,
              cropped,
              distance,
              closestPointRange,
              xAxis = series.xAxis,
              i,
              options = series.options,
              cropThreshold = options.cropThreshold,
              getExtremesFromAll = series.getExtremesFromAll || options.getExtremesFromAll,
              isCartesian = series.isCartesian,
              xExtremes,
              val2lin = xAxis && xAxis.val2lin,
              isLog = xAxis && xAxis.isLog,
              throwOnUnsorted = series.requireSorting,
              min,
              max;

          if (isCartesian && !series.isDirty && !xAxis.isDirty && !series.yAxis.isDirty && !force) {
            return false;
          }

          if (xAxis) {
            xExtremes = xAxis.getExtremes();
            min = xExtremes.min;
            max = xExtremes.max;
          }

          if (isCartesian && series.sorted && !getExtremesFromAll && (!cropThreshold || dataLength > cropThreshold || series.forceCrop)) {
            if (processedXData[dataLength - 1] < min || processedXData[0] > max) {
              processedXData = [];
              processedYData = [];
            } else if (series.yData && (processedXData[0] < min || processedXData[dataLength - 1] > max)) {
              croppedData = this.cropData(series.xData, series.yData, min, max);
              processedXData = croppedData.xData;
              processedYData = croppedData.yData;
              cropStart = croppedData.start;
              cropped = true;
            }
          }

          i = processedXData.length || 1;

          while (--i) {
            distance = isLog ? val2lin(processedXData[i]) - val2lin(processedXData[i - 1]) : processedXData[i] - processedXData[i - 1];

            if (distance > 0 && (typeof closestPointRange === 'undefined' || distance < closestPointRange)) {
              closestPointRange = distance;
            } else if (distance < 0 && throwOnUnsorted) {
              error(15, false, series.chart);
              throwOnUnsorted = false;
            }
          }

          series.cropped = cropped;
          series.cropStart = cropStart;
          series.processedXData = processedXData;
          series.processedYData = processedYData;
          series.closestPointRange = series.basePointRange = closestPointRange;
        },
        cropData: function cropData(xData, yData, min, max, cropShoulder) {
          var dataLength = xData.length,
              cropStart = 0,
              cropEnd = dataLength,
              i,
              j;
          cropShoulder = pick(cropShoulder, this.cropShoulder);

          for (i = 0; i < dataLength; i++) {
            if (xData[i] >= min) {
              cropStart = Math.max(0, i - cropShoulder);
              break;
            }
          }

          for (j = i; j < dataLength; j++) {
            if (xData[j] > max) {
              cropEnd = j + cropShoulder;
              break;
            }
          }

          return {
            xData: xData.slice(cropStart, cropEnd),
            yData: yData.slice(cropStart, cropEnd),
            start: cropStart,
            end: cropEnd
          };
        },
        generatePoints: function generatePoints() {
          var series = this,
              options = series.options,
              dataOptions = options.data,
              data = series.data,
              dataLength,
              processedXData = series.processedXData,
              processedYData = series.processedYData,
              PointClass = series.pointClass,
              processedDataLength = processedXData.length,
              cropStart = series.cropStart || 0,
              cursor,
              hasGroupedData = series.hasGroupedData,
              keys = options.keys,
              point,
              points = [],
              i;

          if (!data && !hasGroupedData) {
            var arr = [];
            arr.length = dataOptions.length;
            data = series.data = arr;
          }

          if (keys && hasGroupedData) {
            series.options.keys = false;
          }

          for (i = 0; i < processedDataLength; i++) {
            cursor = cropStart + i;

            if (!hasGroupedData) {
              point = data[cursor];

              if (!point && typeof dataOptions[cursor] !== 'undefined') {
                data[cursor] = point = new PointClass().init(series, dataOptions[cursor], processedXData[i]);
              }
            } else {
              point = new PointClass().init(series, [processedXData[i]].concat(splat(processedYData[i])));
              point.dataGroup = series.groupMap[i];

              if (point.dataGroup.options) {
                point.options = point.dataGroup.options;
                extend(point, point.dataGroup.options);
                delete point.dataLabels;
              }
            }

            if (point) {
              point.index = cursor;
              points[i] = point;
            }
          }

          series.options.keys = keys;

          if (data && (processedDataLength !== (dataLength = data.length) || hasGroupedData)) {
            for (i = 0; i < dataLength; i++) {
              if (i === cropStart && !hasGroupedData) {
                i += processedDataLength;
              }

              if (data[i]) {
                data[i].destroyElements();
                data[i].plotX = void 0;
              }
            }
          }

          series.data = data;
          series.points = points;
          fireEvent(this, 'afterGeneratePoints');
        },
        getXExtremes: function getXExtremes(xData) {
          return {
            min: arrayMin(xData),
            max: arrayMax(xData)
          };
        },
        getExtremes: function getExtremes(yData) {
          var xAxis = this.xAxis,
              yAxis = this.yAxis,
              xData = this.processedXData || this.xData,
              yDataLength,
              activeYData = [],
              activeCounter = 0,
              xExtremes,
              xMin = 0,
              xMax = 0,
              validValue,
              withinRange,
              shoulder = this.requireSorting ? this.cropShoulder : 0,
              positiveValuesOnly = yAxis ? yAxis.positiveValuesOnly : false,
              x,
              y,
              i,
              j;
          yData = yData || this.stackedYData || this.processedYData || [];
          yDataLength = yData.length;

          if (xAxis) {
            xExtremes = xAxis.getExtremes();
            xMin = xExtremes.min;
            xMax = xExtremes.max;
          }

          for (i = 0; i < yDataLength; i++) {
            x = xData[i];
            y = yData[i];
            validValue = (isNumber(y) || isArray(y)) && (y.length || y > 0 || !positiveValuesOnly);
            withinRange = this.getExtremesFromAll || this.options.getExtremesFromAll || this.cropped || !xAxis || (xData[i + shoulder] || x) >= xMin && (xData[i - shoulder] || x) <= xMax;

            if (validValue && withinRange) {
              j = y.length;

              if (j) {
                while (j--) {
                  if (isNumber(y[j])) {
                    activeYData[activeCounter++] = y[j];
                  }
                }
              } else {
                activeYData[activeCounter++] = y;
              }
            }
          }

          this.dataMin = arrayMin(activeYData);
          this.dataMax = arrayMax(activeYData);
          fireEvent(this, 'afterGetExtremes');
        },
        getFirstValidPoint: function getFirstValidPoint(data) {
          var firstPoint = null,
              dataLength = data.length,
              i = 0;

          while (firstPoint === null && i < dataLength) {
            firstPoint = data[i];
            i++;
          }

          return firstPoint;
        },
        translate: function translate() {
          if (!this.processedXData) {
            this.processData();
          }

          this.generatePoints();
          var series = this,
              options = series.options,
              stacking = options.stacking,
              xAxis = series.xAxis,
              categories = xAxis.categories,
              enabledDataSorting = series.enabledDataSorting,
              yAxis = series.yAxis,
              points = series.points,
              dataLength = points.length,
              hasModifyValue = !!series.modifyValue,
              i,
              pointPlacement = series.pointPlacementToXValue(),
              dynamicallyPlaced = Boolean(pointPlacement),
              threshold = options.threshold,
              stackThreshold = options.startFromThreshold ? threshold : 0,
              plotX,
              lastPlotX,
              stackIndicator,
              zoneAxis = this.zoneAxis || 'y',
              closestPointRangePx = Number.MAX_VALUE;

          function limitedRange(val) {
            return clamp(val, -1e5, 1e5);
          }

          for (i = 0; i < dataLength; i++) {
            var point = points[i],
                xValue = point.x,
                yValue = point.y,
                yBottom = point.low,
                stack = stacking && yAxis.stacks[(series.negStacks && yValue < (stackThreshold ? 0 : threshold) ? '-' : '') + series.stackKey],
                pointStack,
                stackValues;

            if (yAxis.positiveValuesOnly && yValue !== null && yValue <= 0) {
              point.isNull = true;
            }

            point.plotX = plotX = correctFloat(limitedRange(xAxis.translate(xValue, 0, 0, 0, 1, pointPlacement, this.type === 'flags')));

            if (stacking && series.visible && stack && stack[xValue]) {
              stackIndicator = series.getStackIndicator(stackIndicator, xValue, series.index);

              if (!point.isNull) {
                pointStack = stack[xValue];
                stackValues = pointStack.points[stackIndicator.key];
              }
            }

            if (isArray(stackValues)) {
              yBottom = stackValues[0];
              yValue = stackValues[1];

              if (yBottom === stackThreshold && stackIndicator.key === stack[xValue].base) {
                yBottom = pick(isNumber(threshold) && threshold, yAxis.min);
              }

              if (yAxis.positiveValuesOnly && yBottom <= 0) {
                yBottom = null;
              }

              point.total = point.stackTotal = pointStack.total;
              point.percentage = pointStack.total && point.y / pointStack.total * 100;
              point.stackY = yValue;

              if (!series.irregularWidths) {
                pointStack.setOffset(series.pointXOffset || 0, series.barW || 0);
              }
            }

            point.yBottom = defined(yBottom) ? limitedRange(yAxis.translate(yBottom, 0, 1, 0, 1)) : null;

            if (hasModifyValue) {
              yValue = series.modifyValue(yValue, point);
            }

            point.plotY = typeof yValue === 'number' && yValue !== Infinity ? limitedRange(yAxis.translate(yValue, 0, 1, 0, 1)) : void 0;
            point.isInside = this.isPointInside(point);
            point.clientX = dynamicallyPlaced ? correctFloat(xAxis.translate(xValue, 0, 0, 0, 1, pointPlacement)) : plotX;
            point.negative = point[zoneAxis] < (options[zoneAxis + 'Threshold'] || threshold || 0);
            point.category = categories && typeof categories[point.x] !== 'undefined' ? categories[point.x] : point.x;

            if (!point.isNull && point.visible !== false) {
              if (typeof lastPlotX !== 'undefined') {
                closestPointRangePx = Math.min(closestPointRangePx, Math.abs(plotX - lastPlotX));
              }

              lastPlotX = plotX;
            }

            point.zone = this.zones.length && point.getZone();

            if (!point.graphic && series.group && enabledDataSorting) {
              point.isNew = true;
            }
          }

          series.closestPointRangePx = closestPointRangePx;
          fireEvent(this, 'afterTranslate');
        },
        getValidPoints: function getValidPoints(points, insideOnly, allowNull) {
          var chart = this.chart;
          return (points || this.points || []).filter(function isValidPoint(point) {
            if (insideOnly && !chart.isInsidePlot(point.plotX, point.plotY, chart.inverted)) {
              return false;
            }

            return point.visible !== false && (allowNull || !point.isNull);
          });
        },
        getClipBox: function getClipBox(animation, finalBox) {
          var series = this,
              options = series.options,
              chart = series.chart,
              inverted = chart.inverted,
              xAxis = series.xAxis,
              yAxis = xAxis && series.yAxis,
              clipBox;

          if (animation && options.clip === false && yAxis) {
            clipBox = inverted ? {
              y: -chart.chartWidth + yAxis.len + yAxis.pos,
              height: chart.chartWidth,
              width: chart.chartHeight,
              x: -chart.chartHeight + xAxis.len + xAxis.pos
            } : {
              y: -yAxis.pos,
              height: chart.chartHeight,
              width: chart.chartWidth,
              x: -xAxis.pos
            };
          } else {
            clipBox = series.clipBox || chart.clipBox;

            if (finalBox) {
              clipBox.width = chart.plotSizeX;
              clipBox.x = 0;
            }
          }

          return !finalBox ? clipBox : {
            width: clipBox.width,
            x: clipBox.x
          };
        },
        setClip: function setClip(animation) {
          var chart = this.chart,
              options = this.options,
              renderer = chart.renderer,
              inverted = chart.inverted,
              seriesClipBox = this.clipBox,
              clipBox = this.getClipBox(animation),
              sharedClipKey = this.sharedClipKey || ['_sharedClip', animation && animation.duration, animation && animation.easing, clipBox.height, options.xAxis, options.yAxis].join(','),
              clipRect = chart[sharedClipKey],
              markerClipRect = chart[sharedClipKey + 'm'];

          if (animation) {
            clipBox.width = 0;

            if (inverted) {
              clipBox.x = chart.plotHeight + (options.clip !== false ? 0 : chart.plotTop);
            }
          }

          if (!clipRect) {
            if (animation) {
              chart[sharedClipKey + 'm'] = markerClipRect = renderer.clipRect(inverted ? chart.plotSizeX + 99 : -99, inverted ? -chart.plotLeft : -chart.plotTop, 99, inverted ? chart.chartWidth : chart.chartHeight);
            }

            chart[sharedClipKey] = clipRect = renderer.clipRect(clipBox);
            clipRect.count = {
              length: 0
            };
          } else if (!chart.hasLoaded) {
            clipRect.attr(clipBox);
          }

          if (animation) {
            if (!clipRect.count[this.index]) {
              clipRect.count[this.index] = true;
              clipRect.count.length += 1;
            }
          }

          if (options.clip !== false || animation) {
            this.group.clip(animation || seriesClipBox ? clipRect : chart.clipRect);
            this.markerGroup.clip(markerClipRect);
            this.sharedClipKey = sharedClipKey;
          }

          if (!animation) {
            if (clipRect.count[this.index]) {
              delete clipRect.count[this.index];
              clipRect.count.length -= 1;
            }

            if (clipRect.count.length === 0 && sharedClipKey && chart[sharedClipKey]) {
              if (!seriesClipBox) {
                chart[sharedClipKey] = chart[sharedClipKey].destroy();
              }

              if (chart[sharedClipKey + 'm']) {
                chart[sharedClipKey + 'm'] = chart[sharedClipKey + 'm'].destroy();
              }
            }
          }
        },
        animate: function animate(init) {
          var series = this,
              chart = series.chart,
              animation = animObject(series.options.animation),
              clipRect,
              sharedClipKey,
              finalBox;

          if (!chart.hasRendered) {
            if (init) {
              series.setClip(animation);
            } else {
              sharedClipKey = this.sharedClipKey;
              clipRect = chart[sharedClipKey];
              finalBox = series.getClipBox(animation, true);

              if (clipRect) {
                clipRect.animate(finalBox, animation);
              }

              if (chart[sharedClipKey + 'm']) {
                chart[sharedClipKey + 'm'].animate({
                  width: finalBox.width + 99,
                  x: finalBox.x - (chart.inverted ? 0 : 99)
                }, animation);
              }
            }
          }
        },
        afterAnimate: function afterAnimate() {
          this.setClip();
          fireEvent(this, 'afterAnimate');
          this.finishedAnimating = true;
        },
        drawPoints: function drawPoints() {
          var series = this,
              points = series.points,
              chart = series.chart,
              i,
              point,
              graphic,
              verb,
              options = series.options,
              seriesMarkerOptions = options.marker,
              pointMarkerOptions,
              hasPointMarker,
              markerGroup = series[series.specialGroup] || series.markerGroup,
              xAxis = series.xAxis,
              markerAttribs,
              globallyEnabled = pick(seriesMarkerOptions.enabled, !xAxis || xAxis.isRadial ? true : null, series.closestPointRangePx >= seriesMarkerOptions.enabledThreshold * seriesMarkerOptions.radius);

          if (seriesMarkerOptions.enabled !== false || series._hasPointMarkers) {
            for (i = 0; i < points.length; i++) {
              point = points[i];
              graphic = point.graphic;
              verb = graphic ? 'animate' : 'attr';
              pointMarkerOptions = point.marker || {};
              hasPointMarker = !!point.marker;
              var shouldDrawMarker = (globallyEnabled && typeof pointMarkerOptions.enabled === 'undefined' || pointMarkerOptions.enabled) && !point.isNull && point.visible !== false;

              if (shouldDrawMarker) {
                var symbol = pick(pointMarkerOptions.symbol, series.symbol);
                markerAttribs = series.markerAttribs(point, point.selected && 'select');

                if (series.enabledDataSorting) {
                  point.startXPos = xAxis.reversed ? -markerAttribs.width : xAxis.width;
                }

                var isInside = point.isInside !== false;

                if (graphic) {
                  graphic[isInside ? 'show' : 'hide'](isInside).animate(markerAttribs);
                } else if (isInside && (markerAttribs.width > 0 || point.hasImage)) {
                  point.graphic = graphic = chart.renderer.symbol(symbol, markerAttribs.x, markerAttribs.y, markerAttribs.width, markerAttribs.height, hasPointMarker ? pointMarkerOptions : seriesMarkerOptions).add(markerGroup);

                  if (series.enabledDataSorting && chart.hasRendered) {
                    graphic.attr({
                      x: point.startXPos
                    });
                    verb = 'animate';
                  }
                }

                if (graphic && verb === 'animate') {
                  graphic[isInside ? 'show' : 'hide'](isInside).animate(markerAttribs);
                }

                if (graphic && !chart.styledMode) {
                  graphic[verb](series.pointAttribs(point, point.selected && 'select'));
                }

                if (graphic) {
                  graphic.addClass(point.getClassName(), true);
                }
              } else if (graphic) {
                point.graphic = graphic.destroy();
              }
            }
          }
        },
        markerAttribs: function markerAttribs(point, state) {
          var seriesMarkerOptions = this.options.marker,
              seriesStateOptions,
              pointMarkerOptions = point.marker || {},
              symbol = pointMarkerOptions.symbol || seriesMarkerOptions.symbol,
              pointStateOptions,
              radius = pick(pointMarkerOptions.radius, seriesMarkerOptions.radius),
              attribs;

          if (state) {
            seriesStateOptions = seriesMarkerOptions.states[state];
            pointStateOptions = pointMarkerOptions.states && pointMarkerOptions.states[state];
            radius = pick(pointStateOptions && pointStateOptions.radius, seriesStateOptions && seriesStateOptions.radius, radius + (seriesStateOptions && seriesStateOptions.radiusPlus || 0));
          }

          point.hasImage = symbol && symbol.indexOf('url') === 0;

          if (point.hasImage) {
            radius = 0;
          }

          attribs = {
            x: Math.floor(point.plotX) - radius,
            y: point.plotY - radius
          };

          if (radius) {
            attribs.width = attribs.height = 2 * radius;
          }

          return attribs;
        },
        pointAttribs: function pointAttribs(point, state) {
          var seriesMarkerOptions = this.options.marker,
              seriesStateOptions,
              pointOptions = point && point.options,
              pointMarkerOptions = pointOptions && pointOptions.marker || {},
              pointStateOptions,
              color = this.color,
              pointColorOption = pointOptions && pointOptions.color,
              pointColor = point && point.color,
              strokeWidth = pick(pointMarkerOptions.lineWidth, seriesMarkerOptions.lineWidth),
              zoneColor = point && point.zone && point.zone.color,
              fill,
              stroke,
              opacity = 1;
          color = pointColorOption || zoneColor || pointColor || color;
          fill = pointMarkerOptions.fillColor || seriesMarkerOptions.fillColor || color;
          stroke = pointMarkerOptions.lineColor || seriesMarkerOptions.lineColor || color;
          state = state || 'normal';

          if (state) {
            seriesStateOptions = seriesMarkerOptions.states[state];
            pointStateOptions = pointMarkerOptions.states && pointMarkerOptions.states[state] || {};
            strokeWidth = pick(pointStateOptions.lineWidth, seriesStateOptions.lineWidth, strokeWidth + pick(pointStateOptions.lineWidthPlus, seriesStateOptions.lineWidthPlus, 0));
            fill = pointStateOptions.fillColor || seriesStateOptions.fillColor || fill;
            stroke = pointStateOptions.lineColor || seriesStateOptions.lineColor || stroke;
            opacity = pick(pointStateOptions.opacity, seriesStateOptions.opacity, opacity);
          }

          return {
            'stroke': stroke,
            'stroke-width': strokeWidth,
            'fill': fill,
            'opacity': opacity
          };
        },
        destroy: function destroy(keepEventsForUpdate) {
          var series = this,
              chart = series.chart,
              issue134 = /AppleWebKit\/533/.test(win.navigator.userAgent),
              destroy,
              i,
              data = series.data || [],
              point,
              axis;
          fireEvent(series, 'destroy');
          this.removeEvents(keepEventsForUpdate);
          (series.axisTypes || []).forEach(function (AXIS) {
            axis = series[AXIS];

            if (axis && axis.series) {
              erase(axis.series, series);
              axis.isDirty = axis.forceRedraw = true;
            }
          });

          if (series.legendItem) {
            series.chart.legend.destroyItem(series);
          }

          i = data.length;

          while (i--) {
            point = data[i];

            if (point && point.destroy) {
              point.destroy();
            }
          }

          series.points = null;
          U.clearTimeout(series.animationTimeout);
          objectEach(series, function (val, prop) {
            if (val instanceof SVGElement && !val.survive) {
              destroy = issue134 && prop === 'group' ? 'hide' : 'destroy';
              val[destroy]();
            }
          });

          if (chart.hoverSeries === series) {
            chart.hoverSeries = null;
          }

          erase(chart.series, series);
          chart.orderSeries();
          objectEach(series, function (val, prop) {
            if (!keepEventsForUpdate || prop !== 'hcEvents') {
              delete series[prop];
            }
          });
        },
        getGraphPath: function getGraphPath(points, nullsAsZeroes, connectCliffs) {
          var series = this,
              options = series.options,
              step = options.step,
              reversed,
              graphPath = [],
              xMap = [],
              gap;
          points = points || series.points;
          reversed = points.reversed;

          if (reversed) {
            points.reverse();
          }

          step = {
            right: 1,
            center: 2
          }[step] || step && 3;

          if (step && reversed) {
            step = 4 - step;
          }

          points = this.getValidPoints(points, false, !(options.connectNulls && !nullsAsZeroes && !connectCliffs));
          points.forEach(function (point, i) {
            var plotX = point.plotX,
                plotY = point.plotY,
                lastPoint = points[i - 1],
                pathToPoint;

            if ((point.leftCliff || lastPoint && lastPoint.rightCliff) && !connectCliffs) {
              gap = true;
            }

            if (point.isNull && !defined(nullsAsZeroes) && i > 0) {
              gap = !options.connectNulls;
            } else if (point.isNull && !nullsAsZeroes) {
              gap = true;
            } else {
              if (i === 0 || gap) {
                pathToPoint = ['M', point.plotX, point.plotY];
              } else if (series.getPointSpline) {
                pathToPoint = series.getPointSpline(points, point, i);
              } else if (step) {
                if (step === 1) {
                  pathToPoint = ['L', lastPoint.plotX, plotY];
                } else if (step === 2) {
                  pathToPoint = ['L', (lastPoint.plotX + plotX) / 2, lastPoint.plotY, 'L', (lastPoint.plotX + plotX) / 2, plotY];
                } else {
                  pathToPoint = ['L', plotX, lastPoint.plotY];
                }

                pathToPoint.push('L', plotX, plotY);
              } else {
                pathToPoint = ['L', plotX, plotY];
              }

              xMap.push(point.x);

              if (step) {
                xMap.push(point.x);

                if (step === 2) {
                  xMap.push(point.x);
                }
              }

              graphPath.push.apply(graphPath, pathToPoint);
              gap = false;
            }
          });
          graphPath.xMap = xMap;
          series.graphPath = graphPath;
          return graphPath;
        },
        drawGraph: function drawGraph() {
          var series = this,
              options = this.options,
              graphPath = (this.gappedPath || this.getGraphPath).call(this),
              styledMode = this.chart.styledMode,
              props = [['graph', 'highcharts-graph']];

          if (!styledMode) {
            props[0].push(options.lineColor || this.color || '#cccccc', options.dashStyle);
          }

          props = series.getZonesGraphs(props);
          props.forEach(function (prop, i) {
            var graphKey = prop[0],
                graph = series[graphKey],
                verb = graph ? 'animate' : 'attr',
                attribs;

            if (graph) {
              graph.endX = series.preventGraphAnimation ? null : graphPath.xMap;
              graph.animate({
                d: graphPath
              });
            } else if (graphPath.length) {
              series[graphKey] = graph = series.chart.renderer.path(graphPath).addClass(prop[1]).attr({
                zIndex: 1
              }).add(series.group);
            }

            if (graph && !styledMode) {
              attribs = {
                'stroke': prop[2],
                'stroke-width': options.lineWidth,
                'fill': series.fillGraph && series.color || 'none'
              };

              if (prop[3]) {
                attribs.dashstyle = prop[3];
              } else if (options.linecap !== 'square') {
                attribs['stroke-linecap'] = attribs['stroke-linejoin'] = 'round';
              }

              graph[verb](attribs).shadow(i < 2 && options.shadow);
            }

            if (graph) {
              graph.startX = graphPath.xMap;
              graph.isArea = graphPath.isArea;
            }
          });
        },
        getZonesGraphs: function getZonesGraphs(props) {
          this.zones.forEach(function (zone, i) {
            var propset = ['zone-graph-' + i, 'highcharts-graph highcharts-zone-graph-' + i + ' ' + (zone.className || '')];

            if (!this.chart.styledMode) {
              propset.push(zone.color || this.color, zone.dashStyle || this.options.dashStyle);
            }

            props.push(propset);
          }, this);
          return props;
        },
        applyZones: function applyZones() {
          var series = this,
              chart = this.chart,
              renderer = chart.renderer,
              zones = this.zones,
              translatedFrom,
              translatedTo,
              clips = this.clips || [],
              clipAttr,
              graph = this.graph,
              area = this.area,
              chartSizeMax = Math.max(chart.chartWidth, chart.chartHeight),
              axis = this[(this.zoneAxis || 'y') + 'Axis'],
              extremes,
              reversed,
              inverted = chart.inverted,
              horiz,
              pxRange,
              pxPosMin,
              pxPosMax,
              ignoreZones = false;

          if (zones.length && (graph || area) && axis && typeof axis.min !== 'undefined') {
            reversed = axis.reversed;
            horiz = axis.horiz;

            if (graph && !this.showLine) {
              graph.hide();
            }

            if (area) {
              area.hide();
            }

            extremes = axis.getExtremes();
            zones.forEach(function (threshold, i) {
              translatedFrom = reversed ? horiz ? chart.plotWidth : 0 : horiz ? 0 : axis.toPixels(extremes.min) || 0;
              translatedFrom = clamp(pick(translatedTo, translatedFrom), 0, chartSizeMax);
              translatedTo = clamp(Math.round(axis.toPixels(pick(threshold.value, extremes.max), true) || 0), 0, chartSizeMax);

              if (ignoreZones) {
                translatedFrom = translatedTo = axis.toPixels(extremes.max);
              }

              pxRange = Math.abs(translatedFrom - translatedTo);
              pxPosMin = Math.min(translatedFrom, translatedTo);
              pxPosMax = Math.max(translatedFrom, translatedTo);

              if (axis.isXAxis) {
                clipAttr = {
                  x: inverted ? pxPosMax : pxPosMin,
                  y: 0,
                  width: pxRange,
                  height: chartSizeMax
                };

                if (!horiz) {
                  clipAttr.x = chart.plotHeight - clipAttr.x;
                }
              } else {
                clipAttr = {
                  x: 0,
                  y: inverted ? pxPosMax : pxPosMin,
                  width: chartSizeMax,
                  height: pxRange
                };

                if (horiz) {
                  clipAttr.y = chart.plotWidth - clipAttr.y;
                }
              }

              if (inverted && renderer.isVML) {
                if (axis.isXAxis) {
                  clipAttr = {
                    x: 0,
                    y: reversed ? pxPosMin : pxPosMax,
                    height: clipAttr.width,
                    width: chart.chartWidth
                  };
                } else {
                  clipAttr = {
                    x: clipAttr.y - chart.plotLeft - chart.spacingBox.x,
                    y: 0,
                    width: clipAttr.height,
                    height: chart.chartHeight
                  };
                }
              }

              if (clips[i]) {
                clips[i].animate(clipAttr);
              } else {
                clips[i] = renderer.clipRect(clipAttr);
              }

              if (graph) {
                series['zone-graph-' + i].clip(clips[i]);
              }

              if (area) {
                series['zone-area-' + i].clip(clips[i]);
              }

              ignoreZones = threshold.value > extremes.max;

              if (series.resetZones && translatedTo === 0) {
                translatedTo = void 0;
              }
            });
            this.clips = clips;
          } else if (series.visible) {
            if (graph) {
              graph.show(true);
            }

            if (area) {
              area.show(true);
            }
          }
        },
        invertGroups: function invertGroups(inverted) {
          var series = this,
              chart = series.chart;

          function setInvert() {
            ['group', 'markerGroup'].forEach(function (groupName) {
              if (series[groupName]) {
                if (chart.renderer.isVML) {
                  series[groupName].attr({
                    width: series.yAxis.len,
                    height: series.xAxis.len
                  });
                }

                series[groupName].width = series.yAxis.len;
                series[groupName].height = series.xAxis.len;
                series[groupName].invert(series.isRadialSeries ? false : inverted);
              }
            });
          }

          if (!series.xAxis) {
            return;
          }

          series.eventsToUnbind.push(addEvent(chart, 'resize', setInvert));
          setInvert();
          series.invertGroups = setInvert;
        },
        plotGroup: function plotGroup(prop, name, visibility, zIndex, parent) {
          var group = this[prop],
              isNew = !group;

          if (isNew) {
            this[prop] = group = this.chart.renderer.g().attr({
              zIndex: zIndex || 0.1
            }).add(parent);
          }

          group.addClass('highcharts-' + name + ' highcharts-series-' + this.index + ' highcharts-' + this.type + '-series ' + (defined(this.colorIndex) ? 'highcharts-color-' + this.colorIndex + ' ' : '') + (this.options.className || '') + (group.hasClass('highcharts-tracker') ? ' highcharts-tracker' : ''), true);
          group.attr({
            visibility: visibility
          })[isNew ? 'attr' : 'animate'](this.getPlotBox());
          return group;
        },
        getPlotBox: function getPlotBox() {
          var chart = this.chart,
              xAxis = this.xAxis,
              yAxis = this.yAxis;

          if (chart.inverted) {
            xAxis = yAxis;
            yAxis = this.xAxis;
          }

          return {
            translateX: xAxis ? xAxis.left : chart.plotLeft,
            translateY: yAxis ? yAxis.top : chart.plotTop,
            scaleX: 1,
            scaleY: 1
          };
        },
        removeEvents: function removeEvents(keepEventsForUpdate) {
          var series = this;

          if (!keepEventsForUpdate) {
            removeEvent(series);
          } else if (series.eventsToUnbind.length) {
            series.eventsToUnbind.forEach(function (unbind) {
              unbind();
            });
            series.eventsToUnbind.length = 0;
          }
        },
        render: function render() {
          var series = this,
              chart = series.chart,
              group,
              options = series.options,
              animDuration = !series.finishedAnimating && chart.renderer.isSVG && animObject(options.animation).duration,
              visibility = series.visible ? 'inherit' : 'hidden',
              zIndex = options.zIndex,
              hasRendered = series.hasRendered,
              chartSeriesGroup = chart.seriesGroup,
              inverted = chart.inverted;
          fireEvent(this, 'render');
          group = series.plotGroup('group', 'series', visibility, zIndex, chartSeriesGroup);
          series.markerGroup = series.plotGroup('markerGroup', 'markers', visibility, zIndex, chartSeriesGroup);

          if (animDuration && series.animate) {
            series.animate(true);
          }

          group.inverted = series.isCartesian || series.invertable ? inverted : false;

          if (series.drawGraph) {
            series.drawGraph();
            series.applyZones();
          }

          if (series.visible) {
            series.drawPoints();
          }

          if (series.drawDataLabels) {
            series.drawDataLabels();
          }

          if (series.redrawPoints) {
            series.redrawPoints();
          }

          if (series.drawTracker && series.options.enableMouseTracking !== false) {
            series.drawTracker();
          }

          series.invertGroups(inverted);

          if (options.clip !== false && !series.sharedClipKey && !hasRendered) {
            group.clip(chart.clipRect);
          }

          if (animDuration && series.animate) {
            series.animate();
          }

          if (!hasRendered) {
            series.animationTimeout = syncTimeout(function () {
              series.afterAnimate();
            }, animDuration || 0);
          }

          series.isDirty = false;
          series.hasRendered = true;
          fireEvent(series, 'afterRender');
        },
        redraw: function redraw() {
          var series = this,
              chart = series.chart,
              wasDirty = series.isDirty || series.isDirtyData,
              group = series.group,
              xAxis = series.xAxis,
              yAxis = series.yAxis;

          if (group) {
            if (chart.inverted) {
              group.attr({
                width: chart.plotWidth,
                height: chart.plotHeight
              });
            }

            group.animate({
              translateX: pick(xAxis && xAxis.left, chart.plotLeft),
              translateY: pick(yAxis && yAxis.top, chart.plotTop)
            });
          }

          series.translate();
          series.render();

          if (wasDirty) {
            delete this.kdTree;
          }
        },
        kdAxisArray: ['clientX', 'plotY'],
        searchPoint: function searchPoint(e, compareX) {
          var series = this,
              xAxis = series.xAxis,
              yAxis = series.yAxis,
              inverted = series.chart.inverted;
          return this.searchKDTree({
            clientX: inverted ? xAxis.len - e.chartY + xAxis.pos : e.chartX - xAxis.pos,
            plotY: inverted ? yAxis.len - e.chartX + yAxis.pos : e.chartY - yAxis.pos
          }, compareX, e);
        },
        buildKDTree: function buildKDTree(e) {
          this.buildingKdTree = true;
          var series = this,
              dimensions = series.options.findNearestPointBy.indexOf('y') > -1 ? 2 : 1;

          function _kdtree(points, depth, dimensions) {
            var axis,
                median,
                length = points && points.length;

            if (length) {
              axis = series.kdAxisArray[depth % dimensions];
              points.sort(function (a, b) {
                return a[axis] - b[axis];
              });
              median = Math.floor(length / 2);
              return {
                point: points[median],
                left: _kdtree(points.slice(0, median), depth + 1, dimensions),
                right: _kdtree(points.slice(median + 1), depth + 1, dimensions)
              };
            }
          }

          function startRecursive() {
            series.kdTree = _kdtree(series.getValidPoints(null, !series.directTouch), dimensions, dimensions);
            series.buildingKdTree = false;
          }

          delete series.kdTree;
          syncTimeout(startRecursive, series.options.kdNow || e && e.type === 'touchstart' ? 0 : 1);
        },
        searchKDTree: function searchKDTree(point, compareX, e) {
          var series = this,
              kdX = this.kdAxisArray[0],
              kdY = this.kdAxisArray[1],
              kdComparer = compareX ? 'distX' : 'dist',
              kdDimensions = series.options.findNearestPointBy.indexOf('y') > -1 ? 2 : 1;

          function setDistance(p1, p2) {
            var x = defined(p1[kdX]) && defined(p2[kdX]) ? Math.pow(p1[kdX] - p2[kdX], 2) : null,
                y = defined(p1[kdY]) && defined(p2[kdY]) ? Math.pow(p1[kdY] - p2[kdY], 2) : null,
                r = (x || 0) + (y || 0);
            p2.dist = defined(r) ? Math.sqrt(r) : Number.MAX_VALUE;
            p2.distX = defined(x) ? Math.sqrt(x) : Number.MAX_VALUE;
          }

          function _search(search, tree, depth, dimensions) {
            var point = tree.point,
                axis = series.kdAxisArray[depth % dimensions],
                tdist,
                sideA,
                sideB,
                ret = point,
                nPoint1,
                nPoint2;
            setDistance(search, point);
            tdist = search[axis] - point[axis];
            sideA = tdist < 0 ? 'left' : 'right';
            sideB = tdist < 0 ? 'right' : 'left';

            if (tree[sideA]) {
              nPoint1 = _search(search, tree[sideA], depth + 1, dimensions);
              ret = nPoint1[kdComparer] < ret[kdComparer] ? nPoint1 : point;
            }

            if (tree[sideB]) {
              if (Math.sqrt(tdist * tdist) < ret[kdComparer]) {
                nPoint2 = _search(search, tree[sideB], depth + 1, dimensions);
                ret = nPoint2[kdComparer] < ret[kdComparer] ? nPoint2 : ret;
              }
            }

            return ret;
          }

          if (!this.kdTree && !this.buildingKdTree) {
            this.buildKDTree(e);
          }

          if (this.kdTree) {
            return _search(point, this.kdTree, kdDimensions, kdDimensions);
          }
        },
        pointPlacementToXValue: function pointPlacementToXValue() {
          var _a = this,
              _b = _a.options,
              pointPlacement = _b.pointPlacement,
              pointRange = _b.pointRange,
              axis = _a.xAxis;

          var factor = pointPlacement;

          if (factor === 'between') {
            factor = axis.reversed ? -0.5 : 0.5;
          }

          return isNumber(factor) ? factor * pick(pointRange, axis.pointRange) : 0;
        },
        isPointInside: function isPointInside(point) {
          var isInside = typeof point.plotY !== 'undefined' && typeof point.plotX !== 'undefined' && point.plotY >= 0 && point.plotY <= this.yAxis.len && point.plotX >= 0 && point.plotX <= this.xAxis.len;
          return isInside;
        }
      });
    });

    _registerModule(_modules, 'parts/Stacking.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var correctFloat = U.correctFloat,
          defined = U.defined,
          destroyObjectProperties = U.destroyObjectProperties,
          format = U.format,
          objectEach = U.objectEach,
          pick = U.pick;
      var Axis = H.Axis,
          Chart = H.Chart,
          Series = H.Series;

      H.StackItem = function (axis, options, isNegative, x, stackOption) {
        var inverted = axis.chart.inverted;
        this.axis = axis;
        this.isNegative = isNegative;
        this.options = options = options || {};
        this.x = x;
        this.total = null;
        this.points = {};
        this.stack = stackOption;
        this.leftCliff = 0;
        this.rightCliff = 0;
        this.alignOptions = {
          align: options.align || (inverted ? isNegative ? 'left' : 'right' : 'center'),
          verticalAlign: options.verticalAlign || (inverted ? 'middle' : isNegative ? 'bottom' : 'top'),
          y: options.y,
          x: options.x
        };
        this.textAlign = options.textAlign || (inverted ? isNegative ? 'right' : 'left' : 'center');
      };

      H.StackItem.prototype = {
        destroy: function destroy() {
          destroyObjectProperties(this, this.axis);
        },
        render: function render(group) {
          var chart = this.axis.chart,
              options = this.options,
              formatOption = options.format,
              attr = {},
              str = formatOption ? format(formatOption, this, chart) : options.formatter.call(this);

          if (this.label) {
            this.label.attr({
              text: str,
              visibility: 'hidden'
            });
          } else {
            this.label = chart.renderer.label(str, null, null, options.shape, null, null, options.useHTML, false, 'stack-labels');
            attr = {
              text: str,
              rotation: options.rotation,
              padding: pick(options.padding, 5),
              visibility: 'hidden'
            };
            this.label.attr(attr);

            if (!chart.styledMode) {
              this.label.css(options.style);
            }

            if (!this.label.added) {
              this.label.add(group);
            }
          }

          this.label.labelrank = chart.plotHeight;
        },
        setOffset: function setOffset(xOffset, xWidth, boxBottom, boxTop, defaultX) {
          var stackItem = this,
              axis = stackItem.axis,
              chart = axis.chart,
              y = axis.translate(axis.usePercentage ? 100 : boxTop ? boxTop : stackItem.total, 0, 0, 0, 1),
              yZero = axis.translate(boxBottom ? boxBottom : 0),
              h = defined(y) && Math.abs(y - yZero),
              x = pick(defaultX, chart.xAxis[0].translate(stackItem.x)) + xOffset,
              stackBox = defined(y) && stackItem.getStackBox(chart, stackItem, x, y, xWidth, h, axis),
              label = stackItem.label,
              isNegative = stackItem.isNegative,
              isJustify = pick(stackItem.options.overflow, 'justify') === 'justify',
              textAlign = stackItem.textAlign,
              visible;

          if (label && stackBox) {
            var bBox = label.getBBox(),
                padding = label.padding,
                boxOffsetX,
                boxOffsetY;

            if (textAlign === 'left') {
              boxOffsetX = chart.inverted ? -padding : padding;
            } else if (textAlign === 'right') {
              boxOffsetX = bBox.width;
            } else {
              if (chart.inverted && textAlign === 'center') {
                boxOffsetX = bBox.width / 2;
              } else {
                boxOffsetX = chart.inverted ? isNegative ? bBox.width + padding : -padding : bBox.width / 2;
              }
            }

            boxOffsetY = chart.inverted ? bBox.height / 2 : isNegative ? -padding : bBox.height;
            stackItem.alignOptions.x = pick(stackItem.options.x, 0);
            stackItem.alignOptions.y = pick(stackItem.options.y, 0);
            stackBox.x -= boxOffsetX;
            stackBox.y -= boxOffsetY;
            label.align(stackItem.alignOptions, null, stackBox);

            if (chart.isInsidePlot(label.alignAttr.x + boxOffsetX - stackItem.alignOptions.x, label.alignAttr.y + boxOffsetY - stackItem.alignOptions.y)) {
              label.show();
            } else {
              label.alignAttr.y = -9999;
              isJustify = false;
            }

            if (isJustify) {
              Series.prototype.justifyDataLabel.call(this.axis, label, stackItem.alignOptions, label.alignAttr, bBox, stackBox);
            }

            label.attr({
              x: label.alignAttr.x,
              y: label.alignAttr.y
            });

            if (pick(!isJustify && stackItem.options.crop, true)) {
              visible = chart.isInsidePlot(label.x - padding + label.width, label.y) && chart.isInsidePlot(label.x + padding, label.y);

              if (!visible) {
                label.hide();
              }
            }
          }
        },
        getStackBox: function getStackBox(chart, stackItem, x, y, xWidth, h, axis) {
          var reversed = stackItem.axis.reversed,
              inverted = chart.inverted,
              axisPos = axis.height + axis.pos - (inverted ? chart.plotLeft : chart.plotTop),
              neg = stackItem.isNegative && !reversed || !stackItem.isNegative && reversed;
          return {
            x: inverted ? neg ? y : y - h : x,
            y: inverted ? axisPos - x - xWidth : neg ? axisPos - y - h : axisPos - y,
            width: inverted ? h : xWidth,
            height: inverted ? xWidth : h
          };
        }
      };

      Chart.prototype.getStacks = function () {
        var chart = this,
            inverted = chart.inverted;
        chart.yAxis.forEach(function (axis) {
          if (axis.stacks && axis.hasVisibleSeries) {
            axis.oldStacks = axis.stacks;
          }
        });
        chart.series.forEach(function (series) {
          var xAxisOptions = series.xAxis && series.xAxis.options || {};

          if (series.options.stacking && (series.visible === true || chart.options.chart.ignoreHiddenSeries === false)) {
            series.stackKey = [series.type, pick(series.options.stack, ''), inverted ? xAxisOptions.top : xAxisOptions.left, inverted ? xAxisOptions.height : xAxisOptions.width].join(',');
          }
        });
      };

      Axis.prototype.buildStacks = function () {
        var axisSeries = this.series,
            reversedStacks = pick(this.options.reversedStacks, true),
            len = axisSeries.length,
            actualSeries,
            i;

        if (!this.isXAxis) {
          this.usePercentage = false;
          i = len;

          while (i--) {
            actualSeries = axisSeries[reversedStacks ? i : len - i - 1];
            actualSeries.setStackedPoints();
          }

          for (i = 0; i < len; i++) {
            axisSeries[i].modifyStacks();
          }

          H.fireEvent(this, 'afterBuildStacks');
        }
      };

      Axis.prototype.renderStackTotals = function () {
        var axis = this,
            chart = axis.chart,
            renderer = chart.renderer,
            stacks = axis.stacks,
            stackTotalGroup = axis.stackTotalGroup;

        if (!stackTotalGroup) {
          axis.stackTotalGroup = stackTotalGroup = renderer.g('stack-labels').attr({
            visibility: 'visible',
            zIndex: 6
          }).add();
        }

        stackTotalGroup.translate(chart.plotLeft, chart.plotTop);
        objectEach(stacks, function (type) {
          objectEach(type, function (stack) {
            stack.render(stackTotalGroup);
          });
        });
      };

      Axis.prototype.resetStacks = function () {
        var axis = this,
            stacks = axis.stacks;

        if (!axis.isXAxis) {
          objectEach(stacks, function (type) {
            objectEach(type, function (stack, key) {
              if (stack.touched < axis.stacksTouched) {
                stack.destroy();
                delete type[key];
              } else {
                stack.total = null;
                stack.cumulative = null;
              }
            });
          });
        }
      };

      Axis.prototype.cleanStacks = function () {
        var stacks;

        if (!this.isXAxis) {
          if (this.oldStacks) {
            stacks = this.stacks = this.oldStacks;
          }

          objectEach(stacks, function (type) {
            objectEach(type, function (stack) {
              stack.cumulative = stack.total;
            });
          });
        }
      };

      Series.prototype.setStackedPoints = function () {
        if (!this.options.stacking || this.visible !== true && this.chart.options.chart.ignoreHiddenSeries !== false) {
          return;
        }

        var series = this,
            xData = series.processedXData,
            yData = series.processedYData,
            stackedYData = [],
            yDataLength = yData.length,
            seriesOptions = series.options,
            threshold = seriesOptions.threshold,
            stackThreshold = pick(seriesOptions.startFromThreshold && threshold, 0),
            stackOption = seriesOptions.stack,
            stacking = seriesOptions.stacking,
            stackKey = series.stackKey,
            negKey = '-' + stackKey,
            negStacks = series.negStacks,
            yAxis = series.yAxis,
            stacks = yAxis.stacks,
            oldStacks = yAxis.oldStacks,
            stackIndicator,
            isNegative,
            stack,
            other,
            key,
            pointKey,
            i,
            x,
            y;
        yAxis.stacksTouched += 1;

        for (i = 0; i < yDataLength; i++) {
          x = xData[i];
          y = yData[i];
          stackIndicator = series.getStackIndicator(stackIndicator, x, series.index);
          pointKey = stackIndicator.key;
          isNegative = negStacks && y < (stackThreshold ? 0 : threshold);
          key = isNegative ? negKey : stackKey;

          if (!stacks[key]) {
            stacks[key] = {};
          }

          if (!stacks[key][x]) {
            if (oldStacks[key] && oldStacks[key][x]) {
              stacks[key][x] = oldStacks[key][x];
              stacks[key][x].total = null;
            } else {
              stacks[key][x] = new H.StackItem(yAxis, yAxis.options.stackLabels, isNegative, x, stackOption);
            }
          }

          stack = stacks[key][x];

          if (y !== null) {
            stack.points[pointKey] = stack.points[series.index] = [pick(stack.cumulative, stackThreshold)];

            if (!defined(stack.cumulative)) {
              stack.base = pointKey;
            }

            stack.touched = yAxis.stacksTouched;

            if (stackIndicator.index > 0 && series.singleStacks === false) {
              stack.points[pointKey][0] = stack.points[series.index + ',' + x + ',0'][0];
            }
          } else {
            stack.points[pointKey] = stack.points[series.index] = null;
          }

          if (stacking === 'percent') {
            other = isNegative ? stackKey : negKey;

            if (negStacks && stacks[other] && stacks[other][x]) {
              other = stacks[other][x];
              stack.total = other.total = Math.max(other.total, stack.total) + Math.abs(y) || 0;
            } else {
              stack.total = correctFloat(stack.total + (Math.abs(y) || 0));
            }
          } else {
            stack.total = correctFloat(stack.total + (y || 0));
          }

          stack.cumulative = pick(stack.cumulative, stackThreshold) + (y || 0);

          if (y !== null) {
            stack.points[pointKey].push(stack.cumulative);
            stackedYData[i] = stack.cumulative;
          }
        }

        if (stacking === 'percent') {
          yAxis.usePercentage = true;
        }

        this.stackedYData = stackedYData;
        yAxis.oldStacks = {};
      };

      Series.prototype.modifyStacks = function () {
        var series = this,
            stackKey = series.stackKey,
            stacks = series.yAxis.stacks,
            processedXData = series.processedXData,
            stackIndicator,
            stacking = series.options.stacking;

        if (series[stacking + 'Stacker']) {
          [stackKey, '-' + stackKey].forEach(function (key) {
            var i = processedXData.length,
                x,
                stack,
                pointExtremes;

            while (i--) {
              x = processedXData[i];
              stackIndicator = series.getStackIndicator(stackIndicator, x, series.index, key);
              stack = stacks[key] && stacks[key][x];
              pointExtremes = stack && stack.points[stackIndicator.key];

              if (pointExtremes) {
                series[stacking + 'Stacker'](pointExtremes, stack, i);
              }
            }
          });
        }
      };

      Series.prototype.percentStacker = function (pointExtremes, stack, i) {
        var totalFactor = stack.total ? 100 / stack.total : 0;
        pointExtremes[0] = correctFloat(pointExtremes[0] * totalFactor);
        pointExtremes[1] = correctFloat(pointExtremes[1] * totalFactor);
        this.stackedYData[i] = pointExtremes[1];
      };

      Series.prototype.getStackIndicator = function (stackIndicator, x, index, key) {
        if (!defined(stackIndicator) || stackIndicator.x !== x || key && stackIndicator.key !== key) {
          stackIndicator = {
            x: x,
            index: 0,
            key: key
          };
        } else {
          stackIndicator.index++;
        }

        stackIndicator.key = [index, x, stackIndicator.index].join(',');
        return stackIndicator;
      };
    });

    _registerModule(_modules, 'parts/Dynamics.js', [_modules['parts/Globals.js'], _modules['parts/Point.js'], _modules['parts/Time.js'], _modules['parts/Utilities.js']], function (H, Point, Time, U) {
      var addEvent = U.addEvent,
          animate = U.animate,
          createElement = U.createElement,
          css = U.css,
          defined = U.defined,
          erase = U.erase,
          error = U.error,
          extend = U.extend,
          fireEvent = U.fireEvent,
          isArray = U.isArray,
          isNumber = U.isNumber,
          isObject = U.isObject,
          isString = U.isString,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick,
          relativeLength = U.relativeLength,
          setAnimation = U.setAnimation,
          splat = U.splat;
      var Axis = H.Axis,
          Chart = H.Chart,
          Series = H.Series,
          seriesTypes = H.seriesTypes;

      H.cleanRecursively = function (newer, older) {
        var result = {};
        objectEach(newer, function (val, key) {
          var ob;

          if (isObject(newer[key], true) && !newer.nodeType && older[key]) {
            ob = H.cleanRecursively(newer[key], older[key]);

            if (Object.keys(ob).length) {
              result[key] = ob;
            }
          } else if (isObject(newer[key]) || newer[key] !== older[key]) {
            result[key] = newer[key];
          }
        });
        return result;
      };

      extend(Chart.prototype, {
        addSeries: function addSeries(options, redraw, animation) {
          var series,
              chart = this;

          if (options) {
            redraw = pick(redraw, true);
            fireEvent(chart, 'addSeries', {
              options: options
            }, function () {
              series = chart.initSeries(options);
              chart.isDirtyLegend = true;
              chart.linkSeries();

              if (series.enabledDataSorting) {
                series.setData(options.data, false);
              }

              fireEvent(chart, 'afterAddSeries', {
                series: series
              });

              if (redraw) {
                chart.redraw(animation);
              }
            });
          }

          return series;
        },
        addAxis: function addAxis(options, isX, redraw, animation) {
          return this.createAxis(isX ? 'xAxis' : 'yAxis', {
            axis: options,
            redraw: redraw,
            animation: animation
          });
        },
        addColorAxis: function addColorAxis(options, redraw, animation) {
          return this.createAxis('colorAxis', {
            axis: options,
            redraw: redraw,
            animation: animation
          });
        },
        createAxis: function createAxis(type, options) {
          var chartOptions = this.options,
              isColorAxis = type === 'colorAxis',
              axisOptions = options.axis,
              redraw = options.redraw,
              animation = options.animation,
              userOptions = merge(axisOptions, {
            index: this[type].length,
            isX: type === 'xAxis'
          }),
              axis;

          if (isColorAxis) {
            axis = new H.ColorAxis(this, userOptions);
          } else {
            axis = new Axis(this, userOptions);
          }

          chartOptions[type] = splat(chartOptions[type] || {});
          chartOptions[type].push(userOptions);

          if (isColorAxis) {
            this.isDirtyLegend = true;
            this.axes.forEach(function (axis) {
              axis.series = [];
            });
            this.series.forEach(function (series) {
              series.bindAxes();
              series.isDirtyData = true;
            });
          }

          if (pick(redraw, true)) {
            this.redraw(animation);
          }

          return axis;
        },
        showLoading: function showLoading(str) {
          var chart = this,
              options = chart.options,
              loadingDiv = chart.loadingDiv,
              loadingOptions = options.loading,
              setLoadingSize = function setLoadingSize() {
            if (loadingDiv) {
              css(loadingDiv, {
                left: chart.plotLeft + 'px',
                top: chart.plotTop + 'px',
                width: chart.plotWidth + 'px',
                height: chart.plotHeight + 'px'
              });
            }
          };

          if (!loadingDiv) {
            chart.loadingDiv = loadingDiv = createElement('div', {
              className: 'highcharts-loading highcharts-loading-hidden'
            }, null, chart.container);
            chart.loadingSpan = createElement('span', {
              className: 'highcharts-loading-inner'
            }, null, loadingDiv);
            addEvent(chart, 'redraw', setLoadingSize);
          }

          loadingDiv.className = 'highcharts-loading';
          chart.loadingSpan.innerHTML = pick(str, options.lang.loading, '');

          if (!chart.styledMode) {
            css(loadingDiv, extend(loadingOptions.style, {
              zIndex: 10
            }));
            css(chart.loadingSpan, loadingOptions.labelStyle);

            if (!chart.loadingShown) {
              css(loadingDiv, {
                opacity: 0,
                display: ''
              });
              animate(loadingDiv, {
                opacity: loadingOptions.style.opacity || 0.5
              }, {
                duration: loadingOptions.showDuration || 0
              });
            }
          }

          chart.loadingShown = true;
          setLoadingSize();
        },
        hideLoading: function hideLoading() {
          var options = this.options,
              loadingDiv = this.loadingDiv;

          if (loadingDiv) {
            loadingDiv.className = 'highcharts-loading highcharts-loading-hidden';

            if (!this.styledMode) {
              animate(loadingDiv, {
                opacity: 0
              }, {
                duration: options.loading.hideDuration || 100,
                complete: function complete() {
                  css(loadingDiv, {
                    display: 'none'
                  });
                }
              });
            }
          }

          this.loadingShown = false;
        },
        propsRequireDirtyBox: ['backgroundColor', 'borderColor', 'borderWidth', 'borderRadius', 'plotBackgroundColor', 'plotBackgroundImage', 'plotBorderColor', 'plotBorderWidth', 'plotShadow', 'shadow'],
        propsRequireReflow: ['margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft', 'spacing', 'spacingTop', 'spacingRight', 'spacingBottom', 'spacingLeft'],
        propsRequireUpdateSeries: ['chart.inverted', 'chart.polar', 'chart.ignoreHiddenSeries', 'chart.type', 'colors', 'plotOptions', 'time', 'tooltip'],
        collectionsWithUpdate: ['xAxis', 'yAxis', 'zAxis', 'series'],
        update: function update(options, redraw, oneToOne, animation) {
          var chart = this,
              adders = {
            credits: 'addCredits',
            title: 'setTitle',
            subtitle: 'setSubtitle',
            caption: 'setCaption'
          },
              optionsChart,
              updateAllAxes,
              updateAllSeries,
              newWidth,
              newHeight,
              runSetSize,
              isResponsiveOptions = options.isResponsiveOptions,
              itemsForRemoval = [];
          fireEvent(chart, 'update', {
            options: options
          });

          if (!isResponsiveOptions) {
            chart.setResponsive(false, true);
          }

          options = H.cleanRecursively(options, chart.options);
          merge(true, chart.userOptions, options);
          optionsChart = options.chart;

          if (optionsChart) {
            merge(true, chart.options.chart, optionsChart);

            if ('className' in optionsChart) {
              chart.setClassName(optionsChart.className);
            }

            if ('reflow' in optionsChart) {
              chart.setReflow(optionsChart.reflow);
            }

            if ('inverted' in optionsChart || 'polar' in optionsChart || 'type' in optionsChart) {
              chart.propFromSeries();
              updateAllAxes = true;
            }

            if ('alignTicks' in optionsChart) {
              updateAllAxes = true;
            }

            objectEach(optionsChart, function (val, key) {
              if (chart.propsRequireUpdateSeries.indexOf('chart.' + key) !== -1) {
                updateAllSeries = true;
              }

              if (chart.propsRequireDirtyBox.indexOf(key) !== -1) {
                chart.isDirtyBox = true;
              }

              if (!isResponsiveOptions && chart.propsRequireReflow.indexOf(key) !== -1) {
                runSetSize = true;
              }
            });

            if (!chart.styledMode && 'style' in optionsChart) {
              chart.renderer.setStyle(optionsChart.style);
            }
          }

          if (!chart.styledMode && options.colors) {
            this.options.colors = options.colors;
          }

          if (options.plotOptions) {
            merge(true, this.options.plotOptions, options.plotOptions);
          }

          if (options.time && this.time === H.time) {
            this.time = new Time(options.time);
          }

          objectEach(options, function (val, key) {
            if (chart[key] && typeof chart[key].update === 'function') {
              chart[key].update(val, false);
            } else if (typeof chart[adders[key]] === 'function') {
              chart[adders[key]](val);
            }

            if (key !== 'chart' && chart.propsRequireUpdateSeries.indexOf(key) !== -1) {
              updateAllSeries = true;
            }
          });
          this.collectionsWithUpdate.forEach(function (coll) {
            var indexMap;

            if (options[coll]) {
              if (coll === 'series') {
                indexMap = [];
                chart[coll].forEach(function (s, i) {
                  if (!s.options.isInternal) {
                    indexMap.push(pick(s.options.index, i));
                  }
                });
              }

              splat(options[coll]).forEach(function (newOptions, i) {
                var item = defined(newOptions.id) && chart.get(newOptions.id) || chart[coll][indexMap ? indexMap[i] : i];

                if (item && item.coll === coll) {
                  item.update(newOptions, false);

                  if (oneToOne) {
                    item.touched = true;
                  }
                }

                if (!item && oneToOne && chart.collectionsWithInit[coll]) {
                  chart.collectionsWithInit[coll][0].apply(chart, [newOptions].concat(chart.collectionsWithInit[coll][1] || []).concat([false])).touched = true;
                }
              });

              if (oneToOne) {
                chart[coll].forEach(function (item) {
                  if (!item.touched && !item.options.isInternal) {
                    itemsForRemoval.push(item);
                  } else {
                    delete item.touched;
                  }
                });
              }
            }
          });
          itemsForRemoval.forEach(function (item) {
            if (item.remove) {
              item.remove(false);
            }
          });

          if (updateAllAxes) {
            chart.axes.forEach(function (axis) {
              axis.update({}, false);
            });
          }

          if (updateAllSeries) {
            chart.getSeriesOrderByLinks().forEach(function (series) {
              if (series.chart) {
                series.update({}, false);
              }
            }, this);
          }

          if (options.loading) {
            merge(true, chart.options.loading, options.loading);
          }

          newWidth = optionsChart && optionsChart.width;
          newHeight = optionsChart && optionsChart.height;

          if (isString(newHeight)) {
            newHeight = relativeLength(newHeight, newWidth || chart.chartWidth);
          }

          if (runSetSize || isNumber(newWidth) && newWidth !== chart.chartWidth || isNumber(newHeight) && newHeight !== chart.chartHeight) {
            chart.setSize(newWidth, newHeight, animation);
          } else if (pick(redraw, true)) {
            chart.redraw(animation);
          }

          fireEvent(chart, 'afterUpdate', {
            options: options,
            redraw: redraw,
            animation: animation
          });
        },
        setSubtitle: function setSubtitle(options, redraw) {
          this.applyDescription('subtitle', options);
          this.layOutTitles(redraw);
        },
        setCaption: function setCaption(options, redraw) {
          this.applyDescription('caption', options);
          this.layOutTitles(redraw);
        }
      });
      Chart.prototype.collectionsWithInit = {
        xAxis: [Chart.prototype.addAxis, [true]],
        yAxis: [Chart.prototype.addAxis, [false]],
        series: [Chart.prototype.addSeries]
      };
      extend(Point.prototype, {
        update: function update(options, redraw, animation, runEvent) {
          var point = this,
              series = point.series,
              graphic = point.graphic,
              i,
              chart = series.chart,
              seriesOptions = series.options;
          redraw = pick(redraw, true);

          function update() {
            point.applyOptions(options);
            var hasDummyGraphic = graphic && point.hasDummyGraphic;
            var shouldDestroyGraphic = point.y === null ? !hasDummyGraphic : hasDummyGraphic;

            if (graphic && shouldDestroyGraphic) {
              point.graphic = graphic.destroy();
              delete point.hasDummyGraphic;
            }

            if (isObject(options, true)) {
              if (graphic && graphic.element) {
                if (options && options.marker && typeof options.marker.symbol !== 'undefined') {
                  point.graphic = graphic.destroy();
                }
              }

              if (options && options.dataLabels && point.dataLabel) {
                point.dataLabel = point.dataLabel.destroy();
              }

              if (point.connector) {
                point.connector = point.connector.destroy();
              }
            }

            i = point.index;
            series.updateParallelArrays(point, i);
            seriesOptions.data[i] = isObject(seriesOptions.data[i], true) || isObject(options, true) ? point.options : pick(options, seriesOptions.data[i]);
            series.isDirty = series.isDirtyData = true;

            if (!series.fixedBox && series.hasCartesianSeries) {
              chart.isDirtyBox = true;
            }

            if (seriesOptions.legendType === 'point') {
              chart.isDirtyLegend = true;
            }

            if (redraw) {
              chart.redraw(animation);
            }
          }

          if (runEvent === false) {
            update();
          } else {
            point.firePointEvent('update', {
              options: options
            }, update);
          }
        },
        remove: function remove(redraw, animation) {
          this.series.removePoint(this.series.data.indexOf(this), redraw, animation);
        }
      });
      extend(Series.prototype, {
        addPoint: function addPoint(options, redraw, shift, animation, withEvent) {
          var series = this,
              seriesOptions = series.options,
              data = series.data,
              chart = series.chart,
              xAxis = series.xAxis,
              names = xAxis && xAxis.hasNames && xAxis.names,
              dataOptions = seriesOptions.data,
              point,
              xData = series.xData,
              isInTheMiddle,
              i,
              x;
          redraw = pick(redraw, true);
          point = {
            series: series
          };
          series.pointClass.prototype.applyOptions.apply(point, [options]);
          x = point.x;
          i = xData.length;

          if (series.requireSorting && x < xData[i - 1]) {
            isInTheMiddle = true;

            while (i && xData[i - 1] > x) {
              i--;
            }
          }

          series.updateParallelArrays(point, 'splice', i, 0, 0);
          series.updateParallelArrays(point, i);

          if (names && point.name) {
            names[x] = point.name;
          }

          dataOptions.splice(i, 0, options);

          if (isInTheMiddle) {
            series.data.splice(i, 0, null);
            series.processData();
          }

          if (seriesOptions.legendType === 'point') {
            series.generatePoints();
          }

          if (shift) {
            if (data[0] && data[0].remove) {
              data[0].remove(false);
            } else {
              data.shift();
              series.updateParallelArrays(point, 'shift');
              dataOptions.shift();
            }
          }

          if (withEvent !== false) {
            fireEvent(series, 'addPoint', {
              point: point
            });
          }

          series.isDirty = true;
          series.isDirtyData = true;

          if (redraw) {
            chart.redraw(animation);
          }
        },
        removePoint: function removePoint(i, redraw, animation) {
          var series = this,
              data = series.data,
              point = data[i],
              points = series.points,
              chart = series.chart,
              remove = function remove() {
            if (points && points.length === data.length) {
              points.splice(i, 1);
            }

            data.splice(i, 1);
            series.options.data.splice(i, 1);
            series.updateParallelArrays(point || {
              series: series
            }, 'splice', i, 1);

            if (point) {
              point.destroy();
            }

            series.isDirty = true;
            series.isDirtyData = true;

            if (redraw) {
              chart.redraw();
            }
          };

          setAnimation(animation, chart);
          redraw = pick(redraw, true);

          if (point) {
            point.firePointEvent('remove', null, remove);
          } else {
            remove();
          }
        },
        remove: function remove(redraw, animation, withEvent, keepEvents) {
          var series = this,
              chart = series.chart;

          function remove() {
            series.destroy(keepEvents);
            series.remove = null;
            chart.isDirtyLegend = chart.isDirtyBox = true;
            chart.linkSeries();

            if (pick(redraw, true)) {
              chart.redraw(animation);
            }
          }

          if (withEvent !== false) {
            fireEvent(series, 'remove', null, remove);
          } else {
            remove();
          }
        },
        update: function update(options, redraw) {
          options = H.cleanRecursively(options, this.userOptions);
          fireEvent(this, 'update', {
            options: options
          });
          var series = this,
              chart = series.chart,
              oldOptions = series.userOptions,
              seriesOptions,
              initialType = series.initialType || series.type,
              newType = options.type || oldOptions.type || chart.options.chart.type,
              keepPoints = !(this.hasDerivedData || options.dataGrouping || newType && newType !== this.type || typeof options.pointStart !== 'undefined' || options.pointInterval || options.pointIntervalUnit || options.keys),
              initialSeriesProto = seriesTypes[initialType].prototype,
              n,
              groups = ['group', 'markerGroup', 'dataLabelsGroup', 'transformGroup'],
              preserve = ['eventOptions', 'navigatorSeries', 'baseSeries'],
              animation = series.finishedAnimating && {
            animation: false
          },
              kinds = {};

          if (keepPoints) {
            preserve.push('data', 'isDirtyData', 'points', 'processedXData', 'processedYData', 'xIncrement', '_hasPointMarkers', '_hasPointLabels', 'mapMap', 'mapData', 'minY', 'maxY', 'minX', 'maxX');

            if (options.visible !== false) {
              preserve.push('area', 'graph');
            }

            series.parallelArrays.forEach(function (key) {
              preserve.push(key + 'Data');
            });

            if (options.data) {
              if (options.dataSorting) {
                extend(series.options.dataSorting, options.dataSorting);
              }

              this.setData(options.data, false);
            }
          }

          options = merge(oldOptions, animation, {
            index: typeof oldOptions.index === 'undefined' ? series.index : oldOptions.index,
            pointStart: pick(oldOptions.pointStart, series.xData[0])
          }, !keepPoints && {
            data: series.options.data
          }, options);

          if (keepPoints && options.data) {
            options.data = series.options.data;
          }

          preserve = groups.concat(preserve);
          preserve.forEach(function (prop) {
            preserve[prop] = series[prop];
            delete series[prop];
          });
          series.remove(false, null, false, true);

          for (n in initialSeriesProto) {
            series[n] = void 0;
          }

          if (seriesTypes[newType || initialType]) {
            extend(series, seriesTypes[newType || initialType].prototype);
          } else {
            error(17, true, chart, {
              missingModuleFor: newType || initialType
            });
          }

          preserve.forEach(function (prop) {
            series[prop] = preserve[prop];
          });
          series.init(chart, options);

          if (keepPoints && this.points) {
            seriesOptions = series.options;

            if (seriesOptions.visible === false) {
              kinds.graphic = 1;
              kinds.dataLabel = 1;
            } else if (!series._hasPointLabels) {
              var marker = seriesOptions.marker,
                  dataLabels = seriesOptions.dataLabels;

              if (marker && (marker.enabled === false || 'symbol' in marker)) {
                kinds.graphic = 1;
              }

              if (dataLabels && dataLabels.enabled === false) {
                kinds.dataLabel = 1;
              }
            }

            this.points.forEach(function (point) {
              if (point && point.series) {
                point.resolveColor();

                if (Object.keys(kinds).length) {
                  point.destroyElements(kinds);
                }

                if (seriesOptions.showInLegend === false && point.legendItem) {
                  chart.legend.destroyItem(point);
                }
              }
            }, this);
          }

          if (options.zIndex !== oldOptions.zIndex) {
            groups.forEach(function (groupName) {
              if (series[groupName]) {
                series[groupName].attr({
                  zIndex: options.zIndex
                });
              }
            });
          }

          series.initialType = initialType;
          chart.linkSeries();
          fireEvent(this, 'afterUpdate');

          if (pick(redraw, true)) {
            chart.redraw(keepPoints ? void 0 : false);
          }
        },
        setName: function setName(name) {
          this.name = this.options.name = this.userOptions.name = name;
          this.chart.isDirtyLegend = true;
        }
      });
      extend(Axis.prototype, {
        update: function update(options, redraw) {
          var chart = this.chart,
              newEvents = options && options.events || {};
          options = merge(this.userOptions, options);

          if (chart.options[this.coll].indexOf) {
            chart.options[this.coll][chart.options[this.coll].indexOf(this.userOptions)] = options;
          }

          objectEach(chart.options[this.coll].events, function (fn, ev) {
            if (typeof newEvents[ev] === 'undefined') {
              newEvents[ev] = void 0;
            }
          });
          this.destroy(true);
          this.init(chart, extend(options, {
            events: newEvents
          }));
          chart.isDirtyBox = true;

          if (pick(redraw, true)) {
            chart.redraw();
          }
        },
        remove: function remove(redraw) {
          var chart = this.chart,
              key = this.coll,
              axisSeries = this.series,
              i = axisSeries.length;

          while (i--) {
            if (axisSeries[i]) {
              axisSeries[i].remove(false);
            }
          }

          erase(chart.axes, this);
          erase(chart[key], this);

          if (isArray(chart.options[key])) {
            chart.options[key].splice(this.options.index, 1);
          } else {
            delete chart.options[key];
          }

          chart[key].forEach(function (axis, i) {
            axis.options.index = axis.userOptions.index = i;
          });
          this.destroy();
          chart.isDirtyBox = true;

          if (pick(redraw, true)) {
            chart.redraw();
          }
        },
        setTitle: function setTitle(titleOptions, redraw) {
          this.update({
            title: titleOptions
          }, redraw);
        },
        setCategories: function setCategories(categories, redraw) {
          this.update({
            categories: categories
          }, redraw);
        }
      });
    });

    _registerModule(_modules, 'parts/AreaSeries.js', [_modules['parts/Globals.js'], _modules['parts/Color.js'], _modules['mixins/legend-symbol.js'], _modules['parts/Utilities.js']], function (H, Color, LegendSymbolMixin, U) {
      var color = Color.parse;
      var objectEach = U.objectEach,
          pick = U.pick,
          seriesType = U.seriesType;
      var Series = H.Series;
      seriesType('area', 'line', {
        softThreshold: false,
        threshold: 0
      }, {
        singleStacks: false,
        getStackPoints: function getStackPoints(points) {
          var series = this,
              segment = [],
              keys = [],
              xAxis = this.xAxis,
              yAxis = this.yAxis,
              stack = yAxis.stacks[this.stackKey],
              pointMap = {},
              seriesIndex = series.index,
              yAxisSeries = yAxis.series,
              seriesLength = yAxisSeries.length,
              visibleSeries,
              upOrDown = pick(yAxis.options.reversedStacks, true) ? 1 : -1,
              i;
          points = points || this.points;

          if (this.options.stacking) {
            for (i = 0; i < points.length; i++) {
              points[i].leftNull = points[i].rightNull = void 0;
              pointMap[points[i].x] = points[i];
            }

            objectEach(stack, function (stackX, x) {
              if (stackX.total !== null) {
                keys.push(x);
              }
            });
            keys.sort(function (a, b) {
              return a - b;
            });
            visibleSeries = yAxisSeries.map(function (s) {
              return s.visible;
            });
            keys.forEach(function (x, idx) {
              var y = 0,
                  stackPoint,
                  stackedValues;

              if (pointMap[x] && !pointMap[x].isNull) {
                segment.push(pointMap[x]);
                [-1, 1].forEach(function (direction) {
                  var nullName = direction === 1 ? 'rightNull' : 'leftNull',
                      cliffName = direction === 1 ? 'rightCliff' : 'leftCliff',
                      cliff = 0,
                      otherStack = stack[keys[idx + direction]];

                  if (otherStack) {
                    i = seriesIndex;

                    while (i >= 0 && i < seriesLength) {
                      stackPoint = otherStack.points[i];

                      if (!stackPoint) {
                        if (i === seriesIndex) {
                          pointMap[x][nullName] = true;
                        } else if (visibleSeries[i]) {
                          stackedValues = stack[x].points[i];

                          if (stackedValues) {
                            cliff -= stackedValues[1] - stackedValues[0];
                          }
                        }
                      }

                      i += upOrDown;
                    }
                  }

                  pointMap[x][cliffName] = cliff;
                });
              } else {
                i = seriesIndex;

                while (i >= 0 && i < seriesLength) {
                  stackPoint = stack[x].points[i];

                  if (stackPoint) {
                    y = stackPoint[1];
                    break;
                  }

                  i += upOrDown;
                }

                y = yAxis.translate(y, 0, 1, 0, 1);
                segment.push({
                  isNull: true,
                  plotX: xAxis.translate(x, 0, 0, 0, 1),
                  x: x,
                  plotY: y,
                  yBottom: y
                });
              }
            });
          }

          return segment;
        },
        getGraphPath: function getGraphPath(points) {
          var getGraphPath = Series.prototype.getGraphPath,
              graphPath,
              options = this.options,
              stacking = options.stacking,
              yAxis = this.yAxis,
              topPath,
              bottomPath,
              bottomPoints = [],
              graphPoints = [],
              seriesIndex = this.index,
              i,
              areaPath,
              plotX,
              stacks = yAxis.stacks[this.stackKey],
              threshold = options.threshold,
              translatedThreshold = Math.round(yAxis.getThreshold(options.threshold)),
              isNull,
              yBottom,
              connectNulls = pick(options.connectNulls, stacking === 'percent'),
              addDummyPoints = function addDummyPoints(i, otherI, side) {
            var point = points[i],
                stackedValues = stacking && stacks[point.x].points[seriesIndex],
                nullVal = point[side + 'Null'] || 0,
                cliffVal = point[side + 'Cliff'] || 0,
                top,
                bottom,
                isNull = true;

            if (cliffVal || nullVal) {
              top = (nullVal ? stackedValues[0] : stackedValues[1]) + cliffVal;
              bottom = stackedValues[0] + cliffVal;
              isNull = !!nullVal;
            } else if (!stacking && points[otherI] && points[otherI].isNull) {
              top = bottom = threshold;
            }

            if (typeof top !== 'undefined') {
              graphPoints.push({
                plotX: plotX,
                plotY: top === null ? translatedThreshold : yAxis.getThreshold(top),
                isNull: isNull,
                isCliff: true
              });
              bottomPoints.push({
                plotX: plotX,
                plotY: bottom === null ? translatedThreshold : yAxis.getThreshold(bottom),
                doCurve: false
              });
            }
          };

          points = points || this.points;

          if (stacking) {
            points = this.getStackPoints(points);
          }

          for (i = 0; i < points.length; i++) {
            if (!stacking) {
              points[i].leftCliff = points[i].rightCliff = points[i].leftNull = points[i].rightNull = void 0;
            }

            isNull = points[i].isNull;
            plotX = pick(points[i].rectPlotX, points[i].plotX);
            yBottom = pick(points[i].yBottom, translatedThreshold);

            if (!isNull || connectNulls) {
              if (!connectNulls) {
                addDummyPoints(i, i - 1, 'left');
              }

              if (!(isNull && !stacking && connectNulls)) {
                graphPoints.push(points[i]);
                bottomPoints.push({
                  x: i,
                  plotX: plotX,
                  plotY: yBottom
                });
              }

              if (!connectNulls) {
                addDummyPoints(i, i + 1, 'right');
              }
            }
          }

          topPath = getGraphPath.call(this, graphPoints, true, true);
          bottomPoints.reversed = true;
          bottomPath = getGraphPath.call(this, bottomPoints, true, true);

          if (bottomPath.length) {
            bottomPath[0] = 'L';
          }

          areaPath = topPath.concat(bottomPath);
          graphPath = getGraphPath.call(this, graphPoints, false, connectNulls);
          areaPath.xMap = topPath.xMap;
          this.areaPath = areaPath;
          return graphPath;
        },
        drawGraph: function drawGraph() {
          this.areaPath = [];
          Series.prototype.drawGraph.apply(this);
          var series = this,
              areaPath = this.areaPath,
              options = this.options,
              zones = this.zones,
              props = [['area', 'highcharts-area', this.color, options.fillColor]];
          zones.forEach(function (zone, i) {
            props.push(['zone-area-' + i, 'highcharts-area highcharts-zone-area-' + i + ' ' + zone.className, zone.color || series.color, zone.fillColor || options.fillColor]);
          });
          props.forEach(function (prop) {
            var areaKey = prop[0],
                area = series[areaKey],
                verb = area ? 'animate' : 'attr',
                attribs = {};

            if (area) {
              area.endX = series.preventGraphAnimation ? null : areaPath.xMap;
              area.animate({
                d: areaPath
              });
            } else {
              attribs.zIndex = 0;
              area = series[areaKey] = series.chart.renderer.path(areaPath).addClass(prop[1]).add(series.group);
              area.isArea = true;
            }

            if (!series.chart.styledMode) {
              attribs.fill = pick(prop[3], color(prop[2]).setOpacity(pick(options.fillOpacity, 0.75)).get());
            }

            area[verb](attribs);
            area.startX = areaPath.xMap;
            area.shiftUnit = options.step ? 2 : 1;
          });
        },
        drawLegendSymbol: LegendSymbolMixin.drawRectangle
      });
    });

    _registerModule(_modules, 'parts/SplineSeries.js', [_modules['parts/Utilities.js']], function (U) {
      var pick = U.pick,
          seriesType = U.seriesType;
      seriesType('spline', 'line', {}, {
        getPointSpline: function getPointSpline(points, point, i) {
          var smoothing = 1.5,
              denom = smoothing + 1,
              plotX = point.plotX,
              plotY = point.plotY,
              lastPoint = points[i - 1],
              nextPoint = points[i + 1],
              leftContX,
              leftContY,
              rightContX,
              rightContY,
              ret;

          function doCurve(otherPoint) {
            return otherPoint && !otherPoint.isNull && otherPoint.doCurve !== false && !point.isCliff;
          }

          if (doCurve(lastPoint) && doCurve(nextPoint)) {
            var lastX = lastPoint.plotX,
                lastY = lastPoint.plotY,
                nextX = nextPoint.plotX,
                nextY = nextPoint.plotY,
                correction = 0;
            leftContX = (smoothing * plotX + lastX) / denom;
            leftContY = (smoothing * plotY + lastY) / denom;
            rightContX = (smoothing * plotX + nextX) / denom;
            rightContY = (smoothing * plotY + nextY) / denom;

            if (rightContX !== leftContX) {
              correction = (rightContY - leftContY) * (rightContX - plotX) / (rightContX - leftContX) + plotY - rightContY;
            }

            leftContY += correction;
            rightContY += correction;

            if (leftContY > lastY && leftContY > plotY) {
              leftContY = Math.max(lastY, plotY);
              rightContY = 2 * plotY - leftContY;
            } else if (leftContY < lastY && leftContY < plotY) {
              leftContY = Math.min(lastY, plotY);
              rightContY = 2 * plotY - leftContY;
            }

            if (rightContY > nextY && rightContY > plotY) {
              rightContY = Math.max(nextY, plotY);
              leftContY = 2 * plotY - rightContY;
            } else if (rightContY < nextY && rightContY < plotY) {
              rightContY = Math.min(nextY, plotY);
              leftContY = 2 * plotY - rightContY;
            }

            point.rightContX = rightContX;
            point.rightContY = rightContY;
          }

          ret = ['C', pick(lastPoint.rightContX, lastPoint.plotX), pick(lastPoint.rightContY, lastPoint.plotY), pick(leftContX, plotX), pick(leftContY, plotY), plotX, plotY];
          lastPoint.rightContX = lastPoint.rightContY = null;
          return ret;
        }
      });
    });

    _registerModule(_modules, 'parts/AreaSplineSeries.js', [_modules['parts/Globals.js'], _modules['mixins/legend-symbol.js'], _modules['parts/Utilities.js']], function (H, LegendSymbolMixin, U) {
      var seriesType = U.seriesType;
      var areaProto = H.seriesTypes.area.prototype,
          defaultPlotOptions = H.defaultPlotOptions;
      seriesType('areaspline', 'spline', defaultPlotOptions.area, {
        getStackPoints: areaProto.getStackPoints,
        getGraphPath: areaProto.getGraphPath,
        drawGraph: areaProto.drawGraph,
        drawLegendSymbol: LegendSymbolMixin.drawRectangle
      });
    });

    _registerModule(_modules, 'parts/ColumnSeries.js', [_modules['parts/Globals.js'], _modules['parts/Color.js'], _modules['mixins/legend-symbol.js'], _modules['parts/Utilities.js']], function (H, Color, LegendSymbolMixin, U) {

      var color = Color.parse;
      var animObject = U.animObject,
          clamp = U.clamp,
          defined = U.defined,
          extend = U.extend,
          isNumber = U.isNumber,
          merge = U.merge,
          pick = U.pick,
          seriesType = U.seriesType;
      var noop = H.noop,
          Series = H.Series;
      seriesType('column', 'line', {
        borderRadius: 0,
        crisp: true,
        groupPadding: 0.2,
        marker: null,
        pointPadding: 0.1,
        minPointLength: 0,
        cropThreshold: 50,
        pointRange: null,
        states: {
          hover: {
            halo: false,
            brightness: 0.1
          },
          select: {
            color: '#cccccc',
            borderColor: '#000000'
          }
        },
        dataLabels: {
          align: null,
          verticalAlign: null,
          y: null
        },
        softThreshold: false,
        startFromThreshold: true,
        stickyTracking: false,
        tooltip: {
          distance: 6
        },
        threshold: 0,
        borderColor: '#ffffff'
      }, {
        cropShoulder: 0,
        directTouch: true,
        trackerGroups: ['group', 'dataLabelsGroup'],
        negStacks: true,
        init: function init() {
          Series.prototype.init.apply(this, arguments);
          var series = this,
              chart = series.chart;

          if (chart.hasRendered) {
            chart.series.forEach(function (otherSeries) {
              if (otherSeries.type === series.type) {
                otherSeries.isDirty = true;
              }
            });
          }
        },
        getColumnMetrics: function getColumnMetrics() {
          var series = this,
              options = series.options,
              xAxis = series.xAxis,
              yAxis = series.yAxis,
              reversedStacks = xAxis.options.reversedStacks,
              reverseStacks = xAxis.reversed && !reversedStacks || !xAxis.reversed && reversedStacks,
              stackKey,
              stackGroups = {},
              columnCount = 0;

          if (options.grouping === false) {
            columnCount = 1;
          } else {
            series.chart.series.forEach(function (otherSeries) {
              var otherYAxis = otherSeries.yAxis,
                  otherOptions = otherSeries.options,
                  columnIndex;

              if (otherSeries.type === series.type && (otherSeries.visible || !series.chart.options.chart.ignoreHiddenSeries) && yAxis.len === otherYAxis.len && yAxis.pos === otherYAxis.pos) {
                if (otherOptions.stacking) {
                  stackKey = otherSeries.stackKey;

                  if (typeof stackGroups[stackKey] === 'undefined') {
                    stackGroups[stackKey] = columnCount++;
                  }

                  columnIndex = stackGroups[stackKey];
                } else if (otherOptions.grouping !== false) {
                  columnIndex = columnCount++;
                }

                otherSeries.columnIndex = columnIndex;
              }
            });
          }

          var categoryWidth = Math.min(Math.abs(xAxis.transA) * (xAxis.ordinalSlope || options.pointRange || xAxis.closestPointRange || xAxis.tickInterval || 1), xAxis.len),
              groupPadding = categoryWidth * options.groupPadding,
              groupWidth = categoryWidth - 2 * groupPadding,
              pointOffsetWidth = groupWidth / (columnCount || 1),
              pointWidth = Math.min(options.maxPointWidth || xAxis.len, pick(options.pointWidth, pointOffsetWidth * (1 - 2 * options.pointPadding))),
              pointPadding = (pointOffsetWidth - pointWidth) / 2,
              colIndex = (series.columnIndex || 0) + (reverseStacks ? 1 : 0),
              pointXOffset = pointPadding + (groupPadding + colIndex * pointOffsetWidth - categoryWidth / 2) * (reverseStacks ? -1 : 1);
          series.columnMetrics = {
            width: pointWidth,
            offset: pointXOffset
          };
          return series.columnMetrics;
        },
        crispCol: function crispCol(x, y, w, h) {
          var chart = this.chart,
              borderWidth = this.borderWidth,
              xCrisp = -(borderWidth % 2 ? 0.5 : 0),
              yCrisp = borderWidth % 2 ? 0.5 : 1,
              right,
              bottom,
              fromTop;

          if (chart.inverted && chart.renderer.isVML) {
            yCrisp += 1;
          }

          if (this.options.crisp) {
            right = Math.round(x + w) + xCrisp;
            x = Math.round(x) + xCrisp;
            w = right - x;
          }

          bottom = Math.round(y + h) + yCrisp;
          fromTop = Math.abs(y) <= 0.5 && bottom > 0.5;
          y = Math.round(y) + yCrisp;
          h = bottom - y;

          if (fromTop && h) {
            y -= 1;
            h += 1;
          }

          return {
            x: x,
            y: y,
            width: w,
            height: h
          };
        },
        translate: function translate() {
          var series = this,
              chart = series.chart,
              options = series.options,
              dense = series.dense = series.closestPointRange * series.xAxis.transA < 2,
              borderWidth = series.borderWidth = pick(options.borderWidth, dense ? 0 : 1),
              xAxis = series.xAxis,
              yAxis = series.yAxis,
              threshold = options.threshold,
              translatedThreshold = series.translatedThreshold = yAxis.getThreshold(threshold),
              minPointLength = pick(options.minPointLength, 5),
              metrics = series.getColumnMetrics(),
              seriesPointWidth = metrics.width,
              seriesBarW = series.barW = Math.max(seriesPointWidth, 1 + 2 * borderWidth),
              seriesXOffset = series.pointXOffset = metrics.offset,
              dataMin = series.dataMin,
              dataMax = series.dataMax;

          if (chart.inverted) {
            translatedThreshold -= 0.5;
          }

          if (options.pointPadding) {
            seriesBarW = Math.ceil(seriesBarW);
          }

          Series.prototype.translate.apply(series);
          series.points.forEach(function (point) {
            var yBottom = pick(point.yBottom, translatedThreshold),
                safeDistance = 999 + Math.abs(yBottom),
                pointWidth = seriesPointWidth,
                plotX = point.plotX,
                plotY = clamp(point.plotY, -safeDistance, yAxis.len + safeDistance),
                barX = point.plotX + seriesXOffset,
                barW = seriesBarW,
                barY = Math.min(plotY, yBottom),
                up,
                barH = Math.max(plotY, yBottom) - barY;

            if (minPointLength && Math.abs(barH) < minPointLength) {
              barH = minPointLength;
              up = !yAxis.reversed && !point.negative || yAxis.reversed && point.negative;

              if (point.y === threshold && series.dataMax <= threshold && yAxis.min < threshold && dataMin !== dataMax) {
                up = !up;
              }

              barY = Math.abs(barY - translatedThreshold) > minPointLength ? yBottom - minPointLength : translatedThreshold - (up ? minPointLength : 0);
            }

            if (defined(point.options.pointWidth)) {
              pointWidth = barW = Math.ceil(point.options.pointWidth);
              barX -= Math.round((pointWidth - seriesPointWidth) / 2);
            }

            point.barX = barX;
            point.pointWidth = pointWidth;
            point.tooltipPos = chart.inverted ? [yAxis.len + yAxis.pos - chart.plotLeft - plotY, xAxis.len + xAxis.pos - chart.plotTop - (plotX || 0) - seriesXOffset - barW / 2, barH] : [barX + barW / 2, plotY + yAxis.pos - chart.plotTop, barH];
            point.shapeType = series.pointClass.prototype.shapeType || 'rect';
            point.shapeArgs = series.crispCol.apply(series, point.isNull ? [barX, translatedThreshold, barW, 0] : [barX, barY, barW, barH]);
          });
        },
        getSymbol: noop,
        drawLegendSymbol: LegendSymbolMixin.drawRectangle,
        drawGraph: function drawGraph() {
          this.group[this.dense ? 'addClass' : 'removeClass']('highcharts-dense-data');
        },
        pointAttribs: function pointAttribs(point, state) {
          var options = this.options,
              stateOptions,
              ret,
              p2o = this.pointAttrToOptions || {},
              strokeOption = p2o.stroke || 'borderColor',
              strokeWidthOption = p2o['stroke-width'] || 'borderWidth',
              fill = point && point.color || this.color,
              stroke = point && point[strokeOption] || options[strokeOption] || this.color || fill,
              strokeWidth = point && point[strokeWidthOption] || options[strokeWidthOption] || this[strokeWidthOption] || 0,
              dashstyle = point && point.options.dashStyle || options.dashStyle,
              opacity = pick(point && point.opacity, options.opacity, 1),
              zone,
              brightness;

          if (point && this.zones.length) {
            zone = point.getZone();
            fill = point.options.color || zone && (zone.color || point.nonZonedColor) || this.color;

            if (zone) {
              stroke = zone.borderColor || stroke;
              dashstyle = zone.dashStyle || dashstyle;
              strokeWidth = zone.borderWidth || strokeWidth;
            }
          }

          if (state && point) {
            stateOptions = merge(options.states[state], point.options.states && point.options.states[state] || {});
            brightness = stateOptions.brightness;
            fill = stateOptions.color || typeof brightness !== 'undefined' && color(fill).brighten(stateOptions.brightness).get() || fill;
            stroke = stateOptions[strokeOption] || stroke;
            strokeWidth = stateOptions[strokeWidthOption] || strokeWidth;
            dashstyle = stateOptions.dashStyle || dashstyle;
            opacity = pick(stateOptions.opacity, opacity);
          }

          ret = {
            fill: fill,
            stroke: stroke,
            'stroke-width': strokeWidth,
            opacity: opacity
          };

          if (dashstyle) {
            ret.dashstyle = dashstyle;
          }

          return ret;
        },
        drawPoints: function drawPoints() {
          var series = this,
              chart = this.chart,
              options = series.options,
              renderer = chart.renderer,
              animationLimit = options.animationLimit || 250,
              shapeArgs;
          series.points.forEach(function (point) {
            var plotY = point.plotY,
                graphic = point.graphic,
                hasGraphic = !!graphic,
                verb = graphic && chart.pointCount < animationLimit ? 'animate' : 'attr';

            if (isNumber(plotY) && point.y !== null) {
              shapeArgs = point.shapeArgs;

              if (graphic && point.hasNewShapeType()) {
                graphic = graphic.destroy();
              }

              if (series.enabledDataSorting) {
                point.startXPos = series.xAxis.reversed ? -(shapeArgs ? shapeArgs.width : 0) : series.xAxis.width;
              }

              if (!graphic) {
                point.graphic = graphic = renderer[point.shapeType](shapeArgs).add(point.group || series.group);

                if (graphic && series.enabledDataSorting && chart.hasRendered && chart.pointCount < animationLimit) {
                  graphic.attr({
                    x: point.startXPos
                  });
                  hasGraphic = true;
                  verb = 'animate';
                }
              }

              if (graphic && hasGraphic) {
                graphic[verb](merge(shapeArgs));
              }

              if (options.borderRadius) {
                graphic[verb]({
                  r: options.borderRadius
                });
              }

              if (!chart.styledMode) {
                graphic[verb](series.pointAttribs(point, point.selected && 'select')).shadow(point.allowShadow !== false && options.shadow, null, options.stacking && !options.borderRadius);
              }

              graphic.addClass(point.getClassName(), true);
            } else if (graphic) {
              point.graphic = graphic.destroy();
            }
          });
        },
        animate: function animate(init) {
          var series = this,
              yAxis = this.yAxis,
              options = series.options,
              inverted = this.chart.inverted,
              attr = {},
              translateProp = inverted ? 'translateX' : 'translateY',
              translateStart,
              translatedThreshold;

          if (init) {
            attr.scaleY = 0.001;
            translatedThreshold = clamp(yAxis.toPixels(options.threshold), yAxis.pos, yAxis.pos + yAxis.len);

            if (inverted) {
              attr.translateX = translatedThreshold - yAxis.len;
            } else {
              attr.translateY = translatedThreshold;
            }

            if (series.clipBox) {
              series.setClip();
            }

            series.group.attr(attr);
          } else {
            translateStart = series.group.attr(translateProp);
            series.group.animate({
              scaleY: 1
            }, extend(animObject(series.options.animation), {
              step: function step(val, fx) {
                if (series.group) {
                  attr[translateProp] = translateStart + fx.pos * (yAxis.pos - translateStart);
                  series.group.attr(attr);
                }
              }
            }));
          }
        },
        remove: function remove() {
          var series = this,
              chart = series.chart;

          if (chart.hasRendered) {
            chart.series.forEach(function (otherSeries) {
              if (otherSeries.type === series.type) {
                otherSeries.isDirty = true;
              }
            });
          }

          Series.prototype.remove.apply(series, arguments);
        }
      });
    });

    _registerModule(_modules, 'parts/BarSeries.js', [_modules['parts/Utilities.js']], function (U) {
      var seriesType = U.seriesType;
      seriesType('bar', 'column', null, {
        inverted: true
      });
    });

    _registerModule(_modules, 'parts/ScatterSeries.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var addEvent = U.addEvent,
          seriesType = U.seriesType;
      var Series = H.Series;
      seriesType('scatter', 'line', {
        lineWidth: 0,
        findNearestPointBy: 'xy',
        jitter: {
          x: 0,
          y: 0
        },
        marker: {
          enabled: true
        },
        tooltip: {
          headerFormat: "<span style=\"color:{point.color}\">\u25CF</span> " + '<span style="font-size: 10px"> {series.name}</span><br/>',
          pointFormat: 'x: <b>{point.x}</b><br/>y: <b>{point.y}</b><br/>'
        }
      }, {
        sorted: false,
        requireSorting: false,
        noSharedTooltip: true,
        trackerGroups: ['group', 'markerGroup', 'dataLabelsGroup'],
        takeOrdinalPosition: false,
        drawGraph: function drawGraph() {
          if (this.options.lineWidth) {
            Series.prototype.drawGraph.call(this);
          }
        },
        applyJitter: function applyJitter() {
          var series = this,
              jitter = this.options.jitter,
              len = this.points.length;

          function unrandom(seed) {
            var rand = Math.sin(seed) * 10000;
            return rand - Math.floor(rand);
          }

          if (jitter) {
            this.points.forEach(function (point, i) {
              ['x', 'y'].forEach(function (dim, j) {
                var axis,
                    plotProp = 'plot' + dim.toUpperCase(),
                    min,
                    max,
                    translatedJitter;

                if (jitter[dim] && !point.isNull) {
                  axis = series[dim + 'Axis'];
                  translatedJitter = jitter[dim] * axis.transA;

                  if (axis && !axis.isLog) {
                    min = Math.max(0, point[plotProp] - translatedJitter);
                    max = Math.min(axis.len, point[plotProp] + translatedJitter);
                    point[plotProp] = min + (max - min) * unrandom(i + j * len);

                    if (dim === 'x') {
                      point.clientX = point.plotX;
                    }
                  }
                }
              });
            });
          }
        }
      });
      addEvent(Series, 'afterTranslate', function () {
        if (this.applyJitter) {
          this.applyJitter();
        }
      });
    });

    _registerModule(_modules, 'mixins/centered-series.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var isNumber = U.isNumber,
          pick = U.pick,
          relativeLength = U.relativeLength;
      var deg2rad = H.deg2rad;
      H.CenteredSeriesMixin = {
        getCenter: function getCenter() {
          var options = this.options,
              chart = this.chart,
              slicingRoom = 2 * (options.slicedOffset || 0),
              handleSlicingRoom,
              plotWidth = chart.plotWidth - 2 * slicingRoom,
              plotHeight = chart.plotHeight - 2 * slicingRoom,
              centerOption = options.center,
              smallestSize = Math.min(plotWidth, plotHeight),
              size = options.size,
              innerSize = options.innerSize || 0,
              positions,
              i,
              value;

          if (typeof size === 'string') {
            size = parseFloat(size);
          }

          if (typeof innerSize === 'string') {
            innerSize = parseFloat(innerSize);
          }

          positions = [pick(centerOption[0], '50%'), pick(centerOption[1], '50%'), pick(size && size < 0 ? void 0 : options.size, '100%'), pick(innerSize && innerSize < 0 ? void 0 : options.innerSize || 0, '0%')];

          if (chart.angular) {
            positions[3] = 0;
          }

          for (i = 0; i < 4; ++i) {
            value = positions[i];
            handleSlicingRoom = i < 2 || i === 2 && /%$/.test(value);
            positions[i] = relativeLength(value, [plotWidth, plotHeight, smallestSize, positions[2]][i]) + (handleSlicingRoom ? slicingRoom : 0);
          }

          if (positions[3] > positions[2]) {
            positions[3] = positions[2];
          }

          return positions;
        },
        getStartAndEndRadians: function getStartAndEndRadians(start, end) {
          var startAngle = isNumber(start) ? start : 0,
              endAngle = isNumber(end) && end > startAngle && end - startAngle < 360 ? end : startAngle + 360,
              correction = -90;
          return {
            start: deg2rad * (startAngle + correction),
            end: deg2rad * (endAngle + correction)
          };
        }
      };
    });

    _registerModule(_modules, 'parts/PieSeries.js', [_modules['parts/Globals.js'], _modules['mixins/legend-symbol.js'], _modules['parts/Point.js'], _modules['parts/Utilities.js']], function (H, LegendSymbolMixin, Point, U) {
      var addEvent = U.addEvent,
          clamp = U.clamp,
          defined = U.defined,
          fireEvent = U.fireEvent,
          isNumber = U.isNumber,
          merge = U.merge,
          pick = U.pick,
          relativeLength = U.relativeLength,
          seriesType = U.seriesType,
          setAnimation = U.setAnimation;
      var CenteredSeriesMixin = H.CenteredSeriesMixin,
          getStartAndEndRadians = CenteredSeriesMixin.getStartAndEndRadians,
          noop = H.noop,
          Series = H.Series,
          seriesTypes = H.seriesTypes;
      seriesType('pie', 'line', {
        center: [null, null],
        clip: false,
        colorByPoint: true,
        dataLabels: {
          allowOverlap: true,
          connectorPadding: 5,
          connectorShape: 'fixedOffset',
          crookDistance: '70%',
          distance: 30,
          enabled: true,
          formatter: function formatter() {
            return this.point.isNull ? void 0 : this.point.name;
          },
          softConnector: true,
          x: 0
        },
        fillColor: void 0,
        ignoreHiddenPoint: true,
        inactiveOtherPoints: true,
        legendType: 'point',
        marker: null,
        size: null,
        showInLegend: false,
        slicedOffset: 10,
        stickyTracking: false,
        tooltip: {
          followPointer: true
        },
        borderColor: '#ffffff',
        borderWidth: 1,
        lineWidth: void 0,
        states: {
          hover: {
            brightness: 0.1
          }
        }
      }, {
        isCartesian: false,
        requireSorting: false,
        directTouch: true,
        noSharedTooltip: true,
        trackerGroups: ['group', 'dataLabelsGroup'],
        axisTypes: [],
        pointAttribs: seriesTypes.column.prototype.pointAttribs,
        animate: function animate(init) {
          var series = this,
              points = series.points,
              startAngleRad = series.startAngleRad;

          if (!init) {
            points.forEach(function (point) {
              var graphic = point.graphic,
                  args = point.shapeArgs;

              if (graphic && args) {
                graphic.attr({
                  r: pick(point.startR, series.center && series.center[3] / 2),
                  start: startAngleRad,
                  end: startAngleRad
                });
                graphic.animate({
                  r: args.r,
                  start: args.start,
                  end: args.end
                }, series.options.animation);
              }
            });
          }
        },
        hasData: function hasData() {
          return !!this.processedXData.length;
        },
        updateTotals: function updateTotals() {
          var i,
              total = 0,
              points = this.points,
              len = points.length,
              point,
              ignoreHiddenPoint = this.options.ignoreHiddenPoint;

          for (i = 0; i < len; i++) {
            point = points[i];
            total += ignoreHiddenPoint && !point.visible ? 0 : point.isNull ? 0 : point.y;
          }

          this.total = total;

          for (i = 0; i < len; i++) {
            point = points[i];
            point.percentage = total > 0 && (point.visible || !ignoreHiddenPoint) ? point.y / total * 100 : 0;
            point.total = total;
          }
        },
        generatePoints: function generatePoints() {
          Series.prototype.generatePoints.call(this);
          this.updateTotals();
        },
        getX: function getX(y, left, point) {
          var center = this.center,
              radius = this.radii ? this.radii[point.index] : center[2] / 2,
              angle,
              x;
          angle = Math.asin(clamp((y - center[1]) / (radius + point.labelDistance), -1, 1));
          x = center[0] + (left ? -1 : 1) * (Math.cos(angle) * (radius + point.labelDistance)) + (point.labelDistance > 0 ? (left ? -1 : 1) * this.options.dataLabels.padding : 0);
          return x;
        },
        translate: function translate(positions) {
          this.generatePoints();
          var series = this,
              cumulative = 0,
              precision = 1000,
              options = series.options,
              slicedOffset = options.slicedOffset,
              connectorOffset = slicedOffset + (options.borderWidth || 0),
              finalConnectorOffset,
              start,
              end,
              angle,
              radians = getStartAndEndRadians(options.startAngle, options.endAngle),
              startAngleRad = series.startAngleRad = radians.start,
              endAngleRad = series.endAngleRad = radians.end,
              circ = endAngleRad - startAngleRad,
              points = series.points,
              radiusX,
              radiusY,
              labelDistance = options.dataLabels.distance,
              ignoreHiddenPoint = options.ignoreHiddenPoint,
              i,
              len = points.length,
              point;

          if (!positions) {
            series.center = positions = series.getCenter();
          }

          for (i = 0; i < len; i++) {
            point = points[i];
            start = startAngleRad + cumulative * circ;

            if (!ignoreHiddenPoint || point.visible) {
              cumulative += point.percentage / 100;
            }

            end = startAngleRad + cumulative * circ;
            point.shapeType = 'arc';
            point.shapeArgs = {
              x: positions[0],
              y: positions[1],
              r: positions[2] / 2,
              innerR: positions[3] / 2,
              start: Math.round(start * precision) / precision,
              end: Math.round(end * precision) / precision
            };
            point.labelDistance = pick(point.options.dataLabels && point.options.dataLabels.distance, labelDistance);
            point.labelDistance = relativeLength(point.labelDistance, point.shapeArgs.r);
            series.maxLabelDistance = Math.max(series.maxLabelDistance || 0, point.labelDistance);
            angle = (end + start) / 2;

            if (angle > 1.5 * Math.PI) {
              angle -= 2 * Math.PI;
            } else if (angle < -Math.PI / 2) {
              angle += 2 * Math.PI;
            }

            point.slicedTranslation = {
              translateX: Math.round(Math.cos(angle) * slicedOffset),
              translateY: Math.round(Math.sin(angle) * slicedOffset)
            };
            radiusX = Math.cos(angle) * positions[2] / 2;
            radiusY = Math.sin(angle) * positions[2] / 2;
            point.tooltipPos = [positions[0] + radiusX * 0.7, positions[1] + radiusY * 0.7];
            point.half = angle < -Math.PI / 2 || angle > Math.PI / 2 ? 1 : 0;
            point.angle = angle;
            finalConnectorOffset = Math.min(connectorOffset, point.labelDistance / 5);
            point.labelPosition = {
              natural: {
                x: positions[0] + radiusX + Math.cos(angle) * point.labelDistance,
                y: positions[1] + radiusY + Math.sin(angle) * point.labelDistance
              },
              'final': {},
              alignment: point.labelDistance < 0 ? 'center' : point.half ? 'right' : 'left',
              connectorPosition: {
                breakAt: {
                  x: positions[0] + radiusX + Math.cos(angle) * finalConnectorOffset,
                  y: positions[1] + radiusY + Math.sin(angle) * finalConnectorOffset
                },
                touchingSliceAt: {
                  x: positions[0] + radiusX,
                  y: positions[1] + radiusY
                }
              }
            };
          }

          fireEvent(series, 'afterTranslate');
        },
        drawEmpty: function drawEmpty() {
          var centerX,
              centerY,
              options = this.options;

          if (this.total === 0) {
            centerX = this.center[0];
            centerY = this.center[1];

            if (!this.graph) {
              this.graph = this.chart.renderer.circle(centerX, centerY, 0).addClass('highcharts-graph').add(this.group);
            }

            this.graph.animate({
              'stroke-width': options.borderWidth,
              cx: centerX,
              cy: centerY,
              r: this.center[2] / 2,
              fill: options.fillColor || 'none',
              stroke: options.color || '#cccccc'
            }, this.options.animation);
          } else if (this.graph) {
            this.graph = this.graph.destroy();
          }
        },
        redrawPoints: function redrawPoints() {
          var series = this,
              chart = series.chart,
              renderer = chart.renderer,
              groupTranslation,
              graphic,
              pointAttr,
              shapeArgs,
              shadow = series.options.shadow;
          this.drawEmpty();

          if (shadow && !series.shadowGroup && !chart.styledMode) {
            series.shadowGroup = renderer.g('shadow').attr({
              zIndex: -1
            }).add(series.group);
          }

          series.points.forEach(function (point) {
            var animateTo = {};
            graphic = point.graphic;

            if (!point.isNull && graphic) {
              shapeArgs = point.shapeArgs;
              groupTranslation = point.getTranslate();

              if (!chart.styledMode) {
                var shadowGroup = point.shadowGroup;

                if (shadow && !shadowGroup) {
                  shadowGroup = point.shadowGroup = renderer.g('shadow').add(series.shadowGroup);
                }

                if (shadowGroup) {
                  shadowGroup.attr(groupTranslation);
                }

                pointAttr = series.pointAttribs(point, point.selected && 'select');
              }

              if (!point.delayedRendering) {
                graphic.setRadialReference(series.center);

                if (!chart.styledMode) {
                  merge(true, animateTo, pointAttr);
                }

                merge(true, animateTo, shapeArgs, groupTranslation);
                graphic.animate(animateTo);
              } else {
                graphic.setRadialReference(series.center).attr(shapeArgs).attr(groupTranslation);

                if (!chart.styledMode) {
                  graphic.attr(pointAttr).attr({
                    'stroke-linejoin': 'round'
                  }).shadow(shadow, shadowGroup);
                }

                point.delayedRendering = false;
              }

              graphic.attr({
                visibility: point.visible ? 'inherit' : 'hidden'
              });
              graphic.addClass(point.getClassName());
            } else if (graphic) {
              point.graphic = graphic.destroy();
            }
          });
        },
        drawPoints: function drawPoints() {
          var renderer = this.chart.renderer;
          this.points.forEach(function (point) {
            if (point.graphic && point.hasNewShapeType()) {
              point.graphic = point.graphic.destroy();
            }

            if (!point.graphic) {
              point.graphic = renderer[point.shapeType](point.shapeArgs).add(point.series.group);
              point.delayedRendering = true;
            }
          });
        },
        searchPoint: noop,
        sortByAngle: function sortByAngle(points, sign) {
          points.sort(function (a, b) {
            return typeof a.angle !== 'undefined' && (b.angle - a.angle) * sign;
          });
        },
        drawLegendSymbol: LegendSymbolMixin.drawRectangle,
        getCenter: CenteredSeriesMixin.getCenter,
        getSymbol: noop,
        drawGraph: null
      }, {
        init: function init() {
          Point.prototype.init.apply(this, arguments);
          var point = this,
              toggleSlice;
          point.name = pick(point.name, 'Slice');

          toggleSlice = function toggleSlice(e) {
            point.slice(e.type === 'select');
          };

          addEvent(point, 'select', toggleSlice);
          addEvent(point, 'unselect', toggleSlice);
          return point;
        },
        isValid: function isValid() {
          return isNumber(this.y) && this.y >= 0;
        },
        setVisible: function setVisible(vis, redraw) {
          var point = this,
              series = point.series,
              chart = series.chart,
              ignoreHiddenPoint = series.options.ignoreHiddenPoint;
          redraw = pick(redraw, ignoreHiddenPoint);

          if (vis !== point.visible) {
            point.visible = point.options.visible = vis = typeof vis === 'undefined' ? !point.visible : vis;
            series.options.data[series.data.indexOf(point)] = point.options;
            ['graphic', 'dataLabel', 'connector', 'shadowGroup'].forEach(function (key) {
              if (point[key]) {
                point[key][vis ? 'show' : 'hide'](true);
              }
            });

            if (point.legendItem) {
              chart.legend.colorizeItem(point, vis);
            }

            if (!vis && point.state === 'hover') {
              point.setState('');
            }

            if (ignoreHiddenPoint) {
              series.isDirty = true;
            }

            if (redraw) {
              chart.redraw();
            }
          }
        },
        slice: function slice(sliced, redraw, animation) {
          var point = this,
              series = point.series,
              chart = series.chart;
          setAnimation(animation, chart);
          redraw = pick(redraw, true);
          point.sliced = point.options.sliced = sliced = defined(sliced) ? sliced : !point.sliced;
          series.options.data[series.data.indexOf(point)] = point.options;
          point.graphic.animate(this.getTranslate());

          if (point.shadowGroup) {
            point.shadowGroup.animate(this.getTranslate());
          }
        },
        getTranslate: function getTranslate() {
          return this.sliced ? this.slicedTranslation : {
            translateX: 0,
            translateY: 0
          };
        },
        haloPath: function haloPath(size) {
          var shapeArgs = this.shapeArgs;
          return this.sliced || !this.visible ? [] : this.series.chart.renderer.symbols.arc(shapeArgs.x, shapeArgs.y, shapeArgs.r + size, shapeArgs.r + size, {
            innerR: shapeArgs.r - 1,
            start: shapeArgs.start,
            end: shapeArgs.end
          });
        },
        connectorShapes: {
          fixedOffset: function fixedOffset(labelPosition, connectorPosition, options) {
            var breakAt = connectorPosition.breakAt,
                touchingSliceAt = connectorPosition.touchingSliceAt,
                linePath = options.softConnector ? ['C', labelPosition.x + (labelPosition.alignment === 'left' ? -5 : 5), labelPosition.y, 2 * breakAt.x - touchingSliceAt.x, 2 * breakAt.y - touchingSliceAt.y, breakAt.x, breakAt.y] : ['L', breakAt.x, breakAt.y];
            return ['M', labelPosition.x, labelPosition.y].concat(linePath).concat(['L', touchingSliceAt.x, touchingSliceAt.y]);
          },
          straight: function straight(labelPosition, connectorPosition) {
            var touchingSliceAt = connectorPosition.touchingSliceAt;
            return ['M', labelPosition.x, labelPosition.y, 'L', touchingSliceAt.x, touchingSliceAt.y];
          },
          crookedLine: function crookedLine(labelPosition, connectorPosition, options) {
            var touchingSliceAt = connectorPosition.touchingSliceAt,
                series = this.series,
                pieCenterX = series.center[0],
                plotWidth = series.chart.plotWidth,
                plotLeft = series.chart.plotLeft,
                alignment = labelPosition.alignment,
                radius = this.shapeArgs.r,
                crookDistance = relativeLength(options.crookDistance, 1),
                crookX = alignment === 'left' ? pieCenterX + radius + (plotWidth + plotLeft - pieCenterX - radius) * (1 - crookDistance) : plotLeft + (pieCenterX - radius) * crookDistance,
                segmentWithCrook = ['L', crookX, labelPosition.y];

            if (alignment === 'left' ? crookX > labelPosition.x || crookX < touchingSliceAt.x : crookX < labelPosition.x || crookX > touchingSliceAt.x) {
              segmentWithCrook = [];
            }

            return ['M', labelPosition.x, labelPosition.y].concat(segmentWithCrook).concat(['L', touchingSliceAt.x, touchingSliceAt.y]);
          }
        },
        getConnectorPath: function getConnectorPath() {
          var labelPosition = this.labelPosition,
              options = this.series.options.dataLabels,
              connectorShape = options.connectorShape,
              predefinedShapes = this.connectorShapes;

          if (predefinedShapes[connectorShape]) {
            connectorShape = predefinedShapes[connectorShape];
          }

          return connectorShape.call(this, {
            x: labelPosition["final"].x,
            y: labelPosition["final"].y,
            alignment: labelPosition.alignment
          }, labelPosition.connectorPosition, options);
        }
      });
    });

    _registerModule(_modules, 'parts/DataLabels.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var animObject = U.animObject,
          arrayMax = U.arrayMax,
          clamp = U.clamp,
          defined = U.defined,
          extend = U.extend,
          format = U.format,
          isArray = U.isArray,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick,
          relativeLength = U.relativeLength,
          splat = U.splat,
          stableSort = U.stableSort;
      var noop = H.noop,
          Series = H.Series,
          seriesTypes = H.seriesTypes;

      H.distribute = function (boxes, len, maxDistance) {
        var i,
            overlapping = true,
            origBoxes = boxes,
            restBoxes = [],
            box,
            target,
            total = 0,
            reducedLen = origBoxes.reducedLen || len;

        function sortByTarget(a, b) {
          return a.target - b.target;
        }

        i = boxes.length;

        while (i--) {
          total += boxes[i].size;
        }

        if (total > reducedLen) {
          stableSort(boxes, function (a, b) {
            return (b.rank || 0) - (a.rank || 0);
          });
          i = 0;
          total = 0;

          while (total <= reducedLen) {
            total += boxes[i].size;
            i++;
          }

          restBoxes = boxes.splice(i - 1, boxes.length);
        }

        stableSort(boxes, sortByTarget);
        boxes = boxes.map(function (box) {
          return {
            size: box.size,
            targets: [box.target],
            align: pick(box.align, 0.5)
          };
        });

        while (overlapping) {
          i = boxes.length;

          while (i--) {
            box = boxes[i];
            target = (Math.min.apply(0, box.targets) + Math.max.apply(0, box.targets)) / 2;
            box.pos = clamp(target - box.size * box.align, 0, len - box.size);
          }

          i = boxes.length;
          overlapping = false;

          while (i--) {
            if (i > 0 && boxes[i - 1].pos + boxes[i - 1].size > boxes[i].pos) {
              boxes[i - 1].size += boxes[i].size;
              boxes[i - 1].targets = boxes[i - 1].targets.concat(boxes[i].targets);
              boxes[i - 1].align = 0.5;

              if (boxes[i - 1].pos + boxes[i - 1].size > len) {
                boxes[i - 1].pos = len - boxes[i - 1].size;
              }

              boxes.splice(i, 1);
              overlapping = true;
            }
          }
        }

        origBoxes.push.apply(origBoxes, restBoxes);
        i = 0;
        boxes.some(function (box) {
          var posInCompositeBox = 0;

          if (box.targets.some(function () {
            origBoxes[i].pos = box.pos + posInCompositeBox;

            if (typeof maxDistance !== 'undefined' && Math.abs(origBoxes[i].pos - origBoxes[i].target) > maxDistance) {
              origBoxes.slice(0, i + 1).forEach(function (box) {
                delete box.pos;
              });
              origBoxes.reducedLen = (origBoxes.reducedLen || len) - len * 0.1;

              if (origBoxes.reducedLen > len * 0.1) {
                H.distribute(origBoxes, len, maxDistance);
              }

              return true;
            }

            posInCompositeBox += origBoxes[i].size;
            i++;
          })) {
            return true;
          }
        });
        stableSort(origBoxes, sortByTarget);
      };

      Series.prototype.drawDataLabels = function () {
        var series = this,
            chart = series.chart,
            seriesOptions = series.options,
            seriesDlOptions = seriesOptions.dataLabels,
            points = series.points,
            pointOptions,
            hasRendered = series.hasRendered || 0,
            dataLabelsGroup,
            seriesAnimDuration = animObject(seriesOptions.animation).duration,
            fadeInDuration = Math.min(seriesAnimDuration, 200),
            defer = !chart.renderer.forExport && pick(seriesDlOptions.defer, fadeInDuration > 0),
            renderer = chart.renderer;

        function applyFilter(point, options) {
          var filter = options.filter,
              op,
              prop,
              val;

          if (filter) {
            op = filter.operator;
            prop = point[filter.property];
            val = filter.value;

            if (op === '>' && prop > val || op === '<' && prop < val || op === '>=' && prop >= val || op === '<=' && prop <= val || op === '==' && prop == val || op === '===' && prop === val) {
              return true;
            }

            return false;
          }

          return true;
        }

        function mergeArrays(one, two) {
          var res = [],
              i;

          if (isArray(one) && !isArray(two)) {
            res = one.map(function (el) {
              return merge(el, two);
            });
          } else if (isArray(two) && !isArray(one)) {
            res = two.map(function (el) {
              return merge(one, el);
            });
          } else if (!isArray(one) && !isArray(two)) {
            res = merge(one, two);
          } else {
            i = Math.max(one.length, two.length);

            while (i--) {
              res[i] = merge(one[i], two[i]);
            }
          }

          return res;
        }

        seriesDlOptions = mergeArrays(mergeArrays(chart.options.plotOptions && chart.options.plotOptions.series && chart.options.plotOptions.series.dataLabels, chart.options.plotOptions && chart.options.plotOptions[series.type] && chart.options.plotOptions[series.type].dataLabels), seriesDlOptions);
        H.fireEvent(this, 'drawDataLabels');

        if (isArray(seriesDlOptions) || seriesDlOptions.enabled || series._hasPointLabels) {
          dataLabelsGroup = series.plotGroup('dataLabelsGroup', 'data-labels', defer && !hasRendered ? 'hidden' : 'inherit', seriesDlOptions.zIndex || 6);

          if (defer) {
            dataLabelsGroup.attr({
              opacity: +hasRendered
            });

            if (!hasRendered) {
              setTimeout(function () {
                var group = series.dataLabelsGroup;

                if (group) {
                  if (series.visible) {
                    dataLabelsGroup.show(true);
                  }

                  group[seriesOptions.animation ? 'animate' : 'attr']({
                    opacity: 1
                  }, {
                    duration: fadeInDuration
                  });
                }
              }, seriesAnimDuration - fadeInDuration);
            }
          }

          points.forEach(function (point) {
            pointOptions = splat(mergeArrays(seriesDlOptions, point.dlOptions || point.options && point.options.dataLabels));
            pointOptions.forEach(function (labelOptions, i) {
              var labelEnabled = labelOptions.enabled && (!point.isNull || point.dataLabelOnNull) && applyFilter(point, labelOptions),
                  labelConfig,
                  formatString,
                  labelText,
                  style,
                  rotation,
                  attr,
                  dataLabel = point.dataLabels ? point.dataLabels[i] : point.dataLabel,
                  connector = point.connectors ? point.connectors[i] : point.connector,
                  labelDistance = pick(labelOptions.distance, point.labelDistance),
                  isNew = !dataLabel;

              if (labelEnabled) {
                labelConfig = point.getLabelConfig();
                formatString = pick(labelOptions[point.formatPrefix + 'Format'], labelOptions.format);
                labelText = defined(formatString) ? format(formatString, labelConfig, chart) : (labelOptions[point.formatPrefix + 'Formatter'] || labelOptions.formatter).call(labelConfig, labelOptions);
                style = labelOptions.style;
                rotation = labelOptions.rotation;

                if (!chart.styledMode) {
                  style.color = pick(labelOptions.color, style.color, series.color, '#000000');

                  if (style.color === 'contrast') {
                    point.contrastColor = renderer.getContrast(point.color || series.color);
                    style.color = !defined(labelDistance) && labelOptions.inside || labelDistance < 0 || !!seriesOptions.stacking ? point.contrastColor : '#000000';
                  } else {
                    delete point.contrastColor;
                  }

                  if (seriesOptions.cursor) {
                    style.cursor = seriesOptions.cursor;
                  }
                }

                attr = {
                  r: labelOptions.borderRadius || 0,
                  rotation: rotation,
                  padding: labelOptions.padding,
                  zIndex: 1
                };

                if (!chart.styledMode) {
                  attr.fill = labelOptions.backgroundColor;
                  attr.stroke = labelOptions.borderColor;
                  attr['stroke-width'] = labelOptions.borderWidth;
                }

                objectEach(attr, function (val, name) {
                  if (typeof val === 'undefined') {
                    delete attr[name];
                  }
                });
              }

              if (dataLabel && (!labelEnabled || !defined(labelText))) {
                point.dataLabel = point.dataLabel && point.dataLabel.destroy();

                if (point.dataLabels) {
                  if (point.dataLabels.length === 1) {
                    delete point.dataLabels;
                  } else {
                    delete point.dataLabels[i];
                  }
                }

                if (!i) {
                  delete point.dataLabel;
                }

                if (connector) {
                  point.connector = point.connector.destroy();

                  if (point.connectors) {
                    if (point.connectors.length === 1) {
                      delete point.connectors;
                    } else {
                      delete point.connectors[i];
                    }
                  }
                }
              } else if (labelEnabled && defined(labelText)) {
                if (!dataLabel) {
                  point.dataLabels = point.dataLabels || [];
                  dataLabel = point.dataLabels[i] = rotation ? renderer.text(labelText, 0, -9999, labelOptions.useHTML).addClass('highcharts-data-label') : renderer.label(labelText, 0, -9999, labelOptions.shape, null, null, labelOptions.useHTML, null, 'data-label');

                  if (!i) {
                    point.dataLabel = dataLabel;
                  }

                  dataLabel.addClass(' highcharts-data-label-color-' + point.colorIndex + ' ' + (labelOptions.className || '') + (labelOptions.useHTML ? ' highcharts-tracker' : ''));
                } else {
                  attr.text = labelText;
                }

                dataLabel.options = labelOptions;
                dataLabel.attr(attr);

                if (!chart.styledMode) {
                  dataLabel.css(style).shadow(labelOptions.shadow);
                }

                if (!dataLabel.added) {
                  dataLabel.add(dataLabelsGroup);
                }

                if (labelOptions.textPath && !labelOptions.useHTML) {
                  dataLabel.setTextPath(point.getDataLabelPath && point.getDataLabelPath(dataLabel) || point.graphic, labelOptions.textPath);

                  if (point.dataLabelPath && !labelOptions.textPath.enabled) {
                    point.dataLabelPath = point.dataLabelPath.destroy();
                  }
                }

                series.alignDataLabel(point, dataLabel, labelOptions, null, isNew);
              }
            });
          });
        }

        H.fireEvent(this, 'afterDrawDataLabels');
      };

      Series.prototype.alignDataLabel = function (point, dataLabel, options, alignTo, isNew) {
        var series = this,
            chart = this.chart,
            inverted = this.isCartesian && chart.inverted,
            enabledDataSorting = this.enabledDataSorting,
            plotX = pick(point.dlBox && point.dlBox.centerX, point.plotX, -9999),
            plotY = pick(point.plotY, -9999),
            bBox = dataLabel.getBBox(),
            baseline,
            rotation = options.rotation,
            normRotation,
            negRotation,
            align = options.align,
            rotCorr,
            isInsidePlot = chart.isInsidePlot(plotX, Math.round(plotY), inverted),
            alignAttr,
            justify = pick(options.overflow, enabledDataSorting ? 'none' : 'justify') === 'justify',
            visible = this.visible && point.visible !== false && (point.series.forceDL || enabledDataSorting && !justify || isInsidePlot || options.inside && alignTo && chart.isInsidePlot(plotX, inverted ? alignTo.x + 1 : alignTo.y + alignTo.height - 1, inverted)),
            setStartPos = function setStartPos(alignOptions) {
          if (enabledDataSorting && series.xAxis && !justify) {
            series.setDataLabelStartPos(point, dataLabel, isNew, isInsidePlot, alignOptions);
          }
        };

        if (visible) {
          baseline = chart.renderer.fontMetrics(chart.styledMode ? void 0 : options.style.fontSize, dataLabel).b;
          alignTo = extend({
            x: inverted ? this.yAxis.len - plotY : plotX,
            y: Math.round(inverted ? this.xAxis.len - plotX : plotY),
            width: 0,
            height: 0
          }, alignTo);
          extend(options, {
            width: bBox.width,
            height: bBox.height
          });

          if (rotation) {
            justify = false;
            rotCorr = chart.renderer.rotCorr(baseline, rotation);
            alignAttr = {
              x: alignTo.x + options.x + alignTo.width / 2 + rotCorr.x,
              y: alignTo.y + options.y + {
                top: 0,
                middle: 0.5,
                bottom: 1
              }[options.verticalAlign] * alignTo.height
            };
            setStartPos(alignAttr);
            dataLabel[isNew ? 'attr' : 'animate'](alignAttr).attr({
              align: align
            });
            normRotation = (rotation + 720) % 360;
            negRotation = normRotation > 180 && normRotation < 360;

            if (align === 'left') {
              alignAttr.y -= negRotation ? bBox.height : 0;
            } else if (align === 'center') {
              alignAttr.x -= bBox.width / 2;
              alignAttr.y -= bBox.height / 2;
            } else if (align === 'right') {
              alignAttr.x -= bBox.width;
              alignAttr.y -= negRotation ? 0 : bBox.height;
            }

            dataLabel.placed = true;
            dataLabel.alignAttr = alignAttr;
          } else {
            setStartPos(alignTo);
            dataLabel.align(options, null, alignTo);
            alignAttr = dataLabel.alignAttr;
          }

          if (justify && alignTo.height >= 0) {
            this.justifyDataLabel(dataLabel, options, alignAttr, bBox, alignTo, isNew);
          } else if (pick(options.crop, true)) {
            visible = chart.isInsidePlot(alignAttr.x, alignAttr.y) && chart.isInsidePlot(alignAttr.x + bBox.width, alignAttr.y + bBox.height);
          }

          if (options.shape && !rotation) {
            dataLabel[isNew ? 'attr' : 'animate']({
              anchorX: inverted ? chart.plotWidth - point.plotY : point.plotX,
              anchorY: inverted ? chart.plotHeight - point.plotX : point.plotY
            });
          }
        }

        if (isNew && enabledDataSorting) {
          dataLabel.placed = false;
        }

        if (!visible && (!enabledDataSorting || justify)) {
          dataLabel.hide(true);
          dataLabel.placed = false;
        }
      };

      Series.prototype.setDataLabelStartPos = function (point, dataLabel, isNew, isInside, alignOptions) {
        var chart = this.chart,
            inverted = chart.inverted,
            xAxis = this.xAxis,
            reversed = xAxis.reversed,
            labelCenter = inverted ? dataLabel.height / 2 : dataLabel.width / 2,
            pointWidth = point.pointWidth,
            halfWidth = pointWidth ? pointWidth / 2 : 0,
            startXPos,
            startYPos;
        startXPos = inverted ? alignOptions.x : reversed ? -labelCenter - halfWidth : xAxis.width - labelCenter + halfWidth;
        startYPos = inverted ? reversed ? this.yAxis.height - labelCenter + halfWidth : -labelCenter - halfWidth : alignOptions.y;
        dataLabel.startXPos = startXPos;
        dataLabel.startYPos = startYPos;

        if (!isInside) {
          dataLabel.attr({
            opacity: 1
          }).animate({
            opacity: 0
          }, void 0, dataLabel.hide);
        } else if (dataLabel.visibility === 'hidden') {
          dataLabel.show();
          dataLabel.attr({
            opacity: 0
          }).animate({
            opacity: 1
          });
        }

        if (!chart.hasRendered) {
          return;
        }

        if (isNew) {
          dataLabel.attr({
            x: dataLabel.startXPos,
            y: dataLabel.startYPos
          });
        }

        dataLabel.placed = true;
      };

      Series.prototype.justifyDataLabel = function (dataLabel, options, alignAttr, bBox, alignTo, isNew) {
        var chart = this.chart,
            align = options.align,
            verticalAlign = options.verticalAlign,
            off,
            justified,
            padding = dataLabel.box ? 0 : dataLabel.padding || 0;
        off = alignAttr.x + padding;

        if (off < 0) {
          if (align === 'right') {
            options.align = 'left';
            options.inside = true;
          } else {
            options.x = -off;
          }

          justified = true;
        }

        off = alignAttr.x + bBox.width - padding;

        if (off > chart.plotWidth) {
          if (align === 'left') {
            options.align = 'right';
            options.inside = true;
          } else {
            options.x = chart.plotWidth - off;
          }

          justified = true;
        }

        off = alignAttr.y + padding;

        if (off < 0) {
          if (verticalAlign === 'bottom') {
            options.verticalAlign = 'top';
            options.inside = true;
          } else {
            options.y = -off;
          }

          justified = true;
        }

        off = alignAttr.y + bBox.height - padding;

        if (off > chart.plotHeight) {
          if (verticalAlign === 'top') {
            options.verticalAlign = 'bottom';
            options.inside = true;
          } else {
            options.y = chart.plotHeight - off;
          }

          justified = true;
        }

        if (justified) {
          dataLabel.placed = !isNew;
          dataLabel.align(options, null, alignTo);
        }

        return justified;
      };

      if (seriesTypes.pie) {
        seriesTypes.pie.prototype.dataLabelPositioners = {
          radialDistributionY: function radialDistributionY(point) {
            return point.top + point.distributeBox.pos;
          },
          radialDistributionX: function radialDistributionX(series, point, y, naturalY) {
            return series.getX(y < point.top + 2 || y > point.bottom - 2 ? naturalY : y, point.half, point);
          },
          justify: function justify(point, radius, seriesCenter) {
            return seriesCenter[0] + (point.half ? -1 : 1) * (radius + point.labelDistance);
          },
          alignToPlotEdges: function alignToPlotEdges(dataLabel, half, plotWidth, plotLeft) {
            var dataLabelWidth = dataLabel.getBBox().width;
            return half ? dataLabelWidth + plotLeft : plotWidth - dataLabelWidth - plotLeft;
          },
          alignToConnectors: function alignToConnectors(points, half, plotWidth, plotLeft) {
            var maxDataLabelWidth = 0,
                dataLabelWidth;
            points.forEach(function (point) {
              dataLabelWidth = point.dataLabel.getBBox().width;

              if (dataLabelWidth > maxDataLabelWidth) {
                maxDataLabelWidth = dataLabelWidth;
              }
            });
            return half ? maxDataLabelWidth + plotLeft : plotWidth - maxDataLabelWidth - plotLeft;
          }
        };

        seriesTypes.pie.prototype.drawDataLabels = function () {
          var series = this,
              data = series.data,
              point,
              chart = series.chart,
              options = series.options.dataLabels || {},
              connectorPadding = options.connectorPadding,
              connectorWidth,
              plotWidth = chart.plotWidth,
              plotHeight = chart.plotHeight,
              plotLeft = chart.plotLeft,
              maxWidth = Math.round(chart.chartWidth / 3),
              connector,
              seriesCenter = series.center,
              radius = seriesCenter[2] / 2,
              centerY = seriesCenter[1],
              dataLabel,
              dataLabelWidth,
              labelPosition,
              labelHeight,
              halves = [[], []],
              x,
              y,
              visibility,
              j,
              overflow = [0, 0, 0, 0],
              dataLabelPositioners = series.dataLabelPositioners,
              pointDataLabelsOptions;

          if (!series.visible || !options.enabled && !series._hasPointLabels) {
            return;
          }

          data.forEach(function (point) {
            if (point.dataLabel && point.visible && point.dataLabel.shortened) {
              point.dataLabel.attr({
                width: 'auto'
              }).css({
                width: 'auto',
                textOverflow: 'clip'
              });
              point.dataLabel.shortened = false;
            }
          });
          Series.prototype.drawDataLabels.apply(series);
          data.forEach(function (point) {
            if (point.dataLabel) {
              if (point.visible) {
                halves[point.half].push(point);
                point.dataLabel._pos = null;

                if (!defined(options.style.width) && !defined(point.options.dataLabels && point.options.dataLabels.style && point.options.dataLabels.style.width)) {
                  if (point.dataLabel.getBBox().width > maxWidth) {
                    point.dataLabel.css({
                      width: maxWidth * 0.7
                    });
                    point.dataLabel.shortened = true;
                  }
                }
              } else {
                point.dataLabel = point.dataLabel.destroy();

                if (point.dataLabels && point.dataLabels.length === 1) {
                  delete point.dataLabels;
                }
              }
            }
          });
          halves.forEach(function (points, i) {
            var top,
                bottom,
                length = points.length,
                positions = [],
                naturalY,
                sideOverflow,
                size,
                distributionLength;

            if (!length) {
              return;
            }

            series.sortByAngle(points, i - 0.5);

            if (series.maxLabelDistance > 0) {
              top = Math.max(0, centerY - radius - series.maxLabelDistance);
              bottom = Math.min(centerY + radius + series.maxLabelDistance, chart.plotHeight);
              points.forEach(function (point) {
                if (point.labelDistance > 0 && point.dataLabel) {
                  point.top = Math.max(0, centerY - radius - point.labelDistance);
                  point.bottom = Math.min(centerY + radius + point.labelDistance, chart.plotHeight);
                  size = point.dataLabel.getBBox().height || 21;
                  point.distributeBox = {
                    target: point.labelPosition.natural.y - point.top + size / 2,
                    size: size,
                    rank: point.y
                  };
                  positions.push(point.distributeBox);
                }
              });
              distributionLength = bottom + size - top;
              H.distribute(positions, distributionLength, distributionLength / 5);
            }

            for (j = 0; j < length; j++) {
              point = points[j];
              labelPosition = point.labelPosition;
              dataLabel = point.dataLabel;
              visibility = point.visible === false ? 'hidden' : 'inherit';
              naturalY = labelPosition.natural.y;
              y = naturalY;

              if (positions && defined(point.distributeBox)) {
                if (typeof point.distributeBox.pos === 'undefined') {
                  visibility = 'hidden';
                } else {
                  labelHeight = point.distributeBox.size;
                  y = dataLabelPositioners.radialDistributionY(point);
                }
              }

              delete point.positionIndex;

              if (options.justify) {
                x = dataLabelPositioners.justify(point, radius, seriesCenter);
              } else {
                switch (options.alignTo) {
                  case 'connectors':
                    x = dataLabelPositioners.alignToConnectors(points, i, plotWidth, plotLeft);
                    break;

                  case 'plotEdges':
                    x = dataLabelPositioners.alignToPlotEdges(dataLabel, i, plotWidth, plotLeft);
                    break;

                  default:
                    x = dataLabelPositioners.radialDistributionX(series, point, y, naturalY);
                }
              }

              dataLabel._attr = {
                visibility: visibility,
                align: labelPosition.alignment
              };
              pointDataLabelsOptions = point.options.dataLabels || {};
              dataLabel._pos = {
                x: x + pick(pointDataLabelsOptions.x, options.x) + ({
                  left: connectorPadding,
                  right: -connectorPadding
                }[labelPosition.alignment] || 0),
                y: y + pick(pointDataLabelsOptions.y, options.y) - 10
              };
              labelPosition["final"].x = x;
              labelPosition["final"].y = y;

              if (pick(options.crop, true)) {
                dataLabelWidth = dataLabel.getBBox().width;
                sideOverflow = null;

                if (x - dataLabelWidth < connectorPadding && i === 1) {
                    sideOverflow = Math.round(dataLabelWidth - x + connectorPadding);
                    overflow[3] = Math.max(sideOverflow, overflow[3]);
                  } else if (x + dataLabelWidth > plotWidth - connectorPadding && i === 0) {
                    sideOverflow = Math.round(x + dataLabelWidth - plotWidth + connectorPadding);
                    overflow[1] = Math.max(sideOverflow, overflow[1]);
                  }

                if (y - labelHeight / 2 < 0) {
                  overflow[0] = Math.max(Math.round(-y + labelHeight / 2), overflow[0]);
                } else if (y + labelHeight / 2 > plotHeight) {
                  overflow[2] = Math.max(Math.round(y + labelHeight / 2 - plotHeight), overflow[2]);
                }

                dataLabel.sideOverflow = sideOverflow;
              }
            }
          });

          if (arrayMax(overflow) === 0 || this.verifyDataLabelOverflow(overflow)) {
            this.placeDataLabels();
            this.points.forEach(function (point) {
              pointDataLabelsOptions = merge(options, point.options.dataLabels);
              connectorWidth = pick(pointDataLabelsOptions.connectorWidth, 1);

              if (connectorWidth) {
                var isNew;
                connector = point.connector;
                dataLabel = point.dataLabel;

                if (dataLabel && dataLabel._pos && point.visible && point.labelDistance > 0) {
                  visibility = dataLabel._attr.visibility;
                  isNew = !connector;

                  if (isNew) {
                    point.connector = connector = chart.renderer.path().addClass('highcharts-data-label-connector ' + ' highcharts-color-' + point.colorIndex + (point.className ? ' ' + point.className : '')).add(series.dataLabelsGroup);

                    if (!chart.styledMode) {
                      connector.attr({
                        'stroke-width': connectorWidth,
                        'stroke': pointDataLabelsOptions.connectorColor || point.color || '#666666'
                      });
                    }
                  }

                  connector[isNew ? 'attr' : 'animate']({
                    d: point.getConnectorPath()
                  });
                  connector.attr('visibility', visibility);
                } else if (connector) {
                  point.connector = connector.destroy();
                }
              }
            });
          }
        };

        seriesTypes.pie.prototype.placeDataLabels = function () {
          this.points.forEach(function (point) {
            var dataLabel = point.dataLabel,
                _pos;

            if (dataLabel && point.visible) {
              _pos = dataLabel._pos;

              if (_pos) {
                if (dataLabel.sideOverflow) {
                  dataLabel._attr.width = Math.max(dataLabel.getBBox().width - dataLabel.sideOverflow, 0);
                  dataLabel.css({
                    width: dataLabel._attr.width + 'px',
                    textOverflow: (this.options.dataLabels.style || {}).textOverflow || 'ellipsis'
                  });
                  dataLabel.shortened = true;
                }

                dataLabel.attr(dataLabel._attr);
                dataLabel[dataLabel.moved ? 'animate' : 'attr'](_pos);
                dataLabel.moved = true;
              } else if (dataLabel) {
                dataLabel.attr({
                  y: -9999
                });
              }
            }

            delete point.distributeBox;
          }, this);
        };

        seriesTypes.pie.prototype.alignDataLabel = noop;

        seriesTypes.pie.prototype.verifyDataLabelOverflow = function (overflow) {
          var center = this.center,
              options = this.options,
              centerOption = options.center,
              minSize = options.minSize || 80,
              newSize = minSize,
              ret = options.size !== null;

          if (!ret) {
            if (centerOption[0] !== null) {
              newSize = Math.max(center[2] - Math.max(overflow[1], overflow[3]), minSize);
            } else {
              newSize = Math.max(center[2] - overflow[1] - overflow[3], minSize);
              center[0] += (overflow[3] - overflow[1]) / 2;
            }

            if (centerOption[1] !== null) {
              newSize = clamp(newSize, minSize, center[2] - Math.max(overflow[0], overflow[2]));
            } else {
              newSize = clamp(newSize, minSize, center[2] - overflow[0] - overflow[2]);
              center[1] += (overflow[0] - overflow[2]) / 2;
            }

            if (newSize < center[2]) {
              center[2] = newSize;
              center[3] = Math.min(relativeLength(options.innerSize || 0, newSize), newSize);
              this.translate(center);

              if (this.drawDataLabels) {
                this.drawDataLabels();
              }
            } else {
              ret = true;
            }
          }

          return ret;
        };
      }

      if (seriesTypes.column) {
        seriesTypes.column.prototype.alignDataLabel = function (point, dataLabel, options, alignTo, isNew) {
          var inverted = this.chart.inverted,
              series = point.series,
              dlBox = point.dlBox || point.shapeArgs,
              below = pick(point.below, point.plotY > pick(this.translatedThreshold, series.yAxis.len)),
              inside = pick(options.inside, !!this.options.stacking),
              overshoot;

          if (dlBox) {
            alignTo = merge(dlBox);

            if (alignTo.y < 0) {
              alignTo.height += alignTo.y;
              alignTo.y = 0;
            }

            overshoot = alignTo.y + alignTo.height - series.yAxis.len;

            if (overshoot > 0 && overshoot < alignTo.height) {
              alignTo.height -= overshoot;
            }

            if (inverted) {
              alignTo = {
                x: series.yAxis.len - alignTo.y - alignTo.height,
                y: series.xAxis.len - alignTo.x - alignTo.width,
                width: alignTo.height,
                height: alignTo.width
              };
            }

            if (!inside) {
              if (inverted) {
                alignTo.x += below ? 0 : alignTo.width;
                alignTo.width = 0;
              } else {
                alignTo.y += below ? alignTo.height : 0;
                alignTo.height = 0;
              }
            }
          }

          options.align = pick(options.align, !inverted || inside ? 'center' : below ? 'right' : 'left');
          options.verticalAlign = pick(options.verticalAlign, inverted || inside ? 'middle' : below ? 'top' : 'bottom');
          Series.prototype.alignDataLabel.call(this, point, dataLabel, options, alignTo, isNew);

          if (options.inside && point.contrastColor) {
            dataLabel.css({
              color: point.contrastColor
            });
          }
        };
      }
    });

    _registerModule(_modules, 'modules/overlapping-datalabels.src.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var addEvent = U.addEvent,
          fireEvent = U.fireEvent,
          isArray = U.isArray,
          objectEach = U.objectEach,
          pick = U.pick;
      var Chart = H.Chart;
      addEvent(Chart, 'render', function collectAndHide() {
        var labels = [];
        (this.labelCollectors || []).forEach(function (collector) {
          labels = labels.concat(collector());
        });
        (this.yAxis || []).forEach(function (yAxis) {
          if (yAxis.options.stackLabels && !yAxis.options.stackLabels.allowOverlap) {
            objectEach(yAxis.stacks, function (stack) {
              objectEach(stack, function (stackItem) {
                labels.push(stackItem.label);
              });
            });
          }
        });
        (this.series || []).forEach(function (series) {
          var dlOptions = series.options.dataLabels;

          if (series.visible && !(dlOptions.enabled === false && !series._hasPointLabels)) {
            (series.nodes || series.points).forEach(function (point) {
              if (point.visible) {
                var dataLabels = isArray(point.dataLabels) ? point.dataLabels : point.dataLabel ? [point.dataLabel] : [];
                dataLabels.forEach(function (label) {
                  var options = label.options;
                  label.labelrank = pick(options.labelrank, point.labelrank, point.shapeArgs && point.shapeArgs.height);

                  if (!options.allowOverlap) {
                    labels.push(label);
                  }
                });
              }
            });
          }
        });
        this.hideOverlappingLabels(labels);
      });

      Chart.prototype.hideOverlappingLabels = function (labels) {
        var chart = this,
            len = labels.length,
            ren = chart.renderer,
            label,
            i,
            j,
            label1,
            label2,
            box1,
            box2,
            isLabelAffected = false,
            isIntersectRect = function isIntersectRect(box1, box2) {
          return !(box2.x > box1.x + box1.width || box2.x + box2.width < box1.x || box2.y > box1.y + box1.height || box2.y + box2.height < box1.y);
        },
            getAbsoluteBox = function getAbsoluteBox(label) {
          var pos,
              parent,
              bBox,
              padding = label.box ? 0 : label.padding || 0,
              lineHeightCorrection = 0;

          if (label && (!label.alignAttr || label.placed)) {
            pos = label.alignAttr || {
              x: label.attr('x'),
              y: label.attr('y')
            };
            parent = label.parentGroup;

            if (!label.width) {
              bBox = label.getBBox();
              label.width = bBox.width;
              label.height = bBox.height;
              lineHeightCorrection = ren.fontMetrics(null, label.element).h;
            }

            return {
              x: pos.x + (parent.translateX || 0) + padding,
              y: pos.y + (parent.translateY || 0) + padding - lineHeightCorrection,
              width: label.width - 2 * padding,
              height: label.height - 2 * padding
            };
          }
        };

        for (i = 0; i < len; i++) {
          label = labels[i];

          if (label) {
            label.oldOpacity = label.opacity;
            label.newOpacity = 1;
            label.absoluteBox = getAbsoluteBox(label);
          }
        }

        labels.sort(function (a, b) {
          return (b.labelrank || 0) - (a.labelrank || 0);
        });

        for (i = 0; i < len; i++) {
          label1 = labels[i];
          box1 = label1 && label1.absoluteBox;

          for (j = i + 1; j < len; ++j) {
            label2 = labels[j];
            box2 = label2 && label2.absoluteBox;

            if (box1 && box2 && label1 !== label2 && label1.newOpacity !== 0 && label2.newOpacity !== 0) {
              if (isIntersectRect(box1, box2)) {
                (label1.labelrank < label2.labelrank ? label1 : label2).newOpacity = 0;
              }
            }
          }
        }

        labels.forEach(function (label) {
          var complete, newOpacity;

          if (label) {
            newOpacity = label.newOpacity;

            if (label.oldOpacity !== newOpacity) {
              if (label.alignAttr && label.placed) {
                if (newOpacity) {
                  label.show(true);
                } else {
                  complete = function complete() {
                    label.hide(true);
                    label.placed = false;
                  };
                }

                isLabelAffected = true;
                label.alignAttr.opacity = newOpacity;
                label[label.isOld ? 'animate' : 'attr'](label.alignAttr, null, complete);
                fireEvent(chart, 'afterHideOverlappingLabel');
              } else {
                label.attr({
                  opacity: newOpacity
                });
              }
            }

            label.isOld = true;
          }
        });

        if (isLabelAffected) {
          fireEvent(chart, 'afterHideAllOverlappingLabels');
        }
      };
    });

    _registerModule(_modules, 'parts/Interaction.js', [_modules['parts/Globals.js'], _modules['parts/Legend.js'], _modules['parts/Point.js'], _modules['parts/Utilities.js']], function (H, Legend, Point, U) {
      var addEvent = U.addEvent,
          createElement = U.createElement,
          css = U.css,
          defined = U.defined,
          extend = U.extend,
          fireEvent = U.fireEvent,
          isArray = U.isArray,
          isFunction = U.isFunction,
          isObject = U.isObject,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick;
      var Chart = H.Chart,
          defaultOptions = H.defaultOptions,
          defaultPlotOptions = H.defaultPlotOptions,
          hasTouch = H.hasTouch,
          Series = H.Series,
          seriesTypes = H.seriesTypes,
          svg = H.svg,
          TrackerMixin;
      TrackerMixin = H.TrackerMixin = {
        drawTrackerPoint: function drawTrackerPoint() {
          var series = this,
              chart = series.chart,
              pointer = chart.pointer,
              onMouseOver = function onMouseOver(e) {
            var point = pointer.getPointFromEvent(e);

            if (typeof point !== 'undefined') {
              pointer.isDirectTouch = true;
              point.onMouseOver(e);
            }
          },
              dataLabels;

          series.points.forEach(function (point) {
            dataLabels = isArray(point.dataLabels) ? point.dataLabels : point.dataLabel ? [point.dataLabel] : [];

            if (point.graphic) {
              point.graphic.element.point = point;
            }

            dataLabels.forEach(function (dataLabel) {
              if (dataLabel.div) {
                dataLabel.div.point = point;
              } else {
                dataLabel.element.point = point;
              }
            });
          });

          if (!series._hasTracking) {
            series.trackerGroups.forEach(function (key) {
              if (series[key]) {
                series[key].addClass('highcharts-tracker').on('mouseover', onMouseOver).on('mouseout', function (e) {
                  pointer.onTrackerMouseOut(e);
                });

                if (hasTouch) {
                  series[key].on('touchstart', onMouseOver);
                }

                if (!chart.styledMode && series.options.cursor) {
                  series[key].css(css).css({
                    cursor: series.options.cursor
                  });
                }
              }
            });
            series._hasTracking = true;
          }

          fireEvent(this, 'afterDrawTracker');
        },
        drawTrackerGraph: function drawTrackerGraph() {
          var series = this,
              options = series.options,
              trackByArea = options.trackByArea,
              trackerPath = [].concat(trackByArea ? series.areaPath : series.graphPath),
              trackerPathLength = trackerPath.length,
              chart = series.chart,
              pointer = chart.pointer,
              renderer = chart.renderer,
              snap = chart.options.tooltip.snap,
              tracker = series.tracker,
              i,
              onMouseOver = function onMouseOver() {
            if (chart.hoverSeries !== series) {
              series.onMouseOver();
            }
          },
              TRACKER_FILL = 'rgba(192,192,192,' + (svg ? 0.0001 : 0.002) + ')';

          if (trackerPathLength && !trackByArea) {
            i = trackerPathLength + 1;

            while (i--) {
              if (trackerPath[i] === 'M') {
                trackerPath.splice(i + 1, 0, trackerPath[i + 1] - snap, trackerPath[i + 2], 'L');
              }

              if (i && trackerPath[i] === 'M' || i === trackerPathLength) {
                trackerPath.splice(i, 0, 'L', trackerPath[i - 2] + snap, trackerPath[i - 1]);
              }
            }
          }

          if (tracker) {
            tracker.attr({
              d: trackerPath
            });
          } else if (series.graph) {
            series.tracker = renderer.path(trackerPath).attr({
              visibility: series.visible ? 'visible' : 'hidden',
              zIndex: 2
            }).addClass(trackByArea ? 'highcharts-tracker-area' : 'highcharts-tracker-line').add(series.group);

            if (!chart.styledMode) {
              series.tracker.attr({
                'stroke-linejoin': 'round',
                stroke: TRACKER_FILL,
                fill: trackByArea ? TRACKER_FILL : 'none',
                'stroke-width': series.graph.strokeWidth() + (trackByArea ? 0 : 2 * snap)
              });
            }

            [series.tracker, series.markerGroup].forEach(function (tracker) {
              tracker.addClass('highcharts-tracker').on('mouseover', onMouseOver).on('mouseout', function (e) {
                pointer.onTrackerMouseOut(e);
              });

              if (options.cursor && !chart.styledMode) {
                tracker.css({
                  cursor: options.cursor
                });
              }

              if (hasTouch) {
                tracker.on('touchstart', onMouseOver);
              }
            });
          }

          fireEvent(this, 'afterDrawTracker');
        }
      };

      if (seriesTypes.column) {
        seriesTypes.column.prototype.drawTracker = TrackerMixin.drawTrackerPoint;
      }

      if (seriesTypes.pie) {
        seriesTypes.pie.prototype.drawTracker = TrackerMixin.drawTrackerPoint;
      }

      if (seriesTypes.scatter) {
        seriesTypes.scatter.prototype.drawTracker = TrackerMixin.drawTrackerPoint;
      }

      extend(Legend.prototype, {
        setItemEvents: function setItemEvents(item, legendItem, useHTML) {
          var legend = this,
              boxWrapper = legend.chart.renderer.boxWrapper,
              isPoint = item instanceof Point,
              activeClass = 'highcharts-legend-' + (isPoint ? 'point' : 'series') + '-active',
              styledMode = legend.chart.styledMode,
              legendItems = useHTML ? [legendItem, item.legendSymbol] : [item.legendGroup];
          legendItems.forEach(function (element) {
            if (element) {
              element.on('mouseover', function () {
                if (item.visible) {
                  legend.allItems.forEach(function (inactiveItem) {
                    if (item !== inactiveItem) {
                      inactiveItem.setState('inactive', !isPoint);
                    }
                  });
                }

                item.setState('hover');

                if (item.visible) {
                  boxWrapper.addClass(activeClass);
                }

                if (!styledMode) {
                  legendItem.css(legend.options.itemHoverStyle);
                }
              }).on('mouseout', function () {
                if (!legend.chart.styledMode) {
                  legendItem.css(merge(item.visible ? legend.itemStyle : legend.itemHiddenStyle));
                }

                legend.allItems.forEach(function (inactiveItem) {
                  if (item !== inactiveItem) {
                    inactiveItem.setState('', !isPoint);
                  }
                });
                boxWrapper.removeClass(activeClass);
                item.setState();
              }).on('click', function (event) {
                var strLegendItemClick = 'legendItemClick',
                    fnLegendItemClick = function fnLegendItemClick() {
                  if (item.setVisible) {
                    item.setVisible();
                  }

                  legend.allItems.forEach(function (inactiveItem) {
                    if (item !== inactiveItem) {
                      inactiveItem.setState(item.visible ? 'inactive' : '', !isPoint);
                    }
                  });
                };

                boxWrapper.removeClass(activeClass);
                event = {
                  browserEvent: event
                };

                if (item.firePointEvent) {
                  item.firePointEvent(strLegendItemClick, event, fnLegendItemClick);
                } else {
                  fireEvent(item, strLegendItemClick, event, fnLegendItemClick);
                }
              });
            }
          });
        },
        createCheckboxForItem: function createCheckboxForItem(item) {
          var legend = this;
          item.checkbox = createElement('input', {
            type: 'checkbox',
            className: 'highcharts-legend-checkbox',
            checked: item.selected,
            defaultChecked: item.selected
          }, legend.options.itemCheckboxStyle, legend.chart.container);
          addEvent(item.checkbox, 'click', function (event) {
            var target = event.target;
            fireEvent(item.series || item, 'checkboxClick', {
              checked: target.checked,
              item: item
            }, function () {
              item.select();
            });
          });
        }
      });
      extend(Chart.prototype, {
        showResetZoom: function showResetZoom() {
          var chart = this,
              lang = defaultOptions.lang,
              btnOptions = chart.options.chart.resetZoomButton,
              theme = btnOptions.theme,
              states = theme.states,
              alignTo = btnOptions.relativeTo === 'chart' || btnOptions.relativeTo === 'spaceBox' ? null : 'plotBox';

          function zoomOut() {
            chart.zoomOut();
          }

          fireEvent(this, 'beforeShowResetZoom', null, function () {
            chart.resetZoomButton = chart.renderer.button(lang.resetZoom, null, null, zoomOut, theme, states && states.hover).attr({
              align: btnOptions.position.align,
              title: lang.resetZoomTitle
            }).addClass('highcharts-reset-zoom').add().align(btnOptions.position, false, alignTo);
          });
          fireEvent(this, 'afterShowResetZoom');
        },
        zoomOut: function zoomOut() {
          fireEvent(this, 'selection', {
            resetSelection: true
          }, this.zoom);
        },
        zoom: function zoom(event) {
          var chart = this,
              hasZoomed,
              pointer = chart.pointer,
              displayButton = false,
              mouseDownPos = chart.inverted ? pointer.mouseDownX : pointer.mouseDownY,
              resetZoomButton;

          if (!event || event.resetSelection) {
            chart.axes.forEach(function (axis) {
              hasZoomed = axis.zoom();
            });
            pointer.initiated = false;
          } else {
            event.xAxis.concat(event.yAxis).forEach(function (axisData) {
              var axis = axisData.axis,
                  axisStartPos = chart.inverted ? axis.left : axis.top,
                  axisEndPos = chart.inverted ? axisStartPos + axis.width : axisStartPos + axis.height,
                  isXAxis = axis.isXAxis,
                  isWithinPane = false;

              if (!isXAxis && mouseDownPos >= axisStartPos && mouseDownPos <= axisEndPos || isXAxis || !defined(mouseDownPos)) {
                isWithinPane = true;
              }

              if (pointer[isXAxis ? 'zoomX' : 'zoomY'] && isWithinPane) {
                hasZoomed = axis.zoom(axisData.min, axisData.max);

                if (axis.displayBtn) {
                  displayButton = true;
                }
              }
            });
          }

          resetZoomButton = chart.resetZoomButton;

          if (displayButton && !resetZoomButton) {
            chart.showResetZoom();
          } else if (!displayButton && isObject(resetZoomButton)) {
            chart.resetZoomButton = resetZoomButton.destroy();
          }

          if (hasZoomed) {
            chart.redraw(pick(chart.options.chart.animation, event && event.animation, chart.pointCount < 100));
          }
        },
        pan: function pan(e, panning) {
          var chart = this,
              hoverPoints = chart.hoverPoints,
              panningOptions,
              chartOptions = chart.options.chart,
              doRedraw,
              type;

          if (typeof panning === 'object') {
            panningOptions = panning;
          } else {
            panningOptions = {
              enabled: panning,
              type: 'x'
            };
          }

          if (chartOptions && chartOptions.panning) {
            chartOptions.panning = panningOptions;
          }

          type = panningOptions.type;
          fireEvent(this, 'pan', {
            originalEvent: e
          }, function () {
            if (hoverPoints) {
              hoverPoints.forEach(function (point) {
                point.setState();
              });
            }

            var xy = [1];

            if (type === 'xy') {
              xy = [1, 0];
            } else if (type === 'y') {
              xy = [0];
            }

            xy.forEach(function (isX) {
              var axis = chart[isX ? 'xAxis' : 'yAxis'][0],
                  axisOpt = axis.options,
                  horiz = axis.horiz,
                  mousePos = e[horiz ? 'chartX' : 'chartY'],
                  mouseDown = horiz ? 'mouseDownX' : 'mouseDownY',
                  startPos = chart[mouseDown],
                  halfPointRange = (axis.pointRange || 0) / 2,
                  pointRangeDirection = axis.reversed && !chart.inverted || !axis.reversed && chart.inverted ? -1 : 1,
                  extremes = axis.getExtremes(),
                  panMin = axis.toValue(startPos - mousePos, true) + halfPointRange * pointRangeDirection,
                  panMax = axis.toValue(startPos + axis.len - mousePos, true) - halfPointRange * pointRangeDirection,
                  flipped = panMax < panMin,
                  newMin = flipped ? panMax : panMin,
                  newMax = flipped ? panMin : panMax,
                  paddedMin = Math.min(extremes.dataMin, halfPointRange ? extremes.min : axis.toValue(axis.toPixels(extremes.min) - axis.minPixelPadding)),
                  paddedMax = Math.max(extremes.dataMax, halfPointRange ? extremes.max : axis.toValue(axis.toPixels(extremes.max) + axis.minPixelPadding)),
                  spill;

              if (!axisOpt.ordinal) {
                if (isX) {
                  spill = paddedMin - newMin;

                  if (spill > 0) {
                    newMax += spill;
                    newMin = paddedMin;
                  }

                  spill = newMax - paddedMax;

                  if (spill > 0) {
                    newMax = paddedMax;
                    newMin -= spill;
                  }
                }

                if (axis.series.length && newMin !== extremes.min && newMax !== extremes.max && isX ? true : axis.panningState && newMin >= axis.panningState.startMin && newMax <= axis.panningState.startMax) {
                  axis.setExtremes(newMin, newMax, false, false, {
                    trigger: 'pan'
                  });
                  doRedraw = true;
                }

                chart[mouseDown] = mousePos;
              }
            });

            if (doRedraw) {
              chart.redraw(false);
            }

            css(chart.container, {
              cursor: 'move'
            });
          });
        }
      });
      extend(Point.prototype, {
        select: function select(selected, accumulate) {
          var point = this,
              series = point.series,
              chart = series.chart;
          selected = pick(selected, !point.selected);
          this.selectedStaging = selected;
          point.firePointEvent(selected ? 'select' : 'unselect', {
            accumulate: accumulate
          }, function () {
            point.selected = point.options.selected = selected;
            series.options.data[series.data.indexOf(point)] = point.options;
            point.setState(selected && 'select');

            if (!accumulate) {
              chart.getSelectedPoints().forEach(function (loopPoint) {
                var loopSeries = loopPoint.series;

                if (loopPoint.selected && loopPoint !== point) {
                  loopPoint.selected = loopPoint.options.selected = false;
                  loopSeries.options.data[loopSeries.data.indexOf(loopPoint)] = loopPoint.options;
                  loopPoint.setState(chart.hoverPoints && loopSeries.options.inactiveOtherPoints ? 'inactive' : '');
                  loopPoint.firePointEvent('unselect');
                }
              });
            }
          });
          delete this.selectedStaging;
        },
        onMouseOver: function onMouseOver(e) {
          var point = this,
              series = point.series,
              chart = series.chart,
              pointer = chart.pointer;
          e = e ? pointer.normalize(e) : pointer.getChartCoordinatesFromPoint(point, chart.inverted);
          pointer.runPointActions(e, point);
        },
        onMouseOut: function onMouseOut() {
          var point = this,
              chart = point.series.chart;
          point.firePointEvent('mouseOut');

          if (!point.series.options.inactiveOtherPoints) {
            (chart.hoverPoints || []).forEach(function (p) {
              p.setState();
            });
          }

          chart.hoverPoints = chart.hoverPoint = null;
        },
        importEvents: function importEvents() {
          if (!this.hasImportedEvents) {
            var point = this,
                options = merge(point.series.options.point, point.options),
                events = options.events;
            point.events = events;
            objectEach(events, function (event, eventType) {
              if (isFunction(event)) {
                addEvent(point, eventType, event);
              }
            });
            this.hasImportedEvents = true;
          }
        },
        setState: function setState(state, move) {
          var point = this,
              series = point.series,
              previousState = point.state,
              stateOptions = series.options.states[state || 'normal'] || {},
              markerOptions = defaultPlotOptions[series.type].marker && series.options.marker,
              normalDisabled = markerOptions && markerOptions.enabled === false,
              markerStateOptions = markerOptions && markerOptions.states && markerOptions.states[state || 'normal'] || {},
              stateDisabled = markerStateOptions.enabled === false,
              stateMarkerGraphic = series.stateMarkerGraphic,
              pointMarker = point.marker || {},
              chart = series.chart,
              halo = series.halo,
              haloOptions,
              markerAttribs,
              pointAttribs,
              pointAttribsAnimation,
              hasMarkers = markerOptions && series.markerAttribs,
              newSymbol;
          state = state || '';

          if (state === point.state && !move || point.selected && state !== 'select' || stateOptions.enabled === false || state && (stateDisabled || normalDisabled && markerStateOptions.enabled === false) || state && pointMarker.states && pointMarker.states[state] && pointMarker.states[state].enabled === false) {
              return;
            }

          point.state = state;

          if (hasMarkers) {
            markerAttribs = series.markerAttribs(point, state);
          }

          if (point.graphic) {
            if (previousState) {
              point.graphic.removeClass('highcharts-point-' + previousState);
            }

            if (state) {
              point.graphic.addClass('highcharts-point-' + state);
            }

            if (!chart.styledMode) {
              pointAttribs = series.pointAttribs(point, state);
              pointAttribsAnimation = pick(chart.options.chart.animation, stateOptions.animation);

              if (series.options.inactiveOtherPoints) {
                (point.dataLabels || []).forEach(function (label) {
                  if (label) {
                    label.animate({
                      opacity: pointAttribs.opacity
                    }, pointAttribsAnimation);
                  }
                });

                if (point.connector) {
                  point.connector.animate({
                    opacity: pointAttribs.opacity
                  }, pointAttribsAnimation);
                }
              }

              point.graphic.animate(pointAttribs, pointAttribsAnimation);
            }

            if (markerAttribs) {
              point.graphic.animate(markerAttribs, pick(chart.options.chart.animation, markerStateOptions.animation, markerOptions.animation));
            }

            if (stateMarkerGraphic) {
              stateMarkerGraphic.hide();
            }
          } else {
            if (state && markerStateOptions) {
              newSymbol = pointMarker.symbol || series.symbol;

              if (stateMarkerGraphic && stateMarkerGraphic.currentSymbol !== newSymbol) {
                stateMarkerGraphic = stateMarkerGraphic.destroy();
              }

              if (markerAttribs) {
                if (!stateMarkerGraphic) {
                  if (newSymbol) {
                    series.stateMarkerGraphic = stateMarkerGraphic = chart.renderer.symbol(newSymbol, markerAttribs.x, markerAttribs.y, markerAttribs.width, markerAttribs.height).add(series.markerGroup);
                    stateMarkerGraphic.currentSymbol = newSymbol;
                  }
                } else {
                  stateMarkerGraphic[move ? 'animate' : 'attr']({
                    x: markerAttribs.x,
                    y: markerAttribs.y
                  });
                }
              }

              if (!chart.styledMode && stateMarkerGraphic) {
                stateMarkerGraphic.attr(series.pointAttribs(point, state));
              }
            }

            if (stateMarkerGraphic) {
              stateMarkerGraphic[state && point.isInside ? 'show' : 'hide']();
              stateMarkerGraphic.element.point = point;
            }
          }

          haloOptions = stateOptions.halo;
          var markerGraphic = point.graphic || stateMarkerGraphic;
          var markerVisibility = markerGraphic && markerGraphic.visibility || 'inherit';

          if (haloOptions && haloOptions.size && markerGraphic && markerVisibility !== 'hidden' && !point.isCluster) {
            if (!halo) {
              series.halo = halo = chart.renderer.path().add(markerGraphic.parentGroup);
            }

            halo.show()[move ? 'animate' : 'attr']({
              d: point.haloPath(haloOptions.size)
            });
            halo.attr({
              'class': 'highcharts-halo highcharts-color-' + pick(point.colorIndex, series.colorIndex) + (point.className ? ' ' + point.className : ''),
              'visibility': markerVisibility,
              'zIndex': -1
            });
            halo.point = point;

            if (!chart.styledMode) {
              halo.attr(extend({
                'fill': point.color || series.color,
                'fill-opacity': haloOptions.opacity
              }, haloOptions.attributes));
            }
          } else if (halo && halo.point && halo.point.haloPath) {
            halo.animate({
              d: halo.point.haloPath(0)
            }, null, halo.hide);
          }

          fireEvent(point, 'afterSetState');
        },
        haloPath: function haloPath(size) {
          var series = this.series,
              chart = series.chart;
          return chart.renderer.symbols.circle(Math.floor(this.plotX) - size, this.plotY - size, size * 2, size * 2);
        }
      });
      extend(Series.prototype, {
        onMouseOver: function onMouseOver() {
          var series = this,
              chart = series.chart,
              hoverSeries = chart.hoverSeries;

          if (hoverSeries && hoverSeries !== series) {
            hoverSeries.onMouseOut();
          }

          if (series.options.events.mouseOver) {
            fireEvent(series, 'mouseOver');
          }

          series.setState('hover');
          chart.hoverSeries = series;
        },
        onMouseOut: function onMouseOut() {
          var series = this,
              options = series.options,
              chart = series.chart,
              tooltip = chart.tooltip,
              hoverPoint = chart.hoverPoint;
          chart.hoverSeries = null;

          if (hoverPoint) {
            hoverPoint.onMouseOut();
          }

          if (series && options.events.mouseOut) {
            fireEvent(series, 'mouseOut');
          }

          if (tooltip && !series.stickyTracking && (!tooltip.shared || series.noSharedTooltip)) {
            tooltip.hide();
          }

          chart.series.forEach(function (s) {
            s.setState('', true);
          });
        },
        setState: function setState(state, inherit) {
          var series = this,
              options = series.options,
              graph = series.graph,
              inactiveOtherPoints = options.inactiveOtherPoints,
              stateOptions = options.states,
              lineWidth = options.lineWidth,
              opacity = options.opacity,
              stateAnimation = pick(stateOptions[state || 'normal'] && stateOptions[state || 'normal'].animation, series.chart.options.chart.animation),
              attribs,
              i = 0;
          state = state || '';

          if (series.state !== state) {
            [series.group, series.markerGroup, series.dataLabelsGroup].forEach(function (group) {
              if (group) {
                if (series.state) {
                  group.removeClass('highcharts-series-' + series.state);
                }

                if (state) {
                  group.addClass('highcharts-series-' + state);
                }
              }
            });
            series.state = state;

            if (!series.chart.styledMode) {
              if (stateOptions[state] && stateOptions[state].enabled === false) {
                return;
              }

              if (state) {
                lineWidth = stateOptions[state].lineWidth || lineWidth + (stateOptions[state].lineWidthPlus || 0);
                opacity = pick(stateOptions[state].opacity, opacity);
              }

              if (graph && !graph.dashstyle) {
                attribs = {
                  'stroke-width': lineWidth
                };
                graph.animate(attribs, stateAnimation);

                while (series['zone-graph-' + i]) {
                  series['zone-graph-' + i].attr(attribs);
                  i = i + 1;
                }
              }

              if (!inactiveOtherPoints) {
                [series.group, series.markerGroup, series.dataLabelsGroup, series.labelBySeries].forEach(function (group) {
                  if (group) {
                    group.animate({
                      opacity: opacity
                    }, stateAnimation);
                  }
                });
              }
            }
          }

          if (inherit && inactiveOtherPoints && series.points) {
            series.setAllPointsToState(state);
          }
        },
        setAllPointsToState: function setAllPointsToState(state) {
          this.points.forEach(function (point) {
            if (point.setState) {
              point.setState(state);
            }
          });
        },
        setVisible: function setVisible(vis, redraw) {
          var series = this,
              chart = series.chart,
              legendItem = series.legendItem,
              showOrHide,
              ignoreHiddenSeries = chart.options.chart.ignoreHiddenSeries,
              oldVisibility = series.visible;
          series.visible = vis = series.options.visible = series.userOptions.visible = typeof vis === 'undefined' ? !oldVisibility : vis;
          showOrHide = vis ? 'show' : 'hide';
          ['group', 'dataLabelsGroup', 'markerGroup', 'tracker', 'tt'].forEach(function (key) {
            if (series[key]) {
              series[key][showOrHide]();
            }
          });

          if (chart.hoverSeries === series || (chart.hoverPoint && chart.hoverPoint.series) === series) {
            series.onMouseOut();
          }

          if (legendItem) {
            chart.legend.colorizeItem(series, vis);
          }

          series.isDirty = true;

          if (series.options.stacking) {
            chart.series.forEach(function (otherSeries) {
              if (otherSeries.options.stacking && otherSeries.visible) {
                otherSeries.isDirty = true;
              }
            });
          }

          series.linkedSeries.forEach(function (otherSeries) {
            otherSeries.setVisible(vis, false);
          });

          if (ignoreHiddenSeries) {
            chart.isDirtyBox = true;
          }

          fireEvent(series, showOrHide);

          if (redraw !== false) {
            chart.redraw();
          }
        },
        show: function show() {
          this.setVisible(true);
        },
        hide: function hide() {
          this.setVisible(false);
        },
        select: function select(selected) {
          var series = this;
          series.selected = selected = this.options.selected = typeof selected === 'undefined' ? !series.selected : selected;

          if (series.checkbox) {
            series.checkbox.checked = selected;
          }

          fireEvent(series, selected ? 'select' : 'unselect');
        },
        drawTracker: TrackerMixin.drawTrackerGraph
      });
    });

    _registerModule(_modules, 'parts/Responsive.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var find = U.find,
          isArray = U.isArray,
          isObject = U.isObject,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick,
          splat = U.splat,
          uniqueKey = U.uniqueKey;
      var Chart = H.Chart;

      Chart.prototype.setResponsive = function (redraw, reset) {
        var options = this.options.responsive,
            ruleIds = [],
            currentResponsive = this.currentResponsive,
            currentRuleIds,
            undoOptions;

        if (!reset && options && options.rules) {
          options.rules.forEach(function (rule) {
            if (typeof rule._id === 'undefined') {
              rule._id = uniqueKey();
            }

            this.matchResponsiveRule(rule, ruleIds);
          }, this);
        }

        var mergedOptions = merge.apply(0, ruleIds.map(function (ruleId) {
          return find(options.rules, function (rule) {
            return rule._id === ruleId;
          }).chartOptions;
        }));
        mergedOptions.isResponsiveOptions = true;
        ruleIds = ruleIds.toString() || void 0;
        currentRuleIds = currentResponsive && currentResponsive.ruleIds;

        if (ruleIds !== currentRuleIds) {
          if (currentResponsive) {
            this.update(currentResponsive.undoOptions, redraw, true);
          }

          if (ruleIds) {
            undoOptions = this.currentOptions(mergedOptions);
            undoOptions.isResponsiveOptions = true;
            this.currentResponsive = {
              ruleIds: ruleIds,
              mergedOptions: mergedOptions,
              undoOptions: undoOptions
            };
            this.update(mergedOptions, redraw, true);
          } else {
            this.currentResponsive = void 0;
          }
        }
      };

      Chart.prototype.matchResponsiveRule = function (rule, matches) {
        var condition = rule.condition,
            fn = condition.callback || function () {
          return this.chartWidth <= pick(condition.maxWidth, Number.MAX_VALUE) && this.chartHeight <= pick(condition.maxHeight, Number.MAX_VALUE) && this.chartWidth >= pick(condition.minWidth, 0) && this.chartHeight >= pick(condition.minHeight, 0);
        };

        if (fn.call(this)) {
          matches.push(rule._id);
        }
      };

      Chart.prototype.currentOptions = function (options) {
        var chart = this,
            ret = {};

        function getCurrent(options, curr, ret, depth) {
          var i;
          objectEach(options, function (val, key) {
            if (!depth && chart.collectionsWithUpdate.indexOf(key) > -1) {
              val = splat(val);
              ret[key] = [];

              for (i = 0; i < val.length; i++) {
                if (curr[key][i]) {
                  ret[key][i] = {};
                  getCurrent(val[i], curr[key][i], ret[key][i], depth + 1);
                }
              }
            } else if (isObject(val)) {
              ret[key] = isArray(val) ? [] : {};
              getCurrent(val, curr[key] || {}, ret[key], depth + 1);
            } else if (typeof curr[key] === 'undefined') {
              ret[key] = null;
            } else {
              ret[key] = curr[key];
            }
          });
        }

        getCurrent(options, this.options, ret, 0);
        return ret;
      };
    });

    _registerModule(_modules, 'masters/highcharts.src.js', [_modules['parts/Globals.js']], function (Highcharts) {
      return Highcharts;
    });

    _registerModule(_modules, 'parts-gantt/CurrentDateIndicator.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js'], _modules['parts/PlotLineOrBand.js']], function (H, U, PlotLineOrBand) {
      var addEvent = U.addEvent,
          merge = U.merge,
          wrap = U.wrap;
      var Axis = H.Axis;
      var defaultConfig = {
        currentDateIndicator: true,
        color: '#ccd6eb',
        width: 2,
        label: {
          format: '%a, %b %d %Y, %H:%M',
          formatter: function formatter(value, format) {
            return H.dateFormat(format, value);
          },
          rotation: 0,
          style: {
            fontSize: '10px'
          }
        }
      };
      addEvent(Axis, 'afterSetOptions', function () {
        var options = this.options,
            cdiOptions = options.currentDateIndicator;

        if (cdiOptions) {
          cdiOptions = typeof cdiOptions === 'object' ? merge(defaultConfig, cdiOptions) : merge(defaultConfig);
          cdiOptions.value = new Date();

          if (!options.plotLines) {
            options.plotLines = [];
          }

          options.plotLines.push(cdiOptions);
        }
      });
      addEvent(PlotLineOrBand, 'render', function () {
        if (this.label) {
          this.label.attr({
            text: this.getLabelText(this.options.label)
          });
        }
      });
      wrap(PlotLineOrBand.prototype, 'getLabelText', function (defaultMethod, defaultLabelOptions) {
        var options = this.options;

        if (options.currentDateIndicator && options.label && typeof options.label.formatter === 'function') {
          options.value = new Date();
          return options.label.formatter.call(this, options.value, options.label.format);
        }

        return defaultMethod.call(this, defaultLabelOptions);
      });
    });

    _registerModule(_modules, 'parts-gantt/GridAxis.js', [_modules['parts/Globals.js'], _modules['parts/Tick.js'], _modules['parts/Utilities.js']], function (H, Tick, U) {
      var addEvent = U.addEvent,
          defined = U.defined,
          erase = U.erase,
          find = U.find,
          isArray = U.isArray,
          isNumber = U.isNumber,
          merge = U.merge,
          pick = U.pick,
          timeUnits = U.timeUnits,
          wrap = U.wrap;

      var argsToArray = function argsToArray(args) {
        return Array.prototype.slice.call(args, 1);
      },
          dateFormat = H.dateFormat,
          isObject = function isObject(x) {
        return U.isObject(x, true);
      },
          Chart = H.Chart,
          Axis = H.Axis;

      var applyGridOptions = function applyGridOptions(axis) {
        var options = axis.options;

        if (!options.labels) {
          options.labels = {};
        }

        options.labels.align = pick(options.labels.align, 'center');

        if (!axis.categories) {
          options.showLastLabel = false;
        }

        axis.labelRotation = 0;
        options.labels.rotation = 0;
      };

      var axisSide = {
        top: 0,
        right: 1,
        bottom: 2,
        left: 3,
        0: 'top',
        1: 'right',
        2: 'bottom',
        3: 'left'
      };

      Axis.prototype.isOuterAxis = function () {
        var axis = this,
            chart = axis.chart,
            columnIndex = axis.columnIndex,
            columns = axis.linkedParent && axis.linkedParent.columns || axis.columns,
            parentAxis = columnIndex ? axis.linkedParent : axis,
            thisIndex = -1,
            lastIndex = 0;
        chart[axis.coll].forEach(function (otherAxis, index) {
          if (otherAxis.side === axis.side && !otherAxis.options.isInternal) {
            lastIndex = index;

            if (otherAxis === parentAxis) {
              thisIndex = index;
            }
          }
        });
        return lastIndex === thisIndex && (isNumber(columnIndex) ? columns.length === columnIndex : true);
      };

      Axis.prototype.getMaxLabelDimensions = function (ticks, tickPositions) {
        var dimensions = {
          width: 0,
          height: 0
        };
        tickPositions.forEach(function (pos) {
          var tick = ticks[pos],
              tickHeight = 0,
              tickWidth = 0,
              label;

          if (isObject(tick)) {
            label = isObject(tick.label) ? tick.label : {};
            tickHeight = label.getBBox ? label.getBBox().height : 0;

            if (label.textStr && !isNumber(label.textPxLength)) {
              label.textPxLength = label.getBBox().width;
            }

            tickWidth = isNumber(label.textPxLength) ? Math.round(label.textPxLength) : 0;
            dimensions.height = Math.max(tickHeight, dimensions.height);
            dimensions.width = Math.max(tickWidth, dimensions.width);
          }
        });
        return dimensions;
      };

      H.dateFormats.W = function (timestamp) {
        var d = new this.Date(timestamp);
        var firstDay = (this.get('Day', d) + 6) % 7;
        var thursday = new this.Date(d.valueOf());
        this.set('Date', thursday, this.get('Date', d) - firstDay + 3);
        var firstThursday = new this.Date(this.get('FullYear', thursday), 0, 1);

        if (this.get('Day', firstThursday) !== 4) {
          this.set('Month', d, 0);
          this.set('Date', d, 1 + (11 - this.get('Day', firstThursday)) % 7);
        }

        return (1 + Math.floor((thursday.valueOf() - firstThursday.valueOf()) / 604800000)).toString();
      };

      H.dateFormats.E = function (timestamp) {
        return dateFormat('%a', timestamp, true).charAt(0);
      };

      addEvent(Tick, 'afterGetLabelPosition', function (e) {
        var tick = this,
            label = tick.label,
            axis = tick.axis,
            reversed = axis.reversed,
            chart = axis.chart,
            options = axis.options,
            gridOptions = options && isObject(options.grid) ? options.grid : {},
            labelOpts = axis.options.labels,
            align = labelOpts.align,
            side = axisSide[axis.side],
            tickmarkOffset = e.tickmarkOffset,
            tickPositions = axis.tickPositions,
            tickPos = tick.pos - tickmarkOffset,
            nextTickPos = isNumber(tickPositions[e.index + 1]) ? tickPositions[e.index + 1] - tickmarkOffset : axis.max + tickmarkOffset,
            tickSize = axis.tickSize('tick', true),
            tickWidth = isArray(tickSize) ? tickSize[0] : 0,
            crispCorr = tickSize && tickSize[1] / 2,
            labelHeight,
            lblMetrics,
            lines,
            bottom,
            top,
            left,
            right;

        if (gridOptions.enabled === true) {
          if (side === 'top') {
            bottom = axis.top + axis.offset;
            top = bottom - tickWidth;
          } else if (side === 'bottom') {
            top = chart.chartHeight - axis.bottom + axis.offset;
            bottom = top + tickWidth;
          } else {
            bottom = axis.top + axis.len - axis.translate(reversed ? nextTickPos : tickPos);
            top = axis.top + axis.len - axis.translate(reversed ? tickPos : nextTickPos);
          }

          if (side === 'right') {
            left = chart.chartWidth - axis.right + axis.offset;
            right = left + tickWidth;
          } else if (side === 'left') {
            right = axis.left + axis.offset;
            left = right - tickWidth;
          } else {
            left = Math.round(axis.left + axis.translate(reversed ? nextTickPos : tickPos)) - crispCorr;
            right = Math.round(axis.left + axis.translate(reversed ? tickPos : nextTickPos)) - crispCorr;
          }

          tick.slotWidth = right - left;
          e.pos.x = align === 'left' ? left : align === 'right' ? right : left + (right - left) / 2;
          e.pos.y =   top + (bottom - top) / 2;
          lblMetrics = chart.renderer.fontMetrics(labelOpts.style.fontSize, label.element);
          labelHeight = label.getBBox().height;

          if (!labelOpts.useHTML) {
            lines = Math.round(labelHeight / lblMetrics.h);
            e.pos.y += (lblMetrics.b - (lblMetrics.h - lblMetrics.f)) / 2 + -((lines - 1) * lblMetrics.h / 2);
          } else {
            e.pos.y += lblMetrics.b + -(labelHeight / 2);
          }

          e.pos.x += axis.horiz && labelOpts.x || 0;
        }
      });
      addEvent(Axis, 'afterTickSize', function (e) {
        var _a = this,
            defaultLeftAxisOptions = _a.defaultLeftAxisOptions,
            horiz = _a.horiz,
            _b = _a.options.grid,
            gridOptions = _b === void 0 ? {} : _b;

        var dimensions = this.maxLabelDimensions;

        if (gridOptions.enabled) {
          var labelPadding = Math.abs(defaultLeftAxisOptions.labels.x) * 2;
          var distance = horiz ? gridOptions.cellHeight || labelPadding + dimensions.height : labelPadding + dimensions.width;

          if (isArray(e.tickSize)) {
            e.tickSize[0] = distance;
          } else {
            e.tickSize = [distance];
          }
        }
      });
      addEvent(Axis, 'afterGetTitlePosition', function (e) {
        var axis = this,
            options = axis.options,
            gridOptions = options && isObject(options.grid) ? options.grid : {};

        if (gridOptions.enabled === true) {
          var title = axis.axisTitle,
              titleWidth = title && title.getBBox().width,
              horiz = axis.horiz,
              axisLeft = axis.left,
              axisTop = axis.top,
              axisWidth = axis.width,
              axisHeight = axis.height,
              axisTitleOptions = options.title,
              opposite = axis.opposite,
              offset = axis.offset,
              tickSize = axis.tickSize() || [0],
              xOption = axisTitleOptions.x || 0,
              yOption = axisTitleOptions.y || 0,
              titleMargin = pick(axisTitleOptions.margin, horiz ? 5 : 10),
              titleFontSize = axis.chart.renderer.fontMetrics(axisTitleOptions.style && axisTitleOptions.style.fontSize, title).f,
              offAxis = (horiz ? axisTop + axisHeight : axisLeft) + (horiz ? 1 : -1) * (opposite ? -1 : 1) * (tickSize[0] / 2) + (axis.side === axisSide.bottom ? titleFontSize : 0);
          e.titlePosition.x = horiz ? axisLeft - titleWidth / 2 - titleMargin + xOption : offAxis + (opposite ? axisWidth : 0) + offset + xOption;
          e.titlePosition.y = horiz ? offAxis - (opposite ? axisHeight : 0) + (opposite ? titleFontSize : -titleFontSize) / 2 + offset + yOption : axisTop - titleMargin + yOption;
        }
      });
      wrap(Axis.prototype, 'unsquish', function (proceed) {
        var axis = this,
            options = axis.options,
            gridOptions = options && isObject(options.grid) ? options.grid : {};

        if (gridOptions.enabled === true && this.categories) {
          return this.tickInterval;
        }

        return proceed.apply(this, argsToArray(arguments));
      });
      addEvent(Axis, 'afterSetOptions', function (e) {
        var options = this.options,
            userOptions = e.userOptions,
            gridAxisOptions,
            gridOptions = options && isObject(options.grid) ? options.grid : {};

        if (gridOptions.enabled === true) {
          gridAxisOptions = merge(true, {
            className: 'highcharts-grid-axis ' + (userOptions.className || ''),
            dateTimeLabelFormats: {
              hour: {
                list: ['%H:%M', '%H']
              },
              day: {
                list: ['%A, %e. %B', '%a, %e. %b', '%E']
              },
              week: {
                list: ['Week %W', 'W%W']
              },
              month: {
                list: ['%B', '%b', '%o']
              }
            },
            grid: {
              borderWidth: 1
            },
            labels: {
              padding: 2,
              style: {
                fontSize: '13px'
              }
            },
            margin: 0,
            title: {
              text: null,
              reserveSpace: false,
              rotation: 0
            },
            units: [['millisecond', [1, 10, 100]], ['second', [1, 10]], ['minute', [1, 5, 15]], ['hour', [1, 6]], ['day', [1]], ['week', [1]], ['month', [1]], ['year', null]]
          }, userOptions);

          if (this.coll === 'xAxis') {
            if (defined(userOptions.linkedTo) && !defined(userOptions.tickPixelInterval)) {
              gridAxisOptions.tickPixelInterval = 350;
            }

            if (!defined(userOptions.tickPixelInterval) && defined(userOptions.linkedTo) && !defined(userOptions.tickPositioner) && !defined(userOptions.tickInterval)) {
              gridAxisOptions.tickPositioner = function (min, max) {
                var parentInfo = this.linkedParent && this.linkedParent.tickPositions && this.linkedParent.tickPositions.info;

                if (parentInfo) {
                  var unitIdx,
                      count,
                      unitName,
                      i,
                      units = gridAxisOptions.units,
                      unitRange;

                  for (i = 0; i < units.length; i++) {
                    if (units[i][0] === parentInfo.unitName) {
                      unitIdx = i;
                      break;
                    }
                  }

                  if (units[unitIdx + 1]) {
                    unitName = units[unitIdx + 1][0];
                    count = (units[unitIdx + 1][1] || [1])[0];
                  } else if (parentInfo.unitName === 'year') {
                    unitName = 'year';
                    count = parentInfo.count * 10;
                  }

                  unitRange = timeUnits[unitName];
                  this.tickInterval = unitRange * count;
                  return this.getTimeTicks({
                    unitRange: unitRange,
                    count: count,
                    unitName: unitName
                  }, min, max, this.options.startOfWeek);
                }
              };
            }
          }

          merge(true, this.options, gridAxisOptions);

          if (this.horiz) {
            options.minPadding = pick(userOptions.minPadding, 0);
            options.maxPadding = pick(userOptions.maxPadding, 0);
          }

          if (isNumber(options.grid.borderWidth)) {
            options.tickWidth = options.lineWidth = gridOptions.borderWidth;
          }
        }
      });
      addEvent(Axis, 'afterSetAxisTranslation', function () {
        var axis = this,
            options = axis.options,
            gridOptions = options && isObject(options.grid) ? options.grid : {},
            tickInfo = this.tickPositions && this.tickPositions.info,
            userLabels = this.userOptions.labels || {};

        if (this.horiz) {
          if (gridOptions.enabled === true) {
            axis.series.forEach(function (series) {
              series.options.pointRange = 0;
            });
          }

          if (tickInfo && (options.dateTimeLabelFormats[tickInfo.unitName].range === false || tickInfo.count > 1) && !defined(userLabels.align)) {
            options.labels.align = 'left';

            if (!defined(userLabels.x)) {
              options.labels.x = 3;
            }
          }
        }
      });
      addEvent(Axis, 'trimTicks', function () {
        var axis = this,
            options = axis.options,
            gridOptions = options && isObject(options.grid) ? options.grid : {},
            categoryAxis = axis.categories,
            tickPositions = axis.tickPositions,
            firstPos = tickPositions[0],
            lastPos = tickPositions[tickPositions.length - 1],
            linkedMin = axis.linkedParent && axis.linkedParent.min,
            linkedMax = axis.linkedParent && axis.linkedParent.max,
            min = linkedMin || axis.min,
            max = linkedMax || axis.max,
            tickInterval = axis.tickInterval,
            endMoreThanMin = firstPos < min && firstPos + tickInterval > min,
            startLessThanMax = lastPos > max && lastPos - tickInterval < max;

        if (gridOptions.enabled === true && !categoryAxis && (axis.horiz || axis.isLinked)) {
          if (endMoreThanMin && !options.startOnTick) {
            tickPositions[0] = min;
          }

          if (startLessThanMax && !options.endOnTick) {
            tickPositions[tickPositions.length - 1] = max;
          }
        }
      });
      addEvent(Axis, 'afterRender', function () {
        var axis = this,
            options = axis.options,
            gridOptions = options && isObject(options.grid) ? options.grid : {},
            yStartIndex,
            yEndIndex,
            xStartIndex,
            xEndIndex,
            renderer = axis.chart.renderer;

        if (gridOptions.enabled === true) {
          axis.maxLabelDimensions = axis.getMaxLabelDimensions(axis.ticks, axis.tickPositions);

          if (axis.rightWall) {
            axis.rightWall.destroy();
          }

          if (axis.isOuterAxis() && axis.axisLine) {
            var lineWidth = options.lineWidth;

            if (lineWidth) {
              var linePath = axis.getLinePath(lineWidth);
              xStartIndex = linePath.indexOf('M') + 1;
              xEndIndex = linePath.indexOf('L') + 1;
              yStartIndex = linePath.indexOf('M') + 2;
              yEndIndex = linePath.indexOf('L') + 2;
              var distance = (axis.tickSize('tick')[0] - 1) * (axis.side === axisSide.top || axis.side === axisSide.left ? -1 : 1);

              if (axis.horiz) {
                linePath[yStartIndex] = linePath[yStartIndex] + distance;
                linePath[yEndIndex] = linePath[yEndIndex] + distance;
              } else {
                linePath[xStartIndex] = linePath[xStartIndex] + distance;
                linePath[xEndIndex] = linePath[xEndIndex] + distance;
              }

              if (!axis.axisLineExtra) {
                axis.axisLineExtra = renderer.path(linePath).attr({
                  zIndex: 7
                }).addClass('highcharts-axis-line').add(axis.axisGroup);

                if (!renderer.styledMode) {
                  axis.axisLineExtra.attr({
                    stroke: options.lineColor,
                    'stroke-width': lineWidth
                  });
                }
              } else {
                axis.axisLineExtra.animate({
                  d: linePath
                });
              }

              axis.axisLine[axis.showAxis ? 'show' : 'hide'](true);
            }
          }

          (axis.columns || []).forEach(function (column) {
            column.render();
          });
        }
      });

      var onGridAxisAfterGetOffset = function onGridAxisAfterGetOffset() {
        (this.columns || []).forEach(function (column) {
          column.getOffset();
        });
      };

      var onGridAxisAfterInit = function onGridAxisAfterInit() {
        var axis = this,
            chart = axis.chart,
            userOptions = axis.userOptions,
            options = axis.options,
            gridOptions = options && isObject(options.grid) ? options.grid : {};

        if (gridOptions.enabled) {
          applyGridOptions(axis);
          wrap(axis, 'labelFormatter', function (proceed) {
            var axis = this.axis,
                tickPos = axis.tickPositions,
                value = this.value,
                series = (axis.isLinked ? axis.linkedParent : axis).series[0],
                isFirst = value === tickPos[0],
                isLast = value === tickPos[tickPos.length - 1],
                point = series && find(series.options.data, function (p) {
              return p[axis.isXAxis ? 'x' : 'y'] === value;
            });
            this.isFirst = isFirst;
            this.isLast = isLast;
            this.point = point;
            return proceed.call(this);
          });
        }

        if (gridOptions.columns) {
          var columns = axis.columns = [],
              columnIndex = axis.columnIndex = 0;

          while (++columnIndex < gridOptions.columns.length) {
            var columnOptions = merge(userOptions, gridOptions.columns[gridOptions.columns.length - columnIndex - 1], {
              linkedTo: 0,
              type: 'category'
            });
            delete columnOptions.grid.columns;
            var column = new Axis(axis.chart, columnOptions, true);
            column.isColumn = true;
            column.columnIndex = columnIndex;
            erase(chart.axes, column);
            erase(chart[axis.coll], column);
            columns.push(column);
          }
        }
      };

      var onGridAxisAfterSetChartSize = function onGridAxisAfterSetChartSize() {
        this.axes.forEach(function (axis) {
          (axis.columns || []).forEach(function (column) {
            column.setAxisSize();
            column.setAxisTranslation();
          });
        });
      };

      var onGridAxisAfterSetScale = function onGridAxisAfterSetScale() {
        (this.columns || []).forEach(function (column) {
          column.setScale();
        });
      };

      var onGridAxisDestroy = function onGridAxisDestroy(e) {
        (this.columns || []).forEach(function (column) {
          column.destroy(e.keepEvents);
        });
      };

      var onGridAxisInit = function onGridAxisInit(e) {
        var userOptions = e.userOptions,
            gridOptions = userOptions && isObject(userOptions.grid) ? userOptions.grid : {};

        if (gridOptions.enabled && defined(gridOptions.borderColor)) {
          userOptions.tickColor = userOptions.lineColor = gridOptions.borderColor;
        }
      };

      var onGridAxisAfterSetOptions = function onGridAxisAfterSetOptions(e) {
        var axis = this,
            userOptions = e.userOptions,
            gridOptions = userOptions && isObject(userOptions.grid) ? userOptions.grid : {},
            columns = gridOptions.columns;

        if (gridOptions.enabled && columns) {
          merge(true, axis.options, columns[columns.length - 1]);
        }
      };

      var axisEvents = {
        afterGetOffset: onGridAxisAfterGetOffset,
        afterInit: onGridAxisAfterInit,
        afterSetOptions: onGridAxisAfterSetOptions,
        afterSetScale: onGridAxisAfterSetScale,
        destroy: onGridAxisDestroy,
        init: onGridAxisInit
      };
      Object.keys(axisEvents).forEach(function (event) {
        addEvent(Axis, event, axisEvents[event]);
      });
      addEvent(Chart, 'afterSetChartSize', onGridAxisAfterSetChartSize);
    });

    _registerModule(_modules, 'modules/static-scale.src.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var addEvent = U.addEvent,
          defined = U.defined,
          isNumber = U.isNumber,
          pick = U.pick;
      var Chart = H.Chart;
      addEvent(H.Axis, 'afterSetOptions', function () {
        var chartOptions = this.chart.options && this.chart.options.chart;

        if (!this.horiz && isNumber(this.options.staticScale) && (!chartOptions.height || chartOptions.scrollablePlotArea && chartOptions.scrollablePlotArea.minHeight)) {
          this.staticScale = this.options.staticScale;
        }
      });

      Chart.prototype.adjustHeight = function () {
        if (this.redrawTrigger !== 'adjustHeight') {
          (this.axes || []).forEach(function (axis) {
            var chart = axis.chart,
                animate = !!chart.initiatedScale && chart.options.animation,
                staticScale = axis.options.staticScale,
                height,
                diff;

            if (axis.staticScale && defined(axis.min)) {
              height = pick(axis.unitLength, axis.max + axis.tickInterval - axis.min) * staticScale;
              height = Math.max(height, staticScale);
              diff = height - chart.plotHeight;

              if (Math.abs(diff) >= 1) {
                chart.plotHeight = height;
                chart.redrawTrigger = 'adjustHeight';
                chart.setSize(void 0, chart.chartHeight + diff, animate);
              }

              axis.series.forEach(function (series) {
                var clipRect = series.sharedClipKey && chart[series.sharedClipKey];

                if (clipRect) {
                  clipRect.attr({
                    height: chart.plotHeight
                  });
                }
              });
            }
          });
          this.initiatedScale = true;
        }

        this.redrawTrigger = null;
      };

      addEvent(Chart, 'render', Chart.prototype.adjustHeight);
    });

    _registerModule(_modules, 'mixins/tree-series.js', [_modules['parts/Color.js'], _modules['parts/Utilities.js']], function (Color, U) {
      var extend = U.extend,
          isArray = U.isArray,
          isNumber = U.isNumber,
          isObject = U.isObject,
          merge = U.merge,
          pick = U.pick;

      var isBoolean = function isBoolean(x) {
        return typeof x === 'boolean';
      },
          isFn = function isFn(x) {
        return typeof x === 'function';
      };

      var setTreeValues = function setTreeValues(tree, options) {
        var before = options.before,
            idRoot = options.idRoot,
            mapIdToNode = options.mapIdToNode,
            nodeRoot = mapIdToNode[idRoot],
            levelIsConstant = isBoolean(options.levelIsConstant) ? options.levelIsConstant : true,
            points = options.points,
            point = points[tree.i],
            optionsPoint = point && point.options || {},
            childrenTotal = 0,
            children = [],
            value;
        extend(tree, {
          levelDynamic: tree.level - (levelIsConstant ? 0 : nodeRoot.level),
          name: pick(point && point.name, ''),
          visible: idRoot === tree.id || (isBoolean(options.visible) ? options.visible : false)
        });

        if (isFn(before)) {
          tree = before(tree, options);
        }

        tree.children.forEach(function (child, i) {
          var newOptions = extend({}, options);
          extend(newOptions, {
            index: i,
            siblings: tree.children.length,
            visible: tree.visible
          });
          child = setTreeValues(child, newOptions);
          children.push(child);

          if (child.visible) {
            childrenTotal += child.val;
          }
        });
        tree.visible = childrenTotal > 0 || tree.visible;
        value = pick(optionsPoint.value, childrenTotal);
        extend(tree, {
          children: children,
          childrenTotal: childrenTotal,
          isLeaf: tree.visible && !childrenTotal,
          val: value
        });
        return tree;
      };

      var getColor = function getColor(node, options) {
        var index = options.index,
            mapOptionsToLevel = options.mapOptionsToLevel,
            parentColor = options.parentColor,
            parentColorIndex = options.parentColorIndex,
            series = options.series,
            colors = options.colors,
            siblings = options.siblings,
            points = series.points,
            getColorByPoint,
            chartOptionsChart = series.chart.options.chart,
            point,
            level,
            colorByPoint,
            colorIndexByPoint,
            color,
            colorIndex;

        function variation(color) {
          var colorVariation = level && level.colorVariation;

          if (colorVariation) {
            if (colorVariation.key === 'brightness') {
              return Color.parse(color).brighten(colorVariation.to * (index / siblings)).get();
            }
          }

          return color;
        }

        if (node) {
          point = points[node.i];
          level = mapOptionsToLevel[node.level] || {};
          getColorByPoint = point && level.colorByPoint;

          if (getColorByPoint) {
            colorIndexByPoint = point.index % (colors ? colors.length : chartOptionsChart.colorCount);
            colorByPoint = colors && colors[colorIndexByPoint];
          }

          if (!series.chart.styledMode) {
            color = pick(point && point.options.color, level && level.color, colorByPoint, parentColor && variation(parentColor), series.color);
          }

          colorIndex = pick(point && point.options.colorIndex, level && level.colorIndex, colorIndexByPoint, parentColorIndex, options.colorIndex);
        }

        return {
          color: color,
          colorIndex: colorIndex
        };
      };

      var getLevelOptions = function getLevelOptions(params) {
        var result = null,
            defaults,
            converted,
            i,
            from,
            to,
            levels;

        if (isObject(params)) {
          result = {};
          from = isNumber(params.from) ? params.from : 1;
          levels = params.levels;
          converted = {};
          defaults = isObject(params.defaults) ? params.defaults : {};

          if (isArray(levels)) {
            converted = levels.reduce(function (obj, item) {
              var level, levelIsConstant, options;

              if (isObject(item) && isNumber(item.level)) {
                options = merge({}, item);
                levelIsConstant = isBoolean(options.levelIsConstant) ? options.levelIsConstant : defaults.levelIsConstant;
                delete options.levelIsConstant;
                delete options.level;
                level = item.level + (levelIsConstant ? 0 : from - 1);

                if (isObject(obj[level])) {
                  extend(obj[level], options);
                } else {
                  obj[level] = options;
                }
              }

              return obj;
            }, {});
          }

          to = isNumber(params.to) ? params.to : 1;

          for (i = 0; i <= to; i++) {
            result[i] = merge({}, defaults, isObject(converted[i]) ? converted[i] : {});
          }
        }

        return result;
      };

      var updateRootId = function updateRootId(series) {
        var rootId, options;

        if (isObject(series)) {
          options = isObject(series.options) ? series.options : {};
          rootId = pick(series.rootNode, options.rootId, '');

          if (isObject(series.userOptions)) {
            series.userOptions.rootId = rootId;
          }

          series.rootNode = rootId;
        }

        return rootId;
      };

      var result = {
        getColor: getColor,
        getLevelOptions: getLevelOptions,
        setTreeValues: setTreeValues,
        updateRootId: updateRootId
      };
      return result;
    });

    _registerModule(_modules, 'parts-gantt/Tree.js', [_modules['parts/Utilities.js']], function (U) {
      var extend = U.extend,
          isNumber = U.isNumber,
          pick = U.pick;

      var isFunction = function isFunction(x) {
        return typeof x === 'function';
      };

      var getListOfParents = function getListOfParents(data, ids) {
        var listOfParents = data.reduce(function (prev, curr) {
          var parent = pick(curr.parent, '');

          if (typeof prev[parent] === 'undefined') {
            prev[parent] = [];
          }

          prev[parent].push(curr);
          return prev;
        }, {}),
            parents = Object.keys(listOfParents);
        parents.forEach(function (parent, list) {
          var children = listOfParents[parent];

          if (parent !== '' && ids.indexOf(parent) === -1) {
            children.forEach(function (child) {
              list[''].push(child);
            });
            delete list[parent];
          }
        });
        return listOfParents;
      };

      var getNode = function getNode(id, parent, level, data, mapOfIdToChildren, options) {
        var descendants = 0,
            height = 0,
            after = options && options.after,
            before = options && options.before,
            node = {
          data: data,
          depth: level - 1,
          id: id,
          level: level,
          parent: parent
        },
            start,
            end,
            children;

        if (isFunction(before)) {
          before(node, options);
        }

        children = (mapOfIdToChildren[id] || []).map(function (child) {
          var node = getNode(child.id, id, level + 1, child, mapOfIdToChildren, options),
              childStart = child.start,
              childEnd = child.milestone === true ? childStart : child.end;
          start = !isNumber(start) || childStart < start ? childStart : start;
          end = !isNumber(end) || childEnd > end ? childEnd : end;
          descendants = descendants + 1 + node.descendants;
          height = Math.max(node.height + 1, height);
          return node;
        });

        if (data) {
          data.start = pick(data.start, start);
          data.end = pick(data.end, end);
        }

        extend(node, {
          children: children,
          descendants: descendants,
          height: height
        });

        if (isFunction(after)) {
          after(node, options);
        }

        return node;
      };

      var getTree = function getTree(data, options) {
        var ids = data.map(function (d) {
          return d.id;
        }),
            mapOfIdToChildren = getListOfParents(data, ids);
        return getNode('', null, 1, null, mapOfIdToChildren, options);
      };

      var Tree = {
        getListOfParents: getListOfParents,
        getNode: getNode,
        getTree: getTree
      };
      return Tree;
    });

    _registerModule(_modules, 'modules/broken-axis.src.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var addEvent = U.addEvent,
          extend = U.extend,
          find = U.find,
          fireEvent = U.fireEvent,
          isArray = U.isArray,
          pick = U.pick;
      var Axis = H.Axis,
          Series = H.Series;

      var findBreakAt = function findBreakAt(x, breaks) {
        return find(breaks, function (b) {
          return b.from < x && x < b.to;
        });
      };

      extend(Axis.prototype, {
        isInBreak: function isInBreak(brk, val) {
          var ret,
              repeat = brk.repeat || Infinity,
              from = brk.from,
              length = brk.to - brk.from,
              test = val >= from ? (val - from) % repeat : repeat - (from - val) % repeat;

          if (!brk.inclusive) {
            ret = test < length && test !== 0;
          } else {
            ret = test <= length;
          }

          return ret;
        },
        isInAnyBreak: function isInAnyBreak(val, testKeep) {
          var breaks = this.options.breaks,
              i = breaks && breaks.length,
              inbrk,
              keep,
              ret;

          if (i) {
            while (i--) {
              if (this.isInBreak(breaks[i], val)) {
                inbrk = true;

                if (!keep) {
                  keep = pick(breaks[i].showPoints, !this.isXAxis);
                }
              }
            }

            if (inbrk && testKeep) {
              ret = inbrk && !keep;
            } else {
              ret = inbrk;
            }
          }

          return ret;
        }
      });
      addEvent(Axis, 'afterInit', function () {
        if (typeof this.setBreaks === 'function') {
          this.setBreaks(this.options.breaks, false);
        }
      });
      addEvent(Axis, 'afterSetTickPositions', function () {
        if (this.isBroken) {
          var axis = this,
              tickPositions = this.tickPositions,
              info = this.tickPositions.info,
              newPositions = [],
              i;

          for (i = 0; i < tickPositions.length; i++) {
            if (!axis.isInAnyBreak(tickPositions[i])) {
              newPositions.push(tickPositions[i]);
            }
          }

          this.tickPositions = newPositions;
          this.tickPositions.info = info;
        }
      });
      addEvent(Axis, 'afterSetOptions', function () {
        if (this.isBroken) {
          this.options.ordinal = false;
        }
      });

      Axis.prototype.setBreaks = function (breaks, redraw) {
        var axis = this,
            isBroken = isArray(breaks) && !!breaks.length;

        function breakVal2Lin(val) {
          var nval = val,
              brk,
              i;

          for (i = 0; i < axis.breakArray.length; i++) {
            brk = axis.breakArray[i];

            if (brk.to <= val) {
              nval -= brk.len;
            } else if (brk.from >= val) {
              break;
            } else if (axis.isInBreak(brk, val)) {
              nval -= val - brk.from;
              break;
            }
          }

          return nval;
        }

        function breakLin2Val(val) {
          var nval = val,
              brk,
              i;

          for (i = 0; i < axis.breakArray.length; i++) {
            brk = axis.breakArray[i];

            if (brk.from >= nval) {
              break;
            } else if (brk.to < nval) {
              nval += brk.len;
            } else if (axis.isInBreak(brk, nval)) {
              nval += brk.len;
            }
          }

          return nval;
        }

        axis.isDirty = axis.isBroken !== isBroken;
        axis.isBroken = isBroken;
        axis.options.breaks = axis.userOptions.breaks = breaks;
        axis.forceRedraw = true;
        axis.series.forEach(function (series) {
          series.isDirty = true;
        });

        if (!isBroken && axis.val2lin === breakVal2Lin) {
          delete axis.val2lin;
          delete axis.lin2val;
        }

        if (isBroken) {
          axis.userOptions.ordinal = false;
          axis.val2lin = breakVal2Lin;
          axis.lin2val = breakLin2Val;

          axis.setExtremes = function (newMin, newMax, redraw, animation, eventArguments) {
            if (this.isBroken) {
              var axisBreak,
                  breaks = this.options.breaks;

              while (axisBreak = findBreakAt(newMin, breaks)) {
                newMin = axisBreak.to;
              }

              while (axisBreak = findBreakAt(newMax, breaks)) {
                newMax = axisBreak.from;
              }

              if (newMax < newMin) {
                newMax = newMin;
              }
            }

            Axis.prototype.setExtremes.call(this, newMin, newMax, redraw, animation, eventArguments);
          };

          axis.setAxisTranslation = function (saveOld) {
            Axis.prototype.setAxisTranslation.call(this, saveOld);
            this.unitLength = null;

            if (this.isBroken) {
              var breaks = axis.options.breaks,
                  breakArrayT = [],
                  breakArray = [],
                  length = 0,
                  inBrk,
                  repeat,
                  min = axis.userMin || axis.min,
                  max = axis.userMax || axis.max,
                  pointRangePadding = pick(axis.pointRangePadding, 0),
                  start,
                  i;
              breaks.forEach(function (brk) {
                repeat = brk.repeat || Infinity;

                if (axis.isInBreak(brk, min)) {
                  min += brk.to % repeat - min % repeat;
                }

                if (axis.isInBreak(brk, max)) {
                  max -= max % repeat - brk.from % repeat;
                }
              });
              breaks.forEach(function (brk) {
                start = brk.from;
                repeat = brk.repeat || Infinity;

                while (start - repeat > min) {
                  start -= repeat;
                }

                while (start < min) {
                  start += repeat;
                }

                for (i = start; i < max; i += repeat) {
                  breakArrayT.push({
                    value: i,
                    move: 'in'
                  });
                  breakArrayT.push({
                    value: i + (brk.to - brk.from),
                    move: 'out',
                    size: brk.breakSize
                  });
                }
              });
              breakArrayT.sort(function (a, b) {
                return a.value === b.value ? (a.move === 'in' ? 0 : 1) - (b.move === 'in' ? 0 : 1) : a.value - b.value;
              });
              inBrk = 0;
              start = min;
              breakArrayT.forEach(function (brk) {
                inBrk += brk.move === 'in' ? 1 : -1;

                if (inBrk === 1 && brk.move === 'in') {
                  start = brk.value;
                }

                if (inBrk === 0) {
                  breakArray.push({
                    from: start,
                    to: brk.value,
                    len: brk.value - start - (brk.size || 0)
                  });
                  length += brk.value - start - (brk.size || 0);
                }
              });
              axis.breakArray = breakArray;
              axis.unitLength = max - min - length + pointRangePadding;
              fireEvent(axis, 'afterBreaks');

              if (axis.staticScale) {
                axis.transA = axis.staticScale;
              } else if (axis.unitLength) {
                axis.transA *= (max - axis.min + pointRangePadding) / axis.unitLength;
              }

              if (pointRangePadding) {
                axis.minPixelPadding = axis.transA * axis.minPointOffset;
              }

              axis.min = min;
              axis.max = max;
            }
          };
        }

        if (pick(redraw, true)) {
          this.chart.redraw();
        }
      };

      addEvent(Series, 'afterGeneratePoints', function () {
        var _a = this,
            isDirty = _a.isDirty,
            connectNulls = _a.options.connectNulls,
            points = _a.points,
            xAxis = _a.xAxis,
            yAxis = _a.yAxis;

        if (isDirty) {
          var i = points.length;

          while (i--) {
            var point = points[i];
            var nullGap = point.y === null && connectNulls === false;
            var isPointInBreak = !nullGap && (xAxis && xAxis.isInAnyBreak(point.x, true) || yAxis && yAxis.isInAnyBreak(point.y, true));
            point.visible = isPointInBreak ? false : point.options.visible !== false;
          }
        }
      });
      addEvent(Series, 'afterRender', function drawPointsWrapped() {
        this.drawBreaks(this.xAxis, ['x']);
        this.drawBreaks(this.yAxis, pick(this.pointArrayMap, ['y']));
      });

      H.Series.prototype.drawBreaks = function (axis, keys) {
        var series = this,
            points = series.points,
            breaks,
            threshold,
            eventName,
            y;

        if (!axis) {
          return;
        }

        keys.forEach(function (key) {
          breaks = axis.breakArray || [];
          threshold = axis.isXAxis ? axis.min : pick(series.options.threshold, axis.min);
          points.forEach(function (point) {
            y = pick(point['stack' + key.toUpperCase()], point[key]);
            breaks.forEach(function (brk) {
              eventName = false;

              if (threshold < brk.from && y > brk.to || threshold > brk.from && y < brk.from) {
                eventName = 'pointBreak';
              } else if (threshold < brk.from && y > brk.from && y < brk.to || threshold > brk.from && y > brk.to && y < brk.from) {
                eventName = 'pointInBreak';
              }

              if (eventName) {
                fireEvent(axis, eventName, {
                  point: point,
                  brk: brk
                });
              }
            });
          });
        });
      };

      H.Series.prototype.gappedPath = function () {
        var currentDataGrouping = this.currentDataGrouping,
            groupingSize = currentDataGrouping && currentDataGrouping.gapSize,
            gapSize = this.options.gapSize,
            points = this.points.slice(),
            i = points.length - 1,
            yAxis = this.yAxis,
            stack;

        if (gapSize && i > 0) {
          if (this.options.gapUnit !== 'value') {
            gapSize *= this.basePointRange;
          }

          if (groupingSize && groupingSize > gapSize && groupingSize >= this.basePointRange) {
            gapSize = groupingSize;
          }

          var current = void 0,
              next = void 0;

          while (i--) {
            if (!(next && next.visible !== false)) {
              next = points[i + 1];
            }

            current = points[i];

            if (next.visible === false || current.visible === false) {
              continue;
            }

            if (next.x - current.x > gapSize) {
              var xRange = (current.x + next.x) / 2;
              points.splice(i + 1, 0, {
                isNull: true,
                x: xRange
              });

              if (this.options.stacking) {
                stack = yAxis.stacks[this.stackKey][xRange] = new H.StackItem(yAxis, yAxis.options.stackLabels, false, xRange, this.stack);
                stack.total = 0;
              }
            }

            next = current;
          }
        }

        return this.getGraphPath(points);
      };
    });

    _registerModule(_modules, 'parts-gantt/TreeGrid.js', [_modules['parts/Globals.js'], _modules['mixins/tree-series.js'], _modules['parts/Tick.js'], _modules['parts-gantt/Tree.js'], _modules['parts/Utilities.js']], function (H, mixinTreeSeries, Tick, Tree, U) {
      var addEvent = U.addEvent,
          defined = U.defined,
          fireEvent = U.fireEvent,
          extend = U.extend,
          isNumber = U.isNumber,
          isString = U.isString,
          merge = U.merge,
          pick = U.pick,
          wrap = U.wrap;

      var argsToArray = function argsToArray(args) {
        return Array.prototype.slice.call(args, 1);
      },
          find = U.find,
          getLevelOptions = mixinTreeSeries.getLevelOptions,
          isBoolean = function isBoolean(x) {
        return typeof x === 'boolean';
      },
          isObject = function isObject(x) {
        return U.isObject(x, true);
      },
          GridAxis = H.Axis,
          GridAxisTick = Tick;

      var override = function override(obj, methods) {
        var method, func;

        for (method in methods) {
          if (Object.hasOwnProperty.call(methods, method)) {
            func = methods[method];
            wrap(obj, method, func);
          }
        }
      };

      var getBreakFromNode = function getBreakFromNode(node, max) {
        var from = node.collapseStart,
            to = node.collapseEnd;

        if (to >= max) {
          from -= 0.5;
        }

        return {
          from: from,
          to: to,
          showPoints: false
        };
      };

      var getTickPositions = function getTickPositions(axis) {
        return Object.keys(axis.mapOfPosToGridNode).reduce(function (arr, key) {
          var pos = +key;

          if (axis.min <= pos && axis.max >= pos && !axis.isInAnyBreak(pos)) {
            arr.push(pos);
          }

          return arr;
        }, []);
      };

      var isCollapsed = function isCollapsed(axis, node) {
        var breaks = axis.options.breaks || [],
            obj = getBreakFromNode(node, axis.max);
        return breaks.some(function (b) {
          return b.from === obj.from && b.to === obj.to;
        });
      };

      var _collapse = function collapse(axis, node) {
        var breaks = axis.options.breaks || [],
            obj = getBreakFromNode(node, axis.max);
        breaks.push(obj);
        return breaks;
      };

      var _expand = function expand(axis, node) {
        var breaks = axis.options.breaks || [],
            obj = getBreakFromNode(node, axis.max);
        return breaks.reduce(function (arr, b) {
          if (b.to !== obj.to || b.from !== obj.from) {
            arr.push(b);
          }

          return arr;
        }, []);
      };

      var _toggleCollapse = function toggleCollapse(axis, node) {
        return isCollapsed(axis, node) ? _expand(axis, node) : _collapse(axis, node);
      };

      var renderLabelIcon = function renderLabelIcon(tick, params) {
        var icon = tick.labelIcon,
            isNew = !icon,
            renderer = params.renderer,
            labelBox = params.xy,
            options = params.options,
            width = options.width,
            height = options.height,
            iconCenter = {
          x: labelBox.x - width / 2 - options.padding,
          y: labelBox.y - height / 2
        },
            rotation = params.collapsed ? 90 : 180,
            shouldRender = params.show && isNumber(iconCenter.y);

        if (isNew) {
          tick.labelIcon = icon = renderer.path(renderer.symbols[options.type](options.x, options.y, width, height)).addClass('highcharts-label-icon').add(params.group);
        }

        if (!shouldRender) {
          icon.attr({
            y: -9999
          });
        }

        if (!renderer.styledMode) {
          icon.attr({
            'stroke-width': 1,
            'fill': pick(params.color, '#666666')
          }).css({
            cursor: 'pointer',
            stroke: options.lineColor,
            strokeWidth: options.lineWidth
          });
        }

        icon[isNew ? 'attr' : 'animate']({
          translateX: iconCenter.x,
          translateY: iconCenter.y,
          rotation: rotation
        });
      };

      var onTickHover = function onTickHover(label) {
        label.addClass('highcharts-treegrid-node-active');

        if (!label.renderer.styledMode) {
          label.css({
            textDecoration: 'underline'
          });
        }
      };

      var onTickHoverExit = function onTickHoverExit(label, options) {
        var css = defined(options.style) ? options.style : {};
        label.removeClass('highcharts-treegrid-node-active');

        if (!label.renderer.styledMode) {
          label.css({
            textDecoration: css.textDecoration
          });
        }
      };

      var getTreeGridFromData = function getTreeGridFromData(data, uniqueNames, numberOfSeries) {
        var categories = [],
            collapsedNodes = [],
            mapOfIdToNode = {},
            mapOfPosToGridNode = {},
            posIterator = -1,
            uniqueNamesEnabled = isBoolean(uniqueNames) ? uniqueNames : false,
            tree,
            treeParams,
            updateYValuesAndTickPos;
        treeParams = {
          after: function after(node) {
            var gridNode = mapOfPosToGridNode[node.pos],
                height = 0,
                descendants = 0;
            gridNode.children.forEach(function (child) {
              descendants += child.descendants + 1;
              height = Math.max(child.height + 1, height);
            });
            gridNode.descendants = descendants;
            gridNode.height = height;

            if (gridNode.collapsed) {
              collapsedNodes.push(gridNode);
            }
          },
          before: function before(node) {
            var data = isObject(node.data) ? node.data : {},
                name = isString(data.name) ? data.name : '',
                parentNode = mapOfIdToNode[node.parent],
                parentGridNode = isObject(parentNode) ? mapOfPosToGridNode[parentNode.pos] : null,
                hasSameName = function hasSameName(x) {
              return x.name === name;
            },
                gridNode,
                pos;

            if (uniqueNamesEnabled && isObject(parentGridNode) && !!(gridNode = find(parentGridNode.children, hasSameName))) {
              pos = gridNode.pos;
              gridNode.nodes.push(node);
            } else {
              pos = posIterator++;
            }

            if (!mapOfPosToGridNode[pos]) {
              mapOfPosToGridNode[pos] = gridNode = {
                depth: parentGridNode ? parentGridNode.depth + 1 : 0,
                name: name,
                nodes: [node],
                children: [],
                pos: pos
              };

              if (pos !== -1) {
                categories.push(name);
              }

              if (isObject(parentGridNode)) {
                parentGridNode.children.push(gridNode);
              }
            }

            if (isString(node.id)) {
              mapOfIdToNode[node.id] = node;
            }

            if (data.collapsed === true) {
              gridNode.collapsed = true;
            }

            node.pos = pos;
          }
        };

        updateYValuesAndTickPos = function updateYValuesAndTickPos(map, numberOfSeries) {
          var setValues = function setValues(gridNode, start, result) {
            var nodes = gridNode.nodes,
                end = start + (start === -1 ? 0 : numberOfSeries - 1),
                diff = (end - start) / 2,
                padding = 0.5,
                pos = start + diff;
            nodes.forEach(function (node) {
              var data = node.data;

              if (isObject(data)) {
                data.y = start + data.seriesIndex;
                delete data.seriesIndex;
              }

              node.pos = pos;
            });
            result[pos] = gridNode;
            gridNode.pos = pos;
            gridNode.tickmarkOffset = diff + padding;
            gridNode.collapseStart = end + padding;
            gridNode.children.forEach(function (child) {
              setValues(child, end + 1, result);
              end = child.collapseEnd - padding;
            });
            gridNode.collapseEnd = end + padding;
            return result;
          };

          return setValues(map['-1'], -1, {});
        };

        tree = Tree.getTree(data, treeParams);
        mapOfPosToGridNode = updateYValuesAndTickPos(mapOfPosToGridNode, numberOfSeries);
        return {
          categories: categories,
          mapOfIdToNode: mapOfIdToNode,
          mapOfPosToGridNode: mapOfPosToGridNode,
          collapsedNodes: collapsedNodes,
          tree: tree
        };
      };

      var onBeforeRender = function onBeforeRender(e) {
        var chart = e.target,
            axes = chart.axes;
        axes.filter(function (axis) {
          return axis.options.type === 'treegrid';
        }).forEach(function (axis) {
          var options = axis.options || {},
              labelOptions = options.labels,
              removeFoundExtremesEvent,
              uniqueNames = options.uniqueNames,
              numberOfSeries = 0,
              isDirty,
              data,
              treeGrid;
          isDirty = !axis.mapOfPosToGridNode || axis.series.some(function (series) {
            return !series.hasRendered || series.isDirtyData || series.isDirty;
          });

          if (isDirty) {
            data = axis.series.reduce(function (arr, s) {
              if (s.visible) {
                s.options.data.forEach(function (data) {
                  if (isObject(data)) {
                    data.seriesIndex = numberOfSeries;
                    arr.push(data);
                  }
                });

                if (uniqueNames === true) {
                  numberOfSeries++;
                }
              }

              return arr;
            }, []);
            treeGrid = getTreeGridFromData(data, uniqueNames, uniqueNames === true ? numberOfSeries : 1);
            axis.categories = treeGrid.categories;
            axis.mapOfPosToGridNode = treeGrid.mapOfPosToGridNode;
            axis.hasNames = true;
            axis.tree = treeGrid.tree;
            axis.series.forEach(function (series) {
              var data = series.options.data.map(function (d) {
                return isObject(d) ? merge(d) : d;
              });

              if (series.visible) {
                series.setData(data, false);
              }
            });
            axis.mapOptionsToLevel = getLevelOptions({
              defaults: labelOptions,
              from: 1,
              levels: labelOptions.levels,
              to: axis.tree.height
            });

            if (e.type === 'beforeRender') {
              removeFoundExtremesEvent = addEvent(axis, 'foundExtremes', function () {
                treeGrid.collapsedNodes.forEach(function (node) {
                  var breaks = _collapse(axis, node);

                  axis.setBreaks(breaks, false);
                });
                removeFoundExtremesEvent();
              });
            }
          }
        });
      };

      override(GridAxis.prototype, {
        init: function init(proceed, chart, userOptions) {
          var axis = this,
              isTreeGrid = userOptions.type === 'treegrid';

          if (isTreeGrid) {
            addEvent(chart, 'beforeRender', onBeforeRender);
            addEvent(chart, 'beforeRedraw', onBeforeRender);
            userOptions = merge({
              grid: {
                enabled: true
              },
              labels: {
                align: 'left',
                levels: [{
                  level: void 0
                }, {
                  level: 1,
                  style: {
                    fontWeight: 'bold'
                  }
                }],
                symbol: {
                  type: 'triangle',
                  x: -5,
                  y: -5,
                  height: 10,
                  width: 10,
                  padding: 5
                }
              },
              uniqueNames: false
            }, userOptions, {
              reversed: true,
              grid: {
                columns: void 0
              }
            });
          }

          proceed.apply(axis, [chart, userOptions]);

          if (isTreeGrid) {
            axis.hasNames = true;
            axis.options.showLastLabel = true;
          }
        },
        getMaxLabelDimensions: function getMaxLabelDimensions(proceed) {
          var axis = this,
              options = axis.options,
              labelOptions = options && options.labels,
              indentation = labelOptions && isNumber(labelOptions.indentation) ? options.labels.indentation : 0,
              retVal = proceed.apply(axis, argsToArray(arguments)),
              isTreeGrid = axis.options.type === 'treegrid',
              treeDepth;

          if (isTreeGrid && this.mapOfPosToGridNode) {
            treeDepth = axis.mapOfPosToGridNode[-1].height;
            retVal.width += indentation * (treeDepth - 1);
          }

          return retVal;
        },
        generateTick: function generateTick(proceed, pos) {
          var axis = this,
              mapOptionsToLevel = isObject(axis.mapOptionsToLevel) ? axis.mapOptionsToLevel : {},
              isTreeGrid = axis.options.type === 'treegrid',
              ticks = axis.ticks,
              tick = ticks[pos],
              levelOptions,
              options,
              gridNode;

          if (isTreeGrid) {
            gridNode = axis.mapOfPosToGridNode[pos];
            levelOptions = mapOptionsToLevel[gridNode.depth];

            if (levelOptions) {
              options = {
                labels: levelOptions
              };
            }

            if (!tick) {
              ticks[pos] = tick = new GridAxisTick(axis, pos, null, void 0, {
                category: gridNode.name,
                tickmarkOffset: gridNode.tickmarkOffset,
                options: options
              });
            } else {
              tick.parameters.category = gridNode.name;
              tick.options = options;
              tick.addLabel();
            }
          } else {
            proceed.apply(axis, argsToArray(arguments));
          }
        },
        setTickInterval: function setTickInterval(proceed) {
          var axis = this,
              options = axis.options,
              isTreeGrid = options.type === 'treegrid';

          if (isTreeGrid) {
            axis.min = pick(axis.userMin, options.min, axis.dataMin);
            axis.max = pick(axis.userMax, options.max, axis.dataMax);
            fireEvent(axis, 'foundExtremes');
            axis.setAxisTranslation(true);
            axis.tickmarkOffset = 0.5;
            axis.tickInterval = 1;
            axis.tickPositions = this.mapOfPosToGridNode ? getTickPositions(axis) : [];
          } else {
            proceed.apply(axis, argsToArray(arguments));
          }
        }
      });
      override(GridAxisTick.prototype, {
        getLabelPosition: function getLabelPosition(proceed, x, y, label, horiz, labelOptions, tickmarkOffset, index, step) {
          var tick = this,
              lbOptions = pick(tick.options && tick.options.labels, labelOptions),
              pos = tick.pos,
              axis = tick.axis,
              options = axis.options,
              isTreeGrid = options.type === 'treegrid',
              result = proceed.apply(tick, [x, y, label, horiz, lbOptions, tickmarkOffset, index, step]),
              symbolOptions,
              indentation,
              mapOfPosToGridNode,
              node,
              level;

          if (isTreeGrid) {
            symbolOptions = lbOptions && isObject(lbOptions.symbol) ? lbOptions.symbol : {};
            indentation = lbOptions && isNumber(lbOptions.indentation) ? lbOptions.indentation : 0;
            mapOfPosToGridNode = axis.mapOfPosToGridNode;
            node = mapOfPosToGridNode && mapOfPosToGridNode[pos];
            level = node && node.depth || 1;
            result.x += symbolOptions.width + symbolOptions.padding * 2 + (level - 1) * indentation;
          }

          return result;
        },
        renderLabel: function renderLabel(proceed) {
          var tick = this,
              pos = tick.pos,
              axis = tick.axis,
              label = tick.label,
              mapOfPosToGridNode = axis.mapOfPosToGridNode,
              options = axis.options,
              labelOptions = pick(tick.options && tick.options.labels, options && options.labels),
              symbolOptions = labelOptions && isObject(labelOptions.symbol) ? labelOptions.symbol : {},
              node = mapOfPosToGridNode && mapOfPosToGridNode[pos],
              level = node && node.depth,
              isTreeGrid = options.type === 'treegrid',
              hasLabel = !!(label && label.element),
              shouldRender = axis.tickPositions.indexOf(pos) > -1,
              prefixClassName = 'highcharts-treegrid-node-',
              collapsed,
              addClassName,
              removeClassName,
              styledMode = axis.chart.styledMode;

          if (isTreeGrid && node) {
            if (hasLabel) {
              label.addClass(prefixClassName + 'level-' + level);
            }
          }

          proceed.apply(tick, argsToArray(arguments));

          if (isTreeGrid && node && hasLabel && node.descendants > 0) {
            collapsed = isCollapsed(axis, node);
            renderLabelIcon(tick, {
              color: !styledMode && label.styles.color,
              collapsed: collapsed,
              group: label.parentGroup,
              options: symbolOptions,
              renderer: label.renderer,
              show: shouldRender,
              xy: label.xy
            });
            addClassName = prefixClassName + (collapsed ? 'collapsed' : 'expanded');
            removeClassName = prefixClassName + (collapsed ? 'expanded' : 'collapsed');
            label.addClass(addClassName).removeClass(removeClassName);

            if (!styledMode) {
              label.css({
                cursor: 'pointer'
              });
            }

            [label, tick.labelIcon].forEach(function (object) {
              if (!object.attachedTreeGridEvents) {
                addEvent(object.element, 'mouseover', function () {
                  onTickHover(label);
                });
                addEvent(object.element, 'mouseout', function () {
                  onTickHoverExit(label, labelOptions);
                });
                addEvent(object.element, 'click', function () {
                  tick.toggleCollapse();
                });
                object.attachedTreeGridEvents = true;
              }
            });
          }
        }
      });
      extend(GridAxisTick.prototype, {
        collapse: function collapse(redraw) {
          var tick = this,
              axis = tick.axis,
              pos = tick.pos,
              node = axis.mapOfPosToGridNode[pos],
              breaks = _collapse(axis, node);

          axis.setBreaks(breaks, pick(redraw, true));
        },
        expand: function expand(redraw) {
          var tick = this,
              axis = tick.axis,
              pos = tick.pos,
              node = axis.mapOfPosToGridNode[pos],
              breaks = _expand(axis, node);

          axis.setBreaks(breaks, pick(redraw, true));
        },
        toggleCollapse: function toggleCollapse(redraw) {
          var tick = this,
              axis = tick.axis,
              pos = tick.pos,
              node = axis.mapOfPosToGridNode[pos],
              breaks = _toggleCollapse(axis, node);

          axis.setBreaks(breaks, pick(redraw, true));
        }
      });
      GridAxis.prototype.utils = {
        getNode: Tree.getNode
      };
    });

    _registerModule(_modules, 'parts-gantt/PathfinderAlgorithms.js', [_modules['parts/Utilities.js']], function (U) {
      var extend = U.extend,
          pick = U.pick;
      var min = Math.min,
          max = Math.max,
          abs = Math.abs;

      function findLastObstacleBefore(obstacles, xMin, startIx) {
        var left = startIx || 0,
            right = obstacles.length - 1,
            min = xMin - 0.0000001,
            cursor,
            cmp;

        while (left <= right) {
          cursor = right + left >> 1;
          cmp = min - obstacles[cursor].xMin;

          if (cmp > 0) {
            left = cursor + 1;
          } else if (cmp < 0) {
            right = cursor - 1;
          } else {
            return cursor;
          }
        }

        return left > 0 ? left - 1 : 0;
      }

      function pointWithinObstacle(obstacle, point) {
        return point.x <= obstacle.xMax && point.x >= obstacle.xMin && point.y <= obstacle.yMax && point.y >= obstacle.yMin;
      }

      function findObstacleFromPoint(obstacles, point) {
        var i = findLastObstacleBefore(obstacles, point.x + 1) + 1;

        while (i--) {
          if (obstacles[i].xMax >= point.x && pointWithinObstacle(obstacles[i], point)) {
            return i;
          }
        }

        return -1;
      }

      function pathFromSegments(segments) {
        var path = [];

        if (segments.length) {
          path.push('M', segments[0].start.x, segments[0].start.y);

          for (var i = 0; i < segments.length; ++i) {
            path.push('L', segments[i].end.x, segments[i].end.y);
          }
        }

        return path;
      }

      function limitObstacleToBounds(obstacle, bounds) {
        obstacle.yMin = max(obstacle.yMin, bounds.yMin);
        obstacle.yMax = min(obstacle.yMax, bounds.yMax);
        obstacle.xMin = max(obstacle.xMin, bounds.xMin);
        obstacle.xMax = min(obstacle.xMax, bounds.xMax);
      }

      var algorithms = {
        straight: function straight(start, end) {
          return {
            path: ['M', start.x, start.y, 'L', end.x, end.y],
            obstacles: [{
              start: start,
              end: end
            }]
          };
        },
        simpleConnect: extend(function (start, end, options) {
          var segments = [],
              endSegment,
              dir = pick(options.startDirectionX, abs(end.x - start.x) > abs(end.y - start.y)) ? 'x' : 'y',
              chartObstacles = options.chartObstacles,
              startObstacleIx = findObstacleFromPoint(chartObstacles, start),
              endObstacleIx = findObstacleFromPoint(chartObstacles, end),
              startObstacle,
              endObstacle,
              prevWaypoint,
              waypoint,
              waypoint2,
              useMax,
              endPoint;

          function copyFromPoint(from, fromKey, to, toKey, offset) {
            var point = {
              x: from.x,
              y: from.y
            };
            point[fromKey] = to[toKey || fromKey] + (offset || 0);
            return point;
          }

          function getMeOut(obstacle, point, direction) {
            var useMax = abs(point[direction] - obstacle[direction + 'Min']) > abs(point[direction] - obstacle[direction + 'Max']);
            return copyFromPoint(point, direction, obstacle, direction + (useMax ? 'Max' : 'Min'), useMax ? 1 : -1);
          }

          if (endObstacleIx > -1) {
            endObstacle = chartObstacles[endObstacleIx];
            waypoint = getMeOut(endObstacle, end, dir);
            endSegment = {
              start: waypoint,
              end: end
            };
            endPoint = waypoint;
          } else {
            endPoint = end;
          }

          if (startObstacleIx > -1) {
            startObstacle = chartObstacles[startObstacleIx];
            waypoint = getMeOut(startObstacle, start, dir);
            segments.push({
              start: start,
              end: waypoint
            });

            if (waypoint[dir] >= start[dir] === waypoint[dir] >= endPoint[dir]) {
              dir = dir === 'y' ? 'x' : 'y';
              useMax = start[dir] < end[dir];
              segments.push({
                start: waypoint,
                end: copyFromPoint(waypoint, dir, startObstacle, dir + (useMax ? 'Max' : 'Min'), useMax ? 1 : -1)
              });
              dir = dir === 'y' ? 'x' : 'y';
            }
          }

          prevWaypoint = segments.length ? segments[segments.length - 1].end : start;
          waypoint = copyFromPoint(prevWaypoint, dir, endPoint);
          segments.push({
            start: prevWaypoint,
            end: waypoint
          });
          dir = dir === 'y' ? 'x' : 'y';
          waypoint2 = copyFromPoint(waypoint, dir, endPoint);
          segments.push({
            start: waypoint,
            end: waypoint2
          });
          segments.push(endSegment);
          return {
            path: pathFromSegments(segments),
            obstacles: segments
          };
        }, {
          requiresObstacles: true
        }),
        fastAvoid: extend(function (start, end, options) {
          var dirIsX = pick(options.startDirectionX, abs(end.x - start.x) > abs(end.y - start.y)),
              dir = dirIsX ? 'x' : 'y',
              segments,
              useMax,
              extractedEndPoint,
              endSegments = [],
              forceObstacleBreak = false,
              metrics = options.obstacleMetrics,
              softMinX = min(start.x, end.x) - metrics.maxWidth - 10,
              softMaxX = max(start.x, end.x) + metrics.maxWidth + 10,
              softMinY = min(start.y, end.y) - metrics.maxHeight - 10,
              softMaxY = max(start.y, end.y) + metrics.maxHeight + 10,
              chartObstacles = options.chartObstacles,
              startObstacleIx = findLastObstacleBefore(chartObstacles, softMinX),
              endObstacleIx = findLastObstacleBefore(chartObstacles, softMaxX);

          function pivotPoint(fromPoint, toPoint, directionIsX) {
            var firstPoint,
                lastPoint,
                highestPoint,
                lowestPoint,
                i,
                searchDirection = fromPoint.x < toPoint.x ? 1 : -1;

            if (fromPoint.x < toPoint.x) {
              firstPoint = fromPoint;
              lastPoint = toPoint;
            } else {
              firstPoint = toPoint;
              lastPoint = fromPoint;
            }

            if (fromPoint.y < toPoint.y) {
              lowestPoint = fromPoint;
              highestPoint = toPoint;
            } else {
              lowestPoint = toPoint;
              highestPoint = fromPoint;
            }

            i = searchDirection < 0 ? min(findLastObstacleBefore(chartObstacles, lastPoint.x), chartObstacles.length - 1) : 0;

            while (chartObstacles[i] && (searchDirection > 0 && chartObstacles[i].xMin <= lastPoint.x || searchDirection < 0 && chartObstacles[i].xMax >= firstPoint.x)) {
              if (chartObstacles[i].xMin <= lastPoint.x && chartObstacles[i].xMax >= firstPoint.x && chartObstacles[i].yMin <= highestPoint.y && chartObstacles[i].yMax >= lowestPoint.y) {
                if (directionIsX) {
                  return {
                    y: fromPoint.y,
                    x: fromPoint.x < toPoint.x ? chartObstacles[i].xMin - 1 : chartObstacles[i].xMax + 1,
                    obstacle: chartObstacles[i]
                  };
                }

                return {
                  x: fromPoint.x,
                  y: fromPoint.y < toPoint.y ? chartObstacles[i].yMin - 1 : chartObstacles[i].yMax + 1,
                  obstacle: chartObstacles[i]
                };
              }

              i += searchDirection;
            }

            return toPoint;
          }

          function getDodgeDirection(obstacle, fromPoint, toPoint, dirIsX, bounds) {
            var softBounds = bounds.soft,
                hardBounds = bounds.hard,
                dir = dirIsX ? 'x' : 'y',
                toPointMax = {
              x: fromPoint.x,
              y: fromPoint.y
            },
                toPointMin = {
              x: fromPoint.x,
              y: fromPoint.y
            },
                minPivot,
                maxPivot,
                maxOutOfSoftBounds = obstacle[dir + 'Max'] >= softBounds[dir + 'Max'],
                minOutOfSoftBounds = obstacle[dir + 'Min'] <= softBounds[dir + 'Min'],
                maxOutOfHardBounds = obstacle[dir + 'Max'] >= hardBounds[dir + 'Max'],
                minOutOfHardBounds = obstacle[dir + 'Min'] <= hardBounds[dir + 'Min'],
                minDistance = abs(obstacle[dir + 'Min'] - fromPoint[dir]),
                maxDistance = abs(obstacle[dir + 'Max'] - fromPoint[dir]),
                useMax = abs(minDistance - maxDistance) < 10 ? fromPoint[dir] < toPoint[dir] : maxDistance < minDistance;
            toPointMin[dir] = obstacle[dir + 'Min'];
            toPointMax[dir] = obstacle[dir + 'Max'];
            minPivot = pivotPoint(fromPoint, toPointMin, dirIsX)[dir] !== toPointMin[dir];
            maxPivot = pivotPoint(fromPoint, toPointMax, dirIsX)[dir] !== toPointMax[dir];
            useMax = minPivot ? maxPivot ? useMax : true : maxPivot ? false : useMax;
            useMax = minOutOfSoftBounds ? maxOutOfSoftBounds ? useMax : true : maxOutOfSoftBounds ? false : useMax;
            useMax = minOutOfHardBounds ? maxOutOfHardBounds ? useMax : true : maxOutOfHardBounds ? false : useMax;
            return useMax;
          }

          function clearPathTo(fromPoint, toPoint, dirIsX) {
            if (fromPoint.x === toPoint.x && fromPoint.y === toPoint.y) {
              return [];
            }

            var dir = dirIsX ? 'x' : 'y',
                pivot,
                segments,
                waypoint,
                waypointUseMax,
                envelopingObstacle,
                secondEnvelopingObstacle,
                envelopWaypoint,
                obstacleMargin = options.obstacleOptions.margin,
                bounds = {
              soft: {
                xMin: softMinX,
                xMax: softMaxX,
                yMin: softMinY,
                yMax: softMaxY
              },
              hard: options.hardBounds
            };
            envelopingObstacle = findObstacleFromPoint(chartObstacles, fromPoint);

            if (envelopingObstacle > -1) {
              envelopingObstacle = chartObstacles[envelopingObstacle];
              waypointUseMax = getDodgeDirection(envelopingObstacle, fromPoint, toPoint, dirIsX, bounds);
              limitObstacleToBounds(envelopingObstacle, options.hardBounds);
              envelopWaypoint = dirIsX ? {
                y: fromPoint.y,
                x: envelopingObstacle[waypointUseMax ? 'xMax' : 'xMin'] + (waypointUseMax ? 1 : -1)
              } : {
                x: fromPoint.x,
                y: envelopingObstacle[waypointUseMax ? 'yMax' : 'yMin'] + (waypointUseMax ? 1 : -1)
              };
              secondEnvelopingObstacle = findObstacleFromPoint(chartObstacles, envelopWaypoint);

              if (secondEnvelopingObstacle > -1) {
                secondEnvelopingObstacle = chartObstacles[secondEnvelopingObstacle];
                limitObstacleToBounds(secondEnvelopingObstacle, options.hardBounds);
                envelopWaypoint[dir] = waypointUseMax ? max(envelopingObstacle[dir + 'Max'] - obstacleMargin + 1, (secondEnvelopingObstacle[dir + 'Min'] + envelopingObstacle[dir + 'Max']) / 2) : min(envelopingObstacle[dir + 'Min'] + obstacleMargin - 1, (secondEnvelopingObstacle[dir + 'Max'] + envelopingObstacle[dir + 'Min']) / 2);

                if (fromPoint.x === envelopWaypoint.x && fromPoint.y === envelopWaypoint.y) {
                  if (forceObstacleBreak) {
                    envelopWaypoint[dir] = waypointUseMax ? max(envelopingObstacle[dir + 'Max'], secondEnvelopingObstacle[dir + 'Max']) + 1 : min(envelopingObstacle[dir + 'Min'], secondEnvelopingObstacle[dir + 'Min']) - 1;
                  }

                  forceObstacleBreak = !forceObstacleBreak;
                } else {
                  forceObstacleBreak = false;
                }
              }

              segments = [{
                start: fromPoint,
                end: envelopWaypoint
              }];
            } else {
              pivot = pivotPoint(fromPoint, {
                x: dirIsX ? toPoint.x : fromPoint.x,
                y: dirIsX ? fromPoint.y : toPoint.y
              }, dirIsX);
              segments = [{
                start: fromPoint,
                end: {
                  x: pivot.x,
                  y: pivot.y
                }
              }];

              if (pivot[dirIsX ? 'x' : 'y'] !== toPoint[dirIsX ? 'x' : 'y']) {
                waypointUseMax = getDodgeDirection(pivot.obstacle, pivot, toPoint, !dirIsX, bounds);
                limitObstacleToBounds(pivot.obstacle, options.hardBounds);
                waypoint = {
                  x: dirIsX ? pivot.x : pivot.obstacle[waypointUseMax ? 'xMax' : 'xMin'] + (waypointUseMax ? 1 : -1),
                  y: dirIsX ? pivot.obstacle[waypointUseMax ? 'yMax' : 'yMin'] + (waypointUseMax ? 1 : -1) : pivot.y
                };
                dirIsX = !dirIsX;
                segments = segments.concat(clearPathTo({
                  x: pivot.x,
                  y: pivot.y
                }, waypoint, dirIsX));
              }
            }

            segments = segments.concat(clearPathTo(segments[segments.length - 1].end, toPoint, !dirIsX));
            return segments;
          }

          function extractFromObstacle(obstacle, point, goalPoint) {
            var dirIsX = min(obstacle.xMax - point.x, point.x - obstacle.xMin) < min(obstacle.yMax - point.y, point.y - obstacle.yMin),
                bounds = {
              soft: options.hardBounds,
              hard: options.hardBounds
            },
                useMax = getDodgeDirection(obstacle, point, goalPoint, dirIsX, bounds);
            return dirIsX ? {
              y: point.y,
              x: obstacle[useMax ? 'xMax' : 'xMin'] + (useMax ? 1 : -1)
            } : {
              x: point.x,
              y: obstacle[useMax ? 'yMax' : 'yMin'] + (useMax ? 1 : -1)
            };
          }

          chartObstacles = chartObstacles.slice(startObstacleIx, endObstacleIx + 1);

          if ((endObstacleIx = findObstacleFromPoint(chartObstacles, end)) > -1) {
            extractedEndPoint = extractFromObstacle(chartObstacles[endObstacleIx], end, start);
            endSegments.push({
              end: end,
              start: extractedEndPoint
            });
            end = extractedEndPoint;
          }

          while ((endObstacleIx = findObstacleFromPoint(chartObstacles, end)) > -1) {
            useMax = end[dir] - start[dir] < 0;
            extractedEndPoint = {
              x: end.x,
              y: end.y
            };
            extractedEndPoint[dir] = chartObstacles[endObstacleIx][useMax ? dir + 'Max' : dir + 'Min'] + (useMax ? 1 : -1);
            endSegments.push({
              end: end,
              start: extractedEndPoint
            });
            end = extractedEndPoint;
          }

          segments = clearPathTo(start, end, dirIsX);
          segments = segments.concat(endSegments.reverse());
          return {
            path: pathFromSegments(segments),
            obstacles: segments
          };
        }, {
          requiresObstacles: true
        })
      };
      return algorithms;
    });

    _registerModule(_modules, 'parts-gantt/ArrowSymbols.js', [_modules['parts/Globals.js']], function (H) {
      H.SVGRenderer.prototype.symbols.arrow = function (x, y, w, h) {
        return ['M', x, y + h / 2, 'L', x + w, y, 'L', x, y + h / 2, 'L', x + w, y + h];
      };

      H.SVGRenderer.prototype.symbols['arrow-half'] = function (x, y, w, h) {
        return H.SVGRenderer.prototype.symbols.arrow(x, y, w / 2, h);
      };

      H.SVGRenderer.prototype.symbols['triangle-left'] = function (x, y, w, h) {
        return ['M', x + w, y, 'L', x, y + h / 2, 'L', x + w, y + h, 'Z'];
      };

      H.SVGRenderer.prototype.symbols['arrow-filled'] = H.SVGRenderer.prototype.symbols['triangle-left'];

      H.SVGRenderer.prototype.symbols['triangle-left-half'] = function (x, y, w, h) {
        return H.SVGRenderer.prototype.symbols['triangle-left'](x, y, w / 2, h);
      };

      H.SVGRenderer.prototype.symbols['arrow-filled-half'] = H.SVGRenderer.prototype.symbols['triangle-left-half'];
    });

    _registerModule(_modules, 'parts-gantt/Pathfinder.js', [_modules['parts/Globals.js'], _modules['parts/Point.js'], _modules['parts/Utilities.js'], _modules['parts-gantt/PathfinderAlgorithms.js']], function (H, Point, U, pathfinderAlgorithms) {

      var addEvent = U.addEvent,
          defined = U.defined,
          error = U.error,
          extend = U.extend,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick,
          splat = U.splat;
      var deg2rad = H.deg2rad,
          max = Math.max,
          min = Math.min;
      extend(H.defaultOptions, {
        connectors: {
          type: 'straight',
          lineWidth: 1,
          marker: {
            enabled: false,
            align: 'center',
            verticalAlign: 'middle',
            inside: false,
            lineWidth: 1
          },
          startMarker: {
            symbol: 'diamond'
          },
          endMarker: {
            symbol: 'arrow-filled'
          }
        }
      });

      function getPointBB(point) {
        var shapeArgs = point.shapeArgs,
            bb;

        if (shapeArgs) {
          return {
            xMin: shapeArgs.x,
            xMax: shapeArgs.x + shapeArgs.width,
            yMin: shapeArgs.y,
            yMax: shapeArgs.y + shapeArgs.height
          };
        }

        bb = point.graphic && point.graphic.getBBox();
        return bb ? {
          xMin: point.plotX - bb.width / 2,
          xMax: point.plotX + bb.width / 2,
          yMin: point.plotY - bb.height / 2,
          yMax: point.plotY + bb.height / 2
        } : null;
      }

      function calculateObstacleMargin(obstacles) {
        var len = obstacles.length,
            i = 0,
            j,
            obstacleDistance,
            distances = [],
            distance = function distance(a, b, bbMargin) {
          var margin = pick(bbMargin, 10),
              yOverlap = a.yMax + margin > b.yMin - margin && a.yMin - margin < b.yMax + margin,
              xOverlap = a.xMax + margin > b.xMin - margin && a.xMin - margin < b.xMax + margin,
              xDistance = yOverlap ? a.xMin > b.xMax ? a.xMin - b.xMax : b.xMin - a.xMax : Infinity,
              yDistance = xOverlap ? a.yMin > b.yMax ? a.yMin - b.yMax : b.yMin - a.yMax : Infinity;

          if (xOverlap && yOverlap) {
            return margin ? distance(a, b, Math.floor(margin / 2)) : Infinity;
          }

          return min(xDistance, yDistance);
        };

        for (; i < len; ++i) {
          for (j = i + 1; j < len; ++j) {
            obstacleDistance = distance(obstacles[i], obstacles[j]);

            if (obstacleDistance < 80) {
              distances.push(obstacleDistance);
            }
          }
        }

        distances.push(80);
        return max(Math.floor(distances.sort(function (a, b) {
          return a - b;
        })[Math.floor(distances.length / 10)] / 2 - 1), 1);
      }

      function Connection(from, to, options) {
        this.init(from, to, options);
      }

      Connection.prototype = {
        init: function init(from, to, options) {
          this.fromPoint = from;
          this.toPoint = to;
          this.options = options;
          this.chart = from.series.chart;
          this.pathfinder = this.chart.pathfinder;
        },
        renderPath: function renderPath(path, attribs, animation) {
          var connection = this,
              chart = this.chart,
              styledMode = chart.styledMode,
              pathfinder = chart.pathfinder,
              animate = !chart.options.chart.forExport && animation !== false,
              pathGraphic = connection.graphics && connection.graphics.path,
              anim;

          if (!pathfinder.group) {
            pathfinder.group = chart.renderer.g().addClass('highcharts-pathfinder-group').attr({
              zIndex: -1
            }).add(chart.seriesGroup);
          }

          pathfinder.group.translate(chart.plotLeft, chart.plotTop);

          if (!(pathGraphic && pathGraphic.renderer)) {
            pathGraphic = chart.renderer.path().add(pathfinder.group);

            if (!styledMode) {
              pathGraphic.attr({
                opacity: 0
              });
            }
          }

          pathGraphic.attr(attribs);
          anim = {
            d: path
          };

          if (!styledMode) {
            anim.opacity = 1;
          }

          pathGraphic[animate ? 'animate' : 'attr'](anim, animation);
          this.graphics = this.graphics || {};
          this.graphics.path = pathGraphic;
        },
        addMarker: function addMarker(type, options, path) {
          var connection = this,
              chart = connection.fromPoint.series.chart,
              pathfinder = chart.pathfinder,
              renderer = chart.renderer,
              point = type === 'start' ? connection.fromPoint : connection.toPoint,
              anchor = point.getPathfinderAnchorPoint(options),
              markerVector,
              radians,
              rotation,
              box,
              width,
              height,
              pathVector;

          if (!options.enabled) {
            return;
          }

          if (type === 'start') {
            pathVector = {
              x: path[4],
              y: path[5]
            };
          } else {
            pathVector = {
              x: path[path.length - 5],
              y: path[path.length - 4]
            };
          }

          radians = point.getRadiansToVector(pathVector, anchor);
          markerVector = point.getMarkerVector(radians, options.radius, anchor);
          rotation = -radians / deg2rad;

          if (options.width && options.height) {
            width = options.width;
            height = options.height;
          } else {
            width = height = options.radius * 2;
          }

          connection.graphics = connection.graphics || {};
          box = {
            x: markerVector.x - width / 2,
            y: markerVector.y - height / 2,
            width: width,
            height: height,
            rotation: rotation,
            rotationOriginX: markerVector.x,
            rotationOriginY: markerVector.y
          };

          if (!connection.graphics[type]) {
            connection.graphics[type] = renderer.symbol(options.symbol).addClass('highcharts-point-connecting-path-' + type + '-marker').attr(box).add(pathfinder.group);

            if (!renderer.styledMode) {
              connection.graphics[type].attr({
                fill: options.color || connection.fromPoint.color,
                stroke: options.lineColor,
                'stroke-width': options.lineWidth,
                opacity: 0
              }).animate({
                opacity: 1
              }, point.series.options.animation);
            }
          } else {
            connection.graphics[type].animate(box);
          }
        },
        getPath: function getPath(options) {
          var pathfinder = this.pathfinder,
              chart = this.chart,
              algorithm = pathfinder.algorithms[options.type],
              chartObstacles = pathfinder.chartObstacles;

          if (typeof algorithm !== 'function') {
            error('"' + options.type + '" is not a Pathfinder algorithm.');
            return;
          }

          if (algorithm.requiresObstacles && !chartObstacles) {
            chartObstacles = pathfinder.chartObstacles = pathfinder.getChartObstacles(options);
            chart.options.connectors.algorithmMargin = options.algorithmMargin;
            pathfinder.chartObstacleMetrics = pathfinder.getObstacleMetrics(chartObstacles);
          }

          return algorithm(this.fromPoint.getPathfinderAnchorPoint(options.startMarker), this.toPoint.getPathfinderAnchorPoint(options.endMarker), merge({
            chartObstacles: chartObstacles,
            lineObstacles: pathfinder.lineObstacles || [],
            obstacleMetrics: pathfinder.chartObstacleMetrics,
            hardBounds: {
              xMin: 0,
              xMax: chart.plotWidth,
              yMin: 0,
              yMax: chart.plotHeight
            },
            obstacleOptions: {
              margin: options.algorithmMargin
            },
            startDirectionX: pathfinder.getAlgorithmStartDirection(options.startMarker)
          }, options));
        },
        render: function render() {
          var connection = this,
              fromPoint = connection.fromPoint,
              series = fromPoint.series,
              chart = series.chart,
              pathfinder = chart.pathfinder,
              pathResult,
              path,
              options = merge(chart.options.connectors, series.options.connectors, fromPoint.options.connectors, connection.options),
              attribs = {};

          if (!chart.styledMode) {
            attribs.stroke = options.lineColor || fromPoint.color;
            attribs['stroke-width'] = options.lineWidth;

            if (options.dashStyle) {
              attribs.dashstyle = options.dashStyle;
            }
          }

          attribs['class'] = 'highcharts-point-connecting-path ' + 'highcharts-color-' + fromPoint.colorIndex;
          options = merge(attribs, options);

          if (!defined(options.marker.radius)) {
            options.marker.radius = min(max(Math.ceil((options.algorithmMargin || 8) / 2) - 1, 1), 5);
          }

          pathResult = connection.getPath(options);
          path = pathResult.path;

          if (pathResult.obstacles) {
            pathfinder.lineObstacles = pathfinder.lineObstacles || [];
            pathfinder.lineObstacles = pathfinder.lineObstacles.concat(pathResult.obstacles);
          }

          connection.renderPath(path, attribs, series.options.animation);
          connection.addMarker('start', merge(options.marker, options.startMarker), path);
          connection.addMarker('end', merge(options.marker, options.endMarker), path);
        },
        destroy: function destroy() {
          if (this.graphics) {
            objectEach(this.graphics, function (val) {
              val.destroy();
            });
            delete this.graphics;
          }
        }
      };

      function Pathfinder(chart) {
        this.init(chart);
      }

      Pathfinder.prototype = {
        algorithms: pathfinderAlgorithms,
        init: function init(chart) {
          this.chart = chart;
          this.connections = [];
          addEvent(chart, 'redraw', function () {
            this.pathfinder.update();
          });
        },
        update: function update(deferRender) {
          var chart = this.chart,
              pathfinder = this,
              oldConnections = pathfinder.connections;
          pathfinder.connections = [];
          chart.series.forEach(function (series) {
            if (series.visible && !series.options.isInternal) {
              series.points.forEach(function (point) {
                var to,
                    connects = point.options && point.options.connect && splat(point.options.connect);

                if (point.visible && point.isInside !== false && connects) {
                  connects.forEach(function (connect) {
                    to = chart.get(typeof connect === 'string' ? connect : connect.to);

                    if (to instanceof Point && to.series.visible && to.visible && to.isInside !== false) {
                      pathfinder.connections.push(new Connection(point, to, typeof connect === 'string' ? {} : connect));
                    }
                  });
                }
              });
            }
          });

          for (var j = 0, k, found, lenOld = oldConnections.length, lenNew = pathfinder.connections.length; j < lenOld; ++j) {
            found = false;

            for (k = 0; k < lenNew; ++k) {
              if (oldConnections[j].fromPoint === pathfinder.connections[k].fromPoint && oldConnections[j].toPoint === pathfinder.connections[k].toPoint) {
                pathfinder.connections[k].graphics = oldConnections[j].graphics;
                found = true;
                break;
              }
            }

            if (!found) {
              oldConnections[j].destroy();
            }
          }

          delete this.chartObstacles;
          delete this.lineObstacles;
          pathfinder.renderConnections(deferRender);
        },
        renderConnections: function renderConnections(deferRender) {
          if (deferRender) {
            this.chart.series.forEach(function (series) {
              var render = function render() {
                var pathfinder = series.chart.pathfinder,
                    conns = pathfinder && pathfinder.connections || [];
                conns.forEach(function (connection) {
                  if (connection.fromPoint && connection.fromPoint.series === series) {
                    connection.render();
                  }
                });

                if (series.pathfinderRemoveRenderEvent) {
                  series.pathfinderRemoveRenderEvent();
                  delete series.pathfinderRemoveRenderEvent;
                }
              };

              if (series.options.animation === false) {
                render();
              } else {
                series.pathfinderRemoveRenderEvent = addEvent(series, 'afterAnimate', render);
              }
            });
          } else {
            this.connections.forEach(function (connection) {
              connection.render();
            });
          }
        },
        getChartObstacles: function getChartObstacles(options) {
          var obstacles = [],
              series = this.chart.series,
              margin = pick(options.algorithmMargin, 0),
              calculatedMargin;

          for (var i = 0, sLen = series.length; i < sLen; ++i) {
            if (series[i].visible && !series[i].options.isInternal) {
              for (var j = 0, pLen = series[i].points.length, bb, point; j < pLen; ++j) {
                point = series[i].points[j];

                if (point.visible) {
                  bb = getPointBB(point);

                  if (bb) {
                    obstacles.push({
                      xMin: bb.xMin - margin,
                      xMax: bb.xMax + margin,
                      yMin: bb.yMin - margin,
                      yMax: bb.yMax + margin
                    });
                  }
                }
              }
            }
          }

          obstacles = obstacles.sort(function (a, b) {
            return a.xMin - b.xMin;
          });

          if (!defined(options.algorithmMargin)) {
            calculatedMargin = options.algorithmMargin = calculateObstacleMargin(obstacles);
            obstacles.forEach(function (obstacle) {
              obstacle.xMin -= calculatedMargin;
              obstacle.xMax += calculatedMargin;
              obstacle.yMin -= calculatedMargin;
              obstacle.yMax += calculatedMargin;
            });
          }

          return obstacles;
        },
        getObstacleMetrics: function getObstacleMetrics(obstacles) {
          var maxWidth = 0,
              maxHeight = 0,
              width,
              height,
              i = obstacles.length;

          while (i--) {
            width = obstacles[i].xMax - obstacles[i].xMin;
            height = obstacles[i].yMax - obstacles[i].yMin;

            if (maxWidth < width) {
              maxWidth = width;
            }

            if (maxHeight < height) {
              maxHeight = height;
            }
          }

          return {
            maxHeight: maxHeight,
            maxWidth: maxWidth
          };
        },
        getAlgorithmStartDirection: function getAlgorithmStartDirection(markerOptions) {
          var xCenter = markerOptions.align !== 'left' && markerOptions.align !== 'right',
              yCenter = markerOptions.verticalAlign !== 'top' && markerOptions.verticalAlign !== 'bottom',
              undef;
          return xCenter ? yCenter ? undef : false : yCenter ? true : undef;
        }
      };
      H.Connection = Connection;
      H.Pathfinder = Pathfinder;
      extend(Point.prototype, {
        getPathfinderAnchorPoint: function getPathfinderAnchorPoint(markerOptions) {
          var bb = getPointBB(this),
              x,
              y;

          switch (markerOptions.align) {
            case 'right':
              x = 'xMax';
              break;

            case 'left':
              x = 'xMin';
          }

          switch (markerOptions.verticalAlign) {
            case 'top':
              y = 'yMin';
              break;

            case 'bottom':
              y = 'yMax';
          }

          return {
            x: x ? bb[x] : (bb.xMin + bb.xMax) / 2,
            y: y ? bb[y] : (bb.yMin + bb.yMax) / 2
          };
        },
        getRadiansToVector: function getRadiansToVector(v1, v2) {
          var box;

          if (!defined(v2)) {
            box = getPointBB(this);
            v2 = {
              x: (box.xMin + box.xMax) / 2,
              y: (box.yMin + box.yMax) / 2
            };
          }

          return Math.atan2(v2.y - v1.y, v1.x - v2.x);
        },
        getMarkerVector: function getMarkerVector(radians, markerRadius, anchor) {
          var twoPI = Math.PI * 2.0,
              theta = radians,
              bb = getPointBB(this),
              rectWidth = bb.xMax - bb.xMin,
              rectHeight = bb.yMax - bb.yMin,
              rAtan = Math.atan2(rectHeight, rectWidth),
              tanTheta = 1,
              leftOrRightRegion = false,
              rectHalfWidth = rectWidth / 2.0,
              rectHalfHeight = rectHeight / 2.0,
              rectHorizontalCenter = bb.xMin + rectHalfWidth,
              rectVerticalCenter = bb.yMin + rectHalfHeight,
              edgePoint = {
            x: rectHorizontalCenter,
            y: rectVerticalCenter
          },
              markerPoint = {},
              xFactor = 1,
              yFactor = 1;

          while (theta < -Math.PI) {
            theta += twoPI;
          }

          while (theta > Math.PI) {
            theta -= twoPI;
          }

          tanTheta = Math.tan(theta);

          if (theta > -rAtan && theta <= rAtan) {
            yFactor = -1;
            leftOrRightRegion = true;
          } else if (theta > rAtan && theta <= Math.PI - rAtan) {
            yFactor = -1;
          } else if (theta > Math.PI - rAtan || theta <= -(Math.PI - rAtan)) {
            xFactor = -1;
            leftOrRightRegion = true;
          } else {
            xFactor = -1;
          }

          if (leftOrRightRegion) {
            edgePoint.x += xFactor * rectHalfWidth;
            edgePoint.y += yFactor * rectHalfWidth * tanTheta;
          } else {
            edgePoint.x += xFactor * (rectHeight / (2.0 * tanTheta));
            edgePoint.y += yFactor * rectHalfHeight;
          }

          if (anchor.x !== rectHorizontalCenter) {
            edgePoint.x = anchor.x;
          }

          if (anchor.y !== rectVerticalCenter) {
            edgePoint.y = anchor.y;
          }

          markerPoint.x = edgePoint.x + markerRadius * Math.cos(theta);
          markerPoint.y = edgePoint.y - markerRadius * Math.sin(theta);
          return markerPoint;
        }
      });

      function warnLegacy(chart) {
        if (chart.options.pathfinder || chart.series.reduce(function (acc, series) {
          if (series.options) {
            merge(true, series.options.connectors = series.options.connectors || {}, series.options.pathfinder);
          }

          return acc || series.options && series.options.pathfinder;
        }, false)) {
          merge(true, chart.options.connectors = chart.options.connectors || {}, chart.options.pathfinder);
          error('WARNING: Pathfinder options have been renamed. ' + 'Use "chart.connectors" or "series.connectors" instead.');
        }
      }

      H.Chart.prototype.callbacks.push(function (chart) {
        var options = chart.options;

        if (options.connectors.enabled !== false) {
          warnLegacy(chart);
          this.pathfinder = new Pathfinder(this);
          this.pathfinder.update(true);
        }
      });
    });

    _registerModule(_modules, 'modules/xrange.src.js', [_modules['parts/Globals.js'], _modules['parts/Color.js'], _modules['parts/Point.js'], _modules['parts/Utilities.js']], function (H, Color, Point, U) {
      var color = Color.parse;
      var addEvent = U.addEvent,
          clamp = U.clamp,
          correctFloat = U.correctFloat,
          defined = U.defined,
          find = U.find,
          isNumber = U.isNumber,
          isObject = U.isObject,
          merge = U.merge,
          pick = U.pick,
          seriesType = U.seriesType;
      var columnType = H.seriesTypes.column,
          seriesTypes = H.seriesTypes,
          Axis = H.Axis,
          Series = H.Series;

      function getColorByCategory(series, point) {
        var colors = series.options.colors || series.chart.options.colors,
            colorCount = colors ? colors.length : series.chart.options.chart.colorCount,
            colorIndex = point.y % colorCount,
            color = colors && colors[colorIndex];
        return {
          colorIndex: colorIndex,
          color: color
        };
      }

      seriesType('xrange', 'column', {
        colorByPoint: true,
        dataLabels: {
          formatter: function formatter() {
            var point = this.point,
                amount = point.partialFill;

            if (isObject(amount)) {
              amount = amount.amount;
            }

            if (isNumber(amount) && amount > 0) {
              return correctFloat(amount * 100) + '%';
            }
          },
          inside: true,
          verticalAlign: 'middle'
        },
        tooltip: {
          headerFormat: '<span style="font-size: 10px">{point.x} - {point.x2}</span><br/>',
          pointFormat: "<span style=\"color:{point.color}\">\u25CF</span> {series.name}: <b>{point.yCategory}</b><br/>"
        },
        borderRadius: 3,
        pointRange: 0
      }, {
        type: 'xrange',
        parallelArrays: ['x', 'x2', 'y'],
        requireSorting: false,
        animate: seriesTypes.line.prototype.animate,
        cropShoulder: 1,
        getExtremesFromAll: true,
        autoIncrement: H.noop,
        buildKDTree: H.noop,
        getColumnMetrics: function getColumnMetrics() {
          var metrics,
              chart = this.chart;

          function swapAxes() {
            chart.series.forEach(function (s) {
              var xAxis = s.xAxis;
              s.xAxis = s.yAxis;
              s.yAxis = xAxis;
            });
          }

          swapAxes();
          metrics = columnType.prototype.getColumnMetrics.call(this);
          swapAxes();
          return metrics;
        },
        cropData: function cropData(xData, yData, min, max) {
          var cropData = Series.prototype.cropData,
              crop = cropData.call(this, this.x2Data, yData, min, max);
          crop.xData = xData.slice(crop.start, crop.end);
          return crop;
        },
        findPointIndex: function findPointIndex(options) {
          var _a = this,
              cropped = _a.cropped,
              cropStart = _a.cropStart,
              points = _a.points;

          var id = options.id;
          var pointIndex;

          if (id) {
            var point = find(points, function (point) {
              return point.id === id;
            });
            pointIndex = point ? point.index : void 0;
          }

          if (typeof pointIndex === 'undefined') {
            var point = find(points, function (point) {
              return point.x === options.x && point.x2 === options.x2 && !point.touched;
            });
            pointIndex = point ? point.index : void 0;
          }

          if (cropped && isNumber(pointIndex) && isNumber(cropStart) && pointIndex >= cropStart) {
            pointIndex -= cropStart;
          }

          return pointIndex;
        },
        translatePoint: function translatePoint(point) {
          var series = this,
              xAxis = series.xAxis,
              yAxis = series.yAxis,
              metrics = series.columnMetrics,
              options = series.options,
              minPointLength = options.minPointLength || 0,
              plotX = point.plotX,
              posX = pick(point.x2, point.x + (point.len || 0)),
              plotX2 = xAxis.translate(posX, 0, 0, 0, 1),
              length = Math.abs(plotX2 - plotX),
              widthDifference,
              shapeArgs,
              partialFill,
              inverted = this.chart.inverted,
              borderWidth = pick(options.borderWidth, 1),
              crisper = borderWidth % 2 / 2,
              yOffset = metrics.offset,
              pointHeight = Math.round(metrics.width),
              dlLeft,
              dlRight,
              dlWidth,
              clipRectWidth,
              tooltipYOffset;

          if (minPointLength) {
            widthDifference = minPointLength - length;

            if (widthDifference < 0) {
              widthDifference = 0;
            }

            plotX -= widthDifference / 2;
            plotX2 += widthDifference / 2;
          }

          plotX = Math.max(plotX, -10);
          plotX2 = clamp(plotX2, -10, xAxis.len + 10);

          if (defined(point.options.pointWidth)) {
            yOffset -= (Math.ceil(point.options.pointWidth) - pointHeight) / 2;
            pointHeight = Math.ceil(point.options.pointWidth);
          }

          if (options.pointPlacement && isNumber(point.plotY) && yAxis.categories) {
            point.plotY = yAxis.translate(point.y, 0, 1, 0, 1, options.pointPlacement);
          }

          point.shapeArgs = {
            x: Math.floor(Math.min(plotX, plotX2)) + crisper,
            y: Math.floor(point.plotY + yOffset) + crisper,
            width: Math.round(Math.abs(plotX2 - plotX)),
            height: pointHeight,
            r: series.options.borderRadius
          };
          dlLeft = point.shapeArgs.x;
          dlRight = dlLeft + point.shapeArgs.width;

          if (dlLeft < 0 || dlRight > xAxis.len) {
            dlLeft = clamp(dlLeft, 0, xAxis.len);
            dlRight = clamp(dlRight, 0, xAxis.len);
            dlWidth = dlRight - dlLeft;
            point.dlBox = merge(point.shapeArgs, {
              x: dlLeft,
              width: dlRight - dlLeft,
              centerX: dlWidth ? dlWidth / 2 : null
            });
          } else {
            point.dlBox = null;
          }

          var tooltipPos = point.tooltipPos;
          var xIndex = !inverted ? 0 : 1;
          var yIndex = !inverted ? 1 : 0;
          tooltipYOffset = series.columnMetrics ? series.columnMetrics.offset : -metrics.width / 2;
          tooltipPos[xIndex] = clamp(tooltipPos[xIndex] + (!inverted ? 1 : -1) * (xAxis.reversed ? -1 : 1) * (length / 2), 0, xAxis.len - 1);
          tooltipPos[yIndex] = clamp(tooltipPos[yIndex] + (inverted ? -1 : 1) * tooltipYOffset, 0, yAxis.len - 1);
          partialFill = point.partialFill;

          if (partialFill) {
            if (isObject(partialFill)) {
              partialFill = partialFill.amount;
            }

            if (!isNumber(partialFill)) {
              partialFill = 0;
            }

            shapeArgs = point.shapeArgs;
            point.partShapeArgs = {
              x: shapeArgs.x,
              y: shapeArgs.y,
              width: shapeArgs.width,
              height: shapeArgs.height,
              r: series.options.borderRadius
            };
            clipRectWidth = Math.max(Math.round(length * partialFill + point.plotX - plotX), 0);
            point.clipRectArgs = {
              x: xAxis.reversed ? shapeArgs.x + length - clipRectWidth : shapeArgs.x,
              y: shapeArgs.y,
              width: clipRectWidth,
              height: shapeArgs.height
            };
          }
        },
        translate: function translate() {
          columnType.prototype.translate.apply(this, arguments);
          this.points.forEach(function (point) {
            this.translatePoint(point);
          }, this);
        },
        drawPoint: function drawPoint(point, verb) {
          var series = this,
              seriesOpts = series.options,
              renderer = series.chart.renderer,
              graphic = point.graphic,
              type = point.shapeType,
              shapeArgs = point.shapeArgs,
              partShapeArgs = point.partShapeArgs,
              clipRectArgs = point.clipRectArgs,
              pfOptions = point.partialFill,
              cutOff = seriesOpts.stacking && !seriesOpts.borderRadius,
              pointState = point.state,
              stateOpts = seriesOpts.states[pointState || 'normal'] || {},
              pointStateVerb = typeof pointState === 'undefined' ? 'attr' : verb,
              pointAttr = series.pointAttribs(point, pointState),
              animation = pick(series.chart.options.chart.animation, stateOpts.animation),
              fill;

          if (!point.isNull && point.visible !== false) {
            if (graphic) {
              graphic.rect[verb](shapeArgs);
            } else {
              point.graphic = graphic = renderer.g('point').addClass(point.getClassName()).add(point.group || series.group);
              graphic.rect = renderer[type](merge(shapeArgs)).addClass(point.getClassName()).addClass('highcharts-partfill-original').add(graphic);
            }

            if (partShapeArgs) {
              if (graphic.partRect) {
                graphic.partRect[verb](merge(partShapeArgs));
                graphic.partialClipRect[verb](merge(clipRectArgs));
              } else {
                graphic.partialClipRect = renderer.clipRect(clipRectArgs.x, clipRectArgs.y, clipRectArgs.width, clipRectArgs.height);
                graphic.partRect = renderer[type](partShapeArgs).addClass('highcharts-partfill-overlay').add(graphic).clip(graphic.partialClipRect);
              }
            }

            if (!series.chart.styledMode) {
              graphic.rect[verb](pointAttr, animation).shadow(seriesOpts.shadow, null, cutOff);

              if (partShapeArgs) {
                if (!isObject(pfOptions)) {
                  pfOptions = {};
                }

                if (isObject(seriesOpts.partialFill)) {
                  pfOptions = merge(pfOptions, seriesOpts.partialFill);
                }

                fill = pfOptions.fill || color(pointAttr.fill).brighten(-0.3).get() || color(point.color || series.color).brighten(-0.3).get();
                pointAttr.fill = fill;
                graphic.partRect[pointStateVerb](pointAttr, animation).shadow(seriesOpts.shadow, null, cutOff);
              }
            }
          } else if (graphic) {
            point.graphic = graphic.destroy();
          }
        },
        drawPoints: function drawPoints() {
          var series = this,
              verb = series.getAnimationVerb();
          series.points.forEach(function (point) {
            series.drawPoint(point, verb);
          });
        },
        getAnimationVerb: function getAnimationVerb() {
          return this.chart.pointCount < (this.options.animationLimit || 250) ? 'animate' : 'attr';
        }
      }, {
        resolveColor: function resolveColor() {
          var series = this.series,
              colorByPoint;

          if (series.options.colorByPoint && !this.options.color) {
            colorByPoint = getColorByCategory(series, this);

            if (!series.chart.styledMode) {
              this.color = colorByPoint.color;
            }

            if (!this.options.colorIndex) {
              this.colorIndex = colorByPoint.colorIndex;
            }
          } else if (!this.color) {
            this.color = series.color;
          }
        },
        init: function init() {
          Point.prototype.init.apply(this, arguments);

          if (!this.y) {
            this.y = 0;
          }

          return this;
        },
        setState: function setState() {
          Point.prototype.setState.apply(this, arguments);
          this.series.drawPoint(this, this.series.getAnimationVerb());
        },
        getLabelConfig: function getLabelConfig() {
          var point = this,
              cfg = Point.prototype.getLabelConfig.call(point),
              yCats = point.series.yAxis.categories;
          cfg.x2 = point.x2;
          cfg.yCategory = point.yCategory = yCats && yCats[point.y];
          return cfg;
        },
        tooltipDateKeys: ['x', 'x2'],
        isValid: function isValid() {
          return typeof this.x === 'number' && typeof this.x2 === 'number';
        }
      });
      addEvent(Axis, 'afterGetSeriesExtremes', function () {
        var axis = this,
            axisSeries = axis.series,
            dataMax,
            modMax;

        if (axis.isXAxis) {
          dataMax = pick(axis.dataMax, -Number.MAX_VALUE);
          axisSeries.forEach(function (series) {
            if (series.x2Data) {
              series.x2Data.forEach(function (val) {
                if (val > dataMax) {
                  dataMax = val;
                  modMax = true;
                }
              });
            }
          });

          if (modMax) {
            axis.dataMax = dataMax;
          }
        }
      });
    });

    _registerModule(_modules, 'parts-gantt/GanttSeries.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var isNumber = U.isNumber,
          merge = U.merge,
          pick = U.pick,
          seriesType = U.seriesType,
          splat = U.splat;
      var dateFormat = H.dateFormat,
          seriesTypes = H.seriesTypes,
          Series = H.Series,
          parent = seriesTypes.xrange;
      seriesType('gantt', 'xrange', {
        grouping: false,
        dataLabels: {
          enabled: true
        },
        tooltip: {
          headerFormat: '<span style="font-size: 10px">{series.name}</span><br/>',
          pointFormat: null,
          pointFormatter: function pointFormatter() {
            var point = this,
                series = point.series,
                tooltip = series.chart.tooltip,
                xAxis = series.xAxis,
                formats = series.tooltipOptions.dateTimeLabelFormats,
                startOfWeek = xAxis.options.startOfWeek,
                ttOptions = series.tooltipOptions,
                format = ttOptions.xDateFormat,
                start,
                end,
                milestone = point.options.milestone,
                retVal = '<b>' + (point.name || point.yCategory) + '</b>';

            if (ttOptions.pointFormat) {
              return point.tooltipFormatter(ttOptions.pointFormat);
            }

            if (!format) {
              format = splat(tooltip.getDateFormat(xAxis.closestPointRange, point.start, startOfWeek, formats))[0];
            }

            start = dateFormat(format, point.start);
            end = dateFormat(format, point.end);
            retVal += '<br/>';

            if (!milestone) {
              retVal += 'Start: ' + start + '<br/>';
              retVal += 'End: ' + end + '<br/>';
            } else {
              retVal += start + '<br/>';
            }

            return retVal;
          }
        },
        connectors: {
          type: 'simpleConnect',
          animation: {
            reversed: true
          },
          startMarker: {
            enabled: true,
            symbol: 'arrow-filled',
            radius: 4,
            fill: '#fa0',
            align: 'left'
          },
          endMarker: {
            enabled: false,
            align: 'right'
          }
        }
      }, {
        pointArrayMap: ['start', 'end', 'y'],
        keyboardMoveVertical: false,
        translatePoint: function translatePoint(point) {
          var series = this,
              shapeArgs,
              size;
          parent.prototype.translatePoint.call(series, point);

          if (point.options.milestone) {
            shapeArgs = point.shapeArgs;
            size = shapeArgs.height;
            point.shapeArgs = {
              x: shapeArgs.x - size / 2,
              y: shapeArgs.y,
              width: size,
              height: size
            };
          }
        },
        drawPoint: function drawPoint(point, verb) {
          var series = this,
              seriesOpts = series.options,
              renderer = series.chart.renderer,
              shapeArgs = point.shapeArgs,
              plotY = point.plotY,
              graphic = point.graphic,
              state = point.selected && 'select',
              cutOff = seriesOpts.stacking && !seriesOpts.borderRadius,
              diamondShape;

          if (point.options.milestone) {
            if (isNumber(plotY) && point.y !== null && point.visible !== false) {
              diamondShape = renderer.symbols.diamond(shapeArgs.x, shapeArgs.y, shapeArgs.width, shapeArgs.height);

              if (graphic) {
                graphic[verb]({
                  d: diamondShape
                });
              } else {
                point.graphic = graphic = renderer.path(diamondShape).addClass(point.getClassName(), true).add(point.group || series.group);
              }

              if (!series.chart.styledMode) {
                point.graphic.attr(series.pointAttribs(point, state)).shadow(seriesOpts.shadow, null, cutOff);
              }
            } else if (graphic) {
              point.graphic = graphic.destroy();
            }
          } else {
            parent.prototype.drawPoint.call(series, point, verb);
          }
        },
        setData: Series.prototype.setData,
        setGanttPointAliases: function setGanttPointAliases(options) {
          function addIfExists(prop, val) {
            if (typeof val !== 'undefined') {
              options[prop] = val;
            }
          }

          addIfExists('x', pick(options.start, options.x));
          addIfExists('x2', pick(options.end, options.x2));
          addIfExists('partialFill', pick(options.completed, options.partialFill));
          addIfExists('connect', pick(options.dependency, options.connect));
        }
      }, merge(parent.prototype.pointClass.prototype, {
        applyOptions: function applyOptions(options, x) {
          var point = this,
              retVal = merge(options);
          H.seriesTypes.gantt.prototype.setGanttPointAliases(retVal);
          retVal = parent.prototype.pointClass.prototype.applyOptions.call(point, retVal, x);
          return retVal;
        },
        isValid: function isValid() {
          return (typeof this.start === 'number' || typeof this.x === 'number') && (typeof this.end === 'number' || typeof this.x2 === 'number' || this.milestone);
        }
      }));
    });

    _registerModule(_modules, 'parts-gantt/GanttChart.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var isArray = U.isArray,
          merge = U.merge,
          splat = U.splat;
      var Chart = H.Chart;

      H.ganttChart = function (renderTo, options, callback) {
        var hasRenderToArg = typeof renderTo === 'string' || renderTo.nodeName,
            seriesOptions = options.series,
            defaultOptions = H.getOptions(),
            defaultLinkedTo,
            userOptions = options;
        options = arguments[hasRenderToArg ? 1 : 0];

        if (!isArray(options.xAxis)) {
          options.xAxis = [options.xAxis || {}, {}];
        }

        options.xAxis = options.xAxis.map(function (xAxisOptions, i) {
          if (i === 1) {
            defaultLinkedTo = 0;
          }

          return merge(defaultOptions.xAxis, {
            grid: {
              enabled: true
            },
            opposite: true,
            linkedTo: defaultLinkedTo
          }, xAxisOptions, {
            type: 'datetime'
          });
        });
        options.yAxis = splat(options.yAxis || {}).map(function (yAxisOptions) {
          return merge(defaultOptions.yAxis, {
            grid: {
              enabled: true
            },
            staticScale: 50,
            reversed: true,
            type: yAxisOptions.categories ? yAxisOptions.type : 'treegrid'
          }, yAxisOptions);
        });
        options.series = null;
        options = merge(true, {
          chart: {
            type: 'gantt'
          },
          title: {
            text: null
          },
          legend: {
            enabled: false
          }
        }, options, {
          isGantt: true
        });
        options.series = userOptions.series = seriesOptions;
        options.series.forEach(function (series) {
          series.data.forEach(function (point) {
            H.seriesTypes.gantt.prototype.setGanttPointAliases(point);
          });
        });
        return hasRenderToArg ? new Chart(renderTo, options, callback) : new Chart(options, options);
      };
    });

    _registerModule(_modules, 'parts/Scrollbar.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var addEvent = U.addEvent,
          correctFloat = U.correctFloat,
          defined = U.defined,
          destroyObjectProperties = U.destroyObjectProperties,
          fireEvent = U.fireEvent,
          merge = U.merge,
          pick = U.pick,
          removeEvent = U.removeEvent;
      var Axis = H.Axis,
          defaultOptions = H.defaultOptions,
          hasTouch = H.hasTouch,
          isTouchDevice = H.isTouchDevice,
          swapXY;
      var defaultScrollbarOptions = {
        height: isTouchDevice ? 20 : 14,
        barBorderRadius: 0,
        buttonBorderRadius: 0,
        liveRedraw: void 0,
        margin: 10,
        minWidth: 6,
        step: 0.2,
        zIndex: 3,
        barBackgroundColor: '#cccccc',
        barBorderWidth: 1,
        barBorderColor: '#cccccc',
        buttonArrowColor: '#333333',
        buttonBackgroundColor: '#e6e6e6',
        buttonBorderColor: '#cccccc',
        buttonBorderWidth: 1,
        rifleColor: '#333333',
        trackBackgroundColor: '#f2f2f2',
        trackBorderColor: '#f2f2f2',
        trackBorderWidth: 1
      };
      defaultOptions.scrollbar = merge(true, defaultScrollbarOptions, defaultOptions.scrollbar);

      H.swapXY = swapXY = function swapXY(path, vertical) {
        var i,
            len = path.length,
            temp;

        if (vertical) {
          for (i = 0; i < len; i += 3) {
            temp = path[i + 1];
            path[i + 1] = path[i + 2];
            path[i + 2] = temp;
          }
        }

        return path;
      };

      var Scrollbar = function () {
        function Scrollbar(renderer, options, chart) {
          this._events = [];
          this.chartX = 0;
          this.chartY = 0;
          this.from = 0;
          this.group = void 0;
          this.scrollbar = void 0;
          this.scrollbarButtons = [];
          this.scrollbarGroup = void 0;
          this.scrollbarLeft = 0;
          this.scrollbarRifles = void 0;
          this.scrollbarStrokeWidth = 1;
          this.scrollbarTop = 0;
          this.size = 0;
          this.to = 0;
          this.track = void 0;
          this.trackBorderWidth = 1;
          this.userOptions = {};
          this.x = 0;
          this.y = 0;
          this.chart = chart;
          this.options = options;
          this.renderer = chart.renderer;
          this.init(renderer, options, chart);
        }

        Scrollbar.prototype.addEvents = function () {
          var buttonsOrder = this.options.inverted ? [1, 0] : [0, 1],
              buttons = this.scrollbarButtons,
              bar = this.scrollbarGroup.element,
              track = this.track.element,
              mouseDownHandler = this.mouseDownHandler.bind(this),
              mouseMoveHandler = this.mouseMoveHandler.bind(this),
              mouseUpHandler = this.mouseUpHandler.bind(this),
              _events;

          _events = [[buttons[buttonsOrder[0]].element, 'click', this.buttonToMinClick.bind(this)], [buttons[buttonsOrder[1]].element, 'click', this.buttonToMaxClick.bind(this)], [track, 'click', this.trackClick.bind(this)], [bar, 'mousedown', mouseDownHandler], [bar.ownerDocument, 'mousemove', mouseMoveHandler], [bar.ownerDocument, 'mouseup', mouseUpHandler]];

          if (hasTouch) {
            _events.push([bar, 'touchstart', mouseDownHandler], [bar.ownerDocument, 'touchmove', mouseMoveHandler], [bar.ownerDocument, 'touchend', mouseUpHandler]);
          }

          _events.forEach(function (args) {
            addEvent.apply(null, args);
          });

          this._events = _events;
        };

        Scrollbar.prototype.buttonToMaxClick = function (e) {
          var scroller = this;
          var range = (scroller.to - scroller.from) * pick(scroller.options.step, 0.2);
          scroller.updatePosition(scroller.from + range, scroller.to + range);
          fireEvent(scroller, 'changed', {
            from: scroller.from,
            to: scroller.to,
            trigger: 'scrollbar',
            DOMEvent: e
          });
        };

        Scrollbar.prototype.buttonToMinClick = function (e) {
          var scroller = this;
          var range = correctFloat(scroller.to - scroller.from) * pick(scroller.options.step, 0.2);
          scroller.updatePosition(correctFloat(scroller.from - range), correctFloat(scroller.to - range));
          fireEvent(scroller, 'changed', {
            from: scroller.from,
            to: scroller.to,
            trigger: 'scrollbar',
            DOMEvent: e
          });
        };

        Scrollbar.prototype.cursorToScrollbarPosition = function (normalizedEvent) {
          var scroller = this,
              options = scroller.options,
              minWidthDifference = options.minWidth > scroller.calculatedWidth ? options.minWidth : 0;
          return {
            chartX: (normalizedEvent.chartX - scroller.x - scroller.xOffset) / (scroller.barWidth - minWidthDifference),
            chartY: (normalizedEvent.chartY - scroller.y - scroller.yOffset) / (scroller.barWidth - minWidthDifference)
          };
        };

        Scrollbar.prototype.destroy = function () {
          var scroller = this.chart.scroller;
          this.removeEvents();
          ['track', 'scrollbarRifles', 'scrollbar', 'scrollbarGroup', 'group'].forEach(function (prop) {
            if (this[prop] && this[prop].destroy) {
              this[prop] = this[prop].destroy();
            }
          }, this);

          if (scroller && this === scroller.scrollbar) {
            scroller.scrollbar = null;
            destroyObjectProperties(scroller.scrollbarButtons);
          }
        };

        Scrollbar.prototype.drawScrollbarButton = function (index) {
          var scroller = this,
              renderer = scroller.renderer,
              scrollbarButtons = scroller.scrollbarButtons,
              options = scroller.options,
              size = scroller.size,
              group,
              tempElem;
          group = renderer.g().add(scroller.group);
          scrollbarButtons.push(group);
          tempElem = renderer.rect().addClass('highcharts-scrollbar-button').add(group);

          if (!this.chart.styledMode) {
            tempElem.attr({
              stroke: options.buttonBorderColor,
              'stroke-width': options.buttonBorderWidth,
              fill: options.buttonBackgroundColor
            });
          }

          tempElem.attr(tempElem.crisp({
            x: -0.5,
            y: -0.5,
            width: size + 1,
            height: size + 1,
            r: options.buttonBorderRadius
          }, tempElem.strokeWidth()));
          tempElem = renderer.path(swapXY(['M', size / 2 + (index ? -1 : 1), size / 2 - 3, 'L', size / 2 + (index ? -1 : 1), size / 2 + 3, 'L', size / 2 + (index ? 2 : -2), size / 2], options.vertical)).addClass('highcharts-scrollbar-arrow').add(scrollbarButtons[index]);

          if (!this.chart.styledMode) {
            tempElem.attr({
              fill: options.buttonArrowColor
            });
          }
        };

        Scrollbar.prototype.init = function (renderer, options, chart) {
          this.scrollbarButtons = [];
          this.renderer = renderer;
          this.userOptions = options;
          this.options = merge(defaultScrollbarOptions, options);
          this.chart = chart;
          this.size = pick(this.options.size, this.options.height);

          if (options.enabled) {
            this.render();
            this.addEvents();
          }
        };

        Scrollbar.prototype.mouseDownHandler = function (e) {
          var scroller = this;
          var normalizedEvent = scroller.chart.pointer.normalize(e),
              mousePosition = scroller.cursorToScrollbarPosition(normalizedEvent);
          scroller.chartX = mousePosition.chartX;
          scroller.chartY = mousePosition.chartY;
          scroller.initPositions = [scroller.from, scroller.to];
          scroller.grabbedCenter = true;
        };

        Scrollbar.prototype.mouseMoveHandler = function (e) {
          var scroller = this;
          var normalizedEvent = scroller.chart.pointer.normalize(e),
              options = scroller.options,
              direction = options.vertical ? 'chartY' : 'chartX',
              initPositions = scroller.initPositions || [],
              scrollPosition,
              chartPosition,
              change;

          if (scroller.grabbedCenter && (!e.touches || e.touches[0][direction] !== 0)) {
            chartPosition = scroller.cursorToScrollbarPosition(normalizedEvent)[direction];
            scrollPosition = scroller[direction];
            change = chartPosition - scrollPosition;
            scroller.hasDragged = true;
            scroller.updatePosition(initPositions[0] + change, initPositions[1] + change);

            if (scroller.hasDragged) {
              fireEvent(scroller, 'changed', {
                from: scroller.from,
                to: scroller.to,
                trigger: 'scrollbar',
                DOMType: e.type,
                DOMEvent: e
              });
            }
          }
        };

        Scrollbar.prototype.mouseUpHandler = function (e) {
          var scroller = this;

          if (scroller.hasDragged) {
            fireEvent(scroller, 'changed', {
              from: scroller.from,
              to: scroller.to,
              trigger: 'scrollbar',
              DOMType: e.type,
              DOMEvent: e
            });
          }

          scroller.grabbedCenter = scroller.hasDragged = scroller.chartX = scroller.chartY = null;
        };

        Scrollbar.prototype.position = function (x, y, width, height) {
          var scroller = this,
              options = scroller.options,
              vertical = options.vertical,
              xOffset = height,
              yOffset = 0,
              method = scroller.rendered ? 'animate' : 'attr';
          scroller.x = x;
          scroller.y = y + this.trackBorderWidth;
          scroller.width = width;
          scroller.height = height;
          scroller.xOffset = xOffset;
          scroller.yOffset = yOffset;

          if (vertical) {
            scroller.width = scroller.yOffset = width = yOffset = scroller.size;
            scroller.xOffset = xOffset = 0;
            scroller.barWidth = height - width * 2;
            scroller.x = x = x + scroller.options.margin;
          } else {
            scroller.height = scroller.xOffset = height = xOffset = scroller.size;
            scroller.barWidth = width - height * 2;
            scroller.y = scroller.y + scroller.options.margin;
          }

          scroller.group[method]({
            translateX: x,
            translateY: scroller.y
          });
          scroller.track[method]({
            width: width,
            height: height
          });
          scroller.scrollbarButtons[1][method]({
            translateX: vertical ? 0 : width - xOffset,
            translateY: vertical ? height - yOffset : 0
          });
        };

        Scrollbar.prototype.removeEvents = function () {
          this._events.forEach(function (args) {
            removeEvent.apply(null, args);
          });

          this._events.length = 0;
        };

        Scrollbar.prototype.render = function () {
          var scroller = this,
              renderer = scroller.renderer,
              options = scroller.options,
              size = scroller.size,
              styledMode = this.chart.styledMode,
              group;
          scroller.group = group = renderer.g('scrollbar').attr({
            zIndex: options.zIndex,
            translateY: -99999
          }).add();
          scroller.track = renderer.rect().addClass('highcharts-scrollbar-track').attr({
            x: 0,
            r: options.trackBorderRadius || 0,
            height: size,
            width: size
          }).add(group);

          if (!styledMode) {
            scroller.track.attr({
              fill: options.trackBackgroundColor,
              stroke: options.trackBorderColor,
              'stroke-width': options.trackBorderWidth
            });
          }

          this.trackBorderWidth = scroller.track.strokeWidth();
          scroller.track.attr({
            y: -this.trackBorderWidth % 2 / 2
          });
          scroller.scrollbarGroup = renderer.g().add(group);
          scroller.scrollbar = renderer.rect().addClass('highcharts-scrollbar-thumb').attr({
            height: size,
            width: size,
            r: options.barBorderRadius || 0
          }).add(scroller.scrollbarGroup);
          scroller.scrollbarRifles = renderer.path(swapXY(['M', -3, size / 4, 'L', -3, 2 * size / 3, 'M', 0, size / 4, 'L', 0, 2 * size / 3, 'M', 3, size / 4, 'L', 3, 2 * size / 3], options.vertical)).addClass('highcharts-scrollbar-rifles').add(scroller.scrollbarGroup);

          if (!styledMode) {
            scroller.scrollbar.attr({
              fill: options.barBackgroundColor,
              stroke: options.barBorderColor,
              'stroke-width': options.barBorderWidth
            });
            scroller.scrollbarRifles.attr({
              stroke: options.rifleColor,
              'stroke-width': 1
            });
          }

          scroller.scrollbarStrokeWidth = scroller.scrollbar.strokeWidth();
          scroller.scrollbarGroup.translate(-scroller.scrollbarStrokeWidth % 2 / 2, -scroller.scrollbarStrokeWidth % 2 / 2);
          scroller.drawScrollbarButton(0);
          scroller.drawScrollbarButton(1);
        };

        Scrollbar.prototype.setRange = function (from, to) {
          var scroller = this,
              options = scroller.options,
              vertical = options.vertical,
              minWidth = options.minWidth,
              fullWidth = scroller.barWidth,
              fromPX,
              toPX,
              newPos,
              newSize,
              newRiflesPos,
              method = this.rendered && !this.hasDragged && !(this.chart.navigator && this.chart.navigator.hasDragged) ? 'animate' : 'attr';

          if (!defined(fullWidth)) {
            return;
          }

          from = Math.max(from, 0);
          fromPX = Math.ceil(fullWidth * from);
          toPX = fullWidth * Math.min(to, 1);
          scroller.calculatedWidth = newSize = correctFloat(toPX - fromPX);

          if (newSize < minWidth) {
            fromPX = (fullWidth - minWidth + newSize) * from;
            newSize = minWidth;
          }

          newPos = Math.floor(fromPX + scroller.xOffset + scroller.yOffset);
          newRiflesPos = newSize / 2 - 0.5;
          scroller.from = from;
          scroller.to = to;

          if (!vertical) {
            scroller.scrollbarGroup[method]({
              translateX: newPos
            });
            scroller.scrollbar[method]({
              width: newSize
            });
            scroller.scrollbarRifles[method]({
              translateX: newRiflesPos
            });
            scroller.scrollbarLeft = newPos;
            scroller.scrollbarTop = 0;
          } else {
            scroller.scrollbarGroup[method]({
              translateY: newPos
            });
            scroller.scrollbar[method]({
              height: newSize
            });
            scroller.scrollbarRifles[method]({
              translateY: newRiflesPos
            });
            scroller.scrollbarTop = newPos;
            scroller.scrollbarLeft = 0;
          }

          if (newSize <= 12) {
            scroller.scrollbarRifles.hide();
          } else {
            scroller.scrollbarRifles.show(true);
          }

          if (options.showFull === false) {
            if (from <= 0 && to >= 1) {
              scroller.group.hide();
            } else {
              scroller.group.show();
            }
          }

          scroller.rendered = true;
        };

        Scrollbar.prototype.trackClick = function (e) {
          var scroller = this;
          var normalizedEvent = scroller.chart.pointer.normalize(e),
              range = scroller.to - scroller.from,
              top = scroller.y + scroller.scrollbarTop,
              left = scroller.x + scroller.scrollbarLeft;

          if (scroller.options.vertical && normalizedEvent.chartY > top || !scroller.options.vertical && normalizedEvent.chartX > left) {
            scroller.updatePosition(scroller.from + range, scroller.to + range);
          } else {
            scroller.updatePosition(scroller.from - range, scroller.to - range);
          }

          fireEvent(scroller, 'changed', {
            from: scroller.from,
            to: scroller.to,
            trigger: 'scrollbar',
            DOMEvent: e
          });
        };

        Scrollbar.prototype.update = function (options) {
          this.destroy();
          this.init(this.chart.renderer, merge(true, this.options, options), this.chart);
        };

        Scrollbar.prototype.updatePosition = function (from, to) {
          if (to > 1) {
            from = correctFloat(1 - correctFloat(to - from));
            to = 1;
          }

          if (from < 0) {
            to = correctFloat(to - from);
            from = 0;
          }

          this.from = from;
          this.to = to;
        };

        return Scrollbar;
      }();

      if (!H.Scrollbar) {
        addEvent(Axis, 'afterInit', function () {
          var axis = this;

          if (axis.options && axis.options.scrollbar && axis.options.scrollbar.enabled) {
            axis.options.scrollbar.vertical = !axis.horiz;
            axis.options.startOnTick = axis.options.endOnTick = false;
            axis.scrollbar = new Scrollbar(axis.chart.renderer, axis.options.scrollbar, axis.chart);
            addEvent(axis.scrollbar, 'changed', function (e) {
              var unitedMin = Math.min(pick(axis.options.min, axis.min), axis.min, axis.dataMin),
                  unitedMax = Math.max(pick(axis.options.max, axis.max), axis.max, axis.dataMax),
                  range = unitedMax - unitedMin,
                  to,
                  from;

              if (axis.horiz && !axis.reversed || !axis.horiz && axis.reversed) {
                to = unitedMin + range * this.to;
                from = unitedMin + range * this.from;
              } else {
                to = unitedMin + range * (1 - this.from);
                from = unitedMin + range * (1 - this.to);
              }

              if (pick(this.options.liveRedraw, H.svg && !H.isTouchDevice && !this.chart.isBoosting) || e.DOMType === 'mouseup' || !defined(e.DOMType)) {
                axis.setExtremes(from, to, true, e.DOMType !== 'mousemove', e);
              } else {
                this.setRange(this.from, this.to);
              }
            });
          }
        });
        addEvent(Axis, 'afterRender', function () {
          var axis = this,
              scrollMin = Math.min(pick(axis.options.min, axis.min), axis.min, pick(axis.dataMin, axis.min)),
              scrollMax = Math.max(pick(axis.options.max, axis.max), axis.max, pick(axis.dataMax, axis.max)),
              scrollbar = axis.scrollbar,
              offset = axis.axisTitleMargin + (axis.titleOffset || 0),
              scrollbarsOffsets = axis.chart.scrollbarsOffsets,
              axisMargin = axis.options.margin || 0,
              offsetsIndex,
              from,
              to;

          if (scrollbar) {
            if (axis.horiz) {
              if (!axis.opposite) {
                scrollbarsOffsets[1] += offset;
              }

              scrollbar.position(axis.left, axis.top + axis.height + 2 + scrollbarsOffsets[1] - (axis.opposite ? axisMargin : 0), axis.width, axis.height);

              if (!axis.opposite) {
                scrollbarsOffsets[1] += axisMargin;
              }

              offsetsIndex = 1;
            } else {
              if (axis.opposite) {
                scrollbarsOffsets[0] += offset;
              }

              scrollbar.position(axis.left + axis.width + 2 + scrollbarsOffsets[0] - (axis.opposite ? 0 : axisMargin), axis.top, axis.width, axis.height);

              if (axis.opposite) {
                scrollbarsOffsets[0] += axisMargin;
              }

              offsetsIndex = 0;
            }

            scrollbarsOffsets[offsetsIndex] += scrollbar.size + scrollbar.options.margin;

            if (isNaN(scrollMin) || isNaN(scrollMax) || !defined(axis.min) || !defined(axis.max) || axis.min === axis.max) {
                scrollbar.setRange(0, 1);
              } else {
              from = (axis.min - scrollMin) / (scrollMax - scrollMin);
              to = (axis.max - scrollMin) / (scrollMax - scrollMin);

              if (axis.horiz && !axis.reversed || !axis.horiz && axis.reversed) {
                scrollbar.setRange(from, to);
              } else {
                scrollbar.setRange(1 - to, 1 - from);
              }
            }
          }
        });
        addEvent(Axis, 'afterGetOffset', function () {
          var axis = this,
              index = axis.horiz ? 2 : 1,
              scrollbar = axis.scrollbar;

          if (scrollbar) {
            axis.chart.scrollbarsOffsets = [0, 0];
            axis.chart.axisOffset[index] += scrollbar.size + scrollbar.options.margin;
          }
        });
        H.Scrollbar = Scrollbar;
      }

      return H.Scrollbar;
    });

    _registerModule(_modules, 'parts/RangeSelector.js', [_modules['parts/Globals.js'], _modules['parts/Utilities.js']], function (H, U) {
      var addEvent = U.addEvent,
          createElement = U.createElement,
          css = U.css,
          defined = U.defined,
          destroyObjectProperties = U.destroyObjectProperties,
          discardElement = U.discardElement,
          extend = U.extend,
          fireEvent = U.fireEvent,
          isNumber = U.isNumber,
          merge = U.merge,
          objectEach = U.objectEach,
          pick = U.pick,
          pInt = U.pInt,
          splat = U.splat;
      var Axis = H.Axis,
          Chart = H.Chart,
          defaultOptions = H.defaultOptions;
      extend(defaultOptions, {
        rangeSelector: {
          verticalAlign: 'top',
          buttonTheme: {
            width: 28,
            height: 18,
            padding: 2,
            zIndex: 7
          },
          floating: false,
          x: 0,
          y: 0,
          height: void 0,
          inputPosition: {
            align: 'right',
            x: 0,
            y: 0
          },
          buttonPosition: {
            align: 'left',
            x: 0,
            y: 0
          },
          labelStyle: {
            color: '#666666'
          }
        }
      });
      defaultOptions.lang = merge(defaultOptions.lang, {
        rangeSelectorZoom: 'Zoom',
        rangeSelectorFrom: 'From',
        rangeSelectorTo: 'To'
      });

      function RangeSelector(chart) {
        this.init(chart);
      }

      RangeSelector.prototype = {
        clickButton: function clickButton(i, redraw) {
          var rangeSelector = this,
              chart = rangeSelector.chart,
              rangeOptions = rangeSelector.buttonOptions[i],
              baseAxis = chart.xAxis[0],
              unionExtremes = chart.scroller && chart.scroller.getUnionExtremes() || baseAxis || {},
              dataMin = unionExtremes.dataMin,
              dataMax = unionExtremes.dataMax,
              newMin,
              newMax = baseAxis && Math.round(Math.min(baseAxis.max, pick(dataMax, baseAxis.max))),
              type = rangeOptions.type,
              baseXAxisOptions,
              range = rangeOptions._range,
              rangeMin,
              minSetting,
              rangeSetting,
              ctx,
              ytdExtremes,
              dataGrouping = rangeOptions.dataGrouping;

          if (dataMin === null || dataMax === null) {
            return;
          }

          chart.fixedRange = range;

          if (dataGrouping) {
            this.forcedDataGrouping = true;
            Axis.prototype.setDataGrouping.call(baseAxis || {
              chart: this.chart
            }, dataGrouping, false);
            this.frozenStates = rangeOptions.preserveDataGrouping;
          }

          if (type === 'month' || type === 'year') {
            if (!baseAxis) {
              range = rangeOptions;
            } else {
              ctx = {
                range: rangeOptions,
                max: newMax,
                chart: chart,
                dataMin: dataMin,
                dataMax: dataMax
              };
              newMin = baseAxis.minFromRange.call(ctx);

              if (isNumber(ctx.newMax)) {
                newMax = ctx.newMax;
              }
            }
          } else if (range) {
            newMin = Math.max(newMax - range, dataMin);
            newMax = Math.min(newMin + range, dataMax);
          } else if (type === 'ytd') {
            if (baseAxis) {
              if (typeof dataMax === 'undefined') {
                dataMin = Number.MAX_VALUE;
                dataMax = Number.MIN_VALUE;
                chart.series.forEach(function (series) {
                  var xData = series.xData;
                  dataMin = Math.min(xData[0], dataMin);
                  dataMax = Math.max(xData[xData.length - 1], dataMax);
                });
                redraw = false;
              }

              ytdExtremes = rangeSelector.getYTDExtremes(dataMax, dataMin, chart.time.useUTC);
              newMin = rangeMin = ytdExtremes.min;
              newMax = ytdExtremes.max;
            } else {
              rangeSelector.deferredYTDClick = i;
              return;
            }
          } else if (type === 'all' && baseAxis) {
            newMin = dataMin;
            newMax = dataMax;
          }

          newMin += rangeOptions._offsetMin;
          newMax += rangeOptions._offsetMax;
          rangeSelector.setSelected(i);

          if (!baseAxis) {
            baseXAxisOptions = splat(chart.options.xAxis)[0];
            rangeSetting = baseXAxisOptions.range;
            baseXAxisOptions.range = range;
            minSetting = baseXAxisOptions.min;
            baseXAxisOptions.min = rangeMin;
            addEvent(chart, 'load', function resetMinAndRange() {
              baseXAxisOptions.range = rangeSetting;
              baseXAxisOptions.min = minSetting;
            });
          } else {
            baseAxis.setExtremes(newMin, newMax, pick(redraw, 1), null, {
              trigger: 'rangeSelectorButton',
              rangeSelectorButton: rangeOptions
            });
          }
        },
        setSelected: function setSelected(selected) {
          this.selected = this.options.selected = selected;
        },
        defaultButtons: [{
          type: 'month',
          count: 1,
          text: '1m'
        }, {
          type: 'month',
          count: 3,
          text: '3m'
        }, {
          type: 'month',
          count: 6,
          text: '6m'
        }, {
          type: 'ytd',
          text: 'YTD'
        }, {
          type: 'year',
          count: 1,
          text: '1y'
        }, {
          type: 'all',
          text: 'All'
        }],
        init: function init(chart) {
          var rangeSelector = this,
              options = chart.options.rangeSelector,
              buttonOptions = options.buttons || [].concat(rangeSelector.defaultButtons),
              selectedOption = options.selected,
              blurInputs = function blurInputs() {
            var minInput = rangeSelector.minInput,
                maxInput = rangeSelector.maxInput;

            if (minInput && minInput.blur) {
              fireEvent(minInput, 'blur');
            }

            if (maxInput && maxInput.blur) {
              fireEvent(maxInput, 'blur');
            }
          };

          rangeSelector.chart = chart;
          rangeSelector.options = options;
          rangeSelector.buttons = [];
          rangeSelector.buttonOptions = buttonOptions;
          this.unMouseDown = addEvent(chart.container, 'mousedown', blurInputs);
          this.unResize = addEvent(chart, 'resize', blurInputs);
          buttonOptions.forEach(rangeSelector.computeButtonRange);

          if (typeof selectedOption !== 'undefined' && buttonOptions[selectedOption]) {
            this.clickButton(selectedOption, false);
          }

          addEvent(chart, 'load', function () {
            if (chart.xAxis && chart.xAxis[0]) {
              addEvent(chart.xAxis[0], 'setExtremes', function (e) {
                if (this.max - this.min !== chart.fixedRange && e.trigger !== 'rangeSelectorButton' && e.trigger !== 'updatedData' && rangeSelector.forcedDataGrouping && !rangeSelector.frozenStates) {
                  this.setDataGrouping(false, false);
                }
              });
            }
          });
        },
        updateButtonStates: function updateButtonStates() {
          var rangeSelector = this,
              chart = this.chart,
              baseAxis = chart.xAxis[0],
              actualRange = Math.round(baseAxis.max - baseAxis.min),
              hasNoData = !baseAxis.hasVisibleSeries,
              day = 24 * 36e5,
              unionExtremes = chart.scroller && chart.scroller.getUnionExtremes() || baseAxis,
              dataMin = unionExtremes.dataMin,
              dataMax = unionExtremes.dataMax,
              ytdExtremes = rangeSelector.getYTDExtremes(dataMax, dataMin, chart.time.useUTC),
              ytdMin = ytdExtremes.min,
              ytdMax = ytdExtremes.max,
              selected = rangeSelector.selected,
              selectedExists = isNumber(selected),
              allButtonsEnabled = rangeSelector.options.allButtonsEnabled,
              buttons = rangeSelector.buttons;
          rangeSelector.buttonOptions.forEach(function (rangeOptions, i) {
            var range = rangeOptions._range,
                type = rangeOptions.type,
                count = rangeOptions.count || 1,
                button = buttons[i],
                state = 0,
                disable,
                select,
                offsetRange = rangeOptions._offsetMax - rangeOptions._offsetMin,
                isSelected = i === selected,
                isTooGreatRange = range > dataMax - dataMin,
                isTooSmallRange = range < baseAxis.minRange,
                isYTDButNotSelected = false,
                isAllButAlreadyShowingAll = false,
                isSameRange = range === actualRange;

            if ((type === 'month' || type === 'year') && actualRange + 36e5 >= {
              month: 28,
              year: 365
            }[type] * day * count - offsetRange && actualRange - 36e5 <= {
              month: 31,
              year: 366
            }[type] * day * count + offsetRange) {
              isSameRange = true;
            } else if (type === 'ytd') {
              isSameRange = ytdMax - ytdMin + offsetRange === actualRange;
              isYTDButNotSelected = !isSelected;
            } else if (type === 'all') {
              isSameRange = baseAxis.max - baseAxis.min >= dataMax - dataMin;
              isAllButAlreadyShowingAll = !isSelected && selectedExists && isSameRange;
            }

            disable = !allButtonsEnabled && (isTooGreatRange || isTooSmallRange || isAllButAlreadyShowingAll || hasNoData);
            select = isSelected && isSameRange || isSameRange && !selectedExists && !isYTDButNotSelected || isSelected && rangeSelector.frozenStates;

            if (disable) {
              state = 3;
            } else if (select) {
              selectedExists = true;
              state = 2;
            }

            if (button.state !== state) {
              button.setState(state);

              if (state === 0 && selected === i) {
                rangeSelector.setSelected(null);
              }
            }
          });
        },
        computeButtonRange: function computeButtonRange(rangeOptions) {
          var type = rangeOptions.type,
              count = rangeOptions.count || 1,
              fixedTimes = {
            millisecond: 1,
            second: 1000,
            minute: 60 * 1000,
            hour: 3600 * 1000,
            day: 24 * 3600 * 1000,
            week: 7 * 24 * 3600 * 1000
          };

          if (fixedTimes[type]) {
            rangeOptions._range = fixedTimes[type] * count;
          } else if (type === 'month' || type === 'year') {
            rangeOptions._range = {
              month: 30,
              year: 365
            }[type] * 24 * 36e5 * count;
          }

          rangeOptions._offsetMin = pick(rangeOptions.offsetMin, 0);
          rangeOptions._offsetMax = pick(rangeOptions.offsetMax, 0);
          rangeOptions._range += rangeOptions._offsetMax - rangeOptions._offsetMin;
        },
        setInputValue: function setInputValue(name, inputTime) {
          var options = this.chart.options.rangeSelector,
              time = this.chart.time,
              input = this[name + 'Input'];

          if (defined(inputTime)) {
            input.previousValue = input.HCTime;
            input.HCTime = inputTime;
          }

          input.value = time.dateFormat(options.inputEditDateFormat || '%Y-%m-%d', input.HCTime);
          this[name + 'DateBox'].attr({
            text: time.dateFormat(options.inputDateFormat || '%b %e, %Y', input.HCTime)
          });
        },
        showInput: function showInput(name) {
          var inputGroup = this.inputGroup,
              dateBox = this[name + 'DateBox'];
          css(this[name + 'Input'], {
            left: inputGroup.translateX + dateBox.x + 'px',
            top: inputGroup.translateY + 'px',
            width: dateBox.width - 2 + 'px',
            height: dateBox.height - 2 + 'px',
            border: '2px solid silver'
          });
        },
        hideInput: function hideInput(name) {
          css(this[name + 'Input'], {
            border: 0,
            width: '1px',
            height: '1px'
          });
          this.setInputValue(name);
        },
        drawInput: function drawInput(name) {
          var rangeSelector = this,
              chart = rangeSelector.chart,
              chartStyle = chart.renderer.style || {},
              renderer = chart.renderer,
              options = chart.options.rangeSelector,
              lang = defaultOptions.lang,
              div = rangeSelector.div,
              isMin = name === 'min',
              input,
              label,
              dateBox,
              inputGroup = this.inputGroup;

          function updateExtremes() {
            var inputValue = input.value,
                value = (options.inputDateParser || Date.parse)(inputValue),
                chartAxis = chart.xAxis[0],
                dataAxis = chart.scroller && chart.scroller.xAxis ? chart.scroller.xAxis : chartAxis,
                dataMin = dataAxis.dataMin,
                dataMax = dataAxis.dataMax;

            if (value !== input.previousValue) {
              input.previousValue = value;

              if (!isNumber(value)) {
                value = inputValue.split('-');
                value = Date.UTC(pInt(value[0]), pInt(value[1]) - 1, pInt(value[2]));
              }

              if (isNumber(value)) {
                if (!chart.time.useUTC) {
                  value = value + new Date().getTimezoneOffset() * 60 * 1000;
                }

                if (isMin) {
                  if (value > rangeSelector.maxInput.HCTime) {
                    value = void 0;
                  } else if (value < dataMin) {
                    value = dataMin;
                  }
                } else {
                  if (value < rangeSelector.minInput.HCTime) {
                    value = void 0;
                  } else if (value > dataMax) {
                    value = dataMax;
                  }
                }

                if (typeof value !== 'undefined') {
                  chartAxis.setExtremes(isMin ? value : chartAxis.min, isMin ? chartAxis.max : value, void 0, void 0, {
                    trigger: 'rangeSelectorInput'
                  });
                }
              }
            }
          }

          this[name + 'Label'] = label = renderer.label(lang[isMin ? 'rangeSelectorFrom' : 'rangeSelectorTo'], this.inputGroup.offset).addClass('highcharts-range-label').attr({
            padding: 2
          }).add(inputGroup);
          inputGroup.offset += label.width + 5;
          this[name + 'DateBox'] = dateBox = renderer.label('', inputGroup.offset).addClass('highcharts-range-input').attr({
            padding: 2,
            width: options.inputBoxWidth || 90,
            height: options.inputBoxHeight || 17,
            'text-align': 'center'
          }).on('click', function () {
            rangeSelector.showInput(name);
            rangeSelector[name + 'Input'].focus();
          });

          if (!chart.styledMode) {
            dateBox.attr({
              stroke: options.inputBoxBorderColor || '#cccccc',
              'stroke-width': 1
            });
          }

          dateBox.add(inputGroup);
          inputGroup.offset += dateBox.width + (isMin ? 10 : 0);
          this[name + 'Input'] = input = createElement('input', {
            name: name,
            className: 'highcharts-range-selector',
            type: 'text'
          }, {
            top: chart.plotTop + 'px'
          }, div);

          if (!chart.styledMode) {
            label.css(merge(chartStyle, options.labelStyle));
            dateBox.css(merge({
              color: '#333333'
            }, chartStyle, options.inputStyle));
            css(input, extend({
              position: 'absolute',
              border: 0,
              width: '1px',
              height: '1px',
              padding: 0,
              textAlign: 'center',
              fontSize: chartStyle.fontSize,
              fontFamily: chartStyle.fontFamily,
              top: '-9999em'
            }, options.inputStyle));
          }

          input.onfocus = function () {
            rangeSelector.showInput(name);
          };

          input.onblur = function () {
            if (input === H.doc.activeElement) {
              updateExtremes();
            }

            rangeSelector.hideInput(name);
            input.blur();
          };

          input.onchange = updateExtremes;

          input.onkeypress = function (event) {
            if (event.keyCode === 13) {
              updateExtremes();
            }
          };
        },
        getPosition: function getPosition() {
          var chart = this.chart,
              options = chart.options.rangeSelector,
              top = options.verticalAlign === 'top' ? chart.plotTop - chart.axisOffset[0] : 0;
          return {
            buttonTop: top + options.buttonPosition.y,
            inputTop: top + options.inputPosition.y - 10
          };
        },
        getYTDExtremes: function getYTDExtremes(dataMax, dataMin, useUTC) {
          var time = this.chart.time,
              min,
              now = new time.Date(dataMax),
              year = time.get('FullYear', now),
              startOfYear = useUTC ? time.Date.UTC(year, 0, 1) : +new time.Date(year, 0, 1);
          min = Math.max(dataMin || 0, startOfYear);
          now = now.getTime();
          return {
            max: Math.min(dataMax || now, now),
            min: min
          };
        },
        render: function render(min, max) {
          var rangeSelector = this,
              chart = rangeSelector.chart,
              renderer = chart.renderer,
              container = chart.container,
              chartOptions = chart.options,
              navButtonOptions = chartOptions.exporting && chartOptions.exporting.enabled !== false && chartOptions.navigation && chartOptions.navigation.buttonOptions,
              lang = defaultOptions.lang,
              div = rangeSelector.div,
              options = chartOptions.rangeSelector,
              inputsZIndex = pick(chartOptions.chart.style && chartOptions.chart.style.zIndex, 0) + 1,
              floating = options.floating,
              buttons = rangeSelector.buttons,
              inputGroup = rangeSelector.inputGroup,
              buttonTheme = options.buttonTheme,
              buttonPosition = options.buttonPosition,
              inputPosition = options.inputPosition,
              inputEnabled = options.inputEnabled,
              states = buttonTheme && buttonTheme.states,
              plotLeft = chart.plotLeft,
              buttonLeft,
              buttonGroup = rangeSelector.buttonGroup,
              group,
              groupHeight,
              rendered = rangeSelector.rendered,
              verticalAlign = rangeSelector.options.verticalAlign,
              legend = chart.legend,
              legendOptions = legend && legend.options,
              buttonPositionY = buttonPosition.y,
              inputPositionY = inputPosition.y,
              animate = chart.hasLoaded,
              verb = animate ? 'animate' : 'attr',
              exportingX = 0,
              alignTranslateY,
              legendHeight,
              minPosition,
              translateY = 0,
              translateX;

          if (options.enabled === false) {
            return;
          }

          if (!rendered) {
            rangeSelector.group = group = renderer.g('range-selector-group').attr({
              zIndex: 7
            }).add();
            rangeSelector.buttonGroup = buttonGroup = renderer.g('range-selector-buttons').add(group);
            rangeSelector.zoomText = renderer.text(lang.rangeSelectorZoom, 0, 15).add(buttonGroup);

            if (!chart.styledMode) {
              rangeSelector.zoomText.css(options.labelStyle);
              buttonTheme['stroke-width'] = pick(buttonTheme['stroke-width'], 0);
            }

            rangeSelector.buttonOptions.forEach(function (rangeOptions, i) {
              buttons[i] = renderer.button(rangeOptions.text, 0, 0, function (e) {
                var buttonEvents = rangeOptions.events && rangeOptions.events.click,
                    callDefaultEvent;

                if (buttonEvents) {
                  callDefaultEvent = buttonEvents.call(rangeOptions, e);
                }

                if (callDefaultEvent !== false) {
                  rangeSelector.clickButton(i);
                }

                rangeSelector.isActive = true;
              }, buttonTheme, states && states.hover, states && states.select, states && states.disabled).attr({
                'text-align': 'center'
              }).add(buttonGroup);
            });

            if (inputEnabled !== false) {
              rangeSelector.div = div = createElement('div', null, {
                position: 'relative',
                height: 0,
                zIndex: inputsZIndex
              });
              container.parentNode.insertBefore(div, container);
              rangeSelector.inputGroup = inputGroup = renderer.g('input-group').add(group);
              inputGroup.offset = 0;
              rangeSelector.drawInput('min');
              rangeSelector.drawInput('max');
            }
          }

          rangeSelector.zoomText[verb]({
            x: pick(plotLeft + buttonPosition.x, plotLeft)
          });
          buttonLeft = pick(plotLeft + buttonPosition.x, plotLeft) + rangeSelector.zoomText.getBBox().width + 5;
          rangeSelector.buttonOptions.forEach(function (rangeOptions, i) {
            buttons[i][verb]({
              x: buttonLeft
            });
            buttonLeft += buttons[i].width + pick(options.buttonSpacing, 5);
          });
          plotLeft = chart.plotLeft - chart.spacing[3];
          rangeSelector.updateButtonStates();

          if (navButtonOptions && this.titleCollision(chart) && verticalAlign === 'top' && buttonPosition.align === 'right' && buttonPosition.y + buttonGroup.getBBox().height - 12 < (navButtonOptions.y || 0) + navButtonOptions.height) {
            exportingX = -40;
          }

          translateX = buttonPosition.x - chart.spacing[3];

          if (buttonPosition.align === 'right') {
            translateX += exportingX - plotLeft;
          } else if (buttonPosition.align === 'center') {
            translateX -= plotLeft / 2;
          }

          buttonGroup.align({
            y: buttonPosition.y,
            width: buttonGroup.getBBox().width,
            align: buttonPosition.align,
            x: translateX
          }, true, chart.spacingBox);
          rangeSelector.group.placed = animate;
          rangeSelector.buttonGroup.placed = animate;

          if (inputEnabled !== false) {
            var inputGroupX, inputGroupWidth, buttonGroupX, buttonGroupWidth;

            if (navButtonOptions && this.titleCollision(chart) && verticalAlign === 'top' && inputPosition.align === 'right' && inputPosition.y - inputGroup.getBBox().height - 12 < (navButtonOptions.y || 0) + navButtonOptions.height + chart.spacing[0]) {
              exportingX = -40;
            } else {
              exportingX = 0;
            }

            if (inputPosition.align === 'left') {
              translateX = plotLeft;
            } else if (inputPosition.align === 'right') {
              translateX = -Math.max(chart.axisOffset[1], -exportingX);
            }

            inputGroup.align({
              y: inputPosition.y,
              width: inputGroup.getBBox().width,
              align: inputPosition.align,
              x: inputPosition.x + translateX - 2
            }, true, chart.spacingBox);
            inputGroupX = inputGroup.alignAttr.translateX + inputGroup.alignOptions.x - exportingX + inputGroup.getBBox().x + 2;
            inputGroupWidth = inputGroup.alignOptions.width;
            buttonGroupX = buttonGroup.alignAttr.translateX + buttonGroup.getBBox().x;
            buttonGroupWidth = buttonGroup.getBBox().width + 20;

            if (inputPosition.align === buttonPosition.align || buttonGroupX + buttonGroupWidth > inputGroupX && inputGroupX + inputGroupWidth > buttonGroupX && buttonPositionY < inputPositionY + inputGroup.getBBox().height) {
              inputGroup.attr({
                translateX: inputGroup.alignAttr.translateX + (chart.axisOffset[1] >= -exportingX ? 0 : -exportingX),
                translateY: inputGroup.alignAttr.translateY + buttonGroup.getBBox().height + 10
              });
            }

            rangeSelector.setInputValue('min', min);
            rangeSelector.setInputValue('max', max);
            rangeSelector.inputGroup.placed = animate;
          }

          rangeSelector.group.align({
            verticalAlign: verticalAlign
          }, true, chart.spacingBox);
          groupHeight = rangeSelector.group.getBBox().height + 20;
          alignTranslateY = rangeSelector.group.alignAttr.translateY;

          if (verticalAlign === 'bottom') {
            legendHeight = legendOptions && legendOptions.verticalAlign === 'bottom' && legendOptions.enabled && !legendOptions.floating ? legend.legendHeight + pick(legendOptions.margin, 10) : 0;
            groupHeight = groupHeight + legendHeight - 20;
            translateY = alignTranslateY - groupHeight - (floating ? 0 : options.y) - (chart.titleOffset ? chart.titleOffset[2] : 0) - 10;
          }

          if (verticalAlign === 'top') {
            if (floating) {
              translateY = 0;
            }

            if (chart.titleOffset && chart.titleOffset[0]) {
              translateY = chart.titleOffset[0];
            }

            translateY += chart.margin[0] - chart.spacing[0] || 0;
          } else if (verticalAlign === 'middle') {
            if (inputPositionY === buttonPositionY) {
              if (inputPositionY < 0) {
                translateY = alignTranslateY + minPosition;
              } else {
                translateY = alignTranslateY;
              }
            } else if (inputPositionY || buttonPositionY) {
              if (inputPositionY < 0 || buttonPositionY < 0) {
                translateY -= Math.min(inputPositionY, buttonPositionY);
              } else {
                translateY = alignTranslateY - groupHeight + minPosition;
              }
            }
          }

          rangeSelector.group.translate(options.x, options.y + Math.floor(translateY));

          if (inputEnabled !== false) {
            rangeSelector.minInput.style.marginTop = rangeSelector.group.translateY + 'px';
            rangeSelector.maxInput.style.marginTop = rangeSelector.group.translateY + 'px';
          }

          rangeSelector.rendered = true;
        },
        getHeight: function getHeight() {
          var rangeSelector = this,
              options = rangeSelector.options,
              rangeSelectorGroup = rangeSelector.group,
              inputPosition = options.inputPosition,
              buttonPosition = options.buttonPosition,
              yPosition = options.y,
              buttonPositionY = buttonPosition.y,
              inputPositionY = inputPosition.y,
              rangeSelectorHeight = 0,
              minPosition;

          if (options.height) {
            return options.height;
          }

          rangeSelectorHeight = rangeSelectorGroup ? rangeSelectorGroup.getBBox(true).height + 13 + yPosition : 0;
          minPosition = Math.min(inputPositionY, buttonPositionY);

          if (inputPositionY < 0 && buttonPositionY < 0 || inputPositionY > 0 && buttonPositionY > 0) {
            rangeSelectorHeight += Math.abs(minPosition);
          }

          return rangeSelectorHeight;
        },
        titleCollision: function titleCollision(chart) {
          return !(chart.options.title.text || chart.options.subtitle.text);
        },
        update: function update(options) {
          var chart = this.chart;
          merge(true, chart.options.rangeSelector, options);
          this.destroy();
          this.init(chart);
          chart.rangeSelector.render();
        },
        destroy: function destroy() {
          var rSelector = this,
              minInput = rSelector.minInput,
              maxInput = rSelector.maxInput;
          rSelector.unMouseDown();
          rSelector.unResize();
          destroyObjectProperties(rSelector.buttons);

          if (minInput) {
            minInput.onfocus = minInput.onblur = minInput.onchange = null;
          }

          if (maxInput) {
            maxInput.onfocus = maxInput.onblur = maxInput.onchange = null;
          }

          objectEach(rSelector, function (val, key) {
            if (val && key !== 'chart') {
              if (val.destroy) {
                val.destroy();
              } else if (val.nodeType) {
                discardElement(this[key]);
              }
            }

            if (val !== RangeSelector.prototype[key]) {
              rSelector[key] = null;
            }
          }, this);
        }
      };

      Axis.prototype.minFromRange = function () {
        var rangeOptions = this.range,
            type = rangeOptions.type,
            min,
            max = this.max,
            dataMin,
            range,
            time = this.chart.time,
            getTrueRange = function getTrueRange(base, count) {
          var timeName = type === 'year' ? 'FullYear' : 'Month';
          var date = new time.Date(base);
          var basePeriod = time.get(timeName, date);
          time.set(timeName, date, basePeriod + count);

          if (basePeriod === time.get(timeName, date)) {
            time.set('Date', date, 0);
          }

          return date.getTime() - base;
        };

        if (isNumber(rangeOptions)) {
          min = max - rangeOptions;
          range = rangeOptions;
        } else {
          min = max + getTrueRange(max, -rangeOptions.count);

          if (this.chart) {
            this.chart.fixedRange = max - min;
          }
        }

        dataMin = pick(this.dataMin, Number.MIN_VALUE);

        if (!isNumber(min)) {
          min = dataMin;
        }

        if (min <= dataMin) {
          min = dataMin;

          if (typeof range === 'undefined') {
            range = getTrueRange(min, rangeOptions.count);
          }

          this.newMax = Math.min(min + range, this.dataMax);
        }

        if (!isNumber(max)) {
          min = void 0;
        }

        return min;
      };

      if (!H.RangeSelector) {
        addEvent(Chart, 'afterGetContainer', function () {
          if (this.options.rangeSelector.enabled) {
            this.rangeSelector = new RangeSelector(this);
          }
        });
        addEvent(Chart, 'beforeRender', function () {
          var chart = this,
              axes = chart.axes,
              rangeSelector = chart.rangeSelector,
              verticalAlign;

          if (rangeSelector) {
            if (isNumber(rangeSelector.deferredYTDClick)) {
              rangeSelector.clickButton(rangeSelector.deferredYTDClick);
              delete rangeSelector.deferredYTDClick;
            }

            axes.forEach(function (axis) {
              axis.updateNames();
              axis.setScale();
            });
            chart.getAxisMargins();
            rangeSelector.render();
            verticalAlign = rangeSelector.options.verticalAlign;

            if (!rangeSelector.options.floating) {
              if (verticalAlign === 'bottom') {
                this.extraBottomMargin = true;
              } else if (verticalAlign !== 'middle') {
                this.extraTopMargin = true;
              }
            }
          }
        });
        addEvent(Chart, 'update', function (e) {
          var chart = this,
              options = e.options,
              optionsRangeSelector = options.rangeSelector,
              rangeSelector = chart.rangeSelector,
              verticalAlign,
              extraBottomMarginWas = this.extraBottomMargin,
              extraTopMarginWas = this.extraTopMargin;

          if (optionsRangeSelector && optionsRangeSelector.enabled && !defined(rangeSelector)) {
            this.options.rangeSelector.enabled = true;
            this.rangeSelector = new RangeSelector(this);
          }

          this.extraBottomMargin = false;
          this.extraTopMargin = false;

          if (rangeSelector) {
            rangeSelector.render();
            verticalAlign = optionsRangeSelector && optionsRangeSelector.verticalAlign || rangeSelector.options && rangeSelector.options.verticalAlign;

            if (!rangeSelector.options.floating) {
              if (verticalAlign === 'bottom') {
                this.extraBottomMargin = true;
              } else if (verticalAlign !== 'middle') {
                this.extraTopMargin = true;
              }
            }

            if (this.extraBottomMargin !== extraBottomMarginWas || this.extraTopMargin !== extraTopMarginWas) {
              this.isDirtyBox = true;
            }
          }
        });
        addEvent(Chart, 'render', function () {
          var chart = this,
              rangeSelector = chart.rangeSelector,
              verticalAlign;

          if (rangeSelector && !rangeSelector.options.floating) {
            rangeSelector.render();
            verticalAlign = rangeSelector.options.verticalAlign;

            if (verticalAlign === 'bottom') {
              this.extraBottomMargin = true;
            } else if (verticalAlign !== 'middle') {
              this.extraTopMargin = true;
            }
          }
        });
        addEvent(Chart, 'getMargins', function () {
          var rangeSelector = this.rangeSelector,
              rangeSelectorHeight;

          if (rangeSelector) {
            rangeSelectorHeight = rangeSelector.getHeight();

            if (this.extraTopMargin) {
              this.plotTop += rangeSelectorHeight;
            }

            if (this.extraBottomMargin) {
              this.marginBottom += rangeSelectorHeight;
            }
          }
        });
        Chart.prototype.callbacks.push(function (chart) {
          var extremes,
              rangeSelector = chart.rangeSelector,
              unbindRender,
              unbindSetExtremes;

          function renderRangeSelector() {
            extremes = chart.xAxis[0].getExtremes();

            if (isNumber(extremes.min)) {
              rangeSelector.render(extremes.min, extremes.max);
            }
          }

          if (rangeSelector) {
            unbindSetExtremes = addEvent(chart.xAxis[0], 'afterSetExtremes', function (e) {
              rangeSelector.render(e.min, e.max);
            });
            unbindRender = addEvent(chart, 'redraw', renderRangeSelector);
            renderRangeSelector();
          }

          addEvent(chart, 'destroy', function destroyEvents() {
            if (rangeSelector) {
              unbindRender();
              unbindSetExtremes();
            }
          });
        });
        H.RangeSelector = RangeSelector;
      }
    });

    _registerModule(_modules, 'parts/Navigator.js', [_modules['parts/Globals.js'], _modules['parts/Color.js'], _modules['parts/Scrollbar.js'], _modules['parts/Utilities.js']], function (H, Color, Scrollbar, U) {
      var color = Color.parse;
      var addEvent = U.addEvent,
          clamp = U.clamp,
          correctFloat = U.correctFloat,
          defined = U.defined,
          destroyObjectProperties = U.destroyObjectProperties,
          erase = U.erase,
          extend = U.extend,
          find = U.find,
          isArray = U.isArray,
          isNumber = U.isNumber,
          merge = U.merge,
          pick = U.pick,
          removeEvent = U.removeEvent,
          splat = U.splat;

      var Axis = H.Axis,
          Chart = H.Chart,
          defaultOptions = H.defaultOptions,
          hasTouch = H.hasTouch,
          isTouchDevice = H.isTouchDevice,
          Series = H.Series,
          seriesTypes = H.seriesTypes,
          defaultSeriesType,
          numExt = function numExt(extreme) {
        var args = [];

        for (var _i = 1; _i < arguments.length; _i++) {
          args[_i - 1] = arguments[_i];
        }

        var numbers = [].filter.call(args, isNumber);

        if (numbers.length) {
          return Math[extreme].apply(0, numbers);
        }
      };

      defaultSeriesType = typeof seriesTypes.areaspline === 'undefined' ? 'line' : 'areaspline';
      extend(defaultOptions, {
        navigator: {
          height: 40,
          margin: 25,
          maskInside: true,
          handles: {
            width: 7,
            height: 15,
            symbols: ['navigator-handle', 'navigator-handle'],
            enabled: true,
            lineWidth: 1,
            backgroundColor: '#f2f2f2',
            borderColor: '#999999'
          },
          maskFill: color('#6685c2').setOpacity(0.3).get(),
          outlineColor: '#cccccc',
          outlineWidth: 1,
          series: {
            type: defaultSeriesType,
            fillOpacity: 0.05,
            lineWidth: 1,
            compare: null,
            dataGrouping: {
              approximation: 'average',
              enabled: true,
              groupPixelWidth: 2,
              smoothed: true,
              units: [['millisecond', [1, 2, 5, 10, 20, 25, 50, 100, 200, 500]], ['second', [1, 2, 5, 10, 15, 30]], ['minute', [1, 2, 5, 10, 15, 30]], ['hour', [1, 2, 3, 4, 6, 8, 12]], ['day', [1, 2, 3, 4]], ['week', [1, 2, 3]], ['month', [1, 3, 6]], ['year', null]]
            },
            dataLabels: {
              enabled: false,
              zIndex: 2
            },
            id: 'highcharts-navigator-series',
            className: 'highcharts-navigator-series',
            lineColor: null,
            marker: {
              enabled: false
            },
            threshold: null
          },
          xAxis: {
            overscroll: 0,
            className: 'highcharts-navigator-xaxis',
            tickLength: 0,
            lineWidth: 0,
            gridLineColor: '#e6e6e6',
            gridLineWidth: 1,
            tickPixelInterval: 200,
            labels: {
              align: 'left',
              style: {
                color: '#999999'
              },
              x: 3,
              y: -4
            },
            crosshair: false
          },
          yAxis: {
            className: 'highcharts-navigator-yaxis',
            gridLineWidth: 0,
            startOnTick: false,
            endOnTick: false,
            minPadding: 0.1,
            maxPadding: 0.1,
            labels: {
              enabled: false
            },
            crosshair: false,
            title: {
              text: null
            },
            tickLength: 0,
            tickWidth: 0
          }
        }
      });

      H.Renderer.prototype.symbols['navigator-handle'] = function (x, y, w, h, options) {
        var halfWidth = options.width / 2,
            markerPosition = Math.round(halfWidth / 3) + 0.5,
            height = options.height;
        return ['M', -halfWidth - 1, 0.5, 'L', halfWidth, 0.5, 'L', halfWidth, height + 0.5, 'L', -halfWidth - 1, height + 0.5, 'L', -halfWidth - 1, 0.5, 'M', -markerPosition, 4, 'L', -markerPosition, height - 3, 'M', markerPosition - 1, 4, 'L', markerPosition - 1, height - 3];
      };

      Axis.prototype.toFixedRange = function (pxMin, pxMax, fixedMin, fixedMax) {
        var fixedRange = this.chart && this.chart.fixedRange,
            halfPointRange = (this.pointRange || 0) / 2,
            newMin = pick(fixedMin, this.translate(pxMin, true, !this.horiz)),
            newMax = pick(fixedMax, this.translate(pxMax, true, !this.horiz)),
            changeRatio = fixedRange && (newMax - newMin) / fixedRange;

        if (!defined(fixedMin)) {
          newMin = correctFloat(newMin + halfPointRange);
        }

        if (!defined(fixedMax)) {
          newMax = correctFloat(newMax - halfPointRange);
        }

        if (changeRatio > 0.7 && changeRatio < 1.3) {
          if (fixedMax) {
            newMin = newMax - fixedRange;
          } else {
            newMax = newMin + fixedRange;
          }
        }

        if (!isNumber(newMin) || !isNumber(newMax)) {
          newMin = newMax = void 0;
        }

        return {
          min: newMin,
          max: newMax
        };
      };

      var Navigator = function () {
        function Navigator(chart) {
          this.baseSeries = void 0;
          this.chart = void 0;
          this.handles = void 0;
          this.height = void 0;
          this.left = void 0;
          this.navigatorEnabled = void 0;
          this.navigatorGroup = void 0;
          this.navigatorOptions = void 0;
          this.navigatorSeries = void 0;
          this.navigatorSize = void 0;
          this.opposite = void 0;
          this.outline = void 0;
          this.outlineHeight = void 0;
          this.range = void 0;
          this.rendered = void 0;
          this.shades = void 0;
          this.size = void 0;
          this.top = void 0;
          this.xAxis = void 0;
          this.yAxis = void 0;
          this.zoomedMax = void 0;
          this.zoomedMin = void 0;
          this.init(chart);
        }

        Navigator.prototype.drawHandle = function (x, index, inverted, verb) {
          var navigator = this,
              height = navigator.navigatorOptions.handles.height;
          navigator.handles[index][verb](inverted ? {
            translateX: Math.round(navigator.left + navigator.height / 2),
            translateY: Math.round(navigator.top + parseInt(x, 10) + 0.5 - height)
          } : {
            translateX: Math.round(navigator.left + parseInt(x, 10)),
            translateY: Math.round(navigator.top + navigator.height / 2 - height / 2 - 1)
          });
        };

        Navigator.prototype.drawOutline = function (zoomedMin, zoomedMax, inverted, verb) {
          var navigator = this,
              maskInside = navigator.navigatorOptions.maskInside,
              outlineWidth = navigator.outline.strokeWidth(),
              halfOutline = outlineWidth / 2,
              outlineCorrection = outlineWidth % 2 / 2,
              outlineHeight = navigator.outlineHeight,
              scrollbarHeight = navigator.scrollbarHeight,
              navigatorSize = navigator.size,
              left = navigator.left - scrollbarHeight,
              navigatorTop = navigator.top,
              verticalMin,
              path;

          if (inverted) {
            left -= halfOutline;
            verticalMin = navigatorTop + zoomedMax + outlineCorrection;
            zoomedMax = navigatorTop + zoomedMin + outlineCorrection;
            path = ['M', left + outlineHeight, navigatorTop - scrollbarHeight - outlineCorrection, 'L', left + outlineHeight, verticalMin, 'L', left, verticalMin, 'L', left, zoomedMax, 'L', left + outlineHeight, zoomedMax, 'L', left + outlineHeight, navigatorTop + navigatorSize + scrollbarHeight].concat(maskInside ? ['M', left + outlineHeight, verticalMin - halfOutline, 'L', left + outlineHeight, zoomedMax + halfOutline] : []);
          } else {
            zoomedMin += left + scrollbarHeight - outlineCorrection;
            zoomedMax += left + scrollbarHeight - outlineCorrection;
            navigatorTop += halfOutline;
            path = ['M', left, navigatorTop, 'L', zoomedMin, navigatorTop, 'L', zoomedMin, navigatorTop + outlineHeight, 'L', zoomedMax, navigatorTop + outlineHeight, 'L', zoomedMax, navigatorTop, 'L', left + navigatorSize + scrollbarHeight * 2, navigatorTop].concat(maskInside ? ['M', zoomedMin - halfOutline, navigatorTop, 'L', zoomedMax + halfOutline, navigatorTop] : []);
          }

          navigator.outline[verb]({
            d: path
          });
        };

        Navigator.prototype.drawMasks = function (zoomedMin, zoomedMax, inverted, verb) {
          var navigator = this,
              left = navigator.left,
              top = navigator.top,
              navigatorHeight = navigator.height,
              height,
              width,
              x,
              y;

          if (inverted) {
            x = [left, left, left];
            y = [top, top + zoomedMin, top + zoomedMax];
            width = [navigatorHeight, navigatorHeight, navigatorHeight];
            height = [zoomedMin, zoomedMax - zoomedMin, navigator.size - zoomedMax];
          } else {
            x = [left, left + zoomedMin, left + zoomedMax];
            y = [top, top, top];
            width = [zoomedMin, zoomedMax - zoomedMin, navigator.size - zoomedMax];
            height = [navigatorHeight, navigatorHeight, navigatorHeight];
          }

          navigator.shades.forEach(function (shade, i) {
            shade[verb]({
              x: x[i],
              y: y[i],
              width: width[i],
              height: height[i]
            });
          });
        };

        Navigator.prototype.renderElements = function () {
          var navigator = this,
              navigatorOptions = navigator.navigatorOptions,
              maskInside = navigatorOptions.maskInside,
              chart = navigator.chart,
              inverted = chart.inverted,
              renderer = chart.renderer,
              navigatorGroup,
              mouseCursor = {
            cursor: inverted ? 'ns-resize' : 'ew-resize'
          };
          navigator.navigatorGroup = navigatorGroup = renderer.g('navigator').attr({
            zIndex: 8,
            visibility: 'hidden'
          }).add();
          [!maskInside, maskInside, !maskInside].forEach(function (hasMask, index) {
            navigator.shades[index] = renderer.rect().addClass('highcharts-navigator-mask' + (index === 1 ? '-inside' : '-outside')).add(navigatorGroup);

            if (!chart.styledMode) {
              navigator.shades[index].attr({
                fill: hasMask ? navigatorOptions.maskFill : 'rgba(0,0,0,0)'
              }).css(index === 1 && mouseCursor);
            }
          });
          navigator.outline = renderer.path().addClass('highcharts-navigator-outline').add(navigatorGroup);

          if (!chart.styledMode) {
            navigator.outline.attr({
              'stroke-width': navigatorOptions.outlineWidth,
              stroke: navigatorOptions.outlineColor
            });
          }

          if (navigatorOptions.handles.enabled) {
            [0, 1].forEach(function (index) {
              navigatorOptions.handles.inverted = chart.inverted;
              navigator.handles[index] = renderer.symbol(navigatorOptions.handles.symbols[index], -navigatorOptions.handles.width / 2 - 1, 0, navigatorOptions.handles.width, navigatorOptions.handles.height, navigatorOptions.handles);
              navigator.handles[index].attr({
                zIndex: 7 - index
              }).addClass('highcharts-navigator-handle ' + 'highcharts-navigator-handle-' + ['left', 'right'][index]).add(navigatorGroup);

              if (!chart.styledMode) {
                var handlesOptions = navigatorOptions.handles;
                navigator.handles[index].attr({
                  fill: handlesOptions.backgroundColor,
                  stroke: handlesOptions.borderColor,
                  'stroke-width': handlesOptions.lineWidth
                }).css(mouseCursor);
              }
            });
          }
        };

        Navigator.prototype.update = function (options) {
          (this.series || []).forEach(function (series) {
            if (series.baseSeries) {
              delete series.baseSeries.navigatorSeries;
            }
          });
          this.destroy();
          var chartOptions = this.chart.options;
          merge(true, chartOptions.navigator, this.options, options);
          this.init(this.chart);
        };

        Navigator.prototype.render = function (min, max, pxMin, pxMax) {
          var navigator = this,
              chart = navigator.chart,
              navigatorWidth,
              scrollbarLeft,
              scrollbarTop,
              scrollbarHeight = navigator.scrollbarHeight,
              navigatorSize,
              xAxis = navigator.xAxis,
              pointRange = xAxis.pointRange || 0,
              scrollbarXAxis = xAxis.fake ? chart.xAxis[0] : xAxis,
              navigatorEnabled = navigator.navigatorEnabled,
              zoomedMin,
              zoomedMax,
              rendered = navigator.rendered,
              inverted = chart.inverted,
              verb,
              newMin,
              newMax,
              currentRange,
              minRange = chart.xAxis[0].minRange,
              maxRange = chart.xAxis[0].options.maxRange;

          if (this.hasDragged && !defined(pxMin)) {
            return;
          }

          min = correctFloat(min - pointRange / 2);
          max = correctFloat(max + pointRange / 2);

          if (!isNumber(min) || !isNumber(max)) {
            if (rendered) {
              pxMin = 0;
              pxMax = pick(xAxis.width, scrollbarXAxis.width);
            } else {
              return;
            }
          }

          navigator.left = pick(xAxis.left, chart.plotLeft + scrollbarHeight + (inverted ? chart.plotWidth : 0));
          navigator.size = zoomedMax = navigatorSize = pick(xAxis.len, (inverted ? chart.plotHeight : chart.plotWidth) - 2 * scrollbarHeight);

          if (inverted) {
            navigatorWidth = scrollbarHeight;
          } else {
            navigatorWidth = navigatorSize + 2 * scrollbarHeight;
          }

          pxMin = pick(pxMin, xAxis.toPixels(min, true));
          pxMax = pick(pxMax, xAxis.toPixels(max, true));

          if (!isNumber(pxMin) || Math.abs(pxMin) === Infinity) {
            pxMin = 0;
            pxMax = navigatorWidth;
          }

          newMin = xAxis.toValue(pxMin, true);
          newMax = xAxis.toValue(pxMax, true);
          currentRange = Math.abs(correctFloat(newMax - newMin));

          if (currentRange < minRange) {
            if (this.grabbedLeft) {
              pxMin = xAxis.toPixels(newMax - minRange - pointRange, true);
            } else if (this.grabbedRight) {
              pxMax = xAxis.toPixels(newMin + minRange + pointRange, true);
            }
          } else if (defined(maxRange) && correctFloat(currentRange - pointRange) > maxRange) {
            if (this.grabbedLeft) {
              pxMin = xAxis.toPixels(newMax - maxRange - pointRange, true);
            } else if (this.grabbedRight) {
              pxMax = xAxis.toPixels(newMin + maxRange + pointRange, true);
            }
          }

          navigator.zoomedMax = clamp(Math.max(pxMin, pxMax), 0, zoomedMax);
          navigator.zoomedMin = clamp(navigator.fixedWidth ? navigator.zoomedMax - navigator.fixedWidth : Math.min(pxMin, pxMax), 0, zoomedMax);
          navigator.range = navigator.zoomedMax - navigator.zoomedMin;
          zoomedMax = Math.round(navigator.zoomedMax);
          zoomedMin = Math.round(navigator.zoomedMin);

          if (navigatorEnabled) {
            navigator.navigatorGroup.attr({
              visibility: 'visible'
            });
            verb = rendered && !navigator.hasDragged ? 'animate' : 'attr';
            navigator.drawMasks(zoomedMin, zoomedMax, inverted, verb);
            navigator.drawOutline(zoomedMin, zoomedMax, inverted, verb);

            if (navigator.navigatorOptions.handles.enabled) {
              navigator.drawHandle(zoomedMin, 0, inverted, verb);
              navigator.drawHandle(zoomedMax, 1, inverted, verb);
            }
          }

          if (navigator.scrollbar) {
            if (inverted) {
              scrollbarTop = navigator.top - scrollbarHeight;
              scrollbarLeft = navigator.left - scrollbarHeight + (navigatorEnabled || !scrollbarXAxis.opposite ? 0 : (scrollbarXAxis.titleOffset || 0) + scrollbarXAxis.axisTitleMargin);
              scrollbarHeight = navigatorSize + 2 * scrollbarHeight;
            } else {
              scrollbarTop = navigator.top + (navigatorEnabled ? navigator.height : -scrollbarHeight);
              scrollbarLeft = navigator.left - scrollbarHeight;
            }

            navigator.scrollbar.position(scrollbarLeft, scrollbarTop, navigatorWidth, scrollbarHeight);
            navigator.scrollbar.setRange(navigator.zoomedMin / (navigatorSize || 1), navigator.zoomedMax / (navigatorSize || 1));
          }

          navigator.rendered = true;
        };

        Navigator.prototype.addMouseEvents = function () {
          var navigator = this,
              chart = navigator.chart,
              container = chart.container,
              eventsToUnbind = [],
              mouseMoveHandler,
              mouseUpHandler;

          navigator.mouseMoveHandler = mouseMoveHandler = function mouseMoveHandler(e) {
            navigator.onMouseMove(e);
          };

          navigator.mouseUpHandler = mouseUpHandler = function mouseUpHandler(e) {
            navigator.onMouseUp(e);
          };

          eventsToUnbind = navigator.getPartsEvents('mousedown');
          eventsToUnbind.push(addEvent(chart.renderTo, 'mousemove', mouseMoveHandler), addEvent(container.ownerDocument, 'mouseup', mouseUpHandler));

          if (hasTouch) {
            eventsToUnbind.push(addEvent(chart.renderTo, 'touchmove', mouseMoveHandler), addEvent(container.ownerDocument, 'touchend', mouseUpHandler));
            eventsToUnbind.concat(navigator.getPartsEvents('touchstart'));
          }

          navigator.eventsToUnbind = eventsToUnbind;

          if (navigator.series && navigator.series[0]) {
            eventsToUnbind.push(addEvent(navigator.series[0].xAxis, 'foundExtremes', function () {
              chart.navigator.modifyNavigatorAxisExtremes();
            }));
          }
        };

        Navigator.prototype.getPartsEvents = function (eventName) {
          var navigator = this,
              events = [];
          ['shades', 'handles'].forEach(function (name) {
            navigator[name].forEach(function (navigatorItem, index) {
              events.push(addEvent(navigatorItem.element, eventName, function (e) {
                navigator[name + 'Mousedown'](e, index);
              }));
            });
          });
          return events;
        };

        Navigator.prototype.shadesMousedown = function (e, index) {
          e = this.chart.pointer.normalize(e);
          var navigator = this,
              chart = navigator.chart,
              xAxis = navigator.xAxis,
              zoomedMin = navigator.zoomedMin,
              navigatorPosition = navigator.left,
              navigatorSize = navigator.size,
              range = navigator.range,
              chartX = e.chartX,
              fixedMax,
              fixedMin,
              ext,
              left;

          if (chart.inverted) {
            chartX = e.chartY;
            navigatorPosition = navigator.top;
          }

          if (index === 1) {
            navigator.grabbedCenter = chartX;
            navigator.fixedWidth = range;
            navigator.dragOffset = chartX - zoomedMin;
          } else {
            left = chartX - navigatorPosition - range / 2;

            if (index === 0) {
              left = Math.max(0, left);
            } else if (index === 2 && left + range >= navigatorSize) {
              left = navigatorSize - range;

              if (navigator.reversedExtremes) {
                left -= range;
                fixedMin = navigator.getUnionExtremes().dataMin;
              } else {
                fixedMax = navigator.getUnionExtremes().dataMax;
              }
            }

            if (left !== zoomedMin) {
              navigator.fixedWidth = range;
              ext = xAxis.toFixedRange(left, left + range, fixedMin, fixedMax);

              if (defined(ext.min)) {
                chart.xAxis[0].setExtremes(Math.min(ext.min, ext.max), Math.max(ext.min, ext.max), true, null, {
                  trigger: 'navigator'
                });
              }
            }
          }
        };

        Navigator.prototype.handlesMousedown = function (e, index) {
          e = this.chart.pointer.normalize(e);
          var navigator = this,
              chart = navigator.chart,
              baseXAxis = chart.xAxis[0],
              reverse = navigator.reversedExtremes;

          if (index === 0) {
            navigator.grabbedLeft = true;
            navigator.otherHandlePos = navigator.zoomedMax;
            navigator.fixedExtreme = reverse ? baseXAxis.min : baseXAxis.max;
          } else {
            navigator.grabbedRight = true;
            navigator.otherHandlePos = navigator.zoomedMin;
            navigator.fixedExtreme = reverse ? baseXAxis.max : baseXAxis.min;
          }

          chart.fixedRange = null;
        };

        Navigator.prototype.onMouseMove = function (e) {
          var navigator = this,
              chart = navigator.chart,
              left = navigator.left,
              navigatorSize = navigator.navigatorSize,
              range = navigator.range,
              dragOffset = navigator.dragOffset,
              inverted = chart.inverted,
              chartX;

          if (!e.touches || e.touches[0].pageX !== 0) {
            e = chart.pointer.normalize(e);
            chartX = e.chartX;

            if (inverted) {
              left = navigator.top;
              chartX = e.chartY;
            }

            if (navigator.grabbedLeft) {
              navigator.hasDragged = true;
              navigator.render(0, 0, chartX - left, navigator.otherHandlePos);
            } else if (navigator.grabbedRight) {
              navigator.hasDragged = true;
              navigator.render(0, 0, navigator.otherHandlePos, chartX - left);
            } else if (navigator.grabbedCenter) {
              navigator.hasDragged = true;

              if (chartX < dragOffset) {
                chartX = dragOffset;
              } else if (chartX > navigatorSize + dragOffset - range) {
                chartX = navigatorSize + dragOffset - range;
              }

              navigator.render(0, 0, chartX - dragOffset, chartX - dragOffset + range);
            }

            if (navigator.hasDragged && navigator.scrollbar && pick(navigator.scrollbar.options.liveRedraw, H.svg && !isTouchDevice && !this.chart.isBoosting)) {
              e.DOMType = e.type;
              setTimeout(function () {
                navigator.onMouseUp(e);
              }, 0);
            }
          }
        };

        Navigator.prototype.onMouseUp = function (e) {
          var navigator = this,
              chart = navigator.chart,
              xAxis = navigator.xAxis,
              scrollbar = navigator.scrollbar,
              DOMEvent = e.DOMEvent || e,
              inverted = chart.inverted,
              verb = navigator.rendered && !navigator.hasDragged ? 'animate' : 'attr',
              zoomedMax = Math.round(navigator.zoomedMax),
              zoomedMin = Math.round(navigator.zoomedMin),
              unionExtremes,
              fixedMin,
              fixedMax,
              ext;

          if (navigator.hasDragged && (!scrollbar || !scrollbar.hasDragged) || e.trigger === 'scrollbar') {
            unionExtremes = navigator.getUnionExtremes();

            if (navigator.zoomedMin === navigator.otherHandlePos) {
              fixedMin = navigator.fixedExtreme;
            } else if (navigator.zoomedMax === navigator.otherHandlePos) {
              fixedMax = navigator.fixedExtreme;
            }

            if (navigator.zoomedMax === navigator.size) {
              fixedMax = navigator.reversedExtremes ? unionExtremes.dataMin : unionExtremes.dataMax;
            }

            if (navigator.zoomedMin === 0) {
              fixedMin = navigator.reversedExtremes ? unionExtremes.dataMax : unionExtremes.dataMin;
            }

            ext = xAxis.toFixedRange(navigator.zoomedMin, navigator.zoomedMax, fixedMin, fixedMax);

            if (defined(ext.min)) {
              chart.xAxis[0].setExtremes(Math.min(ext.min, ext.max), Math.max(ext.min, ext.max), true, navigator.hasDragged ? false : null, {
                trigger: 'navigator',
                triggerOp: 'navigator-drag',
                DOMEvent: DOMEvent
              });
            }
          }

          if (e.DOMType !== 'mousemove' && e.DOMType !== 'touchmove') {
            navigator.grabbedLeft = navigator.grabbedRight = navigator.grabbedCenter = navigator.fixedWidth = navigator.fixedExtreme = navigator.otherHandlePos = navigator.hasDragged = navigator.dragOffset = null;
          }

          if (navigator.navigatorEnabled) {
            if (navigator.shades) {
              navigator.drawMasks(zoomedMin, zoomedMax, inverted, verb);
            }

            if (navigator.outline) {
              navigator.drawOutline(zoomedMin, zoomedMax, inverted, verb);
            }

            if (navigator.navigatorOptions.handles.enabled && Object.keys(navigator.handles).length === navigator.handles.length) {
              navigator.drawHandle(zoomedMin, 0, inverted, verb);
              navigator.drawHandle(zoomedMax, 1, inverted, verb);
            }
          }
        };

        Navigator.prototype.removeEvents = function () {
          if (this.eventsToUnbind) {
            this.eventsToUnbind.forEach(function (unbind) {
              unbind();
            });
            this.eventsToUnbind = void 0;
          }

          this.removeBaseSeriesEvents();
        };

        Navigator.prototype.removeBaseSeriesEvents = function () {
          var baseSeries = this.baseSeries || [];

          if (this.navigatorEnabled && baseSeries[0]) {
            if (this.navigatorOptions.adaptToUpdatedData !== false) {
              baseSeries.forEach(function (series) {
                removeEvent(series, 'updatedData', this.updatedDataHandler);
              }, this);
            }

            if (baseSeries[0].xAxis) {
              removeEvent(baseSeries[0].xAxis, 'foundExtremes', this.modifyBaseAxisExtremes);
            }
          }
        };

        Navigator.prototype.init = function (chart) {
          var chartOptions = chart.options,
              navigatorOptions = chartOptions.navigator,
              navigatorEnabled = navigatorOptions.enabled,
              scrollbarOptions = chartOptions.scrollbar,
              scrollbarEnabled = scrollbarOptions.enabled,
              height = navigatorEnabled ? navigatorOptions.height : 0,
              scrollbarHeight = scrollbarEnabled ? scrollbarOptions.height : 0;
          this.handles = [];
          this.shades = [];
          this.chart = chart;
          this.setBaseSeries();
          this.height = height;
          this.scrollbarHeight = scrollbarHeight;
          this.scrollbarEnabled = scrollbarEnabled;
          this.navigatorEnabled = navigatorEnabled;
          this.navigatorOptions = navigatorOptions;
          this.scrollbarOptions = scrollbarOptions;
          this.outlineHeight = height + scrollbarHeight;
          this.opposite = pick(navigatorOptions.opposite, Boolean(!navigatorEnabled && chart.inverted));
          var navigator = this,
              baseSeries = navigator.baseSeries,
              xAxisIndex = chart.xAxis.length,
              yAxisIndex = chart.yAxis.length,
              baseXaxis = baseSeries && baseSeries[0] && baseSeries[0].xAxis || chart.xAxis[0] || {
            options: {}
          };
          chart.isDirtyBox = true;

          if (navigator.navigatorEnabled) {
            navigator.xAxis = new Axis(chart, merge({
              breaks: baseXaxis.options.breaks,
              ordinal: baseXaxis.options.ordinal
            }, navigatorOptions.xAxis, {
              id: 'navigator-x-axis',
              yAxis: 'navigator-y-axis',
              isX: true,
              type: 'datetime',
              index: xAxisIndex,
              isInternal: true,
              offset: 0,
              keepOrdinalPadding: true,
              startOnTick: false,
              endOnTick: false,
              minPadding: 0,
              maxPadding: 0,
              zoomEnabled: false
            }, chart.inverted ? {
              offsets: [scrollbarHeight, 0, -scrollbarHeight, 0],
              width: height
            } : {
              offsets: [0, -scrollbarHeight, 0, scrollbarHeight],
              height: height
            }));
            navigator.yAxis = new Axis(chart, merge(navigatorOptions.yAxis, {
              id: 'navigator-y-axis',
              alignTicks: false,
              offset: 0,
              index: yAxisIndex,
              isInternal: true,
              zoomEnabled: false
            }, chart.inverted ? {
              width: height
            } : {
              height: height
            }));

            if (baseSeries || navigatorOptions.series.data) {
              navigator.updateNavigatorSeries(false);
            } else if (chart.series.length === 0) {
              navigator.unbindRedraw = addEvent(chart, 'beforeRedraw', function () {
                if (chart.series.length > 0 && !navigator.series) {
                  navigator.setBaseSeries();
                  navigator.unbindRedraw();
                }
              });
            }

            navigator.reversedExtremes = chart.inverted && !navigator.xAxis.reversed || !chart.inverted && navigator.xAxis.reversed;
            navigator.renderElements();
            navigator.addMouseEvents();
          } else {
            navigator.xAxis = {
              translate: function translate(value, reverse) {
                var axis = chart.xAxis[0],
                    ext = axis.getExtremes(),
                    scrollTrackWidth = axis.len - 2 * scrollbarHeight,
                    min = numExt('min', axis.options.min, ext.dataMin),
                    valueRange = numExt('max', axis.options.max, ext.dataMax) - min;
                return reverse ? value * valueRange / scrollTrackWidth + min : scrollTrackWidth * (value - min) / valueRange;
              },
              toPixels: function toPixels(value) {
                return this.translate(value);
              },
              toValue: function toValue(value) {
                return this.translate(value, true);
              },
              toFixedRange: Axis.prototype.toFixedRange,
              fake: true
            };
          }

          if (chart.options.scrollbar.enabled) {
            chart.scrollbar = navigator.scrollbar = new Scrollbar(chart.renderer, merge(chart.options.scrollbar, {
              margin: navigator.navigatorEnabled ? 0 : 10,
              vertical: chart.inverted
            }), chart);
            addEvent(navigator.scrollbar, 'changed', function (e) {
              var range = navigator.size,
                  to = range * this.to,
                  from = range * this.from;
              navigator.hasDragged = navigator.scrollbar.hasDragged;
              navigator.render(0, 0, from, to);

              if (chart.options.scrollbar.liveRedraw || e.DOMType !== 'mousemove' && e.DOMType !== 'touchmove') {
                setTimeout(function () {
                  navigator.onMouseUp(e);
                });
              }
            });
          }

          navigator.addBaseSeriesEvents();
          navigator.addChartEvents();
        };

        Navigator.prototype.getUnionExtremes = function (returnFalseOnNoBaseSeries) {
          var baseAxis = this.chart.xAxis[0],
              navAxis = this.xAxis,
              navAxisOptions = navAxis.options,
              baseAxisOptions = baseAxis.options,
              ret;

          if (!returnFalseOnNoBaseSeries || baseAxis.dataMin !== null) {
            ret = {
              dataMin: pick(navAxisOptions && navAxisOptions.min, numExt('min', baseAxisOptions.min, baseAxis.dataMin, navAxis.dataMin, navAxis.min)),
              dataMax: pick(navAxisOptions && navAxisOptions.max, numExt('max', baseAxisOptions.max, baseAxis.dataMax, navAxis.dataMax, navAxis.max))
            };
          }

          return ret;
        };

        Navigator.prototype.setBaseSeries = function (baseSeriesOptions, redraw) {
          var chart = this.chart,
              baseSeries = this.baseSeries = [];
          baseSeriesOptions = baseSeriesOptions || chart.options && chart.options.navigator.baseSeries || (chart.series.length ? find(chart.series, function (s) {
            return !s.options.isInternal;
          }).index : 0);
          (chart.series || []).forEach(function (series, i) {
            if (!series.options.isInternal && (series.options.showInNavigator || (i === baseSeriesOptions || series.options.id === baseSeriesOptions) && series.options.showInNavigator !== false)) {
              baseSeries.push(series);
            }
          });

          if (this.xAxis && !this.xAxis.fake) {
            this.updateNavigatorSeries(true, redraw);
          }
        };

        Navigator.prototype.updateNavigatorSeries = function (addEvents, redraw) {
          var navigator = this,
              chart = navigator.chart,
              baseSeries = navigator.baseSeries,
              baseOptions,
              mergedNavSeriesOptions,
              chartNavigatorSeriesOptions = navigator.navigatorOptions.series,
              baseNavigatorOptions,
              navSeriesMixin = {
            enableMouseTracking: false,
            index: null,
            linkedTo: null,
            group: 'nav',
            padXAxis: false,
            xAxis: 'navigator-x-axis',
            yAxis: 'navigator-y-axis',
            showInLegend: false,
            stacking: false,
            isInternal: true,
            states: {
              inactive: {
                opacity: 1
              }
            }
          },
              navigatorSeries = navigator.series = (navigator.series || []).filter(function (navSeries) {
            var base = navSeries.baseSeries;

            if (baseSeries.indexOf(base) < 0) {
              if (base) {
                removeEvent(base, 'updatedData', navigator.updatedDataHandler);
                delete base.navigatorSeries;
              }

              if (navSeries.chart) {
                navSeries.destroy();
              }

              return false;
            }

            return true;
          });

          if (baseSeries && baseSeries.length) {
            baseSeries.forEach(function eachBaseSeries(base) {
              var linkedNavSeries = base.navigatorSeries,
                  userNavOptions = extend({
                color: base.color,
                visible: base.visible
              }, !isArray(chartNavigatorSeriesOptions) ? chartNavigatorSeriesOptions : defaultOptions.navigator.series);

              if (linkedNavSeries && navigator.navigatorOptions.adaptToUpdatedData === false) {
                return;
              }

              navSeriesMixin.name = 'Navigator ' + baseSeries.length;
              baseOptions = base.options || {};
              baseNavigatorOptions = baseOptions.navigatorOptions || {};
              mergedNavSeriesOptions = merge(baseOptions, navSeriesMixin, userNavOptions, baseNavigatorOptions);
              mergedNavSeriesOptions.pointRange = pick(userNavOptions.pointRange, baseNavigatorOptions.pointRange, defaultOptions.plotOptions[mergedNavSeriesOptions.type || 'line'].pointRange);
              var navigatorSeriesData = baseNavigatorOptions.data || userNavOptions.data;
              navigator.hasNavigatorData = navigator.hasNavigatorData || !!navigatorSeriesData;
              mergedNavSeriesOptions.data = navigatorSeriesData || baseOptions.data && baseOptions.data.slice(0);

              if (linkedNavSeries && linkedNavSeries.options) {
                linkedNavSeries.update(mergedNavSeriesOptions, redraw);
              } else {
                base.navigatorSeries = chart.initSeries(mergedNavSeriesOptions);
                base.navigatorSeries.baseSeries = base;
                navigatorSeries.push(base.navigatorSeries);
              }
            });
          }

          if (chartNavigatorSeriesOptions.data && !(baseSeries && baseSeries.length) || isArray(chartNavigatorSeriesOptions)) {
            navigator.hasNavigatorData = false;
            chartNavigatorSeriesOptions = splat(chartNavigatorSeriesOptions);
            chartNavigatorSeriesOptions.forEach(function (userSeriesOptions, i) {
              navSeriesMixin.name = 'Navigator ' + (navigatorSeries.length + 1);
              mergedNavSeriesOptions = merge(defaultOptions.navigator.series, {
                color: chart.series[i] && !chart.series[i].options.isInternal && chart.series[i].color || chart.options.colors[i] || chart.options.colors[0]
              }, navSeriesMixin, userSeriesOptions);
              mergedNavSeriesOptions.data = userSeriesOptions.data;

              if (mergedNavSeriesOptions.data) {
                navigator.hasNavigatorData = true;
                navigatorSeries.push(chart.initSeries(mergedNavSeriesOptions));
              }
            });
          }

          if (addEvents) {
            this.addBaseSeriesEvents();
          }
        };

        Navigator.prototype.addBaseSeriesEvents = function () {
          var navigator = this,
              baseSeries = navigator.baseSeries || [];

          if (baseSeries[0] && baseSeries[0].xAxis) {
            addEvent(baseSeries[0].xAxis, 'foundExtremes', this.modifyBaseAxisExtremes);
          }

          baseSeries.forEach(function (base) {
            addEvent(base, 'show', function () {
              if (this.navigatorSeries) {
                this.navigatorSeries.setVisible(true, false);
              }
            });
            addEvent(base, 'hide', function () {
              if (this.navigatorSeries) {
                this.navigatorSeries.setVisible(false, false);
              }
            });

            if (this.navigatorOptions.adaptToUpdatedData !== false) {
              if (base.xAxis) {
                addEvent(base, 'updatedData', this.updatedDataHandler);
              }
            }

            addEvent(base, 'remove', function () {
              if (this.navigatorSeries) {
                erase(navigator.series, this.navigatorSeries);

                if (defined(this.navigatorSeries.options)) {
                  this.navigatorSeries.remove(false);
                }

                delete this.navigatorSeries;
              }
            });
          }, this);
        };

        Navigator.prototype.getBaseSeriesMin = function (currentSeriesMin) {
          return this.baseSeries.reduce(function (min, series) {
            return Math.min(min, series.xData ? series.xData[0] : min);
          }, currentSeriesMin);
        };

        Navigator.prototype.modifyNavigatorAxisExtremes = function () {
          var xAxis = this.xAxis,
              unionExtremes;

          if (typeof xAxis.getExtremes !== 'undefined') {
            unionExtremes = this.getUnionExtremes(true);

            if (unionExtremes && (unionExtremes.dataMin !== xAxis.min || unionExtremes.dataMax !== xAxis.max)) {
              xAxis.min = unionExtremes.dataMin;
              xAxis.max = unionExtremes.dataMax;
            }
          }
        };

        Navigator.prototype.modifyBaseAxisExtremes = function () {
          var baseXAxis = this,
              navigator = baseXAxis.chart.navigator,
              baseExtremes = baseXAxis.getExtremes(),
              baseMin = baseExtremes.min,
              baseMax = baseExtremes.max,
              baseDataMin = baseExtremes.dataMin,
              baseDataMax = baseExtremes.dataMax,
              range = baseMax - baseMin,
              stickToMin = navigator.stickToMin,
              stickToMax = navigator.stickToMax,
              overscroll = pick(baseXAxis.options.overscroll, 0),
              newMax,
              newMin,
              navigatorSeries = navigator.series && navigator.series[0],
              hasSetExtremes = !!baseXAxis.setExtremes,
              unmutable = baseXAxis.eventArgs && baseXAxis.eventArgs.trigger === 'rangeSelectorButton';

          if (!unmutable) {
            if (stickToMin) {
              newMin = baseDataMin;
              newMax = newMin + range;
            }

            if (stickToMax) {
              newMax = baseDataMax + overscroll;

              if (!stickToMin) {
                newMin = Math.max(newMax - range, navigator.getBaseSeriesMin(navigatorSeries && navigatorSeries.xData ? navigatorSeries.xData[0] : -Number.MAX_VALUE));
              }
            }

            if (hasSetExtremes && (stickToMin || stickToMax)) {
              if (isNumber(newMin)) {
                baseXAxis.min = baseXAxis.userMin = newMin;
                baseXAxis.max = baseXAxis.userMax = newMax;
              }
            }
          }

          navigator.stickToMin = navigator.stickToMax = null;
        };

        Navigator.prototype.updatedDataHandler = function () {
          var navigator = this.chart.navigator,
              baseSeries = this,
              navigatorSeries = this.navigatorSeries,
              xDataMin = navigator.getBaseSeriesMin(baseSeries.xData[0]);
          navigator.stickToMax = navigator.reversedExtremes ? Math.round(navigator.zoomedMin) === 0 : Math.round(navigator.zoomedMax) >= Math.round(navigator.size);
          navigator.stickToMin = isNumber(baseSeries.xAxis.min) && baseSeries.xAxis.min <= xDataMin && (!this.chart.fixedRange || !navigator.stickToMax);

          if (navigatorSeries && !navigator.hasNavigatorData) {
            navigatorSeries.options.pointStart = baseSeries.xData[0];
            navigatorSeries.setData(baseSeries.options.data, false, null, false);
          }
        };

        Navigator.prototype.addChartEvents = function () {
          if (!this.eventsToUnbind) {
            this.eventsToUnbind = [];
          }

          this.eventsToUnbind.push(addEvent(this.chart, 'redraw', function () {
            var navigator = this.navigator,
                xAxis = navigator && (navigator.baseSeries && navigator.baseSeries[0] && navigator.baseSeries[0].xAxis || navigator.scrollbar && this.xAxis[0]);

            if (xAxis) {
              navigator.render(xAxis.min, xAxis.max);
            }
          }), addEvent(this.chart, 'getMargins', function () {
            var chart = this,
                navigator = chart.navigator,
                marginName = navigator.opposite ? 'plotTop' : 'marginBottom';

            if (chart.inverted) {
              marginName = navigator.opposite ? 'marginRight' : 'plotLeft';
            }

            chart[marginName] = (chart[marginName] || 0) + (navigator.navigatorEnabled || !chart.inverted ? navigator.outlineHeight : 0) + navigator.navigatorOptions.margin;
          }));
        };

        Navigator.prototype.destroy = function () {
          this.removeEvents();

          if (this.xAxis) {
            erase(this.chart.xAxis, this.xAxis);
            erase(this.chart.axes, this.xAxis);
          }

          if (this.yAxis) {
            erase(this.chart.yAxis, this.yAxis);
            erase(this.chart.axes, this.yAxis);
          }

          (this.series || []).forEach(function (s) {
            if (s.destroy) {
              s.destroy();
            }
          });
          ['series', 'xAxis', 'yAxis', 'shades', 'outline', 'scrollbarTrack', 'scrollbarRifles', 'scrollbarGroup', 'scrollbar', 'navigatorGroup', 'rendered'].forEach(function (prop) {
            if (this[prop] && this[prop].destroy) {
              this[prop].destroy();
            }

            this[prop] = null;
          }, this);
          [this.handles].forEach(function (coll) {
            destroyObjectProperties(coll);
          }, this);
        };

        return Navigator;
      }();

      if (!H.Navigator) {
        H.Navigator = Navigator;
        addEvent(Axis, 'zoom', function (e) {
          var chart = this.chart,
              chartOptions = chart.options,
              zoomType = chartOptions.chart.zoomType,
              pinchType = chartOptions.chart.pinchType,
              previousZoom,
              navigator = chartOptions.navigator,
              rangeSelector = chartOptions.rangeSelector;

          if (this.isXAxis && (navigator && navigator.enabled || rangeSelector && rangeSelector.enabled)) {
            if (zoomType === 'y') {
              e.zoomed = false;
            } else if ((!isTouchDevice && zoomType === 'xy' || isTouchDevice && pinchType === 'xy') && this.options.range) {
              previousZoom = this.previousZoom;

              if (defined(e.newMin)) {
                this.previousZoom = [this.min, this.max];
              } else if (previousZoom) {
                e.newMin = previousZoom[0];
                e.newMax = previousZoom[1];
                delete this.previousZoom;
              }
            }
          }

          if (typeof e.zoomed !== 'undefined') {
            e.preventDefault();
          }
        });
        addEvent(Chart, 'beforeShowResetZoom', function () {
          var chartOptions = this.options,
              navigator = chartOptions.navigator,
              rangeSelector = chartOptions.rangeSelector;

          if ((navigator && navigator.enabled || rangeSelector && rangeSelector.enabled) && (!isTouchDevice && chartOptions.chart.zoomType === 'x' || isTouchDevice && chartOptions.chart.pinchType === 'x')) {
            return false;
          }
        });
        addEvent(Chart, 'beforeRender', function () {
          var options = this.options;

          if (options.navigator.enabled || options.scrollbar.enabled) {
            this.scroller = this.navigator = new Navigator(this);
          }
        });
        addEvent(Chart, 'afterSetChartSize', function () {
          var legend = this.legend,
              navigator = this.navigator,
              scrollbarHeight,
              legendOptions,
              xAxis,
              yAxis;

          if (navigator) {
            legendOptions = legend && legend.options;
            xAxis = navigator.xAxis;
            yAxis = navigator.yAxis;
            scrollbarHeight = navigator.scrollbarHeight;

            if (this.inverted) {
              navigator.left = navigator.opposite ? this.chartWidth - scrollbarHeight - navigator.height : this.spacing[3] + scrollbarHeight;
              navigator.top = this.plotTop + scrollbarHeight;
            } else {
              navigator.left = this.plotLeft + scrollbarHeight;
              navigator.top = navigator.navigatorOptions.top || this.chartHeight - navigator.height - scrollbarHeight - this.spacing[2] - (this.rangeSelector && this.extraBottomMargin ? this.rangeSelector.getHeight() : 0) - (legendOptions && legendOptions.verticalAlign === 'bottom' && legendOptions.enabled && !legendOptions.floating ? legend.legendHeight + pick(legendOptions.margin, 10) : 0) - (this.titleOffset ? this.titleOffset[2] : 0);
            }

            if (xAxis && yAxis) {
              if (this.inverted) {
                xAxis.options.left = yAxis.options.left = navigator.left;
              } else {
                xAxis.options.top = yAxis.options.top = navigator.top;
              }

              xAxis.setAxisSize();
              yAxis.setAxisSize();
            }
          }
        });
        addEvent(Chart, 'update', function (e) {
          var navigatorOptions = e.options.navigator || {},
              scrollbarOptions = e.options.scrollbar || {};

          if (!this.navigator && !this.scroller && (navigatorOptions.enabled || scrollbarOptions.enabled)) {
            merge(true, this.options.navigator, navigatorOptions);
            merge(true, this.options.scrollbar, scrollbarOptions);
            delete e.options.navigator;
            delete e.options.scrollbar;
          }
        });
        addEvent(Chart, 'afterUpdate', function (event) {
          if (!this.navigator && !this.scroller && (this.options.navigator.enabled || this.options.scrollbar.enabled)) {
            this.scroller = this.navigator = new Navigator(this);

            if (pick(event.redraw, true)) {
              this.redraw(event.animation);
            }
          }
        });
        addEvent(Chart, 'afterAddSeries', function () {
          if (this.navigator) {
            this.navigator.setBaseSeries(null, false);
          }
        });
        addEvent(Series, 'afterUpdate', function () {
          if (this.chart.navigator && !this.options.isInternal) {
            this.chart.navigator.setBaseSeries(null, false);
          }
        });
        Chart.prototype.callbacks.push(function (chart) {
          var extremes,
              navigator = chart.navigator;

          if (navigator && chart.xAxis[0]) {
            extremes = chart.xAxis[0].getExtremes();
            navigator.render(extremes.min, extremes.max);
          }
        });
      }

      H.Navigator = Navigator;
      return H.Navigator;
    });

    _registerModule(_modules, 'masters/modules/gantt.src.js', [], function () {});

    _registerModule(_modules, 'masters/highcharts-gantt.src.js', [_modules['masters/highcharts.src.js']], function (Highcharts) {
      Highcharts.product = 'Highcharts Gantt';
      return Highcharts;
    });

    _modules['masters/highcharts-gantt.src.js']._modules = _modules;
    return _modules['masters/highcharts-gantt.src.js'];
  });
});

var highchartsReact_min = createCommonjsModule(function (module, exports) {
  !function (e, t) {
     module.exports = t(React, highcharts) ;
  }("undefined" != typeof self ? self : commonjsGlobal, function (e, t) {
    return function (e) {
      function t(n) {
        if (r[n]) return r[n].exports;
        var o = r[n] = {
          i: n,
          l: !1,
          exports: {}
        };
        return e[n].call(o.exports, o, o.exports, t), o.l = !0, o.exports;
      }

      var r = {};
      return t.m = e, t.c = r, t.d = function (e, r, n) {
        t.o(e, r) || Object.defineProperty(e, r, {
          configurable: !1,
          enumerable: !0,
          get: n
        });
      }, t.n = function (e) {
        var r = e && e.__esModule ? function () {
          return e["default"];
        } : function () {
          return e;
        };
        return t.d(r, "a", r), r;
      }, t.o = function (e, t) {
        return Object.prototype.hasOwnProperty.call(e, t);
      }, t.p = "", t(t.s = 0);
    }([function (e, t, r) {

      function n(e) {
        return e && e.__esModule ? e : {
          "default": e
        };
      }

      function o(e) {
        if (Array.isArray(e)) {
          for (var t = 0, r = Array(e.length); t < e.length; t++) {
            r[t] = e[t];
          }

          return r;
        }

        return Array.from(e);
      }

      function a(e, t) {
        if (!(e instanceof t)) throw new TypeError("Cannot call a class as a function");
      }

      function i(e, t) {
        if (!e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
        return !t || "object" != typeof t && "function" != typeof t ? e : t;
      }

      function c(e, t) {
        if ("function" != typeof t && null !== t) throw new TypeError("Super expression must either be null or a function, not " + typeof t);
        e.prototype = Object.create(t && t.prototype, {
          constructor: {
            value: e,
            enumerable: !1,
            writable: !0,
            configurable: !0
          }
        }), t && (Object.setPrototypeOf ? Object.setPrototypeOf(e, t) : e.__proto__ = t);
      }

      Object.defineProperty(t, "__esModule", {
        value: !0
      });

      var u = Object.assign || function (e) {
        for (var t = 1; t < arguments.length; t++) {
          var r = arguments[t];

          for (var n in r) {
            Object.prototype.hasOwnProperty.call(r, n) && (e[n] = r[n]);
          }
        }

        return e;
      },
          s = function () {
        function e(e, t) {
          for (var r = 0; r < t.length; r++) {
            var n = t[r];
            n.enumerable = n.enumerable || !1, n.configurable = !0, "value" in n && (n.writable = !0), Object.defineProperty(e, n.key, n);
          }
        }

        return function (t, r, n) {
          return r && e(t.prototype, r), n && e(t, n), t;
        };
      }(),
          f = r(1),
          p = n(f),
          l = r(2),
          h = n(l),
          y = function (e) {
        function t(e) {
          a(this, t);
          var r = i(this, (t.__proto__ || Object.getPrototypeOf(t)).call(this, e));
          return r.container = p["default"].createRef(), r;
        }

        return c(t, e), s(t, [{
          key: "createChart",
          value: function value() {
            var e = this.props,
                t = e.highcharts || h["default"],
                r = e.constructorType || "chart";
            t ? t[r] ? e.options ? this.chart = t[r](this.container.current, e.options, e.callback ? e.callback : void 0) : console.warn('The "options" property was not passed.') : console.warn('The "constructorType" property is incorrect or some required module is not imported.') : console.warn('The "highcharts" property was not passed.');
          }
        }, {
          key: "componentDidMount",
          value: function value() {
            this.createChart();
          }
        }, {
          key: "componentDidUpdate",
          value: function value() {
            var e = this.props;
            if (!1 !== e.allowChartUpdate) if (!e.immutable && this.chart) {
              var t;
              (t = this.chart).update.apply(t, [e.options].concat(o(e.updateArgs || [!0, !0])));
            } else this.createChart();
          }
        }, {
          key: "componentWillUnmount",
          value: function value() {
            this.chart && (this.chart.destroy(), this.chart = null);
          }
        }, {
          key: "render",
          value: function value() {
            return p["default"].createElement("div", u({}, this.props.containerProps, {
              ref: this.container
            }));
          }
        }]), t;
      }(p["default"].PureComponent);

      t["default"] = y;
    }, function (t, r) {
      t.exports = e;
    }, function (e, r) {
      e.exports = t;
    }]);
  });
});
var HighchartsReact = unwrapExports(highchartsReact_min);

var noDataToDisplay = createCommonjsModule(function (module) {
  (function (a) {
     module.exports ? (a["default"] = a, module.exports = a) :  a("undefined" !== typeof Highcharts ? Highcharts : void 0);
  })(function (a) {
    function c(a, d, c, e) {
      a.hasOwnProperty(d) || (a[d] = e.apply(null, c));
    }

    a = a ? a._modules : {};
    c(a, "modules/no-data-to-display.src.js", [a["parts/Globals.js"], a["parts/Utilities.js"]], function (a, d) {
      var c = d.addEvent,
          e = d.extend;
      d = a.Chart.prototype;
      var f = a.getOptions();
      e(f.lang, {
        noData: "No data to display"
      });
      f.noData = {
        attr: {
          zIndex: 1
        },
        position: {
          x: 0,
          y: 0,
          align: "center",
          verticalAlign: "middle"
        },
        style: {
          fontWeight: "bold",
          fontSize: "12px",
          color: "#666666"
        }
      };

      d.showNoData = function (a) {
        var b = this.options;
        a = a || b && b.lang.noData;
        b = b && b.noData;
        !this.noDataLabel && this.renderer && (this.noDataLabel = this.renderer.label(a, 0, 0, null, null, null, b.useHTML, null, "no-data"), this.styledMode || this.noDataLabel.attr(b.attr).css(b.style), this.noDataLabel.add(), this.noDataLabel.align(e(this.noDataLabel.getBBox(), b.position), !1, "plotBox"));
      };

      d.hideNoData = function () {
        this.noDataLabel && (this.noDataLabel = this.noDataLabel.destroy());
      };

      d.hasData = function () {
        for (var a = this.series || [], b = a.length; b--;) {
          if (a[b].hasData() && !a[b].options.isInternal) return !0;
        }

        return this.loadingShown;
      };

      c(a.Chart, "render", function () {
        this.hasData() ? this.hideNoData() : this.showNoData();
      });
    });
    c(a, "masters/modules/no-data-to-display.src.js", [], function () {});
  });
});

noDataToDisplay(highcharts);
var defaultOptions = {
  title: null,
  xAxis: {
    gridLineWidth: 1
  },
  yAxis: {
    gridLineWidth: 1
  },
  series: [],
  credits: {
    enabled: false
  },
  lang: {
    noData: 'No data'
  },
  noData: {
    style: {
      fontWeight: 'bold',
      fontSize: '15px',
      color: '#303030'
    }
  },
  time: {
    useUTC: false
  }
};

var ChartComponent = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(ChartComponent, _React$Component);

  function ChartComponent(props) {
    var _this;

    _this = _React$Component.call(this, props) || this;

    _this.componentDidUpdate = function () {
      _this.forceReflow();
    };

    _this.forceReflow = function () {
      if (_this.highchartsComponent && _this.highchartsComponent.current) {
        _this.highchartsComponent.current.chart.reflow();
      }
    };

    _this.highchartsComponent = React.createRef();
    return _this;
  }

  var _proto = ChartComponent.prototype;

  _proto.render = function render() {
    var _this$props = this.props,
        options = _this$props.options,
        constructorType = _this$props.constructorType,
        chartRef = _this$props.chartRef;

    if (chartRef && this.highchartsComponent) {
      chartRef.current = this.highchartsComponent.current;
    }

    return /*#__PURE__*/React.createElement(HighchartsReact, {
      constructorType: constructorType || 'chart',
      containerProps: {
        style: {
          height: '100%'
        }
      },
      highcharts: constructorType === 'ganttChart' ? highchartsGantt_src : highcharts,
      options: _extends({}, defaultOptions, {}, options),
      ref: this.highchartsComponent
    });
  };

  return ChartComponent;
}(React.Component);
ChartComponent.propTypes = {
  options: PropTypes.shape({}),
  constructorType: PropTypes.string,
  chartRef: PropTypes.shape({
    current: PropTypes.any
  })
};
ChartComponent.defaultProps = {
  options: null,
  constructorType: null,
  chartRef: null
};

/* loaded by smart-asset */
var image = require("./loader~oKpOSUuf.gif");

var styles$4 = {"gantt":"_3sNiW","capitalize":"_GTQ1N","loadingBlock":"_2E_MY","loadingData":"_2yM6n"};

var getEntitySidebarCollapseState = selectors.getEntitySidebarCollapseState;

var HighchartsSchedule = function HighchartsSchedule(_ref) {
  var chartData = _ref.chartData,
      onClickChart = _ref.onClickChart,
      dates = _ref.dates,
      configChange = _ref.configChange;
  var chartRef = useRef(null);
  var main = useRef(0);

  var _useState = useState(null),
      options = _useState[0],
      setOptions = _useState[1];

  var _useState2 = useState(false),
      trigger = _useState2[0],
      setTrigger = _useState2[1];

  var sidebarCollapsed = useSelector(function (state) {
    return getEntitySidebarCollapseState(state);
  }).payload;
  var colors = {
    recommended: 'rgb(193, 218, 214)',
    past: 'rgb(232, 208, 169)',
    scheduled: 'lightblue'
  };
  var jsonKeys = {
    recommended_events: 'recommended',
    past_events: 'past',
    scheduled_events: 'scheduled'
  };

  var abbrFlow = function abbrFlow(data) {
    if (data) return ", " + data / 1000 + "k gal";
    return '';
  };

  var abbrPeriod = function abbrPeriod(data) {
    var duration = moment.duration(moment(data[1]).diff(moment(data[0])));
    var durationHr = duration.asHours();
    if (durationHr % 1 !== 0) return durationHr.toFixed(1) + " hrs";
    return durationHr.toFixed() + " hrs";
  };

  var series = [];
  var count = 0;
  var yHeight = 0;
  global.chartYaxisCategories = [];
  global.chartYaxisCategoryTypes = [];
  chartData.forEach(function (block) {
    Object.keys(jsonKeys).forEach(function (irrgType) {
      var irrgEventArr = block[irrgType];
      var obj = {};
      obj.name = block.name ? block.name : "<img id=" + styles$4.loadingBlock + " src=" + image + " alt=\"Loading...\" />";
      global.chartYaxisCategories.push({
        name: block.name,
        id: block.id,
        startDate: null
      });
      global.chartYaxisCategoryTypes.push(jsonKeys[irrgType]);
      obj.current = {
        name: block.name,
        irrigationType: jsonKeys[irrgType]
      };
      obj.current.irrigationType = jsonKeys[irrgType];
      obj.data = [];

      if (!irrgEventArr || !irrgEventArr.length) {
        if (block.selectedIrrigationTypes && block.selectedIrrigationTypes.includes(jsonKeys[irrgType])) {
          obj.data.push({
            id: "block-" + count,
            y: yHeight
          });
          count += 1;
          series.push(obj);
          yHeight += 1;
        }
      }

      if (irrgEventArr) {
        main.current = 1;

        if (irrgEventArr.length > 0) {
          irrgEventArr.forEach(function (event) {
            obj.data.push({
              id: "block-" + count,
              schId: event.id,
              blkId: block.id,
              totalgallons: abbrPeriod([event.start_date, event.stop_date]) + "\n                        " + abbrFlow(event.flow),
              flow: event.flow,
              start: Date.parse(event.start_date),
              end: Date.parse(event.stop_date),
              y: yHeight,
              color: colors[jsonKeys[irrgType]]
            });
            count += 1;
          });
          series.push(obj);
          yHeight += 1;
        }
      }
    });
  });
  var divElement = document.querySelector('.schedule-chart-inner');
  var divHeight = divElement ? divElement.offsetHeight : null;
  var height;

  if (divHeight && chartData && chartData[0] && chartData[0].selectedIrrigationTypes) {
    if (chartData[0].selectedIrrigationTypes.length) {
      var heightVal = chartData.length * chartData[0].selectedIrrigationTypes.length * 50;
      height = heightVal > divHeight ? heightVal : divHeight;
    } else height = divHeight;
  } else height = divHeight;

  var xaxisDateFormat;
  var dateDiff;

  if (dates) {
    dateDiff = dates[1].diff(dates[0], 'days');
    if (dateDiff > 1 && dateDiff < 7) xaxisDateFormat = '%e %b %Y';else if (dateDiff > 7 && dateDiff <= 14) xaxisDateFormat = '%e %b';else if (dateDiff > 14) xaxisDateFormat = '%e';else if (!dateDiff) xaxisDateFormat = '%H:%M';else xaxisDateFormat = '%e %b %Y';
  }

  var xaxisOption;

  if (dates) {
    xaxisOption = [{
      tickInterval: dateDiff ? 1000 * 60 * 60 * 24 : 3600 * 1000,
      type: 'datetime',
      min: dates[0].valueOf(),
      max: dates[1].valueOf(),
      currentDateIndicator: true,
      dateTimeLabelFormats: {
        day: xaxisDateFormat
      }
    }, {
      startOfWeek: 0
    }];
  } else {
    xaxisOption = [{
      currentDateIndicator: true,
      grid: {
        cellHeight: 60
      }
    }, {
      startOfWeek: 0
    }];
  }

  if (!options && series && !configChange && !trigger) {
    var defaultOption = {
      series: series,
      chart: {
        marginLeft: 130,
        animation: false,
        height: height ? height + "px" : 'calc(100vh - 100%)',
        reflow: true,
        events: {
          click: function click(e) {
            if (global.chartYaxisCategories.length > 0) {
              var clickObj = global.chartYaxisCategories[Math.abs(Math.round(e.yAxis[0].value))];

              if (clickObj) {
                clickObj.startDate = moment(e.xAxis[0].value);
                clickObj.irrigationType = global.chartYaxisCategoryTypes[Math.abs(Math.round(e.yAxis[0].value))];
                onClickChart(clickObj);
              }
            }
          }
        }
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            format: '{point.totalgallons}',
            style: {
              cursor: 'default',
              pointerEvents: 'none',
              color: 'black'
            }
          },
          states: {
            select: {
              color: '',
              borderColor: 'none'
            }
          },
          allowPointSelect: true,
          point: {
            events: {
              click: function click(e) {
                onClickChart({
                  scheduleId: e.point.options.schId,
                  blockId: e.point.options.blkId
                });

                if (chartRef.current) {
                  var chart = chartRef.current.chart;
                  if (chart) chart.tooltip.hide();
                }
              }
            }
          }
        }
      },
      tooltip: {
        pointFormat: '<span>Total Gallons: {point.flow}</span><br/>' + '<span>Period: {point.totalgallons}</span><br/>' + '<span>From: {point.start:%Y-%m-%d %H:%M:%S}</span><br/>' + '<span>To: {point.end:%Y-%m-%d %H:%M:%S}</span>'
      },
      xAxis: xaxisOption,
      yAxis: {
        type: 'category',
        labels: {
          useHTML: true,
          style: {
            width: '90px',
            minWidth: '90px',
            textAlign: 'center',
            paddingTop: '10px'
          }
        },
        grid: {
          columns: [{
            title: {
              text: 'Blocks'
            },
            categories: series.map(function (s) {
              return !s.name.includes('<img') ? s.name + "<br><p class=" + styles$4.capitalize + ">(" + s.current.irrigationType + ")" : s.name;
            })
          }]
        }
      }
    };
    setOptions(defaultOption);
  } else if (trigger) {
    var newNames = series.map(function (s) {
      return s.name;
    });
    var loader = newNames.filter(function (d) {
      return d.includes('Loading...');
    }).length;

    if (!loader) {
      if (chartRef.current) {
        var chart = chartRef.current.chart;

        if (main.current) {
          while (chart.series.length > 0) {
            chart.series[0].remove();
          }

          main.current = 0;
        }
      }

      var tempObj = options;
      tempObj.series = series;
      tempObj.xAxis = xaxisOption;
      tempObj.chart.height = height ? height + "px" : 'calc(100vh - 100%)';
      tempObj.yAxis.grid.columns[0].categories = series.map(function (s) {
        return !s.name.includes('<img') ? s.name + "<br><p class=" + styles$4.capitalize + ">(" + s.current.irrigationType + ")" : s.name;
      });
      setOptions(tempObj);
      setTrigger(false);
    } else {
      var _tempObj = options;
      _tempObj.chart.height = height ? height + "px" : 'calc(100vh - 100%)';
    }
  }

  useEffect(function () {
    if (chartRef.current && divHeight && window.innerWidth > 599) {
      var width = window.innerWidth - 20;

      if (!sidebarCollapsed) {
        chartRef.current.chart.setSize(width - 320, divHeight, false);
      } else {
        chartRef.current.chart.setSize(width, divHeight, false);
      }
    }
  }, [divHeight, sidebarCollapsed]);
  useEffect(function () {
    if (!trigger && !configChange) {
      setTrigger(true);
    }
  }, [configChange, trigger]);

  if (chartRef.current) {
    var _chart = chartRef.current.chart;

    _chart.showLoading("<img id=" + styles$4.loadingData + " src=" + image + " alt=\"Loading...\" />");

    setTimeout(function () {
      return _chart.hideLoading();
    }, 1500);
  }

  window.onresize = function onresize() {
    var chartContainer = document.querySelector('.schedule-chart-outer');

    if (chartContainer) {
      var width = chartContainer.clientWidth;
      var boxheight = chartContainer.clientHeight;

      if (chartRef.current) {
        var _chart2 = chartRef.current.chart;

        if (boxheight > 400) {
          if (boxheight > height) _chart2.setSize(width, boxheight, false);else _chart2.setSize(width, height, false);
        }
      }
    }
  };

  return /*#__PURE__*/React.createElement(ChartComponent, {
    constructorType: "ganttChart",
    options: options,
    chartRef: chartRef
  });
};

HighchartsSchedule.propTypes = {
  chartData: PropTypes.arrayOf(PropTypes.any),
  dates: PropTypes.arrayOf(PropTypes.object),
  onClickChart: PropTypes.func,
  configChange: PropTypes.bool
};
HighchartsSchedule.defaultProps = {
  chartData: null,
  dates: null,
  onClickChart: function onClickChart() {
    return null;
  },
  configChange: null
};

var selectAllBlockSchedule = selectors.selectAllBlockSchedule;
var loadBlockSchedule = actions.loadBlockSchedule;
var useBlockNames = hooks.useBlockNames;

var IrrigationSchedule = function IrrigationSchedule(_ref) {
  var blocks = _ref.blocks,
      dates = _ref.dates,
      irrigationTypes = _ref.irrigationTypes,
      onDataRetrieval = _ref.onDataRetrieval,
      onClickChart = _ref.onClickChart;
  var dispatch = useDispatch();
  useEffect(function () {
    var loadData = function loadData() {
      try {
        blocks.forEach(function (d) {
          dispatch(loadBlockSchedule(d));
        });
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    };

    if (blocks.length > 0) {
      loadData();
    }
  }, [blocks, dispatch]);
  var blockScheduleData = useSelector(selectAllBlockSchedule).filter(function (bSch) {
    return blocks.some(function (d) {
      return d === bSch.id;
    });
  });
  var loading = blockScheduleData.reduce(function (a, p) {
    return !p || a || p.loading;
  }, false);
  var blockNames = useBlockNames(blocks);
  var jsonKeys = {
    recommended: 'recommended_events',
    past: 'past_events',
    scheduled: 'scheduled_events'
  };
  var filteredData = blockScheduleData.map(function (blockSchedule) {
    var data = [];

    if (blockSchedule && !blockSchedule.loading) {
      data = _extends({}, blockSchedule);
      data.name = blockNames[data.id];
      data.selectedIrrigationTypes = irrigationTypes;
      irrigationTypes.forEach(function (irgType) {
        data[jsonKeys[irgType]] = data[jsonKeys[irgType]] && data[jsonKeys[irgType]].filter(function (d) {
          return Date.parse(d.start_date) >= Date.parse(dates[0].format()) && Date.parse(d.stop_date) <= Date.parse(dates[1].format());
        });
      });
      Object.keys(jsonKeys).forEach(function (key) {
        if (!irrigationTypes.includes(key)) {
          data[jsonKeys[key]] = [];
          data.selectedIrrigationTypes = irrigationTypes;
        }
      });
      onDataRetrieval({
        data_retrieved: data
      });
    }

    return data;
  });
  var names = filteredData.map(function (d) {
    return d.name;
  }).filter(function (d) {
    return d !== undefined;
  });
  return /*#__PURE__*/React.createElement(HighchartsSchedule, {
    chartData: filteredData,
    dates: dates,
    configChange: names.length ? loading : false,
    onClickChart: onClickChart
  });
};

IrrigationSchedule.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.any),
  dates: PropTypes.arrayOf(PropTypes.object),
  irrigationTypes: PropTypes.arrayOf(PropTypes.any),
  onDataRetrieval: PropTypes.func,
  onClickChart: PropTypes.func
};
IrrigationSchedule.defaultProps = {
  blocks: null,
  dates: null,
  irrigationTypes: null,
  onDataRetrieval: function onDataRetrieval() {
    return null;
  },
  onClickChart: null
};

var uaParser_min = createCommonjsModule(function (module, exports) {
  (function (window, undefined$1) {

    var LIBVERSION = "0.7.21",
        EMPTY = "",
        UNKNOWN = "?",
        FUNC_TYPE = "function",
        OBJ_TYPE = "object",
        STR_TYPE = "string",
        MAJOR = "major",
        MODEL = "model",
        NAME = "name",
        TYPE = "type",
        VENDOR = "vendor",
        VERSION = "version",
        ARCHITECTURE = "architecture",
        CONSOLE = "console",
        MOBILE = "mobile",
        TABLET = "tablet",
        SMARTTV = "smarttv",
        WEARABLE = "wearable",
        EMBEDDED = "embedded";
    var util = {
      extend: function extend(regexes, extensions) {
        var mergedRegexes = {};

        for (var i in regexes) {
          if (extensions[i] && extensions[i].length % 2 === 0) {
            mergedRegexes[i] = extensions[i].concat(regexes[i]);
          } else {
            mergedRegexes[i] = regexes[i];
          }
        }

        return mergedRegexes;
      },
      has: function has(str1, str2) {
        if (typeof str1 === "string") {
          return str2.toLowerCase().indexOf(str1.toLowerCase()) !== -1;
        } else {
          return false;
        }
      },
      lowerize: function lowerize(str) {
        return str.toLowerCase();
      },
      major: function major(version) {
        return typeof version === STR_TYPE ? version.replace(/[^\d\.]/g, "").split(".")[0] : undefined$1;
      },
      trim: function trim(str) {
        return str.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, "");
      }
    };
    var mapper = {
      rgx: function rgx(ua, arrays) {
        var i = 0,
            j,
            k,
            p,
            q,
            matches,
            match;

        while (i < arrays.length && !matches) {
          var regex = arrays[i],
              props = arrays[i + 1];
          j = k = 0;

          while (j < regex.length && !matches) {
            matches = regex[j++].exec(ua);

            if (!!matches) {
              for (p = 0; p < props.length; p++) {
                match = matches[++k];
                q = props[p];

                if (typeof q === OBJ_TYPE && q.length > 0) {
                  if (q.length == 2) {
                    if (typeof q[1] == FUNC_TYPE) {
                      this[q[0]] = q[1].call(this, match);
                    } else {
                      this[q[0]] = q[1];
                    }
                  } else if (q.length == 3) {
                    if (typeof q[1] === FUNC_TYPE && !(q[1].exec && q[1].test)) {
                      this[q[0]] = match ? q[1].call(this, match, q[2]) : undefined$1;
                    } else {
                      this[q[0]] = match ? match.replace(q[1], q[2]) : undefined$1;
                    }
                  } else if (q.length == 4) {
                    this[q[0]] = match ? q[3].call(this, match.replace(q[1], q[2])) : undefined$1;
                  }
                } else {
                  this[q] = match ? match : undefined$1;
                }
              }
            }
          }

          i += 2;
        }
      },
      str: function str(_str, map) {
        for (var i in map) {
          if (typeof map[i] === OBJ_TYPE && map[i].length > 0) {
            for (var j = 0; j < map[i].length; j++) {
              if (util.has(map[i][j], _str)) {
                return i === UNKNOWN ? undefined$1 : i;
              }
            }
          } else if (util.has(map[i], _str)) {
            return i === UNKNOWN ? undefined$1 : i;
          }
        }

        return _str;
      }
    };
    var maps = {
      browser: {
        oldsafari: {
          version: {
            "1.0": "/8",
            1.2: "/1",
            1.3: "/3",
            "2.0": "/412",
            "2.0.2": "/416",
            "2.0.3": "/417",
            "2.0.4": "/419",
            "?": "/"
          }
        }
      },
      device: {
        amazon: {
          model: {
            "Fire Phone": ["SD", "KF"]
          }
        },
        sprint: {
          model: {
            "Evo Shift 4G": "7373KT"
          },
          vendor: {
            HTC: "APA",
            Sprint: "Sprint"
          }
        }
      },
      os: {
        windows: {
          version: {
            ME: "4.90",
            "NT 3.11": "NT3.51",
            "NT 4.0": "NT4.0",
            2000: "NT 5.0",
            XP: ["NT 5.1", "NT 5.2"],
            Vista: "NT 6.0",
            7: "NT 6.1",
            8: "NT 6.2",
            8.1: "NT 6.3",
            10: ["NT 6.4", "NT 10.0"],
            RT: "ARM"
          }
        }
      }
    };
    var regexes = {
      browser: [[/(opera\smini)\/([\w\.-]+)/i, /(opera\s[mobiletab]+).+version\/([\w\.-]+)/i, /(opera).+version\/([\w\.]+)/i, /(opera)[\/\s]+([\w\.]+)/i], [NAME, VERSION], [/(opios)[\/\s]+([\w\.]+)/i], [[NAME, "Opera Mini"], VERSION], [/\s(opr)\/([\w\.]+)/i], [[NAME, "Opera"], VERSION], [/(kindle)\/([\w\.]+)/i, /(lunascape|maxthon|netfront|jasmine|blazer)[\/\s]?([\w\.]*)/i, /(avant\s|iemobile|slim)(?:browser)?[\/\s]?([\w\.]*)/i, /(bidubrowser|baidubrowser)[\/\s]?([\w\.]+)/i, /(?:ms|\()(ie)\s([\w\.]+)/i, /(rekonq)\/([\w\.]*)/i, /(chromium|flock|rockmelt|midori|epiphany|silk|skyfire|ovibrowser|bolt|iron|vivaldi|iridium|phantomjs|bowser|quark|qupzilla|falkon)\/([\w\.-]+)/i], [NAME, VERSION], [/(konqueror)\/([\w\.]+)/i], [[NAME, "Konqueror"], VERSION], [/(trident).+rv[:\s]([\w\.]+).+like\sgecko/i], [[NAME, "IE"], VERSION], [/(edge|edgios|edga|edg)\/((\d+)?[\w\.]+)/i], [[NAME, "Edge"], VERSION], [/(yabrowser)\/([\w\.]+)/i], [[NAME, "Yandex"], VERSION], [/(Avast)\/([\w\.]+)/i], [[NAME, "Avast Secure Browser"], VERSION], [/(AVG)\/([\w\.]+)/i], [[NAME, "AVG Secure Browser"], VERSION], [/(puffin)\/([\w\.]+)/i], [[NAME, "Puffin"], VERSION], [/(focus)\/([\w\.]+)/i], [[NAME, "Firefox Focus"], VERSION], [/(opt)\/([\w\.]+)/i], [[NAME, "Opera Touch"], VERSION], [/((?:[\s\/])uc?\s?browser|(?:juc.+)ucweb)[\/\s]?([\w\.]+)/i], [[NAME, "UCBrowser"], VERSION], [/(comodo_dragon)\/([\w\.]+)/i], [[NAME, /_/g, " "], VERSION], [/(windowswechat qbcore)\/([\w\.]+)/i], [[NAME, "WeChat(Win) Desktop"], VERSION], [/(micromessenger)\/([\w\.]+)/i], [[NAME, "WeChat"], VERSION], [/(brave)\/([\w\.]+)/i], [[NAME, "Brave"], VERSION], [/(qqbrowserlite)\/([\w\.]+)/i], [NAME, VERSION], [/(QQ)\/([\d\.]+)/i], [NAME, VERSION], [/m?(qqbrowser)[\/\s]?([\w\.]+)/i], [NAME, VERSION], [/(baiduboxapp)[\/\s]?([\w\.]+)/i], [NAME, VERSION], [/(2345Explorer)[\/\s]?([\w\.]+)/i], [NAME, VERSION], [/(MetaSr)[\/\s]?([\w\.]+)/i], [NAME], [/(LBBROWSER)/i], [NAME], [/xiaomi\/miuibrowser\/([\w\.]+)/i], [VERSION, [NAME, "MIUI Browser"]], [/;fbav\/([\w\.]+);/i], [VERSION, [NAME, "Facebook"]], [/safari\s(line)\/([\w\.]+)/i, /android.+(line)\/([\w\.]+)\/iab/i], [NAME, VERSION], [/headlesschrome(?:\/([\w\.]+)|\s)/i], [VERSION, [NAME, "Chrome Headless"]], [/\swv\).+(chrome)\/([\w\.]+)/i], [[NAME, /(.+)/, "$1 WebView"], VERSION], [/((?:oculus|samsung)browser)\/([\w\.]+)/i], [[NAME, /(.+(?:g|us))(.+)/, "$1 $2"], VERSION], [/android.+version\/([\w\.]+)\s+(?:mobile\s?safari|safari)*/i], [VERSION, [NAME, "Android Browser"]], [/(sailfishbrowser)\/([\w\.]+)/i], [[NAME, "Sailfish Browser"], VERSION], [/(chrome|omniweb|arora|[tizenoka]{5}\s?browser)\/v?([\w\.]+)/i], [NAME, VERSION], [/(dolfin)\/([\w\.]+)/i], [[NAME, "Dolphin"], VERSION], [/(qihu|qhbrowser|qihoobrowser|360browser)/i], [[NAME, "360 Browser"]], [/((?:android.+)crmo|crios)\/([\w\.]+)/i], [[NAME, "Chrome"], VERSION], [/(coast)\/([\w\.]+)/i], [[NAME, "Opera Coast"], VERSION], [/fxios\/([\w\.-]+)/i], [VERSION, [NAME, "Firefox"]], [/version\/([\w\.]+).+?mobile\/\w+\s(safari)/i], [VERSION, [NAME, "Mobile Safari"]], [/version\/([\w\.]+).+?(mobile\s?safari|safari)/i], [VERSION, NAME], [/webkit.+?(gsa)\/([\w\.]+).+?(mobile\s?safari|safari)(\/[\w\.]+)/i], [[NAME, "GSA"], VERSION], [/webkit.+?(mobile\s?safari|safari)(\/[\w\.]+)/i], [NAME, [VERSION, mapper.str, maps.browser.oldsafari.version]], [/(webkit|khtml)\/([\w\.]+)/i], [NAME, VERSION], [/(navigator|netscape)\/([\w\.-]+)/i], [[NAME, "Netscape"], VERSION], [/(swiftfox)/i, /(icedragon|iceweasel|camino|chimera|fennec|maemo\sbrowser|minimo|conkeror)[\/\s]?([\w\.\+]+)/i, /(firefox|seamonkey|k-meleon|icecat|iceape|firebird|phoenix|palemoon|basilisk|waterfox)\/([\w\.-]+)$/i, /(mozilla)\/([\w\.]+).+rv\:.+gecko\/\d+/i, /(polaris|lynx|dillo|icab|doris|amaya|w3m|netsurf|sleipnir)[\/\s]?([\w\.]+)/i, /(links)\s\(([\w\.]+)/i, /(gobrowser)\/?([\w\.]*)/i, /(ice\s?browser)\/v?([\w\._]+)/i, /(mosaic)[\/\s]([\w\.]+)/i], [NAME, VERSION]],
      cpu: [[/(?:(amd|x(?:(?:86|64)[_-])?|wow|win)64)[;\)]/i], [[ARCHITECTURE, "amd64"]], [/(ia32(?=;))/i], [[ARCHITECTURE, util.lowerize]], [/((?:i[346]|x)86)[;\)]/i], [[ARCHITECTURE, "ia32"]], [/windows\s(ce|mobile);\sppc;/i], [[ARCHITECTURE, "arm"]], [/((?:ppc|powerpc)(?:64)?)(?:\smac|;|\))/i], [[ARCHITECTURE, /ower/, "", util.lowerize]], [/(sun4\w)[;\)]/i], [[ARCHITECTURE, "sparc"]], [/((?:avr32|ia64(?=;))|68k(?=\))|arm(?:64|(?=v\d+[;l]))|(?=atmel\s)avr|(?:irix|mips|sparc)(?:64)?(?=;)|pa-risc)/i], [[ARCHITECTURE, util.lowerize]]],
      device: [[/\((ipad|playbook);[\w\s\),;-]+(rim|apple)/i], [MODEL, VENDOR, [TYPE, TABLET]], [/applecoremedia\/[\w\.]+ \((ipad)/], [MODEL, [VENDOR, "Apple"], [TYPE, TABLET]], [/(apple\s{0,1}tv)/i], [[MODEL, "Apple TV"], [VENDOR, "Apple"], [TYPE, SMARTTV]], [/(archos)\s(gamepad2?)/i, /(hp).+(touchpad)/i, /(hp).+(tablet)/i, /(kindle)\/([\w\.]+)/i, /\s(nook)[\w\s]+build\/(\w+)/i, /(dell)\s(strea[kpr\s\d]*[\dko])/i], [VENDOR, MODEL, [TYPE, TABLET]], [/(kf[A-z]+)\sbuild\/.+silk\//i], [MODEL, [VENDOR, "Amazon"], [TYPE, TABLET]], [/(sd|kf)[0349hijorstuw]+\sbuild\/.+silk\//i], [[MODEL, mapper.str, maps.device.amazon.model], [VENDOR, "Amazon"], [TYPE, MOBILE]], [/android.+aft([bms])\sbuild/i], [MODEL, [VENDOR, "Amazon"], [TYPE, SMARTTV]], [/\((ip[honed|\s\w*]+);.+(apple)/i], [MODEL, VENDOR, [TYPE, MOBILE]], [/\((ip[honed|\s\w*]+);/i], [MODEL, [VENDOR, "Apple"], [TYPE, MOBILE]], [/(blackberry)[\s-]?(\w+)/i, /(blackberry|benq|palm(?=\-)|sonyericsson|acer|asus|dell|meizu|motorola|polytron)[\s_-]?([\w-]*)/i, /(hp)\s([\w\s]+\w)/i, /(asus)-?(\w+)/i], [VENDOR, MODEL, [TYPE, MOBILE]], [/\(bb10;\s(\w+)/i], [MODEL, [VENDOR, "BlackBerry"], [TYPE, MOBILE]], [/android.+(transfo[prime\s]{4,10}\s\w+|eeepc|slider\s\w+|nexus 7|padfone|p00c)/i], [MODEL, [VENDOR, "Asus"], [TYPE, TABLET]], [/(sony)\s(tablet\s[ps])\sbuild\//i, /(sony)?(?:sgp.+)\sbuild\//i], [[VENDOR, "Sony"], [MODEL, "Xperia Tablet"], [TYPE, TABLET]], [/android.+\s([c-g]\d{4}|so[-l]\w+)(?=\sbuild\/|\).+chrome\/(?![1-6]{0,1}\d\.))/i], [MODEL, [VENDOR, "Sony"], [TYPE, MOBILE]], [/\s(ouya)\s/i, /(nintendo)\s([wids3u]+)/i], [VENDOR, MODEL, [TYPE, CONSOLE]], [/android.+;\s(shield)\sbuild/i], [MODEL, [VENDOR, "Nvidia"], [TYPE, CONSOLE]], [/(playstation\s[34portablevi]+)/i], [MODEL, [VENDOR, "Sony"], [TYPE, CONSOLE]], [/(sprint\s(\w+))/i], [[VENDOR, mapper.str, maps.device.sprint.vendor], [MODEL, mapper.str, maps.device.sprint.model], [TYPE, MOBILE]], [/(htc)[;_\s-]+([\w\s]+(?=\)|\sbuild)|\w+)/i, /(zte)-(\w*)/i, /(alcatel|geeksphone|nexian|panasonic|(?=;\s)sony)[_\s-]?([\w-]*)/i], [VENDOR, [MODEL, /_/g, " "], [TYPE, MOBILE]], [/(nexus\s9)/i], [MODEL, [VENDOR, "HTC"], [TYPE, TABLET]], [/d\/huawei([\w\s-]+)[;\)]/i, /(nexus\s6p|vog-l29|ane-lx1|eml-l29)/i], [MODEL, [VENDOR, "Huawei"], [TYPE, MOBILE]], [/android.+(bah2?-a?[lw]\d{2})/i], [MODEL, [VENDOR, "Huawei"], [TYPE, TABLET]], [/(microsoft);\s(lumia[\s\w]+)/i], [VENDOR, MODEL, [TYPE, MOBILE]], [/[\s\(;](xbox(?:\sone)?)[\s\);]/i], [MODEL, [VENDOR, "Microsoft"], [TYPE, CONSOLE]], [/(kin\.[onetw]{3})/i], [[MODEL, /\./g, " "], [VENDOR, "Microsoft"], [TYPE, MOBILE]], [/\s(milestone|droid(?:[2-4x]|\s(?:bionic|x2|pro|razr))?:?(\s4g)?)[\w\s]+build\//i, /mot[\s-]?(\w*)/i, /(XT\d{3,4}) build\//i, /(nexus\s6)/i], [MODEL, [VENDOR, "Motorola"], [TYPE, MOBILE]], [/android.+\s(mz60\d|xoom[\s2]{0,2})\sbuild\//i], [MODEL, [VENDOR, "Motorola"], [TYPE, TABLET]], [/hbbtv\/\d+\.\d+\.\d+\s+\([\w\s]*;\s*(\w[^;]*);([^;]*)/i], [[VENDOR, util.trim], [MODEL, util.trim], [TYPE, SMARTTV]], [/hbbtv.+maple;(\d+)/i], [[MODEL, /^/, "SmartTV"], [VENDOR, "Samsung"], [TYPE, SMARTTV]], [/\(dtv[\);].+(aquos)/i], [MODEL, [VENDOR, "Sharp"], [TYPE, SMARTTV]], [/android.+((sch-i[89]0\d|shw-m380s|gt-p\d{4}|gt-n\d+|sgh-t8[56]9|nexus 10))/i, /((SM-T\w+))/i], [[VENDOR, "Samsung"], MODEL, [TYPE, TABLET]], [/smart-tv.+(samsung)/i], [VENDOR, [TYPE, SMARTTV], MODEL], [/((s[cgp]h-\w+|gt-\w+|galaxy\snexus|sm-\w[\w\d]+))/i, /(sam[sung]*)[\s-]*(\w+-?[\w-]*)/i, /sec-((sgh\w+))/i], [[VENDOR, "Samsung"], MODEL, [TYPE, MOBILE]], [/sie-(\w*)/i], [MODEL, [VENDOR, "Siemens"], [TYPE, MOBILE]], [/(maemo|nokia).*(n900|lumia\s\d+)/i, /(nokia)[\s_-]?([\w-]*)/i], [[VENDOR, "Nokia"], MODEL, [TYPE, MOBILE]], [/android[x\d\.\s;]+\s([ab][1-7]\-?[0178a]\d\d?)/i], [MODEL, [VENDOR, "Acer"], [TYPE, TABLET]], [/android.+([vl]k\-?\d{3})\s+build/i], [MODEL, [VENDOR, "LG"], [TYPE, TABLET]], [/android\s3\.[\s\w;-]{10}(lg?)-([06cv9]{3,4})/i], [[VENDOR, "LG"], MODEL, [TYPE, TABLET]], [/(lg) netcast\.tv/i], [VENDOR, MODEL, [TYPE, SMARTTV]], [/(nexus\s[45])/i, /lg[e;\s\/-]+(\w*)/i, /android.+lg(\-?[\d\w]+)\s+build/i], [MODEL, [VENDOR, "LG"], [TYPE, MOBILE]], [/(lenovo)\s?(s(?:5000|6000)(?:[\w-]+)|tab(?:[\s\w]+))/i], [VENDOR, MODEL, [TYPE, TABLET]], [/android.+(ideatab[a-z0-9\-\s]+)/i], [MODEL, [VENDOR, "Lenovo"], [TYPE, TABLET]], [/(lenovo)[_\s-]?([\w-]+)/i], [VENDOR, MODEL, [TYPE, MOBILE]], [/linux;.+((jolla));/i], [VENDOR, MODEL, [TYPE, MOBILE]], [/((pebble))app\/[\d\.]+\s/i], [VENDOR, MODEL, [TYPE, WEARABLE]], [/android.+;\s(oppo)\s?([\w\s]+)\sbuild/i], [VENDOR, MODEL, [TYPE, MOBILE]], [/crkey/i], [[MODEL, "Chromecast"], [VENDOR, "Google"], [TYPE, SMARTTV]], [/android.+;\s(glass)\s\d/i], [MODEL, [VENDOR, "Google"], [TYPE, WEARABLE]], [/android.+;\s(pixel c)[\s)]/i], [MODEL, [VENDOR, "Google"], [TYPE, TABLET]], [/android.+;\s(pixel( [23])?( xl)?)[\s)]/i], [MODEL, [VENDOR, "Google"], [TYPE, MOBILE]], [/android.+;\s(\w+)\s+build\/hm\1/i, /android.+(hm[\s\-_]*note?[\s_]*(?:\d\w)?)\s+build/i, /android.+(mi[\s\-_]*(?:a\d|one|one[\s_]plus|note lte)?[\s_]*(?:\d?\w?)[\s_]*(?:plus)?)\s+build/i, /android.+(redmi[\s\-_]*(?:note)?(?:[\s_]*[\w\s]+))\s+build/i], [[MODEL, /_/g, " "], [VENDOR, "Xiaomi"], [TYPE, MOBILE]], [/android.+(mi[\s\-_]*(?:pad)(?:[\s_]*[\w\s]+))\s+build/i], [[MODEL, /_/g, " "], [VENDOR, "Xiaomi"], [TYPE, TABLET]], [/android.+;\s(m[1-5]\snote)\sbuild/i], [MODEL, [VENDOR, "Meizu"], [TYPE, MOBILE]], [/(mz)-([\w-]{2,})/i], [[VENDOR, "Meizu"], MODEL, [TYPE, MOBILE]], [/android.+a000(1)\s+build/i, /android.+oneplus\s(a\d{4})[\s)]/i], [MODEL, [VENDOR, "OnePlus"], [TYPE, MOBILE]], [/android.+[;\/]\s*(RCT[\d\w]+)\s+build/i], [MODEL, [VENDOR, "RCA"], [TYPE, TABLET]], [/android.+[;\/\s]+(Venue[\d\s]{2,7})\s+build/i], [MODEL, [VENDOR, "Dell"], [TYPE, TABLET]], [/android.+[;\/]\s*(Q[T|M][\d\w]+)\s+build/i], [MODEL, [VENDOR, "Verizon"], [TYPE, TABLET]], [/android.+[;\/]\s+(Barnes[&\s]+Noble\s+|BN[RT])(V?.*)\s+build/i], [[VENDOR, "Barnes & Noble"], MODEL, [TYPE, TABLET]], [/android.+[;\/]\s+(TM\d{3}.*\b)\s+build/i], [MODEL, [VENDOR, "NuVision"], [TYPE, TABLET]], [/android.+;\s(k88)\sbuild/i], [MODEL, [VENDOR, "ZTE"], [TYPE, TABLET]], [/android.+[;\/]\s*(gen\d{3})\s+build.*49h/i], [MODEL, [VENDOR, "Swiss"], [TYPE, MOBILE]], [/android.+[;\/]\s*(zur\d{3})\s+build/i], [MODEL, [VENDOR, "Swiss"], [TYPE, TABLET]], [/android.+[;\/]\s*((Zeki)?TB.*\b)\s+build/i], [MODEL, [VENDOR, "Zeki"], [TYPE, TABLET]], [/(android).+[;\/]\s+([YR]\d{2})\s+build/i, /android.+[;\/]\s+(Dragon[\-\s]+Touch\s+|DT)(\w{5})\sbuild/i], [[VENDOR, "Dragon Touch"], MODEL, [TYPE, TABLET]], [/android.+[;\/]\s*(NS-?\w{0,9})\sbuild/i], [MODEL, [VENDOR, "Insignia"], [TYPE, TABLET]], [/android.+[;\/]\s*((NX|Next)-?\w{0,9})\s+build/i], [MODEL, [VENDOR, "NextBook"], [TYPE, TABLET]], [/android.+[;\/]\s*(Xtreme\_)?(V(1[045]|2[015]|30|40|60|7[05]|90))\s+build/i], [[VENDOR, "Voice"], MODEL, [TYPE, MOBILE]], [/android.+[;\/]\s*(LVTEL\-)?(V1[12])\s+build/i], [[VENDOR, "LvTel"], MODEL, [TYPE, MOBILE]], [/android.+;\s(PH-1)\s/i], [MODEL, [VENDOR, "Essential"], [TYPE, MOBILE]], [/android.+[;\/]\s*(V(100MD|700NA|7011|917G).*\b)\s+build/i], [MODEL, [VENDOR, "Envizen"], [TYPE, TABLET]], [/android.+[;\/]\s*(Le[\s\-]+Pan)[\s\-]+(\w{1,9})\s+build/i], [VENDOR, MODEL, [TYPE, TABLET]], [/android.+[;\/]\s*(Trio[\s\-]*.*)\s+build/i], [MODEL, [VENDOR, "MachSpeed"], [TYPE, TABLET]], [/android.+[;\/]\s*(Trinity)[\-\s]*(T\d{3})\s+build/i], [VENDOR, MODEL, [TYPE, TABLET]], [/android.+[;\/]\s*TU_(1491)\s+build/i], [MODEL, [VENDOR, "Rotor"], [TYPE, TABLET]], [/android.+(KS(.+))\s+build/i], [MODEL, [VENDOR, "Amazon"], [TYPE, TABLET]], [/android.+(Gigaset)[\s\-]+(Q\w{1,9})\s+build/i], [VENDOR, MODEL, [TYPE, TABLET]], [/\s(tablet|tab)[;\/]/i, /\s(mobile)(?:[;\/]|\ssafari)/i], [[TYPE, util.lowerize], VENDOR, MODEL], [/[\s\/\(](smart-?tv)[;\)]/i], [[TYPE, SMARTTV]], [/(android[\w\.\s\-]{0,9});.+build/i], [MODEL, [VENDOR, "Generic"]]],
      engine: [[/windows.+\sedge\/([\w\.]+)/i], [VERSION, [NAME, "EdgeHTML"]], [/webkit\/537\.36.+chrome\/(?!27)([\w\.]+)/i], [VERSION, [NAME, "Blink"]], [/(presto)\/([\w\.]+)/i, /(webkit|trident|netfront|netsurf|amaya|lynx|w3m|goanna)\/([\w\.]+)/i, /(khtml|tasman|links)[\/\s]\(?([\w\.]+)/i, /(icab)[\/\s]([23]\.[\d\.]+)/i], [NAME, VERSION], [/rv\:([\w\.]{1,9}).+(gecko)/i], [VERSION, NAME]],
      os: [[/microsoft\s(windows)\s(vista|xp)/i], [NAME, VERSION], [/(windows)\snt\s6\.2;\s(arm)/i, /(windows\sphone(?:\sos)*)[\s\/]?([\d\.\s\w]*)/i, /(windows\smobile|windows)[\s\/]?([ntce\d\.\s]+\w)/i], [NAME, [VERSION, mapper.str, maps.os.windows.version]], [/(win(?=3|9|n)|win\s9x\s)([nt\d\.]+)/i], [[NAME, "Windows"], [VERSION, mapper.str, maps.os.windows.version]], [/\((bb)(10);/i], [[NAME, "BlackBerry"], VERSION], [/(blackberry)\w*\/?([\w\.]*)/i, /(tizen|kaios)[\/\s]([\w\.]+)/i, /(android|webos|palm\sos|qnx|bada|rim\stablet\sos|meego|sailfish|contiki)[\/\s-]?([\w\.]*)/i], [NAME, VERSION], [/(symbian\s?os|symbos|s60(?=;))[\/\s-]?([\w\.]*)/i], [[NAME, "Symbian"], VERSION], [/\((series40);/i], [NAME], [/mozilla.+\(mobile;.+gecko.+firefox/i], [[NAME, "Firefox OS"], VERSION], [/(nintendo|playstation)\s([wids34portablevu]+)/i, /(mint)[\/\s\(]?(\w*)/i, /(mageia|vectorlinux)[;\s]/i, /(joli|[kxln]?ubuntu|debian|suse|opensuse|gentoo|(?=\s)arch|slackware|fedora|mandriva|centos|pclinuxos|redhat|zenwalk|linpus)[\/\s-]?(?!chrom)([\w\.-]*)/i, /(hurd|linux)\s?([\w\.]*)/i, /(gnu)\s?([\w\.]*)/i], [NAME, VERSION], [/(cros)\s[\w]+\s([\w\.]+\w)/i], [[NAME, "Chromium OS"], VERSION], [/(sunos)\s?([\w\.\d]*)/i], [[NAME, "Solaris"], VERSION], [/\s([frentopc-]{0,4}bsd|dragonfly)\s?([\w\.]*)/i], [NAME, VERSION], [/(haiku)\s(\w+)/i], [NAME, VERSION], [/cfnetwork\/.+darwin/i, /ip[honead]{2,4}(?:.*os\s([\w]+)\slike\smac|;\sopera)/i], [[VERSION, /_/g, "."], [NAME, "iOS"]], [/(mac\sos\sx)\s?([\w\s\.]*)/i, /(macintosh|mac(?=_powerpc)\s)/i], [[NAME, "Mac OS"], [VERSION, /_/g, "."]], [/((?:open)?solaris)[\/\s-]?([\w\.]*)/i, /(aix)\s((\d)(?=\.|\)|\s)[\w\.])*/i, /(plan\s9|minix|beos|os\/2|amigaos|morphos|risc\sos|openvms|fuchsia)/i, /(unix)\s?([\w\.]*)/i], [NAME, VERSION]]
    };

    var UAParser = function UAParser(uastring, extensions) {
      if (typeof uastring === "object") {
        extensions = uastring;
        uastring = undefined$1;
      }

      if (!(this instanceof UAParser)) {
        return new UAParser(uastring, extensions).getResult();
      }

      var ua = uastring || (window && window.navigator && window.navigator.userAgent ? window.navigator.userAgent : EMPTY);
      var rgxmap = extensions ? util.extend(regexes, extensions) : regexes;

      this.getBrowser = function () {
        var browser = {
          name: undefined$1,
          version: undefined$1
        };
        mapper.rgx.call(browser, ua, rgxmap.browser);
        browser.major = util.major(browser.version);
        return browser;
      };

      this.getCPU = function () {
        var cpu = {
          architecture: undefined$1
        };
        mapper.rgx.call(cpu, ua, rgxmap.cpu);
        return cpu;
      };

      this.getDevice = function () {
        var device = {
          vendor: undefined$1,
          model: undefined$1,
          type: undefined$1
        };
        mapper.rgx.call(device, ua, rgxmap.device);
        return device;
      };

      this.getEngine = function () {
        var engine = {
          name: undefined$1,
          version: undefined$1
        };
        mapper.rgx.call(engine, ua, rgxmap.engine);
        return engine;
      };

      this.getOS = function () {
        var os = {
          name: undefined$1,
          version: undefined$1
        };
        mapper.rgx.call(os, ua, rgxmap.os);
        return os;
      };

      this.getResult = function () {
        return {
          ua: this.getUA(),
          browser: this.getBrowser(),
          engine: this.getEngine(),
          os: this.getOS(),
          device: this.getDevice(),
          cpu: this.getCPU()
        };
      };

      this.getUA = function () {
        return ua;
      };

      this.setUA = function (uastring) {
        ua = uastring;
        return this;
      };

      return this;
    };

    UAParser.VERSION = LIBVERSION;
    UAParser.BROWSER = {
      NAME: NAME,
      MAJOR: MAJOR,
      VERSION: VERSION
    };
    UAParser.CPU = {
      ARCHITECTURE: ARCHITECTURE
    };
    UAParser.DEVICE = {
      MODEL: MODEL,
      VENDOR: VENDOR,
      TYPE: TYPE,
      CONSOLE: CONSOLE,
      MOBILE: MOBILE,
      SMARTTV: SMARTTV,
      TABLET: TABLET,
      WEARABLE: WEARABLE,
      EMBEDDED: EMBEDDED
    };
    UAParser.ENGINE = {
      NAME: NAME,
      VERSION: VERSION
    };
    UAParser.OS = {
      NAME: NAME,
      VERSION: VERSION
    };

    {
      if ( module.exports) {
        exports = module.exports = UAParser;
      }

      exports.UAParser = UAParser;
    }

    var $ = window && (window.jQuery || window.Zepto);

    if ($ && !$.ua) {
      var parser = new UAParser();
      $.ua = parser.getResult();

      $.ua.get = function () {
        return parser.getUA();
      };

      $.ua.set = function (uastring) {
        parser.setUA(uastring);
        var result = parser.getResult();

        for (var prop in result) {
          $.ua[prop] = result[prop];
        }
      };
    }
  })(typeof window === "object" ? window : commonjsGlobal);
});

var main = createCommonjsModule(function (module, exports) {

  Object.defineProperty(exports, '__esModule', {
    value: true
  });

  function _interopDefault(ex) {
    return ex && typeof ex === 'object' && 'default' in ex ? ex['default'] : ex;
  }

  var React__default = _interopDefault(React);

  var UA = new uaParser_min();
  var browser = UA.getBrowser();
  var cpu = UA.getCPU();
  var device = UA.getDevice();
  var engine = UA.getEngine();
  var os = UA.getOS();
  var ua = UA.getUA();

  var setDefaults = function setDefaults(p) {
    var d = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'none';
    return p ? p : d;
  };

  var getNavigatorInstance = function getNavigatorInstance() {
    if (typeof window !== 'undefined') {
      if (window.navigator || navigator) {
        return window.navigator || navigator;
      }
    }

    return false;
  };

  var isIOS13Check = function isIOS13Check(type) {
    var nav = getNavigatorInstance();
    return nav && nav.platform && (nav.platform.indexOf(type) !== -1 || nav.platform === 'MacIntel' && nav.maxTouchPoints > 1 && !window.MSStream);
  };

  function _typeof(obj) {
    if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
      _typeof = function _typeof(obj) {
        return typeof obj;
      };
    } else {
      _typeof = function _typeof(obj) {
        return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
      };
    }

    return _typeof(obj);
  }

  function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
      throw new TypeError("Cannot call a class as a function");
    }
  }

  function _defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
  }

  function _defineProperty(obj, key, value) {
    if (key in obj) {
      Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
      });
    } else {
      obj[key] = value;
    }

    return obj;
  }

  function _extends() {
    _extends = Object.assign || function (target) {
      for (var i = 1; i < arguments.length; i++) {
        var source = arguments[i];

        for (var key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            target[key] = source[key];
          }
        }
      }

      return target;
    };

    return _extends.apply(this, arguments);
  }

  function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);

    if (Object.getOwnPropertySymbols) {
      var symbols = Object.getOwnPropertySymbols(object);
      if (enumerableOnly) symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
      keys.push.apply(keys, symbols);
    }

    return keys;
  }

  function _objectSpread2(target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i] != null ? arguments[i] : {};

      if (i % 2) {
        ownKeys(source, true).forEach(function (key) {
          _defineProperty(target, key, source[key]);
        });
      } else if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
      } else {
        ownKeys(source).forEach(function (key) {
          Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
      }
    }

    return target;
  }

  function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
      throw new TypeError("Super expression must either be null or a function");
    }

    subClass.prototype = Object.create(superClass && superClass.prototype, {
      constructor: {
        value: subClass,
        writable: true,
        configurable: true
      }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
  }

  function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
      return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
  }

  function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
      o.__proto__ = p;
      return o;
    };

    return _setPrototypeOf(o, p);
  }

  function _assertThisInitialized(self) {
    if (self === void 0) {
      throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }

    return self;
  }

  function _possibleConstructorReturn(self, call) {
    if (call && (typeof call === "object" || typeof call === "function")) {
      return call;
    }

    return _assertThisInitialized(self);
  }

  var DEVICE_TYPES = {
    MOBILE: 'mobile',
    TABLET: 'tablet',
    SMART_TV: 'smarttv',
    CONSOLE: 'console',
    WEARABLE: 'wearable',
    BROWSER: undefined
  };
  var BROWSER_TYPES = {
    CHROME: 'Chrome',
    FIREFOX: "Firefox",
    OPERA: "Opera",
    YANDEX: "Yandex",
    SAFARI: "Safari",
    INTERNET_EXPLORER: "Internet Explorer",
    EDGE: "Edge",
    CHROMIUM: "Chromium",
    IE: 'IE',
    MOBILE_SAFARI: "Mobile Safari",
    EDGE_CHROMIUM: "Edge Chromium"
  };
  var OS_TYPES = {
    IOS: 'iOS',
    ANDROID: "Android",
    WINDOWS_PHONE: "Windows Phone",
    WINDOWS: 'Windows',
    MAC_OS: 'Mac OS'
  };
  var initialData = {
    isMobile: false,
    isTablet: false,
    isBrowser: false,
    isSmartTV: false,
    isConsole: false,
    isWearable: false
  };

  var checkType = function checkType(type) {
    switch (type) {
      case DEVICE_TYPES.MOBILE:
        return {
          isMobile: true
        };

      case DEVICE_TYPES.TABLET:
        return {
          isTablet: true
        };

      case DEVICE_TYPES.SMART_TV:
        return {
          isSmartTV: true
        };

      case DEVICE_TYPES.CONSOLE:
        return {
          isConsole: true
        };

      case DEVICE_TYPES.WEARABLE:
        return {
          isWearable: true
        };

      case DEVICE_TYPES.BROWSER:
        return {
          isBrowser: true
        };

      default:
        return initialData;
    }
  };

  var broPayload = function broPayload(isBrowser, browser, engine, os, ua) {
    return {
      isBrowser: isBrowser,
      browserMajorVersion: setDefaults(browser.major),
      browserFullVersion: setDefaults(browser.version),
      browserName: setDefaults(browser.name),
      engineName: setDefaults(engine.name),
      engineVersion: setDefaults(engine.version),
      osName: setDefaults(os.name),
      osVersion: setDefaults(os.version),
      userAgent: setDefaults(ua)
    };
  };

  var mobilePayload = function mobilePayload(type, device, os, ua) {
    return _objectSpread2({}, type, {
      vendor: setDefaults(device.vendor),
      model: setDefaults(device.model),
      os: setDefaults(os.name),
      osVersion: setDefaults(os.version),
      ua: setDefaults(ua)
    });
  };

  var stvPayload = function stvPayload(isSmartTV, engine, os, ua) {
    return {
      isSmartTV: isSmartTV,
      engineName: setDefaults(engine.name),
      engineVersion: setDefaults(engine.version),
      osName: setDefaults(os.name),
      osVersion: setDefaults(os.version),
      userAgent: setDefaults(ua)
    };
  };

  var consolePayload = function consolePayload(isConsole, engine, os, ua) {
    return {
      isConsole: isConsole,
      engineName: setDefaults(engine.name),
      engineVersion: setDefaults(engine.version),
      osName: setDefaults(os.name),
      osVersion: setDefaults(os.version),
      userAgent: setDefaults(ua)
    };
  };

  var wearPayload = function wearPayload(isWearable, engine, os, ua) {
    return {
      isWearable: isWearable,
      engineName: setDefaults(engine.name),
      engineVersion: setDefaults(engine.version),
      osName: setDefaults(os.name),
      osVersion: setDefaults(os.version),
      userAgent: setDefaults(ua)
    };
  };

  var type = checkType(device.type);

  function deviceDetect() {
    var isBrowser = type.isBrowser,
        isMobile = type.isMobile,
        isTablet = type.isTablet,
        isSmartTV = type.isSmartTV,
        isConsole = type.isConsole,
        isWearable = type.isWearable;

    if (isBrowser) {
      return broPayload(isBrowser, browser, engine, os, ua);
    }

    if (isSmartTV) {
      return stvPayload(isSmartTV, engine, os, ua);
    }

    if (isConsole) {
      return consolePayload(isConsole, engine, os, ua);
    }

    if (isMobile) {
      return mobilePayload(type, device, os, ua);
    }

    if (isTablet) {
      return mobilePayload(type, device, os, ua);
    }

    if (isWearable) {
      return wearPayload(isWearable, engine, os, ua);
    }
  }

  var isMobileType = function isMobileType() {
    return device.type === DEVICE_TYPES.MOBILE;
  };

  var isTabletType = function isTabletType() {
    return device.type === DEVICE_TYPES.TABLET;
  };

  var isMobileAndTabletType = function isMobileAndTabletType() {
    switch (device.type) {
      case DEVICE_TYPES.MOBILE:
      case DEVICE_TYPES.TABLET:
        return true;

      default:
        return false;
    }
  };

  var isEdgeChromiumType = function isEdgeChromiumType() {
    if (os.name === OS_TYPES.WINDOWS && os.version === '10') {
      return typeof ua === 'string' && ua.indexOf('Edg/') !== -1;
    }

    return false;
  };

  var isSmartTVType = function isSmartTVType() {
    return device.type === DEVICE_TYPES.SMART_TV;
  };

  var isBrowserType = function isBrowserType() {
    return device.type === DEVICE_TYPES.BROWSER;
  };

  var isWearableType = function isWearableType() {
    return device.type === DEVICE_TYPES.WEARABLE;
  };

  var isConsoleType = function isConsoleType() {
    return device.type === DEVICE_TYPES.CONSOLE;
  };

  var isAndroidType = function isAndroidType() {
    return os.name === OS_TYPES.ANDROID;
  };

  var isWindowsType = function isWindowsType() {
    return os.name === OS_TYPES.WINDOWS;
  };

  var isMacOsType = function isMacOsType() {
    return os.name === OS_TYPES.MAC_OS;
  };

  var isWinPhoneType = function isWinPhoneType() {
    return os.name === OS_TYPES.WINDOWS_PHONE;
  };

  var isIOSType = function isIOSType() {
    return os.name === OS_TYPES.IOS;
  };

  var isChromeType = function isChromeType() {
    return browser.name === BROWSER_TYPES.CHROME;
  };

  var isFirefoxType = function isFirefoxType() {
    return browser.name === BROWSER_TYPES.FIREFOX;
  };

  var isChromiumType = function isChromiumType() {
    return browser.name === BROWSER_TYPES.CHROMIUM;
  };

  var isEdgeType = function isEdgeType() {
    return browser.name === BROWSER_TYPES.EDGE;
  };

  var isYandexType = function isYandexType() {
    return browser.name === BROWSER_TYPES.YANDEX;
  };

  var isSafariType = function isSafariType() {
    return browser.name === BROWSER_TYPES.SAFARI || browser.name === BROWSER_TYPES.MOBILE_SAFARI;
  };

  var isMobileSafariType = function isMobileSafariType() {
    return browser.name === BROWSER_TYPES.MOBILE_SAFARI;
  };

  var isOperaType = function isOperaType() {
    return browser.name === BROWSER_TYPES.OPERA;
  };

  var isIEType = function isIEType() {
    return browser.name === BROWSER_TYPES.INTERNET_EXPLORER || browser.name === BROWSER_TYPES.IE;
  };

  var isElectronType = function isElectronType() {
    var nav = getNavigatorInstance();
    var ua = nav && nav.userAgent.toLowerCase();
    return typeof ua === 'string' ? /electron/.test(ua) : false;
  };

  var getIOS13 = function getIOS13() {
    var nav = getNavigatorInstance();
    return nav && (/iPad|iPhone|iPod/.test(nav.platform) || nav.platform === 'MacIntel' && nav.maxTouchPoints > 1) && !window.MSStream;
  };

  var getIPad13 = function getIPad13() {
    return isIOS13Check('iPad');
  };

  var getIphone13 = function getIphone13() {
    return isIOS13Check('iPhone');
  };

  var getIPod13 = function getIPod13() {
    return isIOS13Check('iPod');
  };

  var getBrowserFullVersion = function getBrowserFullVersion() {
    return setDefaults(browser.version);
  };

  var getBrowserVersion = function getBrowserVersion() {
    return setDefaults(browser.major);
  };

  var getOsVersion = function getOsVersion() {
    return setDefaults(os.version);
  };

  var getOsName = function getOsName() {
    return setDefaults(os.name);
  };

  var getBrowserName = function getBrowserName() {
    return setDefaults(browser.name);
  };

  var getMobileVendor = function getMobileVendor() {
    return setDefaults(device.vendor);
  };

  var getMobileModel = function getMobileModel() {
    return setDefaults(device.model);
  };

  var getEngineName = function getEngineName() {
    return setDefaults(engine.name);
  };

  var getEngineVersion = function getEngineVersion() {
    return setDefaults(engine.version);
  };

  var getUseragent = function getUseragent() {
    return setDefaults(ua);
  };

  var getDeviceType = function getDeviceType() {
    return setDefaults(device.type, 'browser');
  };

  var isSmartTV = isSmartTVType();
  var isConsole = isConsoleType();
  var isWearable = isWearableType();
  var isMobileSafari = isMobileSafariType() || getIPad13();
  var isChromium = isChromiumType();
  var isMobile = isMobileAndTabletType() || getIPad13();
  var isMobileOnly = isMobileType();
  var isTablet = isTabletType() || getIPad13();
  var isBrowser = isBrowserType();
  var isAndroid = isAndroidType();
  var isWinPhone = isWinPhoneType();
  var isIOS = isIOSType() || getIPad13();
  var isChrome = isChromeType();
  var isFirefox = isFirefoxType();
  var isSafari = isSafariType();
  var isOpera = isOperaType();
  var isIE = isIEType();
  var osVersion = getOsVersion();
  var osName = getOsName();
  var fullBrowserVersion = getBrowserFullVersion();
  var browserVersion = getBrowserVersion();
  var browserName = getBrowserName();
  var mobileVendor = getMobileVendor();
  var mobileModel = getMobileModel();
  var engineName = getEngineName();
  var engineVersion = getEngineVersion();
  var getUA = getUseragent();
  var isEdge = isEdgeType() || isEdgeChromiumType();
  var isYandex = isYandexType();
  var deviceType = getDeviceType();
  var isIOS13 = getIOS13();
  var isIPad13 = getIPad13();
  var isIPhone13 = getIphone13();
  var isIPod13 = getIPod13();
  var isElectron = isElectronType();
  var isEdgeChromium = isEdgeChromiumType();
  var isLegacyEdge = isEdgeType();
  var isWindows = isWindowsType();
  var isMacOs = isMacOsType();

  var AndroidView = function AndroidView(_ref) {
    var renderWithFragment = _ref.renderWithFragment,
        children = _ref.children,
        viewClassName = _ref.viewClassName,
        style = _ref.style;
    return isAndroid ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var BrowserView = function BrowserView(_ref2) {
    var renderWithFragment = _ref2.renderWithFragment,
        children = _ref2.children,
        viewClassName = _ref2.viewClassName,
        style = _ref2.style;
    return isBrowser ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var IEView = function IEView(_ref3) {
    var renderWithFragment = _ref3.renderWithFragment,
        children = _ref3.children,
        viewClassName = _ref3.viewClassName,
        style = _ref3.style;
    return isIE ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var IOSView = function IOSView(_ref4) {
    var renderWithFragment = _ref4.renderWithFragment,
        children = _ref4.children,
        viewClassName = _ref4.viewClassName,
        style = _ref4.style;
    return isIOS ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var MobileView = function MobileView(_ref5) {
    var renderWithFragment = _ref5.renderWithFragment,
        children = _ref5.children,
        viewClassName = _ref5.viewClassName,
        style = _ref5.style;
    return isMobile ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var TabletView = function TabletView(_ref6) {
    var renderWithFragment = _ref6.renderWithFragment,
        children = _ref6.children,
        viewClassName = _ref6.viewClassName,
        style = _ref6.style;
    return isTablet ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var WinPhoneView = function WinPhoneView(_ref7) {
    var renderWithFragment = _ref7.renderWithFragment,
        children = _ref7.children,
        viewClassName = _ref7.viewClassName,
        style = _ref7.style;
    return isWinPhone ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var MobileOnlyView = function MobileOnlyView(_ref8) {
    var renderWithFragment = _ref8.renderWithFragment,
        children = _ref8.children,
        viewClassName = _ref8.viewClassName,
        style = _ref8.style;
    return isMobileOnly ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var SmartTVView = function SmartTVView(_ref9) {
    var renderWithFragment = _ref9.renderWithFragment,
        children = _ref9.children,
        viewClassName = _ref9.viewClassName,
        style = _ref9.style;
    return isSmartTV ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var ConsoleView = function ConsoleView(_ref10) {
    var renderWithFragment = _ref10.renderWithFragment,
        children = _ref10.children,
        viewClassName = _ref10.viewClassName,
        style = _ref10.style;
    return isConsole ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var WearableView = function WearableView(_ref11) {
    var renderWithFragment = _ref11.renderWithFragment,
        children = _ref11.children,
        viewClassName = _ref11.viewClassName,
        style = _ref11.style;
    return isWearable ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  var CustomView = function CustomView(_ref12) {
    var renderWithFragment = _ref12.renderWithFragment,
        children = _ref12.children,
        viewClassName = _ref12.viewClassName,
        style = _ref12.style,
        condition = _ref12.condition;
    return condition ? renderWithFragment ? React__default.createElement(React.Fragment, null, children) : React__default.createElement("div", {
      className: viewClassName,
      style: style
    }, children) : null;
  };

  function withOrientationChange(WrappedComponent) {
    return /*#__PURE__*/function (_React$Component) {
      _inherits(_class, _React$Component);

      function _class(props) {
        var _this;

        _classCallCheck(this, _class);

        _this = _possibleConstructorReturn(this, _getPrototypeOf(_class).call(this, props));
        _this.isEventListenerAdded = false;
        _this.handleOrientationChange = _this.handleOrientationChange.bind(_assertThisInitialized(_this));
        _this.onOrientationChange = _this.onOrientationChange.bind(_assertThisInitialized(_this));
        _this.onPageLoad = _this.onPageLoad.bind(_assertThisInitialized(_this));
        _this.state = {
          isLandscape: false,
          isPortrait: false
        };
        return _this;
      }

      _createClass(_class, [{
        key: "handleOrientationChange",
        value: function handleOrientationChange() {
          if (!this.isEventListenerAdded) {
            this.isEventListenerAdded = true;
          }

          var orientation = window.innerWidth > window.innerHeight ? 90 : 0;
          this.setState({
            isPortrait: orientation === 0,
            isLandscape: orientation === 90
          });
        }
      }, {
        key: "onOrientationChange",
        value: function onOrientationChange() {
          this.handleOrientationChange();
        }
      }, {
        key: "onPageLoad",
        value: function onPageLoad() {
          this.handleOrientationChange();
        }
      }, {
        key: "componentDidMount",
        value: function componentDidMount() {
          if ((typeof window === "undefined" ? "undefined" : _typeof(window)) !== undefined && isMobile) {
            if (!this.isEventListenerAdded) {
              this.handleOrientationChange();
              window.addEventListener("load", this.onPageLoad, false);
            } else {
              window.removeEventListener("load", this.onPageLoad, false);
            }

            window.addEventListener("resize", this.onOrientationChange, false);
          }
        }
      }, {
        key: "componentWillUnmount",
        value: function componentWillUnmount() {
          window.removeEventListener("resize", this.onOrientationChange, false);
        }
      }, {
        key: "render",
        value: function render() {
          return React__default.createElement(WrappedComponent, _extends({}, this.props, {
            isLandscape: this.state.isLandscape,
            isPortrait: this.state.isPortrait
          }));
        }
      }]);

      return _class;
    }(React__default.Component);
  }

  exports.AndroidView = AndroidView;
  exports.BrowserView = BrowserView;
  exports.ConsoleView = ConsoleView;
  exports.CustomView = CustomView;
  exports.IEView = IEView;
  exports.IOSView = IOSView;
  exports.MobileOnlyView = MobileOnlyView;
  exports.MobileView = MobileView;
  exports.SmartTVView = SmartTVView;
  exports.TabletView = TabletView;
  exports.WearableView = WearableView;
  exports.WinPhoneView = WinPhoneView;
  exports.browserName = browserName;
  exports.browserVersion = browserVersion;
  exports.deviceDetect = deviceDetect;
  exports.deviceType = deviceType;
  exports.engineName = engineName;
  exports.engineVersion = engineVersion;
  exports.fullBrowserVersion = fullBrowserVersion;
  exports.getUA = getUA;
  exports.isAndroid = isAndroid;
  exports.isBrowser = isBrowser;
  exports.isChrome = isChrome;
  exports.isChromium = isChromium;
  exports.isConsole = isConsole;
  exports.isEdge = isEdge;
  exports.isEdgeChromium = isEdgeChromium;
  exports.isElectron = isElectron;
  exports.isFirefox = isFirefox;
  exports.isIE = isIE;
  exports.isIOS = isIOS;
  exports.isIOS13 = isIOS13;
  exports.isIPad13 = isIPad13;
  exports.isIPhone13 = isIPhone13;
  exports.isIPod13 = isIPod13;
  exports.isLegacyEdge = isLegacyEdge;
  exports.isMacOs = isMacOs;
  exports.isMobile = isMobile;
  exports.isMobileOnly = isMobileOnly;
  exports.isMobileSafari = isMobileSafari;
  exports.isOpera = isOpera;
  exports.isSafari = isSafari;
  exports.isSmartTV = isSmartTV;
  exports.isTablet = isTablet;
  exports.isWearable = isWearable;
  exports.isWinPhone = isWinPhone;
  exports.isWindows = isWindows;
  exports.isYandex = isYandex;
  exports.mobileModel = mobileModel;
  exports.mobileVendor = mobileVendor;
  exports.osName = osName;
  exports.osVersion = osVersion;
  exports.withOrientationChange = withOrientationChange;
});
unwrapExports(main);
var main_38 = main.isMobile;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;

var _ListCache = ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new _ListCache;
  this.size = 0;
}

var _stackClear = stackClear;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas;

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol$1 = _root.Symbol;

var _Symbol = Symbol$1;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

/** Used to detect overreaching core-js shims. */
var coreJsData = _root['__core-js_shared__'];

var _coreJsData = coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked;

/** Used for built-in method references. */
var funcProto = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }
  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}

var _baseIsNative = baseIsNative;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}

var _getNative = getNative;

/* Built-in method references that are verified to be native. */
var Map = _getNative(_root, 'Map');

var _Map = Map;

/* Built-in method references that are verified to be native. */
var nativeCreate = _getNative(Object, 'create');

var _nativeCreate = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}

var _hashClear = hashClear;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet;

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? (data[key] !== undefined) : hasOwnProperty$3.call(data, key);
}

var _hashHas = hashHas;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (_nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;

var _Hash = Hash;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash,
    'map': new (_Map || _ListCache),
    'string': new _Hash
  };
}

var _mapCacheClear = mapCacheClear;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}

var _mapCacheGet = mapCacheGet;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}

var _mapCacheHas = mapCacheHas;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;

var _MapCache = MapCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof _ListCache) {
    var pairs = data.__data__;
    if (!_Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new _MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;

var _Stack = Stack;

/**
 * A specialized version of `_.forEach` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns `array`.
 */
function arrayEach(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}

var _arrayEach = arrayEach;

var defineProperty = (function() {
  try {
    var func = _getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty;

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && _defineProperty) {
    _defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$4.call(object, key) && eq_1(objValue, value)) ||
      (value === undefined && !(key in object))) {
    _baseAssignValue(object, key, value);
  }
}

var _assignValue = assignValue;

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      _baseAssignValue(object, key, newValue);
    } else {
      _assignValue(object, key, newValue);
    }
  }
  return object;
}

var _copyObject = copyObject;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

var _baseTimes = baseTimes;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag;
}

var _baseIsArguments = baseIsArguments;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$6.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$6.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = _baseIsArguments(function() { return arguments; }()) ? _baseIsArguments : function(value) {
  return isObjectLike_1(value) && hasOwnProperty$5.call(value, 'callee') &&
    !propertyIsEnumerable.call(value, 'callee');
};

var isArguments_1 = isArguments;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray_1 = isArray;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

var isBuffer_1 = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse_1;

module.exports = isBuffer;
});

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var isLength_1 = isLength;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    objectTag = '[object Object]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
typedArrayTags[errorTag] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag] = typedArrayTags[numberTag] =
typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
typedArrayTags[setTag] = typedArrayTags[stringTag] =
typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike_1(value) &&
    isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}

var _baseIsTypedArray = baseIsTypedArray;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

var _baseUnary = baseUnary;

var _nodeUtil = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && _freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

module.exports = nodeUtil;
});

/* Node.js helper references. */
var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;

var isTypedArray_1 = isTypedArray;

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
      isArg = !isArr && isArguments_1(value),
      isBuff = !isArr && !isArg && isBuffer_1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? _baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$6.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           _isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

var _arrayLikeKeys = arrayLikeKeys;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$8;

  return value === proto;
}

var _isPrototype = isPrototype;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = _overArg(Object.keys, Object);

var _nativeKeys = nativeKeys;

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$9.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$7.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

var _baseKeys = baseKeys;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}

var keys_1 = keys;

/**
 * The base implementation of `_.assign` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssign(object, source) {
  return object && _copyObject(source, keys_1(source), object);
}

var _baseAssign = baseAssign;

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function nativeKeysIn(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

var _nativeKeysIn = nativeKeysIn;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$a.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  if (!isObject_1(object)) {
    return _nativeKeysIn(object);
  }
  var isProto = _isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$8.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

var _baseKeysIn = baseKeysIn;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object, true) : _baseKeysIn(object);
}

var keysIn_1 = keysIn;

/**
 * The base implementation of `_.assignIn` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssignIn(object, source) {
  return object && _copyObject(source, keysIn_1(source), object);
}

var _baseAssignIn = baseAssignIn;

var _cloneBuffer = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined,
    allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined;

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

module.exports = cloneBuffer;
});

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */
function copyArray(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

var _copyArray = copyArray;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var _arrayFilter = arrayFilter;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

var stubArray_1 = stubArray;

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$b.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray_1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable$1.call(object, symbol);
  });
};

var _getSymbols = getSymbols;

/**
 * Copies own symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbols(source, object) {
  return _copyObject(source, _getSymbols(source), object);
}

var _copySymbols = copySymbols;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var _arrayPush = arrayPush;

/** Built-in value references. */
var getPrototype = _overArg(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbolsIn = !nativeGetSymbols$1 ? stubArray_1 : function(object) {
  var result = [];
  while (object) {
    _arrayPush(result, _getSymbols(object));
    object = _getPrototype(object);
  }
  return result;
};

var _getSymbolsIn = getSymbolsIn;

/**
 * Copies own and inherited symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbolsIn(source, object) {
  return _copyObject(source, _getSymbolsIn(source), object);
}

var _copySymbolsIn = copySymbolsIn;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}

var _getAllKeys = getAllKeys;

/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeysIn(object) {
  return _baseGetAllKeys(object, keysIn_1, _getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn;

/* Built-in method references that are verified to be native. */
var DataView = _getNative(_root, 'DataView');

var _DataView = DataView;

/* Built-in method references that are verified to be native. */
var Promise$1 = _getNative(_root, 'Promise');

var _Promise = Promise$1;

/* Built-in method references that are verified to be native. */
var Set = _getNative(_root, 'Set');

var _Set = Set;

/* Built-in method references that are verified to be native. */
var WeakMap = _getNative(_root, 'WeakMap');

var _WeakMap = WeakMap;

/** `Object#toString` result references. */
var mapTag$1 = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$1 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$1 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = _toSource(_DataView),
    mapCtorString = _toSource(_Map),
    promiseCtorString = _toSource(_Promise),
    setCtorString = _toSource(_Set),
    weakMapCtorString = _toSource(_WeakMap);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = _baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag$1) ||
    (_Map && getTag(new _Map) != mapTag$1) ||
    (_Promise && getTag(_Promise.resolve()) != promiseTag) ||
    (_Set && getTag(new _Set) != setTag$1) ||
    (_WeakMap && getTag(new _WeakMap) != weakMapTag$1)) {
  getTag = function(value) {
    var result = _baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? _toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$1;
        case mapCtorString: return mapTag$1;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$1;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

var _getTag = getTag;

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$c.hasOwnProperty;

/**
 * Initializes an array clone.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the initialized clone.
 */
function initCloneArray(array) {
  var length = array.length,
      result = new array.constructor(length);

  // Add properties assigned by `RegExp#exec`.
  if (length && typeof array[0] == 'string' && hasOwnProperty$9.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}

var _initCloneArray = initCloneArray;

/** Built-in value references. */
var Uint8Array = _root.Uint8Array;

var _Uint8Array = Uint8Array;

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new _Uint8Array(result).set(new _Uint8Array(arrayBuffer));
  return result;
}

var _cloneArrayBuffer = cloneArrayBuffer;

/**
 * Creates a clone of `dataView`.
 *
 * @private
 * @param {Object} dataView The data view to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned data view.
 */
function cloneDataView(dataView, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

var _cloneDataView = cloneDataView;

/** Used to match `RegExp` flags from their coerced string values. */
var reFlags = /\w*$/;

/**
 * Creates a clone of `regexp`.
 *
 * @private
 * @param {Object} regexp The regexp to clone.
 * @returns {Object} Returns the cloned regexp.
 */
function cloneRegExp(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

var _cloneRegExp = cloneRegExp;

/** Used to convert symbols to primitives and strings. */
var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * Creates a clone of the `symbol` object.
 *
 * @private
 * @param {Object} symbol The symbol object to clone.
 * @returns {Object} Returns the cloned symbol object.
 */
function cloneSymbol(symbol) {
  return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
}

var _cloneSymbol = cloneSymbol;

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

var _cloneTypedArray = cloneTypedArray;

/** `Object#toString` result references. */
var boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    mapTag$2 = '[object Map]',
    numberTag$1 = '[object Number]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag = '[object Symbol]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

/**
 * Initializes an object clone based on its `toStringTag`.
 *
 * **Note:** This function only supports cloning values with tags of
 * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
 *
 * @private
 * @param {Object} object The object to clone.
 * @param {string} tag The `toStringTag` of the object to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneByTag(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$1:
      return _cloneArrayBuffer(object);

    case boolTag$1:
    case dateTag$1:
      return new Ctor(+object);

    case dataViewTag$2:
      return _cloneDataView(object, isDeep);

    case float32Tag$1: case float64Tag$1:
    case int8Tag$1: case int16Tag$1: case int32Tag$1:
    case uint8Tag$1: case uint8ClampedTag$1: case uint16Tag$1: case uint32Tag$1:
      return _cloneTypedArray(object, isDeep);

    case mapTag$2:
      return new Ctor;

    case numberTag$1:
    case stringTag$1:
      return new Ctor(object);

    case regexpTag$1:
      return _cloneRegExp(object);

    case setTag$2:
      return new Ctor;

    case symbolTag:
      return _cloneSymbol(object);
  }
}

var _initCloneByTag = initCloneByTag;

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate = (function() {
  function object() {}
  return function(proto) {
    if (!isObject_1(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

var _baseCreate = baseCreate;

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject(object) {
  return (typeof object.constructor == 'function' && !_isPrototype(object))
    ? _baseCreate(_getPrototype(object))
    : {};
}

var _initCloneObject = initCloneObject;

/** `Object#toString` result references. */
var mapTag$3 = '[object Map]';

/**
 * The base implementation of `_.isMap` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 */
function baseIsMap(value) {
  return isObjectLike_1(value) && _getTag(value) == mapTag$3;
}

var _baseIsMap = baseIsMap;

/* Node.js helper references. */
var nodeIsMap = _nodeUtil && _nodeUtil.isMap;

/**
 * Checks if `value` is classified as a `Map` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 * @example
 *
 * _.isMap(new Map);
 * // => true
 *
 * _.isMap(new WeakMap);
 * // => false
 */
var isMap = nodeIsMap ? _baseUnary(nodeIsMap) : _baseIsMap;

var isMap_1 = isMap;

/** `Object#toString` result references. */
var setTag$3 = '[object Set]';

/**
 * The base implementation of `_.isSet` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 */
function baseIsSet(value) {
  return isObjectLike_1(value) && _getTag(value) == setTag$3;
}

var _baseIsSet = baseIsSet;

/* Node.js helper references. */
var nodeIsSet = _nodeUtil && _nodeUtil.isSet;

/**
 * Checks if `value` is classified as a `Set` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 * @example
 *
 * _.isSet(new Set);
 * // => true
 *
 * _.isSet(new WeakSet);
 * // => false
 */
var isSet = nodeIsSet ? _baseUnary(nodeIsSet) : _baseIsSet;

var isSet_1 = isSet;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG = 1,
    CLONE_FLAT_FLAG = 2,
    CLONE_SYMBOLS_FLAG = 4;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    mapTag$4 = '[object Map]',
    numberTag$2 = '[object Number]',
    objectTag$2 = '[object Object]',
    regexpTag$2 = '[object RegExp]',
    setTag$4 = '[object Set]',
    stringTag$2 = '[object String]',
    symbolTag$1 = '[object Symbol]',
    weakMapTag$2 = '[object WeakMap]';

var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$3 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';

/** Used to identify `toStringTag` values supported by `_.clone`. */
var cloneableTags = {};
cloneableTags[argsTag$2] = cloneableTags[arrayTag$1] =
cloneableTags[arrayBufferTag$2] = cloneableTags[dataViewTag$3] =
cloneableTags[boolTag$2] = cloneableTags[dateTag$2] =
cloneableTags[float32Tag$2] = cloneableTags[float64Tag$2] =
cloneableTags[int8Tag$2] = cloneableTags[int16Tag$2] =
cloneableTags[int32Tag$2] = cloneableTags[mapTag$4] =
cloneableTags[numberTag$2] = cloneableTags[objectTag$2] =
cloneableTags[regexpTag$2] = cloneableTags[setTag$4] =
cloneableTags[stringTag$2] = cloneableTags[symbolTag$1] =
cloneableTags[uint8Tag$2] = cloneableTags[uint8ClampedTag$2] =
cloneableTags[uint16Tag$2] = cloneableTags[uint32Tag$2] = true;
cloneableTags[errorTag$1] = cloneableTags[funcTag$2] =
cloneableTags[weakMapTag$2] = false;

/**
 * The base implementation of `_.clone` and `_.cloneDeep` which tracks
 * traversed objects.
 *
 * @private
 * @param {*} value The value to clone.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Deep clone
 *  2 - Flatten inherited properties
 *  4 - Clone symbols
 * @param {Function} [customizer] The function to customize cloning.
 * @param {string} [key] The key of `value`.
 * @param {Object} [object] The parent object of `value`.
 * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
 * @returns {*} Returns the cloned value.
 */
function baseClone(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG,
      isFlat = bitmask & CLONE_FLAT_FLAG,
      isFull = bitmask & CLONE_SYMBOLS_FLAG;

  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }
  if (result !== undefined) {
    return result;
  }
  if (!isObject_1(value)) {
    return value;
  }
  var isArr = isArray_1(value);
  if (isArr) {
    result = _initCloneArray(value);
    if (!isDeep) {
      return _copyArray(value, result);
    }
  } else {
    var tag = _getTag(value),
        isFunc = tag == funcTag$2 || tag == genTag$1;

    if (isBuffer_1(value)) {
      return _cloneBuffer(value, isDeep);
    }
    if (tag == objectTag$2 || tag == argsTag$2 || (isFunc && !object)) {
      result = (isFlat || isFunc) ? {} : _initCloneObject(value);
      if (!isDeep) {
        return isFlat
          ? _copySymbolsIn(value, _baseAssignIn(result, value))
          : _copySymbols(value, _baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = _initCloneByTag(value, tag, isDeep);
    }
  }
  // Check for circular references and return its corresponding clone.
  stack || (stack = new _Stack);
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);

  if (isSet_1(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap_1(value)) {
    value.forEach(function(subValue, key) {
      result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = isFull
    ? (isFlat ? _getAllKeysIn : _getAllKeys)
    : (isFlat ? keysIn_1 : keys_1);

  var props = isArr ? undefined : keysFunc(value);
  _arrayEach(props || value, function(subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }
    // Recursively populate clone (susceptible to call stack limits).
    _assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

var _baseClone = baseClone;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG$1 = 1,
    CLONE_SYMBOLS_FLAG$1 = 4;

/**
 * This method is like `_.clone` except that it recursively clones `value`.
 *
 * @static
 * @memberOf _
 * @since 1.0.0
 * @category Lang
 * @param {*} value The value to recursively clone.
 * @returns {*} Returns the deep cloned value.
 * @see _.clone
 * @example
 *
 * var objects = [{ 'a': 1 }, { 'b': 2 }];
 *
 * var deep = _.cloneDeep(objects);
 * console.log(deep[0] === objects[0]);
 * // => false
 */
function cloneDeep(value) {
  return _baseClone(value, CLONE_DEEP_FLAG$1 | CLONE_SYMBOLS_FLAG$1);
}

var cloneDeep_1 = cloneDeep;

var dist = createCommonjsModule(function (module, exports) {
  !function (e, t) {
     module.exports = t(moment, React) ;
  }(commonjsGlobal, function (e, t) {
    return function (e) {
      var t = {};

      function n(r) {
        if (t[r]) return t[r].exports;
        var o = t[r] = {
          i: r,
          l: !1,
          exports: {}
        };
        return e[r].call(o.exports, o, o.exports, n), o.l = !0, o.exports;
      }

      return n.m = e, n.c = t, n.d = function (e, t, r) {
        n.o(e, t) || Object.defineProperty(e, t, {
          enumerable: !0,
          get: r
        });
      }, n.r = function (e) {
        "undefined" != typeof Symbol && Symbol.toStringTag && Object.defineProperty(e, Symbol.toStringTag, {
          value: "Module"
        }), Object.defineProperty(e, "__esModule", {
          value: !0
        });
      }, n.t = function (e, t) {
        if (1 & t && (e = n(e)), 8 & t) return e;
        if (4 & t && "object" == typeof e && e && e.__esModule) return e;
        var r = Object.create(null);
        if (n.r(r), Object.defineProperty(r, "default", {
          enumerable: !0,
          value: e
        }), 2 & t && "string" != typeof e) for (var o in e) {
          n.d(r, o, function (t) {
            return e[t];
          }.bind(null, o));
        }
        return r;
      }, n.n = function (e) {
        var t = e && e.__esModule ? function () {
          return e["default"];
        } : function () {
          return e;
        };
        return n.d(t, "a", t), t;
      }, n.o = function (e, t) {
        return Object.prototype.hasOwnProperty.call(e, t);
      }, n.p = "", n(n.s = 6);
    }([function (e, t, n) {
      e.exports = n(3)();
    }, function (t, n) {
      t.exports = e;
    }, function (e, n) {
      e.exports = t;
    }, function (e, t, n) {

      var r = n(4);

      function o() {}

      function i() {}

      i.resetWarningCache = o, e.exports = function () {
        function e(e, t, n, o, i, a) {
          if (a !== r) {
            var l = new Error("Calling PropTypes validators directly is not supported by the `prop-types` package. Use PropTypes.checkPropTypes() to call them. Read more at http://fb.me/use-check-prop-types");
            throw l.name = "Invariant Violation", l;
          }
        }

        function t() {
          return e;
        }

        e.isRequired = e;
        var n = {
          array: e,
          bool: e,
          func: e,
          number: e,
          object: e,
          string: e,
          symbol: e,
          any: e,
          arrayOf: t,
          element: e,
          elementType: e,
          instanceOf: t,
          node: e,
          objectOf: t,
          oneOf: t,
          oneOfType: t,
          shape: t,
          exact: t,
          checkPropTypes: i,
          resetWarningCache: o
        };
        return n.PropTypes = n, n;
      };
    }, function (e, t, n) {

      e.exports = "SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED";
    }, function (e, t, n) {
      var r, o, i, a, l;
      a = this, l = function l(e) {
        var t = !1,
            n = !1,
            r = "escape years months weeks days hours minutes seconds milliseconds general".split(" "),
            o = [{
          type: "seconds",
          targets: [{
            type: "minutes",
            value: 60
          }, {
            type: "hours",
            value: 3600
          }, {
            type: "days",
            value: 86400
          }, {
            type: "weeks",
            value: 604800
          }, {
            type: "months",
            value: 2678400
          }, {
            type: "years",
            value: 31536e3
          }]
        }, {
          type: "minutes",
          targets: [{
            type: "hours",
            value: 60
          }, {
            type: "days",
            value: 1440
          }, {
            type: "weeks",
            value: 10080
          }, {
            type: "months",
            value: 44640
          }, {
            type: "years",
            value: 525600
          }]
        }, {
          type: "hours",
          targets: [{
            type: "days",
            value: 24
          }, {
            type: "weeks",
            value: 168
          }, {
            type: "months",
            value: 744
          }, {
            type: "years",
            value: 8760
          }]
        }, {
          type: "days",
          targets: [{
            type: "weeks",
            value: 7
          }, {
            type: "months",
            value: 31
          }, {
            type: "years",
            value: 365
          }]
        }, {
          type: "months",
          targets: [{
            type: "years",
            value: 12
          }]
        }];

        function i(e, t) {
          return !(t.length > e.length) && -1 !== e.indexOf(t);
        }

        function a(e) {
          for (var t = ""; e;) {
            t += "0", e -= 1;
          }

          return t;
        }

        function l(e, t, r) {
          var o,
              i,
              u,
              s = t.useToLocaleString,
              c = t.useGrouping,
              m = c && t.grouping.slice(),
              f = t.maximumSignificantDigits,
              p = t.minimumIntegerDigits || 1,
              g = t.fractionDigits || 0,
              y = t.groupingSeparator,
              h = t.decimalSeparator;

          if (s && r) {
            var d = {
              minimumIntegerDigits: p,
              useGrouping: c
            };

            if (g && (d.maximumFractionDigits = g, d.minimumFractionDigits = g), f && e > 0 && (d.maximumSignificantDigits = f), !n) {
              var v = w({}, t);
              v.useGrouping = !1, v.decimalSeparator = ".", e = parseFloat(l(e, v), 10);
            }

            return e.toLocaleString(r, d);
          }

          var b = (f ? e.toPrecision(f + 1) : e.toFixed(g + 1)).split("e");
          u = b[1] || "", i = (b = b[0].split("."))[1] || "";

          var S = (o = b[0] || "").length,
              T = i.length,
              _ = S + T,
              x = o + i;

          (f && _ === f + 1 || !f && T === g + 1) && ((x = function (e) {
            for (var t = e.split("").reverse(), n = 0, r = !0; r && n < t.length;) {
              n ? "9" === t[n] ? t[n] = "0" : (t[n] = (parseInt(t[n], 10) + 1).toString(), r = !1) : (parseInt(t[n], 10) < 5 && (r = !1), t[n] = "0"), n += 1;
            }

            return r && t.push("1"), t.reverse().join("");
          }(x)).length === _ + 1 && (S += 1), T && (x = x.slice(0, -1)), o = x.slice(0, S), i = x.slice(S)), f && (i = i.replace(/0*$/, ""));
          var O = parseInt(u, 10);
          O > 0 ? i.length <= O ? (o += i += a(O - i.length), i = "") : (o += i.slice(0, O), i = i.slice(O)) : O < 0 && (i = a(Math.abs(O) - o.length) + o + i, o = "0"), f || ((i = i.slice(0, g)).length < g && (i += a(g - i.length)), o.length < p && (o = a(p - o.length) + o));
          var V,
              D = "";
          if (c) for (b = o; b.length;) {
            m.length && (V = m.shift()), D && (D = y + D), D = b.slice(-V) + D, b = b.slice(0, -V);
          } else D = o;
          return i && (D = D + h + i), D;
        }

        function u(e, t) {
          return e.label.length > t.label.length ? -1 : e.label.length < t.label.length ? 1 : 0;
        }

        var s,
            c = {
          durationLabelsStandard: {
            S: "millisecond",
            SS: "milliseconds",
            s: "second",
            ss: "seconds",
            m: "minute",
            mm: "minutes",
            h: "hour",
            hh: "hours",
            d: "day",
            dd: "days",
            w: "week",
            ww: "weeks",
            M: "month",
            MM: "months",
            y: "year",
            yy: "years"
          },
          durationLabelsShort: {
            S: "msec",
            SS: "msecs",
            s: "sec",
            ss: "secs",
            m: "min",
            mm: "mins",
            h: "hr",
            hh: "hrs",
            d: "dy",
            dd: "dys",
            w: "wk",
            ww: "wks",
            M: "mo",
            MM: "mos",
            y: "yr",
            yy: "yrs"
          },
          durationTimeTemplates: {
            HMS: "h:mm:ss",
            HM: "h:mm",
            MS: "m:ss"
          },
          durationLabelTypes: [{
            type: "standard",
            string: "__"
          }, {
            type: "short",
            string: "_"
          }],
          durationPluralKey: function durationPluralKey(e, t, n) {
            return 1 === t && null === n ? e : e + e;
          }
        };

        function m(e) {
          return "[object Array]" === Object.prototype.toString.call(e);
        }

        function f(e) {
          return "[object Object]" === Object.prototype.toString.call(e);
        }

        function p(e, t) {
          var n,
              r = 0,
              o = e && e.length || 0;

          for ("function" != typeof t && (n = t, t = function t(e) {
            return e === n;
          }); r < o;) {
            if (t(e[r])) return e[r];
            r += 1;
          }
        }

        function g(e, t) {
          var n = 0,
              r = e.length;
          if (e && r) for (; n < r;) {
            if (!1 === t(e[n], n)) return;
            n += 1;
          }
        }

        function y(e, t) {
          var n = 0,
              r = e.length,
              o = [];
          if (!e || !r) return o;

          for (; n < r;) {
            o[n] = t(e[n], n), n += 1;
          }

          return o;
        }

        function h(e, t) {
          return y(e, function (e) {
            return e[t];
          });
        }

        function d(e) {
          var t = [];
          return g(e, function (e) {
            e && t.push(e);
          }), t;
        }

        function v(e) {
          var t = [];
          return g(e, function (e) {
            p(t, e) || t.push(e);
          }), t;
        }

        function b(e, t) {
          var n = [];
          return g(e, function (e) {
            g(t, function (t) {
              e === t && n.push(e);
            });
          }), v(n);
        }

        function S(e, t) {
          var n = [];
          return g(e, function (r, o) {
            if (!t(r)) return n = e.slice(o), !1;
          }), n;
        }

        function w(e, t) {
          for (var n in t) {
            t.hasOwnProperty(n) && (e[n] = t[n]);
          }

          return e;
        }

        function T(e) {
          var t = [];

          for (var n in e) {
            e.hasOwnProperty(n) && t.push(n);
          }

          return t;
        }

        function _(e, t) {
          var n = 0,
              r = e.length;
          if (!e || !r) return !1;

          for (; n < r;) {
            if (!0 === t(e[n], n)) return !0;
            n += 1;
          }

          return !1;
        }

        function x() {
          var e,
              t = [].slice.call(arguments),
              n = {};
          if (g(t, function (t, r) {
            if (!r) {
              if (!m(t)) throw "Expected array as the first argument to durationsFormat.";
              e = t;
            }

            "string" != typeof t && "function" != typeof t ? "number" != typeof t ? f(t) && w(n, t) : n.precision = t : n.template = t;
          }), !e || !e.length) return [];
          n.returnMomentTypes = !0;
          var o,
              i = y(e, function (e) {
            return e.format(n);
          }),
              a = b(r, v(h((o = [], g(i, function (e) {
            o = o.concat(e);
          }), o), "type"))),
              l = n.largest;
          return l && (a = a.slice(0, l)), n.returnMomentTypes = !1, n.outputTypes = a, y(e, function (e) {
            return e.format(n);
          });
        }

        function O() {
          var n = [].slice.call(arguments),
              a = w({}, this.format.defaults),
              s = this.asMilliseconds(),
              x = this.asMonths();
          "function" == typeof this.isValid && !1 === this.isValid() && (s = 0, x = 0);
          var O = s < 0,
              V = e.duration(Math.abs(s), "milliseconds"),
              D = e.duration(Math.abs(x), "months");
          g(n, function (e) {
            "string" != typeof e && "function" != typeof e ? "number" != typeof e ? f(e) && w(a, e) : a.precision = e : a.template = e;
          });
          var L = {
            years: "y",
            months: "M",
            weeks: "w",
            days: "d",
            hours: "h",
            minutes: "m",
            seconds: "s",
            milliseconds: "S"
          },
              j = {
            escape: /\[(.+?)\]/,
            years: /\*?[Yy]+/,
            months: /\*?M+/,
            weeks: /\*?[Ww]+/,
            days: /\*?[Dd]+/,
            hours: /\*?[Hh]+/,
            minutes: /\*?m+/,
            seconds: /\*?s+/,
            milliseconds: /\*?S+/,
            general: /.+?/
          };
          a.types = r;

          var M = function M(e) {
            return p(r, function (t) {
              return j[t].test(e);
            });
          },
              k = new RegExp(y(r, function (e) {
            return j[e].source;
          }).join("|"), "g");

          a.duration = this;
          var P = "function" == typeof a.template ? a.template.apply(a) : a.template,
              E = a.outputTypes,
              F = a.returnMomentTypes,
              I = a.largest,
              C = [];
          E || (m(a.stopTrim) && (a.stopTrim = a.stopTrim.join("")), a.stopTrim && g(a.stopTrim.match(k), function (e) {
            var t = M(e);
            "escape" !== t && "general" !== t && C.push(t);
          }));
          var N = e.localeData();
          N || (N = {}), g(T(c), function (e) {
            "function" != typeof c[e] ? N["_" + e] || (N["_" + e] = c[e]) : N[e] || (N[e] = c[e]);
          }), g(T(N._durationTimeTemplates), function (e) {
            P = P.replace("_" + e + "_", N._durationTimeTemplates[e]);
          });
          var G = a.userLocale || e.locale(),
              R = a.useLeftUnits,
              U = a.usePlural,
              q = a.precision,
              z = a.forceLength,
              H = a.useGrouping,
              W = a.trunc,
              $ = a.useSignificantDigits && q > 0,
              A = $ ? a.precision : 0,
              B = A,
              K = a.minValue,
              Y = !1,
              J = a.maxValue,
              Q = !1,
              X = a.useToLocaleString,
              Z = a.groupingSeparator,
              ee = a.decimalSeparator,
              te = a.grouping;
          X = X && t;
          var ne = a.trim;
          m(ne) && (ne = ne.join(" ")), null === ne && (I || J || $) && (ne = "all"), null !== ne && !0 !== ne && "left" !== ne && "right" !== ne || (ne = "large"), !1 === ne && (ne = "");

          var re = function re(e) {
            return e.test(ne);
          },
              oe = /both/,
              ie = /^all|[^sm]all/,
              ae = I > 0 || _([/large/, oe, ie], re),
              le = _([/small/, oe, ie], re),
              ue = _([/mid/, ie], re),
              se = _([/final/, ie], re),
              ce = y(P.match(k), function (e, t) {
            var n = M(e);
            return "*" === e.slice(0, 1) && (e = e.slice(1), "escape" !== n && "general" !== n && C.push(n)), {
              index: t,
              length: e.length,
              text: "",
              token: "escape" === n ? e.replace(j.escape, "$1") : e,
              type: "escape" === n || "general" === n ? null : n
            };
          }),
              me = {
            index: 0,
            length: 0,
            token: "",
            text: "",
            type: null
          },
              fe = [];

          R && ce.reverse(), g(ce, function (e) {
            if (e.type) return (me.type || me.text) && fe.push(me), void (me = e);
            R ? me.text = e.token + me.text : me.text += e.token;
          }), (me.type || me.text) && fe.push(me), R && fe.reverse();
          var pe = b(r, v(d(h(fe, "type"))));
          if (!pe.length) return h(fe, "text").join("");
          pe = y(pe, function (e, t) {
            var n,
                r = t + 1 === pe.length,
                o = !t;
            n = "years" === e || "months" === e ? D.as(e) : V.as(e);
            var i = Math.floor(n),
                l = n - i,
                u = p(fe, function (t) {
              return e === t.type;
            });
            return o && J && n > J && (Q = !0), r && K && Math.abs(a.duration.as(e)) < K && (Y = !0), o && null === z && u.length > 1 && (z = !0), V.subtract(i, e), D.subtract(i, e), {
              rawValue: n,
              wholeValue: i,
              decimalValue: r ? l : 0,
              isSmallest: r,
              isLargest: o,
              type: e,
              tokenLength: u.length
            };
          });

          var ge,
              ye = W ? Math.floor : Math.round,
              he = function he(e, t) {
            var n = Math.pow(10, t);
            return ye(e * n) / n;
          },
              de = !1,
              ve = !1,
              be = function be(e, t) {
            var n = {
              useGrouping: H,
              groupingSeparator: Z,
              decimalSeparator: ee,
              grouping: te,
              useToLocaleString: X
            };
            return $ && (A <= 0 ? (e.rawValue = 0, e.wholeValue = 0, e.decimalValue = 0) : (n.maximumSignificantDigits = A, e.significantDigits = A)), Q && !ve && (e.isLargest ? (e.wholeValue = J, e.decimalValue = 0) : (e.wholeValue = 0, e.decimalValue = 0)), Y && !ve && (e.isSmallest ? (e.wholeValue = K, e.decimalValue = 0) : (e.wholeValue = 0, e.decimalValue = 0)), e.isSmallest || e.significantDigits && e.significantDigits - e.wholeValue.toString().length <= 0 ? q < 0 ? e.value = he(e.wholeValue, q) : 0 === q ? e.value = ye(e.wholeValue + e.decimalValue) : $ ? (e.value = W ? he(e.rawValue, A - e.wholeValue.toString().length) : e.rawValue, e.wholeValue && (A -= e.wholeValue.toString().length)) : (n.fractionDigits = q, e.value = W ? e.wholeValue + he(e.decimalValue, q) : e.wholeValue + e.decimalValue) : $ && e.wholeValue ? (e.value = Math.round(he(e.wholeValue, e.significantDigits - e.wholeValue.toString().length)), A -= e.wholeValue.toString().length) : e.value = e.wholeValue, e.tokenLength > 1 && (z || de) && (n.minimumIntegerDigits = e.tokenLength, ve && n.maximumSignificantDigits < e.tokenLength && delete n.maximumSignificantDigits), !de && (e.value > 0 || "" === ne || p(C, e.type) || p(E, e.type)) && (de = !0), e.formattedValue = l(e.value, n, G), n.useGrouping = !1, n.decimalSeparator = ".", e.formattedValueEn = l(e.value, n, "en"), 2 === e.tokenLength && "milliseconds" === e.type && (e.formattedValueMS = l(e.value, {
              minimumIntegerDigits: 3,
              useGrouping: !1
            }, "en").slice(0, 2)), e;
          };

          if ((pe = d(pe = y(pe, be))).length > 1) {
            var Se = function Se(e) {
              return p(pe, function (t) {
                return t.type === e;
              });
            };

            g(o, function (e) {
              var t = Se(e.type);
              t && g(e.targets, function (e) {
                var n = Se(e.type);
                n && parseInt(t.formattedValueEn, 10) === e.value && (t.rawValue = 0, t.wholeValue = 0, t.decimalValue = 0, n.rawValue += 1, n.wholeValue += 1, n.decimalValue = 0, n.formattedValueEn = n.wholeValue.toString(), ve = !0);
              });
            });
          }

          return ve && (de = !1, A = B, pe = d(pe = y(pe, be))), !E || Q && !a.trim ? (ae && (pe = S(pe, function (e) {
            return !e.isSmallest && !e.wholeValue && !p(C, e.type);
          })), I && pe.length && (pe = pe.slice(0, I)), le && pe.length > 1 && (ge = function ge(e) {
            return !e.wholeValue && !p(C, e.type) && !e.isLargest;
          }, pe = S(pe.slice().reverse(), ge).reverse()), ue && (pe = d(pe = y(pe, function (e, t) {
            return t > 0 && t < pe.length - 1 && !e.wholeValue ? null : e;
          }))), !se || 1 !== pe.length || pe[0].wholeValue || !W && pe[0].isSmallest && pe[0].rawValue < K || (pe = [])) : pe = d(pe = y(pe, function (e) {
            return p(E, function (t) {
              return e.type === t;
            }) ? e : null;
          })), F ? pe : (g(fe, function (e) {
            var t = L[e.type],
                n = p(pe, function (t) {
              return t.type === e.type;
            });

            if (t && n) {
              var r = n.formattedValueEn.split(".");
              r[0] = parseInt(r[0], 10), r[1] ? r[1] = parseFloat("0." + r[1], 10) : r[1] = null;

              var o = N.durationPluralKey(t, r[0], r[1]),
                  a = function (e, t) {
                var n = [];
                return g(T(t), function (r) {
                  if ("_durationLabels" === r.slice(0, 15)) {
                    var o = r.slice(15).toLowerCase();
                    g(T(t[r]), function (i) {
                      i.slice(0, 1) === e && n.push({
                        type: o,
                        key: i,
                        label: t[r][i]
                      });
                    });
                  }
                }), n;
              }(t, N),
                  l = !1,
                  s = {};

              g(N._durationLabelTypes, function (t) {
                var n = p(a, function (e) {
                  return e.type === t.type && e.key === o;
                });
                n && (s[n.type] = n.label, i(e.text, t.string) && (e.text = e.text.replace(t.string, n.label), l = !0));
              }), U && !l && (a.sort(u), g(a, function (t) {
                return s[t.type] === t.label ? !i(e.text, t.label) && void 0 : i(e.text, t.label) ? (e.text = e.text.replace(t.label, s[t.type]), !1) : void 0;
              }));
            }
          }), (fe = y(fe, function (e) {
            if (!e.type) return e.text;
            var t = p(pe, function (t) {
              return t.type === e.type;
            });
            if (!t) return "";
            var n = "";
            return R && (n += e.text), (O && Q || !O && Y) && (n += "< ", Q = !1, Y = !1), (O && Y || !O && Q) && (n += "> ", Q = !1, Y = !1), O && (t.value > 0 || "" === ne || p(C, t.type) || p(E, t.type)) && (n += "-", O = !1), "milliseconds" === e.type && t.formattedValueMS ? n += t.formattedValueMS : n += t.formattedValue, R || (n += e.text), n;
          })).join("").replace(/(,| |:|\.)*$/, "").replace(/^(,| |:|\.)*/, ""));
        }

        function V() {
          var e = this.duration,
              t = function t(_t) {
            return e._data[_t];
          },
              n = p(this.types, t),
              r = function (e, t) {
            for (var n = e.length; n -= 1;) {
              if (t(e[n])) return e[n];
            }
          }(this.types, t);

          switch (n) {
            case "milliseconds":
              return "S __";

            case "seconds":
            case "minutes":
              return "*_MS_";

            case "hours":
              return "_HMS_";

            case "days":
              if (n === r) return "d __";

            case "weeks":
              return n === r ? "w __" : (null === this.trim && (this.trim = "both"), "w __, d __, h __");

            case "months":
              if (n === r) return "M __";

            case "years":
              return n === r ? "y __" : (null === this.trim && (this.trim = "both"), "y __, M __, d __");

            default:
              return null === this.trim && (this.trim = "both"), "y __, d __, h __, m __, s __";
          }
        }

        function D(e) {
          if (!e) throw "Moment Duration Format init cannot find moment instance.";
          e.duration.format = x, e.duration.fn.format = O, e.duration.fn.format.defaults = {
            trim: null,
            stopTrim: null,
            largest: null,
            maxValue: null,
            minValue: null,
            precision: 0,
            trunc: !1,
            forceLength: null,
            userLocale: null,
            usePlural: !0,
            useLeftUnits: !1,
            useGrouping: !0,
            useSignificantDigits: !1,
            template: V,
            useToLocaleString: !0,
            groupingSeparator: ",",
            decimalSeparator: ".",
            grouping: [3]
          }, e.updateLocale("en", c);
        }

        return t = !!((s = (s = !0) && function () {
          try {
            0 .toLocaleString("i");
          } catch (e) {
            return "RangeError" === e.name;
          }

          return !1;
        }()) && (s = (s = (s = s && "1" === 1..toLocaleString("en", {
          minimumIntegerDigits: 1
        })) && "01" === 1..toLocaleString("en", {
          minimumIntegerDigits: 2
        })) && "001" === 1..toLocaleString("en", {
          minimumIntegerDigits: 3
        })) && (s = (s = (s = (s = s && "100" === 99.99.toLocaleString("en", {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        })) && "100.0" === 99.99.toLocaleString("en", {
          maximumFractionDigits: 1,
          minimumFractionDigits: 1
        })) && "99.99" === 99.99.toLocaleString("en", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2
        })) && "99.990" === 99.99.toLocaleString("en", {
          maximumFractionDigits: 3,
          minimumFractionDigits: 3
        })) && (s = (s = (s = (s = (s = s && "100" === 99.99.toLocaleString("en", {
          maximumSignificantDigits: 1
        })) && "100" === 99.99.toLocaleString("en", {
          maximumSignificantDigits: 2
        })) && "100" === 99.99.toLocaleString("en", {
          maximumSignificantDigits: 3
        })) && "99.99" === 99.99.toLocaleString("en", {
          maximumSignificantDigits: 4
        })) && "99.99" === 99.99.toLocaleString("en", {
          maximumSignificantDigits: 5
        })) && (s = (s = s && "1,000" === 1e3.toLocaleString("en", {
          useGrouping: !0
        })) && "1000" === 1e3.toLocaleString("en", {
          useGrouping: !1
        }))), n = t && "3.6" === 3.55.toLocaleString("en", {
          useGrouping: !1,
          minimumIntegerDigits: 1,
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        }), D(e), D;
      }, o = [n(1)], void 0 === (i = "function" == typeof (r = l) ? r.apply(t, o) : r) || (e.exports = i), a && (a.momentDurationFormatSetup = a.moment ? l(a.moment) : l);
    }, function (e, t, n) {

      n.r(t);
      var r = n(2),
          o = n.n(r),
          i = n(0),
          a = n.n(i),
          l = n(1),
          u = n.n(l);
      n(5);

      function s(e) {
        return (s = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (e) {
          return typeof e;
        } : function (e) {
          return e && "function" == typeof Symbol && e.constructor === Symbol && e !== Symbol.prototype ? "symbol" : typeof e;
        })(e);
      }

      function c(e, t) {
        if (null == e) return {};

        var n,
            r,
            o = function (e, t) {
          if (null == e) return {};
          var n,
              r,
              o = {},
              i = Object.keys(e);

          for (r = 0; r < i.length; r++) {
            n = i[r], t.indexOf(n) >= 0 || (o[n] = e[n]);
          }

          return o;
        }(e, t);

        if (Object.getOwnPropertySymbols) {
          var i = Object.getOwnPropertySymbols(e);

          for (r = 0; r < i.length; r++) {
            n = i[r], t.indexOf(n) >= 0 || Object.prototype.propertyIsEnumerable.call(e, n) && (o[n] = e[n]);
          }
        }

        return o;
      }

      function m(e) {
        return (m = Object.setPrototypeOf ? Object.getPrototypeOf : function (e) {
          return e.__proto__ || Object.getPrototypeOf(e);
        })(e);
      }

      function f(e) {
        if (void 0 === e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
        return e;
      }

      function p(e, t) {
        for (var n = 0; n < t.length; n++) {
          var r = t[n];
          r.enumerable = r.enumerable || !1, r.configurable = !0, "value" in r && (r.writable = !0), Object.defineProperty(e, r.key, r);
        }
      }

      function g(e, t, n) {
        return t && p(e.prototype, t), n && p(e, n), e;
      }

      function y(e, t) {
        return (y = Object.setPrototypeOf || function (e, t) {
          return e.__proto__ = t, e;
        })(e, t);
      }

      function h(e, t, n) {
        return t in e ? Object.defineProperty(e, t, {
          value: n,
          enumerable: !0,
          configurable: !0,
          writable: !0
        }) : e[t] = n, e;
      }

      n.d(t, "default", function () {
        return S;
      });

      var d = [a.a.string, a.a.number, a.a.array, a.a.object],
          v = [a.a.string, a.a.array],
          b = [a.a.object, a.a.bool],
          S = function (e) {
        function t(e) {
          var n, r, o;
          return function (e, t) {
            if (!(e instanceof t)) throw new TypeError("Cannot call a class as a function");
          }(this, t), r = this, o = m(t).call(this, e), n = !o || "object" !== s(o) && "function" != typeof o ? f(r) : o, h(f(n), "setTimer", function () {
            var e = n.props.interval;
            n.clearTimer(), t.pooledTimer || 0 === e || (n.timer = setInterval(function () {
              n.update(n.props);
            }, e));
          }), h(f(n), "getTitle", function () {
            var e = n.props.titleFormat,
                r = t.getDatetime(n.props),
                o = e || t.globalFormat;
            return r.format(o);
          }), h(f(n), "clearTimer", function () {
            !t.pooledTimer && n.timer && (clearInterval(n.timer), n.timer = null), t.pooledTimer && !n.timer && t.removePooledElement(f(n));
          }), t.globalMoment || (t.globalMoment = u.a), n.state = {
            content: ""
          }, n.timer = null, n;
        }

        return function (e, t) {
          if ("function" != typeof t && null !== t) throw new TypeError("Super expression must either be null or a function");
          e.prototype = Object.create(t && t.prototype, {
            constructor: {
              value: e,
              writable: !0,
              configurable: !0
            }
          }), t && y(e, t);
        }(t, o.a.Component), g(t, null, [{
          key: "startPooledTimer",
          value: function value() {
            var e = arguments.length > 0 && void 0 !== arguments[0] ? arguments[0] : 6e4;
            t.clearPooledTimer(), t.pooledTimer = setInterval(function () {
              t.pooledElements.forEach(function (e) {
                0 !== e.props.interval && e.update();
              });
            }, e);
          }
        }, {
          key: "clearPooledTimer",
          value: function value() {
            t.pooledTimer && (clearInterval(t.pooledTimer), t.pooledTimer = null, t.pooledElements = []);
          }
        }, {
          key: "pushPooledElement",
          value: function value(e) {
            e instanceof t ? -1 === t.pooledElements.indexOf(e) && t.pooledElements.push(e) : console.error("Element not an instance of Moment.");
          }
        }, {
          key: "removePooledElement",
          value: function value(e) {
            var n = t.pooledElements.indexOf(e);
            -1 !== n && t.pooledElements.splice(n, 1);
          }
        }, {
          key: "getDatetime",
          value: function value(e) {
            var n = e.utc,
                r = e.unix,
                o = e.date,
                i = e.locale,
                a = e.parse,
                l = e.tz,
                u = e.local;
            o = o || e.children, a = a || t.globalParse, u = u || t.globalLocal, l = l || t.globalTimezone, i = t.globalLocale ? t.globalLocale : i || t.globalMoment.locale();
            var s = null;
            return s = n ? t.globalMoment.utc(o, a, i) : r ? t.globalMoment(1e3 * o, a, i) : t.globalMoment(o, a, i), l ? s = s.tz(l) : u && (s = s.local()), s;
          }
        }, {
          key: "getContent",
          value: function value(e) {
            var n = e.fromNow,
                r = e.fromNowDuring,
                o = e.from,
                i = e.add,
                a = e.subtract,
                l = e.toNow,
                s = e.to,
                c = e.ago,
                m = e.calendar,
                f = e.diff,
                p = e.duration,
                g = e.durationFromNow,
                y = e.unit,
                h = e.decimal,
                d = e.format;
            d = d || t.globalFormat;
            var v = t.getDatetime(e);
            i && v.add(i), a && v.subtract(a);
            var b = Boolean(r) && -v.diff(u()()) < r,
                S = "";
            return S = d && !b ? v.format(d) : o ? v.from(o, c) : n || b ? v.fromNow(c) : s ? v.to(s, c) : l ? v.toNow(c) : m ? v.calendar(null, m) : f ? v.diff(f, y, h) : p ? v.diff(p) : g ? u()().diff(v) : v.toString(), (p || g) && (S = (S = u.a.duration(S)).format(d)), S = (t.globalFilter || e.filter)(S);
          }
        }]), g(t, [{
          key: "componentDidMount",
          value: function value() {
            this.setTimer(), t.pooledTimer && t.pushPooledElement(this);
          }
        }, {
          key: "componentDidUpdate",
          value: function value(e) {
            var t = this.props.interval;
            e.interval !== t && this.setTimer();
          }
        }, {
          key: "componentWillUnmount",
          value: function value() {
            this.clearTimer();
          }
        }, {
          key: "update",
          value: function value(e) {
            var n = e || this.props,
                r = n.onChange,
                o = t.getContent(n);
            this.setState({
              content: o
            }, function () {
              r(o);
            });
          }
        }, {
          key: "render",
          value: function value() {
            var e,
                n,
                r,
                i,
                a = this.props,
                l = a.withTitle,
                u = a.element,
                s = c(a, ["withTitle", "element"]),
                m = this.state.content,
                f = (e = s, n = t.propTypes, r = Object.keys(n), i = Object.assign({}, e), Object.keys(i).filter(function (e) {
              return -1 !== r.indexOf(e);
            }).forEach(function (e) {
              return delete i[e];
            }), i);
            return l && (f.title = this.getTitle()), o.a.createElement(u || t.globalElement, function (e) {
              for (var t = 1; t < arguments.length; t++) {
                var n = null != arguments[t] ? arguments[t] : {},
                    r = Object.keys(n);
                "function" == typeof Object.getOwnPropertySymbols && (r = r.concat(Object.getOwnPropertySymbols(n).filter(function (e) {
                  return Object.getOwnPropertyDescriptor(n, e).enumerable;
                }))), r.forEach(function (t) {
                  h(e, t, n[t]);
                });
              }

              return e;
            }({
              dateTime: t.getDatetime(this.props)
            }, f), m);
          }
        }], [{
          key: "getDerivedStateFromProps",
          value: function value(e) {
            return {
              content: t.getContent(e)
            };
          }
        }]), t;
      }();

      h(S, "propTypes", {
        element: a.a.any,
        date: a.a.oneOfType(d),
        parse: a.a.oneOfType(v),
        format: a.a.string,
        add: a.a.object,
        subtract: a.a.object,
        ago: a.a.bool,
        fromNow: a.a.bool,
        fromNowDuring: a.a.number,
        from: a.a.oneOfType(d),
        toNow: a.a.bool,
        to: a.a.oneOfType(d),
        calendar: a.a.oneOfType(b),
        unix: a.a.bool,
        utc: a.a.bool,
        local: a.a.bool,
        tz: a.a.string,
        withTitle: a.a.bool,
        titleFormat: a.a.string,
        locale: a.a.string,
        interval: a.a.number,
        diff: a.a.oneOfType(d),
        duration: a.a.oneOfType(d),
        durationFromNow: a.a.bool,
        unit: a.a.string,
        decimal: a.a.bool,
        filter: a.a.func,
        onChange: a.a.func
      }), h(S, "defaultProps", {
        element: null,
        fromNow: !1,
        toNow: !1,
        calendar: !1,
        ago: !1,
        unix: !1,
        utc: !1,
        local: !1,
        unit: null,
        withTitle: !1,
        decimal: !1,
        titleFormat: "",
        interval: 6e4,
        filter: function filter(e) {
          return e;
        },
        onChange: function onChange() {}
      }), h(S, "globalMoment", null), h(S, "globalLocale", null), h(S, "globalLocal", null), h(S, "globalFormat", null), h(S, "globalParse", null), h(S, "globalFilter", null), h(S, "globalElement", "time"), h(S, "globalTimezone", null), h(S, "pooledElements", []), h(S, "pooledTimer", null);
    }]);
  });
});
var Moment = unwrapExports(dist);

function leafletMarkerClusterGroup(L) {
  if (L.MarkerClusterGroup) {
    return;
  }

  var MarkerClusterGroup = L.MarkerClusterGroup = L.FeatureGroup.extend({
    options: {
      maxClusterRadius: 80,
      iconCreateFunction: null,
      clusterPane: L.Marker.prototype.options.pane,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true,
      singleMarkerMode: false,
      shouldSpiderfy: null,
      unspiderfyOnMapClick: true,
      disableClusteringAtZoom: null,
      removeOutsideVisibleBounds: true,
      animate: true,
      animateAddingMarkers: false,
      spiderfyDistanceMultiplier: 1,
      spiderLegPolylineOptions: {
        weight: 1.5,
        color: '#222',
        opacity: 0.5
      },
      chunkedLoading: false,
      chunkInterval: 200,
      chunkDelay: 50,
      chunkProgress: null,
      polygonOptions: {}
    },
    initialize: function initialize(options) {
      L.Util.setOptions(this, options);

      if (!this.options.iconCreateFunction) {
        this.options.iconCreateFunction = this._defaultIconCreateFunction;
      }

      this._featureGroup = L.featureGroup();

      this._featureGroup.addEventParent(this);

      this._nonPointGroup = L.featureGroup();

      this._nonPointGroup.addEventParent(this);

      this._inZoomAnimation = 0;
      this._needsClustering = [];
      this._needsRemoving = [];
      this._currentShownBounds = null;
      this._queue = [];
      this._childMarkerEventHandlers = {
        'dragstart': this._childMarkerDragStart,
        'move': this._childMarkerMoved,
        'dragend': this._childMarkerDragEnd
      };
      var animate = L.DomUtil.TRANSITION && this.options.animate;
      L.extend(this, animate ? this._withAnimation : this._noAnimation);
      this._markerCluster = animate ? L.MarkerCluster : L.MarkerClusterNonAnimated;
    },
    refresh: function refresh() {
      this._topClusterLevel._recursivelySpiderfy(this._map._zoom, this._currentShownBounds, this.options.shouldSpiderfy);

      this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, this._zoom, this._currentShownBounds);

      this._topClusterLevel._recursivelyAddChildrenToMap(null, this._map._zoom, this._currentShownBounds);
    },
    addLayer: function addLayer(layer) {
      if (layer instanceof L.LayerGroup) {
        return this.addLayers([layer]);
      }

      if (!layer.getLatLng) {
        this._nonPointGroup.addLayer(layer);

        this.fire('layeradd', {
          layer: layer
        });
        return this;
      }

      if (!this._map) {
        this._needsClustering.push(layer);

        this.fire('layeradd', {
          layer: layer
        });
        return this;
      }

      if (this.hasLayer(layer)) {
        return this;
      }

      if (this._unspiderfy) {
        this._unspiderfy();
      }

      this._addLayer(layer, this._maxZoom);

      this.fire('layeradd', {
        layer: layer
      });

      this._topClusterLevel._recalculateBounds();

      this._refreshClustersIcons();

      var visibleLayer = layer,
          currentZoom = this._zoom;

      if (layer.__parent) {
        while (visibleLayer.__parent._zoom >= currentZoom) {
          visibleLayer = visibleLayer.__parent;
        }
      }

      if (this._currentShownBounds.contains(visibleLayer.getLatLng())) {
        if (this.options.animateAddingMarkers) {
          this._animationAddLayer(layer, visibleLayer);
        } else {
          this._animationAddLayerNonAnimated(layer, visibleLayer);
        }
      }

      return this;
    },
    removeLayer: function removeLayer(layer) {
      if (layer instanceof L.LayerGroup) {
        return this.removeLayers([layer]);
      }

      if (!layer.getLatLng) {
        this._nonPointGroup.removeLayer(layer);

        this.fire('layerremove', {
          layer: layer
        });
        return this;
      }

      if (!this._map) {
        if (!this._arraySplice(this._needsClustering, layer) && this.hasLayer(layer)) {
          this._needsRemoving.push({
            layer: layer,
            latlng: layer._latlng
          });
        }

        this.fire('layerremove', {
          layer: layer
        });
        return this;
      }

      if (!layer.__parent) {
        return this;
      }

      if (this._unspiderfy) {
        this._unspiderfy();

        this._unspiderfyLayer(layer);
      }

      this._removeLayer(layer, true);

      this.fire('layerremove', {
        layer: layer
      });

      this._topClusterLevel._recalculateBounds();

      this._refreshClustersIcons();

      layer.off(this._childMarkerEventHandlers, this);

      if (this._featureGroup.hasLayer(layer)) {
        this._featureGroup.removeLayer(layer);

        if (layer.clusterShow) {
          layer.clusterShow();
        }
      }

      return this;
    },
    addLayers: function addLayers(layersArray, skipLayerAddEvent) {
      if (!L.Util.isArray(layersArray)) {
        return this.addLayer(layersArray);
      }

      var fg = this._featureGroup,
          npg = this._nonPointGroup,
          chunked = this.options.chunkedLoading,
          chunkInterval = this.options.chunkInterval,
          chunkProgress = this.options.chunkProgress,
          l = layersArray.length,
          offset = 0,
          originalArray = true,
          m;

      if (this._map) {
        var started = new Date().getTime();
        var process = L.bind(function () {
          var start = new Date().getTime();

          for (; offset < l; offset++) {
            if (chunked && offset % 200 === 0) {
              var elapsed = new Date().getTime() - start;

              if (elapsed > chunkInterval) {
                break;
              }
            }

            m = layersArray[offset];

            if (m instanceof L.LayerGroup) {
              if (originalArray) {
                layersArray = layersArray.slice();
                originalArray = false;
              }

              this._extractNonGroupLayers(m, layersArray);

              l = layersArray.length;
              continue;
            }

            if (!m.getLatLng) {
              npg.addLayer(m);

              if (!skipLayerAddEvent) {
                this.fire('layeradd', {
                  layer: m
                });
              }

              continue;
            }

            if (this.hasLayer(m)) {
              continue;
            }

            this._addLayer(m, this._maxZoom);

            if (!skipLayerAddEvent) {
              this.fire('layeradd', {
                layer: m
              });
            }

            if (m.__parent) {
              if (m.__parent.getChildCount() === 2) {
                var markers = m.__parent.getAllChildMarkers(),
                    otherMarker = markers[0] === m ? markers[1] : markers[0];

                fg.removeLayer(otherMarker);
              }
            }
          }

          if (chunkProgress) {
            chunkProgress(offset, l, new Date().getTime() - started);
          }

          if (offset === l) {
            this._topClusterLevel._recalculateBounds();

            this._refreshClustersIcons();

            this._topClusterLevel._recursivelyAddChildrenToMap(null, this._zoom, this._currentShownBounds);
          } else {
            setTimeout(process, this.options.chunkDelay);
          }
        }, this);
        process();
      } else {
        var needsClustering = this._needsClustering;

        for (; offset < l; offset++) {
          m = layersArray[offset];

          if (m instanceof L.LayerGroup) {
            if (originalArray) {
              layersArray = layersArray.slice();
              originalArray = false;
            }

            this._extractNonGroupLayers(m, layersArray);

            l = layersArray.length;
            continue;
          }

          if (!m.getLatLng) {
            npg.addLayer(m);
            continue;
          }

          if (this.hasLayer(m)) {
            continue;
          }

          needsClustering.push(m);
        }
      }

      return this;
    },
    removeLayers: function removeLayers(layersArray) {
      var i,
          m,
          l = layersArray.length,
          fg = this._featureGroup,
          npg = this._nonPointGroup,
          originalArray = true;

      if (!this._map) {
        for (i = 0; i < l; i++) {
          m = layersArray[i];

          if (m instanceof L.LayerGroup) {
            if (originalArray) {
              layersArray = layersArray.slice();
              originalArray = false;
            }

            this._extractNonGroupLayers(m, layersArray);

            l = layersArray.length;
            continue;
          }

          this._arraySplice(this._needsClustering, m);

          npg.removeLayer(m);

          if (this.hasLayer(m)) {
            this._needsRemoving.push({
              layer: m,
              latlng: m._latlng
            });
          }

          this.fire('layerremove', {
            layer: m
          });
        }

        return this;
      }

      if (this._unspiderfy) {
        this._unspiderfy();

        var layersArray2 = layersArray.slice(),
            l2 = l;

        for (i = 0; i < l2; i++) {
          m = layersArray2[i];

          if (m instanceof L.LayerGroup) {
            this._extractNonGroupLayers(m, layersArray2);

            l2 = layersArray2.length;
            continue;
          }

          this._unspiderfyLayer(m);
        }
      }

      for (i = 0; i < l; i++) {
        m = layersArray[i];

        if (m instanceof L.LayerGroup) {
          if (originalArray) {
            layersArray = layersArray.slice();
            originalArray = false;
          }

          this._extractNonGroupLayers(m, layersArray);

          l = layersArray.length;
          continue;
        }

        if (!m.__parent) {
          npg.removeLayer(m);
          this.fire('layerremove', {
            layer: m
          });
          continue;
        }

        this._removeLayer(m, true, true);

        this.fire('layerremove', {
          layer: m
        });

        if (fg.hasLayer(m)) {
          fg.removeLayer(m);

          if (m.clusterShow) {
            m.clusterShow();
          }
        }
      }

      this._topClusterLevel._recalculateBounds();

      this._refreshClustersIcons();

      this._topClusterLevel._recursivelyAddChildrenToMap(null, this._zoom, this._currentShownBounds);

      return this;
    },
    clearLayers: function clearLayers() {
      if (!this._map) {
        this._needsClustering = [];
        this._needsRemoving = [];
        delete this._gridClusters;
        delete this._gridUnclustered;
      }

      if (this._noanimationUnspiderfy) {
        this._noanimationUnspiderfy();
      }

      this._featureGroup.clearLayers();

      this._nonPointGroup.clearLayers();

      this.eachLayer(function (marker) {
        marker.off(this._childMarkerEventHandlers, this);
        delete marker.__parent;
      }, this);

      if (this._map) {
        this._generateInitialClusters();
      }

      return this;
    },
    getBounds: function getBounds() {
      var bounds = new L.LatLngBounds();

      if (this._topClusterLevel) {
        bounds.extend(this._topClusterLevel._bounds);
      }

      for (var i = this._needsClustering.length - 1; i >= 0; i--) {
        bounds.extend(this._needsClustering[i].getLatLng());
      }

      bounds.extend(this._nonPointGroup.getBounds());
      return bounds;
    },
    eachLayer: function eachLayer(method, context) {
      var markers = this._needsClustering.slice(),
          needsRemoving = this._needsRemoving,
          thisNeedsRemoving,
          i,
          j;

      if (this._topClusterLevel) {
        this._topClusterLevel.getAllChildMarkers(markers);
      }

      for (i = markers.length - 1; i >= 0; i--) {
        thisNeedsRemoving = true;

        for (j = needsRemoving.length - 1; j >= 0; j--) {
          if (needsRemoving[j].layer === markers[i]) {
            thisNeedsRemoving = false;
            break;
          }
        }

        if (thisNeedsRemoving) {
          method.call(context, markers[i]);
        }
      }

      this._nonPointGroup.eachLayer(method, context);
    },
    getLayers: function getLayers() {
      var layers = [];
      this.eachLayer(function (l) {
        layers.push(l);
      });
      return layers;
    },
    getLayer: function getLayer(id) {
      var result = null;
      id = parseInt(id, 10);
      this.eachLayer(function (l) {
        if (L.stamp(l) === id) {
          result = l;
        }
      });
      return result;
    },
    hasLayer: function hasLayer(layer) {
      if (!layer) {
        return false;
      }

      var i,
          anArray = this._needsClustering;

      for (i = anArray.length - 1; i >= 0; i--) {
        if (anArray[i] === layer) {
          return true;
        }
      }

      anArray = this._needsRemoving;

      for (i = anArray.length - 1; i >= 0; i--) {
        if (anArray[i].layer === layer) {
          return false;
        }
      }

      return !!(layer.__parent && layer.__parent._group === this) || this._nonPointGroup.hasLayer(layer);
    },
    zoomToShowLayer: function zoomToShowLayer(layer, callback) {
      if (typeof callback !== 'function') {
        callback = function callback() {};
      }

      var showMarker = function showMarker() {
        if ((layer._icon || layer.__parent._icon) && !this._inZoomAnimation) {
          this._map.off('moveend', showMarker, this);

          this.off('animationend', showMarker, this);

          if (layer._icon) {
            callback();
          } else if (layer.__parent._icon) {
            this.once('spiderfied', callback, this);

            layer.__parent.spiderfy();
          }
        }
      };

      if (layer._icon && this._map.getBounds().contains(layer.getLatLng())) {
        callback();
      } else if (layer.__parent._zoom < Math.round(this._map._zoom)) {
        this._map.on('moveend', showMarker, this);

        this._map.panTo(layer.getLatLng());
      } else {
        this._map.on('moveend', showMarker, this);

        this.on('animationend', showMarker, this);

        layer.__parent.zoomToBounds();
      }
    },
    onAdd: function onAdd(map) {
      this._map = map;
      var i, l, layer;

      if (!isFinite(this._map.getMaxZoom())) {
        throw "Map has no maxZoom specified";
      }

      this._featureGroup.addTo(map);

      this._nonPointGroup.addTo(map);

      if (!this._gridClusters) {
        this._generateInitialClusters();
      }

      this._maxLat = map.options.crs.projection.MAX_LATITUDE;

      for (i = 0, l = this._needsRemoving.length; i < l; i++) {
        layer = this._needsRemoving[i];
        layer.newlatlng = layer.layer._latlng;
        layer.layer._latlng = layer.latlng;
      }

      for (i = 0, l = this._needsRemoving.length; i < l; i++) {
        layer = this._needsRemoving[i];

        this._removeLayer(layer.layer, true);

        layer.layer._latlng = layer.newlatlng;
      }

      this._needsRemoving = [];
      this._zoom = Math.round(this._map._zoom);
      this._currentShownBounds = this._getExpandedVisibleBounds();

      this._map.on('zoomend', this._zoomEnd, this);

      this._map.on('moveend', this._moveEnd, this);

      if (this._spiderfierOnAdd) {
        this._spiderfierOnAdd();
      }

      this._bindEvents();

      l = this._needsClustering;
      this._needsClustering = [];
      this.addLayers(l, true);

      this._topClusterLevel._recursivelySpiderfy(this._map._zoom, this._currentShownBounds, this.options.shouldSpiderfy);
    },
    onRemove: function onRemove(map) {
      map.off('zoomend', this._zoomEnd, this);
      map.off('moveend', this._moveEnd, this);

      this._unbindEvents();

      this._map._mapPane.className = this._map._mapPane.className.replace(' leaflet-cluster-anim', '');

      if (this._spiderfierOnRemove) {
        this._spiderfierOnRemove();
      }

      delete this._maxLat;

      this._hideCoverage();

      this._featureGroup.remove();

      this._nonPointGroup.remove();

      this._featureGroup.clearLayers();

      this._map = null;
    },
    getVisibleParent: function getVisibleParent(marker) {
      var vMarker = marker;

      while (vMarker && !vMarker._icon) {
        vMarker = vMarker.__parent;
      }

      return vMarker || null;
    },
    _arraySplice: function _arraySplice(anArray, obj) {
      for (var i = anArray.length - 1; i >= 0; i--) {
        if (anArray[i] === obj) {
          anArray.splice(i, 1);
          return true;
        }
      }
    },
    _removeFromGridUnclustered: function _removeFromGridUnclustered(marker, z) {
      var map = this._map,
          gridUnclustered = this._gridUnclustered,
          minZoom = Math.floor(this._map.getMinZoom());

      for (; z >= minZoom; z--) {
        if (!gridUnclustered[z].removeObject(marker, map.project(marker.getLatLng(), z))) {
          break;
        }
      }
    },
    _childMarkerDragStart: function _childMarkerDragStart(e) {
      e.target.__dragStart = e.target._latlng;
    },
    _childMarkerMoved: function _childMarkerMoved(e) {
      if (!this._ignoreMove && !e.target.__dragStart) {
        var isPopupOpen = e.target._popup && e.target._popup.isOpen();

        this._moveChild(e.target, e.oldLatLng, e.latlng);

        if (isPopupOpen) {
          e.target.openPopup();
        }
      }
    },
    _moveChild: function _moveChild(layer, from, to) {
      layer._latlng = from;
      this.removeLayer(layer);
      layer._latlng = to;
      this.addLayer(layer);
    },
    _childMarkerDragEnd: function _childMarkerDragEnd(e) {
      var dragStart = e.target.__dragStart;
      delete e.target.__dragStart;

      if (dragStart) {
        this._moveChild(e.target, dragStart, e.target._latlng);
      }
    },
    _removeLayer: function _removeLayer(marker, removeFromDistanceGrid, dontUpdateMap) {
      var gridClusters = this._gridClusters,
          gridUnclustered = this._gridUnclustered,
          fg = this._featureGroup,
          map = this._map,
          minZoom = Math.floor(this._map.getMinZoom());

      if (removeFromDistanceGrid) {
        this._removeFromGridUnclustered(marker, this._maxZoom);
      }

      var cluster = marker.__parent,
          markers = cluster._markers,
          otherMarker;

      this._arraySplice(markers, marker);

      while (cluster) {
        cluster._childCount--;
        cluster._boundsNeedUpdate = true;

        if (cluster._zoom < minZoom) {
          break;
        } else if (removeFromDistanceGrid && cluster._childCount <= 1) {
          otherMarker = cluster._markers[0] === marker ? cluster._markers[1] : cluster._markers[0];

          gridClusters[cluster._zoom].removeObject(cluster, map.project(cluster._cLatLng, cluster._zoom));

          gridUnclustered[cluster._zoom].addObject(otherMarker, map.project(otherMarker.getLatLng(), cluster._zoom));

          this._arraySplice(cluster.__parent._childClusters, cluster);

          cluster.__parent._markers.push(otherMarker);

          otherMarker.__parent = cluster.__parent;

          if (cluster._icon) {
            fg.removeLayer(cluster);

            if (!dontUpdateMap) {
              fg.addLayer(otherMarker);
            }
          }
        } else {
          cluster._iconNeedsUpdate = true;
        }

        cluster = cluster.__parent;
      }

      delete marker.__parent;
    },
    _isOrIsParent: function _isOrIsParent(el, oel) {
      while (oel) {
        if (el === oel) {
          return true;
        }

        oel = oel.parentNode;
      }

      return false;
    },
    fire: function fire(type, data, propagate) {
      if (data && data.layer instanceof L.MarkerCluster) {
        if (data.originalEvent && this._isOrIsParent(data.layer._icon, data.originalEvent.relatedTarget)) {
          return;
        }

        type = 'cluster' + type;
      }

      L.FeatureGroup.prototype.fire.call(this, type, data, propagate);
    },
    listens: function listens(type, propagate) {
      return L.FeatureGroup.prototype.listens.call(this, type, propagate) || L.FeatureGroup.prototype.listens.call(this, 'cluster' + type, propagate);
    },
    _defaultIconCreateFunction: function _defaultIconCreateFunction(cluster) {
      var childCount = cluster.getChildCount();
      var c = ' marker-cluster-';

      if (childCount < 10) {
        c += 'small';
      } else if (childCount < 100) {
        c += 'medium';
      } else {
        c += 'large';
      }

      return new L.DivIcon({
        html: '<div><span>' + childCount + '</span></div>',
        className: 'marker-cluster' + c,
        iconSize: new L.Point(40, 40)
      });
    },
    _bindEvents: function _bindEvents() {
      var map = this._map,
          spiderfyOnMaxZoom = this.options.spiderfyOnMaxZoom,
          showCoverageOnHover = this.options.showCoverageOnHover,
          zoomToBoundsOnClick = this.options.zoomToBoundsOnClick;

      if (spiderfyOnMaxZoom || zoomToBoundsOnClick) {
        this.on('clusterclick', this._zoomOrSpiderfy, this);
      }

      if (showCoverageOnHover) {
        this.on('clustermouseover', this._showCoverage, this);
        this.on('clustermouseout', this._hideCoverage, this);
        map.on('zoomend', this._hideCoverage, this);
      }
    },
    _zoomOrSpiderfy: function _zoomOrSpiderfy(e) {
      var cluster = e.layer,
          bottomCluster = cluster;

      while (bottomCluster._childClusters.length === 1) {
        bottomCluster = bottomCluster._childClusters[0];
      }

      if (bottomCluster._zoom === this._maxZoom && bottomCluster._childCount === cluster._childCount && this.options.spiderfyOnMaxZoom) {
        cluster.spiderfy();
      } else if (this.options.zoomToBoundsOnClick) {
        cluster.zoomToBounds();
      }

      if (e.originalEvent && e.originalEvent.keyCode === 13) {
        this._map._container.focus();
      }
    },
    _showCoverage: function _showCoverage(e) {
      var map = this._map;

      if (this._inZoomAnimation) {
        return;
      }

      if (this._shownPolygon) {
        map.removeLayer(this._shownPolygon);
      }

      if (e.layer.getChildCount() > 2 && e.layer !== this._spiderfied) {
        this._shownPolygon = new L.Polygon(e.layer.getConvexHull(), this.options.polygonOptions);
        map.addLayer(this._shownPolygon);
      }
    },
    _hideCoverage: function _hideCoverage() {
      if (this._shownPolygon) {
        this._map.removeLayer(this._shownPolygon);

        this._shownPolygon = null;
      }
    },
    _unbindEvents: function _unbindEvents() {
      var spiderfyOnMaxZoom = this.options.spiderfyOnMaxZoom,
          showCoverageOnHover = this.options.showCoverageOnHover,
          zoomToBoundsOnClick = this.options.zoomToBoundsOnClick,
          map = this._map;

      if (spiderfyOnMaxZoom || zoomToBoundsOnClick) {
        this.off('clusterclick', this._zoomOrSpiderfy, this);
      }

      if (showCoverageOnHover) {
        this.off('clustermouseover', this._showCoverage, this);
        this.off('clustermouseout', this._hideCoverage, this);
        map.off('zoomend', this._hideCoverage, this);
      }
    },
    _zoomEnd: function _zoomEnd() {
      if (!this._map) {
        return;
      }

      this._mergeSplitClusters();

      this._zoom = Math.round(this._map._zoom);
      this._currentShownBounds = this._getExpandedVisibleBounds();

      this._topClusterLevel._recursivelySpiderfy(this._map._zoom, this._currentShownBounds, this.options.shouldSpiderfy);

      this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, this._zoom, this._currentShownBounds);

      this._topClusterLevel._recursivelyAddChildrenToMap(null, this._map._zoom, this._currentShownBounds);
    },
    _moveEnd: function _moveEnd() {
      if (this._inZoomAnimation) {
        return;
      }

      var newBounds = this._getExpandedVisibleBounds();

      this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, Math.floor(this._map.getMinZoom()), this._zoom, newBounds);

      this._topClusterLevel._recursivelyAddChildrenToMap(null, Math.round(this._map._zoom), newBounds);

      this._currentShownBounds = newBounds;
      return;
    },
    _generateInitialClusters: function _generateInitialClusters() {
      var maxZoom = Math.ceil(this._map.getMaxZoom()),
          minZoom = Math.floor(this._map.getMinZoom()),
          radius = this.options.maxClusterRadius,
          radiusFn = radius;

      if (typeof radius !== "function") {
        radiusFn = function radiusFn() {
          return radius;
        };
      }

      if (this.options.disableClusteringAtZoom !== null) {
        maxZoom = this.options.disableClusteringAtZoom - 1;
      }

      this._maxZoom = maxZoom;
      this._gridClusters = {};
      this._gridUnclustered = {};

      for (var zoom = maxZoom; zoom >= minZoom; zoom--) {
        this._gridClusters[zoom] = new L.DistanceGrid(radiusFn(zoom));
        this._gridUnclustered[zoom] = new L.DistanceGrid(radiusFn(zoom));
      }

      this._topClusterLevel = new this._markerCluster(this, minZoom - 1);
    },
    _addLayer: function _addLayer(layer, zoom) {
      var gridClusters = this._gridClusters,
          gridUnclustered = this._gridUnclustered,
          minZoom = Math.floor(this._map.getMinZoom()),
          markerPoint,
          z;

      if (this.options.singleMarkerMode) {
        this._overrideMarkerIcon(layer);
      }

      layer.on(this._childMarkerEventHandlers, this);

      for (; zoom >= minZoom; zoom--) {
        markerPoint = this._map.project(layer.getLatLng(), zoom);
        var closest = gridClusters[zoom].getNearObject(markerPoint);

        if (closest) {
          closest._addChild(layer);

          layer.__parent = closest;
          return;
        }

        closest = gridUnclustered[zoom].getNearObject(markerPoint);

        if (closest) {
          var parent = closest.__parent;

          if (parent) {
            this._removeLayer(closest, false);
          }

          var newCluster = new this._markerCluster(this, zoom, closest, layer);
          gridClusters[zoom].addObject(newCluster, this._map.project(newCluster._cLatLng, zoom));
          closest.__parent = newCluster;
          layer.__parent = newCluster;
          var lastParent = newCluster;

          for (z = zoom - 1; z > parent._zoom; z--) {
            lastParent = new this._markerCluster(this, z, lastParent);
            gridClusters[z].addObject(lastParent, this._map.project(closest.getLatLng(), z));
          }

          parent._addChild(lastParent);

          this._removeFromGridUnclustered(closest, zoom);

          return;
        }

        gridUnclustered[zoom].addObject(layer, markerPoint);
      }

      this._topClusterLevel._addChild(layer);

      layer.__parent = this._topClusterLevel;
      return;
    },
    _refreshClustersIcons: function _refreshClustersIcons() {
      this._featureGroup.eachLayer(function (c) {
        if (c instanceof L.MarkerCluster && c._iconNeedsUpdate) {
          c._updateIcon();
        }
      });
    },
    _enqueue: function _enqueue(fn) {
      this._queue.push(fn);

      if (!this._queueTimeout) {
        this._queueTimeout = setTimeout(L.bind(this._processQueue, this), 300);
      }
    },
    _processQueue: function _processQueue() {
      for (var i = 0; i < this._queue.length; i++) {
        this._queue[i].call(this);
      }

      this._queue.length = 0;
      clearTimeout(this._queueTimeout);
      this._queueTimeout = null;
    },
    _mergeSplitClusters: function _mergeSplitClusters() {
      var mapZoom = Math.round(this._map._zoom);

      this._processQueue();

      if (this._zoom < mapZoom && this._currentShownBounds.intersects(this._getExpandedVisibleBounds())) {
        this._animationStart();

        this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, Math.floor(this._map.getMinZoom()), this._zoom, this._getExpandedVisibleBounds());

        this._animationZoomIn(this._zoom, mapZoom);
      } else if (this._zoom > mapZoom) {
        this._animationStart();

        this._animationZoomOut(this._zoom, mapZoom);
      } else {
        this._moveEnd();
      }
    },
    _getExpandedVisibleBounds: function _getExpandedVisibleBounds() {
      if (!this.options.removeOutsideVisibleBounds) {
        return this._mapBoundsInfinite;
      } else if (L.Browser.mobile) {
        return this._checkBoundsMaxLat(this._map.getBounds());
      }

      return this._checkBoundsMaxLat(this._map.getBounds().pad(1));
    },
    _checkBoundsMaxLat: function _checkBoundsMaxLat(bounds) {
      var maxLat = this._maxLat;

      if (maxLat !== undefined) {
        if (bounds.getNorth() >= maxLat) {
          bounds._northEast.lat = Infinity;
        }

        if (bounds.getSouth() <= -maxLat) {
          bounds._southWest.lat = -Infinity;
        }
      }

      return bounds;
    },
    _animationAddLayerNonAnimated: function _animationAddLayerNonAnimated(layer, newCluster) {
      if (newCluster === layer) {
        this._featureGroup.addLayer(layer);
      } else if (newCluster._childCount === 2) {
        newCluster._addToMap();

        var markers = newCluster.getAllChildMarkers();

        this._featureGroup.removeLayer(markers[0]);

        this._featureGroup.removeLayer(markers[1]);
      } else {
        newCluster._updateIcon();
      }
    },
    _extractNonGroupLayers: function _extractNonGroupLayers(group, output) {
      var layers = group.getLayers(),
          i = 0,
          layer;
      output = output || [];

      for (; i < layers.length; i++) {
        layer = layers[i];

        if (layer instanceof L.LayerGroup) {
          this._extractNonGroupLayers(layer, output);

          continue;
        }

        output.push(layer);
      }

      return output;
    },
    _overrideMarkerIcon: function _overrideMarkerIcon(layer) {
      var icon = layer.options.icon = this.options.iconCreateFunction({
        getChildCount: function getChildCount() {
          return 1;
        },
        getAllChildMarkers: function getAllChildMarkers() {
          return [layer];
        }
      });
      return icon;
    }
  });
  L.MarkerClusterGroup.include({
    _mapBoundsInfinite: new L.LatLngBounds(new L.LatLng(-Infinity, -Infinity), new L.LatLng(Infinity, Infinity))
  });
  L.MarkerClusterGroup.include({
    _noAnimation: {
      _animationStart: function _animationStart() {},
      _animationZoomIn: function _animationZoomIn(previousZoomLevel, newZoomLevel) {
        this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, Math.floor(this._map.getMinZoom()), previousZoomLevel);

        this._topClusterLevel._recursivelyAddChildrenToMap(null, newZoomLevel, this._getExpandedVisibleBounds());

        this.fire('animationend');
      },
      _animationZoomOut: function _animationZoomOut(previousZoomLevel, newZoomLevel) {
        this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, Math.floor(this._map.getMinZoom()), previousZoomLevel);

        this._topClusterLevel._recursivelyAddChildrenToMap(null, newZoomLevel, this._getExpandedVisibleBounds());

        this.fire('animationend');
      },
      _animationAddLayer: function _animationAddLayer(layer, newCluster) {
        this._animationAddLayerNonAnimated(layer, newCluster);
      }
    },
    _withAnimation: {
      _animationStart: function _animationStart() {
        this._map._mapPane.className += ' leaflet-cluster-anim';
        this._inZoomAnimation++;
      },
      _animationZoomIn: function _animationZoomIn(previousZoomLevel, newZoomLevel) {
        var bounds = this._getExpandedVisibleBounds(),
            fg = this._featureGroup,
            minZoom = Math.floor(this._map.getMinZoom()),
            i;

        this._ignoreMove = true;

        this._topClusterLevel._recursively(bounds, previousZoomLevel, minZoom, function (c) {
          var startPos = c._latlng,
              markers = c._markers,
              m;

          if (!bounds.contains(startPos)) {
            startPos = null;
          }

          if (c._isSingleParent() && previousZoomLevel + 1 === newZoomLevel) {
            fg.removeLayer(c);

            c._recursivelyAddChildrenToMap(null, newZoomLevel, bounds);
          } else {
            c.clusterHide();

            c._recursivelyAddChildrenToMap(startPos, newZoomLevel, bounds);
          }

          for (i = markers.length - 1; i >= 0; i--) {
            m = markers[i];

            if (!bounds.contains(m._latlng)) {
              fg.removeLayer(m);
            }
          }
        });

        this._forceLayout();

        this._topClusterLevel._recursivelyBecomeVisible(bounds, newZoomLevel);

        fg.eachLayer(function (n) {
          if (!(n instanceof L.MarkerCluster) && n._icon) {
            n.clusterShow();
          }
        });

        this._topClusterLevel._recursively(bounds, previousZoomLevel, newZoomLevel, function (c) {
          c._recursivelyRestoreChildPositions(newZoomLevel);
        });

        this._ignoreMove = false;

        this._enqueue(function () {
          this._topClusterLevel._recursively(bounds, previousZoomLevel, minZoom, function (c) {
            fg.removeLayer(c);
            c.clusterShow();
          });

          this._animationEnd();
        });
      },
      _animationZoomOut: function _animationZoomOut(previousZoomLevel, newZoomLevel) {
        this._animationZoomOutSingle(this._topClusterLevel, previousZoomLevel - 1, newZoomLevel);

        this._topClusterLevel._recursivelyAddChildrenToMap(null, newZoomLevel, this._getExpandedVisibleBounds());

        this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, Math.floor(this._map.getMinZoom()), previousZoomLevel, this._getExpandedVisibleBounds());
      },
      _animationAddLayer: function _animationAddLayer(layer, newCluster) {
        var me = this,
            fg = this._featureGroup;
        fg.addLayer(layer);

        if (newCluster !== layer) {
          if (newCluster._childCount > 2) {
            newCluster._updateIcon();

            this._forceLayout();

            this._animationStart();

            layer._setPos(this._map.latLngToLayerPoint(newCluster.getLatLng()));

            layer.clusterHide();

            this._enqueue(function () {
              fg.removeLayer(layer);
              layer.clusterShow();

              me._animationEnd();
            });
          } else {
            this._forceLayout();

            me._animationStart();

            me._animationZoomOutSingle(newCluster, this._map.getMaxZoom(), this._zoom);
          }
        }
      }
    },
    _animationZoomOutSingle: function _animationZoomOutSingle(cluster, previousZoomLevel, newZoomLevel) {
      var bounds = this._getExpandedVisibleBounds(),
          minZoom = Math.floor(this._map.getMinZoom());

      cluster._recursivelyAnimateChildrenInAndAddSelfToMap(bounds, minZoom, previousZoomLevel + 1, newZoomLevel);

      var me = this;

      this._forceLayout();

      cluster._recursivelyBecomeVisible(bounds, newZoomLevel);

      this._enqueue(function () {
        if (cluster._childCount === 1) {
          var m = cluster._markers[0];
          this._ignoreMove = true;
          m.setLatLng(m.getLatLng());
          this._ignoreMove = false;

          if (m.clusterShow) {
            m.clusterShow();
          }
        } else {
          cluster._recursively(bounds, newZoomLevel, minZoom, function (c) {
            c._recursivelyRemoveChildrenFromMap(bounds, minZoom, previousZoomLevel + 1);
          });
        }

        me._animationEnd();
      });
    },
    _animationEnd: function _animationEnd() {
      if (this._map) {
        this._map._mapPane.className = this._map._mapPane.className.replace(' leaflet-cluster-anim', '');
      }

      this._inZoomAnimation--;
      this.fire('animationend');
    },
    _forceLayout: function _forceLayout() {
      L.Util.falseFn(document.body.offsetWidth);
    }
  });

  L.markerClusterGroup = function (options) {
    return new L.MarkerClusterGroup(options);
  };

  var MarkerCluster = L.MarkerCluster = L.Marker.extend({
    options: L.Icon.prototype.options,
    initialize: function initialize(group, zoom, a, b) {
      L.Marker.prototype.initialize.call(this, a ? a._cLatLng || a.getLatLng() : new L.LatLng(0, 0), {
        icon: this,
        pane: group.options.clusterPane
      });
      this._group = group;
      this._zoom = zoom;
      this._markers = [];
      this._childClusters = [];
      this._childCount = 0;
      this._iconNeedsUpdate = true;
      this._boundsNeedUpdate = true;
      this._bounds = new L.LatLngBounds();

      if (a) {
        this._addChild(a);
      }

      if (b) {
        this._addChild(b);
      }
    },
    getAllChildMarkers: function getAllChildMarkers(storageArray, ignoreDraggedMarker) {
      storageArray = storageArray || [];

      for (var i = this._childClusters.length - 1; i >= 0; i--) {
        this._childClusters[i].getAllChildMarkers(storageArray);
      }

      for (var j = this._markers.length - 1; j >= 0; j--) {
        if (ignoreDraggedMarker && this._markers[j].__dragStart) {
          continue;
        }

        storageArray.push(this._markers[j]);
      }

      return storageArray;
    },
    getChildCount: function getChildCount() {
      return this._childCount;
    },
    zoomToBounds: function zoomToBounds(fitBoundsOptions) {
      var childClusters = this._childClusters.slice(),
          map = this._group._map,
          boundsZoom = map.getBoundsZoom(this._bounds),
          zoom = this._zoom + 1,
          mapZoom = map.getZoom(),
          i;

      while (childClusters.length > 0 && boundsZoom > zoom) {
        zoom++;
        var newClusters = [];

        for (i = 0; i < childClusters.length; i++) {
          newClusters = newClusters.concat(childClusters[i]._childClusters);
        }

        childClusters = newClusters;
      }

      if (boundsZoom > zoom) {
        this._group._map.setView(this._latlng, zoom);
      } else if (boundsZoom <= mapZoom) {
        this._group._map.setView(this._latlng, mapZoom + 1);
      } else {
        this._group._map.fitBounds(this._bounds, fitBoundsOptions);
      }
    },
    getBounds: function getBounds() {
      var bounds = new L.LatLngBounds();
      bounds.extend(this._bounds);
      return bounds;
    },
    _updateIcon: function _updateIcon() {
      this._iconNeedsUpdate = true;

      if (this._icon) {
        this.setIcon(this);
      }
    },
    createIcon: function createIcon() {
      if (this._iconNeedsUpdate) {
        this._iconObj = this._group.options.iconCreateFunction(this);
        this._iconNeedsUpdate = false;
      }

      return this._iconObj.createIcon();
    },
    createShadow: function createShadow() {
      return this._iconObj.createShadow();
    },
    _addChild: function _addChild(new1, isNotificationFromChild) {
      this._iconNeedsUpdate = true;
      this._boundsNeedUpdate = true;

      this._setClusterCenter(new1);

      if (new1 instanceof L.MarkerCluster) {
        if (!isNotificationFromChild) {
          this._childClusters.push(new1);

          new1.__parent = this;
        }

        this._childCount += new1._childCount;
      } else {
        if (!isNotificationFromChild) {
          this._markers.push(new1);
        }

        this._childCount++;
      }

      if (this.__parent) {
        this.__parent._addChild(new1, true);
      }
    },
    _setClusterCenter: function _setClusterCenter(child) {
      if (!this._cLatLng) {
        this._cLatLng = child._cLatLng || child._latlng;
      }
    },
    _resetBounds: function _resetBounds() {
      var bounds = this._bounds;

      if (bounds._southWest) {
        bounds._southWest.lat = Infinity;
        bounds._southWest.lng = Infinity;
      }

      if (bounds._northEast) {
        bounds._northEast.lat = -Infinity;
        bounds._northEast.lng = -Infinity;
      }
    },
    _recalculateBounds: function _recalculateBounds() {
      var markers = this._markers,
          childClusters = this._childClusters,
          latSum = 0,
          lngSum = 0,
          totalCount = this._childCount,
          i,
          child,
          childLatLng,
          childCount;

      if (totalCount === 0) {
        return;
      }

      this._resetBounds();

      for (i = 0; i < markers.length; i++) {
        childLatLng = markers[i]._latlng;

        this._bounds.extend(childLatLng);

        latSum += childLatLng.lat;
        lngSum += childLatLng.lng;
      }

      for (i = 0; i < childClusters.length; i++) {
        child = childClusters[i];

        if (child._boundsNeedUpdate) {
          child._recalculateBounds();
        }

        this._bounds.extend(child._bounds);

        childLatLng = child._wLatLng;
        childCount = child._childCount;
        latSum += childLatLng.lat * childCount;
        lngSum += childLatLng.lng * childCount;
      }

      this._latlng = this._wLatLng = new L.LatLng(latSum / totalCount, lngSum / totalCount);
      this._boundsNeedUpdate = false;
    },
    _addToMap: function _addToMap(startPos) {
      if (startPos) {
        this._backupLatlng = this._latlng;
        this.setLatLng(startPos);
      }

      this._group._featureGroup.addLayer(this);
    },
    _recursivelyAnimateChildrenIn: function _recursivelyAnimateChildrenIn(bounds, center, maxZoom) {
      this._recursively(bounds, this._group._map.getMinZoom(), maxZoom - 1, function (c) {
        var markers = c._markers,
            i,
            m;

        for (i = markers.length - 1; i >= 0; i--) {
          m = markers[i];

          if (m._icon) {
            m._setPos(center);

            m.clusterHide();
          }
        }
      }, function (c) {
        var childClusters = c._childClusters,
            j,
            cm;

        for (j = childClusters.length - 1; j >= 0; j--) {
          cm = childClusters[j];

          if (cm._icon) {
            cm._setPos(center);

            cm.clusterHide();
          }
        }
      });
    },
    _recursivelyAnimateChildrenInAndAddSelfToMap: function _recursivelyAnimateChildrenInAndAddSelfToMap(bounds, mapMinZoom, previousZoomLevel, newZoomLevel) {
      this._recursively(bounds, newZoomLevel, mapMinZoom, function (c) {
        c._recursivelyAnimateChildrenIn(bounds, c._group._map.latLngToLayerPoint(c.getLatLng()).round(), previousZoomLevel);

        if (c._isSingleParent() && previousZoomLevel - 1 === newZoomLevel) {
          c.clusterShow();

          c._recursivelyRemoveChildrenFromMap(bounds, mapMinZoom, previousZoomLevel);
        } else {
          c.clusterHide();
        }

        c._addToMap();
      });
    },
    _recursivelyBecomeVisible: function _recursivelyBecomeVisible(bounds, zoomLevel) {
      this._recursively(bounds, this._group._map.getMinZoom(), zoomLevel, null, function (c) {
        c.clusterShow();
      });
    },
    _recursivelyAddChildrenToMap: function _recursivelyAddChildrenToMap(startPos, zoomLevel, bounds) {
      this._recursively(bounds, this._group._map.getMinZoom() - 1, zoomLevel, function (c) {
        if (zoomLevel === c._zoom) {
          return;
        }

        for (var i = c._markers.length - 1; i >= 0; i--) {
          var nm = c._markers[i];

          if (!bounds.contains(nm._latlng)) {
            continue;
          }

          if (startPos) {
            nm._backupLatlng = nm.getLatLng();
            nm.setLatLng(startPos);

            if (nm.clusterHide) {
              nm.clusterHide();
            }
          }

          c._group._featureGroup.addLayer(nm);
        }
      }, function (c) {
        c._addToMap(startPos);
      });
    },
    _recursivelySpiderfy: function _recursivelySpiderfy(zoomLevel, bounds, shouldSpiderfy) {
      this._recursively(bounds, -1, zoomLevel, function (c) {
        c._noanimationUnspiderfy();
      }, function (c) {
        if (shouldSpiderfy && shouldSpiderfy(c, zoomLevel)) {
          c.spiderfy();
        }
      });
    },
    _recursivelyRestoreChildPositions: function _recursivelyRestoreChildPositions(zoomLevel) {
      for (var i = this._markers.length - 1; i >= 0; i--) {
        var nm = this._markers[i];

        if (nm._backupLatlng) {
          nm.setLatLng(nm._backupLatlng);
          delete nm._backupLatlng;
        }
      }

      if (zoomLevel - 1 === this._zoom) {
        for (var j = this._childClusters.length - 1; j >= 0; j--) {
          this._childClusters[j]._restorePosition();
        }
      } else {
        for (var k = this._childClusters.length - 1; k >= 0; k--) {
          this._childClusters[k]._recursivelyRestoreChildPositions(zoomLevel);
        }
      }
    },
    _restorePosition: function _restorePosition() {
      if (this._backupLatlng) {
        this.setLatLng(this._backupLatlng);
        delete this._backupLatlng;
      }
    },
    _recursivelyRemoveChildrenFromMap: function _recursivelyRemoveChildrenFromMap(previousBounds, mapMinZoom, zoomLevel, exceptBounds) {
      var m, i;

      this._recursively(previousBounds, mapMinZoom - 1, zoomLevel - 1, function (c) {
        for (i = c._markers.length - 1; i >= 0; i--) {
          m = c._markers[i];

          if (!exceptBounds || !exceptBounds.contains(m._latlng)) {
            c._group._featureGroup.removeLayer(m);

            if (m.clusterShow) {
              m.clusterShow();
            }
          }
        }
      }, function (c) {
        for (i = c._childClusters.length - 1; i >= 0; i--) {
          m = c._childClusters[i];

          if (!exceptBounds || !exceptBounds.contains(m._latlng)) {
            c._group._featureGroup.removeLayer(m);

            if (m.clusterShow) {
              m.clusterShow();
            }
          }
        }
      });
    },
    _recursively: function _recursively(boundsToApplyTo, zoomLevelToStart, zoomLevelToStop, runAtEveryLevel, runAtBottomLevel) {
      var childClusters = this._childClusters,
          zoom = this._zoom,
          i,
          c;

      if (zoomLevelToStart <= zoom) {
        if (runAtEveryLevel) {
          runAtEveryLevel(this);
        }

        if (runAtBottomLevel && zoom === zoomLevelToStop) {
          runAtBottomLevel(this);
        }
      }

      if (zoom < zoomLevelToStart || zoom < zoomLevelToStop) {
        for (i = childClusters.length - 1; i >= 0; i--) {
          c = childClusters[i];

          if (c._boundsNeedUpdate) {
            c._recalculateBounds();
          }

          if (boundsToApplyTo.intersects(c._bounds)) {
            c._recursively(boundsToApplyTo, zoomLevelToStart, zoomLevelToStop, runAtEveryLevel, runAtBottomLevel);
          }
        }
      }
    },
    _isSingleParent: function _isSingleParent() {
      return this._childClusters.length > 0 && this._childClusters[0]._childCount === this._childCount;
    }
  });
  L.Marker.include({
    clusterHide: function clusterHide() {
      var backup = this.options.opacity;
      this.setOpacity(0);
      this.options.opacity = backup;
      return this;
    },
    clusterShow: function clusterShow() {
      return this.setOpacity(this.options.opacity);
    }
  });

  L.DistanceGrid = function (cellSize) {
    this._cellSize = cellSize;
    this._sqCellSize = cellSize * cellSize;
    this._grid = {};
    this._objectPoint = {};
  };

  L.DistanceGrid.prototype = {
    addObject: function addObject(obj, point) {
      var x = this._getCoord(point.x),
          y = this._getCoord(point.y),
          grid = this._grid,
          row = grid[y] = grid[y] || {},
          cell = row[x] = row[x] || [],
          stamp = L.Util.stamp(obj);

      this._objectPoint[stamp] = point;
      cell.push(obj);
    },
    updateObject: function updateObject(obj, point) {
      this.removeObject(obj);
      this.addObject(obj, point);
    },
    removeObject: function removeObject(obj, point) {
      var x = this._getCoord(point.x),
          y = this._getCoord(point.y),
          grid = this._grid,
          row = grid[y] = grid[y] || {},
          cell = row[x] = row[x] || [],
          i,
          len;

      delete this._objectPoint[L.Util.stamp(obj)];

      for (i = 0, len = cell.length; i < len; i++) {
        if (cell[i] === obj) {
          cell.splice(i, 1);

          if (len === 1) {
            delete row[x];
          }

          return true;
        }
      }
    },
    eachObject: function eachObject(fn, context) {
      var i,
          j,
          k,
          len,
          row,
          cell,
          removed,
          grid = this._grid;

      for (i in grid) {
        row = grid[i];

        for (j in row) {
          cell = row[j];

          for (k = 0, len = cell.length; k < len; k++) {
            removed = fn.call(context, cell[k]);

            if (removed) {
              k--;
              len--;
            }
          }
        }
      }
    },
    getNearObject: function getNearObject(point) {
      var x = this._getCoord(point.x),
          y = this._getCoord(point.y),
          i,
          j,
          k,
          row,
          cell,
          len,
          obj,
          dist,
          objectPoint = this._objectPoint,
          closestDistSq = this._sqCellSize,
          closest = null;

      for (i = y - 1; i <= y + 1; i++) {
        row = this._grid[i];

        if (row) {
          for (j = x - 1; j <= x + 1; j++) {
            cell = row[j];

            if (cell) {
              for (k = 0, len = cell.length; k < len; k++) {
                obj = cell[k];
                dist = this._sqDist(objectPoint[L.Util.stamp(obj)], point);

                if (dist < closestDistSq || dist <= closestDistSq && closest === null) {
                  closestDistSq = dist;
                  closest = obj;
                }
              }
            }
          }
        }
      }

      return closest;
    },
    _getCoord: function _getCoord(x) {
      var coord = Math.floor(x / this._cellSize);
      return isFinite(coord) ? coord : x;
    },
    _sqDist: function _sqDist(p, p2) {
      var dx = p2.x - p.x,
          dy = p2.y - p.y;
      return dx * dx + dy * dy;
    }
  };

  (function () {
    L.QuickHull = {
      getDistant: function getDistant(cpt, bl) {
        var vY = bl[1].lat - bl[0].lat,
            vX = bl[0].lng - bl[1].lng;
        return vX * (cpt.lat - bl[0].lat) + vY * (cpt.lng - bl[0].lng);
      },
      findMostDistantPointFromBaseLine: function findMostDistantPointFromBaseLine(baseLine, latLngs) {
        var maxD = 0,
            maxPt = null,
            newPoints = [],
            i,
            pt,
            d;

        for (i = latLngs.length - 1; i >= 0; i--) {
          pt = latLngs[i];
          d = this.getDistant(pt, baseLine);

          if (d > 0) {
            newPoints.push(pt);
          } else {
            continue;
          }

          if (d > maxD) {
            maxD = d;
            maxPt = pt;
          }
        }

        return {
          maxPoint: maxPt,
          newPoints: newPoints
        };
      },
      buildConvexHull: function buildConvexHull(baseLine, latLngs) {
        var convexHullBaseLines = [],
            t = this.findMostDistantPointFromBaseLine(baseLine, latLngs);

        if (t.maxPoint) {
          convexHullBaseLines = convexHullBaseLines.concat(this.buildConvexHull([baseLine[0], t.maxPoint], t.newPoints));
          convexHullBaseLines = convexHullBaseLines.concat(this.buildConvexHull([t.maxPoint, baseLine[1]], t.newPoints));
          return convexHullBaseLines;
        } else {
          return [baseLine[0]];
        }
      },
      getConvexHull: function getConvexHull(latLngs) {
        var maxLat = false,
            minLat = false,
            maxLng = false,
            minLng = false,
            maxLatPt = null,
            minLatPt = null,
            maxLngPt = null,
            minLngPt = null,
            maxPt = null,
            minPt = null,
            i;

        for (i = latLngs.length - 1; i >= 0; i--) {
          var pt = latLngs[i];

          if (maxLat === false || pt.lat > maxLat) {
            maxLatPt = pt;
            maxLat = pt.lat;
          }

          if (minLat === false || pt.lat < minLat) {
            minLatPt = pt;
            minLat = pt.lat;
          }

          if (maxLng === false || pt.lng > maxLng) {
            maxLngPt = pt;
            maxLng = pt.lng;
          }

          if (minLng === false || pt.lng < minLng) {
            minLngPt = pt;
            minLng = pt.lng;
          }
        }

        if (minLat !== maxLat) {
          minPt = minLatPt;
          maxPt = maxLatPt;
        } else {
          minPt = minLngPt;
          maxPt = maxLngPt;
        }

        var ch = [].concat(this.buildConvexHull([minPt, maxPt], latLngs), this.buildConvexHull([maxPt, minPt], latLngs));
        return ch;
      }
    };
  })();

  L.MarkerCluster.include({
    getConvexHull: function getConvexHull() {
      var childMarkers = this.getAllChildMarkers(),
          points = [],
          p,
          i;

      for (i = childMarkers.length - 1; i >= 0; i--) {
        p = childMarkers[i].getLatLng();
        points.push(p);
      }

      return L.QuickHull.getConvexHull(points);
    }
  });
  L.MarkerCluster.include({
    _2PI: Math.PI * 2,
    _circleFootSeparation: 25,
    _circleStartAngle: 0,
    _spiralFootSeparation: 28,
    _spiralLengthStart: 11,
    _spiralLengthFactor: 5,
    _circleSpiralSwitchover: 9,
    spiderfy: function spiderfy() {
      if (this._group._isSpiderfied(this)) {
        return;
      }

      var childMarkers = this.getAllChildMarkers(null, true),
          group = this._group,
          map = group._map,
          center = map.latLngToLayerPoint(this._latlng),
          positions;

      this._group._addSpiderfied(this);

      if (childMarkers.length >= this._circleSpiralSwitchover) {
        positions = this._generatePointsSpiral(childMarkers.length, center);
      } else {
        center.y += 10;
        positions = this._generatePointsCircle(childMarkers.length, center);
      }

      this._animationSpiderfy(childMarkers, positions);
    },
    unspiderfy: function unspiderfy(zoomDetails) {
      this._animationUnspiderfy(zoomDetails);

      this._group._removeSpiderfied(this);
    },
    _generatePointsCircle: function _generatePointsCircle(count, centerPt) {
      var circumference = this._group.options.spiderfyDistanceMultiplier * this._circleFootSeparation * (2 + count),
          legLength = circumference / this._2PI,
          angleStep = this._2PI / count,
          res = [],
          i,
          angle;
      legLength = Math.max(legLength, 35);
      res.length = count;

      for (i = 0; i < count; i++) {
        angle = this._circleStartAngle + i * angleStep;
        res[i] = new L.Point(centerPt.x + legLength * Math.cos(angle), centerPt.y + legLength * Math.sin(angle))._round();
      }

      return res;
    },
    _generatePointsSpiral: function _generatePointsSpiral(count, centerPt) {
      var spiderfyDistanceMultiplier = this._group.options.spiderfyDistanceMultiplier,
          legLength = spiderfyDistanceMultiplier * this._spiralLengthStart,
          separation = spiderfyDistanceMultiplier * this._spiralFootSeparation,
          lengthFactor = spiderfyDistanceMultiplier * this._spiralLengthFactor * this._2PI,
          angle = 0,
          res = [],
          i;
      res.length = count;

      for (i = count; i >= 0; i--) {
        if (i < count) {
          res[i] = new L.Point(centerPt.x + legLength * Math.cos(angle), centerPt.y + legLength * Math.sin(angle))._round();
        }

        angle += separation / legLength + i * 0.0005;
        legLength += lengthFactor / angle;
      }

      return res;
    },
    _noanimationUnspiderfy: function _noanimationUnspiderfy() {
      var group = this._group,
          map = group._map,
          fg = group._featureGroup,
          childMarkers = this.getAllChildMarkers(null, true),
          m,
          i;
      group._ignoreMove = true;
      this.setOpacity(1);

      for (i = childMarkers.length - 1; i >= 0; i--) {
        m = childMarkers[i];
        fg.removeLayer(m);

        if (m._preSpiderfyLatlng) {
          m.setLatLng(m._preSpiderfyLatlng);
          delete m._preSpiderfyLatlng;
        }

        if (m.setZIndexOffset) {
          m.setZIndexOffset(0);
        }

        if (m._spiderLeg) {
          map.removeLayer(m._spiderLeg);
          delete m._spiderLeg;
        }
      }

      group.fire('unspiderfied', {
        cluster: this,
        markers: childMarkers
      });

      group._removeSpiderfied(this);
    }
  });
  L.MarkerClusterNonAnimated = L.MarkerCluster.extend({
    _animationSpiderfy: function _animationSpiderfy(childMarkers, positions) {
      var group = this._group,
          map = group._map,
          fg = group._featureGroup,
          legOptions = this._group.options.spiderLegPolylineOptions,
          i,
          m,
          leg,
          newPos;

      for (i = 0; i < childMarkers.length; i++) {
        newPos = map.layerPointToLatLng(positions[i]);
        m = childMarkers[i];
        leg = new L.Polyline([this._latlng, newPos], legOptions);
        map.addLayer(leg);
        m._spiderLeg = leg;
        m._preSpiderfyLatlng = m._latlng;
        m.setLatLng(newPos);

        if (m.setZIndexOffset) {
          m.setZIndexOffset(1000000);
        }

        fg.addLayer(m);
      }

      this.setOpacity(0.3);
      group.fire('spiderfied', {
        cluster: this,
        markers: childMarkers
      });
    },
    _animationSpiderfyOld: function _animationSpiderfyOld(childMarkers, positions) {
      var group = this._group,
          map = group._map,
          fg = group._featureGroup,
          legOptions = this._group.options.spiderLegPolylineOptions,
          i,
          m,
          leg,
          newPos;
      group._ignoreMove = true;

      for (i = 0; i < childMarkers.length; i++) {
        newPos = map.layerPointToLatLng(positions[i]);
        m = childMarkers[i];
        leg = new L.Polyline([this._latlng, newPos], legOptions);
        map.addLayer(leg);
        m._spiderLeg = leg;
        m._preSpiderfyLatlng = m._latlng;
        m.setLatLng(newPos);

        if (m.setZIndexOffset) {
          m.setZIndexOffset(1000000);
        }

        fg.addLayer(m);
      }

      this.setOpacity(0.3);
      group._ignoreMove = false;
      group.fire('spiderfied', {
        cluster: this,
        markers: childMarkers
      });
    },
    _animationUnspiderfy: function _animationUnspiderfy() {
      this._noanimationUnspiderfy();
    }
  });
  L.MarkerCluster.include({
    _animationSpiderfy: function _animationSpiderfy(childMarkers, positions) {
      var me = this,
          group = this._group,
          map = group._map,
          fg = group._featureGroup,
          thisLayerLatLng = this._latlng,
          thisLayerPos = map.latLngToLayerPoint(thisLayerLatLng),
          svg = L.Path.SVG,
          legOptions = L.extend({}, this._group.options.spiderLegPolylineOptions),
          finalLegOpacity = legOptions.opacity,
          i,
          m,
          leg,
          legPath,
          legLength,
          newPos;

      if (finalLegOpacity === undefined) {
        finalLegOpacity = L.MarkerClusterGroup.prototype.options.spiderLegPolylineOptions.opacity;
      }

      if (svg) {
        legOptions.opacity = 0;
        legOptions.className = (legOptions.className || '') + ' leaflet-cluster-spider-leg';
      } else {
        legOptions.opacity = finalLegOpacity;
      }

      group._ignoreMove = true;

      for (i = 0; i < childMarkers.length; i++) {
        m = childMarkers[i];
        newPos = map.layerPointToLatLng(positions[i]);
        leg = new L.Polyline([thisLayerLatLng, newPos], legOptions);
        map.addLayer(leg);
        m._spiderLeg = leg;

        if (svg) {
          legPath = leg._path;
          legLength = legPath.getTotalLength() + 0.1;
          legPath.style.strokeDasharray = legLength;
          legPath.style.strokeDashoffset = legLength;
        }

        if (m.setZIndexOffset) {
          m.setZIndexOffset(1000000);
        }

        if (m.clusterHide) {
          m.clusterHide();
        }

        fg.addLayer(m);

        if (m._setPos) {
          m._setPos(thisLayerPos);
        }
      }

      group._forceLayout();

      group._animationStart();

      for (i = childMarkers.length - 1; i >= 0; i--) {
        newPos = map.layerPointToLatLng(positions[i]);
        m = childMarkers[i];
        m._preSpiderfyLatlng = m._latlng;
        m.setLatLng(newPos);

        if (m.clusterShow) {
          m.clusterShow();
        }

        if (svg) {
          leg = m._spiderLeg;
          legPath = leg._path;
          legPath.style.strokeDashoffset = 0;
          leg.setStyle({
            opacity: finalLegOpacity
          });
        }
      }

      this.setOpacity(0.3);
      group._ignoreMove = false;
      setTimeout(function () {
        group._animationEnd();

        group.fire('spiderfied', {
          cluster: me,
          markers: childMarkers
        });
      }, 200);
    },
    _animationUnspiderfy: function _animationUnspiderfy(zoomDetails) {
      var me = this,
          group = this._group,
          map = group._map,
          fg = group._featureGroup,
          thisLayerPos = zoomDetails ? map._latLngToNewLayerPoint(this._latlng, zoomDetails.zoom, zoomDetails.center) : map.latLngToLayerPoint(this._latlng),
          childMarkers = this.getAllChildMarkers(null, true),
          svg = L.Path.SVG,
          m,
          i,
          leg,
          legPath,
          legLength,
          nonAnimatable;
      group._ignoreMove = true;

      group._animationStart();

      this.setOpacity(1);

      for (i = childMarkers.length - 1; i >= 0; i--) {
        m = childMarkers[i];

        if (!m._preSpiderfyLatlng) {
          continue;
        }

        m.closePopup();
        m.setLatLng(m._preSpiderfyLatlng);
        delete m._preSpiderfyLatlng;
        nonAnimatable = true;

        if (m._setPos) {
          m._setPos(thisLayerPos);

          nonAnimatable = false;
        }

        if (m.clusterHide) {
          m.clusterHide();
          nonAnimatable = false;
        }

        if (nonAnimatable) {
          fg.removeLayer(m);
        }

        if (svg) {
          leg = m._spiderLeg;
          legPath = leg._path;
          legLength = legPath.getTotalLength() + 0.1;
          legPath.style.strokeDashoffset = legLength;
          leg.setStyle({
            opacity: 0
          });
        }
      }

      group._ignoreMove = false;
      setTimeout(function () {
        var stillThereChildCount = 0;

        for (i = childMarkers.length - 1; i >= 0; i--) {
          m = childMarkers[i];

          if (m._spiderLeg) {
            stillThereChildCount++;
          }
        }

        for (i = childMarkers.length - 1; i >= 0; i--) {
          m = childMarkers[i];

          if (!m._spiderLeg) {
            continue;
          }

          if (m.clusterShow) {
            m.clusterShow();
          }

          if (m.setZIndexOffset) {
            m.setZIndexOffset(0);
          }

          if (stillThereChildCount > 1) {
            fg.removeLayer(m);
          }

          map.removeLayer(m._spiderLeg);
          delete m._spiderLeg;
        }

        group._animationEnd();

        group.fire('unspiderfied', {
          cluster: me,
          markers: childMarkers
        });
      }, 200);
    }
  });
  L.MarkerClusterGroup.include({
    _spiderfied: {},
    _isSpiderfied: function _isSpiderfied(marker) {
      return this._spiderfied[marker._leaflet_id];
    },
    _addSpiderfied: function _addSpiderfied(marker) {
      this._spiderfied[marker._leaflet_id] = marker;
    },
    _removeSpiderfied: function _removeSpiderfied(marker) {
      delete this._spiderfied[marker._leaflet_id];
    },
    _spiderfierOnAdd: function _spiderfierOnAdd() {
      if (this.options.unspiderfyOnMapClick) {
        this._map.on('click', this._unspiderfyWrapper, this);
      }
    },
    _spiderfierOnRemove: function _spiderfierOnRemove() {
      this._map.off('click', this._unspiderfyWrapper, this);

      this._map.off('zoomstart', this._unspiderfyZoomStart, this);

      this._map.off('zoomanim', this._unspiderfyZoomAnim, this);

      this._map.off('zoomend', this._noanimationUnspiderfy, this);

      this._noanimationUnspiderfy();
    },
    _unspiderfyZoomStart: function _unspiderfyZoomStart() {
      if (!this._map) {
        return;
      }

      this._map.on('zoomanim', this._unspiderfyZoomAnim, this);
    },
    _unspiderfyZoomAnim: function _unspiderfyZoomAnim() {
      if (L.DomUtil.hasClass(this._map._mapPane, 'leaflet-touching')) {
        return;
      }

      this._map.off('zoomanim', this._unspiderfyZoomAnim, this);
    },
    _unspiderfyWrapper: function _unspiderfyWrapper() {
      this._unspiderfy();
    },
    _unspiderfy: function _unspiderfy(zoomDetails) {
      for (var mKey in this._spiderfied) {
        if (this._spiderfied.hasOwnProperty(mKey)) {
          this._spiderfied[mKey].unspiderfy(zoomDetails);
        }
      }
    },
    _noanimationUnspiderfy: function _noanimationUnspiderfy() {
      for (var mKey in this._spiderfied) {
        if (this._spiderfied.hasOwnProperty(mKey)) {
          this._spiderfied[mKey]._noanimationUnspiderfy();
        }
      }
    },
    _unspiderfyLayer: function _unspiderfyLayer(layer) {
      if (layer._spiderLeg) {
        this._featureGroup.removeLayer(layer);

        if (layer.clusterShow) {
          layer.clusterShow();
        }

        if (layer.setZIndexOffset) {
          layer.setZIndexOffset(0);
        }

        this._map.removeLayer(layer._spiderLeg);

        delete layer._spiderLeg;
      }
    }
  });
  L.MarkerClusterGroup.include({
    refreshClusters: function refreshClusters(layers) {
      if (!layers) {
        layers = this._topClusterLevel.getAllChildMarkers();
      } else if (layers instanceof L.MarkerClusterGroup) {
        layers = layers._topClusterLevel.getAllChildMarkers();
      } else if (layers instanceof L.LayerGroup) {
        layers = layers._layers;
      } else if (layers instanceof L.MarkerCluster) {
        layers = layers.getAllChildMarkers();
      } else if (layers instanceof L.Marker) {
        layers = [layers];
      }

      this._flagParentsIconsNeedUpdate(layers);

      this._refreshClustersIcons();

      if (this.options.singleMarkerMode) {
        this._refreshSingleMarkerModeMarkers(layers);
      }

      return this;
    },
    _flagParentsIconsNeedUpdate: function _flagParentsIconsNeedUpdate(layers) {
      var id, parent;

      for (id in layers) {
        parent = layers[id].__parent;

        while (parent) {
          parent._iconNeedsUpdate = true;
          parent = parent.__parent;
        }
      }
    },
    _refreshSingleMarkerModeMarkers: function _refreshSingleMarkerModeMarkers(layers) {
      var id, layer;

      for (id in layers) {
        layer = layers[id];

        if (this.hasLayer(layer)) {
          layer.setIcon(this._overrideMarkerIcon(layer));
        }
      }
    }
  });
  L.Marker.include({
    refreshIconOptions: function refreshIconOptions(options, directlyRefreshClusters) {
      var icon = this.options.icon;
      L.setOptions(icon, options);
      this.setIcon(icon);

      if (directlyRefreshClusters && this.__parent) {
        this.__parent._group.refreshClusters(this);
      }

      return this;
    }
  });
}

/** `Object#toString` result references. */
var numberTag$3 = '[object Number]';

/**
 * Checks if `value` is classified as a `Number` primitive or object.
 *
 * **Note:** To exclude `Infinity`, `-Infinity`, and `NaN`, which are
 * classified as numbers, use the `_.isFinite` method.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a number, else `false`.
 * @example
 *
 * _.isNumber(3);
 * // => true
 *
 * _.isNumber(Number.MIN_VALUE);
 * // => true
 *
 * _.isNumber(Infinity);
 * // => true
 *
 * _.isNumber('3');
 * // => false
 */
function isNumber(value) {
  return typeof value == 'number' ||
    (isObjectLike_1(value) && _baseGetTag(value) == numberTag$3);
}

var isNumber_1 = isNumber;

var stateColorCode = [{
  iconColor: '#aaaaaa',
  markerColor: 'white',
  fillColor: '#ffffff',
  textColor: '#000000'
}, {
  iconColor: '#ffffff',
  markerColor: '#cccccc',
  fillColor: '#cccccc',
  textColor: '#000000'
}, {
  iconColor: '#ff98a4',
  markerColor: '#e23549',
  fillColor: '#e23549',
  textColor: '#35e2ce'
}, {
  iconColor: '#fff4cc',
  markerColor: '#FFCC00',
  fillColor: '#FFCC00',
  textColor: '#0033ff'
}, {
  iconColor: '#a0ccff',
  markerColor: '#429aff',
  fillColor: '#429aff',
  textColor: '#fa742'
}, {
  iconColor: '#4DFA90',
  markerColor: '#3dc873',
  fillColor: '#3dc873',
  textColor: '#c83d92'
}];
var icons = {
  dendrometer: 'icon-leaf',
  farmx_dendrometer: 'icon-leaf',
  ir_plant: 'icon-camera',
  weather_station: 'icon-ios-rainy',
  water_pressure: 'icon-gauge',
  water_flow: 'icon-waterdrop',
  water_flow_analog: 'icon-waterdrop',
  gateway: 'icon-radio-tower',
  boxer_station: 'icon-radio-tower',
  aquacheck_soil: 'icon-eyedropper',
  pixl_soil: 'icon-eyedropper',
  cavalier: 'icon-eyedropper'
};
var sensorTitles = {
  dendrometer: {
    singular: 'Dendrometer',
    plural: 'Dendrometers'
  },
  farmx_dendrometer: {
    singular: 'Dendrometer',
    plural: 'Dendrometers'
  },
  ir_plant: {
    singular: 'IR Sensor',
    plural: 'IR Sensors'
  },
  weather_station: {
    singular: 'Weather Station',
    plural: 'Weather Stations'
  },
  water_pressure: {
    singular: 'Pressure Sensor',
    plural: 'Pressure Sensors'
  },
  water_flow: {
    singular: 'Flow Meter',
    plural: 'Flow Meters'
  },
  water_flow_analog: {
    singular: 'Flow Meter Analog',
    plural: 'Flow Meters Analog'
  },
  gateway: {
    singular: 'Gateway',
    plural: 'Gateways'
  },
  boxer_station: {
    singular: 'Boxer Station',
    plural: 'Boxer Stations'
  },
  aquacheck_soil: {
    singular: 'Aquacheck Soil Sensor',
    plural: 'Aquacheck Soil Sensors'
  },
  pixl_soil: {
    singular: 'Pixl Soil Sensor',
    plural: 'Pixl Soil Sensors'
  },
  cavalier: {
    singular: 'Cavalier',
    plural: 'Cavaliers'
  }
};
var mapPresentationModes = {
  connectivity: {
    title: 'Connectivity',
    excludedSensors: ['cavalier']
  },
  waterpressure: {
    title: 'Water pressure',
    includedSensors: ['water_pressure'],
    psiLevel: 5
  },
  provision: {
    title: 'Provision',
    excludedSensors: []
  }
};
function sensorTypeAllowedForMode(presentationMode, sensorType) {
  if (mapPresentationModes[presentationMode].excludedSensors && !mapPresentationModes[presentationMode].includedSensors) {
    return !mapPresentationModes[presentationMode].excludedSensors.includes(sensorType);
  }

  if (mapPresentationModes[presentationMode].includedSensors && !mapPresentationModes[presentationMode].excludedSensors) {
    return mapPresentationModes[presentationMode].includedSensors.includes(sensorType);
  }

  if (!mapPresentationModes[presentationMode]) {
    throw new Error(presentationMode + " is not defined in mapPresentationModes");
  }

  throw new Error('mapPresentationModes can not have both includedSensors and excludedSensors');
}
function prepareNumberForDisplay(n) {
  if (!isNumber_1(n)) {
    return n;
  }

  if (Math.abs(n) < 1) {
    return n.toFixed(3);
  }

  return n.toFixed(1);
}
function getMarkerTooltipBuyPresentationMode(feature, presentationMode) {
  if (presentationMode === 'waterpressure') {
    return prepareNumberForDisplay(feature.properties.psi);
  }

  return feature.properties.identifier.slice(-5);
}

function getColorsByLastPostedInMinutes(feature) {
  var minutes = feature.properties.lastPostedInMinutes;

  if (minutes === undefined) {
    return stateColorCode[0];
  }

  if (minutes <= 30) {
    return stateColorCode[5];
  }

  if (minutes > 30 && minutes <= 120) {
    return stateColorCode[3];
  }

  if (minutes > 120) {
    return stateColorCode[2];
  }

  if (minutes === -1) {
    return stateColorCode[1];
  }

  return stateColorCode[1];
}

function prepareMarker(feature, latlng, presentationMode) {
  var opts = {};
  opts.markerColor = '#fff';
  opts.iconColor = '#000';
  opts.selected = false;

  if (presentationMode === 'connectivity') {
    var color = getColorsByLastPostedInMinutes(feature);
    opts.markerColor = color.markerColor;
    opts.iconColor = 'white';
  }

  if (presentationMode === 'waterpressure') {
    opts.markerColor = 'white';
    opts.iconColor = 'black';

    if (feature.properties.psi > mapPresentationModes.waterpressure.psiLevel) {
      opts.markerColor = '#429aff';
      opts.iconColor = 'white';
    }
  }

  if (presentationMode === 'provision') {
    opts.iconColor = 'white';
    opts.markerColor = '#e23549';

    if (feature.properties.provisioned) {
      opts.markerColor = '#3dc873';
    }
  }

  var markerIcon = L.VectorMarkers.icon({
    icon: icons[feature.properties.type] || 'icon-radio-waves',
    prefix: 'icon',
    markerColor: opts.markerColor,
    iconColor: opts.iconColor,
    dataTestId: feature.id,
    selected: opts.selected
  });
  var marker = new L.marker(latlng, {
    icon: markerIcon
  });
  return marker;
}

var prepareMarkerClusterIcon = function prepareMarkerClusterIcon(presentationMode) {
  return function (cluster) {
    var childCount = cluster.getChildCount();
    var c = ' marker-cluster-';

    if (childCount < 10) {
      c += 'small';
    } else if (childCount < 100) {
      c += 'medium';
    } else {
      c += 'large';
    }

    var markers = cluster.getAllChildMarkers();

    if (presentationMode === 'connectivity') {
      c = ' marker-cluster-unknown';
      var minutes = 0;

      for (var i = 0; i < markers.length; i++) {
        var marker = markers[i];
        var lastPosted = marker.feature.properties.lastPostedInMinutes;

        if (lastPosted === undefined || lastPosted === -1) {
          minutes = undefined;
          break;
        }

        if (lastPosted > minutes) {
          minutes = lastPosted;
        }
      }

      if (minutes === undefined) {
        c = ' marker-cluster-unknown';
      } else if (minutes <= 30) {
        c = ' marker-cluster-good';
      } else if (minutes > 30 && minutes <= 120) {
        c = ' marker-cluster-warning';
      } else if (minutes > 120) {
        c = ' marker-cluster-bad';
      } else if (minutes === -1) {
        c = ' marker-cluster-offline';
      }
    } else if (presentationMode === 'waterpressure') {
      for (var _i = 0; _i < markers.length; _i++) {
        c = ' marker-cluster-unknown';
        var _marker = markers[_i];
        var psi = _marker.feature.properties.psi;

        if (psi > mapPresentationModes.waterpressure.psiLevel) {
          c = ' marker-cluster-over';
          break;
        }
      }
    } else if (presentationMode === 'provision') {
      for (var _i2 = 0; _i2 < markers.length; _i2++) {
        c = ' marker-cluster-good';
        var _marker2 = markers[_i2];
        var provisioned = _marker2.feature.properties.provisioned;

        if (!provisioned) {
          c = ' marker-cluster-bad';
          break;
        }
      }
    }

    return new L.DivIcon({
      html: '<div><span>' + markers.length + '</span></div>',
      className: 'marker-cluster' + c,
      iconSize: new L.Point(40, 40)
    });
  };
};

leafletMarkerClusterGroup(L);
leafletMarkerClusterGroup(L);

var shouldSpiderfy = function shouldSpiderfy(c) {
  return c.getChildCount() < 7;
};

function MarkerCluster(_ref) {
  var sensorsGeoJSON = _ref.sensorsGeoJSON,
      showAllSensorLabels = _ref.showAllSensorLabels,
      activePopUp = _ref.activePopUp,
      selectSensor = _ref.selectSensor,
      showPopUpOnMarkerClick = _ref.showPopUpOnMarkerClick,
      presentationMode = _ref.presentationMode,
      selectedSensor = _ref.selectedSensor,
      refreshMap = _ref.refreshMap;
  var marketClusterGroup = L.markerClusterGroup({
    maxClusterRadius: 50,
    unspiderfyOnMapClick: false,
    shouldSpiderfy: shouldSpiderfy,
    animate: false,
    iconCreateFunction: prepareMarkerClusterIcon(presentationMode),
    spiderLegPolylineOptions: {
      weight: 1.5,
      color: '#eee',
      opacity: 0.8
    },
    polygonOptions: {
      stroke: false,
      fill: false
    }
  });

  var _useLeaflet = useLeaflet(),
      map = _useLeaflet.map;

  var prevsensorsGeoJSON = useRef(JSON.stringify({}));
  var markers = useRef([]);

  var onMarkerPopupOpen = function onMarkerPopupOpen(_ref2) {
    var _ref2$target$feature$ = _ref2.target.feature.properties,
        type = _ref2$target$feature$.type,
        identifier = _ref2$target$feature$.identifier;
    selectSensor(type, identifier);
  };

  useEffect(function () {
    var destroy = function destroy() {
      markers.current.forEach(function (marker) {
        marker.off('popupopen', onMarkerPopupOpen);
      });
      marketClusterGroup.clearLayers();
    };

    return destroy;
  }, []);
  useEffect(function () {
    markers.current.forEach(function (marker) {
      if (marker.unbindTooltip) {
        marker.unbindTooltip();
      }
    });

    if (showAllSensorLabels) {
      markers.current.forEach(function (marker) {
        marker.bindTooltip(getMarkerTooltipBuyPresentationMode(marker.feature, presentationMode), {
          direction: 'auto',
          permanent: true,
          className: marker.feature.properties.type + "-" + marker.feature.properties.identifier
        });
      });
    }
  }, [showAllSensorLabels]);
  useEffect(function () {
    if (typeof sensorsGeoJSON === 'undefined') {
      marketClusterGroup.clearLayers();
      markers.current = [];
      return;
    }

    var sensorsGeoJSONString = JSON.stringify(sensorsGeoJSON);

    if (prevsensorsGeoJSON.current !== sensorsGeoJSONString) {
      marketClusterGroup.clearLayers();
      markers.current = [];
      sensorsGeoJSON.features = sensorsGeoJSON.features.filter(function (feature) {
        return feature.geometry.coordinates[0] && feature.geometry.coordinates[1];
      });

      if (!sensorsGeoJSON.features.length) {
        return;
      }

      var geojson = L.geoJSON(sensorsGeoJSON, {
        pointToLayer: function pointToLayer(feature, latlng) {
          var marker = prepareMarker(feature, latlng, presentationMode);
          markers.current.push(marker);

          if (showPopUpOnMarkerClick) {
            var popupContent = "<div class=\"popup-inner\" data-test-id=\"popup-" + feature.id + "\">\n              <div class=\"popup-title\">" + feature.properties.type + "</div>\n              <div>" + feature.properties.identifier + "</div>\n              <div class=\"popup-location\">" + feature.geometry.coordinates[1] + ", " + feature.geometry.coordinates[0] + "</div>\n              </div>";
            marker.bindPopup(popupContent, {
              autoPan: false
            });
          }

          if (showAllSensorLabels) {
            marker.bindTooltip(getMarkerTooltipBuyPresentationMode(feature, presentationMode), {
              direction: 'auto',
              permanent: true,
              className: feature.properties.type + "-" + feature.properties.identifier
            });
          } else if (presentationMode === 'connectivity') {
            marker.bindTooltip(feature.properties.name || 'NO NAME', {
              direction: 'auto',
              permanent: false,
              className: feature.properties.type + "-" + feature.properties.identifier
            });
          }

          marker.on('click', onMarkerPopupOpen);
          return marker;
        }
      });
      marketClusterGroup.addLayer(geojson);
      map.addLayer(marketClusterGroup);
      map.setZoom(Math.round(map.getZoom()));
      prevsensorsGeoJSON.current = sensorsGeoJSONString;
    }
  }, [sensorsGeoJSON]);
  useEffect(function () {
    if (activePopUp === Object(activePopUp) && activePopUp.identifier) {
      var identifier = activePopUp.identifier;
      var markersLength = markers.current.length;

      for (var i = 0; i < markersLength; i += 1) {
        var marker = markers.current[i];

        if (marker.feature.properties.identifier === identifier) {
          marker.openPopup();
          break;
        }
      }
    }
  }, [activePopUp, map]);
  useEffect(function () {
    markers.current.forEach(function (marker) {
      var iconOpts = marker.getIcon().options;

      if (selectedSensor && marker.feature.properties.identifier === selectedSensor.properties.identifier && marker.feature.properties.type === selectedSensor.properties.type) {
        marker.setIcon(L.VectorMarkers.icon(_extends({}, iconOpts, {
          selected: true
        })));
      } else if (iconOpts.selected) {
        marker.setIcon(L.VectorMarkers.icon(_extends({}, iconOpts, {
          selected: false
        })));
      }
    });
  }, [selectedSensor]);
  refreshMap();
  return null;
}
MarkerCluster.propTypes = {
  activePopUp: PropTypes.object,
  selectSensor: PropTypes.func,
  showAllSensorLabels: PropTypes.bool,
  sensorsGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.arrayOf(PropTypes.number),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  showPopUpOnMarkerClick: PropTypes.bool,
  presentationMode: PropTypes.string,
  refreshMap: PropTypes.func.isRequired,
  selectedSensor: PropTypes.any
};
MarkerCluster.defaultProps = {
  activePopUp: undefined,
  selectSensor: undefined,
  showAllSensorLabels: undefined,
  sensorsGeoJSON: undefined,
  showPopUpOnMarkerClick: true,
  presentationMode: undefined,
  selectedSensor: undefined
};

function leafletVectorMarkers(L) {
  if (L.VectorMarkers) {
    return;
  }

  L.VectorMarkers = {};
  L.VectorMarkers.version = "1.0.0";
  L.VectorMarkers.MAP_PIN = 'M6.19,28.81a16,16,0,1,1,22.63,0L17.5,40.13Z';
  L.VectorMarkers.PIN_INNER = 'M7.9,27.1a13.58,13.58,0,1,1,19.21,0l-9.6,9.6Z';
  L.VectorMarkers.Icon = L.Icon.extend({
    options: {
      iconSize: [32, 40],
      iconAnchor: [16, 40],
      popupAnchor: [2, -40],
      shadowAnchor: [24, 24],
      shadowSize: [32, 40],
      className: "vector-marker",
      prefix: "fa",
      spinClass: "fa-spin",
      extraClasses: "",
      icon: "home",
      markerColor: "blue",
      iconColor: "white",
      selected: false
    },
    initialize: function initialize(options) {
      return options = L.Util.setOptions(this, options);
    },
    setSelected: function setSelected() {
      alert(1);
    },
    createIcon: function createIcon(oldIcon) {
      var div, icon, options, pin_path, pin_inner;
      div = oldIcon && oldIcon.tagName === "DIV" ? oldIcon : document.createElement("div");
      options = this.options;
      div.setAttribute('data-test-id', "marker-" + options.dataTestId);

      if (options.icon) {
        icon = this._createInner();
      }

      pin_path = L.VectorMarkers.MAP_PIN;
      pin_inner = L.VectorMarkers.PIN_INNER;
      div.innerHTML = '<svg width="35px" height="40px" viewBox="0 0 35 40" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' + '<path class="pin-outer ' + ("marker-pin-outer-" + options.dataTestId) + '" d="' + pin_path + '" fill="' + options.markerColor + '"></path>' + '<path class="pin-inner ' + ("marker-pin-inner-" + options.dataTestId) + '" d="' + pin_inner + '" fill="' + options.markerColor + '"></path>' + icon + '</svg>';

      this._setIconStyles(div, "icon");

      if (options.selected) {
        this._setIconStyles(div, " selected-icon");
      }

      return div;
    },
    _createInner: function _createInner() {
      var iconClass, iconColorClass, iconColorStyle, iconSpinClass, options;
      iconClass = void 0;
      iconSpinClass = "";
      iconColorClass = "";
      iconColorStyle = "";
      options = this.options;

      if (options.icon.slice(0, options.prefix.length + 1) === options.prefix + "-") {
        iconClass = options.icon;
      } else {
        iconClass = options.prefix + "-" + options.icon;
      }

      if (options.spin && typeof options.spinClass === "string") {
        iconSpinClass = options.spinClass;
      }

      if (options.iconColor) {
        if (options.iconColor === "white" || options.iconColor === "black") {
          iconColorClass = "icon-" + options.iconColor;
        } else {
          iconColorStyle = "style='color: " + options.iconColor + "' ";
        }
      }

      return "<i " + iconColorStyle + "class='" + options.extraClasses + " " + options.prefix + " " + iconClass + " " + iconSpinClass + " " + iconColorClass + "'></i>";
    },
    _setIconStyles: function _setIconStyles(img, name) {
      var anchor, options, size;
      options = this.options;
      size = L.point(options[name === "shadow" ? "shadowSize" : "iconSize"]);
      anchor = void 0;

      if (name === "shadow") {
        anchor = L.point(options.shadowAnchor || options.iconAnchor);
      } else {
        anchor = L.point(options.iconAnchor);
      }

      if (!anchor && size) {
        anchor = size.divideBy(2, true);
      }

      img.className = "vector-marker" + name + " " + options.className;

      if (anchor) {
        img.style.marginLeft = -anchor.x + "px";
        img.style.marginTop = -anchor.y + "px";
      }

      if (size) {
        img.style.width = size.x + "px";
        return img.style.height = size.y + "px";
      }
    },
    createShadow: function createShadow() {
      var div;
      div = document.createElement("div");

      this._setIconStyles(div, "shadow");

      return div;
    }
  });

  L.VectorMarkers.icon = function (options) {
    return new L.VectorMarkers.Icon(options);
  };
}

var antIcon = /*#__PURE__*/React.createElement(LoadingOutlined, {
  style: {
    fontSize: 24
  },
  spin: true
});

var isEqual = require('react-fast-compare');

var LabelMarker = function LabelMarker(_ref) {
  var position = _ref.position,
      name = _ref.name,
      mapRef = _ref.mapRef,
      className = _ref.className;
  var zoom = 100;

  try {
    zoom = mapRef.current.leafletElement.getZoom();
  } catch (error) {
    console.log('Error LabelMarker mapRef.current.leafletElement.getZoom', error);
  }

  var style = {};

  if (zoom <= 13) {
    style.display = 'none';
  }

  var icon = L.divIcon({
    className: 'custom-icon',
    html: ReactDOMServer.renderToString( /*#__PURE__*/React.createElement("div", {
      style: style,
      className: className
    }, name))
  });
  return /*#__PURE__*/React.createElement(Marker, {
    position: position,
    icon: icon
  });
};

leafletVectorMarkers(L);
var accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
var initialState = {
  viewBox: {
    latitude: 37.646,
    longitude: -121.016,
    zoom: 6
  },
  sensorsGeoJSON: {
    features: []
  }
};

function Map$1(_ref2) {
  var children = _ref2.children,
      sensorsGeoJSON = _ref2.sensorsGeoJSON,
      mapBoxPadding = _ref2.mapBoxPadding,
      width = _ref2.width,
      height = _ref2.height,
      activePopUp = _ref2.activePopUp,
      selectSensor = _ref2.selectSensor,
      showAllSensorLabels = _ref2.showAllSensorLabels,
      ranchesGeoJSON = _ref2.ranchesGeoJSON,
      blocksGeoJSON = _ref2.blocksGeoJSON,
      showPopUpOnMarkerClick = _ref2.showPopUpOnMarkerClick,
      presentationMode = _ref2.presentationMode,
      selectedSensor = _ref2.selectedSensor,
      isDataLoading = _ref2.isDataLoading;

  var _useState = useState(initialState),
      state = _useState[0],
      setState = _useState[1];

  var mounted = useRef(false);
  var mapRef = useRef({});
  var el = document;
  var event = document.createEvent('HTMLEvents');
  event.initEvent('resize', true, false);
  useEffect(function () {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png')
    });
    mounted.current = true;
    el.dispatchEvent(event);
    return function () {
      mounted.current = false;
    };
  }, []);
  useEffect(function () {
    if (!mounted.current) {
      return;
    }

    if ((width !== state.width || height !== state.height) && mapRef.current.leafletElement) {
      try {
        mapRef.current.leafletElement.setZoom(mapRef.current.leafletElement.getZoom());
      } catch (error) {
        console.log('error mapRef.current.leafletElement.getZoom', error);
      }
    }

    var incomingState = {
      sensorsGeoJSON: sensorsGeoJSON,
      mapBoxPadding: mapBoxPadding,
      width: width,
      height: height,
      viewBox: state.viewBox
    };

    if (isEqual(incomingState, state)) {
      return;
    }

    if (!sensorsGeoJSON) {
      setState(_extends({}, state, {
        sensorsGeoJSON: sensorsGeoJSON,
        mapBoxPadding: mapBoxPadding
      }));
      return;
    }

    sensorsGeoJSON.features = sensorsGeoJSON.features.filter(function (feature) {
      return feature.geometry.coordinates[0] && feature.geometry.coordinates[1];
    });

    if (!sensorsGeoJSON.features.length) {
      setState(_extends({}, state, {
        sensorsGeoJSON: sensorsGeoJSON,
        mapBoxPadding: mapBoxPadding
      }));
      return;
    }

    try {
      if (!width || !height) {
        return;
      }

      var _bbox = bbox(sensorsGeoJSON),
          minLng = _bbox[0],
          minLat = _bbox[1],
          maxLng = _bbox[2],
          maxLat = _bbox[3];

      var webMercatorViewport = new WebMercatorViewport(_extends({}, state.viewBox, {
        width: width,
        height: height
      }));

      var _webMercatorViewport$ = webMercatorViewport.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
        padding: {
          top: mapBoxPadding.top,
          bottom: mapBoxPadding.bottom,
          left: mapBoxPadding.left,
          right: mapBoxPadding.right
        }
      }),
          longitude = _webMercatorViewport$.longitude,
          latitude = _webMercatorViewport$.latitude,
          zoom = _webMercatorViewport$.zoom;

      var newState = _extends({}, state, {
        sensorsGeoJSON: sensorsGeoJSON,
        mapBoxPadding: mapBoxPadding,
        viewBox: {
          longitude: longitude,
          latitude: latitude,
          zoom: zoom
        },
        width: width,
        height: height
      });

      setState(newState);
    } catch (error) {
      console.log('WebMercatorViewport', error);
    }
  }, [sensorsGeoJSON, mapBoxPadding, width, height]);
  return /*#__PURE__*/React.createElement(Fragment, null, isDataLoading && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      zIndex: '9999'
    },
    className: "map-data-is-loading"
  }, /*#__PURE__*/React.createElement(Spin, {
    indicator: antIcon
  })), /*#__PURE__*/React.createElement(Map$2, {
    ref: mapRef,
    attributionControl: false,
    className: "map-leaflet",
    viewport: {
      center: [state.viewBox.latitude, state.viewBox.longitude],
      zoom: state.viewBox.zoom
    },
    onViewportChanged: function onViewportChanged(viewport) {
      if (viewport && viewport.center) {
        if (viewport.center[0] !== state.viewBox.latitude || viewport.center[1] !== state.viewBox.longitude || state.viewBox.zoom !== viewport.zoom) {
          setState(_extends({}, state, {
            viewBox: {
              latitude: viewport.center[0],
              longitude: viewport.center[1],
              zoom: viewport.zoom
            }
          }));
        }
      }

      try {
        el.dispatchEvent(event);
      } catch (error) {
        console.log('Error map viewport changed', error);
      }
    },
    zoomControl: false
  }, /*#__PURE__*/React.createElement(TileLayer, {
    url: "https://api.mapbox.com/styles/v1/farmx/cinjdgdic0015adnjk1bl7775/tiles/{z}/{x}/{y}?access_token={accessToken}",
    accessToken: accessToken,
    id: "farmx.cinjdgdic0015adnjk1bl7775",
    tileSize: 512,
    zoomOffset: -1,
    detectRetina: true
  }), Boolean(ranchesGeoJSON) && Boolean(ranchesGeoJSON.features.length) && ranchesGeoJSON.features.map(function (feature) {
    return /*#__PURE__*/React.createElement(GeoJSON, {
      className: "map-ranch-boundary-" + feature.id,
      style: {
        color: '#e23549',
        weight: 2,
        fillOpacity: 0,
        fillColor: '#e23549',
        dashArray: [10, 10]
      },
      key: "" + feature.id,
      data: [feature]
    });
  }), mounted.current && Boolean(blocksGeoJSON) && Boolean(blocksGeoJSON.features.length) && blocksGeoJSON.features.map(function (feature) {
    var _bbox2 = bbox(feature),
        minLng = _bbox2[0],
        minLat = _bbox2[1],
        maxLng = _bbox2[2],
        maxLat = _bbox2[3];

    var position = [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
    return /*#__PURE__*/React.createElement(GeoJSON, {
      className: "map-ranch-block-b-" + feature.properties.name,
      style: {
        color: '#fff',
        weight: 1,
        fillOpacity: 0.4,
        fillColor: '#fff'
      },
      key: "" + presentationMode + feature.id,
      data: [feature]
    }, /*#__PURE__*/React.createElement(LabelMarker, {
      position: position,
      name: feature.properties.name,
      mapRef: mapRef,
      className: "map-ranch-block-i-" + feature.properties.name
    }));
  }), Boolean(state.sensorsGeoJSON) && Boolean(state.sensorsGeoJSON.features.length) && /*#__PURE__*/React.createElement(MarkerCluster, {
    key: JSON.stringify({
      presentationMode: presentationMode,
      s: state.sensorsGeoJSON
    }),
    sensorsGeoJSON: state.sensorsGeoJSON,
    activePopUp: activePopUp || {},
    selectSensor: selectSensor,
    showAllSensorLabels: showAllSensorLabels || false,
    showPopUpOnMarkerClick: showPopUpOnMarkerClick,
    presentationMode: presentationMode,
    selectedSensor: selectedSensor,
    refreshMap: function refreshMap() {
      el.dispatchEvent(event);
    }
  }), children));
}

Map$1.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  width: PropTypes.number,
  height: PropTypes.number,
  activePopUp: PropTypes.object,
  selectSensor: PropTypes.func,
  showAllSensorLabels: PropTypes.bool,
  presentationMode: PropTypes.string,
  ranchesGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  blocksGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  sensorsGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.arrayOf(PropTypes.number),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  mapBoxPadding: PropTypes.shape({
    top: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number
  }),
  showPopUpOnMarkerClick: PropTypes.bool,
  selectedSensor: PropTypes.any,
  isDataLoading: PropTypes.bool
};
Map$1.defaultProps = {
  children: null,
  width: 500,
  height: 500,
  activePopUp: undefined,
  selectSensor: undefined,
  showAllSensorLabels: undefined,
  ranchesGeoJSON: undefined,
  blocksGeoJSON: undefined,
  sensorsGeoJSON: undefined,
  presentationMode: undefined,
  mapBoxPadding: {
    top: 100,
    bottom: 100,
    left: 100,
    right: 100
  },
  showPopUpOnMarkerClick: true,
  selectedSensor: undefined,
  isDataLoading: false
};
function ResizeDetector(props) {
  return /*#__PURE__*/React.createElement("div", {
    className: "map-wrapper"
  }, /*#__PURE__*/React.createElement(ReactResizeDetector, {
    handleWidth: true,
    handleHeight: true,
    refreshMode: "debounce",
    refreshRate: 533
  }, function (_ref3) {
    var width = _ref3.width,
        height = _ref3.height;
    return /*#__PURE__*/React.createElement("div", {
      className: "map-inner-container"
    }, /*#__PURE__*/React.createElement(Map$1, _extends({
      key: JSON.stringify(props.sensorsGeoJSON)
    }, _extends({}, props, {
      width: Math.round(width),
      height: Math.round(height)
    }))));
  }));
}

function prepareZeroIslandSensors(sensorsGeoJSON) {
  var sensors = cloneDeep_1(sensorsGeoJSON);
  var zeroIslands = [];
  var gw = {};
  sensors.features.forEach(function (s, i) {
    var coordinates = s.geometry.coordinates;
    var _s$properties = s.properties,
        type = _s$properties.type,
        id = _s$properties.id;

    if (type === 'gateway') {
      gw[id] = coordinates;
    }

    if (!coordinates[0] && !coordinates[1]) {
      zeroIslands.push({
        gw: s.properties.gateway,
        idx: i
      });
    }
  });

  if (!zeroIslands.length) {
    return sensorsGeoJSON;
  }

  zeroIslands.forEach(function (z) {
    if (gw[z.gw]) {
      sensors.features[z.idx].geometry.coordinates = gw[z.gw];
    }
  });
  return sensors;
}

function MapSettingsMenu(_ref) {
  var renderItemShowLabels = _ref.renderItemShowLabels,
      renderItemPresentationMode = _ref.renderItemPresentationMode,
      onChangePresentationMode = _ref.onChangePresentationMode,
      showLabels = _ref.showLabels,
      toggleShowLabels = _ref.toggleShowLabels;
  return /*#__PURE__*/React.createElement(Menu, {
    className: "settings-menu",
    selectable: false
  }, renderItemPresentationMode && /*#__PURE__*/React.createElement(Menu.Item, {
    key: "1"
  }, /*#__PURE__*/React.createElement(Select, {
    defaultValue: "connectivity",
    style: {
      width: 140
    },
    onChange: onChangePresentationMode,
    "data-test-id": "presentationmode-select"
  }, /*#__PURE__*/React.createElement(Select.Option, {
    value: "connectivity",
    className: "presentationmode-option-connectivity"
  }, "Connectivity"), /*#__PURE__*/React.createElement(Select.Option, {
    value: "waterpressure",
    className: "presentationmode-option-waterpressure"
  }, "Water Pressure"), /*#__PURE__*/React.createElement(Select.Option, {
    value: "provision",
    className: "presentationmode-option-provision"
  }, "Provision"))), renderItemShowLabels && /*#__PURE__*/React.createElement(Menu.Item, {
    key: "2"
  }, /*#__PURE__*/React.createElement("span", null, "Show Labels:"), /*#__PURE__*/React.createElement(Switch, {
    checked: showLabels,
    onChange: toggleShowLabels,
    className: "switch-toggle-labels"
  })));
}
MapSettingsMenu.propTypes = {
  renderItemShowLabels: PropTypes.bool.isRequired,
  renderItemPresentationMode: PropTypes.bool.isRequired,
  onChangePresentationMode: PropTypes.func.isRequired,
  showLabels: PropTypes.bool.isRequired,
  toggleShowLabels: PropTypes.func.isRequired
};

var skeletonListData = {
  a: [1, 2, 3],
  b: [1, 2, 3],
  c: [1, 2, 3],
  d: [1, 2, 3]
};
function ListByRanchId(_ref) {
  var isMobile = _ref.isMobile,
      listData = _ref.listData,
      isDataLoading = _ref.isDataLoading;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/React.createElement("div", {
    className: "list-by-ranch-id-page" + (isMobile ? ' list-by-ranch-id-page-mobile' : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: "list-by-ranch-id-page-container"
  }, isDataLoading && Object.keys(skeletonListData).map(function (k) {
    return /*#__PURE__*/React.createElement("div", {
      key: k
    }, /*#__PURE__*/React.createElement(Skeleton.Input, {
      style: {
        width: 600
      },
      active: true,
      size: "small"
    }), skeletonListData[k].map(function (ki) {
      return /*#__PURE__*/React.createElement("div", {
        key: "" + k + ki,
        style: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#35363a',
          paddingTop: '10px',
          paddingBottom: '10px',
          borderBottom: '1px solid #f0f0f0'
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: '10px'
        }
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Skeleton.Input, {
        style: {
          width: 50
        },
        active: true,
        size: "small"
      })), /*#__PURE__*/React.createElement("div", {
        style: {
          paddingTop: '5px',
          fontSize: 'smaller'
        }
      }, /*#__PURE__*/React.createElement(Skeleton.Input, {
        style: {
          width: 200
        },
        active: true,
        size: "small"
      }))), /*#__PURE__*/React.createElement("div", null));
    }));
  }), !isDataLoading && Object.keys(listData).map(function (typeKey) {
    return /*#__PURE__*/React.createElement("div", {
      key: typeKey
    }, /*#__PURE__*/React.createElement("div", {
      className: "list-header list-header-" + typeKey
    }, /*#__PURE__*/React.createElement("i", {
      className: icons[typeKey] || 'icon-radio-waves'
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        marginLeft: '10px'
      }
    }, t(sensorTitles[typeKey].plural))), listData[typeKey].map(function (sensor) {
      return /*#__PURE__*/React.createElement("div", {
        key: typeKey + "-" + sensor.identifier,
        className: "list-body list-body-" + typeKey + "-" + sensor.identifier
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          display: 'flex',
          flexDirection: 'column'
        }
      }, /*#__PURE__*/React.createElement("div", null, t(sensor.name || 'No name')), /*#__PURE__*/React.createElement("div", {
        style: {
          fontSize: 'smaller'
        }
      }, t(sensor.identifier))), /*#__PURE__*/React.createElement("div", null));
    }));
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      height: '2em'
    }
  })));
}

var isEqual$1 = require('react-fast-compare');

function renderOptionalDescription(label, value, optional) {
  if (optional && value === undefined) {
    return null;
  }

  var longClassName = (label + "-" + value).replace(/\s+/g, '-').toLowerCase();
  var shortClassName = ("" + label).replace(/\s+/g, '-').toLowerCase();
  return /*#__PURE__*/React.createElement(Descriptions.Item, {
    label: label,
    className: shortClassName + " " + longClassName
  }, Boolean(value) && value);
}

var loadRanchDetail = actions.loadRanchDetail,
    loadAllSensors$1 = actions.loadAllSensors,
    loadBlockList = actions.loadBlockList,
    loadSensorStatus$1 = actions.loadSensorStatus;
var selectSensorsForRanchId = selectors.selectSensorsForRanchId,
    selectRanchById$1 = selectors.selectRanchById,
    selectBlocksForRanchId = selectors.selectBlocksForRanchId;
var MapByRanchId = function MapByRanchId(_ref) {
  var match = _ref.match;

  var _useState = useState('connectivity'),
      presentationMode = _useState[0],
      setPresentationMode = _useState[1];

  var _useState2 = useState(true),
      showLabels = _useState2[0],
      setShowLabels = _useState2[1];

  var _useState3 = useState({}),
      listDataState = _useState3[0],
      setListDataState = _useState3[1];

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var containerRef = useRef(null);
  var dispatch = useDispatch();

  var _useState4 = useState(false),
      isDrawerVisible = _useState4[0],
      setIsDrawerVisible = _useState4[1];

  var _useState5 = useState('map'),
      viewType = _useState5[0],
      setViewType = _useState5[1];

  var _useState6 = useState(undefined),
      selectedSensor = _useState6[0],
      setSelectedSensor = _useState6[1];

  var _useState7 = useState(true),
      isDataLoading = _useState7[0],
      setIsDataLoading = _useState7[1];

  var _useState8 = useState(undefined),
      ranchesGeoJSONState = _useState8[0],
      setRanchesGeoJSONState = _useState8[1];

  var _useState9 = useState(undefined),
      blocksGeoJSONState = _useState9[0],
      setBlocksGeoJSONState = _useState9[1];

  var _useState10 = useState(undefined),
      sensorsGeoJSONState = _useState10[0],
      setSensorsGeoJSONState = _useState10[1];

  var _useState11 = useState(undefined),
      sensorsAllRawState = _useState11[0],
      setSensorsAllRawState = _useState11[1];

  var _useState12 = useState(undefined),
      sensorsStatusGeoJSONState = _useState12[0],
      setSensorsStatusGeoJSONState = _useState12[1];

  var ranchId = Number(match.params.ranch_id);
  var selRanchById = useSelector(function (state) {
    return selectRanchById$1(state, ranchId);
  });
  var selBlocksByRanchId = useSelector(function (state) {
    return selectBlocksForRanchId(state, ranchId);
  });
  var selSensorsByRanchId = useSelector(function (state) {
    return selectSensorsForRanchId(state, ranchId);
  });
  var selSensorsDataStatus = useSelector(function (state) {
    return state.sensorsData.status;
  });
  useEffect(function () {
    dispatch(loadRanchDetail(ranchId));
    dispatch(loadAllSensors$1());
    dispatch(loadBlockList());
  }, [ranchId, dispatch]);
  useEffect(function () {
    if (!selSensorsByRanchId) {
      return;
    }

    if (selSensorsByRanchId.loading) {
      return;
    }

    if (isEqual$1(selSensorsByRanchId, sensorsAllRawState)) {
      return;
    }

    var sensorsGeoJSON = {
      type: 'FeatureCollection',
      features: selSensorsByRanchId.filter(function (s) {
        return s.visible;
      }).map(function (s) {
        return {
          id: s.id,
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [s.location.lng, s.location.lat]
          },
          properties: s
        };
      })
    };
    prepareZeroIslandSensors(sensorsGeoJSON);

    if (!isEqual$1(sensorsGeoJSONState, sensorsGeoJSON)) {
      setSensorsAllRawState(selSensorsByRanchId);
      setSensorsGeoJSONState(sensorsGeoJSON);
      sensorsGeoJSON.features.forEach(function (feature) {
        var _feature$properties = feature.properties,
            identifier = _feature$properties.identifier,
            type = _feature$properties.type;
        dispatch(loadSensorStatus$1({
          identifier: identifier,
          type: type
        }));
      });
    }
  }, [selSensorsByRanchId, sensorsGeoJSONState, dispatch, sensorsAllRawState]);
  useEffect(function () {
    if (sensorsGeoJSONState && !selSensorsDataStatus.loading && sensorsGeoJSONState.features.length === selSensorsDataStatus.ids.length) {
      var entitiesLoaded = true;

      for (var i = 0; i < selSensorsDataStatus.ids.length; i++) {
        if (selSensorsDataStatus.entities[selSensorsDataStatus.ids[i]].loading) {
          entitiesLoaded = false;
          break;
        }
      }

      if (entitiesLoaded) {
        var now = new Date().getTime();
        var featuresWithStatus = sensorsGeoJSONState.features.filter(function (feature) {
          return sensorTypeAllowedForMode(presentationMode, feature.properties.type);
        }).map(function (feature) {
          var f = cloneDeep_1(feature);
          var id = feature.properties.type + "/" + feature.properties.identifier;
          var lastPostedInMinutes = -1;

          if (selSensorsDataStatus.entities[id].earliestDate && selSensorsDataStatus.entities[id].latestDate) {
            var earliestDate = new Date(selSensorsDataStatus.entities[id].earliestDate).getTime();
            var lastPosted = new Date(selSensorsDataStatus.entities[id].latestDate).getTime();

            if (lastPosted >= earliestDate) {
              lastPostedInMinutes = Math.round((now - lastPosted) / 1000 / 60);
            }
          }

          f.properties = _extends({}, cloneDeep_1(selSensorsDataStatus.entities[id]), {}, cloneDeep_1(feature.properties), {
            lastPostedInMinutes: lastPostedInMinutes
          });
          return f;
        });
        var sensorsWithStateGeoJSON = {
          type: 'FeatureCollection',
          features: featuresWithStatus
        };

        if (!isEqual$1(sensorsWithStateGeoJSON, sensorsStatusGeoJSONState)) {
          setSensorsStatusGeoJSONState(sensorsWithStateGeoJSON);
        }
      }
    }
  }, [selSensorsDataStatus, sensorsGeoJSONState, sensorsStatusGeoJSONState, presentationMode]);
  useEffect(function () {
    if (sensorsGeoJSONState && !selSensorsDataStatus.loading && sensorsGeoJSONState.features.length === selSensorsDataStatus.ids.length) {
      var entitiesLoaded = true;

      for (var i = 0; i < selSensorsDataStatus.ids.length; i++) {
        if (selSensorsDataStatus.entities[selSensorsDataStatus.ids[i]].loading) {
          entitiesLoaded = false;
          break;
        }
      }

      if (entitiesLoaded) {
        var now = new Date().getTime();
        var listData = {};
        sensorsGeoJSONState.features.filter(function (feature) {
          return feature.properties.type !== 'cavalier';
        }).forEach(function (feature) {
          var id = feature.properties.type + "/" + feature.properties.identifier;
          var lastPostedInMinutes = -1;

          if (selSensorsDataStatus.entities[id].earliestDate && selSensorsDataStatus.entities[id].latestDate) {
            var earliestDate = new Date(selSensorsDataStatus.entities[id].earliestDate).getTime();
            var lastPosted = new Date(selSensorsDataStatus.entities[id].latestDate).getTime();

            if (lastPosted >= earliestDate) {
              lastPostedInMinutes = Math.round((now - lastPosted) / 1000 / 60);
            }
          }

          var properties = _extends({}, cloneDeep_1(selSensorsDataStatus.entities[id]), {}, cloneDeep_1(feature.properties), {
            lastPostedInMinutes: lastPostedInMinutes,
            id: id
          });

          if (!listData[properties.type]) {
            listData[properties.type] = [];
          }

          listData[properties.type].push(properties);
        });

        if (!isEqual$1(listData, listDataState)) {
          setListDataState(listData);
          setIsDataLoading(false);
        }
      }
    }
  }, [selSensorsDataStatus, sensorsGeoJSONState, listDataState]);
  useEffect(function () {
    if (selRanchById && !selRanchById.loading) {
      var ranchesGeoJSON = {
        type: 'FeatureCollection',
        features: [{
          id: "" + selRanchById.id,
          type: 'Feature',
          geometry: selRanchById.bounds,
          properties: selRanchById
        }]
      };

      if (JSON.stringify(ranchesGeoJSONState) !== JSON.stringify(ranchesGeoJSON)) {
        setRanchesGeoJSONState(ranchesGeoJSON);
      }
    }
  }, [selRanchById, ranchesGeoJSONState]);
  useEffect(function () {
    if (selBlocksByRanchId && !selBlocksByRanchId.loading && selBlocksByRanchId instanceof Array) {
      var blocksGeoJSON = {
        type: 'FeatureCollection',
        features: selBlocksByRanchId.map(function (b) {
          return {
            id: "" + b.id,
            type: 'Feature',
            geometry: b.bounds,
            properties: b
          };
        })
      };

      if (JSON.stringify(blocksGeoJSONState) !== JSON.stringify(blocksGeoJSON)) {
        setBlocksGeoJSONState(blocksGeoJSON);
      }
    }
  }, [selBlocksByRanchId, blocksGeoJSONState]);
  var padding = {
    top: 30,
    left: 30,
    bottom: 30,
    right: 130
  };

  if (main_38) {
    padding.top = 10;
    padding.left = 10;
    padding.bottom = 10;
    padding.right = 10;
  }

  if (isDrawerVisible && main_38) {
    padding.bottom = 300 + 10;
  }

  if (isDrawerVisible && !main_38) {
    padding.right = 400 + 30;
  }

  var sensor = selectedSensor || {
    properties: {}
  };
  var isPortrait = window.innerWidth < window.innerHeight;
  return /*#__PURE__*/React.createElement("div", {
    className: "sensors-by-ranch-id-page"
  }, /*#__PURE__*/React.createElement("div", {
    className: "list-switch-package"
  }, /*#__PURE__*/React.createElement(Button, {
    type: viewType === 'map' ? 'primary' : 'secondary',
    value: "map",
    icon: /*#__PURE__*/React.createElement(FaMapMarkedAlt, {
      style: {
        marginTop: '4px'
      }
    }),
    size: "large",
    "data-test-id": "switch-to-mapview",
    onClick: function onClick() {
      if (viewType !== 'map') {
        setViewType('map');
      }
    }
  }), /*#__PURE__*/React.createElement(Button, {
    type: viewType === 'list' ? 'primary' : 'secondary',
    value: "list",
    icon: /*#__PURE__*/React.createElement(FaList, {
      style: {
        marginTop: '4px'
      }
    }),
    size: "large",
    "data-test-id": "switch-to-listview",
    onClick: function onClick() {
      if (viewType !== 'list') {
        setViewType('list');
      }
    }
  })), viewType === 'list' && /*#__PURE__*/React.createElement(ListByRanchId, {
    listData: listDataState,
    isDataLoading: isDataLoading
  }), viewType === 'map' && /*#__PURE__*/React.createElement("div", {
    className: "map-by-ranch-id-page"
  }, /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: "map-by-ranch-id-page" + (main_38 ? ' map-by-ranch-id-page-mobile' : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: "top-controls-package"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    trigger: ['click'],
    className: "gear-dropdown-menu",
    overlay: function overlay() {
      return /*#__PURE__*/React.createElement(MapSettingsMenu, {
        visible: true,
        showLabels: showLabels,
        renderItemShowLabels: presentationMode === 'waterpressure',
        renderItemPresentationMode: true,
        toggleShowLabels: function toggleShowLabels() {
          setShowLabels(!showLabels);
        },
        onChangePresentationMode: function onChangePresentationMode(value) {
          setIsDrawerVisible(false);
          setSelectedSensor(undefined);
          setPresentationMode(value);
        }
      });
    }
  }, /*#__PURE__*/React.createElement(Button, {
    icon: /*#__PURE__*/React.createElement(SettingOutlined, null),
    type: "link",
    size: "large"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "map-page-map"
  }, /*#__PURE__*/React.createElement(ResizeDetector, {
    selectSensor: function selectSensor(sensorType, sensorIdentifier) {
      var s = cloneDeep_1(sensorsStatusGeoJSONState);

      for (var i = 0; i < s.features.length; i++) {
        var _s$features$i$propert = s.features[i].properties,
            identifier = _s$features$i$propert.identifier,
            type = _s$features$i$propert.type;

        if (sensorIdentifier === identifier && sensorType === type) {
          setSelectedSensor(s.features[i]);
          setIsDrawerVisible(true);
          break;
        }
      }
    },
    showPopUpOnMarkerClick: false,
    showAllSensorLabels: presentationMode === 'waterpressure' && showLabels,
    activePopUp: undefined,
    ranchesGeoJSON: ranchesGeoJSONState,
    blocksGeoJSON: blocksGeoJSONState,
    sensorsGeoJSON: sensorsStatusGeoJSONState,
    presentationMode: presentationMode,
    mapBoxPadding: padding,
    selectedSensor: selectedSensor,
    isDataLoading: isDataLoading
  })), !main_38 && /*#__PURE__*/React.createElement(Drawer, {
    className: "drawer-desktop",
    style: {
      marginTop: '50px',
      minWidth: isDrawerVisible ? '300px' : '0px'
    },
    title: t('Item Properties'),
    placement: "right",
    closable: true,
    onClose: function onClose() {
      setIsDrawerVisible(false);
      setSelectedSensor(undefined);
    },
    visible: isDrawerVisible,
    key: "right",
    mask: false
  }, /*#__PURE__*/React.createElement(Descriptions, {
    column: 1
  }, renderOptionalDescription('Name', sensor.properties.name, true), renderOptionalDescription('Sensor Type', sensor.properties.type), renderOptionalDescription('Identifier', sensor.properties.identifier), /*#__PURE__*/React.createElement(Descriptions.Item, {
    label: "Last Post",
    className: "last-posted"
  }, sensor.properties.latestDate ? /*#__PURE__*/React.createElement(Moment, {
    fromNow: true,
    ago: true
  }, sensor.properties.latestDate) : 'None'), presentationMode === 'waterpressure' && renderOptionalDescription('PSI', prepareNumberForDisplay(sensor.properties.psi)))), main_38 && /*#__PURE__*/React.createElement(Drawer, {
    style: {
      minHeight: isDrawerVisible ? isPortrait ? '250px' : '200px' : '0px'
    },
    title: t('Item Properties'),
    placement: "bottom",
    closable: true,
    onClose: function onClose() {
      setIsDrawerVisible(false);
      setSelectedSensor(undefined);
    },
    visible: isDrawerVisible,
    key: "bottom" + isPortrait,
    mask: false
  }, /*#__PURE__*/React.createElement(Descriptions, {
    column: 1
  }, renderOptionalDescription('Name', sensor.properties.name, true), renderOptionalDescription('Sensor Type', sensor.properties.type), renderOptionalDescription('Identifier', sensor.properties.identifier), /*#__PURE__*/React.createElement(Descriptions.Item, {
    label: "Last Post",
    className: "last-posted"
  }, sensor.properties.latestDate ? /*#__PURE__*/React.createElement(Moment, {
    fromNow: true,
    ago: true
  }, sensor.properties.latestDate) : 'None'), presentationMode === 'waterpressure' && renderOptionalDescription('PSI', prepareNumberForDisplay(sensor.properties.psi)))))));
};
MapByRanchId.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      ranch_id: PropTypes.string
    })
  }).isRequired
};

function LogoutRedirect() {
  var _useState = useState(true),
      processing = _useState[0],
      setProcessing = _useState[1];

  useEffect(function () {
    authService.logout().then(function () {
      setProcessing(false);
    });
  }, [setProcessing]);
  if (processing) return null;
  return /*#__PURE__*/React.createElement(Redirect, {
    to: "/"
  });
}

export { AppTemplate, BlockControlList, BlockSelect, BooleanTag, ChartComponent, CollapsableSidebar, RefreshContext as ControlSummaryRefreshContext, EntityList, FilterListItem, HighchartsSchedule, InstallStateTag, IrrigationSchedule, LogoutRedirect, ResizeDetector as Map, MapByRanchId, MapSettingsMenu, PrivateRoute, PumpControlList, RanchSelect, ScheduleCreateForm, ScheduleUpdateForm, SensorSelect, SensorTypeSelect, SensorTypeTag, UserMenu, mapPresentationModes, prepareNumberForDisplay, prepareZeroIslandSensors, sensorTypeAllowedForMode };
//# sourceMappingURL=index.modern.js.map
